/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, Tooltip, ResponsiveContainer,
  ReferenceLine, ReferenceDot, ReferenceArea, LineChart, Line,
  ComposedChart, Cell,
} from "recharts";
import { UniverseDataProvider } from "./hooks/useUniverseData";
import * as Haptics from "./haptics";

// ── Install prompt — module-level setup ────────────────────────────────────────
// beforeinstallprompt can fire before React mounts, so stash it at module level.
let _bipEvt = null;
try { window.addEventListener('beforeinstallprompt', (e) => { e.preventDefault(); _bipEvt = e; }, { once: true }); } catch (_) {}
const INSTALL_SESSIONS_KEY  = 'gp_install_sessions';
const INSTALL_DISMISSED_KEY = 'gp_install_dismissed';
const INSTALL_SNOOZED_KEY   = 'gp_install_snoozed';
function _isIosSafari() {
  const ua = navigator.userAgent;
  return /iPhone|iPad/.test(ua) && /Safari/.test(ua) && !/CriOS|FxiOS/.test(ua);
}
function _isStandalone() {
  return window.navigator.standalone === true
    || window.matchMedia('(display-mode: standalone)').matches;
}
function _shouldShowInstall() {
  try {
    if (localStorage.getItem(INSTALL_DISMISSED_KEY) === 'true') return false;
    const snz = localStorage.getItem(INSTALL_SNOOZED_KEY);
    if (snz && Date.now() - new Date(snz).getTime() < 7 * 24 * 60 * 60 * 1000) return false;
    return parseInt(localStorage.getItem(INSTALL_SESSIONS_KEY) || '0', 10) >= 1;
  } catch { return false; }
}

// --- API base URL -- swap this to your Railway URL when deployed -------------
const API = "https://api.gempulse.io";

// VAPID public key for web push subscriptions (v7.9.99).
// Private key lives in backend .env (VAPID_PRIVATE_KEY). This is intentionally public.
const VAPID_PUBLIC_KEY = "BMZQalybkbYo-SP-2K2xkJS86FsctFNptRGiUqtYTN7Tk8pCj6dZczIJ6UnCcsyNA6a8-o_dISDMAaGYMT49H9s";

function urlBase64ToUint8Array(b64) {
  const pad = '='.repeat((4 - (b64.length % 4)) % 4);
  const raw = atob((b64 + pad).replace(/-/g, '+').replace(/_/g, '/'));
  return new Uint8Array([...raw].map(c => c.charCodeAt(0)));
}

// No hardcoded version — update detection uses localStorage to track the last
// API version the browser saw. Banner fires on genuine backend changes without
// ever needing a manual version bump in this file.

// NYSE market holidays 2025-2027 (observed dates, YYYY-MM-DD ET)
// Updated each year — add next year when approaching December.
const NYSE_HOLIDAYS = new Set([
  // 2025
  "2025-01-01","2025-01-20","2025-02-17","2025-04-18",
  "2025-05-26","2025-06-19","2025-07-04","2025-09-01",
  "2025-11-27","2025-12-25",
  // 2026
  "2026-01-01","2026-01-19","2026-02-16","2026-04-03",
  "2026-05-25","2026-06-19","2026-07-03","2026-09-07",
  "2026-11-26","2026-12-25",
  // 2027
  "2027-01-01","2027-01-18","2027-02-15","2027-03-26",
  "2027-05-31","2027-06-18","2027-07-05","2027-09-06",
  "2027-11-25","2027-12-24",
]);

// Market hours helper -- module level so all components can use it
function isMarketOpen() {
  const now = new Date();
  const et  = new Date(now.toLocaleString("en-US", {timeZone:"America/New_York"}));
  const day = et.getDay();
  if (day === 0 || day === 6) return false;
  // Check NYSE holidays (format YYYY-MM-DD in ET)
  const yy  = et.getFullYear();
  const mm  = String(et.getMonth() + 1).padStart(2, "0");
  const dd  = String(et.getDate()).padStart(2, "0");
  if (NYSE_HOLIDAYS.has(`${yy}-${mm}-${dd}`)) return false;
  const mins = et.getHours() * 60 + et.getMinutes();
  return mins >= 570 && mins <= 960; // 9:30am to 4:00pm ET
}

// --- Design tokens -----------------------------------------------------------
// Trading-preference label SELECTOR (v8.4.296). The BACKEND swingType/ltType are the
// single source of truth (they already reflect the score + risk-profile gates, computed
// once and cached). This just PICKS which canonical label to show for the user's
// trade preference — it no longer re-derives a divergent label from raw scores with
// different thresholds (the old 72/58 vs backend 75/60 mismatch that made the same stock
// read "Buy" on one surface and "Watch" on another). "lt" → the long-term label;
// "swing"/"blended" → the swing (near-term actionable) label. Score args kept for
// call-site signature compatibility but unused.
function prefSignal(swingScore, swingType, ltScore, ltType, tradePref) {
  if (tradePref === "lt") return { type: ltType || "watch" };
  return { type: swingType || "watch" };
}

// Currency conversion helper — used throughout for price display
// currency: "native" | "usd" | "cad"
// ticker: used to detect .TO stocks (native = CAD)
function fmtCurrency(price, ticker, currency, fxRates) {
  if (price == null || price === 0) return "—";
  const isTSX  = String(ticker || "").endsWith(".TO");
  // CADUSD=X Yahoo ticker: rate = USD per 1 CAD ≈ 0.735
  // USDCAD = 1/rate ≈ 1.36 (CAD per 1 USD) — use this to convert USD→CAD
  // CADUSD = rate  ≈ 0.735 (USD per 1 CAD) — use this to convert CAD→USD
  const usdToCad = fxRates?.USDCAD || 1.36; // multiply USD price by this to get CAD
  const cadToUsd = fxRates?.CADUSD || 0.735; // multiply CAD price by this to get USD

  let converted = price;
  let symbol    = "$";

  if (currency === "usd") {
    // TSX prices are in CAD → convert to USD
    converted = isTSX ? price * cadToUsd : price;
    symbol    = "US$";
  } else if (currency === "cad") {
    // US prices are in USD → convert to CAD
    converted = isTSX ? price : price * usdToCad;
    symbol    = "CA$";
  }
  // "native" = no conversion

  if (converted >= 1000) return `${symbol}${converted.toLocaleString("en",{maximumFractionDigits:0})}`;
  if (converted >= 10)   return `${symbol}${converted.toFixed(2)}`;
  return `${symbol}${converted.toFixed(3)}`;
}

const C = {
  bg:"#080c14", surface:"#0f1623", card:"#131d2e",
  border:"rgba(255,255,255,0.07)", accent:"#00d4ff",
  green:"#00e676", greenBg:"rgba(0,230,118,0.1)",
  yellow:"#ffca28", yellowBg:"rgba(255,202,40,0.1)",
  red:"#ff5252",   redBg:"rgba(255,82,82,0.1)",
  blue:"#448aff",  blueBg:"rgba(68,138,255,0.1)",
  text:"#e8eaf0",  muted:"#5a6a88", divider:"rgba(255,255,255,0.06)",
};

const SIGNAL_META = {
  strong_buy:  { color:C.green,  bg:C.greenBg,  icon:"↑↑", short:"STRONG BUY"  },
  buy:         { color:C.green,  bg:C.greenBg,  icon:"↑",  short:"BUY"         },
  watch:       { color:C.yellow, bg:C.yellowBg, icon:"→",  short:"WATCH"       },
  pass:        { color:C.red,    bg:C.redBg,    icon:"↓",  short:"PASS"        },
};

const HORIZONS = [
  { key:"1m", label:"1 Month" },
  { key:"3m", label:"3 Months" },
  { key:"6m", label:"6 Months" },
  { key:"1y", label:"1 Year" },
  { key:"3y", label:"3 Years" },
];

const PROFILES = [
  { key:"conservative", emoji:"🛡", label:"Conservative",
    desc:"Low risk. Capital protection first." },
  { key:"moderate",     emoji:"⚖️", label:"Moderate",
    desc:"Balanced. Growth with managed risk." },
  { key:"aggressive",   emoji:"🚀", label:"Aggressive",
    desc:"High growth. Comfortable with swings." },
];

const DEFAULT_WATCHLIST = [
  "AAPL","TSLA","NVDA","MSFT","GOOGL","AMZN",
  "META","AMD","SPY","QQQ","AVGO","COIN","HOOD","PLTR",
];

// --- API helpers -------------------------------------------------------------
// Special error class so App can catch 401s and log out cleanly
class AuthError extends Error { constructor(m) { super(m); this.name="AuthError"; } }

async function apiFetch(path, token, options={}) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 180000); // 3 min timeout
  try {
    const res = await fetch(`${API}${path}`, {
      ...options,
      signal: controller.signal,
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        ...(options.headers || {}),
      },
    });
    clearTimeout(timeout);
    const data = await res.json().catch(() => ({}));
    if (res.status === 401) throw new AuthError("Session expired -- please log in again.");
    // 403 + "deactivated" or "User not found" → server rejected the session
    // (admin deactivated this user, or user was deleted). Treat as AuthError
    // so handleAuthError boots them to the login screen immediately instead
    // of letting them keep scrolling on stale UI.
    if (res.status === 403) {
      const detailStr = typeof data.detail === "string"
        ? data.detail.toLowerCase()
        : JSON.stringify(data.detail || "").toLowerCase();
      if (detailStr.includes("deactivated") || detailStr.includes("not found")) {
        throw new AuthError("Your account is no longer active. Please contact an admin.");
      }
    }
    if (!res.ok) {
      // Detail can be:
      //   • string (the common case — HTTPException(detail="..."))
      //   • array (Pydantic validation errors — flatten "loc: msg")
      //   • object (some routes return structured detail — extract .message
      //     or fall back to JSON.stringify so we don't render "[object Object]")
      let msg = data.detail;
      if (Array.isArray(msg)) {
        msg = msg.map(e => `${(e.loc||[]).join(".")}: ${e.msg}`).join("; ");
      } else if (msg && typeof msg === "object") {
        msg = msg.message || msg.error || JSON.stringify(msg);
      }
      throw new Error(msg || `Request failed (${res.status})`);
    }
    return data;
  } catch(e) {
    clearTimeout(timeout);
    if (e.name === "AbortError") throw new Error("Request timed out -- please try again.");
    throw e;
  }
}

// ── Resilient widget fetch (v8.4.251) ───────────────────────────────────────
// Immunizes homepage widgets against the recurring "vanish and never come back" bug:
// a fetch-once effect that does setState(response) unconditionally (so an empty/failed
// fetch overwrites good data) and renders null when data is absent. Especially bites
// right AFTER A REDEPLOY (backend caches cold until Celery re-scores) and when markets
// are closed. This hook:
//   • keeps the LAST GOOD data — never overwrites it with an empty/failed response, and
//   • REFRESHES on an interval so a cold-cache miss self-heals.
// `isEmpty(d)` decides what counts as "no data" for that endpoint (default: null/undefined).
// v8.4.356: module-level last-good cache keyed by path — survives component
// UNMOUNT/REMOUNT (navigation), so a widget like Metals & Crypto no longer
// vanishes when you leave and return and the refetch comes back empty (market
// closed / cold backend). Component-local state alone only survived a transient
// empty *refresh*, not navigation. Mirrors the TickerLogo module-cache pattern.
const _resilientCache = new Map(); // path -> { data, ts }

function useResilientFetch(path, token, { isEmpty, refreshMs = 90000 } = {}) {
  const cached = path ? _resilientCache.get(path) : null;
  const [data, setData]           = React.useState(cached ? cached.data : null);
  const [updatedAt, setUpdatedAt] = React.useState(cached ? cached.ts   : null);
  // No spinner if we already have cached data to show immediately.
  const [loading, setLoading]     = React.useState(!cached);
  // Did THIS mount get a confirmed-fresh (non-empty) refresh yet? If not and we
  // are showing cached data, it may be delayed → surface a stale note.
  const [fresh, setFresh]         = React.useState(false);
  const emptyRef = React.useRef(isEmpty);
  emptyRef.current = isEmpty;
  React.useEffect(() => {
    if (!token || !path) { setLoading(false); return; }
    let alive = true;
    const _isEmpty = emptyRef.current || ((d) => d == null);
    const load = () => {
      apiFetch(path, token)
        .then(d => { if (alive) {
          if (!_isEmpty(d)) {
            const ts = Date.now();
            setData(d); setUpdatedAt(ts); setFresh(true);
            _resilientCache.set(path, { data: d, ts });
          }
          setLoading(false);
        } })
        .catch(() => { if (alive) setLoading(false); });
    };
    load();
    const iv = refreshMs ? setInterval(load, refreshMs) : null;
    return () => { alive = false; if (iv) clearInterval(iv); };
  }, [path, token, refreshMs]);
  // stale = showing data we have NOT confirmed-refreshed this view (cache-restored
  // on remount, or every refresh since came back empty).
  const stale = data != null && !fresh;
  return { data, loading, stale, updatedAt };
}

// Map API candle fields to chart-friendly keys
// o/h/l added v8.3.28 for candlestick chart mode
function normaliseCandles(candles) {
  return (candles || []).map(c => ({
    d:   c.date  || c.d  || "",
    iso: c.iso   || "",   // v8.4.255: unambiguous YYYY-MM-DD for earnings-marker matching

    o:   c.open  || c.o  || null,
    h:   c.high  || c.h  || null,
    l:   c.low   || c.l  || null,
    c:   c.close || c.c  || 0,
    vol: c.volume|| c.vol|| 0,
    s20: c.sma20 || c.s20|| null,
    s50: c.sma50 || c.s50|| null,
  }));
}

// ── Candlestick bar shape for recharts ComposedChart ──────────────────────
// Renders a single OHLC candle (wick + body) inside a recharts <Bar>.
// Receives standard recharts Bar shape props (x, y, width, height) plus
// yDomain injected via the shape callback.
// Math: with baseValue={yDomain[0]} on the Bar, recharts ensures:
//   y + height  = pixel y of yDomain[0]  (= plot-area bottom)
// So:  toY(p) = plotH * (1 − (p − yMin) / (yMax − yMin))
const CandleBar = ({ x, y, width, height, payload, yDomain }) => {
  if (!payload || !yDomain || height == null || y == null) return null;
  const { o, h, l, c } = payload;
  if (c == null) return null;
  const [yMin, yMax] = yDomain;
  const range = yMax - yMin;
  if (!range) return null;
  const plotH = y + height;                          // pixel y of yDomain[0]
  const toY   = p => plotH * (1 - (p - yMin) / range);
  const clamp = p => Math.min(yMax, Math.max(yMin, p));
  const isBull = (o != null ? c >= o : true);
  // isPrior: previous session muted context. isAH: after-hours bar (dimmed)
  const isPrior = payload.isPrior === true;
  const isAH    = payload.isAH    === true;
  const opacity = isPrior ? 0.3 : isAH ? 0.5 : 1.0;
  const col = (isPrior || isAH)
    ? (isBull ? `rgba(0,230,118,${opacity})` : `rgba(255,82,82,${opacity})`)
    : (isBull ? "#00e676" : "#ff5252");
  const yC   = toY(c);
  const yO   = o  != null ? toY(o)         : yC;
  const yH   = h  != null ? toY(clamp(h))  : Math.min(yC, yO);
  const yL   = l  != null ? toY(clamp(l))  : Math.max(yC, yO);
  const top  = Math.min(yO, yC);
  const bH   = Math.max(Math.abs(yC - yO), 1.5);   // body height ≥ 1.5px
  const bW   = Math.max(width - 1, 1);
  const midX = x + width / 2;
  return (
    <g opacity={isPrior ? 0.5 : isAH ? 0.6 : 1}>
      {/* High-low wick */}
      <line x1={midX} y1={yH} x2={midX} y2={yL} stroke={col} strokeWidth={1}/>
      {/* Open-close body: filled (bull) or hollow (bear) */}
      <rect x={x + 0.5} y={top} width={bW} height={bH}
        fill={isBull ? col : "none"} stroke={col} strokeWidth={1}/>
    </g>
  );
};

// --- Utilities ---------------------------------------------------------------
const round2 = (n) => Math.round(n * 100) / 100;

// Company logo — 3-source cascade, instant fallback, no retry delays.
//
// Source order:
//   0. Parqet direct   — assets.parqet.com/logos/symbol/{ticker}?format=png
//                        Primary source; best visual quality; broad coverage
//                        including S&P 500, NASDAQ, TSX, international.
//                        Note: returns HTTP 200 with a small placeholder for
//                        completely unknown symbols — acceptable trade-off since
//                        the app only shows real traded tickers.
//   1. FMP direct      — financialmodelingprep.com/image-stock/{ticker}.png
//                        Fallback; returns real HTTP 404 for unknowns so onError
//                        fires cleanly. Good US/CA large-mid cap coverage.
//   2. Initials avatar — always renders; distinct color per ticker initial.
//
// The backend proxy (/logo/{ticker}) is intentionally excluded from the browser
// cascade: on old builds (< v7.9.30) it serves Parqet placeholders as valid
// logos, causing onLoad to fire on a placeholder and blocking the FMP fallback.
// After the backend is rebuilt the proxy is used server-side during warmup;
// the frontend gets the same real logos via the Parqet CDN directly.
//
// <img> elements bypass CORS so CDN URLs load from any domain. Successful
// responses are browser-cached so the same ticker on any screen is instant.
// Module-level cache of SUCCESSFUL logo resolutions per clean ticker symbol.
// Survives React unmounts (navigation, modal close, screen switch) so a second
// mount of the same ticker renders the real logo immediately without
// re-flashing initials through the CDN cascade.
//
// Only SUCCESSES are cached. Failures are intentionally NOT cached: transient
// CDN issues (Parqet 429 rate-limit spikes on bulk mount, FMP slow responses)
// must be allowed to retry on the next remount. Caching failures turned the
// first bad network blip into a permanent missing-logo for the whole session.
const _logoCache = new Map(); // clean -> { srcIdx } (only set on success)

// Cascade order (restored to pre-v8.3.32 — FMP at position 2):
//   1. Parqet      - fast free CDN, best for large/mega caps
//   2. FMP         - good PNG logos for most US large-caps; orange placeholder for
//                    unknowns (HTTP 200 — onLoad fires, stops cascade here).
//                    Accepted trade-off: FMP's named placeholder looks nicer than
//                    SA/TV SVGs for major names. VRT gets orange — known low-priority.
//   3. StockAnalysis - SVG, broad coverage incl. mid/small caps
//   4. TradingView   - SVG, very complete for US + ETFs
//   5. Backend /logo - disk-cached server proxy (includes Polygon). Last resort
//                      because it adds a network hop and FMP already covers most names.
const _LOGO_SOURCES = (clean) => [
  `https://assets.parqet.com/logos/symbol/${clean}?format=png`,
  `https://financialmodelingprep.com/image-stock/${clean}.png`,
  `https://assets.stockanalysis.com/stocks/${clean.toLowerCase()}/logo.svg`,
  `https://s3-symbol-logo.tradingview.com/${clean}--big.svg`,
  `${API}/logo/${clean}`,
];

function TickerLogoBase({ ticker, size=28 }) {
  const clean = (ticker || "").replace(/\.(TO|V|TSX)$/i, "");

  const SOURCES = _LOGO_SOURCES(clean);

  // If we've previously loaded this logo in the session, mount at "ok" with
  // the winning srcIdx so there's no flash. Otherwise start at the top of the
  // cascade and let the original retry logic run.
  const cached = _logoCache.get(clean);
  const [srcIdx, setSrcIdx] = React.useState(cached ? cached.srcIdx : 0);
  const [status, setStatus] = React.useState(cached ? "ok" : "loading");
  const lastCleanRef = React.useRef(clean);

  // If the same instance is reused for a different ticker (e.g. virtualized
  // row), re-hydrate from the cache for the new ticker — or restart the
  // cascade if we haven't seen that ticker yet.
  React.useEffect(() => {
    if (lastCleanRef.current !== clean) {
      lastCleanRef.current = clean;
      const c = _logoCache.get(clean);
      setSrcIdx(c ? c.srcIdx : 0);
      setStatus(c ? "ok" : "loading");
    }
  }, [clean]);

  const handleLoad = () => {
    setStatus("ok");
    _logoCache.set(clean, { srcIdx });   // cache the winning CDN for this ticker
  };

  const handleError = () => {
    const next = srcIdx + 1;
    if (next < SOURCES.length) {
      setSrcIdx(next);                   // do NOT cache — let next remount retry
    } else {
      setStatus("failed");               // do NOT cache failures
    }
  };

  const initials = clean.slice(0, 2).toUpperCase();
  const colors   = ["#00d4ff","#00e676","#ffca28","#ff7043","#ab47bc","#26c6da"];
  const bg       = colors[(clean.charCodeAt(0) || 0) % colors.length];

  if (!clean) return null;

  return (
    <div style={{width:size, height:size, flexShrink:0, position:"relative",
      display:"inline-flex", alignItems:"center", justifyContent:"center"}}>
      {/* Initials avatar — always rendered; fades out when logo is ready */}
      <div style={{position:"absolute", inset:0, borderRadius:"50%",
        background:`${bg}22`, border:`1px solid ${bg}44`,
        display:"flex", alignItems:"center", justifyContent:"center",
        opacity: status === "ok" ? 0 : 1,
        transition: "opacity 150ms ease-out",
        pointerEvents: "none"}}>
        <span style={{fontSize:size*0.38, fontWeight:800, color:bg,
          fontFamily:"monospace"}}>{initials}</span>
      </div>
      {/* Logo image — cycles through SOURCES on error */}
      {status !== "failed" && (
        <img
          key={`${clean}-${srcIdx}`}
          src={SOURCES[srcIdx]}
          alt=""
          onLoad={handleLoad}
          onError={handleError}
          style={{
            position:"absolute", inset:0, borderRadius:"50%",
            objectFit:"contain", background:"rgba(255,255,255,0.06)",
            width:size, height:size,
            opacity: status === "ok" ? 1 : 0,
            transition: "opacity 150ms ease-out",
          }}
        />
      )}
    </div>
  );
}

const TickerLogo = React.memo(
  TickerLogoBase,
  (a, b) => a.ticker === b.ticker && a.size === b.size
);

// RSI visual pill -- color + label + mini arc gauge
function RSIPill({ rsi, size="sm" }) {
  if (rsi == null) return null;
  const v = parseFloat(rsi);

  // Color, label, action hint
  const { color, bg, border, label, action } =
    v < 30  ? { color:"#00e676", bg:"rgba(0,230,118,0.12)", border:"rgba(0,230,118,0.3)",  label:"Oversold",   action:"Buy signal" } :
    v < 40  ? { color:"#69f0ae", bg:"rgba(105,240,174,0.1)", border:"rgba(105,240,174,0.25)",label:"Low",        action:"Watch for entry" } :
    v < 55  ? { color:"rgba(255,255,255,0.5)", bg:"rgba(255,255,255,0.05)", border:"rgba(255,255,255,0.1)", label:"Neutral", action:"No signal" } :
    v < 65  ? { color:"#ffca28", bg:"rgba(255,202,40,0.1)",  border:"rgba(255,202,40,0.25)", label:"Elevated",  action:"Watch closely" } :
    v < 75  ? { color:"#ff7043", bg:"rgba(255,112,67,0.1)",  border:"rgba(255,112,67,0.25)", label:"High",      action:"Caution" } :
              { color:"#ff5252", bg:"rgba(255,82,82,0.12)",  border:"rgba(255,82,82,0.3)",   label:"Overbought","action":"Pullback risk" };

  // Mini arc: sweep from left to right, filled proportionally
  const pct    = Math.min(100, Math.max(0, v));
  const r      = 7;
  const cx     = 10; const cy = 10;
  const start  = { x: cx - r, y: cy };           // leftmost point
  const sweep  = (pct / 100) * Math.PI;
  const ex     = cx + r * Math.cos(Math.PI - sweep);
  const ey     = cy - r * Math.sin(sweep);
  const large  = sweep > Math.PI / 2 ? 1 : 0;

  if (size === "xs") return (
    <span style={{
      display:"inline-flex", alignItems:"center", gap:3,
      background:bg, border:`1px solid ${border}`,
      borderRadius:20, padding:"1px 6px",
      fontSize:10, fontWeight:700, color, lineHeight:1.4,
      whiteSpace:"nowrap",
    }}>
      {Math.round(v)}
    </span>
  );

  return (
    <span title={`RSI ${Math.round(v)} -- ${label}: ${action}`} style={{
      display:"inline-flex", alignItems:"center", gap:5,
      background:bg, border:`1px solid ${border}`,
      borderRadius:20, padding:"2px 8px 2px 5px",
      whiteSpace:"nowrap", cursor:"default",
    }}>
      {/* Mini arc gauge */}
      <svg width="20" height="12" viewBox="0 0 20 12" style={{flexShrink:0}}>
        {/* Track */}
        <path d={`M ${cx-r} ${cy} A ${r} ${r} 0 0 1 ${cx+r} ${cy}`}
          fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="2.5"
          strokeLinecap="round"/>
        {/* Fill */}
        {pct > 2 && (
          <path d={`M ${cx-r} ${cy} A ${r} ${r} 0 ${large} 1 ${ex} ${ey}`}
            fill="none" stroke={color} strokeWidth="2.5"
            strokeLinecap="round"/>
        )}
        {/* Needle dot */}
        <circle cx={ex} cy={ey} r="1.8" fill={color}/>
      </svg>
      <span style={{fontSize:10,fontWeight:700,color}}>{Math.round(v)}</span>
      <span style={{fontSize:9,color:"rgba(255,255,255,0.45)"}}>{label}</span>
    </span>
  );
}

// Beta pill -- volatility relative to market
function BetaPill({ beta }) {
  if (beta == null) return null;
  const v = parseFloat(beta);
  const { color, bg, border, label } =
    v < 0.5  ? { color:"#69f0ae", bg:"rgba(105,240,174,0.1)", border:"rgba(105,240,174,0.25)", label:"Low vol" } :
    v < 0.85 ? { color:"rgba(255,255,255,0.55)", bg:"rgba(255,255,255,0.05)", border:"rgba(255,255,255,0.1)", label:"Stable" } :
    v < 1.15 ? { color:"rgba(255,255,255,0.55)", bg:"rgba(255,255,255,0.05)", border:"rgba(255,255,255,0.1)", label:"Market" } :
    v < 1.5  ? { color:"#ffca28", bg:"rgba(255,202,40,0.1)", border:"rgba(255,202,40,0.25)", label:"Volatile" } :
               { color:"#ff7043", bg:"rgba(255,112,67,0.1)", border:"rgba(255,112,67,0.25)", label:"High vol" };
  return (
    <span title={`Beta ${v.toFixed(2)} -- ${label}. Measures how much this stock moves relative to the S&P 500. Beta > 1 = more volatile than market.`}
      style={{display:"inline-flex",alignItems:"center",gap:4,
        background:bg,border:`1px solid ${border}`,
        borderRadius:20,padding:"2px 8px",whiteSpace:"nowrap",cursor:"default"}}>
      <span style={{fontSize:11,fontWeight:700,color,fontFamily:"monospace"}}>β {v.toFixed(2)}</span>
      <span style={{fontSize:9,color:"rgba(255,255,255,0.45)"}}>{label}</span>
    </span>
  );
}

// ATR pill -- average true range as % of price
function AtrPill({ atr, price }) {
  if (atr == null || !price) return null;
  const pct = (parseFloat(atr) / parseFloat(price)) * 100;
  const { color, bg, border, label } =
    pct < 1   ? { color:"#69f0ae", bg:"rgba(105,240,174,0.1)", border:"rgba(105,240,174,0.25)", label:"Low" } :
    pct < 2   ? { color:"rgba(255,255,255,0.55)", bg:"rgba(255,255,255,0.05)", border:"rgba(255,255,255,0.1)", label:"Normal" } :
    pct < 3.5 ? { color:"#ffca28", bg:"rgba(255,202,40,0.1)", border:"rgba(255,202,40,0.25)", label:"Active" } :
                { color:"#ff7043", bg:"rgba(255,112,67,0.1)", border:"rgba(255,112,67,0.25)", label:"Wild" };
  return (
    <span title={`ATR $${parseFloat(atr).toFixed(2)} = ${pct.toFixed(1)}% of price. Average daily trading range -- higher means bigger daily swings.`}
      style={{display:"inline-flex",alignItems:"center",gap:4,
        background:bg,border:`1px solid ${border}`,
        borderRadius:20,padding:"2px 8px",whiteSpace:"nowrap",cursor:"default"}}>
      <span style={{fontSize:11,fontWeight:700,color,fontFamily:"monospace"}}>{pct.toFixed(1)}%</span>
      <span style={{fontSize:9,color:"rgba(255,255,255,0.45)"}}>ATR {label}</span>
    </span>
  );
}


// --- App Logo -- Option C hexagon with blue diamond peak ----------------------
function AppLogo({ size=80 }) {
  return (
    <div style={{width:size,height:size,flexShrink:0,overflow:"hidden",
      borderRadius:size*0.22,background:"#09131c"}}>
      <img src="/logo192.png" alt="GemPulse" width={size} height={size}
        style={{display:"block",width:"100%",height:"100%",objectFit:"cover"}}/>
    </div>
  );
}


// --- Frozen (sticky) screen header -------------------------------------------
// Pins screen-level chrome (title + controls) to the top of the viewport on
// scroll. Handles the two gotchas the stock-detail header hit:
//   1) Notch: the app root reserves the safe-area inset as paddingTop, which
//      scrolls away once pinned — so pull up by the inset (negative marginTop)
//      and add it back as paddingTop so content stays below the status bar.
//   2) Full-bleed: screens pad their body by `bleed`px; break the header out to
//      full width and re-pad inside, so scrolling content can't peek through the
//      side gaps. Pass bleed=0 for screens whose wrapper has no horizontal pad.
// Requires the screen's scroll ancestor to NOT be overflow:auto/hidden (that
// traps sticky) — same rule as the app shell.
function FrozenHeader({ children, bleed=16, padX=16, padTop=14, padBottom=10, zIndex=20 }) {
  return (
    <div style={{
      position:"sticky", top:0, zIndex,
      background:C.bg, borderBottom:`1px solid ${C.border}`,
      marginTop:"calc(-1 * env(safe-area-inset-top, 0px))",
      marginLeft:-bleed, marginRight:-bleed,
      paddingTop:`calc(${padTop}px + env(safe-area-inset-top, 0px))`,
      paddingBottom:padBottom, paddingLeft:padX, paddingRight:padX,
    }}>
      {children}
    </div>
  );
}


// --- App wordmark (logo + text) ----------------------------------------------
function AppWordmark({ logoSize=44, showTagline=false }) {
  return (
    <div style={{display:"flex",alignItems:"center",gap:14}}>
      <AppLogo size={logoSize}/>
      <div>
        <div style={{fontSize:logoSize*0.45,fontWeight:900,letterSpacing:"-0.03em",lineHeight:1}}>
          <span style={{color:C.text}}>Gem</span><span style={{color:C.accent}}>Pulse</span>
        </div>
        {showTagline && (
          <div style={{fontSize:logoSize*0.18,color:"rgba(255,255,255,0.4)",marginTop:4,
            letterSpacing:"0.04em"}}>
            Scan the Swings ◆ Stack the Wins
          </div>
        )}
      </div>
    </div>
  );
}


// ── Sparkline — tiny 24×10 SVG showing the day's intraday price path ──
// Drawn from the last ~14 close points. Stroke + fill colored green if last >
// first (up day), red otherwise. Renders nothing if fewer than 2 points
// (avoids a meaningless flat line on tickers where intraday candles failed).
function Sparkline({ data, width = 36, height = 14, up = null }) {
  if (!Array.isArray(data) || data.length < 2) return null;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = (max - min) || 1;
  // x-step across width; y is normalized to 1..(height-1) so stroke isn't clipped
  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * (width - 2) + 1;
    const y = height - 1 - ((v - min) / range) * (height - 2);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
  // Direction inferred from data unless caller already knows it (e.g. from changePct)
  const isUp = up == null ? data[data.length - 1] >= data[0] : !!up;
  const stroke = isUp ? "#00e676" : "#ff5252";
  const fill   = isUp ? "rgba(0,230,118,0.12)" : "rgba(255,82,82,0.12)";
  // Build polygon for fill (line + bottom corners)
  const polyPts = `${pts} ${width-1},${height-1} 1,${height-1}`;
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}
         style={{display:"block",overflow:"visible"}}>
      <polygon points={polyPts} fill={fill} stroke="none"/>
      <polyline points={pts} fill="none" stroke={stroke}
                strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}


// ── extractIntradaySparkline — derive the "1D" sparkline series from the
// same candle payload the stock-detail 1D chart renders, so the watchlist/
// discovery tile sparkline always matches the detail chart's shape.
//
// candles_intraday_15m may be either:
//   - 1-min Polygon bars (today + yesterday, ~390 bars/session, "d" labeled
//     "HH:MM" for today / "Day HH:MM" for prior session), or
//   - 15-min yfinance bars (~26 bars/session)
//
// The detail chart isolates TODAY's bars via the "HH:MM"-only label regex.
// A flat `.slice(-26)` on 1-min data only grabs the last 26 MINUTES — not
// the full session — which hides earlier-session moves (e.g. a V-shaped
// recovery). Mirror the detail chart's logic: prefer today's bars; fall
// back to the most-recent-session window when no "today" bars exist
// (pre-market / weekend / holiday).
//
// Once today's session has at least one bar, anchor the line to the prior
// session's last close ("Day HH:MM"-labeled bars) so the sparkline opens as
// a 2-point line (yesterday's close → today's first print) instead of
// either a lone unrenderable dot or a jarring jump straight to yesterday's
// full shape. If no prior-session bar is available, duplicate the single
// today bar so Sparkline (which needs >= 2 points) still renders a flat dot.
function extractIntradaySparkline(candles15m, candles60m) {
  const c15 = Array.isArray(candles15m) ? candles15m : [];
  const c60 = Array.isArray(candles60m) ? candles60m : [];
  const intradayCandles = c15.length > 0 ? c15 : c60;
  if (!intradayCandles.length) return [];

  // v8.4.139: prefer the backend's authoritative `today` boolean (Polygon
  // 1-min bars) when present — eliminates label-format ambiguity that could
  // cause a full prior session to be misclassified as "today" (rendering as
  // a full-day-looking line a few minutes after open). Fall back to the
  // label regex for yfinance 15m records (Canadian tickers), which lack the
  // `today` field.
  const hasTodayFlag = intradayCandles.some(c => typeof c?.today === "boolean");
  const todayIntraday = hasTodayFlag
    ? intradayCandles.filter(c => c?.today === true)
    : intradayCandles.filter(c => /^\d{2}:\d{2}$/.test(c?.d));
  const priorIntraday = hasTodayFlag
    ? intradayCandles.filter(c => c?.today === false)
    : intradayCandles.filter(c => /^\w{3} \d{2}:\d{2}$/.test(c?.d));
  let series;
  if (todayIntraday.length > 0) {
    const anchor = priorIntraday.length > 0 ? [priorIntraday[priorIntraday.length - 1]] : [];
    series = [...anchor, ...todayIntraday];
  } else {
    const is1MinData = intradayCandles === c15 && intradayCandles.length > 100;
    const windowBars = is1MinData ? 390 : (intradayCandles === c15 ? 26 : 8);
    series = intradayCandles.slice(-windowBars);
  }
  // v8.4.137: Sparkline needs >= 2 points to render. The duplicate-single-
  // point safeguard previously only applied to the "today" branch — when
  // the fallback (`else`) branch resolved to a single bar (e.g. a cached
  // intraday series with only 1 entry, or candles whose "d" labels don't
  // match either regex), the result silently came back as a 1-element
  // array, hasI() failed, and the tile lost its "1D" toggle entirely
  // (only "3W" remained). Apply the duplicate fix after BOTH branches.
  if (series.length === 1) series = [series[0], series[0]];
  return series
    .map(c => (typeof c === "object" ? c.c : c))
    .filter(v => typeof v === "number" && !isNaN(v));
}


// Small info icon with tooltip
// P/E pill — forward P/E ratio colour-coded by valuation
function PePill({ pe, peType }) {
  if (pe == null || pe <= 0 || pe > 2000) return (
    <span style={{display:"inline-flex",alignItems:"center",gap:4,
      background:"rgba(255,255,255,0.05)",border:"1px solid rgba(255,255,255,0.1)",
      borderRadius:20,padding:"2px 10px",fontSize:11,fontWeight:700,color:C.muted}}>
      N/A
    </span>
  );
  const v = parseFloat(pe);
  // Colour bands: <15 cheap (green), 15-25 fair (yellow), 25-40 stretched (orange), >40 expensive (red)
  const color = v < 15 ? C.green : v < 25 ? "#ffca28" : v < 40 ? "#ff7043" : "#ff5252";
  const bg    = v < 15 ? "rgba(0,230,118,0.1)" : v < 25 ? "rgba(255,202,40,0.1)"
              : v < 40 ? "rgba(255,112,67,0.1)" : "rgba(255,82,82,0.1)";
  const label = v < 15 ? "Cheap" : v < 25 ? "Fair" : v < 40 ? "Stretched" : "Expensive";
  return (
    <span title={`${peType==="trailing"?"Trailing":"Forward"} P/E: ${v.toFixed(1)}x`} style={{
      display:"inline-flex",alignItems:"center",gap:4,
      background:bg, border:`1px solid ${color}44`,
      borderRadius:20, padding:"2px 10px", fontSize:11, fontWeight:700, color}}>
      {v.toFixed(1)}x
      <span style={{fontSize:9,color,opacity:0.8}}>{label}</span>
    </span>
  );
}

// PEG pill — Price/Earnings-to-Growth ratio (forward P/E ÷ growth %).
// Lynch interpretation: <1 undervalued, ~1 fair, >2 expensive, >3 overvalued.
// Negative-growth or extreme-outlier (>5) readings render as N/A — the backend
// also skips score adjustments for those (see _peg_adj in api.py).
function PegPill({ peg }) {
  const valid = peg != null && !Number.isNaN(parseFloat(peg))
                && parseFloat(peg) > 0 && parseFloat(peg) < 5;
  if (!valid) return (
    <span style={{display:"inline-flex",alignItems:"center",gap:4,
      background:"rgba(255,255,255,0.05)",border:"1px solid rgba(255,255,255,0.1)",
      borderRadius:20,padding:"2px 10px",fontSize:11,fontWeight:700,color:C.muted}}>
      N/A
    </span>
  );
  const v = parseFloat(peg);
  // Colour bands match the _peg_adj signal mapping in api.py:
  //  <1.0 green (under), 1.0-1.5 yellow (fair), 1.5-2.0 orange (mild expensive),
  //  >2.0 red (expensive / overvalued).
  const color = v < 1.0 ? C.green : v < 1.5 ? "#ffca28"
              : v < 2.0 ? "#ff7043" : "#ff5252";
  const bg    = v < 1.0 ? "rgba(0,230,118,0.1)" : v < 1.5 ? "rgba(255,202,40,0.1)"
              : v < 2.0 ? "rgba(255,112,67,0.1)" : "rgba(255,82,82,0.1)";
  const label = v < 1.0 ? "Under" : v < 1.5 ? "Fair"
              : v < 2.0 ? "Pricey" : "Overvalued";
  return (
    <span title={`PEG ratio: ${v.toFixed(2)}x — ${label}`} style={{
      display:"inline-flex",alignItems:"center",gap:4,
      background:bg, border:`1px solid ${color}44`,
      borderRadius:20, padding:"2px 10px", fontSize:11, fontWeight:700, color}}>
      {v.toFixed(2)}
      <span style={{fontSize:9,color,opacity:0.8}}>{label}</span>
    </span>
  );
}

// Dividend Yield pill — only renders if yield > 0
// divYield from yfinance is a decimal fraction (0.0044 = 0.44%).
// Guard against stale/corrupt cached values that arrive as large decimals (e.g. 0.38 stored
// when data was mis-populated) by capping at 25% — nothing realistic exceeds that.
function DivYieldPill({ divYield, divYieldPct }) {
  // Prefer Polygon's authoritative `divYieldPct` field (post-v8.3); fall back
  // to legacy `divYield` decimal-fraction shape for older cached responses.
  let pct = null;
  if (typeof divYieldPct === "number" && divYieldPct > 0) {
    pct = divYieldPct;
  } else if (divYield && divYield > 0) {
    pct = parseFloat(divYield) * 100;
  }
  if (!pct || pct <= 0) return null;
  if (pct > 25) return null; // data sanity cap (very few legit yields exceed this; mostly leftover yfinance bugs)
  const color = pct >= 4 ? C.green : pct >= 2 ? "#ffca28" : C.accent;
  const bg    = pct >= 4 ? "rgba(0,230,118,0.1)" : pct >= 2 ? "rgba(255,202,40,0.1)" : "rgba(0,212,255,0.1)";
  const border= pct >= 4 ? "rgba(0,230,118,0.3)"  : pct >= 2 ? "rgba(255,202,40,0.3)"  : "rgba(0,212,255,0.3)";
  return (
    <span style={{display:"inline-flex",alignItems:"center",gap:4,
      background:bg,border:`1px solid ${border}`,
      borderRadius:20,padding:"2px 10px",fontSize:11,fontWeight:700,color}}>
      {pct.toFixed(2)}%
    </span>
  );
}

// FCF/share pill — trailing 12-month free cash flow per share
function FcfPerSharePill({ freeCashFlow, sharesOutstanding }) {
  if (freeCashFlow == null || !sharesOutstanding) return null;
  const fcfPs = parseFloat(freeCashFlow) / parseFloat(sharesOutstanding);
  if (isNaN(fcfPs)) return null;
  const color = fcfPs >= 0 ? "#00e676" : "#ff5252";
  const bg    = fcfPs >= 0 ? "rgba(0,230,118,0.1)" : "rgba(255,82,82,0.1)";
  const border= fcfPs >= 0 ? "rgba(0,230,118,0.3)" : "rgba(255,82,82,0.3)";
  return (
    <span title="Trailing 12-month free cash flow per share" style={{
      display:"inline-flex", alignItems:"center", gap:4,
      background:bg, border:`1px solid ${border}`,
      borderRadius:20, padding:"2px 10px", fontSize:11, fontWeight:700, color,
      fontFamily:"monospace",
    }}>
      FCF ${Math.abs(fcfPs) >= 1 ? fcfPs.toFixed(2) : fcfPs.toFixed(3)}
    </span>
  );
}

// Debt/Equity pill — long-term debt / stockholders equity
function DebtEquityPill({ debtToEquity }) {
  if (debtToEquity == null) return null;
  const v = parseFloat(debtToEquity);
  if (isNaN(v) || v < 0) return null;
  const color = v > 2.0 ? "#ff5252" : v > 1.0 ? "#ffca28" : "rgba(255,255,255,0.5)";
  const bg    = v > 2.0 ? "rgba(255,82,82,0.1)" : v > 1.0 ? "rgba(255,202,40,0.1)" : "rgba(255,255,255,0.05)";
  const border= v > 2.0 ? "rgba(255,82,82,0.3)" : v > 1.0 ? "rgba(255,202,40,0.3)" : "rgba(255,255,255,0.1)";
  return (
    <span title="Long-term debt / stockholders equity" style={{
      display:"inline-flex", alignItems:"center", gap:4,
      background:bg, border:`1px solid ${border}`,
      borderRadius:20, padding:"2px 10px", fontSize:11, fontWeight:700, color,
      fontFamily:"monospace",
    }}>
      D/E {v.toFixed(1)}
    </span>
  );
}

// Current Ratio pill — current assets / current liabilities
function CurrentRatioPill({ currentRatio }) {
  if (currentRatio == null) return null;
  const v = parseFloat(currentRatio);
  if (isNaN(v) || v < 0) return null;
  const color = v < 1.0 ? "#ff5252" : v > 2.0 ? "#00e676" : "rgba(255,255,255,0.5)";
  const bg    = v < 1.0 ? "rgba(255,82,82,0.1)" : v > 2.0 ? "rgba(0,230,118,0.1)" : "rgba(255,255,255,0.05)";
  const border= v < 1.0 ? "rgba(255,82,82,0.3)" : v > 2.0 ? "rgba(0,230,118,0.3)" : "rgba(255,255,255,0.1)";
  return (
    <span title="Current assets / current liabilities — liquidity indicator" style={{
      display:"inline-flex", alignItems:"center", gap:4,
      background:bg, border:`1px solid ${border}`,
      borderRadius:20, padding:"2px 10px", fontSize:11, fontWeight:700, color,
      fontFamily:"monospace",
    }}>
      CR {v.toFixed(1)}
    </span>
  );
}

// Format helpers for the Fundamentals card
const _fmtPrice = (n) =>
  (n == null || isNaN(n)) ? "—" : `$${Number(n).toFixed(2)}`;
const _fmtMarketCap = (n) => {
  if (n == null || isNaN(n)) return "—";
  const abs = Math.abs(n);
  if (abs >= 1e12) return `$${(n / 1e12).toFixed(2)}T`;
  if (abs >= 1e9)  return `$${(n / 1e9 ).toFixed(2)}B`;
  if (abs >= 1e6)  return `$${(n / 1e6 ).toFixed(1)}M`;
  return `$${Math.round(n).toLocaleString()}`;
};
const _fmtVolume = (n) => {
  if (n == null || isNaN(n)) return "—";
  const abs = Math.abs(n);
  if (abs >= 1e9) return `${(n / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `${(n / 1e3).toFixed(0)}K`;
  return Math.round(n).toLocaleString();
};

// Compact, collapsible block of static company fundamentals.
// Sits below Key Indicators on the stock detail page. Values come from
// get_fundamentals (.info) — no extra network hit.
// ─── AI: Why is this stock moving today? ───────────────────────────────────
// Stage A of the AI agent roadmap (v8.3). Single-shot Gemini synthesis of
// news + earnings proximity + insider flow + regime + analyst consensus
// into a 3-paragraph thesis. Fires on user request (button click) so cost
// is bounded; backend caches 30 min per ticker.
function WhyMovingCard({ token, ticker }) {
  const [thesis,   setThesis]   = React.useState(null);
  const [ctx,      setCtx]      = React.useState(null);
  const [sources,  setSources]  = React.useState([]);
  const [provider, setProvider] = React.useState(null);
  const [loading,  setLoading]  = React.useState(false);
  const [err,      setErr]      = React.useState("");
  const [open,     setOpen]     = React.useState(false);

  // Reset when user changes ticker
  React.useEffect(() => {
    setThesis(null); setCtx(null); setSources([]); setProvider(null);
    setErr(""); setOpen(false);
  }, [ticker]);

  const fetchThesis = async () => {
    if (!token || !ticker) return;
    setLoading(true); setErr("");
    try {
      // v8.3.15: one silent auto-retry on transient failures
      const d = await _aiAgentFetch(
        () => apiFetch(`/ai/why-moving?ticker=${ticker}`, token, { method: "POST" })
      );
      setThesis(d.thesis || "");
      setCtx(d.context || null);
      setSources(d.sources || []);
      setProvider(d.provider || null);
      setOpen(true);
    } catch (e) {
      setErr(e.message || "AI agent unavailable");
    } finally {
      setLoading(false);
    }
  };

  // Paragraph split: backend asked for 3 paragraphs separated by blank lines
  const paras = thesis ? thesis.split(/\n\s*\n/).filter(p => p.trim()) : [];

  return (
    <Card>
      {/* Shared AI header/button styling (v8.3.15) */}
      <AIFeatureHeader
        title={`Why is ${ticker} moving today?`}
        subtitle={thesis ? null : "News + earnings + insider flow + regime + analyst"}
        action={
          !thesis && !loading ? (
            <AIActionButton onClick={fetchThesis} loading={loading}>
              Generate
            </AIActionButton>
          ) : thesis ? (
            <AIActionButton onClick={() => setOpen(o => !o)} variant="secondary">
              {open ? "Hide" : "Show"}
            </AIActionButton>
          ) : null
        }
      />

      {loading && (
        <div style={{ color: C.muted, fontSize: 12, padding: "8px 0", textAlign: "center" }}>
          Assembling news, earnings, insider flow, regime context… (5-15s)
        </div>
      )}
      <AgentErrorBox message={err} onRetry={fetchThesis}/>

      {thesis && open && (
        <>
          {paras.map((p, i) => (
            <p key={i} style={{
              color: i === 0 ? C.text : C.muted,
              fontSize: 13, lineHeight: 1.55, margin: "0 0 10px 0",
              whiteSpace: "pre-wrap",
            }}>{p}</p>
          ))}

          {/* Context summary chips */}
          {ctx && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8,
                          paddingTop: 10, borderTop: `1px solid ${C.border}` }}>
              {ctx.headlineCount > 0 && (
                <span style={chipStyle}>📰 {ctx.headlineCount} news</span>
              )}
              {ctx.daysUntilEarnings != null && ctx.daysUntilEarnings <= 14 && (
                <span style={{ ...chipStyle, color: "#ffca28",
                               borderColor: "rgba(255,202,40,0.3)",
                               display:"inline-flex", alignItems:"center", gap:4 }}>
                  <EarningsGlyph size={11} color="#ffca28"/> Earnings in {ctx.daysUntilEarnings}d
                </span>
              )}
              {ctx.insiderBuys > 0 && (
                <span style={{ ...chipStyle, color: C.green,
                               borderColor: "rgba(0,230,118,0.3)" }}>
                  💼 {ctx.insiderBuys} insider buy{ctx.insiderBuys > 1 ? "s" : ""}
                </span>
              )}
              {ctx.shortVolZscore != null && ctx.shortVolZscore >= 1.0 && (
                <span style={chipStyle}>🌑 dark short z={ctx.shortVolZscore}</span>
              )}
              {ctx.regimeLabel && (
                <span style={chipStyle}>🌐 {ctx.regimeLabel}</span>
              )}
            </div>
          )}

          {sources.length > 0 && (
            <div style={{ marginTop: 10, fontSize: 11, color: C.muted }}>
              <strong style={{ color: C.text }}>Sources:</strong>{" "}
              {sources.slice(0, 4).map((s, i) => (
                <a key={i} href={s.url} target="_blank" rel="noopener noreferrer"
                  style={{ color: C.accent, marginRight: 8 }}>
                  {(s.title || s.url).slice(0, 40)}
                </a>
              ))}
            </div>
          )}

          <div style={{ marginTop: 10, fontSize: 10, color: "#666", textAlign: "right" }}>
            {provider === "gemini" ? "Powered by Gemini" : provider === "groq" ? "Powered by Groq" : ""}
            {" · Cached 30 min · Not financial advice"}
          </div>
        </>
      )}
    </Card>
  );
}

// Shared chip style for context badges
const chipStyle = {
  fontSize: 10, padding: "3px 8px", borderRadius: 10,
  background: "rgba(0,212,255,0.10)", color: C.accent,
  border: `1px solid rgba(0,212,255,0.30)`,
};

// Shared AI feature button — used by AI Trade Explainer, Why-Moving,
// Buy-Today, Tweet Generator, Options Rationale. v8.3.15 unification:
// previously each feature had its own button styling (some full-width
// cyan box, some pill, some text-only). User reported inconsistent
// look-and-feel. This component is the single source of truth for
// AI action button styling.
function AIActionButton({ onClick, disabled, loading, children,
                          fullWidth = false, variant = "primary" }) {
  const isPrimary = variant === "primary";
  return (
    <button onClick={onClick} disabled={disabled || loading}
      style={{
        width:      fullWidth ? "100%" : "auto",
        padding:    fullWidth ? "10px 14px" : "6px 14px",
        background: isPrimary ? C.accent : "transparent",
        color:      isPrimary ? "#000"   : C.accent,
        border:     isPrimary ? "none"   : `1px solid ${C.accent}`,
        borderRadius: 10,
        fontSize:   fullWidth ? 13 : 12,
        fontWeight: 700,
        fontFamily: "inherit",
        cursor:     (disabled || loading) ? "default" : "pointer",
        opacity:    (disabled || loading) ? 0.6 : 1,
        whiteSpace: "nowrap",
        transition: "opacity 0.15s",
        display:    "inline-flex",
        alignItems: "center",
        gap:        6,
      }}>
      {loading && (
        <span style={{
          width:12, height:12, borderRadius:"50%",
          border:`2px solid ${isPrimary ? "#000" : C.accent}`,
          borderTopColor:"transparent",
          animation:"spin 0.8s linear infinite",
          flexShrink:0,
        }}/>
      )}
      {children}
    </button>
  );
}

// Shared AI feature card header — consistent 🤖 + title + subtitle pattern
// used across all AI cards. Pairs with AIActionButton.
function AIFeatureHeader({ title, subtitle, action }) {
  return (
    <div style={{
      display:"flex", alignItems:"center", justifyContent:"space-between",
      gap:10, marginBottom: action ? 0 : 4,
    }}>
      <div style={{display:"flex", alignItems:"center", gap:10, flex:1, minWidth:0}}>
        <span style={{fontSize:18, flexShrink:0}}>🤖</span>
        <div style={{flex:1, minWidth:0}}>
          <div style={{fontSize:9, fontWeight:700, letterSpacing:"0.12em",
                       color:C.muted, textTransform:"uppercase"}}>
            {title}
          </div>
          {subtitle && (
            <div style={{fontSize:11, color:C.muted, marginTop:2,
                         overflow:"hidden", textOverflow:"ellipsis",
                         whiteSpace:"nowrap"}}>
              {subtitle}
            </div>
          )}
        </div>
        {action && <div style={{flexShrink:0}}>{action}</div>}
      </div>
    </div>
  );
}

// Auto-retry wrapper for AI agent fetches. v8.3.15: the user reported that
// hitting "Retry" on a failed agent usually succeeds — the first call
// timed out on a cold cache or transient network blip. Wrap the call in
// one silent auto-retry so the user doesn't have to click Retry manually.
//   maxAttempts: how many total attempts (1 = no retry, 2 = one retry)
//   delayMs:     wait between attempts (defaults to 600ms — enough for
//                a transient blip to clear but fast enough to feel seamless)
// Returns the successful result, or throws the LAST attempt's error.
async function _aiAgentFetch(fn, { maxAttempts = 2, delayMs = 600 } = {}) {
  let lastErr = null;
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await fn();
    } catch (e) {
      lastErr = e;
      // Don't auto-retry on auth errors — the global handler will boot to login
      if (e?.name === "AuthError") throw e;
      // Don't retry on validation errors (400-class). Only network/5xx.
      const msg = String(e?.message || "");
      if (msg.includes("Request failed (4")) throw e;
      if (attempt < maxAttempts) {
        await new Promise(r => setTimeout(r, delayMs));
      }
    }
  }
  throw lastErr;
}

// Visible, copy-friendly error block for AI agent cards. Old display was a
// tiny red line that scrolled off-screen and made errors easy to miss.
// Shows the full backend error message + a Copy button so users can paste
// the exact failure (which after v8.3.10 includes the failing step name
// and exception type) back to support.
function AgentErrorBox({ message, onRetry }) {
  const [copied, setCopied] = React.useState(false);
  if (!message) return null;
  const copy = () => {
    try {
      navigator.clipboard.writeText(String(message));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_) { /* clipboard API not available */ }
  };
  return (
    <div style={{
      background: "rgba(255,82,82,0.08)", border: "1px solid rgba(255,82,82,0.3)",
      borderRadius: 8, padding: "10px 12px", marginTop: 8,
    }}>
      <div style={{display:"flex", justifyContent:"space-between",
                   alignItems:"flex-start", gap:8, marginBottom: 6}}>
        <div style={{fontSize:11, fontWeight:700, color:"#ff6b6b",
                     letterSpacing:"0.04em", textTransform:"uppercase"}}>
          ⚠ Agent failed
        </div>
        <div style={{display:"flex", gap:6, flexShrink:0}}>
          <button onClick={copy}
            style={{padding:"2px 8px", fontSize:10, background:"transparent",
                    color:"#ff6b6b", border:"1px solid rgba(255,82,82,0.4)",
                    borderRadius:6, cursor:"pointer", fontFamily:"inherit"}}>
            {copied ? "Copied" : "Copy"}
          </button>
          {onRetry && (
            <button onClick={onRetry}
              style={{padding:"2px 8px", fontSize:10, background:"transparent",
                      color:C.muted, border:`1px solid ${C.border}`,
                      borderRadius:6, cursor:"pointer", fontFamily:"inherit"}}>
              Retry
            </button>
          )}
        </div>
      </div>
      <div style={{fontSize:11, color:"rgba(255,107,107,0.9)", lineHeight:1.5,
                   fontFamily:"monospace", wordBreak:"break-word",
                   whiteSpace:"pre-wrap", maxHeight:140, overflowY:"auto"}}>
        {String(message)}
      </div>
    </div>
  );
}


// ─── AI Agent: What should I buy today? ────────────────────────────────────
// Stage B multi-step research agent (project_ai_agent_roadmap.md). Backend:
//   Step 1: gather candidates from watchlist + universe top + trending
//   Step 2: profile-gated filter (swing/lt thresholds, beta cap, analyst cov)
//   Step 3: enrich top-6 with news + earnings + insider Form-4
//   Step 4: single Gemini synthesis call → 3 picks (swing / LT / blended)
// Cached 2h per (user, horizon, profile).
function BuyTodayAgentCard({ token, horizon, profile, onSelectTicker }) {
  const [data,      setData]      = React.useState(null);
  const [loading,   setLoading]   = React.useState(false);
  const [err,       setErr]       = React.useState("");
  const [showInfo,  setShowInfo]  = React.useState(false);
  const [collapsed, setCollapsed] = React.useState(
    () => localStorage.getItem("gempulse_buy_agent_collapsed") === "1"
  );
  // v8.4.310: optional "focus today" — biases the picks toward a theme/sector/setup
  // in the SELECTION only (never the scores). Empty = no focus (default behavior).
  const [focus, setFocus] = React.useState("");
  const FOCUS_OPTIONS = [
    "AI & Chips","Energy","Healthcare","Financials","Dividends",
    "Oversold bounces","High momentum","Value",
  ];

  const toggleCollapsed = () => {
    setCollapsed(prev => {
      const next = !prev;
      localStorage.setItem("gempulse_buy_agent_collapsed", next ? "1" : "0");
      return next;
    });
  };

  const fetchPicks = async () => {
    if (!token) return;
    setLoading(true); setErr(""); setData(null);
    try {
      // v8.4.388: async submit→poll (was a synchronous POST via _aiAgentFetch with 3
      // retries). Buy-today does heavy cold-compute (gather + filter + enrich + a
      // 1600-tok LLM writer + per-pick fact-checks) that could exceed Cloudflare's
      // ~100s edge timeout — the old retries were papering over that. Same submit→poll
      // pattern as the tweet gen / briefing: the POST returns a request_id instantly
      // and each poll is fast, so the edge timeout can't fire. The done payload nests
      // the buy-today response under `.data` (its own `status` field would otherwise
      // collide with the poll state). A warm 2h cache resolves on the first poll.
      const qs = `horizon=${horizon || "6m"}&profile=${profile || "moderate"}`
        + (focus ? `&focus=${encodeURIComponent(focus)}` : "");
      const sub = await apiFetch(`/ai/buy-today-async?${qs}`, token, { method: "POST" });
      const reqId = sub?.request_id;
      if (!reqId) throw new Error("no request_id returned");
      let finalInfo = null;
      const start = Date.now();
      while (Date.now() - start < 180000) {   // poll up to 3 min; first poll immediate
        const info = await apiFetch(`/ai/buy-today-async/${reqId}`, token);
        if (info?.status === "done")  { finalInfo = info; break; }
        if (info?.status === "error") { finalInfo = info; break; }
        await new Promise(r => setTimeout(r, 3000));
      }
      if (!finalInfo) throw new Error("timed out after 3 min");
      if (finalInfo.status === "error") throw new Error(finalInfo.error || "AI agent unavailable");
      setData(finalInfo.data);
    } catch (e) {
      setErr(e.message || "AI agent unavailable");
    } finally {
      setLoading(false);
    }
  };

  const PICK_META = {
    swing:      { label: "Swing trade",   color: C.accent, Icon: SwingIcon },
    long_term:  { label: "Long-term",     color: C.green,  Icon: LTIcon },
    blended:    { label: "Blended pick",  color: "#7b61ff", Icon: null },
  };

  return (
    <>
      {/* Info modal — matches ProfileDivergenceCard pattern */}
      {showInfo && (
        <div onClick={() => setShowInfo(false)}
          style={{
            position:"fixed", inset:0, zIndex:1000,
            background:"rgba(0,0,0,0.72)",
            display:"flex", alignItems:"center", justifyContent:"center",
            padding:"0 20px", boxSizing:"border-box",
          }}>
          <div onClick={e => e.stopPropagation()}
            style={{
              background:C.surface, border:`1px solid ${C.border}`,
              borderRadius:16, padding:"22px 20px",
              width:"100%", maxWidth:400, boxSizing:"border-box",
            }}>
            <div style={{display:"flex", justifyContent:"space-between",
                         alignItems:"center", marginBottom:14}}>
              <div style={{display:"flex", alignItems:"center", gap:8}}>
                <span style={{fontSize:14}}>🤖</span>
                <span style={{fontWeight:700, fontSize:10, color:C.muted,
                              letterSpacing:"0.1em", textTransform:"uppercase"}}>
                  Daily Picks Agent
                </span>
              </div>
              <button onClick={() => setShowInfo(false)}
                style={{background:"none", border:"none", color:C.muted,
                        fontSize:16, cursor:"pointer", padding:"0 2px", lineHeight:1}}
              >✕</button>
            </div>
            <p style={{fontSize:13, color:"rgba(255,255,255,0.75)",
                       lineHeight:1.65, margin:"0 0 12px"}}>
              A four-step research agent that recommends three stocks tuned to your
              risk profile — the best <strong style={{color:C.accent}}>swing</strong>,
              {" "}<strong style={{color:C.green}}>long-term hold</strong>, and a
              {" "}<strong style={{color:"#7b61ff"}}>blended</strong> pick.
            </p>
            <div style={{fontSize:12, color:"rgba(255,255,255,0.65)", lineHeight:1.75,
                         paddingLeft:12, borderLeft:`2px solid rgba(0,212,255,0.25)`,
                         marginBottom:12}}>
              <div><strong style={{color:C.text}}>1. Gather</strong> — your watchlist + universe top scorers + StockTwits trending</div>
              <div><strong style={{color:C.text}}>2. Filter</strong> — apply profile thresholds (swing/LT score floors, beta cap, analyst coverage)</div>
              <div><strong style={{color:C.text}}>3. Enrich</strong> — pull Polygon news, earnings proximity, insider Form 4 for top 6</div>
              <div><strong style={{color:C.text}}>4. Synthesise</strong> — Gemini compares them and writes the recommendation</div>
            </div>
            <p style={{fontSize:11, color:C.muted, lineHeight:1.6, margin:"0"}}>
              Cached 2 hours per profile + horizon. Tap any pick to open its full analysis.
              Not financial advice — for research only.
            </p>
          </div>
        </div>
      )}

      <div style={{background:C.surface, borderRadius:14, padding:"12px 14px",
                   border:`1px solid ${C.border}`, marginBottom:14}}>
        {/* Header — matches ProfileDivergenceCard */}
        <div style={{display:"flex", justifyContent:"space-between",
                     alignItems:"center", marginBottom: collapsed ? 0 : 10}}>
          <div style={{display:"flex", alignItems:"center", gap:6}}>
            <span style={{fontSize:11}}>🤖</span>
            <span style={{fontSize:10, fontWeight:700, color:C.muted,
                          letterSpacing:"0.1em", textTransform:"uppercase"}}>
              What should I buy today?
            </span>
            <button onClick={e => { e.stopPropagation(); setShowInfo(true); }}
              style={{
                width:14, height:14, borderRadius:"50%",
                border:"1px solid rgba(255,255,255,0.2)",
                background:"rgba(255,255,255,0.06)", color:"rgba(255,255,255,0.5)",
                fontSize:9, fontWeight:700, cursor:"pointer", fontFamily:"inherit",
                display:"inline-flex", alignItems:"center", justifyContent:"center",
                flexShrink:0, lineHeight:1, padding:0,
              }}>i</button>
          </div>
          <div style={{display:"flex", alignItems:"center", gap:8}}>
            {data?.profile && !collapsed && (
              <span style={{fontSize:9, color:C.muted, fontFamily:"inherit"}}>
                {data.profile.toUpperCase()} · {(data.horizon || horizon || "6m").toUpperCase()}
              </span>
            )}
            {/* v8.4.311: back to the focus selector to re-run with different criteria */}
            {data && !collapsed && (
              <button onClick={()=>{ setData(null); setErr(""); }}
                title="Change focus and re-run"
                style={{background:"none", border:`1px solid ${C.border}`, borderRadius:6,
                        padding:"2px 8px", color:C.accent, fontSize:10, cursor:"pointer",
                        fontFamily:"inherit", lineHeight:1.4}}>
                ↺ Reselect
              </button>
            )}
            <button onClick={toggleCollapsed}
              style={{background:"none", border:`1px solid ${C.border}`, borderRadius:6,
                      padding:"2px 8px", color:C.muted, fontSize:10, cursor:"pointer",
                      fontFamily:"inherit", lineHeight:1.4}}>
              {collapsed ? "Show" : "Hide"}
            </button>
          </div>
        </div>

        {/* Collapsed summary row — shows pick count so user knows results are ready */}
        {collapsed && data?.status === "ok" && data.picks?.length > 0 && (
          <div style={{fontSize:11, color:C.muted, paddingTop:6}}>
            {data.picks.length} pick{data.picks.length !== 1 ? "s" : ""} ready ·{" "}
            {data.picks.map(p => p.ticker).join(", ")} ·{" "}
            <span style={{color:C.accent, cursor:"pointer"}} onClick={toggleCollapsed}>Show</span>
          </div>
        )}

        {!collapsed && !data && !loading && !err && (
          <div style={{padding:"8px 0"}}>
            <p style={{fontSize:12, color:C.muted, lineHeight:1.6, margin:"0 0 12px"}}>
              A four-step research agent that scans your watchlist, top universe
              scorers, and trending names — then picks the best swing trade,
              long-term hold, and blended pick for your <strong style={{color:C.accent}}>
              {profile || "moderate"}</strong> profile.
            </p>
            {/* v8.4.310: optional "focus today" chips — bias the picks toward a
                theme/sector/setup. Tap to select, tap again to clear. */}
            <div style={{fontSize:10,color:C.muted,fontWeight:700,letterSpacing:"0.06em",
              textTransform:"uppercase",marginBottom:6}}>Focus today (optional)</div>
            <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:12}}>
              {FOCUS_OPTIONS.map(opt=>(
                <button key={opt} onClick={()=>setFocus(f=>f===opt?"":opt)} style={{
                  padding:"4px 10px",borderRadius:20,fontSize:11,fontWeight:600,
                  border:`1px solid ${focus===opt?C.accent:C.border}`,
                  background:focus===opt?"rgba(0,212,255,0.12)":"none",
                  color:focus===opt?C.accent:C.muted,cursor:"pointer",
                  fontFamily:"inherit",transition:"all 0.15s"}}>{opt}</button>
              ))}
            </div>
            <button onClick={fetchPicks}
              style={{width:"100%", padding:"10px 14px", background:C.accent,
                      color:"#000", border:"none", borderRadius:10, fontSize:13,
                      fontWeight:700, cursor:"pointer"}}>
              {focus ? `Run agent · ${focus}` : "Run agent"}
            </button>
          </div>
        )}

        {!collapsed && loading && (
          <div style={{padding:"12px 0", color:C.muted, fontSize:13, textAlign:"center"}}>
            <div style={{marginBottom:6}}>Researching candidates…</div>
            <div style={{fontSize:11, color:"rgba(255,255,255,0.4)"}}>
              Gather → filter → enrich → synthesise · 10-20s
            </div>
          </div>
        )}

        {!collapsed && <AgentErrorBox message={err} onRetry={fetchPicks}/>}

        {!collapsed && data && data.status !== "ok" && (
          <div style={{padding:"10px 0"}}>
            <div style={{color:C.muted, fontSize:12, lineHeight:1.6, marginBottom:10}}>
              {data.message || "No picks available yet."}
            </div>
            <button onClick={fetchPicks}
              style={{padding:"6px 12px", background:"none",
                      color:C.muted, border:`1px solid ${C.border}`,
                      borderRadius:8, fontSize:11, cursor:"pointer"}}>
              Retry
            </button>
          </div>
        )}

        {!collapsed && data?.status === "ok" && data.picks && data.picks.length > 0 && (
          <>
            {data.picks.map((p, i) => {
              const meta = PICK_META[p.type] || PICK_META.blended;
              return (
                <div key={`${p.ticker}-${i}`}
                  onClick={() => onSelectTicker && onSelectTicker(p.ticker)}
                  style={{
                    padding:"10px 0", cursor:"pointer",
                    borderTop: i === 0 ? "none" : `1px solid ${C.border}`,
                  }}>
                  {/* Row 1: tag pill + ticker + name + score */}
                  <div style={{display:"flex", alignItems:"center", gap:10, marginBottom:6}}>
                    <span style={{
                      display:"inline-flex", alignItems:"center", gap:4,
                      fontSize:9, fontWeight:700, padding:"3px 8px",
                      borderRadius:10, color:meta.color,
                      background:`${meta.color}15`,
                      border:`1px solid ${meta.color}40`,
                      letterSpacing:"0.06em", textTransform:"uppercase",
                      flexShrink:0,
                    }}>
                      {meta.Icon && <meta.Icon size={10} color={meta.color}/>}
                      {meta.label}
                    </span>
                    <TickerLogo ticker={p.ticker} size={22}/>
                    <div style={{flex:1, minWidth:0}}>
                      <div style={{fontFamily:"monospace", fontWeight:800,
                                   fontSize:14, color:C.text}}>{p.ticker}</div>
                      <div style={{fontSize:11, color:C.muted,
                                   whiteSpace:"nowrap", overflow:"hidden",
                                   textOverflow:"ellipsis"}}>
                        {p.shortName}{p.sector ? ` · ${p.sector}` : ""}
                      </div>
                    </div>
                    <div style={{textAlign:"right", fontSize:11, flexShrink:0}}>
                      {p.price != null && (
                        <div style={{fontFamily:"monospace", color:C.text, fontSize:12}}>
                          ${Number(p.price).toFixed(2)}
                        </div>
                      )}
                      {p.changePct != null && (
                        <div style={{color: p.changePct >= 0 ? C.green : C.red,
                                     fontFamily:"monospace"}}>
                          {p.changePct >= 0 ? "+" : ""}{Number(p.changePct).toFixed(2)}%
                        </div>
                      )}
                    </div>
                  </div>
                  {/* Row 2: reasoning */}
                  {p.reasoning && (
                    <div style={{fontSize:12, color:"rgba(255,255,255,0.78)",
                                 lineHeight:1.6, marginLeft:0}}>
                      {p.reasoning}
                    </div>
                  )}
                  {/* Row 3: score chips */}
                  <div style={{display:"flex", gap:6, marginTop:6, flexWrap:"wrap"}}>
                    {p.swingScore != null && (
                      <span style={{...chipStyle, color:C.accent,
                                    background:"rgba(0,212,255,0.10)"}}>
                        <SwingIcon size={9} color={C.accent}/> Swing {Math.round(p.swingScore)}
                      </span>
                    )}
                    {p.ltScore != null && (
                      <span style={{...chipStyle, color:C.green,
                                    background:"rgba(0,230,118,0.10)",
                                    border:`1px solid rgba(0,230,118,0.30)`}}>
                        <LTIcon size={9} color={C.green}/> LT {Math.round(p.ltScore)}
                      </span>
                    )}
                    {p.beta != null && (
                      <span style={chipStyle}>β {Number(p.beta).toFixed(2)}</span>
                    )}
                    {p.itLabel && (() => {
                      const _ic = {green:"#00e676",yellow:"#ffca28",red:"#ff5252"}[p.itColor]||C.muted;
                      return (
                        <span style={{...chipStyle, color:_ic, background:`${_ic}12`,
                                      border:`1px solid ${_ic}30`,
                                      display:"flex", alignItems:"center", gap:3}}>
                          <span style={{width:5,height:5,borderRadius:"50%",
                                        background:_ic,flexShrink:0}}/>
                          {p.itLabel}
                        </span>
                      );
                    })()}
                  </div>
                </div>
              );
            })}
            <div style={{display:"flex", justifyContent:"space-between",
                         alignItems:"center", marginTop:10, paddingTop:10,
                         borderTop:`1px solid ${C.border}`}}>
              <div style={{fontSize:10, color:C.muted}}>
                Scanned {data.pipeline?.candidates_gathered || 0} ·
                qualified {data.pipeline?.qualifying_after_filter || 0}
                {data.regime?.label && ` · regime ${data.regime.label}`}
                {focus && <span> · focus: <span style={{color:C.accent}}>{focus}</span></span>}
              </div>
              <button onClick={fetchPicks}
                style={{background:"none", border:"none", cursor:"pointer",
                        color:C.muted, fontSize:10, padding:"4px 8px"}}>
                ⟳ Refresh
              </button>
            </div>
            {/* v8.4.77: score-staleness footnote — warns that cached scores
                may differ from the live /analyze view on the detail page. */}
            {(() => {
              const _prov = data.provider === "gemini" ? "Gemini"
                          : data.provider === "groq"   ? "Groq" : null;
              // scoresTs is Unix seconds (oldest pick's scoredAt from Celery)
              const _sts = data.scoresTs;
              let _ageStr = null;
              if (_sts) {
                const _ageSec = Math.floor(Date.now() / 1000) - _sts;
                if (_ageSec >= 0) {
                  if (_ageSec < 60)        _ageStr = "< 1 min ago";
                  else if (_ageSec < 3600) _ageStr = `${Math.round(_ageSec / 60)} min ago`;
                  else                     _ageStr = `${Math.round(_ageSec / 3600)}h ago`;
                }
              }
              return (
                <div style={{fontSize:10, color:"#555", marginTop:4, lineHeight:1.6}}>
                  <div style={{textAlign:"right"}}>
                    {_prov && <span>{`Powered by ${_prov}`} · </span>}
                    <span>Picks cached 2h · Not financial advice</span>
                  </div>
                  {_ageStr && (
                    <div style={{textAlign:"right", color:"#4a4a5a", marginTop:1}}>
                      Scores from universe cache{" "}
                      <span style={{fontStyle:"italic"}}>{_ageStr}</span>
                      {" — tap a pick for live score"}
                    </div>
                  )}
                </div>
              );
            })()}
          </>
        )}
      </div>
    </>
  );
}


// ETF top-holdings — collapsed card for ETFs/funds (backend gates data.etfHoldings). Shows
// holdings when yfinance has them; for a CONFIRMED fund with no holdings (isFund + empty),
// offers an "Ask AI" button that fetches + persists a dated snapshot. v8.4.247/254.
function EtfHoldingsCard({ data, token }) {
  const [open, setOpen]   = React.useState(false);   // collapsed by default
  const [aiData, setAiData] = React.useState(null);  // freshly-fetched AI snapshot
  const [asking, setAsking] = React.useState(false);
  const [askErr, setAskErr] = React.useState("");

  const eh = aiData || data?.etfHoldings;
  const holdings = Array.isArray(eh?.holdings) ? eh.holdings : [];
  const isFund   = !!eh?.isFund;
  // Render only for funds — either we have holdings, or it's a confirmed fund we can Ask-AI.
  if (!holdings.length && !isFund) return null;

  const isAi  = eh?.source === "ai";
  const asOf  = eh?.asOf;

  const askAI = async () => {
    if (!token || asking) return;
    setAsking(true); setAskErr("");
    try {
      const r = await apiFetch(`/etf/holdings-ai/${data?.ticker}`, token, { method: "POST" });
      if (r?.holdings?.length) { setAiData(r); setOpen(true); }
      else setAskErr("No reliable holdings found");
    } catch (e) {
      setAskErr(e?.message || "AI lookup failed — try again");
    } finally { setAsking(false); }
  };

  return (
    <div style={{background:C.card, borderRadius:16,
      border:`1px solid ${C.border}`, padding:"12px 16px", marginBottom:12}}>
      <button onClick={() => setOpen(o => !o)} style={{
        display:"flex", alignItems:"center", justifyContent:"space-between",
        width:"100%", background:"none", border:"none", padding:0, cursor:"pointer",
        fontFamily:"inherit"}}>
        <div style={{fontSize:9, fontWeight:700, letterSpacing:"0.12em",
          color:C.muted, textTransform:"uppercase"}}>
          Top Holdings{holdings.length ? <span style={{color:C.text}}> · {holdings.length}</span> : ""}
        </div>
        <span style={{fontSize:11, color:C.muted, lineHeight:1}}>{open ? "▾" : "▸"}</span>
      </button>
      {open && (
        <div style={{marginTop:12}}>
          {holdings.map((h, i) => (
            <div key={i} style={{display:"flex", alignItems:"center", gap:10,
              justifyContent:"space-between",
              borderBottom:`1px dashed ${C.border}`, padding:"6px 0"}}>
              <div style={{display:"flex", alignItems:"center", gap:8, minWidth:0, flex:1}}>
                <span style={{fontSize:12, color:C.text, fontWeight:700,
                  fontFamily:"monospace", minWidth:52, flexShrink:0}}>{h.ticker}</span>
                <span style={{fontSize:11, color:C.muted, overflow:"hidden",
                  textOverflow:"ellipsis", whiteSpace:"nowrap"}}>{h.name}</span>
              </div>
              {h.weight != null && (
                <span style={{fontSize:12, color:C.text, fontWeight:600,
                  fontFamily:"monospace", flexShrink:0}}>{Number(h.weight).toFixed(2)}%</span>
              )}
            </div>
          ))}

          {holdings.length > 0 ? (
            <div style={{fontSize:9, color:C.muted, marginTop:8, lineHeight:1.4, opacity:0.85}}>
              {isAi
                ? `AI-sourced${asOf ? ` · as of ${asOf}` : ""}${eh?.aiSource ? ` · ${eh.aiSource}` : ""} — best-effort, verify before relying on it.`
                : "Top holdings by weight — cached, best-effort; may be delayed or incomplete for newer/niche funds."}
            </div>
          ) : (
            /* Confirmed fund, no yfinance holdings → offer Ask AI */
            <div style={{marginTop:4}}>
              <div style={{fontSize:11, color:C.muted, lineHeight:1.5, marginBottom:8}}>
                Holdings for this fund aren't in our data source. Pull the latest top-10
                with AI (persisted with a date, until better data is available).
              </div>
              <button onClick={askAI} disabled={asking}
                style={{width:"100%", padding:"8px", borderRadius:10, cursor: asking?"default":"pointer",
                  border:`1px solid ${C.accent}55`, background:`${C.accent}14`, color:C.accent,
                  fontFamily:"inherit", fontSize:12, fontWeight:700}}>
                {asking ? "Asking AI…" : "✨ Ask AI for top holdings"}
              </button>
              {askErr && <div style={{fontSize:10, color:C.red, marginTop:6}}>{askErr}</div>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Federal contract awards — collapsed card that renders ONLY when the company
// behind this ticker has recent USAspending.gov contracts (defense / gov-exposed
// names). Catalyst-CONFIRMATION / context, NOT a real-time catalyst — award
// records lag the announcement. Self-fetches /awards/{ticker} (day-cached
// server-side); stays null for the common no-contracts case. v8.4.375.
function FederalContractsCard({ data, token }) {
  const [open, setOpen] = React.useState(false);
  const [aw, setAw]     = React.useState(null);
  const ticker = data?.ticker;
  React.useEffect(() => {
    let alive = true;
    if (!ticker || !token) return;
    (async () => {
      try {
        const r = await apiFetch(`/awards/${ticker}`, token);
        if (alive && r && (r.total_awards || 0) > 0) setAw(r);
      } catch (_) { /* best-effort — silent when unavailable */ }
    })();
    return () => { alive = false; };
  }, [ticker, token]);

  if (!aw || !(aw.total_awards > 0)) return null;
  const awards = Array.isArray(aw.awards) ? aw.awards : [];
  const fmtUsd = (v) => {
    const n = Number(v) || 0;
    if (n >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
    if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
    if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
    return `$${n}`;
  };
  return (
    <div style={{background:C.card, borderRadius:16,
      border:`1px solid ${C.border}`, padding:"12px 16px", marginBottom:12}}>
      <button onClick={() => setOpen(o => !o)} style={{
        display:"flex", alignItems:"center", justifyContent:"space-between",
        width:"100%", background:"none", border:"none", padding:0, cursor:"pointer",
        fontFamily:"inherit"}}>
        <div style={{fontSize:9, fontWeight:700, letterSpacing:"0.12em",
          color:C.muted, textTransform:"uppercase"}}>
          🏛️ Federal Contracts<span style={{color:C.text}}> · {aw.total_awards}</span>
          {aw.total_value ? <span style={{color:C.muted}}> · {fmtUsd(aw.total_value)}</span> : ""}
        </div>
        <span style={{fontSize:11, color:C.muted, lineHeight:1}}>{open ? "▾" : "▸"}</span>
      </button>
      {open && (
        <div style={{marginTop:12}}>
          {awards.map((a, i) => (
            <div key={i} style={{borderBottom:`1px dashed ${C.border}`, padding:"7px 0"}}>
              <div style={{display:"flex", justifyContent:"space-between", gap:10,
                alignItems:"baseline"}}>
                <span style={{fontSize:11, color:C.text, fontWeight:700, overflow:"hidden",
                  textOverflow:"ellipsis", whiteSpace:"nowrap", flex:1}}>
                  {a.description || "Federal contract"}</span>
                <span style={{fontSize:12, color:C.green, fontWeight:700,
                  fontFamily:"monospace", flexShrink:0}}>{fmtUsd(a.amount)}</span>
              </div>
              <div style={{fontSize:9, color:C.muted, marginTop:2}}>
                {a.agency}{a.start_date ? ` · ${a.start_date}` : ""}
              </div>
            </div>
          ))}
          <div style={{fontSize:9, color:C.muted, marginTop:8, lineHeight:1.4, opacity:0.85}}>
            {aw.top_agency ? `Top agency: ${aw.top_agency}. ` : ""}
            Source: USAspending.gov — award records lag the announcement; context, not a real-time catalyst.
          </div>
        </div>
      )}
    </div>
  );
}

// ── Insider Activity (SEC Form 4, last 90 days) — self-fetching; renders only when there is any open-market activity ──
function InsiderActivityCard({ data, token }) {
  const [open, setOpen] = React.useState(false);
  const [ia, setIa]     = React.useState(null);
  const ticker = data?.ticker;
  React.useEffect(() => {
    let alive = true;
    if (!ticker || !token) return;
    (async () => {
      try {
        const r = await apiFetch(`/insider/${ticker}?days=90`, token);
        if (alive && r && r.available) setIa(r);
      } catch (_) { /* best-effort — silent when unavailable */ }
    })();
    return () => { alive = false; };
  }, [ticker, token]);

  if (!ia) return null;
  const buys = ia.buys || 0, sells = ia.sells || 0;
  if (!buys && !sells) return null;
  const rows = Array.isArray(ia.open_market) ? ia.open_market : [];
  const plural = (n, w) => `${n} ${w}${n === 1 ? "" : "s"}`;
  return (
    <div style={{background:C.card, borderRadius:16, border:`1px solid ${C.border}`, padding:"12px 16px", marginBottom:12}}>
      <button onClick={() => setOpen(o => !o)} style={{
        display:"flex", alignItems:"center", justifyContent:"space-between", gap:8,
        width:"100%", background:"none", border:"none", padding:0, cursor:"pointer", fontFamily:"inherit", textAlign:"left"}}>
        <div style={{fontSize:9, fontWeight:700, letterSpacing:"0.12em", color:C.muted, textTransform:"uppercase", lineHeight:1.5}}>
          💼 Insider Activity<span style={{color:C.muted}}> · 90d</span>
          <span style={{color:buys ? C.green : C.muted}}> · {plural(buys, "buy")}{buys ? ` ${_usdShort(ia.buy_value)}` : ""}</span>
          <span style={{color:C.muted}}> · {plural(sells, "sale")}{sells ? ` ${_usdShort(ia.sell_value)}` : ""}</span>
          {ia.cluster_buy && <span style={{color:C.green}}> · 👥 buying cluster</span>}
        </div>
        <span style={{fontSize:11, color:C.muted, lineHeight:1, flexShrink:0}}>{open ? "▾" : "▸"}</span>
      </button>
      {open && (
        <div style={{marginTop:12}}>
          {rows.length === 0 && <div style={{fontSize:11, color:C.muted}}>No open-market transactions to list.</div>}
          {rows.map((f, i) => (
            <div key={i} style={{borderBottom:`1px dashed ${C.border}`, padding:"7px 0", display:"flex", justifyContent:"space-between", gap:10}}>
              <div style={{minWidth:0}}>
                <span style={{fontSize:11, fontWeight:700, color:f.code === "P" ? C.green : C.muted}}>
                  {f.code === "P" ? "Bought" : "Sold"}</span>
                <span style={{fontSize:11, color:C.text}}> {f.name || "Insider"}</span>
                {f.title ? <span style={{fontSize:10, color:C.muted}}> · {f.title}</span> : null}
                <div style={{fontSize:9, color:C.muted, marginTop:2}}>
                  {f.date}{f.price ? ` · ${Number(f.shares).toLocaleString("en")} sh @ $${Number(f.price).toFixed(2)}` : ""}
                  {f.plan ? " · pre-planned (10b5-1)" : ""}{f.entity ? " · fund/entity filer" : ""}
                </div>
              </div>
              <div style={{textAlign:"right", flexShrink:0}}>
                <div style={{fontSize:12, fontWeight:700, fontFamily:"monospace", color:f.code === "P" ? C.green : C.muted}}>{_usdShort(f.value)}</div>
                {f.url && <a href={f.url} target="_blank" rel="noopener noreferrer" style={{fontSize:9, color:C.accent, textDecoration:"none"}}>SEC ↗</a>}
              </div>
            </div>
          ))}
          <div style={{fontSize:9, color:C.muted, marginTop:8, lineHeight:1.45, opacity:0.9}}>
            Open-market purchases and sales from public SEC Form 4 filings. Sales are often routine (pre-planned, tax-related or
            diversification), so purchases matter more. In our past-year backtest, clusters of several officers/directors buying in
            mid-size stocks modestly outperformed on average; single purchases and sales showed no reliable signal. Context, not a trade signal.
          </div>
        </div>
      )}
    </div>
  );
}

// ── Ownership filings (SEC EDGAR Schedule 13D / 13G) — self-fetching, renders only when filings exist ──
function OwnershipFilingsCard({ data, token }) {
  const [open, setOpen] = React.useState(false);
  const [fl, setFl]     = React.useState(null);
  const ticker = data?.ticker;
  React.useEffect(() => {
    let alive = true;
    if (!ticker || !token) return;
    (async () => {
      try {
        const r = await apiFetch(`/filings/${ticker}`, token);
        if (alive && r && (r.count || 0) > 0) setFl(r);
      } catch (_) { /* best-effort — silent when unavailable */ }
    })();
    return () => { alive = false; };
  }, [ticker, token]);

  if (!fl || !(fl.count > 0)) return null;
  const rows = Array.isArray(fl.filings) ? fl.filings : [];
  const ago = (iso) => {
    const d = Math.round((Date.now() - new Date(iso + "T12:00:00").getTime()) / 86400000);
    return d <= 0 ? "today" : d === 1 ? "1d ago" : d < 60 ? `${d}d ago` : `${Math.round(d / 30)}mo ago`;
  };
  return (
    <div style={{background:C.card, borderRadius:16, border:`1px solid ${C.border}`, padding:"12px 16px", marginBottom:12}}>
      <button onClick={() => setOpen(o => !o)} style={{
        display:"flex", alignItems:"center", justifyContent:"space-between",
        width:"100%", background:"none", border:"none", padding:0, cursor:"pointer", fontFamily:"inherit"}}>
        <div style={{fontSize:9, fontWeight:700, letterSpacing:"0.12em", color:C.muted, textTransform:"uppercase"}}>
          📄 Ownership Filings<span style={{color:C.text}}> · {fl.count}</span>
          {fl.activist90 && <span style={{color:C.yellow}}> · 13D {ago(fl.latestActivist)}</span>}
        </div>
        <span style={{fontSize:11, color:C.muted, lineHeight:1}}>{open ? "▾" : "▸"}</span>
      </button>
      {open && (
        <div style={{marginTop:12}}>
          {rows.map((f, i) => (
            <div key={f.accession || i} style={{borderBottom:`1px dashed ${C.border}`, padding:"7px 0",
              display:"flex", justifyContent:"space-between", alignItems:"baseline", gap:10}}>
              <div style={{minWidth:0}}>
                <span style={{fontSize:11, fontWeight:700, color:f.kind === "activist" ? C.yellow : C.text}}>
                  {f.kind === "activist" ? "13D · activist" : "13G · passive"}{f.amendment ? " (amended)" : ""}</span>
                <div style={{fontSize:9, color:C.muted, marginTop:2}}>Filed {f.filed} · {ago(f.filed)}</div>
              </div>
              <a href={f.url} target="_blank" rel="noopener noreferrer"
                style={{fontSize:10, color:C.accent, fontWeight:700, textDecoration:"none", flexShrink:0}}>View on SEC ↗</a>
            </div>
          ))}
          <div style={{fontSize:9, color:C.muted, marginTop:8, lineHeight:1.4, opacity:0.85}}>
            A 13D is filed by a holder of 5%+ who may seek to influence the company; a 13G is the passive version (funds, index
            holders). Source: SEC EDGAR, last 12 months — public, dated context, not a trade signal.
          </div>
        </div>
      )}
    </div>
  );
}

function FundamentalsCard({ data }) {
  const [open, setOpen] = React.useState(true);
  const f = data?.fundamentals || {};
  // Polygon `divYieldPct` is the authoritative source (post-v8.3); fall back
  // to legacy `divYield` decimal fraction for older cached responses.
  let _divPctNum = null;
  if (typeof f.divYieldPct === "number" && f.divYieldPct > 0) {
    _divPctNum = f.divYieldPct;
  } else if (f.divYield && f.divYield > 0) {
    _divPctNum = f.divYield * 100;
  }
  // Polygon dividends are authoritative — no 25% cap needed in FundamentalsCard.
  // DivYieldPill keeps its own 25% safety guard for safety-net purposes.
  const divPct = (_divPctNum && _divPctNum > 0)
    ? `${_divPctNum.toFixed(2)}%` : null;

  // Prev close: prefer the explicit backend field (v8.4.165 adds it to the
  // /analyze response), but fall back to deriving it from price − changeAmt
  // (or price ÷ (1+changePct)) so freshly-clicked tickers that predate the
  // backend fix — or any response missing prevClose — still show a value
  // instead of "—".
  let _prevClose = data?.prevClose;
  if (_prevClose == null && data?.price != null) {
    if (data?.changeAmt != null) {
      _prevClose = data.price - data.changeAmt;
    } else if (data?.changePct != null && data.changePct !== 0) {
      _prevClose = data.price / (1 + data.changePct / 100);
    }
  }

  // Build the row list — only include 52-week range if both bounds exist.
  const rows = [
    { label: "Prev close",   value: _fmtPrice(_prevClose) },
    { label: "Open",          value: _fmtPrice(f.openPrice) },
    ...(f.dayHigh != null ? [{ label: "Day high", value: _fmtPrice(f.dayHigh) }] : []),
    ...(f.dayLow  != null ? [{ label: "Day low",  value: _fmtPrice(f.dayLow)  }] : []),
    { label: "Market cap",    value: _fmtMarketCap(f.marketCap) },
    { label: "Avg volume",    value: _fmtVolume(f.averageVolume) },
    { label: "50-day MA",     value: _fmtPrice(f.fiftyDayAvg) },
    { label: "200-day MA",    value: _fmtPrice(f.twoHundredDayAvg) },
    { label: "52-wk high",    value: _fmtPrice(f.fiftyTwoWeekHigh) },
    { label: "52-wk low",     value: _fmtPrice(f.fiftyTwoWeekLow) },
    ...(divPct ? [{ label: "Dividend yield", value: divPct }] : []),
    ...(f.freeCashFlow != null ? [{ label: "Free cash flow (TTM)", value: _fmtMarketCap(f.freeCashFlow) }] : []),
    ...(f.grossMargin != null ? [{ label: "Gross margin", value: `${(f.grossMargin * 100).toFixed(1)}%` }] : []),
    ...(f.debtToEquity != null ? [{ label: "Debt / equity", value: parseFloat(f.debtToEquity).toFixed(2) }] : []),
    ...(f.altmanZ != null ? [{ label: "Altman Z-score", value: `${f.altmanZ.toFixed(1)} · ${f.altmanZZone === "safe" ? "Safe" : f.altmanZZone === "grey" ? "Grey zone" : "Distress"}` }] : []),
  ];

  return (
    <div style={{background:C.card, borderRadius:16,
      border:`1px solid ${C.border}`, padding:"12px 16px", marginBottom:12}}>
      <button onClick={() => setOpen(o => !o)} style={{
        display:"flex", alignItems:"center", justifyContent:"space-between",
        width:"100%", background:"none", border:"none", padding:0, cursor:"pointer",
        fontFamily:"inherit",
      }}>
        <div style={{fontSize:9, fontWeight:700, letterSpacing:"0.12em",
          color:C.muted, textTransform:"uppercase"}}>
          Fundamentals
        </div>
        <span style={{fontSize:11, color:C.muted, lineHeight:1}}>
          {open ? "▾" : "▸"}
        </span>
      </button>
      {open && (
        <div style={{display:"grid",
          gridTemplateColumns:"repeat(auto-fit, minmax(120px, 1fr))",
          gap:"8px 14px", marginTop:12}}>
          {rows.map((r, i) => (
            <div key={i} style={{display:"flex",
              justifyContent:"space-between", alignItems:"baseline",
              borderBottom:`1px dashed ${C.border}`, paddingBottom:5}}>
              <span style={{fontSize:10, color:C.muted, letterSpacing:"0.04em",
                textTransform:"uppercase"}}>{r.label}</span>
              <span style={{fontSize:12, color:C.text, fontWeight:600,
                fontFamily:"monospace"}}>{r.value}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// EPS Growth pill — earnings per share growth rate
function EpsPill({ epsGrowth }) {
  if (epsGrowth == null) return (
    <span style={{display:"inline-flex",alignItems:"center",gap:4,
      background:"rgba(255,255,255,0.05)",border:"1px solid rgba(255,255,255,0.1)",
      borderRadius:20,padding:"2px 10px",fontSize:11,fontWeight:700,color:C.muted}}>
      N/A
    </span>
  );
  const pct   = parseFloat(epsGrowth) * 100;
  const color = pct >= 20 ? C.green : pct >= 5 ? "#ffca28" : pct >= 0 ? "#ff7043" : "#ff5252";
  const bg    = pct >= 20 ? "rgba(0,230,118,0.1)" : pct >= 5 ? "rgba(255,202,40,0.1)"
              : pct >= 0  ? "rgba(255,112,67,0.1)" : "rgba(255,82,82,0.1)";
  const label = pct >= 20 ? "High" : pct >= 5 ? "Moderate" : pct >= 0 ? "Low" : "Negative";
  return (
    <span title={`EPS Growth: ${pct.toFixed(1)}%`} style={{
      display:"inline-flex",alignItems:"center",gap:4,
      background:bg, border:`1px solid ${color}44`,
      borderRadius:20, padding:"2px 10px", fontSize:11, fontWeight:700, color}}>
      {pct >= 0 ? "+" : ""}{pct.toFixed(1)}%
      <span style={{fontSize:9,color,opacity:0.8}}>{label}</span>
    </span>
  );
}

// Earnings & Revenue bar chart component
// Single source of truth for the news-sentiment legend — mirrors the backend
// fetch_sentiment mapping (api.py). DERIVE the icon from the label at render time so
// the icon can never drift out of sync with the label (they used to be carried as two
// separate fields through many merge/cache paths, producing "Neutral 🔥" mismatches).
// Legend: 🔥 Very Bullish · 🟢 Bullish · 📈 Slightly Bullish · ➖ Neutral ·
//         📉 Slightly Bearish · 🔴 Bearish · 💀 Very Bearish
function sentimentIcon(label) {
  switch ((label || "").trim()) {
    case "Very Bullish":     return "🔥";
    case "Bullish":          return "🟢";
    case "Slightly Bullish": return "📈";
    case "Neutral":          return "➖";
    case "Slightly Bearish": return "📉";
    case "Bearish":          return "🔴";
    case "Very Bearish":     return "💀";
    default:                 return "➖";
  }
}

// v8.4.346 — earnings-date labels. Show the DATE (+ before/after-market), never "today":
// /analyze is cached ~20h, so a "Reported today" computed yesterday stays "today" into the
// next day (AMAT reported Aug 12 after close still read "today" on Aug 13). A date is
// cache-stable and unambiguous. bmo/amc disambiguates same-day-boundary reports.
function fmtEarnDate(iso) {
  try { return new Date(iso + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" }); }
  catch (_) { return iso || ""; }
}
function hourLabel(h) {
  return h === "bmo" ? "before open" : h === "amc" ? "after close" : h === "dmh" ? "during hrs" : "";
}

function EarningsChart({ quarters }) {
  const [mode, setMode] = React.useState("eps"); // "eps" | "rev"
  if (!quarters || quarters.length === 0) return null;

  const isEps = mode === "eps";

  // Mode toggle — always rendered so user can switch back from empty rev view
  const ModeToggle = () => (
    <div style={{display:"flex",gap:6,marginBottom:14}}>
      {[["eps","EPS"],["rev","Revenue"]].map(([key,label]) => (
        <button key={key} onClick={() => setMode(key)} style={{
          flex:1, padding:"6px 0", borderRadius:8, border:"none",
          fontFamily:"inherit", fontSize:11, fontWeight:700, cursor:"pointer",
          background: mode===key ? "rgba(0,212,255,0.15)" : "rgba(255,255,255,0.05)",
          color:       mode===key ? C.accent : C.muted,
          borderBottom: `2px solid ${mode===key ? C.accent : "transparent"}`,
          transition:"all 0.15s",
        }}>{label}</button>
      ))}
    </div>
  );

  // Build display data per quarter
  const bars = quarters.map(q => {
    if (isEps) {
      return {
        label:      q.label,
        actual:     q.epsReported ?? null,
        estimate:   q.epsEstimate ?? null,
        isForecast: q.isForecast || false,
        beat:       q.beat ?? null,
        surprise:   q.epsSurprise ?? null,
        daysUntil:  q.daysUntil ?? null,
      };
    } else {
      return {
        label:      q.label,
        actual:     q.revActual   ?? null,
        estimate:   q.revEstimate ?? null,
        isForecast: q.isForecast || false,
        beat:       q.revActual != null && q.revEstimate != null
                    ? q.revActual >= q.revEstimate : null,
        surprise:   (q.revActual != null && q.revEstimate != null && q.revEstimate > 0)
                    ? Math.round((q.revActual - q.revEstimate) / q.revEstimate * 1000) / 10
                    : null,
        daysUntil:  q.daysUntil ?? null,
      };
    }
  });

  // Compute chart scale — if no data in current mode, show empty state but keep toggle
  const allVals = bars.flatMap(b => [b.actual, b.estimate].filter(v => v != null));
  if (allVals.length === 0) {
    return (
      <div>
        <ModeToggle/>
        <div style={{textAlign:"center",padding:"18px 0",color:C.muted,fontSize:11}}>
          {isEps ? "EPS data unavailable" : "Revenue data unavailable"}
        </div>
      </div>
    );
  }

  const hasNeg  = allVals.some(v => v < 0);
  const maxAbs  = Math.max(...allVals.map(Math.abs), 0.01);
  const minVal  = hasNeg ? Math.min(...allVals) : 0;
  const maxVal  = Math.max(...allVals) * 1.18; // 18% headroom
  const range   = maxVal - Math.min(minVal, 0);

  // Convert value → % height from bottom
  const toH = (v) => {
    if (v == null) return 0;
    return Math.max(2, Math.min(100, ((v - Math.min(minVal,0)) / (range || 1)) * 100));
  };

  const unit = isEps ? "" : "B";

  const formatVal = (v) => {
    if (v == null) return "—";
    if (!isEps) return `$${Math.abs(v) >= 1 ? v.toFixed(1) : v.toFixed(2)}B`;
    return `$${v >= 0 ? "" : "-"}${Math.abs(v).toFixed(2)}`;
  };

  return (
    <div>
      <ModeToggle/>

      {/* Zero line label */}
      {hasNeg && (
        <div style={{fontSize:9,color:C.muted,marginBottom:2,textAlign:"right"}}>
          $0
        </div>
      )}

      {/* Bar chart */}
      <div style={{display:"flex",gap:6,alignItems:"flex-end",height:120,marginBottom:6}}>
        {bars.map((b, i) => {
          const actualH   = toH(b.actual);
          const estimateH = toH(b.estimate);
          const zeroH     = toH(0);
          const isBeat    = b.beat === true;
          // "meet" = a miss within ±2% (same rule as the recent-earnings pill); color it
          // yellow, not red, so the bar agrees with the pill instead of reading as a miss.
          const isMeet    = b.beat === false && b.surprise != null && Math.abs(b.surprise) <= 2;
          const isMiss    = b.beat === false && !isMeet;
          // If no estimate but actual exists: green for positive EPS, red for negative
          const actColor  = b.isForecast ? "rgba(0,212,255,0.25)"
                          : isBeat       ? "#00e676"
                          : isMeet       ? "#ffca28"
                          : isMiss       ? "#ff5252"
                          : b.actual != null && b.estimate == null
                            ? (b.actual >= 0 ? "rgba(0,230,118,0.6)" : "rgba(255,82,82,0.6)")
                          : "rgba(255,255,255,0.25)";
          const estColor  = b.isForecast ? C.accent : "rgba(100,100,120,0.5)";

          return (
            <div key={i} style={{flex:1,display:"flex",flexDirection:"column",
              alignItems:"center",gap:0,height:"100%",position:"relative"}}>
              {/* Bar group — side by side */}
              <div style={{width:"100%",height:"100%",display:"flex",
                alignItems:"flex-end",gap:2,position:"relative"}}>
                {/* Estimate bar */}
                {b.estimate != null && (
                  <div style={{flex:1,position:"relative",height:"100%",
                    display:"flex",alignItems:"flex-end"}}>
                    <div title={`Est: ${formatVal(b.estimate)}`} style={{
                      width:"100%",
                      height:`${Math.abs(estimateH - zeroH)}%`,
                      background:estColor,
                      borderRadius:"3px 3px 0 0",
                      border: b.isForecast ? `1px dashed ${C.accent}` : "none",
                      borderBottom:"none",
                      marginBottom: 0,
                      marginTop: b.estimate >= 0 ? "auto" : 0,
                    }}/>
                  </div>
                )}
                {/* Actual bar (or forecast placeholder) */}
                {b.actual != null ? (
                  <div style={{flex:1,position:"relative",height:"100%",
                    display:"flex",alignItems:"flex-end"}}>
                    <div title={`Actual: ${formatVal(b.actual)}`} style={{
                      width:"100%",
                      height:`${Math.abs(actualH - zeroH)}%`,
                      background:actColor,
                      borderRadius:"3px 3px 0 0",
                      minHeight:2,
                    }}/>
                  </div>
                ) : b.isForecast ? (
                  <div style={{flex:1,height:"100%",display:"flex",
                    alignItems:"center",justifyContent:"center"}}>
                    <span style={{fontSize:8,color:C.accent,fontWeight:700,
                      writingMode:"vertical-rl",textOrientation:"mixed",
                      opacity:0.7}}>
                      {b.daysUntil != null ? `${b.daysUntil}d` : "TBD"}
                    </span>
                  </div>
                ) : null}
                {/* Zero line for negative charts */}
                {hasNeg && (
                  <div style={{position:"absolute",bottom:`${zeroH}%`,left:0,right:0,
                    height:1,background:"rgba(255,255,255,0.15)"}}/>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Quarter labels */}
      <div style={{display:"flex",gap:6,marginBottom:8}}>
        {bars.map((b,i) => (
          <div key={i} style={{flex:1,textAlign:"center"}}>
            <div style={{fontSize:9,color:b.isForecast?C.accent:C.muted,
              fontWeight:b.isForecast?700:400,
              whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
              {b.label}
              {b.isForecast && <span style={{marginLeft:2,fontSize:8}}>▸</span>}
            </div>
          </div>
        ))}
      </div>

      {/* Actual values row */}
      <div style={{display:"flex",gap:6,marginBottom:4}}>
        {bars.map((b,i) => (
          <div key={i} style={{flex:1,textAlign:"center"}}>
            {b.actual != null ? (
              <div style={{fontSize:10,fontWeight:700,fontFamily:"monospace",
                color: b.isForecast ? C.muted
                     : b.beat===true ? C.green
                     : (b.beat===false && b.surprise != null && Math.abs(b.surprise) <= 2) ? C.yellow
                     : b.beat===false ? C.red
                     : C.text}}>
                {formatVal(b.actual)}
              </div>
            ) : b.isForecast && b.estimate != null ? (
              <div style={{fontSize:10,fontWeight:700,fontFamily:"monospace",
                color:C.accent}}>
                {formatVal(b.estimate)}
                <span style={{fontSize:8,marginLeft:2,opacity:0.7}}>est</span>
              </div>
            ) : (
              <div style={{fontSize:10,color:"rgba(255,255,255,0.15)"}}>—</div>
            )}
          </div>
        ))}
      </div>

      {/* Surprise row */}
      <div style={{display:"flex",gap:6,marginBottom:10}}>
        {bars.map((b,i) => (
          <div key={i} style={{flex:1,textAlign:"center"}}>
            {!b.isForecast && b.surprise != null ? (
              <div style={{fontSize:9,fontFamily:"monospace",
                color:b.surprise>=0?C.green:C.red}}>
                {b.surprise>=0?"+":""}{b.surprise}%
              </div>
            ) : (
              <div style={{fontSize:9,color:"rgba(255,255,255,0.1)"}}>
                {b.isForecast ? "forecast" : ""}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Legend */}
      <div style={{display:"flex",gap:12,justifyContent:"center",flexWrap:"wrap"}}>
        <div style={{display:"flex",alignItems:"center",gap:4,fontSize:9,color:C.muted}}>
          <div style={{width:10,height:10,borderRadius:2,
            background:"rgba(255,255,255,0.2)"}}/>
          Estimate
        </div>
        <div style={{display:"flex",alignItems:"center",gap:4,fontSize:9,color:C.muted}}>
          <div style={{width:10,height:10,borderRadius:2,
            background:"rgba(0,230,118,0.85)"}}/>
          Beat
        </div>
        <div style={{display:"flex",alignItems:"center",gap:4,fontSize:9,color:C.muted}}>
          <div style={{width:10,height:10,borderRadius:2,
            background:"rgba(255,82,82,0.8)"}}/>
          Miss
        </div>
        <div style={{display:"flex",alignItems:"center",gap:4,fontSize:9,color:C.muted}}>
          <div style={{width:10,height:10,borderRadius:2,border:`1px dashed ${C.accent}`,
            background:"rgba(0,212,255,0.25)"}}/>
          Forecast
        </div>
      </div>
    </div>
  );
}

// ── Add-to-Home-Screen install prompt ─────────────────────────────────────────
// Android/Chrome: banner + native install sheet via beforeinstallprompt.
// iOS Safari:     step-by-step modal (Share → Add to Home Screen).
// Shows after the 3rd session. Supports snooze (7 days) and permanent dismiss.
function InstallPrompt() {
  const [mode, setMode] = React.useState(null); // null | "banner" | "ios"

  React.useEffect(() => {
    if (!_shouldShowInstall()) return;
    if (_isIosSafari() && !_isStandalone()) {
      setMode("ios");
      return;
    }
    // Android/Chrome: show banner when beforeinstallprompt event is ready
    if (_bipEvt) { setMode("banner"); return; }
    // Event may still be incoming — poll briefly (fires ~1-2 s after page load)
    const iv = setInterval(() => { if (_bipEvt) { setMode("banner"); clearInterval(iv); } }, 300);
    const to = setTimeout(() => clearInterval(iv), 5000);
    return () => { clearInterval(iv); clearTimeout(to); };
  }, []);

  const dismiss = (permanent) => {
    setMode(null);
    try {
      if (permanent) localStorage.setItem(INSTALL_DISMISSED_KEY, 'true');
      else           localStorage.setItem(INSTALL_SNOOZED_KEY, new Date().toISOString());
    } catch (_) {}
  };

  const handleInstall = async () => {
    if (!_bipEvt) return;
    _bipEvt.prompt();
    const { outcome } = await _bipEvt.userChoice.catch(() => ({ outcome: 'dismissed' }));
    _bipEvt = null;
    dismiss(outcome === 'accepted'); // permanent if accepted, snooze if dismissed
  };

  // ── Android / desktop Chrome banner ────────────────────────────────────────
  if (mode === "banner") return (
    <div style={{
      background:"#111113",
      borderBottom:"1px solid rgba(255,255,255,0.07)",
      padding:"11px 16px",
      display:"flex", alignItems:"center", gap:10,
    }}>
      <img src="/icon-192.png" alt="" style={{width:36,height:36,borderRadius:8,flexShrink:0}}
        onError={e => { e.target.style.display = 'none'; }}/>
      <div style={{flex:1,minWidth:0}}>
        <div style={{fontSize:13,fontWeight:700,color:"#ffffff",lineHeight:1.3}}>
          Add GemPulse to your home screen
        </div>
        <div style={{fontSize:11,color:"rgba(255,255,255,0.4)",marginTop:1}}>
          for the full experience
        </div>
      </div>
      <button onClick={handleInstall} style={{
        padding:"7px 14px",borderRadius:20,border:"none",cursor:"pointer",
        background:"#00d4ff",color:"#0a0f1e",fontSize:12,fontWeight:700,
        fontFamily:"inherit",flexShrink:0,whiteSpace:"nowrap",
      }}>Add</button>
      <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:4,flexShrink:0}}>
        <button onClick={()=>dismiss(false)} style={{
          background:"none",border:"none",color:"rgba(255,255,255,0.3)",fontSize:18,
          lineHeight:1,cursor:"pointer",padding:"2px 0",fontFamily:"inherit",
        }}>×</button>
        <button onClick={()=>dismiss(true)} style={{
          background:"none",border:"none",color:"rgba(255,255,255,0.25)",
          fontSize:9,fontWeight:600,cursor:"pointer",fontFamily:"inherit",
          whiteSpace:"nowrap",padding:0,textDecoration:"underline",
        }}>Don't show again</button>
      </div>
    </div>
  );

  // ── iOS Safari step-by-step modal ──────────────────────────────────────────
  if (mode === "ios") return (
    <>
      {/* Backdrop */}
      <div onClick={()=>dismiss(false)} style={{
        position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:8000,
      }}/>
      {/* Panel */}
      <div onClick={e=>e.stopPropagation()} style={{
        position:"fixed",inset:0,margin:"auto",
        width:"min(380px, calc(100vw - 32px))",
        height:"fit-content",
        background:"#111113",
        border:"1px solid rgba(255,255,255,0.07)",
        borderRadius:20,padding:22,zIndex:8001,
        boxShadow:"0 16px 64px rgba(0,0,0,0.7)",
      }}>
        <div style={{display:"flex",justifyContent:"space-between",
          alignItems:"flex-start",marginBottom:18}}>
          <div>
            <div style={{fontSize:16,fontWeight:800,color:"#ffffff",marginBottom:4}}>
              Install GemPulse
            </div>
            <div style={{fontSize:12,color:"rgba(255,255,255,0.4)",lineHeight:1.4}}>
              Add to your Home Screen for the full experience
            </div>
          </div>
          <button onClick={()=>dismiss(false)} style={{
            background:"none",border:"none",color:"rgba(255,255,255,0.3)",
            fontSize:20,lineHeight:1,cursor:"pointer",padding:0,
            fontFamily:"inherit",flexShrink:0,marginLeft:8,
          }}>×</button>
        </div>

        {[
          { n:"1", text: <><b style={{color:"#00d4ff"}}>Tap the Share icon</b>{" "}
                          (the box with an arrow ↑) in Safari's toolbar</> },
          { n:"2", text: <>Scroll down and tap{" "}
                          <b style={{color:"#00d4ff"}}>Add to Home Screen</b></> },
          { n:"3", text: <>Tap{" "}
                          <b style={{color:"#00d4ff"}}>Add</b>{" "}
                          in the top-right corner</> },
        ].map(({n, text}) => (
          <div key={n} style={{display:"flex",gap:12,alignItems:"flex-start",marginBottom:14}}>
            <div style={{
              width:26,height:26,borderRadius:"50%",flexShrink:0,
              background:"rgba(0,212,255,0.1)",border:"1px solid rgba(0,212,255,0.25)",
              display:"flex",alignItems:"center",justifyContent:"center",
              fontSize:11,fontWeight:800,color:"#00d4ff",
            }}>{n}</div>
            <div style={{fontSize:13,color:"rgba(255,255,255,0.7)",
              lineHeight:1.55,paddingTop:3}}>{text}</div>
          </div>
        ))}

        <div style={{borderTop:"1px solid rgba(255,255,255,0.07)",
          paddingTop:14,display:"flex",gap:8,marginTop:4}}>
          <button onClick={()=>dismiss(true)} style={{
            flex:1,padding:"9px",borderRadius:10,
            border:"1px solid rgba(255,255,255,0.08)",
            background:"none",color:"rgba(255,255,255,0.4)",
            fontFamily:"inherit",fontSize:11,fontWeight:600,cursor:"pointer",
          }}>Don't show again</button>
          <button onClick={()=>dismiss(false)} style={{
            flex:2,padding:"9px",borderRadius:10,border:"none",
            background:"#00d4ff",color:"#0a0f1e",
            fontFamily:"inherit",fontSize:12,fontWeight:700,cursor:"pointer",
          }}>Got it</button>
        </div>
      </div>
    </>
  );

  return null;
}

// --- Gap Alert Toggle — radio button + collapsible dropdown panel ------------
// Matches the ◉/◎ radio indicator style used in stock-detail alerts tab.
// Active alert count shown as a badge. Manage/delete alerts via Settings > Alerts.
function GapAlertToggle({ token, onAlertsChanged }) {
  const [count,      setCount]      = React.useState(0);
  const [showPanel,  setShowPanel]  = React.useState(false);
  const [selGap,     setSelGap]     = React.useState(2);
  const [selSectors, setSelSectors] = React.useState([]);
  const [selRsi,     setSelRsi]     = React.useState(null);
  // Score filter: null = any score; otherwise one of
  //   "swing_buy" | "lt_buy" | "either_buy" | "both_buy"
  // Buy threshold mirrors the ScoreDonut green tier (≥ 70).
  const [selScore,   setSelScore]   = React.useState(null);
  const [saving,     setSaving]     = React.useState(false);
  const [saveErr,    setSaveErr]    = React.useState("");
  const panelRef = React.useRef(null);


  const loadCount = React.useCallback(() => {
    if (!token) return;
    apiFetch("/alerts", token)
      .then(alerts => {
        setCount(Array.isArray(alerts)
          ? alerts.filter(a => a.alert_type === "gap_scanner" && a.is_active).length
          : 0);
      })
      .catch(() => {});
  }, [token]);

  React.useEffect(() => { loadCount(); }, [loadCount]);

  // Close panel on outside click
  React.useEffect(() => {
    if (!showPanel) return;
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setShowPanel(false);
      }
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("touchstart", handler, {passive:true});
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("touchstart", handler);
    };
  }, [showPanel]);

  const SECTORS = ["Technology","Financials","Healthcare","Energy",
                   "Consumer Cyclical","Industrials","Communication Services","Other"];
  const RSI_OPTIONS = [
    {key:"oversold",  label:"<30 Oversold",   hint:"potential buy"},
    {key:"neutral",   label:"45–55 Neutral",  hint:"natural momentum"},
    {key:"overbought",label:">70 Overbought", hint:"potential pullback"},
  ];
  // Score filter options — threshold is ≥70 (ScoreDonut "BUY" tier).
  // Persisted to the alert notes JSON and applied server-side at email time.
  const SCORE_OPTIONS = [
    {key:"swing_buy",  label:"Swing ≥ Buy",      hint:"Swing Score ≥ 70"},
    {key:"lt_buy",     label:"LT ≥ Buy",         hint:"LT Score ≥ 70"},
    {key:"either_buy", label:"Either ≥ Buy",     hint:"Swing OR LT ≥ 70"},
    {key:"both_buy",   label:"Both ≥ Buy",       hint:"Swing AND LT ≥ 70"},
  ];

  const save = async () => {
    setSaving(true); setSaveErr("");
    try {
      const notes = JSON.stringify({
        sectors: selSectors,
        rsi_filter: selRsi,
        score_filter: selScore,
      });
      await apiFetch("/alerts", token, {
        method:"POST",
        body: JSON.stringify({
          ticker:"GAP_SCANNER", alert_type:"gap_scanner",
          target_value: selGap, notes,
        }),
      });
      setShowPanel(false);
      setSelGap(2); setSelSectors([]); setSelRsi(null); setSelScore(null);
      loadCount();
      if (onAlertsChanged) onAlertsChanged();
    } catch(e) {
      const msg = e.message || "";
      // Old server versions limit to 1 gap alert — surface a human-readable message
      if (msg.toLowerCase().includes("already") || msg.toLowerCase().includes("one ") ||
          msg.toLowerCase().includes("exist") || msg.toLowerCase().includes("limit") ||
          msg.includes("400")) {
        setSaveErr("Server needs update to allow multiple alerts — rebuild Docker to enable");
      } else if (msg.toLowerCase().includes("notes") || msg.includes("500")) {
        setSaveErr("Server schema out of date — rebuild Docker container to fix");
      } else {
        setSaveErr(msg || "Save failed — please try again");
      }
    } finally { setSaving(false); }
  };

  const hasAlerts = count > 0;

  return (
    <div ref={panelRef} style={{position:"relative",flexShrink:0}}>
      {/* Radio-style trigger button — ◉ when alerts active, ◎ when none */}
      <button
        onClick={() => { setShowPanel(p => !p); setSaveErr(""); }}
        title={hasAlerts ? `${count} gap alert${count>1?"s":""} active` : "Set gap alert"}
        style={{
          display:"flex",alignItems:"center",gap:5,
          padding:"3px 9px 3px 7px",borderRadius:8,border:"none",cursor:"pointer",
          fontFamily:"inherit",
          background: hasAlerts?"rgba(0,212,255,0.12)":"rgba(255,255,255,0.06)",
          color: hasAlerts?C.accent:C.muted,
          outline: showPanel?`1px solid ${C.accent}`:"none",
        }}>
        <svg width="13" height="13" viewBox="0 0 16 16" style={{flexShrink:0}}>
          <circle cx="8" cy="8" r="7"
            stroke={hasAlerts?C.accent:"rgba(255,255,255,0.3)"}
            strokeWidth="1.5" fill="none"/>
          {hasAlerts && <circle cx="8" cy="8" r="4" fill={C.accent}/>}
        </svg>
        <span style={{fontSize:9,fontWeight:700}}>Alert</span>
        {count > 0 && (
          <span style={{fontSize:8,fontWeight:800,background:C.accent,
            color:"#0a0f1e",borderRadius:6,padding:"1px 4px",
            minWidth:14,textAlign:"center",lineHeight:"14px"}}>
            {count}
          </span>
        )}
      </button>

      {/* Collapsible filter panel — drops below the radio button.
          maxHeight + overflowY prevent the panel from spilling off the bottom
          of the viewport as more filter sections are added; maxWidth already
          caps horizontal spill at 24px from the viewport edge. */}
      {showPanel && (
        <div style={{
          position:"absolute", right:0, top:"calc(100% + 6px)",
          background:"#0d1528", border:`1px solid ${C.border}`,
          borderRadius:12, padding:14, zIndex:500,
          width:270, maxWidth:"calc(100vw - 24px)",
          maxHeight:"calc(100vh - 120px)", overflowY:"auto",
          boxShadow:"0 8px 32px rgba(0,0,0,0.55)",
        }}>
          <div style={{fontSize:12,fontWeight:700,color:C.text,marginBottom:10}}>
            New Gap Alert
          </div>

          {/* Min gap selector */}
          <div style={{fontSize:10,color:C.muted,marginBottom:5,fontWeight:600}}>
            Minimum gap size
          </div>
          <div style={{display:"flex",gap:5,marginBottom:12}}>
            {[2,3,5,7,10].map(v => (
              <button key={v} onClick={()=>setSelGap(v)} style={{
                flex:1,padding:"6px 0",borderRadius:7,border:"none",
                fontFamily:"inherit",fontSize:11,fontWeight:700,cursor:"pointer",
                background:selGap===v?"rgba(0,212,255,0.18)":"rgba(255,255,255,0.06)",
                color:selGap===v?C.accent:C.muted,
                outline:selGap===v?`1px solid ${C.accent}`:"none",
              }}>{v}%</button>
            ))}
          </div>

          {/* Sector chips */}
          <div style={{fontSize:10,color:C.muted,marginBottom:5,fontWeight:600}}>
            Sectors (optional)
          </div>
          <div style={{display:"flex",flexWrap:"wrap",gap:4,marginBottom:12}}>
            {SECTORS.map(s => {
              const on = selSectors.includes(s);
              return (
                <button key={s}
                  onClick={()=>setSelSectors(p=>on?p.filter(x=>x!==s):[...p,s])}
                  style={{fontSize:9,padding:"3px 7px",borderRadius:6,border:"none",
                    cursor:"pointer",fontFamily:"inherit",fontWeight:600,
                    background:on?"rgba(0,212,255,0.15)":"rgba(255,255,255,0.06)",
                    color:on?C.accent:C.muted,
                    outline:on?`1px solid ${C.accent}`:"none"}}>
                  {s}
                </button>
              );
            })}
          </div>

          {/* RSI filter — radio rows */}
          <div style={{fontSize:10,color:C.muted,marginBottom:5,fontWeight:600}}>
            RSI filter (optional)
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:4,marginBottom:12}}>
            {[{key:null,label:"No RSI filter",hint:"all stocks"}, ...RSI_OPTIONS].map(o => {
              const sel = selRsi === o.key;
              return (
                <button key={String(o.key)} onClick={()=>setSelRsi(o.key)}
                  style={{display:"flex",alignItems:"center",gap:8,padding:"7px 10px",
                    borderRadius:8,border:"none",cursor:"pointer",fontFamily:"inherit",
                    textAlign:"left",width:"100%",
                    background:sel?"rgba(0,212,255,0.10)":"rgba(255,255,255,0.04)",
                    borderLeft:`3px solid ${sel?C.accent:"transparent"}`,}}>
                  <svg width="14" height="14" viewBox="0 0 16 16">
                    <circle cx="8" cy="8" r="7"
                      stroke={sel?C.accent:"rgba(255,255,255,0.2)"}
                      strokeWidth="1.5" fill="none"/>
                    {sel && <circle cx="8" cy="8" r="4" fill={C.accent}/>}
                  </svg>
                  <div>
                    <div style={{fontSize:11,fontWeight:600,
                      color:sel?C.text:"rgba(255,255,255,0.6)"}}>{o.label}</div>
                    <div style={{fontSize:9,color:C.muted}}>{o.hint}</div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Score filter — quality gate on swing/LT scores.  Applied
              server-side at email-send time so the email only lists names
              whose universe scores clear the chosen threshold. */}
          <div style={{fontSize:10,color:C.muted,marginBottom:5,fontWeight:600}}>
            Score filter (optional)
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:4,marginBottom:12}}>
            {[{key:null,label:"No score filter",hint:"all scores"}, ...SCORE_OPTIONS].map(o => {
              const sel = selScore === o.key;
              return (
                <button key={String(o.key)} onClick={()=>setSelScore(o.key)}
                  style={{display:"flex",alignItems:"center",gap:8,padding:"7px 10px",
                    borderRadius:8,border:"none",cursor:"pointer",fontFamily:"inherit",
                    textAlign:"left",width:"100%",
                    background:sel?"rgba(0,212,255,0.10)":"rgba(255,255,255,0.04)",
                    borderLeft:`3px solid ${sel?C.accent:"transparent"}`,}}>
                  <svg width="14" height="14" viewBox="0 0 16 16">
                    <circle cx="8" cy="8" r="7"
                      stroke={sel?C.accent:"rgba(255,255,255,0.2)"}
                      strokeWidth="1.5" fill="none"/>
                    {sel && <circle cx="8" cy="8" r="4" fill={C.accent}/>}
                  </svg>
                  <div>
                    <div style={{fontSize:11,fontWeight:600,
                      color:sel?C.text:"rgba(255,255,255,0.6)"}}>{o.label}</div>
                    <div style={{fontSize:9,color:C.muted}}>{o.hint}</div>
                  </div>
                </button>
              );
            })}
          </div>

          {saveErr && (
            <div style={{fontSize:10,color:"#ff5252",marginBottom:8,padding:"5px 8px",
              background:"rgba(255,82,82,0.08)",borderRadius:6}}>
              ⚠ {saveErr}
            </div>
          )}

          <div style={{display:"flex",gap:8}}>
            <button
              onClick={()=>{ setShowPanel(false); setSaveErr(""); setSelGap(2); setSelSectors([]); setSelRsi(null); setSelScore(null); }}
              style={{flex:1,padding:"9px 0",borderRadius:8,
                border:`1px solid ${C.border}`,background:"none",
                color:C.muted,fontFamily:"inherit",fontSize:12,fontWeight:600,cursor:"pointer"}}>
              Cancel
            </button>
            <button onClick={(e)=>{Haptics.success(e.currentTarget);save();}} disabled={saving}
              style={{flex:2,padding:"9px 0",borderRadius:8,border:"none",
                background:saving?"rgba(0,212,255,0.35)":C.accent,
                color:"#0a0f1e",fontFamily:"inherit",fontSize:12,
                fontWeight:700,cursor:saving?"default":"pointer"}}>
              {saving ? "Saving…" : "OK"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Gap scanner LIVE filter dropdown ───────────────────────────────────────
// Mirrors GapAlertToggle UX (sectors / RSI band / score gate) but wires the
// selections into the parent card's `displayed` filter instead of saving an
// alert. Filters apply instantly client-side — no API roundtrip.
//
// `filters` shape: { sectors: string[], rsi: string|null, score: string|null }
// Caller owns the state; this component just renders the popover and calls
// setters on change. Filters always-additive — empty arrays / nulls = match-all.
function GapScannerFilters({ filters, setFilters, displayedCount, totalCount }) {
  const [showPanel, setShowPanel] = React.useState(false);
  const panelRef = React.useRef(null);

  React.useEffect(() => {
    if (!showPanel) return;
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setShowPanel(false);
      }
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("touchstart", handler, {passive:true});
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("touchstart", handler);
    };
  }, [showPanel]);

  const SECTORS = ["Technology","Financials","Healthcare","Energy",
                   "Consumer Cyclical","Industrials","Communication Services","Other"];
  const RSI_OPTIONS = [
    {key:"oversold",  label:"<30 Oversold",   hint:"potential buy"},
    {key:"neutral",   label:"45–55 Neutral",  hint:"natural momentum"},
    {key:"overbought",label:">70 Overbought", hint:"potential pullback"},
  ];
  const SCORE_OPTIONS = [
    {key:"swing_buy",  label:"Swing ≥ Buy",  hint:"Swing Score ≥ 70"},
    {key:"lt_buy",     label:"LT ≥ Buy",     hint:"LT Score ≥ 70"},
    {key:"either_buy", label:"Either ≥ Buy", hint:"Swing OR LT ≥ 70"},
    {key:"both_buy",   label:"Both ≥ Buy",   hint:"Swing AND LT ≥ 70"},
  ];

  // Count active dimensions (not selections) for the badge — sectors + rsi + score
  const activeCount = (filters.sectors?.length ? 1 : 0)
                    + (filters.rsi ? 1 : 0)
                    + (filters.score ? 1 : 0);
  const hasFilters = activeCount > 0;

  const toggleSector = (s) => setFilters(f => ({
    ...f,
    sectors: f.sectors.includes(s) ? f.sectors.filter(x=>x!==s) : [...f.sectors, s]
  }));

  const clearAll = () => setFilters({ sectors: [], rsi: null, score: null });

  return (
    <div ref={panelRef} style={{position:"relative",flexShrink:0}}>
      <button
        onClick={() => setShowPanel(p => !p)}
        title={hasFilters ? `${activeCount} filter${activeCount>1?"s":""} active`
                          : "Filter the gap list"}
        style={{
          display:"flex",alignItems:"center",gap:5,
          padding:"3px 9px 3px 7px",borderRadius:8,border:"none",cursor:"pointer",
          fontFamily:"inherit",
          background: hasFilters?"rgba(0,212,255,0.12)":"rgba(255,255,255,0.06)",
          color: hasFilters?C.accent:C.muted,
          outline: showPanel?`1px solid ${C.accent}`:"none",
        }}>
        {/* Funnel icon */}
        <svg width="11" height="11" viewBox="0 0 16 16" style={{flexShrink:0}}>
          <path d="M2 3 H14 L10 8.5 V13 L6 13 V8.5 Z"
            fill="none" stroke={hasFilters?C.accent:"rgba(255,255,255,0.45)"}
            strokeWidth="1.4" strokeLinejoin="round"/>
        </svg>
        <span style={{fontSize:9,fontWeight:700}}>Filter</span>
        {activeCount > 0 && (
          <span style={{fontSize:8,fontWeight:800,background:C.accent,
            color:"#0a0f1e",borderRadius:6,padding:"1px 4px",
            minWidth:14,textAlign:"center",lineHeight:"14px"}}>
            {activeCount}
          </span>
        )}
      </button>

      {showPanel && (
        <>
          {/* Backdrop — clicking dismisses; rendered via portal-equivalent
              by using position:fixed so it escapes any parent stacking context. */}
          <div onClick={()=>setShowPanel(false)} style={{
            position:"fixed", inset:0,
            background:"rgba(0,0,0,0.6)",
            zIndex:9000,
          }}/>
          {/* Centered modal — viewport-anchored so the popover never spills off
              the screen edge, regardless of where the trigger button sits.
              `inset:0; margin:auto` is the modern centering trick that respects
              max-width AND max-height without needing transform tricks that
              break safari sub-pixel rounding. */}
          <div onClick={e=>e.stopPropagation()} style={{
            position:"fixed", inset:0, margin:"auto",
            width:"min(420px, calc(100vw - 32px))",
            height:"fit-content",
            maxHeight:"min(620px, calc(100vh - 80px))",
            overflowY:"auto",
            background:"#0d1528", border:`1px solid ${C.border}`,
            borderRadius:16, padding:18, zIndex:9001,
            boxShadow:"0 12px 48px rgba(0,0,0,0.6)",
          }}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",
            marginBottom:10}}>
            <div style={{fontSize:13,fontWeight:700,color:C.text}}>Live Filters</div>
            <div style={{display:"flex",alignItems:"center",gap:10}}>
              <span style={{fontSize:9,color:C.muted}}>
                {displayedCount}/{totalCount}
              </span>
              <button onClick={()=>setShowPanel(false)} style={{
                background:"none",border:"none",color:C.muted,cursor:"pointer",
                fontSize:18,lineHeight:1,padding:0,fontFamily:"inherit",
              }} title="Close">×</button>
            </div>
          </div>

          {/* Sectors */}
          <div style={{fontSize:10,color:C.muted,marginBottom:5,fontWeight:600}}>
            Sectors {filters.sectors.length > 0 && `(${filters.sectors.length})`}
          </div>
          <div style={{display:"flex",flexWrap:"wrap",gap:5,marginBottom:12}}>
            {SECTORS.map(s => {
              const sel = filters.sectors.includes(s);
              return (
                <button key={s} onClick={()=>toggleSector(s)} style={{
                  padding:"4px 8px",borderRadius:14,border:"none",
                  fontFamily:"inherit",fontSize:10,fontWeight:600,cursor:"pointer",
                  background:sel?"rgba(0,212,255,0.18)":"rgba(255,255,255,0.06)",
                  color:sel?C.accent:C.muted,
                  outline:sel?`1px solid ${C.accent}`:"none",
                }}>{s}</button>
              );
            })}
          </div>

          {/* RSI band */}
          <div style={{fontSize:10,color:C.muted,marginBottom:5,fontWeight:600}}>
            RSI band
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:4,marginBottom:12}}>
            {RSI_OPTIONS.map(o => {
              const sel = filters.rsi === o.key;
              return (
                <button key={o.key}
                  onClick={()=>setFilters(f=>({...f, rsi: sel?null:o.key}))}
                  style={{
                  display:"flex",alignItems:"center",gap:8,
                  padding:"7px 10px",borderRadius:8,border:"none",cursor:"pointer",
                  fontFamily:"inherit",textAlign:"left",
                  background:sel?"rgba(0,212,255,0.12)":"rgba(255,255,255,0.04)",
                  outline:sel?`1px solid ${C.accent}`:"none",
                }}>
                  <svg width="12" height="12" viewBox="0 0 16 16" style={{flexShrink:0}}>
                    <circle cx="8" cy="8" r="6.5"
                      stroke={sel?C.accent:"rgba(255,255,255,0.3)"}
                      strokeWidth="1.4" fill="none"/>
                    {sel && <circle cx="8" cy="8" r="3.5" fill={C.accent}/>}
                  </svg>
                  <div>
                    <div style={{fontSize:11,fontWeight:600,
                      color:sel?C.text:"rgba(255,255,255,0.6)"}}>{o.label}</div>
                    <div style={{fontSize:9,color:C.muted}}>{o.hint}</div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Score gate */}
          <div style={{fontSize:10,color:C.muted,marginBottom:5,fontWeight:600}}>
            Score gate
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:4,marginBottom:12}}>
            {SCORE_OPTIONS.map(o => {
              const sel = filters.score === o.key;
              return (
                <button key={o.key}
                  onClick={()=>setFilters(f=>({...f, score: sel?null:o.key}))}
                  style={{
                  display:"flex",alignItems:"center",gap:8,
                  padding:"7px 10px",borderRadius:8,border:"none",cursor:"pointer",
                  fontFamily:"inherit",textAlign:"left",
                  background:sel?"rgba(0,212,255,0.12)":"rgba(255,255,255,0.04)",
                  outline:sel?`1px solid ${C.accent}`:"none",
                }}>
                  <svg width="12" height="12" viewBox="0 0 16 16" style={{flexShrink:0}}>
                    <circle cx="8" cy="8" r="6.5"
                      stroke={sel?C.accent:"rgba(255,255,255,0.3)"}
                      strokeWidth="1.4" fill="none"/>
                    {sel && <circle cx="8" cy="8" r="3.5" fill={C.accent}/>}
                  </svg>
                  <div>
                    <div style={{fontSize:11,fontWeight:600,
                      color:sel?C.text:"rgba(255,255,255,0.6)"}}>{o.label}</div>
                    <div style={{fontSize:9,color:C.muted}}>{o.hint}</div>
                  </div>
                </button>
              );
            })}
          </div>

          <div style={{display:"flex",gap:8}}>
            <button onClick={clearAll} disabled={!hasFilters}
              style={{flex:1,padding:"8px 0",borderRadius:8,
                border:`1px solid ${C.border}`,background:"none",
                color:hasFilters?C.muted:"rgba(255,255,255,0.2)",
                fontFamily:"inherit",fontSize:11,fontWeight:600,
                cursor:hasFilters?"pointer":"default"}}>
              Clear
            </button>
            <button onClick={(e)=>{Haptics.light(e.currentTarget);setShowPanel(false);}}
              style={{flex:2,padding:"8px 0",borderRadius:8,border:"none",
                background:C.accent,color:"#0a0f1e",
                fontFamily:"inherit",fontSize:11,fontWeight:700,cursor:"pointer"}}>
              Done
            </button>
          </div>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Unusual Activity Card ─────────────────────────────────────────────────
// Polygon Starter-tier institutional signal scanner. Combines off-exchange
// short volume (dark-pool proxy), insider Form 4 buys, and relative volume.
// Real-time tick-level dark-pool tagging requires Polygon Developer ($79/mo).

// SVG glyphs for the unusual-activity signal types — matches SwingIcon / LTIcon
// / TrendingIcon style (24x24 viewBox, stroke-based, inherits color).
function DarkPoolIcon({ size = 12, color = "currentColor" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      style={{display:"inline-block",verticalAlign:"middle",flexShrink:0}}>
      <circle cx="12" cy="12" r="9" stroke={color} strokeWidth="2"/>
      <path d="M12 3a9 9 0 000 18 6 6 0 010-18z" fill={color} opacity="0.7"/>
    </svg>
  );
}
function InsiderIcon({ size = 12, color = "currentColor" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      style={{display:"inline-block",verticalAlign:"middle",flexShrink:0}}>
      <rect x="3" y="7" width="18" height="13" rx="2" stroke={color} strokeWidth="2"/>
      <path d="M9 7V5a2 2 0 012-2h2a2 2 0 012 2v2" stroke={color} strokeWidth="2"/>
      <path d="M3 12h18" stroke={color} strokeWidth="2"/>
    </svg>
  );
}
function RelVolumeIcon({ size = 12, color = "currentColor" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      style={{display:"inline-block",verticalAlign:"middle",flexShrink:0}}>
      <path d="M3 20h18" stroke={color} strokeWidth="2" strokeLinecap="round"/>
      <rect x="5"  y="13" width="3" height="6" stroke={color} strokeWidth="2"/>
      <rect x="11" y="8"  width="3" height="11" stroke={color} strokeWidth="2"/>
      <rect x="17" y="4"  width="3" height="15" stroke={color} strokeWidth="2"/>
    </svg>
  );
}
function UnusualIcon({ size = 10, color = "currentColor" }) {
  // Pulsing-eye glyph — represents "watching for unusual activity"
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      style={{display:"inline-block",verticalAlign:"middle",flexShrink:0}}>
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z"
        stroke={color} strokeWidth="2"/>
      <circle cx="12" cy="12" r="2.5" fill={color}/>
    </svg>
  );
}

const SIGNAL_TYPE_META = {
  dark_short:  { Icon: DarkPoolIcon,  label: "Dark short" },
  insider_buy: { Icon: InsiderIcon,   label: "Insider buy" },
  rel_volume:  { Icon: RelVolumeIcon, label: "Rel vol" },
};

function UnusualActivityCard({ token, horizon = "6m", profile = "moderate", onNavigate }) {
  const [data,     setData]     = React.useState(null);
  const [loading,  setLoading]  = React.useState(true);
  const [err,      setErr]      = React.useState("");
  const [filter,   setFilter]   = React.useState("all");   // all | dark_short | insider_buy | rel_volume
  const [expanded, setExpanded] = React.useState(false);
  const [showInfo, setShowInfo] = React.useState(false);

  React.useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setLoading(true); setErr("");
    // v8.3.22: auto-retry up to 3× before showing "failed". On cold-load,
    // the universe cache may still be filling, Polygon snapshots may be
    // transiently slow, etc. Backoff: 0.6s, 2s, 5s. User asked: "can
    // there be various auto refreshes until the tables populate and only
    // identify a failure if they in fact fail".
    const attemptLoad = async () => {
      const delays = [0, 600, 2000, 5000];
      let lastErr = null;
      for (let i = 0; i < delays.length; i++) {
        if (cancelled) return;
        if (delays[i] > 0) await new Promise(r => setTimeout(r, delays[i]));
        try {
          const d = await apiFetch(`/unusual-activity?horizon=${horizon}&profile=${profile}`, token);
          if (cancelled) return;
          setData(d); setLoading(false); setErr("");
          return;
        } catch (e) {
          if (e?.name === "AuthError") { setErr(e.message); setLoading(false); return; }
          lastErr = e;
        }
      }
      if (!cancelled) {
        setErr(lastErr?.message || "Failed to load after 4 attempts");
        setLoading(false);
      }
    };
    attemptLoad();
    return () => { cancelled = true; };
  }, [token, horizon, profile]);

  const results = data?.results || [];
  const filtered = filter === "all"
    ? results
    : results.filter(r => (r.signals || []).some(s => s.type === filter));
  const INITIAL = 5;
  const top    = filtered.slice(0, INITIAL);
  const rest   = filtered.slice(INITIAL);

  const FILTER_OPTIONS = [
    { id: "all",         label: "All",         Icon: null },
    { id: "dark_short",  label: "Dark short",  Icon: DarkPoolIcon },
    { id: "insider_buy", label: "Insider buy", Icon: InsiderIcon },
    { id: "rel_volume",  label: "Rel vol",     Icon: RelVolumeIcon },
  ];

  // Composite verdict colour for the indicator dot
  const VERDICT_COLOR = {
    green: C.green,
    amber: "#ffca28",
    red:   C.red,
  };

  const Row = ({ r, isLast }) => (
    <div key={r.ticker}
      onClick={() => onNavigate && onNavigate(r.ticker)}
      style={{
        padding:"10px 0",
        borderBottom: isLast ? "none" : `1px solid ${C.border}`,
        cursor:"pointer",
      }}>
      {/* Row 1: verdict dot + logo + ticker + name + price */}
      <div style={{display:"flex", alignItems:"center", gap:10}}>
        {/* Traffic-light verdict indicator — derived backend-side from
            signal mix + price direction. Tooltip = the human reason. */}
        <span
          title={r.verdictReason || r.verdict || ""}
          style={{
            width:8, height:8, borderRadius:"50%", flexShrink:0,
            background: VERDICT_COLOR[r.verdict] || C.muted,
            boxShadow: `0 0 6px ${VERDICT_COLOR[r.verdict] || "transparent"}55`,
          }}/>
        <TickerLogo ticker={r.ticker} size={26} />
        <div style={{flex:1, minWidth:0}}>
          <div style={{fontWeight:700, color:C.text, fontSize:14, fontFamily:"monospace"}}>
            {r.ticker}
          </div>
          <div style={{fontSize:11, color:C.muted, whiteSpace:"nowrap",
                       overflow:"hidden", textOverflow:"ellipsis"}}>
            {r.shortName}
          </div>
        </div>
        <div style={{textAlign:"right", fontSize:12, color:C.text, flexShrink:0}}>
          {r.price != null && (
            <div style={{fontFamily:"monospace"}}>${Number(r.price).toFixed(2)}</div>
          )}
          {r.changePct != null && (
            <div style={{color: r.changePct >= 0 ? C.green : C.red, fontSize:11,
                         fontFamily:"monospace"}}>
              {r.changePct >= 0 ? "+" : ""}{Number(r.changePct).toFixed(2)}%
            </div>
          )}
        </div>
      </div>
      {/* Row 2: signal pills — full width, wrap when many */}
      {(r.signals || []).length > 0 && (
        <div style={{display:"flex", flexWrap:"wrap", gap:6, marginTop:6, marginLeft:54}}>
          {r.signals.map((s, i) => {
            const meta = SIGNAL_TYPE_META[s.type];
            return (
              <span key={i} style={{
                display:"inline-flex", alignItems:"center", gap:4,
                fontSize:10, padding:"3px 8px", borderRadius:10,
                background:"rgba(0,212,255,0.10)", color:C.accent,
                border:`1px solid rgba(0,212,255,0.30)`,
              }}>
                {meta && <meta.Icon size={10} color={C.accent} />}
                {s.label}
              </span>
            );
          })}
          {r.verdictReason && (
            <span style={{fontSize:10, color:C.muted, marginLeft:2, alignSelf:"center"}}>
              · {r.verdictReason}
            </span>
          )}
        </div>
      )}
    </div>
  );

  return (
    <>
      {/* Info modal — matches ProfileDivergenceCard style (solid surface, NOT transparent) */}
      {showInfo && (
        <div onClick={() => setShowInfo(false)}
          style={{
            position:"fixed", inset:0, zIndex:1000,
            background:"rgba(0,0,0,0.72)",
            display:"flex", alignItems:"center", justifyContent:"center",
            padding:"0 20px", boxSizing:"border-box",
          }}>
          <div onClick={e => e.stopPropagation()}
            style={{
              background:C.surface, border:`1px solid ${C.border}`,
              borderRadius:16, padding:"22px 20px",
              width:"100%", maxWidth:380, boxSizing:"border-box",
              maxHeight:"calc(100vh - 120px)", overflowY:"auto",
              WebkitOverflowScrolling:"touch",
            }}>
            <div style={{display:"flex", justifyContent:"space-between",
                         alignItems:"center", marginBottom:14}}>
              <div style={{display:"flex", alignItems:"center", gap:8}}>
                <UnusualIcon size={12} color={C.muted}/>
                <span style={{fontWeight:700, fontSize:10, color:C.muted,
                              letterSpacing:"0.1em", textTransform:"uppercase"}}>
                  Unusual Activity
                </span>
              </div>
              <button onClick={() => setShowInfo(false)}
                style={{background:"none", border:"none", color:C.muted,
                        fontSize:16, cursor:"pointer", padding:"0 2px", lineHeight:1}}
              >✕</button>
            </div>
            <p style={{fontSize:13, color:"rgba(255,255,255,0.75)",
                       lineHeight:1.65, margin:"0 0 12px"}}>
              Three institutional-flow signals derived from Polygon Starter-tier data,
              combined into a single ranked list. Tickers are scored when one or more
              signal fires above its threshold.
            </p>
            <div style={{fontSize:12, color:"rgba(255,255,255,0.65)", lineHeight:1.7,
                         paddingLeft:12, borderLeft:`2px solid rgba(0,212,255,0.25)`}}>
              <div style={{marginBottom:8}}>
                <span style={{color:C.accent, fontWeight:700}}>
                  <DarkPoolIcon size={11} color={C.accent}/> Dark short
                </span>
                {" — "}Off-exchange short volume (FINRA ATS report). Z-score ≥ 1σ above the 20-day
                baseline. Proxy for institutional shorting through dark pools.
              </div>
              <div style={{marginBottom:8}}>
                <span style={{color:C.accent, fontWeight:700}}>
                  <InsiderIcon size={11} color={C.accent}/> Insider buy
                </span>
                {" — "}SEC Form 4 open-market purchases (code 'P') by officers/directors/10%-owners
                in the last 30 days. Multiple buys or a single officer purchase ≥ $250k triggers.
              </div>
              <div>
                <span style={{color:C.accent, fontWeight:700}}>
                  <RelVolumeIcon size={11} color={C.accent}/> Rel vol
                </span>
                {" — "}Today's volume vs 20-day average from Polygon snapshot. Flagged when
                ≥ 1.5× typical volume.
              </div>
            </div>
            <p style={{fontSize:12, fontWeight:700, color:C.text,
                       margin:"16px 0 8px"}}>Verdict dot</p>
            <div style={{fontSize:12, color:"rgba(255,255,255,0.65)", lineHeight:1.7}}>
              <div>
                <span style={{display:"inline-block", width:8, height:8, borderRadius:"50%",
                              background:C.green, marginRight:6, verticalAlign:"middle",
                              boxShadow:`0 0 6px ${C.green}55`}}/>
                <strong style={{color:C.green}}>Green</strong> — insider buys, not contradicted
                by heavy dark-pool shorting. Constructive institutional signal.
              </div>
              <div>
                <span style={{display:"inline-block", width:8, height:8, borderRadius:"50%",
                              background:"#ffca28", marginRight:6, verticalAlign:"middle",
                              boxShadow:`0 0 6px #ffca2855`}}/>
                <strong style={{color:"#ffca28"}}>Amber</strong> — mixed or informational
                (elevated volume only, or insiders + shorts simultaneously).
              </div>
              <div>
                <span style={{display:"inline-block", width:8, height:8, borderRadius:"50%",
                              background:C.red, marginRight:6, verticalAlign:"middle",
                              boxShadow:`0 0 6px ${C.red}55`}}/>
                <strong style={{color:C.red}}>Red</strong> — heavy off-exchange shorting
                (z≥1.5σ), no insider buys, today's price is down. Bearish institutional flow.
              </div>
            </div>

            {/* Statistical Z-score definition (v8.3.25) — explains how the
                dark-short signal scoring works. NOT to be confused with
                Altman Z-Score (bankruptcy predictor) which uses different
                thresholds and is a separate concept entirely. */}
            <p style={{fontSize:12, fontWeight:700, color:C.text,
                       margin:"16px 0 8px"}}>What does the z-score mean?</p>
            <div style={{fontSize:12, color:"rgba(255,255,255,0.65)", lineHeight:1.7}}>
              <div style={{marginBottom:8}}>
                The z-score on the dark-short signal is a <strong style={{color:C.text}}>
                statistical measure</strong> of how unusual today's off-exchange short volume
                is compared with the prior 20-day baseline. Higher = more anomalous.
              </div>
              <div style={{fontSize:11, color:"rgba(255,255,255,0.55)", lineHeight:1.65,
                           paddingLeft:10, borderLeft:`2px solid rgba(0,212,255,0.25)`}}>
                <div><span style={{color:C.green, fontWeight:700}}>z &lt; 1.5</span> — Normal
                short-volume activity (no flag)</div>
                <div><span style={{color:"#ffca28", fontWeight:700}}>z 1.5–2.0</span> — Elevated
                off-exchange shorting (amber signal)</div>
                <div><span style={{color:C.red, fontWeight:700}}>z ≥ 2.0</span> — Heavy
                shorting (red verdict trigger when not contradicted by insider buys)</div>
              </div>
              <p style={{fontSize:10, color:C.muted, lineHeight:1.55, marginTop:8}}>
                Not the same as Altman Z-Score (a bankruptcy-risk metric from corporate
                finance with different thresholds). The dark-short z here is a moving-window
                anomaly detector applied to FINRA-reported short volume.
              </p>
            </div>
          </div>
        </div>
      )}

      <div style={{background:C.surface, borderRadius:14, padding:"12px 14px",
                   border:`1px solid ${C.border}`, marginBottom:14}}>
        {/* Header — matches ProfileDivergenceCard exactly */}
        <div style={{display:"flex", justifyContent:"space-between",
                     alignItems:"center", marginBottom:10}}>
          <div style={{display:"flex", alignItems:"center", gap:6}}>
            <UnusualIcon size={10} color={C.muted}/>
            <span style={{fontSize:10, fontWeight:700, color:C.muted,
                          letterSpacing:"0.1em", textTransform:"uppercase"}}>
              Unusual Activity
            </span>
            <button
              onClick={e => { e.stopPropagation(); setShowInfo(true); }}
              style={{
                width:14, height:14, borderRadius:"50%",
                border:"1px solid rgba(255,255,255,0.2)",
                background:"rgba(255,255,255,0.06)",
                color:"rgba(255,255,255,0.5)",
                fontSize:9, fontWeight:700, cursor:"pointer", fontFamily:"inherit",
                display:"inline-flex", alignItems:"center", justifyContent:"center",
                flexShrink:0, lineHeight:1, padding:0,
              }}>i</button>
          </div>
          {!loading && filtered.length > 0 && (
            <span style={{fontSize:9, color:C.muted, fontFamily:"inherit"}}>
              {filtered.length} signal{filtered.length === 1 ? "" : "s"}
            </span>
          )}
        </div>

        {/* Filter pills — SVG icons; share one row evenly so "Rel vol" never wraps */}
        <div style={{display:"flex", gap:5, marginBottom:10}}>
          {FILTER_OPTIONS.map(p => {
            const active = filter === p.id;
            const color = active ? C.accent : C.muted;
            return (
              <button key={p.id} onClick={() => setFilter(p.id)}
                style={{
                  flex:1, minWidth:0,
                  display:"inline-flex", alignItems:"center", justifyContent:"center", gap:4,
                  padding:"5px 4px", borderRadius:14, fontSize:10, whiteSpace:"nowrap",
                  border:`1px solid ${active ? C.accent : C.border}`,
                  background: active ? "rgba(0,212,255,0.12)" : "transparent",
                  color, cursor:"pointer", fontFamily:"inherit",
                }}>
                {p.Icon && <p.Icon size={10} color={color} />}
                {p.label}
              </button>
            );
          })}
        </div>

        {loading && (
          <div style={{color:C.muted, fontSize:13, padding:"12px 0", textAlign:"center"}}>
            Scanning for unusual activity…
          </div>
        )}
        {err && (
          <div style={{color:"#ff6b6b", fontSize:12, padding:"12px 0"}}>{err}</div>
        )}

        {!loading && !err && filtered.length === 0 && (
          <div style={{color:C.muted, fontSize:12, padding:"12px 0", textAlign:"center"}}>
            No unusual activity flagged. Try changing the filter.
          </div>
        )}

        {top.map((r, i) => (
          <Row key={r.ticker} r={r}
               isLast={!expanded ? (i === top.length - 1 && rest.length === 0)
                                : false}/>
        ))}
        {expanded && rest.map((r, i) => (
          <Row key={r.ticker} r={r} isLast={i === rest.length - 1}/>
        ))}

        {rest.length > 0 && (
          <button
            onClick={() => setExpanded(p => !p)}
            style={{
              background:"none", border:"none", cursor:"pointer",
              color:C.muted, fontSize:10, fontFamily:"inherit",
              padding:"8px 0 2px", width:"100%", textAlign:"center",
              letterSpacing:"0.04em",
            }}>
            {expanded ? "▲ less" : `▼ +${rest.length} more`}
          </button>
        )}
      </div>
    </>
  );
}


function GapScannerCard({ token, session, horizon = "6m", profile = "moderate",
                          onNavigate, onAlertsChanged }) {
  const [allGappers, setAllGappers] = React.useState(null); // full unfiltered list (≥1%)
  const [loading,    setLoading]    = React.useState(true);
  const [loadErr,    setLoadErr]    = React.useState("");
  // minGap also persists across navigation (v8.3.15) so failing-then-back
  // doesn't reset the user's gap threshold.
  const _GAP_MINGAP_KEY = "gp_gap_mingap_v1";
  const [minGap, setMinGap] = React.useState(() => {
    try {
      const raw = parseFloat(localStorage.getItem(_GAP_MINGAP_KEY));
      if (Number.isFinite(raw) && raw >= 0 && raw <= 20) return raw;
    } catch (_) {}
    return 2;
  });
  React.useEffect(() => {
    try { localStorage.setItem(_GAP_MINGAP_KEY, String(minGap)); } catch (_) {}
  }, [minGap]);
  const [expanded,     setExpanded]     = React.useState(true);
  const [rowsExpanded, setRowsExpanded] = React.useState(false); // show all universe rows beyond first 20
  const [tier2Expanded, setTier2Expanded] = React.useState(false); // non-universe notable gaps
  const [lastSession, setLastSession] = React.useState(null);
  // Live filter state — applied client-side via the GapScannerFilters popover.
  // v8.3.15: persist to localStorage so navigating to stock detail and back
  // (or having stock detail fail and return) doesn't wipe the user's filter.
  // User report: clicking a ticker that failed reset the scanner to no
  // filter, forcing them to re-dig for the same name. Filters now survive
  // unmount/remount AND full page reloads.
  const GAP_FILTERS_KEY = "gp_gap_filters_v1";
  const [liveFilters, setLiveFilters] = React.useState(() => {
    try {
      const raw = localStorage.getItem(GAP_FILTERS_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        // Defensive shape check — bail to defaults if persisted data is corrupt
        if (parsed && Array.isArray(parsed.sectors)) {
          return { sectors: parsed.sectors || [],
                   rsi: parsed.rsi ?? null,
                   score: parsed.score ?? null };
        }
      }
    } catch (_) { /* corrupt JSON — use defaults */ }
    return { sectors: [], rsi: null, score: null };
  });
  // Persist whenever filters change
  React.useEffect(() => {
    try {
      localStorage.setItem(GAP_FILTERS_KEY, JSON.stringify(liveFilters));
    } catch (_) {}
  }, [liveFilters]);

  // Track whether we have data so we can use background mode on re-fetches.
  // Using a ref (not state) means no extra renders and the value is always
  // current when the load effect fires.
  const hasDataRef = React.useRef(false);

  // Always fetch at 1% so client-side filter is instant with no extra API calls.
  // horizon/profile plumb the viewer's settings through to the backend so
  // swing/LT scores come from the SAME universe cache entry the watchlist
  // and discovery screens use — scores now match across screens.
  //
  // v8.3.22: auto-retry up to 4 attempts (0s/0.6s/2s/5s backoff) on first
  // load before showing "Failed". User asked: "when the app is first
  // loading these appear as failed, can there be various auto refreshes
  // until the tables populate and only identify a failure if they in
  // fact fail". Background refreshes still use a single attempt (silent —
  // the existing card stays mounted with its prior data).
  const load = React.useCallback(async (isBackground = false) => {
    if (!isBackground) setLoading(true);
    // v8.4.241: only clear the error on a FOREGROUND load. A background refresh that
    // cleared it here and then returned early on an empty result left the card with
    // loadErr="" + allGappers=null → the `return null` guard below hid the whole card
    // (the "fails then disappears" the user saw). Background keeps the current state.
    if (!isBackground) setLoadErr("");
    const delays = isBackground ? [0] : [0, 600, 2000, 5000];
    let lastErr = null;
    for (let i = 0; i < delays.length; i++) {
      if (delays[i] > 0) await new Promise(r => setTimeout(r, delays[i]));
      try {
        // min_gap=0 → backend returns the full universe with gap data so the
        // user can apply secondary filters (sectors / RSI / score) over the
        // entire cached universe, not just movers. Critical on closed days
        // when most stocks moved <1% on Friday.
        const d = await apiFetch(
          `/gap-scanner?min_gap=0&horizon=${horizon}&profile=${profile}`, token);
        // v8.4.240: with min_gap=0 a HEALTHY scan returns the whole universe
        // (hundreds of rows), so an empty list means the backend scan FAILED
        // (open-bell Polygon snapshot empty / rate-limited), not "no gappers".
        // Treat empty as a transient miss: retry (foreground) or keep the prior
        // populated card (background) — never blank good data, which is what made
        // the scanner "populate or vanish on retry" through the day.
        const got = Array.isArray(d?.gappers) ? d.gappers.length : 0;
        if (got === 0) {
          lastErr = new Error("Gap scan returned empty — retrying");
          if (isBackground) return;   // keep existing data; next cycle retries
          continue;                   // foreground: fall through to next backoff
        }
        setAllGappers(d);
        setLastSession(d?.session);
        hasDataRef.current = true;
        setLoadErr("");   // v8.4.241: a recovering (bg or fg) load clears a stale error
        if (!isBackground) setLoading(false);
        return;
      } catch (e) {
        if (e?.name === "AuthError") {
          if (!isBackground) { setLoadErr(e.message); setLoading(false); }
          return;
        }
        lastErr = e;
      }
    }
    if (!isBackground) {
      // Only surface an error if we have nothing to show — if a prior scan
      // populated the card, keep it rather than flipping to a failure screen.
      if (hasDataRef.current) setLoading(false);
      else { setLoadErr(lastErr?.message || "Failed to load gap data after 4 attempts"); setLoading(false); }
    }
  }, [token, horizon, profile]);

  React.useEffect(() => {
    // Use background mode if we already have results so the list stays mounted
    // and TickerLogo components preserve their "ok" state (no logo flash).
    // First ever load uses foreground so the user sees the spinner instead of
    // an empty card.
    load(hasDataRef.current);
    // Auto-refresh during active market sessions; skip during closed hours (data doesn't change)
    const active = session === "pre" || session === "post" || session === "open";
    if (!active) return;
    const iv = setInterval(() => load(true), 2 * 60 * 1000); // background=true → no spinner
    return () => clearInterval(iv);
  }, [load, session]);

  // Client-side filter — instant, no API call. Layered filters:
  //   1. inUniverse — Tier 1 only (scored tickers with RSI/swing/LT data)
  //   2. minGap (existing pill row)
  //   3. liveFilters.sectors  — match-any if non-empty
  //   4. liveFilters.rsi      — band membership
  //   5. liveFilters.score    — swing/lt buy gate (≥ 70, mirroring ScoreDonut)
  // v8.4.87: universe-only filter added; non-universe tickers moved to tier2Gappers.
  // Backward-compat: if backend hasn't deployed inUniverse yet (undefined),
  // treat as universe so existing rows still appear.
  const displayed = React.useMemo(() => {
    if (!allGappers?.gappers) return [];
    const { sectors, rsi: rsiBand, score: scoreGate } = liveFilters;
    const BUY = 70;
    return allGappers.gappers.filter(g => {
      if (g.inUniverse === false) return false; // non-universe → Tier 2 only
      if (Math.abs(g.gap_pct) < minGap) return false;
      if (sectors.length > 0) {
        const sec = g.sector || "Other";
        if (!sectors.includes(sec)) return false;
      }
      if (rsiBand) {
        const r = Number(g.rsi);
        if (!isFinite(r)) return false;
        if (rsiBand === "oversold"   && !(r < 30))                 return false;
        if (rsiBand === "neutral"    && !(r >= 45 && r <= 55))     return false;
        if (rsiBand === "overbought" && !(r > 70))                 return false;
      }
      if (scoreGate) {
        const sw = Number(g.swingScore) || 0;
        const lt = Number(g.ltScore)    || 0;
        if (scoreGate === "swing_buy"  && !(sw >= BUY))             return false;
        if (scoreGate === "lt_buy"     && !(lt >= BUY))             return false;
        if (scoreGate === "either_buy" && !(sw >= BUY || lt >= BUY))return false;
        if (scoreGate === "both_buy"   && !(sw >= BUY && lt >= BUY))return false;
      }
      return true;
    });
  }, [allGappers, minGap, liveFilters]);

  // Tier 2 — non-universe gappers filtered to meaningful movers only.
  // Quality floor: price ≥ $10 + volume ≥ 200K to filter out micro/nano-cap
  // wild swings. Sorted by absolute gap % descending, capped at 20.
  // minGap still applies (respects user's gap threshold), but no score/RSI/sector filters.
  const tier2Gappers = React.useMemo(() => {
    if (!allGappers?.gappers) return [];
    return allGappers.gappers
      .filter(g =>
        g.inUniverse === false &&
        Math.abs(g.gap_pct) >= Math.max(minGap, 1) &&
        (g.current  || 0) >= 10 &&
        (g.volume   || 0) >= 200000
      )
      .sort((a, b) => Math.abs(b.gap_pct) - Math.abs(a.gap_pct))
      .slice(0, 20);
  }, [allGappers, minGap]);

  const sessionLabel = {
    pre:    "🌅 Pre-Market",
    open:   "📈 Market Open",
    post:   "🌙 Post-Market",
    closed: "💤 Market Closed",
  }[lastSession || session] || "—";

  const sessionColor = {
    pre: "#ffca28", open: C.green, post: "#ab47bc", closed: C.muted
  }[lastSession || session] || C.muted;

  if (!allGappers && !loading && !loadErr) return null;

  return (
    <div style={{background:C.surface,borderRadius:14,padding:"12px 14px",
      border:`1px solid ${C.border}`,marginBottom:14}}>
      {/* Header — own line, matches Market Pulse subheading style */}
      <div style={{marginBottom:10}}>
        {/* Title row */}
        <div style={{display:"flex",justifyContent:"space-between",
          alignItems:"center",marginBottom:6}}>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <div style={{fontSize:10,fontWeight:700,color:C.muted,
              letterSpacing:"0.1em",textTransform:"uppercase"}}>
              {/* ⚡ rendered as a monochromatic text glyph, no emoji color */}
              <span style={{fontStyle:"normal",opacity:0.7}}>↯</span> Gap Scanner
            </div>
            <span style={{fontSize:9,color:sessionColor,fontWeight:600}}>
              {sessionLabel}
            </span>
            <InfoTip text="Scans the universe comparing pre/post-market price against prior close. Ranked by gap %. Use filter buttons to set minimum threshold. Refreshes every 2 min during active sessions." />
          </div>
          <button onClick={()=>setExpanded(e=>!e)} style={{
            background:"none",border:"none",color:C.muted,cursor:"pointer",
            fontSize:12,fontFamily:"inherit",padding:"0 2px",
          }}>{expanded?"▲":"▼"}</button>
        </div>
        {/* Filter + count row. "All" pill (minGap=0) = no client-side gap
            filter applied — useful when the user wants to scan the whole
            universe and rely on sector / RSI / score filters instead. */}
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          <div style={{display:"flex",gap:3}}>
            {[
              {v:0, l:"All"},
              {v:1, l:"1%"},
              {v:2, l:"2%"},
              {v:3, l:"3%"},
              {v:5, l:"5%"},
            ].map(({v,l}) => (
              <button key={v} onClick={()=>setMinGap(v)} style={{
                padding:"2px 7px",borderRadius:6,border:"none",cursor:"pointer",
                fontFamily:"inherit",fontSize:9,fontWeight:700,
                background:minGap===v?"rgba(0,212,255,0.2)":"rgba(255,255,255,0.06)",
                color:minGap===v?C.accent:C.muted,
              }}>{l}</button>
            ))}
          </div>
          {allGappers && (
            <span style={{fontSize:9,color:C.muted,flex:1,minWidth:0,
              overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
              {displayed.length} {minGap===0 ? "stocks" : `gap${displayed.length!==1?"s":""} ≥${minGap}%`}
              {allGappers.scanned ? ` · ${allGappers.scanned} scanned` : ""}
            </span>
          )}
          <GapScannerFilters
            filters={liveFilters}
            setFilters={setLiveFilters}
            displayedCount={displayed.length}
            totalCount={allGappers?.gappers?.length || 0}
          />
          <GapAlertToggle token={token} minGap={minGap} onAlertsChanged={onAlertsChanged}/>
        </div>
      </div>

      {/* Gap list */}
      {expanded && (
        loading ? (
          <div style={{height:60,display:"flex",alignItems:"center",
            justifyContent:"center",color:C.muted,fontSize:12}}>
            <div style={{width:16,height:16,border:`2px solid rgba(0,212,255,0.2)`,
              borderTopColor:C.accent,borderRadius:"50%",
              animation:"spin 0.8s linear infinite",marginRight:8}}/>
            Scanning gaps…
          </div>
        ) : loadErr ? (
          <div style={{textAlign:"center",padding:"12px 0",fontSize:12,color:"#ff7043"}}>
            ⚠ {loadErr} —{" "}
            <span onClick={load} style={{color:C.accent,cursor:"pointer",textDecoration:"underline"}}>
              retry
            </span>
          </div>
        ) : !displayed.length ? (
          <div style={{textAlign:"center",padding:"12px 0",fontSize:12,color:C.muted}}>
            {session === "closed"
              ? "Markets closed — showing last session's gap data"
              : `No stocks gapping ≥${minGap}% right now`}
          </div>
        ) : (
          <div>
            {/* Column headers — show "1D Rtn" when market is closed (data = last session return) */}
            {(lastSession||session) === "closed" && (
              <div style={{fontSize:9,color:C.muted,textAlign:"center",marginBottom:4,fontStyle:"italic"}}>
                Δ% = last session close-to-close change
              </div>
            )}
            <div style={{display:"flex",alignItems:"center",gap:4,
              marginBottom:4,padding:"0 2px"}}>
              <div style={{flex:"0 0 24px"}}/>
              <div style={{flex:"0 0 50px",fontSize:9,color:C.muted,fontWeight:700,
                letterSpacing:"0.06em",textTransform:"uppercase"}}>Ticker</div>
              <div style={{flex:"0 0 46px",fontSize:9,color:C.muted,fontWeight:700,
                letterSpacing:"0.06em",textTransform:"uppercase",textAlign:"right"}}>
                {(lastSession||session)==="closed" ? "1D Rtn" : "Gap"}
              </div>
              <div style={{flex:"0 0 58px",fontSize:9,color:C.muted,fontWeight:700,
                letterSpacing:"0.06em",textTransform:"uppercase",textAlign:"right"}}>Price</div>
              {/* Combined Swing+LT column — SwingIcon/LTIcon muted numerics
                  stacked vertically (matches the portfolio optimizer score
                  badges).  Single column so Vol stays on screen on mobile. */}
              <div style={{flex:"0 0 44px",fontSize:9,color:C.muted,fontWeight:700,
                letterSpacing:"0.06em",textTransform:"uppercase",textAlign:"right"}}>Score</div>
              <div style={{flex:"0 0 32px",fontSize:9,color:C.muted,fontWeight:700,
                letterSpacing:"0.06em",textTransform:"uppercase",textAlign:"right"}}>RSI</div>
              <div style={{flex:"0 0 42px",fontSize:9,color:C.muted,fontWeight:700,
                letterSpacing:"0.06em",textTransform:"uppercase",textAlign:"right"}}>Vol</div>
            </div>
            {(() => {
              const INITIAL = 20;
              const visible = rowsExpanded ? displayed : displayed.slice(0, INITIAL);
              const hiddenCount = Math.max(0, displayed.length - INITIAL);
              // Format volume: 1.2M, 450K, etc
              const fmtVol = v => {
                if (!v) return "—";
                if (v >= 1e6) return `${(v/1e6).toFixed(1)}M`;
                if (v >= 1e3) return `${(v/1e3).toFixed(0)}K`;
                return String(v);
              };
              return (<>
                {visible.map((g, i) => {
                  const isUp  = g.gap_pct > 0;
                  const color = isUp ? C.green : C.red;
                  return (
                    <div key={g.ticker}
                      onClick={()=>onNavigate&&onNavigate(g.ticker)}
                      style={{display:"flex",alignItems:"center",gap:4,
                      padding:"6px 2px",cursor:onNavigate?"pointer":"default",
                      borderBottom:i<visible.length-1?`1px solid rgba(255,255,255,0.04)`:"none",
                      borderRadius:6,transition:"background 0.15s"}}
                  onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.03)"}
                  onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                  {/* Logo */}
                  <TickerLogo ticker={g.ticker} size={22} style={{flex:"0 0 24px"}}/>
                  {/* Ticker + stacked H/L day prices ($) — user prefers absolute
                      values over % offsets for at-a-glance reference. Narrower
                      50px column keeps row compact alongside gap%. */}
                  <div style={{flex:"0 0 50px",minWidth:0,overflow:"hidden"}}>
                    <div style={{fontSize:12,fontWeight:700,color:C.text,
                      fontFamily:"monospace",lineHeight:1,
                      overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{g.ticker}</div>
                    {g.low != null && g.high != null && g.low > 0 && (
                      <div style={{fontSize:8,color:C.muted,marginTop:2,
                        fontFamily:"monospace",lineHeight:1.2,
                        overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                        <div>H ${g.high}</div>
                        <div>L ${g.low}</div>
                      </div>
                    )}
                  </div>
                  {/* Gap % */}
                  <div style={{flex:"0 0 46px",textAlign:"right"}}>
                    <span style={{fontSize:13,fontWeight:800,fontFamily:"monospace",color}}>
                      {isUp?"+":""}{g.gap_pct}%
                    </span>
                  </div>
                  {/* Current price */}
                  <div style={{flex:"0 0 58px",textAlign:"right"}}>
                    <div style={{fontSize:12,fontFamily:"monospace",
                      fontWeight:600,color:C.text,whiteSpace:"nowrap"}}>${g.current}</div>
                    <div style={{fontSize:9,color:C.muted,whiteSpace:"nowrap"}}>
                      vs ${g.prev_close}
                    </div>
                  </div>
                  {/* Swing + LT scores stacked — same SwingIcon/LTIcon glyphs
                      the portfolio optimizer uses, at a smaller scale so
                      they fit one column.  Shows "—" when the universe
                      cache hasn't populated the score yet. */}
                  <div style={{flex:"0 0 44px",display:"flex",flexDirection:"column",
                    gap:2,alignItems:"flex-end",lineHeight:1}}>
                    <div style={{display:"flex",alignItems:"center",gap:3}}>
                      <SwingIcon size={10} color={C.muted}/>
                      <span style={{fontSize:10,fontFamily:"monospace",fontWeight:700,
                        color: g.swingScore >= 70 ? C.green
                             : g.swingScore >= 45 ? C.muted
                             : g.swingScore > 0   ? "rgba(255,82,82,0.75)"
                             : "rgba(255,255,255,0.2)",
                        minWidth:16,textAlign:"right"}}>
                        {g.swingScore > 0 ? Math.round(g.swingScore) : "—"}
                      </span>
                    </div>
                    <div style={{display:"flex",alignItems:"center",gap:3}}>
                      <LTIcon size={10} color={C.muted}/>
                      <span style={{fontSize:10,fontFamily:"monospace",fontWeight:700,
                        color: g.ltScore >= 70 ? C.green
                             : g.ltScore >= 45 ? C.muted
                             : g.ltScore > 0   ? "rgba(255,82,82,0.75)"
                             : "rgba(255,255,255,0.2)",
                        minWidth:16,textAlign:"right"}}>
                        {g.ltScore > 0 ? Math.round(g.ltScore) : "—"}
                      </span>
                    </div>
                  </div>
                  {/* RSI — fixed width to keep columns aligned */}
                  <div style={{flex:"0 0 32px",textAlign:"right"}}>
                    {g.rsi != null ? (
                      <span style={{
                        fontSize:9,fontWeight:700,fontFamily:"monospace",
                        color: g.rsi < 30 ? C.green : g.rsi > 70 ? "#ff5252" : C.muted,
                      }}>
                        {g.rsi}
                        {g.rsi < 30 ? " ↑" : g.rsi > 70 ? " ↓" : ""}
                      </span>
                    ) : <span style={{fontSize:9,color:"rgba(255,255,255,0.12)"}}>—</span>}
                  </div>
                  {/* Volume */}
                  <div style={{flex:"0 0 42px",textAlign:"right"}}>
                    <span style={{fontSize:10,fontFamily:"monospace",
                      color:C.muted}}>{fmtVol(g.volume)}</span>
                  </div>
                  {onNavigate && (
                    <span style={{fontSize:11,color:"rgba(255,255,255,0.2)",
                      flexShrink:0}}>›</span>
                  )}
                </div>
              );
            })}
            {hiddenCount > 0 && (
              <button
                onClick={() => setRowsExpanded(p => !p)}
                style={{background:"none", border:"none", cursor:"pointer",
                  color:C.muted, fontSize:10, fontFamily:"inherit",
                  padding:"8px 0 2px", width:"100%", textAlign:"center",
                  letterSpacing:"0.04em"}}>
                {rowsExpanded ? "▲ less" : `▼ +${hiddenCount} more`}
              </button>
            )}
            </>);
            })()}

            {/* ── Tier 2: non-universe notable gaps ────────────────────────
                Collapsed by default. Price ≥$10 + Vol ≥200K quality floor.
                No scores / RSI / sector — informational only. v8.4.87 */}
            {tier2Gappers.length > 0 && (
              <div style={{marginTop:10, borderTop:`1px solid rgba(255,255,255,0.05)`, paddingTop:8}}>
                <button
                  onClick={() => setTier2Expanded(e => !e)}
                  style={{background:"none", border:"none", cursor:"pointer",
                    width:"100%", textAlign:"left", padding:"0 0 6px",
                    display:"flex", alignItems:"center", gap:6, fontFamily:"inherit"}}>
                  <span style={{fontSize:9, color:C.muted, fontWeight:700,
                    letterSpacing:"0.08em", textTransform:"uppercase"}}>
                    {tier2Expanded ? "▲" : "▼"} Other Notable Gaps ({tier2Gappers.length})
                  </span>
                  <span style={{fontSize:9, color:"rgba(255,255,255,0.25)", fontWeight:400}}>
                    · ≥$10 · ≥200K vol · no scores
                  </span>
                </button>
                {tier2Expanded && (
                  <div>
                    {tier2Gappers.map((g, i) => {
                      const isUp  = g.gap_pct > 0;
                      const color = isUp ? C.green : C.red;
                      const fmtV  = v => {
                        if (!v) return "—";
                        if (v >= 1e6) return `${(v/1e6).toFixed(1)}M`;
                        if (v >= 1e3) return `${(v/1e3).toFixed(0)}K`;
                        return String(v);
                      };
                      return (
                        <div key={g.ticker}
                          onClick={() => onNavigate && onNavigate(g.ticker)}
                          style={{display:"flex", alignItems:"center", gap:4,
                            padding:"5px 2px", cursor:onNavigate?"pointer":"default",
                            borderBottom:i<tier2Gappers.length-1
                              ?`1px solid rgba(255,255,255,0.03)`:"none",
                            opacity:0.65, transition:"opacity 0.15s"}}
                          onMouseEnter={e=>e.currentTarget.style.opacity="1"}
                          onMouseLeave={e=>e.currentTarget.style.opacity="0.65"}>
                          <TickerLogo ticker={g.ticker} size={18} style={{flex:"0 0 22px"}}/>
                          <div style={{flex:"0 0 54px", minWidth:0, overflow:"hidden"}}>
                            <div style={{fontSize:11, fontWeight:700, color:C.text,
                              fontFamily:"monospace", overflow:"hidden",
                              textOverflow:"ellipsis", whiteSpace:"nowrap"}}>
                              {g.ticker}
                            </div>
                          </div>
                          <div style={{flex:"0 0 48px", textAlign:"right"}}>
                            <span style={{fontSize:12, fontWeight:800,
                              fontFamily:"monospace", color}}>
                              {isUp?"+":""}{g.gap_pct}%
                            </span>
                          </div>
                          <div style={{flex:1, textAlign:"right"}}>
                            <span style={{fontSize:11, fontFamily:"monospace", color:C.muted}}>
                              ${g.current}
                            </span>
                          </div>
                          <div style={{flex:"0 0 44px", textAlign:"right"}}>
                            <span style={{fontSize:9, fontFamily:"monospace", color:"rgba(255,255,255,0.3)"}}>
                              {fmtV(g.volume)}
                            </span>
                          </div>
                          {onNavigate && (
                            <span style={{fontSize:10, color:"rgba(255,255,255,0.15)",
                              flexShrink:0}}>›</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            <div style={{fontSize:9,color:"rgba(255,255,255,0.15)",
              marginTop:8,textAlign:"right"}}>
              Refreshes every 2 min · {typeof allGappers?.ts === "string" ? allGappers.ts.slice(11,16) : ""} UTC
            </div>
          </div>
        )
      )}
    </div>
  );
}


// Sector ETF Heatmap — live day performance via Finnhub/Yahoo
// Replaces the RS-rating-based heatmap with real-time sector ETF data
// v8.4.309: top-movers bubble chart — bubble SIZE = today's dollar volume (how much
// $ traded), COLOR/intensity = daily % move (green up / red down). A snapshot of
// where the money + momentum is. Sized-circle grid (largest first) — legible on mobile
// without a physics-pack library. Tap a bubble to open the stock.
function _fmtDollarVol(v) {
  if (!v || v <= 0) return "—";
  if (v >= 1e9) return `$${(v/1e9).toFixed(1)}B`;
  if (v >= 1e6) return `$${(v/1e6).toFixed(0)}M`;
  if (v >= 1e3) return `$${(v/1e3).toFixed(0)}K`;
  return `$${Math.round(v)}`;
}
function MoversBubbleChart({ universeData, onSelectTicker, market = "All" }) {
  const items = React.useMemo(() => {
    const arr = Object.values(universeData || {})
      .filter(r => {
        if (!r || !r.ticker || r.changePct == null) return false;
        // v8.4.437: follow the US / Canada / All toggle (Canada = .TO names)
        const isCa = String(r.ticker).toUpperCase().endsWith(".TO");
        if (market === "CA" && !isCa) return false;
        if (market === "US" && isCa) return false;
        // Only REAL movers — drop flat/0% (stale or genuinely unchanged) names.
        if (Math.abs(r.changePct) < 0.5) return false;
        // Liquidity floor so illiquid micro-caps (random big %s) don't dominate.
        // v8.4.315: use dollar volume ONLY when it's a real positive number — a
        // scorer can stamp dollarVolume=0/NaN (volume missing at scoring time), and
        // the old `!= null` check then failed the 3M floor WITHOUT falling back to
        // marketCap, emptying the whole chart (esp. after close / a fresh re-score).
        if (Number.isFinite(r.dollarVolume) && r.dollarVolume > 0) return r.dollarVolume >= 3e6;
        return (r.marketCap || 0) >= 1e9;
      })
      .map(r => ({
        ticker: String(r.ticker), changePct: r.changePct, shortName: r.shortName,
        // dollar volume preferred; fall back to a marketCap proxy until a re-score
        // populates dollarVolume across the universe.
        size: r.dollarVolume || (r.marketCap ? r.marketCap / 1000 : 0),
      }));
    arr.sort((a, b) => Math.abs(b.changePct) - Math.abs(a.changePct)); // biggest movers first
    return arr.slice(0, 50);
  }, [universeData, market]);
  if (items.length < 5) {
    // A region filter that leaves too few movers (e.g. TSX closed for a holiday) should say so, not blank the card.
    return market === "All" ? null : (
      <div style={{padding:"18px 8px",textAlign:"center",fontSize:12,color:C.muted,lineHeight:1.6}}>
        Not enough {market === "CA" ? "Canadian" : "U.S."} movers right now{market === "CA" ? " (the TSX may be closed)" : ""}. Try the All tab.
      </div>
    );
  }
  const sizes = items.map(i => i.size).filter(s => s > 0);
  const minS = Math.min(...sizes), maxS = Math.max(...sizes);
  const scale = (s) => {
    if (!(maxS > minS) || !s) return 58;
    const t = (Math.sqrt(s) - Math.sqrt(minS)) / (Math.sqrt(maxS) - Math.sqrt(minS));
    return Math.round(44 + t * 52); // 44–96px diameter
  };
  const color = (chg) => {
    const a = Math.min(0.85, 0.28 + Math.abs(chg) / 14);
    return chg >= 0 ? `rgba(0,200,110,${a})` : `rgba(255,72,72,${a})`;
  };
  return (
    <div style={{display:"flex",flexWrap:"wrap",gap:6,justifyContent:"center",
      alignItems:"center",padding:"10px 4px"}}>
      {items.map(it => {
        const d = scale(it.size);
        return (
          <button key={it.ticker} onClick={()=>onSelectTicker && onSelectTicker(it.ticker)}
            title={`${it.ticker} ${it.changePct>=0?"+":""}${it.changePct}% · $vol ${_fmtDollarVol(it.size)}`}
            style={{width:d,height:d,borderRadius:"50%",border:"1px solid rgba(255,255,255,0.08)",
              cursor:"pointer",background:color(it.changePct),display:"flex",
              flexDirection:"column",alignItems:"center",justifyContent:"center",
              fontFamily:"inherit",color:"#fff",padding:0,lineHeight:1.1}}>
            <span style={{fontSize:Math.max(9,Math.min(13,d/6)),fontWeight:800,fontFamily:"monospace"}}>{it.ticker}</span>
            <span style={{fontSize:Math.max(8,Math.min(11,d/8)),opacity:0.92}}>
              {it.changePct>=0?"+":""}{Math.round(it.changePct)}%
            </span>
          </button>
        );
      })}
    </div>
  );
}

function SectorHeatmap({ token, universeData, onSectorClick, resetKey, market = "US" }) {
  // v8.4.438 — follows the US / Canada / All toggle: US = Select Sector SPDRs (Polygon), Canada = TSX iShares sector ETFs
  // (8 of 11 sectors; the rest estimated from the Canadian names we track), All = iShares GLOBAL sector ETFs.
  const region = market === "CA" ? "CA" : market === "All" ? "GLOBAL" : "US";
  const [byRegion, setByRegion] = React.useState({});     // region -> { sectors, asof, ts }
  const [loading, setLoading]   = React.useState(false);
  const [activeSector, setActiveSector] = React.useState(null);
  const cur     = byRegion[region] || { sectors: [], asof: null, ts: 0 };
  const sectors = cur.sectors;

  // Load live sector data (cached 5 min server-side)
  const loadSectors = React.useCallback(async () => {
    if (!token) return;
    const have = byRegion[region];
    // Only refetch if >5 min since last fetch
    if (have && have.sectors.length > 0 && Date.now() - have.ts < 5 * 60 * 1000) return;
    setLoading(true);
    try {
      const d = await apiFetch(region === "US" ? "/sector-heatmap" : `/sector-heatmap?region=${region}`, token);
      if (d?.sectors?.length > 0) {
        setByRegion(prev => ({ ...prev, [region]: { sectors: d.sectors, asof: d.asof || null, ts: Date.now() } }));
      }
    } catch(e) {
      // Fallback to universeData RS aggregation if API unavailable
    } finally {
      setLoading(false);
    }
  }, [token, region, byRegion]);

  React.useEffect(() => { loadSectors(); }, [loadSectors]);

  // Reset selection when parent tells us a new list was chosen, or the region changes
  React.useEffect(() => {
    if (resetKey > 0) setActiveSector(null);
  }, [resetKey]);
  React.useEffect(() => { setActiveSector(null); }, [region]);

  // Fallback: compute from universeData RS if no live data
  const displaySectors = React.useMemo(() => {
    if (sectors.length > 0) {
      if (region !== "CA") return sectors;
      // Canada: no iShares sector ETF for Consumer Cyclical / Industrials / Communication Services — estimate those from the
      // average day-change of the Canadian (.TO) names we track in that sector (needs >= 4 names), flagged "est.".
      const have = new Set(sectors.map(x => x.sector));
      const est = [];
      ["Technology","Healthcare","Financials","Energy","Consumer Cyclical","Consumer Defensive","Industrials",
       "Materials","Real Estate","Utilities","Communication Services"].forEach(sec => {
        if (have.has(sec)) return;
        const chgs = Object.values(universeData || {})
          .filter(r => r?.sector === sec && String(r.ticker || "").toUpperCase().endsWith(".TO") && Number.isFinite(r.changePct))
          .map(r => r.changePct);
        if (chgs.length >= 4) est.push({ sector: sec, chg_pct: Math.round(chgs.reduce((a, b) => a + b, 0) / chgs.length * 100) / 100, est: true, n: chgs.length });
      });
      return [...sectors, ...est];
    }
    if (region !== "US") return [];     // the RS fallback below mixes all regions — only valid for the US/default grid
    if (!universeData) return [];
    const sectorRS = {}, sectorCount = {};
    Object.values(universeData).forEach(r => {
      if (!r?.sector || r.sector === "Other" || r.rsRating == null) return;
      sectorRS[r.sector]    = (sectorRS[r.sector]    || 0) + r.rsRating;
      sectorCount[r.sector] = (sectorCount[r.sector] || 0) + 1;
    });
    return Object.entries(sectorRS)
      .filter(([,sum]) => sectorCount[Object.keys(sectorRS).find(k=>sectorRS[k]===sum)] >= 2)
      .map(([sector, sum]) => ({
        sector,
        chg_pct: null,
        rsAvg: Math.round(sum / sectorCount[sector]),
      }))
      .sort((a,b) => b.rsAvg - a.rsAvg);
  }, [sectors, universeData, region]);

  if (displaySectors.length < 3) return null;

  const useLive = sectors.length > 0;
  const regionTag = region === "CA" ? "🇨🇦 Canada sectors" : region === "GLOBAL" ? "🌐 Global sectors" : "🇺🇸 U.S. sectors";
  const asofLabel = cur.asof ? new Date(cur.asof + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" }) : null;
  const hasEst    = displaySectors.some(x => x.est);

  // Color mapping: -3%=deep red, 0%=neutral, +3%=deep green
  const pctToColor = (pct) => {
    if (pct === null) return "rgba(255,255,255,0.12)";
    const clamped = Math.max(-4, Math.min(4, pct));
    if (clamped > 0) {
      const g = Math.round(40 + (clamped / 4) * 190);
      return `rgba(0,${g},80,0.75)`;
    } else {
      const r = Math.round(120 + (Math.abs(clamped) / 4) * 135);
      return `rgba(${r},30,30,0.75)`;
    }
  };

  const textColor = (pct) => {
    if (pct === null) return C.muted;
    return pct > 0 ? "#00e676" : pct < 0 ? "#ff5252" : C.muted;
  };

  return (
    <div style={{background:C.surface,borderRadius:14,padding:"12px 14px",
      border:`1px solid ${C.border}`,marginBottom:14}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
        <div style={{fontSize:10,fontWeight:700,color:C.muted,letterSpacing:"0.1em",
          textTransform:"uppercase"}}>
          Sector Performance
          {useLive && (
            <span style={{fontSize:9,color:C.accent,fontWeight:700,marginLeft:8,
              textTransform:"none",letterSpacing:0}}>
              {regionTag}
            </span>
          )}
          {!useLive && sectors.length === 0 && (
            <span style={{fontSize:8,color:C.muted,fontWeight:400,
              marginLeft:6,textTransform:"none",letterSpacing:0}}>
              · RS est.
            </span>
          )}
        </div>
        {loading && (
          <div style={{width:10,height:10,border:`2px solid rgba(0,212,255,0.2)`,
            borderTopColor:C.accent,borderRadius:"50%",
            animation:"spin 0.8s linear infinite"}}/>
        )}
      </div>
      {/* Treemap-style grid */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:5}}>
        {displaySectors.slice(0,11).map(s => {
          const pct = s.chg_pct ?? null;
          const label = s.sector?.replace("Consumer ","C. ").replace("Communication Services","Comm.");
          return (
            <div key={s.sector}
              onClick={()=>{
                if (!onSectorClick) return;
                const next = activeSector === s.sector ? null : s.sector;
                setActiveSector(next);
                if (next) onSectorClick(s.sector);
              }}
              style={{
                background: pctToColor(pct),
                borderRadius:8,padding:"7px 8px",
                border: activeSector === s.sector
                  ? "1px solid rgba(0,212,255,0.7)"
                  : "1px solid rgba(255,255,255,0.06)",
                cursor: onSectorClick ? "pointer" : "default",
                transform: activeSector === s.sector ? "scale(0.97)" : "scale(1)",
                transition:"transform 0.1s, border 0.15s",
                position:"relative",
              }}>
              {onSectorClick && activeSector === s.sector && (
                <div style={{position:"absolute",top:3,right:4,
                  fontSize:7,color:"rgba(0,212,255,0.8)",fontWeight:700}}>
                  ▼
                </div>
              )}
              <div style={{fontSize:9,color:"rgba(255,255,255,0.7)",
                lineHeight:1.3,marginBottom:3,fontWeight:600,
                paddingRight: onSectorClick && activeSector===s.sector ? 10 : 0}}>
                {label}{s.est && <span style={{opacity:0.6,fontWeight:400}}> est.</span>}
              </div>
              {useLive ? (
                <div style={{fontSize:13,fontWeight:800,fontFamily:"monospace",
                  color:textColor(pct)}}>
                  {pct != null ? `${pct > 0 ? "+" : ""}${pct.toFixed(2)}%` : "—"}
                </div>
              ) : (
                <div style={{fontSize:12,fontWeight:700,fontFamily:"monospace",
                  color:s.rsAvg >= 70 ? C.green : s.rsAvg >= 50 ? "#ffca28" : "#ff7043"}}>
                  RS {s.rsAvg}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div style={{display:"flex",justifyContent:"space-between",
        alignItems:"center",marginTop:8}}>
        {onSectorClick && (
          <div style={{fontSize:9,color:"rgba(255,255,255,0.2)"}}>
            Tap a sector to see top stocks
          </div>
        )}
        {useLive && (
          <div style={{fontSize:9,color:"rgba(255,255,255,0.2)",marginLeft:"auto"}}>
            {region === "CA"
              ? `Via TSX sector ETFs${asofLabel ? ` · as of ${asofLabel}` : ""}${hasEst ? " · est. = avg of tracked names" : ""}`
              : region === "GLOBAL" ? "Via iShares Global sector ETFs · updates every 5 min"
              : "Via U.S. sector ETFs · updates every 5 min"}
          </div>
        )}
      </div>
    </div>
  );
}


// Company description with expand/collapse
function CompanyDescription({ text }) {
  const [expanded, setExpanded] = React.useState(false);
  if (!text) return null;
  const SHORT = 180;
  const isLong = text.length > SHORT;
  const shown  = expanded || !isLong ? text : text.slice(0, SHORT).trimEnd() + "…";
  return (
    <div>
      <p style={{
        fontSize:12, color:"rgba(255,255,255,0.6)", lineHeight:1.7,
        margin:0, transition:"all 0.2s",
      }}>
        {shown}
      </p>
      {isLong && (
        <button
          onClick={() => setExpanded(e => !e)}
          style={{
            marginTop:6, background:"none", border:"none", padding:0,
            fontSize:11, fontWeight:700, color:C.accent, cursor:"pointer",
            fontFamily:"inherit",
          }}>
          {expanded ? "Show less ↑" : "Read more ↓"}
        </button>
      )}
    </div>
  );
}

// RS Rating pill — Relative Strength Rating (1-99, IBD-style)
function RSRatingPill({ rating }) {
  if (rating == null) return null;
  const r = parseInt(rating);
  // Color: 80-99 = strong (green), 60-79 = good (yellow), 40-59 = neutral, <40 = weak (red)
  const color = r >= 80 ? C.green : r >= 60 ? "#ffca28" : r >= 40 ? "#ff7043" : "#ff5252";
  const bg    = r >= 80 ? "rgba(0,230,118,0.1)" : r >= 60 ? "rgba(255,202,40,0.1)"
              : r >= 40 ? "rgba(255,112,67,0.1)" : "rgba(255,82,82,0.1)";
  const label = r >= 80 ? "Leader" : r >= 60 ? "Above avg" : r >= 40 ? "Average" : "Laggard";
  return (
    <span title={`RS Rating ${r}/99 — price momentum vs S&P 500 universe`} style={{
      display:"inline-flex",alignItems:"center",gap:4,
      background:bg, border:`1px solid ${color}44`,
      borderRadius:20, padding:"2px 10px", fontSize:11, fontWeight:700, color}}>
      {r}
      <span style={{fontSize:9,color,opacity:0.8}}>{label}</span>
    </span>
  );
}

// Global InfoTip state — only one tip open at a time
let _infoTipCloseAll = null;

function InfoTip({ text }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    if (!open) return;
    // Register this tip's close function globally
    if (_infoTipCloseAll) _infoTipCloseAll(); // close any other open tip
    const myClose = () => setOpen(false);
    _infoTipCloseAll = myClose;

    // Close on click outside
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        if (_infoTipCloseAll === myClose) _infoTipCloseAll = null;
      }
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("touchstart", handler, {passive:true});
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("touchstart", handler);
    };
  }, [open]);

  return (
    <span ref={ref} style={{position:"relative",display:"inline-flex",alignItems:"center"}}>
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
        style={{
          width:14,height:14,borderRadius:"50%",border:"1px solid rgba(255,255,255,0.2)",
          background:"rgba(255,255,255,0.06)",color:"rgba(255,255,255,0.5)",
          fontSize:9,fontWeight:700,cursor:"pointer",fontFamily:"inherit",
          display:"inline-flex",alignItems:"center",justifyContent:"center",
          flexShrink:0,lineHeight:1,padding:0,
        }}>i</button>
      {open && (() => {
        const rect = ref.current?.getBoundingClientRect() || {top:0,bottom:0,left:0,right:0};
        // tipH is a generous upper-bound estimate used for collision detection only;
        // the actual rendered div is auto-height so text never truncates.
        const tipH = 260;
        const tipW = Math.min(240, window.innerWidth - 24);
        // Horizontal: if the trigger is in the right half of the screen, right-anchor
        // the tooltip so it doesn't spill off the right edge.
        const triggerMid = rect.left + (rect.width / 2);
        const useRightAnchor = triggerMid > window.innerWidth / 2;
        const leftPos = useRightAnchor
          ? Math.max(12, rect.right - tipW)          // right-aligned to trigger
          : Math.min(Math.max(12, rect.left - 16), window.innerWidth - tipW - 12);
        // Vertical: prefer below, fall back to above if not enough room.
        const topBelow = rect.bottom + 8;
        const topAbove = rect.top - tipH - 8;
        const hasRoomBelow = topBelow + tipH + 8 <= window.innerHeight;
        const rawTop = hasRoomBelow ? topBelow : Math.max(8, topAbove);
        const topPos = Math.max(8, Math.min(rawTop, window.innerHeight - tipH - 8));
        return (
          <div style={{
            position:"fixed",
            background:"#1a2035",border:"1px solid rgba(0,212,255,0.3)",
            borderRadius:8,padding:"10px 12px",fontSize:11,color:"rgba(255,255,255,0.85)",
            lineHeight:1.7,width:tipW,zIndex:9999,whiteSpace:"pre-line",
            boxShadow:"0 4px 24px rgba(0,0,0,0.6)",
            pointerEvents:"none",
            left: leftPos,
            top: topPos,
          }}>
            {text}
          </div>
        );
      })()}
    </span>
  );
}


function SwingIcon({ size=14, color="currentColor" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      style={{display:"inline-block",verticalAlign:"middle",flexShrink:0}}>
      <path d="M2 16 Q6 8 10 12 Q14 16 18 6 L22 2M22 2h-5M22 2v5"
        stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function LTIcon({ size=14, color="currentColor" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      style={{display:"inline-block",verticalAlign:"middle",flexShrink:0}}>
      <rect x="3" y="12" width="4" height="9" rx="1" stroke={color} strokeWidth="2"/>
      <rect x="10" y="7" width="4" height="14" rx="1" stroke={color} strokeWidth="2"/>
      <rect x="17" y="3" width="4" height="18" rx="1" stroke={color} strokeWidth="2"/>
    </svg>
  );
}


// Ticker search with Yahoo Finance autocomplete
// Accepts both ticker symbols (AAPL) and company names (Apple)
function TickerSearch({ value, onChange, onSelect, placeholder, size="md", token }) {
  const [suggestions, setSuggestions]  = useState([]);
  const [showDrop,    setShowDrop]     = useState(false);
  const [loading,     setLoading]      = useState(false);
  const debounceRef = React.useRef(null);
  const wrapRef     = React.useRef(null);

  const pad   = size==="sm" ? "6px 10px" : "8px 12px";
  // iOS zooms in on any input with font-size < 16px. Clamp to 16 so the
  // screen stays locked when the user taps the search field on iPhone.
  const fs    = 16;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target))
        setShowDrop(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSuggestions = (q) => {
    if (!q || q.length < 1) { setSuggestions([]); setShowDrop(false); return; }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        // Use backend proxy -- Yahoo Finance blocks direct browser requests (CORS)
        const res  = await fetch(`${API}/search?q=${encodeURIComponent(q)}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json();
        const hits = (data?.results || []).slice(0, 6);
        setSuggestions(hits);
        setShowDrop(hits.length > 0);
      } catch(e) {
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 280);
  };

  const handleChange = (e) => {
    const v = e.target.value.toUpperCase().replace(/[^A-Z0-9.\s]/g, "");
    onChange(v);
    fetchSuggestions(v);
  };

  const handleSelect = (hit) => {
    onChange(hit.ticker);
    setSuggestions([]);
    setShowDrop(false);
    onSelect(hit.ticker, hit.name || hit.ticker);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      setShowDrop(false);
      onSelect(value.trim().toUpperCase().replace(/\s/g,""), "");
    }
    if (e.key === "Escape") setShowDrop(false);
  };

  return (
    <div ref={wrapRef} style={{position:"relative",flex:1}}>
      <div style={{display:"flex",alignItems:"center",gap:7}}>
        {value.length >= 1 && !value.includes(" ") && (
          <TickerLogo ticker={value} size={size==="sm"?20:26}/>
        )}
        <input
          style={{
            flex:1, background:C.card,
            border:`1px solid ${value.length>0?C.accent:C.border}`,
            borderRadius:8, padding:pad, color:C.text,
            fontSize:fs, fontFamily:"monospace", outline:"none",
            textTransform:value.includes(" ")?"none":"uppercase",
            transition:"border-color 0.15s",
          }}
          placeholder={placeholder || "Search ticker or company name…"}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={() => suggestions.length > 0 && setShowDrop(true)}
          maxLength={40}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck="false"
        />
        {loading && (
          <div style={{width:14,height:14,border:`2px solid ${C.accent}`,
            borderTopColor:"transparent",borderRadius:"50%",flexShrink:0,
            animation:"spin 0.7s linear infinite"}}/>
        )}
      </div>

      {/* Dropdown */}
      {showDrop && suggestions.length > 0 && (
        <div style={{
          position:"absolute", top:"100%", left:0, right:0, zIndex:500,
          background:C.card, border:`1px solid ${C.accent}`,
          borderRadius:"0 0 10px 10px", overflow:"hidden",
          boxShadow:"0 8px 24px rgba(0,0,0,0.4)", marginTop:2,
        }}>
          {suggestions.map((hit,i) => (
            <div key={hit.ticker}
              onMouseDown={() => handleSelect(hit)}
              style={{
                display:"flex", alignItems:"center", gap:10,
                padding:"9px 12px", cursor:"pointer",
                background:"transparent",
                borderBottom: i < suggestions.length-1
                  ? `1px solid ${C.border}` : "none",
                transition:"background 0.1s",
              }}
              onMouseEnter={e=>e.currentTarget.style.background="rgba(0,212,255,0.06)"}
              onMouseLeave={e=>e.currentTarget.style.background="transparent"}
            >
              <TickerLogo ticker={hit.ticker} size={24}/>
              <div style={{flex:1,minWidth:0}}>
                <div style={{display:"flex",alignItems:"center",gap:7}}>
                  <span style={{fontSize:13,fontWeight:700,color:C.accent,
                    fontFamily:"monospace"}}>{hit.ticker}</span>
                  <span style={{fontSize:10,color:C.muted,background:C.surface,
                    padding:"1px 6px",borderRadius:4}}>
                    {hit.exchange}
                  </span>
                </div>
                <div style={{fontSize:11,color:C.muted,marginTop:1,
                  overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                  {hit.name}
                </div>
              </div>
              <span style={{fontSize:11,color:C.muted,flexShrink:0}}>
                {hit.type==="ETF"?"ETF":"Stock"}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
// v8.4.278 — compact "extended, consider trimming" chip for list tiles. Backend
// sets row.profitTakeFlag only when a name is overextended AND the market regime
// is weak (the one condition where stretched names historically underperform).
function ExtendedFlag({ size="sm" }) {
  const pad = size === "sm" ? "1px 6px" : "2px 8px";
  const fs  = size === "sm" ? 9 : 10;
  return (
    <span title="Overextended in a weak market regime — historically underperforms over the next month. Consider trimming or waiting for a pullback."
      style={{display:"inline-flex",alignItems:"center",gap:3,borderRadius:12,
        padding:pad,background:"rgba(245,158,11,0.15)",
        border:"1px solid rgba(245,158,11,0.4)",fontSize:fs,fontWeight:700,
        color:"#f59e0b",whiteSpace:"nowrap"}}>
      ⚠️ Trim
    </span>
  );
}
// v8.4.283 — compact pullback-risk / bounce chips for list tiles. Backend sets
// row.pullbackFlag only when a name is stretched AND the tape is weak / rotation
// risk-off (validated ml/pullback_risk_signal.py); row.bounceFlag on an oversold
// bounce setup. Pullback supersedes the "Trim" chip on tiles (richer, gated).
function PullbackFlag({ size="sm" }) {
  const pad = size === "sm" ? "1px 6px" : "2px 8px";
  const fs  = size === "sm" ? 9 : 10;
  return (
    <span title="Stretched into a weak / rotation-risk-off tape — historically an elevated chance of a sharp (>5%) pullback within a week. Consider trimming or waiting for a pullback."
      style={{display:"inline-flex",alignItems:"center",gap:3,borderRadius:12,
        padding:pad,background:"rgba(245,158,11,0.15)",
        border:"1px solid rgba(245,158,11,0.4)",fontSize:fs,fontWeight:700,
        color:"#f59e0b",whiteSpace:"nowrap"}}>
      ⚠️ Pullback
    </span>
  );
}
// v8.4.418 — activist Schedule 13D chip (SEC EDGAR, last 90 days). One batched, cached request per
// ticker set (Portfolio holdings / Watchlist); returns {TICKER: {latest}} for qualifying tickers only.
const _batchFlagCache = {};
// Shared by the 13D and insider-cluster chips: one batched, 6h-cached request per ticker set →
// {TICKER: info} for qualifying tickers only. `field` is the key the endpoint nests results under.
function _useBatchTickerFlags(endpoint, field, maxN, tickers, token) {
  const [map, setMap] = React.useState({});
  // First maxN in the caller's (display/rank) order; the key is sorted only so the same set always hits the cache.
  const key = Array.from(new Set((tickers || []).map(t => String(t || "").toUpperCase()).filter(Boolean)))
    .slice(0, maxN).sort().join(",");
  React.useEffect(() => {
    if (!key || !token) return;
    let alive = true;
    const ck = endpoint + "|" + key;
    const hit = _batchFlagCache[ck];
    if (hit && Date.now() - hit.ts < 6 * 3600 * 1000) { setMap(hit.data); return; }
    // Debounced: progressively-streamed lists (Discovery) change the ticker set several times in a row.
    const timer = setTimeout(() => {
      apiFetch(`${endpoint}?tickers=${encodeURIComponent(key)}`, token)
        .then(r => { const d = (r && r[field]) || {}; _batchFlagCache[ck] = {ts: Date.now(), data: d}; if (alive) setMap(d); })
        .catch(() => { /* best-effort — chip simply doesn't show */ });
    }, 500);
    return () => { alive = false; clearTimeout(timer); };
  }, [key, token, endpoint, field, maxN]);
  return map;
}
const useActivistFilings = (tickers, token) => _useBatchTickerFlags("/activist-filings", "activist", 60, tickers, token);
// v8.4.420 — insider BUY-CLUSTER chip (several different insiders buying in the open market, last 30d; Polygon Form 4).
const useInsiderClusters = (tickers, token) => _useBatchTickerFlags("/insider-clusters", "clusters", 40, tickers, token);
const _usdShort = (v) => { const n = Number(v) || 0; return n >= 1e6 ? `$${(n/1e6).toFixed(1)}M` : n >= 1e3 ? `$${Math.round(n/1e3)}K` : `$${Math.round(n)}`; };
const _isoAgo = (iso) => {
  if (!iso) return "";
  const d = Math.round((Date.now() - new Date(iso + "T12:00:00").getTime()) / 86400000);
  return d <= 0 ? "today" : d === 1 ? "1d ago" : `${d}d ago`;
};
function InsiderClusterFlag({ info, size="sm" }) {
  const pad = size === "sm" ? "1px 6px" : "2px 8px";
  const fs  = size === "sm" ? 9 : 10;
  const usd = (v) => { const n = Number(v) || 0; return n >= 1e6 ? `$${(n/1e6).toFixed(1)}M` : n >= 1e3 ? `$${Math.round(n/1e3)}K` : `$${n}`; };
  return (
    <span title={`${info?.buyers ?? "Several"} different insiders bought on the open market${info?.value ? " (" + usd(info.value) + " total)" : ""} in the last 30 days${info?.latest ? " — latest filing " + info.latest : ""}. Public Form 4 filings; context, not a guarantee.`}
      style={{display:"inline-flex",alignItems:"center",gap:3,borderRadius:12,
        padding:pad,background:"rgba(0,230,118,0.13)",
        border:"1px solid rgba(0,230,118,0.4)",fontSize:fs,fontWeight:700,
        color:"#00e676",whiteSpace:"nowrap"}}>
      👥 Insider buying
    </span>
  );
}
function ActivistFilingFlag({ info, size="sm" }) {
  const pad = size === "sm" ? "1px 6px" : "2px 8px";
  const fs  = size === "sm" ? 9 : 10;
  return (
    <span title={`A Schedule 13D was filed${info?.latest ? " on " + info.latest : " recently"} — a holder of 5%+ who may seek to influence the company. Public SEC filing: context, not a trade signal. Details on the stock page.`}
      style={{display:"inline-flex",alignItems:"center",gap:3,borderRadius:12,
        padding:pad,background:"rgba(255,202,40,0.13)",
        border:"1px solid rgba(255,202,40,0.4)",fontSize:fs,fontWeight:700,
        color:"#ffca28",whiteSpace:"nowrap"}}>
      📄 13D
    </span>
  );
}
// v8.4.419 — setup-quality chip for list rows (backend `setupQuality` from /universe). Strong/Weak only:
// "Mixed" is the unremarkable default and would just add noise to already-dense rows.
function SetupQualityFlag({ q, size="sm" }) {
  if (q !== "strong" && q !== "weak") return null;
  const strong = q === "strong";
  const pad = size === "sm" ? "1px 6px" : "2px 8px";
  const fs  = size === "sm" ? 9 : 10;
  return (
    <span title={strong
        ? "Strong setup — swing label, technical confirmation, chart patterns and (market open) timing mostly line up. Context, not a guarantee."
        : "Weak setup — technicals, chart patterns or timing are mostly working against the swing label. Context, not a guarantee."}
      style={{display:"inline-flex",alignItems:"center",gap:3,borderRadius:12,padding:pad,fontSize:fs,fontWeight:700,whiteSpace:"nowrap",
        background:strong ? "rgba(0,230,118,0.13)" : "rgba(255,82,82,0.14)",
        border:`1px solid ${strong ? "rgba(0,230,118,0.4)" : "rgba(255,82,82,0.42)"}`,
        color:strong ? "#00e676" : "#ff7a7a"}}>
      {strong ? "✔ Strong setup" : "✖ Weak setup"}
    </span>
  );
}
function BounceFlag({ size="sm" }) {
  const pad = size === "sm" ? "1px 6px" : "2px 8px";
  const fs  = size === "sm" ? 9 : 10;
  return (
    <span title="Sharp drop while still in an uptrend and oversold — historically an above-average bounce setup (next-day ~+1%, ~62% positive in testing). Not a guarantee."
      style={{display:"inline-flex",alignItems:"center",gap:3,borderRadius:12,
        padding:pad,background:"rgba(0,230,118,0.13)",
        border:"1px solid rgba(0,230,118,0.4)",fontSize:fs,fontWeight:700,
        color:"#00e676",whiteSpace:"nowrap"}}>
      🔄 Bounce
    </span>
  );
}
// v8.4.297 — sharp-drop bounce-candidate / rotation-risk chips (ml/sharp_drop_recovery.py).
// Backend sets bounceCandidate when a -8%/-10% drop is an isolated dip in a steady tape
// (historically ~+3-5% 5d recovery); rotationRisk when it's part of a broad, weak selloff.
function DropBounceFlag({ size="sm" }) {
  const pad = size === "sm" ? "1px 6px" : "2px 8px";
  const fs  = size === "sm" ? 9 : 10;
  return (
    <span title="Sharp drop, but an isolated dip in a steady tape — historically similar setups recovered ~+3-5% over the next week (~50% bounce rate, strongest in high-beta names). A probability tilt, not a guarantee."
      style={{display:"inline-flex",alignItems:"center",gap:3,borderRadius:12,
        padding:pad,background:"rgba(0,230,118,0.13)",
        border:"1px solid rgba(0,230,118,0.4)",fontSize:fs,fontWeight:700,
        color:"#00e676",whiteSpace:"nowrap"}}>
      🟢 Bounce candidate
    </span>
  );
}
function DropRotationFlag({ size="sm" }) {
  const pad = size === "sm" ? "1px 6px" : "2px 8px";
  const fs  = size === "sm" ? 9 : 10;
  return (
    <span title="Sharp drop alongside a broad, weak-market selloff — historically these recover less and tend to keep sliding near-term. Looks like rotation, not a dip to buy yet."
      style={{display:"inline-flex",alignItems:"center",gap:3,borderRadius:12,
        padding:pad,background:"rgba(255,82,82,0.14)",
        border:"1px solid rgba(255,82,82,0.42)",fontSize:fs,fontWeight:700,
        color:"#ff7a7a",whiteSpace:"nowrap"}}>
      ⚠️ Rotation risk
    </span>
  );
}
function ScoreDonut({ score, size=72, label="" }) {
  // v8.4.34: defensive normalization. Some Redis entries (last written by
  // /analyze) carry nested {score, label, labelType, ...} shapes that can
  // sneak through if a caller forgets to extract `.score`. `({...}) > 0` is
  // `NaN > 0 → false` — so unnormalized objects rendered "—" forever.
  // Backend /universe now flattens these (v8.4.34) but this guard means the
  // donut survives ANY future path that forgets the extraction.
  if (score != null && typeof score === 'object') score = score.score;
  // Treat null, undefined, and 0 as "no data" — 0 is never a real score
  const hasScore = score != null && score > 0;
  const r=(size-10)/2, circ=2*Math.PI*r;
  const fill = hasScore ? (score/100)*circ : 0;
  const color = !hasScore ? "rgba(255,255,255,0.15)"
              : score>=70 ? C.green : score>=45 ? C.yellow : C.red;
  return (
    <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:3}}>
      <svg width={size} height={size}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth={5}/>
        {hasScore && (
          <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={5}
            strokeDasharray={`${fill} ${circ-fill}`} strokeLinecap="round"
            transform={`rotate(-90 ${size/2} ${size/2})`}
            style={{transition:"stroke-dasharray 0.8s cubic-bezier(.4,0,.2,1)"}}/>
        )}
        <text x={size/2} y={size/2} textAnchor="middle" dominantBaseline="central"
          fill={color} fontSize={size*0.22} fontWeight="700" fontFamily="monospace">
          {hasScore ? score : "—"}
        </text>
      </svg>
      {label && <div style={{fontSize:10,color:C.muted,letterSpacing:"0.06em",textTransform:"uppercase"}}>{label}</div>}
    </div>
  );
}

function SignalPill({ type, label, size="md" }) {
  const m = SIGNAL_META[type] || SIGNAL_META.watch;
  const fs = size==="sm" ? 10 : 12;
  return (
    <span style={{display:"inline-flex",alignItems:"center",gap:4,
      padding: size==="sm"?"2px 8px":"4px 12px",
      borderRadius:20, background:m.bg, color:m.color,
      fontSize:fs, fontWeight:700, letterSpacing:"0.05em", whiteSpace:"nowrap"}}>
      {m.icon} {label || m.short}
    </span>
  );
}

function Card({ children, style={}, onClick }) {
  return (
    <div onClick={onClick} style={{
      background:C.card, borderRadius:16,
      border:`1px solid ${C.border}`, padding:16,
      cursor:onClick?"pointer":"default", ...style,
    }}>{children}</div>
  );
}

function SLabel({ children }) {
  return <div style={{fontSize:9,fontWeight:700,letterSpacing:"0.12em",
    color:C.muted,textTransform:"uppercase",marginBottom:10}}>{children}</div>;
}

// ═════════════════════════════════════════════════════════════════════════════
//  FEATURE TOURS (v8.4.405) — reusable live-spotlight coach marks.
//  Elements opt in with a `data-tour="<id>"` attribute; a step list points at those
//  ids with a title + one-liner. Renders a dimmed screen with a cutout around the
//  real element (not a screenshot) + a small card with Back/Next/Skip and dot
//  progress. A step whose element isn't mounted (or renders 0×0, e.g. a card that
//  returns null when empty) is skipped automatically — never a dead end.
//  Seen/not-seen is per-DEVICE localStorage (matches every other UI preference in
//  this app — sort order, currency, text scale are all localStorage-only, nothing
//  here is synced server-side). Each tour is versioned so a future redesign of a
//  screen can bump the version and only THAT tour reappears.
// ═════════════════════════════════════════════════════════════════════════════
const _TOURS_LS_KEY = "gempulse_tours_seen";

function _toursSeenMap() {
  try { return JSON.parse(localStorage.getItem(_TOURS_LS_KEY) || "{}"); } catch (_) { return {}; }
}
function _tourKey(id, version) { return `${id}_v${version}`; }
function _isTourSeen(id, version) {
  try { return !!_toursSeenMap()[_tourKey(id, version)]; } catch (_) { return true; }  // fail safe: never show on a storage error
}
function _markTourSeen(id, version) {
  try { const m = _toursSeenMap(); m[_tourKey(id, version)] = 1; localStorage.setItem(_TOURS_LS_KEY, JSON.stringify(m)); } catch (_) {}
}
function _resetTour(id, version) {
  try { const m = _toursSeenMap(); delete m[_tourKey(id, version)]; localStorage.setItem(_TOURS_LS_KEY, JSON.stringify(m)); } catch (_) {}
}
function _resetAllTours() { try { localStorage.removeItem(_TOURS_LS_KEY); } catch (_) {} }

// Registry — one entry per tour, used by both the auto-trigger mounts AND the
// Settings "Help & tours" replay list, so the two can never drift apart.
const TOUR_REGISTRY = [
  { id: "app_intro",          version: 1, label: "App basics",         desc: "The five tabs and what each is for." },
  { id: "portfolio_empty",    version: 1, label: "Getting started",    desc: "Build vs. add holdings manually." },
  { id: "portfolio_dashboard",version: 2, label: "Portfolio dashboard",desc: "Today's action, snapshot, holdings, rotation ideas, cash and more." },
  { id: "portfolio_build",    version: 1, label: "Portfolio builder",  desc: "Amount, strategy and running the optimizer." },
  { id: "watchlist",          version: 1, label: "Watchlist",           desc: "Filtering, sorting and adding tickers." },
  { id: "discover",           version: 1, label: "Discover",            desc: "The AI buy-today agent, heatmap, search and quick lists." },
];

// On a brand-new install, `gempulse_tours_seen` doesn't exist yet. If OTHER app
// localStorage already exists, this is a returning user's device (or a re-deploy
// wiping in-memory state, not a fresh browser) — don't surprise them with a
// "Welcome" tour; seed app_intro as already-seen. A genuinely fresh browser (new
// user, or an existing user's new device) still gets it once.
function _bootstrapTourSeenState() {
  try {
    if (localStorage.getItem(_TOURS_LS_KEY) != null) return;
    const priorUser = ["gempulse_remember_me", "gempulse_universe", "swingai_tradepref", "gempulse_wl_sort"]
      .some(k => localStorage.getItem(k) != null);
    if (priorUser) _markTourSeen("app_intro", 1);
  } catch (_) {}
}

const _tourBtnPrimary = {padding:"7px 14px",borderRadius:9,border:"none",background:C.accent,color:"#04121a",
  fontWeight:800,fontSize:12,cursor:"pointer",fontFamily:"inherit"};
const _tourBtnGhost = {padding:"7px 12px",borderRadius:9,border:`1px solid ${C.border}`,background:"transparent",
  color:C.muted,fontWeight:700,fontSize:12,cursor:"pointer",fontFamily:"inherit"};

function FeatureTour({ tourId, version = 1, steps, active }) {
  const [open, setOpen] = useState(false);
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState(null);

  useEffect(() => {
    if (!active || _isTourSeen(tourId, version)) return;
    const t = setTimeout(() => { setIdx(0); setOpen(true); }, 650);   // let the screen finish laying out first
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, tourId, version]);

  const finish = React.useCallback(() => { _markTourSeen(tourId, version); setOpen(false); }, [tourId, version]);

  React.useEffect(() => {
    if (!open) return;
    const step = steps[idx];
    if (!step) { finish(); return; }
    const measure = () => {
      const el = document.querySelector(`[data-tour="${step.sel}"]`);
      const r = el ? el.getBoundingClientRect() : null;
      if (!r || (r.width < 2 && r.height < 2)) {           // missing, or a card that rendered null (0×0) — skip it
        if (idx < steps.length - 1) setIdx(i => i + 1); else finish();
        return;
      }
      el.scrollIntoView({block: "center", behavior: "smooth"});
      setRect(r);
    };
    measure();
    const t = setTimeout(measure, 380);                    // re-measure after the smooth-scroll settles
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, idx, steps]);

  React.useEffect(() => {
    if (!open) return;
    const step = steps[idx];
    const onMove = () => {
      if (!step) return;
      const el = document.querySelector(`[data-tour="${step.sel}"]`);
      if (el) setRect(el.getBoundingClientRect());
    };
    window.addEventListener("scroll", onMove, {passive: true});
    window.addEventListener("resize", onMove);
    return () => { window.removeEventListener("scroll", onMove); window.removeEventListener("resize", onMove); };
  }, [open, idx, steps]);

  if (!open || !rect) return null;
  const step = steps[idx];
  const pad = 6;
  const top = Math.max(0, rect.top - pad), left = Math.max(0, rect.left - pad);
  const w = rect.width + pad * 2, h = Math.max(rect.height + pad * 2, 4);
  const vw = window.innerWidth, vh = window.innerHeight;
  const cardW = Math.min(320, vw - 32);
  const placeBelow = (vh - (top + h)) > 170 || top < 170;
  const cardLeft = Math.min(Math.max(16, left), Math.max(16, vw - cardW - 16));
  const dim = "rgba(4,6,10,0.78)";

  return (
    <div style={{position:"fixed", inset:0, zIndex:8500}}>
      <div style={{position:"fixed", left:0, top:0, right:0, height:top, background:dim}}/>
      <div style={{position:"fixed", left:0, top:top+h, right:0, bottom:0, background:dim}}/>
      <div style={{position:"fixed", left:0, top, width:left, height:h, background:dim}}/>
      <div style={{position:"fixed", left:left+w, top, right:0, height:h, background:dim}}/>
      {/* the "hole" itself absorbs taps so the real element stays inert during the tour (Next/Skip only) */}
      <div style={{position:"fixed", left, top, width:w, height:h, pointerEvents:"auto"}}/>
      <div style={{position:"fixed", left, top, width:w, height:h, borderRadius:14,
        border:`2px solid ${C.accent}`, boxShadow:"0 0 0 4px rgba(0,212,255,0.15)", pointerEvents:"none"}}/>
      <div style={{position:"fixed", left:cardLeft, width:cardW,
        ...(placeBelow ? {top: Math.min(top + h + 12, vh - 190)} : {bottom: Math.max(12, vh - top + 12)}),
        background:C.card, border:`1px solid ${C.border}`, borderRadius:14, padding:14,
        boxShadow:"0 12px 40px rgba(0,0,0,0.55)", boxSizing:"border-box"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8,marginBottom:6}}>
          <div style={{fontSize:13,fontWeight:800,color:C.text}}>{step.title}</div>
          <button onClick={finish} aria-label="Close tour" style={{background:"none",border:"none",color:C.muted,
            fontSize:17,cursor:"pointer",lineHeight:1,padding:0,flexShrink:0}}>×</button>
        </div>
        <div style={{fontSize:12,color:C.muted,lineHeight:1.55,marginBottom:12}}>{step.body}</div>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8}}>
          <div style={{display:"flex",gap:4}}>
            {steps.map((_, i) => (
              <span key={i} style={{width:i===idx?14:6,height:6,borderRadius:3,transition:"all .2s",
                background:i===idx?C.accent:"rgba(255,255,255,0.18)"}}/>))}
          </div>
          <div style={{display:"flex",gap:8}}>
            {idx > 0 && <button onClick={()=>setIdx(i=>i-1)} style={_tourBtnGhost}>Back</button>}
            <button onClick={()=> idx < steps.length-1 ? setIdx(i=>i+1) : finish()} style={_tourBtnPrimary}>
              {idx < steps.length - 1 ? "Next" : "Done"}
            </button>
          </div>
        </div>
        <div onClick={finish} style={{textAlign:"center",marginTop:9,fontSize:11,color:C.muted,cursor:"pointer"}}>Skip tour</div>
      </div>
    </div>
  );
}

const APP_INTRO_STEPS = [
  {sel:"nav-landing",   title:"Welcome to GemPulse 👋", body:"Home — market pulse, your watchlist highlights, and quick actions, all in one glance."},
  {sel:"nav-scanner",   title:"Watchlist",  body:"Track the tickers you care about with live Swing and Long-Term scores, sorted your way."},
  {sel:"nav-discover",  title:"Discover",   body:"Scan the market, search any ticker, or ask the AI agent what to buy today."},
  {sel:"nav-portfolio", title:"Portfolio",  body:"Log your real holdings and get a daily action, a health check, and an AI briefing built around them."},
  {sel:"nav-settings",  title:"Settings",   body:"Your risk profile, currency and alerts live here — and if you ever want a refresher, these tours do too, under Help & tours."},
];

const PORTFOLIO_EMPTY_STEPS = [
  {sel:"pd-empty-build",  title:"Build a portfolio", body:"Not sure where to start? Answer a couple of questions and get an AI-optimised allocation."},
  {sel:"pd-empty-manual", title:"Or add holdings manually", body:"Already own stocks? Log your real positions here and this dashboard fills in around them."},
  {sel:"pd-empty-picks",  title:"Gem picks", body:"A running list of today's highest-quality setups, updated automatically — worth a look even before you have holdings."},
];

const PORTFOLIO_DASHBOARD_STEPS = [
  {sel:"pd-actions",       title:"Today's action",   body:"The single most important thing to look at today — swipe or tap a dot if there's more than one suggestion."},
  {sel:"pd-snapshot",      title:"Snapshot",         body:"Your total value, today's move, health score and sector mix at a glance."},
  {sel:"pd-performance",   title:"Performance",      body:"Unrealized and realized P&L, plus your value curve — including what would've happened if you'd just held your opening positions."},
  {sel:"pd-briefing",      title:"Your briefing",    body:"An AI summary of what to watch today, built from your real holdings and the optimizer's actual verdicts."},
  {sel:"pd-holdings",      title:"Holdings",         body:"Every position with its verdict — ADD, HOLD, TRIM, EXIT. Tap 💎 to mark a core holding the optimizer should never suggest trimming."},
  {sel:"pd-rotation",      title:"Rotate for better setups", body:"When a HOLD is flat but a materially stronger setup exists, this suggests a same-value swap — sell one, buy the other — including whether right now is a good entry."},
  {sel:"pd-cash",          title:"Cash",             body:"Track cash or a margin balance here, with regime-aware guidance on whether to deploy or hold."},
  {sel:"pd-discover",      title:"Discover for you", body:"Today's best-scoring picks, tagged against what you already hold or what would diversify you."},
  {sel:"pd-concentration", title:"Concentration",    body:"A check on how concentrated you are by sector and position, against sensible caps."},
];

const PORTFOLIO_BUILD_STEPS = [
  {sel:"build-amount-strategy", title:"Amount & strategy", body:"Pick how much you'd invest and whether to lean Swing, Long-term, or a Blended approach."},
  {sel:"build-advanced",        title:"Advanced filters",  body:"Optionally narrow by market cap, style, sector or volatility — leave it on Auto to let the optimizer choose."},
  {sel:"build-submit",          title:"Build it",          body:"Runs the optimizer against the live universe and returns a ranked, rebalance-ready allocation."},
];

const WATCHLIST_STEPS = [
  {sel:"wl-filter", title:"Filter by signal",  body:"All, Buy, Watch or Pass — narrow the list to just the setups worth a look."},
  {sel:"wl-sort",   title:"Sort your way",     body:"Added order, Swing, Long-Term, today's % move, or A-Z — whatever you want to scan first."},
  {sel:"wl-add",    title:"Add any ticker",    body:"Search and add a ticker to start tracking it — GemPulse keeps its Swing and Long-Term scores current automatically."},
  {sel:"wl-card",   title:"Tap for the full picture", body:"Swing and Long-Term scores, today's move, and a signal pill at a glance — tap any card for the full stock detail."},
];

const DISCOVER_STEPS = [
  {sel:"disc-agent",   title:"Ask what to buy today", body:"The AI agent scans the market and hands you its highest-conviction pick, with reasoning — no manual digging needed."},
  {sel:"disc-heatmap", title:"See where the action is", body:"A live sector heatmap, or switch to Movers for a bubble chart of today's biggest movers by dollar volume."},
  {sel:"disc-search",  title:"Search any ticker",     body:"Score any stock instantly, US or Canadian — even ones outside your watchlist."},
  {sel:"disc-lists",   title:"Or scan a curated list", body:"Tap a quick list — S&P 100, sector baskets and more — to score a whole group at once."},
];

function PillButton({ label, active, onClick, color, emoji }) {
  return (
    <button onClick={onClick} style={{
      flex:1, padding:"10px 6px", borderRadius:12, border:"none", cursor:"pointer",
      fontFamily:"inherit", fontSize:12, fontWeight:600,
      background: active ? (color||C.accent) : C.surface,
      color: active ? (color?C.bg:C.bg) : C.muted,
      transition:"all 0.15s", display:"flex", flexDirection:"column",
      alignItems:"center", gap:2,
    }}>
      {emoji && <span style={{fontSize:16}}>{emoji}</span>}
      {label}
    </button>
  );
}

function Explanation({ text, type }) {
  const m = SIGNAL_META[type] || SIGNAL_META.watch;
  return (
    <div style={{background:m.bg, borderLeft:`3px solid ${m.color}`,
      borderRadius:"0 10px 10px 0", padding:"10px 14px",
      fontSize:13, color:C.text, lineHeight:1.6, marginTop:8}}>
      {text}
    </div>
  );
}

// --- Login screen --------------------------------------------------------------
function LoginScreen({ onLogin }) {
  const [email,      setEmail]      = useState("");
  const [password,   setPassword]   = useState("");
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState("");
  const [showRequest,setShowRequest]= useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [reqName,    setReqName]    = useState("");
  const [reqEmail,   setReqEmail]   = useState("");
  const [reqNote,    setReqNote]    = useState("");
  const [reqLoading, setReqLoading] = useState(false);
  const [reqMsg,     setReqMsg]     = useState("");
  // "Keep me logged in" — opt-in persistence (v7.9.52). Default false so a
  // shared / public device doesn't accidentally retain the session. The
  // user's last preference is remembered across visits via localStorage so
  // they don't have to re-tick on every login.
  const [rememberMe, setRememberMe] = useState(() => {
    try {
      return localStorage.getItem("gempulse_remember_me") === "1";
    } catch { return false; }
  });
  const persistRememberPref = (val) => {
    try { localStorage.setItem("gempulse_remember_me", val ? "1" : "0"); }
    catch (_) {}
    setRememberMe(val);
  };

  const handleLogin = async () => {
    setLoading(true); setError("");
    try {
      const form = new URLSearchParams();
      form.append("username", email);
      form.append("password", password);
      const res  = await fetch(`${API}/auth/login`, {
        method:"POST",
        headers:{"Content-Type":"application/x-www-form-urlencoded"},
        body: form,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Login failed");
      onLogin(data.user, data.access_token, rememberMe);
    } catch(e) {
      setError(e.message || "Login failed. Check your email and password.");
    } finally {
      setLoading(false);
    }
  };

  const handleRequest = async () => {
    if (!reqName.trim() || !reqEmail.trim()) {
      setReqMsg("Please enter your name and email."); return;
    }
    setReqLoading(true); setReqMsg("");
    try {
      const res  = await fetch(`${API}/auth/request-access`, {
        method:"POST",
        headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ name:reqName, email:reqEmail, note:reqNote }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Request failed");
      // v8.4.301: signups auto-provision — backend detail says "check your email
      // for credentials" (or the manual-review message if auto-approve is off).
      setReqMsg("✅ " + (data.detail || "You're in! Check your email for your login credentials."));
      setReqName(""); setReqEmail(""); setReqNote("");
    } catch(e) {
      setReqMsg(`❌ ${e.message}`);
    } finally {
      setReqLoading(false);
    }
  };

  // ── Forgot-password (v7.9.49) ────────────────────────────────────────
  // Backend always returns 200 with a generic message — never leaks whether
  // the email exists. The success message intentionally doesn't claim the
  // email "exists" or that mail "was sent" — it says "if registered, ...".
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotMsg, setForgotMsg] = useState("");

  const handleForgot = async () => {
    if (!forgotEmail.trim()) {
      setForgotMsg("Please enter your email."); return;
    }
    setForgotLoading(true); setForgotMsg("");
    try {
      const res = await fetch(`${API}/auth/forgot-password`, {
        method:"POST",
        headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ email: forgotEmail.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Request failed");
      // Generic success — don't echo the email back, keep messaging vague to
      // match backend's anti-enumeration design.
      setForgotMsg("✅ If that email is registered, a reset link is on its way. Check your inbox (and spam folder).");
      setForgotEmail("");
    } catch(e) {
      setForgotMsg(`❌ ${e.message}`);
    } finally {
      setForgotLoading(false);
    }
  };

  const inp = {
    background:C.surface, border:`1px solid ${C.border}`,
    borderRadius:12, padding:"14px 16px", color:C.text,
    fontSize:16, fontFamily:"inherit", outline:"none",
    width:"100%", boxSizing:"border-box", display:"block",
  };
  const btnStyle = {
    background:C.accent, color:C.bg, border:"none", borderRadius:12,
    padding:"14px 16px", fontSize:15, fontWeight:700, cursor:"pointer",
    width:"100%", boxSizing:"border-box", display:"block", fontFamily:"inherit",
    transition:"opacity 0.15s",
  };

  if (showRequest) return (
    <div style={{minHeight:"100vh",display:"flex",flexDirection:"column",
      justifyContent:"center",padding:"0 28px",background:C.bg}}>
      <div style={{overflow:"hidden",borderRadius:12,marginBottom:16,
        marginLeft:-4,marginRight:-4}}>
        <img src="/header.jpg" alt="GemPulse"
          style={{width:"100%",display:"block",objectFit:"cover",
            objectPosition:"center",maxHeight:140,borderRadius:12}}/>
      </div>
      <div style={{textAlign:"center",marginBottom:24}}>
        <div style={{fontSize:20,fontWeight:900,color:C.text}}>Request Access</div>
        <div style={{fontSize:13,color:C.muted,marginTop:6}}>
          Enter your name and email — we'll email your login credentials instantly.
        </div>
      </div>
      <div style={{display:"flex",flexDirection:"column",gap:12}}>
        <input style={inp} type="text" placeholder="Your full name"
          value={reqName} onChange={e=>setReqName(e.target.value)}/>
        <input style={inp} type="email" placeholder="Your email address"
          value={reqEmail} onChange={e=>setReqEmail(e.target.value)}/>
        <textarea style={{...inp,height:80,resize:"none"}}
          placeholder="Optional: why do you want access?"
          value={reqNote} onChange={e=>setReqNote(e.target.value)}/>
        {reqMsg && <div style={{fontSize:12,textAlign:"center",
          color:reqMsg.startsWith("✅")?C.green:C.red,lineHeight:1.6}}>{reqMsg}</div>}
        <button onClick={handleRequest} disabled={reqLoading} style={{
          background:C.accent,color:C.bg,border:"none",borderRadius:12,
          padding:"15px",fontSize:15,fontWeight:700,cursor:"pointer",
          fontFamily:"inherit",opacity:reqLoading?0.7:1,
        }}>{reqLoading?"Submitting…":"Submit Request"}</button>
        <button onClick={()=>{setShowRequest(false);setReqMsg("");}} style={{
          background:"none",border:"none",color:C.muted,fontSize:13,
          cursor:"pointer",fontFamily:"inherit",padding:"8px",
        }}>← Back to Sign In</button>
      </div>
    </div>
  );

  // ── Forgot-password view (v7.9.49) ──────────────────────────────────
  // Uses the same visual chrome as showRequest so the user sees a coherent
  // pre-auth flow. Submitting always succeeds visually — the real check
  // happens server-side and never leaks email existence.
  if (showForgot) return (
    <div style={{minHeight:"100vh",display:"flex",flexDirection:"column",
      justifyContent:"center",padding:"0 28px",background:C.bg}}>
      <div style={{overflow:"hidden",borderRadius:12,marginBottom:16,
        marginLeft:-4,marginRight:-4}}>
        <img src="/header.jpg" alt="GemPulse"
          style={{width:"100%",display:"block",objectFit:"cover",
            objectPosition:"center",maxHeight:140,borderRadius:12}}/>
      </div>
      <div style={{textAlign:"center",marginBottom:24}}>
        <div style={{fontSize:20,fontWeight:900,color:C.text}}>Reset your password</div>
        <div style={{fontSize:13,color:C.muted,marginTop:6,lineHeight:1.6}}>
          Enter your email and we'll send you a link to set a new password.
        </div>
      </div>
      <form onSubmit={e => { e.preventDefault(); handleForgot(); }}
        autoComplete="on"
        style={{display:"flex",flexDirection:"column",gap:12}}>
        <input style={inp} type="email" name="email"
          autoComplete="username" autoCapitalize="none"
          autoCorrect="off" spellCheck={false}
          inputMode="email" enterKeyHint="send"
          placeholder="Your email address"
          value={forgotEmail} onChange={e=>setForgotEmail(e.target.value)}/>
        {forgotMsg && <div style={{fontSize:12,textAlign:"center",
          color:forgotMsg.startsWith("✅")?C.green:C.red,lineHeight:1.6}}>{forgotMsg}</div>}
        <button type="submit" disabled={forgotLoading} style={{
          ...btnStyle, opacity:forgotLoading?0.7:1,
        }}>{forgotLoading?"Sending…":"Send reset link"}</button>
        <button type="button" onClick={()=>{setShowForgot(false);setForgotMsg("");}} style={{
          background:"none",border:"none",color:C.muted,fontSize:13,
          cursor:"pointer",fontFamily:"inherit",padding:"8px",textAlign:"center",
        }}>← Back to Sign In</button>
      </form>
    </div>
  );

  return (
    <div style={{minHeight:"100vh",display:"flex",flexDirection:"column",
      justifyContent:"center",padding:"0 28px",background:C.bg}}>
      <div style={{overflow:"hidden",borderRadius:12,marginBottom:16,
        marginLeft:-4,marginRight:-4}}>
        <img src="/header.jpg" alt="GemPulse"
          style={{width:"100%",display:"block",objectFit:"cover",
            objectPosition:"center",maxHeight:140,borderRadius:12}}/>
      </div>
      <div style={{textAlign:"center",marginBottom:28}}>
        <div style={{fontSize:13,color:C.muted}}>
          Sign in to your account
        </div>
      </div>
      {/* Wrapping inputs in a real <form> with autoComplete="on" lets iOS
          Keychain / 1Password / Chrome password manager identify this as a
          standard login form and auto-fill / save credentials. The submit
          handler intercepts the synthetic submit event so we still call
          handleLogin without a page navigation. name+autoComplete pairs
          match the WHATWG autofill spec so password managers can correlate. */}
      <form onSubmit={e => { e.preventDefault(); handleLogin(); }}
        autoComplete="on"
        style={{display:"flex",flexDirection:"column",gap:12}}>
        <input style={inp} type="email" name="email"
          autoComplete="username" autoCapitalize="none"
          autoCorrect="off" spellCheck={false}
          inputMode="email" enterKeyHint="next"
          placeholder="Email address"
          value={email} onChange={e=>setEmail(e.target.value)}/>
        <input style={inp} type="password" name="password"
          autoComplete="current-password"
          enterKeyHint="go"
          placeholder="Password"
          value={password} onChange={e=>setPassword(e.target.value)}/>

        {/* Keep me logged in (v7.9.52). Opt-in persistence — when checked,
            session writes to localStorage and survives browser restarts.
            Default UNCHECKED → sessionStorage only, clears on tab close.
            Last preference is remembered across visits so the user doesn't
            have to re-tick on every login. */}
        <label style={{
          display:"flex",alignItems:"center",gap:10,
          padding:"4px 2px",cursor:"pointer",userSelect:"none",
          fontFamily:"inherit",
        }}>
          <input type="checkbox" checked={rememberMe}
            onChange={e=>persistRememberPref(e.target.checked)}
            style={{
              width:18,height:18,cursor:"pointer",
              accentColor:C.accent,flexShrink:0,
            }}/>
          <span style={{fontSize:13,color:C.muted}}>
            Keep me logged in
          </span>
        </label>

        {error && <div style={{color:C.red,fontSize:12,textAlign:"center"}}>{error}</div>}
        <button type="submit" disabled={loading}
          style={{...btnStyle, opacity:loading?0.6:1}}>
          {loading ? "Signing in…" : "Sign In"}
        </button>
        {/* Forgot-password link — sits between the submit and the request-access
            CTA. type="button" so Enter inside the form doesn't accidentally
            open the forgot view. */}
        <button type="button" onClick={()=>{setShowForgot(true);setForgotMsg("");}}
          style={{
            background:"none",border:"none",color:C.accent,fontSize:13,
            cursor:"pointer",fontFamily:"inherit",padding:"6px 0",textAlign:"center",
          }}>Forgot password?</button>
        <button type="button" onClick={()=>setShowRequest(true)} style={{
          background:"none",border:"none",color:C.muted,fontSize:13,
          cursor:"pointer",fontFamily:"inherit",padding:"8px 0",textAlign:"center",
        }}>Don't have an account? Request access</button>
      </form>
    </div>
  );
}

// ── Register screen (v7.9.50) ──────────────────────────────────────────────
// Rendered when the URL contains ?invite=CODE — App detects on boot and
// routes here instead of LoginScreen. The code is validated up-front via
// /auth/validate-invite so the user sees clear feedback (e.g. "expired",
// "already used", "locked to a different email") before filling the form.
//
// On successful registration the backend returns a token + user; we
// onLogin(...) which persists to localStorage exactly like the login flow,
// so the user lands inside the app already authenticated.
function RegisterScreen({ inviteCode, onLogin, onDone }) {
  const [validating, setValidating] = useState(true);
  const [codeInfo,   setCodeInfo]   = useState(null);   // {early_adopter_tier, ...}
  const [codeError,  setCodeError]  = useState("");

  // Form state
  const [name,     setName]     = useState("");
  const [email,    setEmail]    = useState("");
  const [pw1,      setPw1]      = useState("");
  const [pw2,      setPw2]      = useState("");
  const [profile,  setProfile]  = useState("moderate");
  const [submitting, setSubmitting] = useState(false);
  const [error,    setError]    = useState("");
  // Same persistence preference as LoginScreen — share the localStorage
  // flag so the user's choice carries across sign-in / sign-up flows.
  const [rememberMe, setRememberMe] = useState(() => {
    try { return localStorage.getItem("gempulse_remember_me") === "1"; }
    catch { return false; }
  });
  const persistRememberPref = (val) => {
    try { localStorage.setItem("gempulse_remember_me", val ? "1" : "0"); }
    catch (_) {}
    setRememberMe(val);
  };

  // Validate the code once on mount. Doesn't consume — that happens on
  // /auth/register success.
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/auth/validate-invite`, {
          method:"POST",
          headers:{"Content-Type":"application/json"},
          body: JSON.stringify({ code: inviteCode }),
        });
        const d = await r.json();
        if (cancelled) return;
        if (d?.valid) {
          setCodeInfo(d);
        } else {
          setCodeError(d?.error || "This invite link is no longer valid.");
        }
      } catch (e) {
        if (!cancelled) setCodeError("Couldn't validate this invite link. Please try again.");
      } finally {
        if (!cancelled) setValidating(false);
      }
    })();
    return () => { cancelled = true; };
  }, [inviteCode]);

  const submit = async () => {
    setError("");
    if (name.trim().length < 2) return setError("Please enter your full name (minimum 2 characters).");
    if (!email.trim())          return setError("Please enter your email.");
    if (pw1.length < 6)        return setError("Password must be at least 6 characters.");
    if (pw1 !== pw2)           return setError("Passwords don't match.");
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/auth/register`, {
        method:"POST",
        headers:{"Content-Type":"application/json"},
        body: JSON.stringify({
          email: email.trim(), name: name.trim(),
          password: pw1, risk_profile: profile,
          invite_code: inviteCode,
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Registration failed");
      // Same shape as /auth/login — token + user. Hand off to App.handleLogin
      // so the cached-auth + splash flow runs identically to a normal login.
      // rememberMe controls whether the new session persists across browser
      // restarts (localStorage) or only this tab (sessionStorage).
      onLogin(d.user, d.access_token, rememberMe);
    } catch (e) {
      setError(e.message || "Registration failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const inp = {
    background:C.surface, border:`1px solid ${C.border}`,
    borderRadius:12, padding:"14px 16px", color:C.text,
    fontSize:16, fontFamily:"inherit", outline:"none",
    width:"100%", boxSizing:"border-box", display:"block",
  };
  const btnStyle = {
    background:C.accent, color:C.bg, border:"none", borderRadius:12,
    padding:"14px 16px", fontSize:15, fontWeight:700, cursor:"pointer",
    width:"100%", boxSizing:"border-box", display:"block", fontFamily:"inherit",
  };

  // Friendly label for the early-adopter tier — shown as a badge so the
  // invitee sees what they're getting.
  const tierLabel = {
    founder:        "🌟 Founding Member",
    friends_family: "💛 Friends & Family",
    beta:           "🧪 Beta",
  }[codeInfo?.early_adopter_tier] || null;

  const wrap = (inner) => (
    <div style={{minHeight:"100vh",display:"flex",flexDirection:"column",
      justifyContent:"center",padding:"0 28px",background:C.bg}}>
      <div style={{overflow:"hidden",borderRadius:12,marginBottom:16,
        marginLeft:-4,marginRight:-4}}>
        <img src="/header.jpg" alt="GemPulse"
          style={{width:"100%",display:"block",objectFit:"cover",
            objectPosition:"center",maxHeight:140,borderRadius:12}}/>
      </div>
      {inner}
    </div>
  );

  if (validating) return wrap(
    <div style={{textAlign:"center",padding:"40px 0"}}>
      <div style={{width:24,height:24,border:`3px solid ${C.accent}`,
        borderTopColor:"transparent",borderRadius:"50%",
        margin:"0 auto",animation:"spin 0.8s linear infinite"}}/>
      <div style={{fontSize:13,color:C.muted,marginTop:14}}>
        Verifying your invite…
      </div>
    </div>
  );

  if (codeError) return wrap(
    <>
      <div style={{textAlign:"center",marginBottom:24}}>
        <div style={{fontSize:20,fontWeight:900,color:C.red}}>Invite unavailable</div>
        <div style={{fontSize:13,color:C.muted,marginTop:8,lineHeight:1.6}}>
          {codeError}
        </div>
      </div>
      <button onClick={onDone} style={btnStyle}>Back to Sign In</button>
    </>
  );

  return wrap(
    <>
      <div style={{textAlign:"center",marginBottom:20}}>
        <div style={{fontSize:20,fontWeight:900,color:C.text}}>Create your account</div>
        {tierLabel && (
          <div style={{display:"inline-block",marginTop:10,
            background:"rgba(0,212,255,0.12)",border:`1px solid rgba(0,212,255,0.3)`,
            color:C.accent,fontSize:12,fontWeight:700,
            padding:"4px 12px",borderRadius:20}}>
            {tierLabel}
          </div>
        )}
        <div style={{fontSize:12,color:C.muted,marginTop:10,lineHeight:1.6}}>
          Welcome to GemPulse — fill in the basics below.
        </div>
      </div>
      <form onSubmit={e => { e.preventDefault(); submit(); }}
        autoComplete="on"
        style={{display:"flex",flexDirection:"column",gap:12}}>
        <input style={inp} type="text" name="name"
          autoComplete="name" enterKeyHint="next"
          placeholder="Full name"
          value={name} onChange={e=>setName(e.target.value)}/>
        <input style={inp} type="email" name="email"
          autoComplete="email" autoCapitalize="none"
          autoCorrect="off" spellCheck={false}
          inputMode="email" enterKeyHint="next"
          placeholder="Email"
          value={email} onChange={e=>setEmail(e.target.value)}/>
        <input style={inp} type="password" name="new-password"
          autoComplete="new-password" enterKeyHint="next"
          placeholder="Password (min 6 chars)"
          value={pw1} onChange={e=>setPw1(e.target.value)}/>
        <input style={inp} type="password" name="new-password-confirm"
          autoComplete="new-password" enterKeyHint="go"
          placeholder="Confirm password"
          value={pw2} onChange={e=>setPw2(e.target.value)}/>

        {/* Risk profile picker — same options the rest of the app uses */}
        <div>
          <div style={{fontSize:11,color:C.muted,marginBottom:6,fontWeight:600}}>
            Risk profile
          </div>
          <div style={{display:"flex",gap:6}}>
            {[
              {key:"conservative", label:"Conservative", emoji:"🛡"},
              {key:"moderate",     label:"Moderate",     emoji:"⚖"},
              {key:"aggressive",   label:"Aggressive",   emoji:"🚀"},
            ].map(o => (
              <button key={o.key} type="button"
                onClick={()=>setProfile(o.key)} style={{
                flex:1,padding:"10px 6px",borderRadius:10,border:"none",
                cursor:"pointer",fontFamily:"inherit",
                background:profile===o.key?"rgba(0,212,255,0.12)":"rgba(255,255,255,0.04)",
                outline:profile===o.key?`1px solid ${C.accent}`:"none",
              }}>
                <div style={{fontSize:18}}>{o.emoji}</div>
                <div style={{fontSize:10,fontWeight:700,marginTop:2,
                  color:profile===o.key?C.accent:C.text}}>{o.label}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Keep me logged in — same opt-in pattern as LoginScreen.
            Default value is read from localStorage so it carries the user's
            previous preference (if any) into this signup. */}
        <label style={{
          display:"flex",alignItems:"center",gap:10,
          padding:"4px 2px",cursor:"pointer",userSelect:"none",
          fontFamily:"inherit",
        }}>
          <input type="checkbox" checked={rememberMe}
            onChange={e=>persistRememberPref(e.target.checked)}
            style={{
              width:18,height:18,cursor:"pointer",
              accentColor:C.accent,flexShrink:0,
            }}/>
          <span style={{fontSize:13,color:C.muted}}>
            Keep me logged in
          </span>
        </label>

        {error && <div style={{color:C.red,fontSize:12,textAlign:"center"}}>{error}</div>}
        <button type="submit" disabled={submitting}
          style={{...btnStyle, opacity:submitting?0.7:1}}>
          {submitting ? "Creating account…" : "Create account"}
        </button>
        <button type="button" onClick={onDone} style={{
          background:"none",border:"none",color:C.muted,fontSize:13,
          cursor:"pointer",fontFamily:"inherit",padding:"8px 0",textAlign:"center",
        }}>← Already have an account? Sign in</button>
      </form>
    </>
  );
}


// ── Reset-password screen (v7.9.49) ────────────────────────────────────────
// Rendered when the URL contains ?reset_token=... — App detects the param on
// boot and routes here instead of LoginScreen. The screen validates the token
// against /auth/reset-password/validate before showing the new-password form
// so users with stale links see a clear error up front rather than after typing.
//
// On successful reset: redirect to login (clearing ?reset_token from URL) so
// the user can sign in with the fresh credentials. We do NOT auto-login —
// requiring an explicit sign-in confirms the user remembers the new password
// and exercises the credential they just set.
function ResetPasswordScreen({ token, onDone }) {
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error,  setError]  = useState("");
  const [done,   setDone]   = useState(false);

  // Validate the token on mount — fast read-only check that doesn't consume.
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/auth/reset-password/validate?token=${encodeURIComponent(token)}`);
        const d = await r.json();
        if (cancelled) return;
        setTokenValid(!!d?.valid);
      } catch (e) {
        if (!cancelled) setTokenValid(false);
      } finally {
        if (!cancelled) setValidating(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const submit = async () => {
    setError("");
    if (pw1.length < 6)      return setError("Password must be at least 6 characters.");
    if (pw1 !== pw2)         return setError("Passwords don't match.");
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/auth/reset-password`, {
        method:"POST",
        headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ token, new_password: pw1 }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Reset failed");
      setDone(true);
    } catch (e) {
      setError(e.message || "Reset failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const inp = {
    background:C.surface, border:`1px solid ${C.border}`,
    borderRadius:12, padding:"14px 16px", color:C.text,
    fontSize:16, fontFamily:"inherit", outline:"none",
    width:"100%", boxSizing:"border-box", display:"block",
  };
  const btnStyle = {
    background:C.accent, color:C.bg, border:"none", borderRadius:12,
    padding:"14px 16px", fontSize:15, fontWeight:700, cursor:"pointer",
    width:"100%", boxSizing:"border-box", display:"block", fontFamily:"inherit",
  };

  return (
    <div style={{minHeight:"100vh",display:"flex",flexDirection:"column",
      justifyContent:"center",padding:"0 28px",background:C.bg}}>
      <div style={{overflow:"hidden",borderRadius:12,marginBottom:16,
        marginLeft:-4,marginRight:-4}}>
        <img src="/header.jpg" alt="GemPulse"
          style={{width:"100%",display:"block",objectFit:"cover",
            objectPosition:"center",maxHeight:140,borderRadius:12}}/>
      </div>

      {validating ? (
        <div style={{textAlign:"center",padding:"40px 0"}}>
          <div style={{width:24,height:24,border:`3px solid ${C.accent}`,
            borderTopColor:"transparent",borderRadius:"50%",
            margin:"0 auto",animation:"spin 0.8s linear infinite"}}/>
          <div style={{fontSize:13,color:C.muted,marginTop:14}}>
            Verifying reset link…
          </div>
        </div>
      ) : !tokenValid ? (
        <>
          <div style={{textAlign:"center",marginBottom:24}}>
            <div style={{fontSize:20,fontWeight:900,color:C.red}}>Reset link expired</div>
            <div style={{fontSize:13,color:C.muted,marginTop:8,lineHeight:1.6}}>
              This password reset link is invalid or has expired.
              Reset links work for 1 hour after they're sent.
            </div>
          </div>
          {/* Pass false — invalid link is not a successful reset, so keep
              any existing cached auth intact. */}
          <button onClick={()=>onDone(false)} style={btnStyle}>Back to Sign In</button>
        </>
      ) : done ? (
        <>
          <div style={{textAlign:"center",marginBottom:24}}>
            <div style={{fontSize:20,fontWeight:900,color:C.green}}>Password updated ✓</div>
            <div style={{fontSize:13,color:C.muted,marginTop:8,lineHeight:1.6}}>
              You can now sign in with your new password.
            </div>
          </div>
          <button onClick={()=>onDone(true)} style={btnStyle}>Sign In</button>
        </>
      ) : (
        <>
          <div style={{textAlign:"center",marginBottom:24}}>
            <div style={{fontSize:20,fontWeight:900,color:C.text}}>Set a new password</div>
            <div style={{fontSize:13,color:C.muted,marginTop:8,lineHeight:1.6}}>
              Choose a strong password — at least 6 characters.
            </div>
          </div>
          <form onSubmit={e => { e.preventDefault(); submit(); }}
            autoComplete="on"
            style={{display:"flex",flexDirection:"column",gap:12}}>
            <input style={inp} type="password" name="new-password"
              autoComplete="new-password" enterKeyHint="next"
              placeholder="New password"
              value={pw1} onChange={e=>setPw1(e.target.value)}/>
            <input style={inp} type="password" name="new-password-confirm"
              autoComplete="new-password" enterKeyHint="go"
              placeholder="Confirm new password"
              value={pw2} onChange={e=>setPw2(e.target.value)}/>
            {error && <div style={{color:C.red,fontSize:12,textAlign:"center"}}>{error}</div>}
            <button type="submit" disabled={submitting}
              style={{...btnStyle, opacity:submitting?0.7:1}}>
              {submitting ? "Updating…" : "Update password"}
            </button>
            {/* Cancel — keep cached auth, just close the reset flow. */}
            <button type="button" onClick={()=>onDone(false)} style={{
              background:"none",border:"none",color:C.muted,fontSize:13,
              cursor:"pointer",fontFamily:"inherit",padding:"8px 0",textAlign:"center",
            }}>Cancel</button>
          </form>
        </>
      )}
    </div>
  );
}

// --- Alerts panel (used in SettingsScreen) ------------------------------------
function AlertsPanel({ token, user, refreshKey }) {
  const [alerts,   setAlerts]  = useState([]);
  const [loading,  setLoading] = useState(true);
  const [testMsg,  setTestMsg] = useState("");
  const [editId,   setEditId]  = useState(null);   // id of alert being edited
  const [editVal,  setEditVal] = useState("");      // new target_value string

  // ── Push notification toggle ───────────────────────────────────────────────
  // Account-wide — covers gap scanner digests AND individual price/score alerts.
  const [pushState, setPushState] = useState('checking');
  const [pushBusy,  setPushBusy]  = useState(false);

  useEffect(() => {
    if (!('Notification' in window) || !('serviceWorker' in navigator) || !('PushManager' in window)) {
      setPushState('unsupported'); return;
    }
    if (Notification.permission === 'denied') { setPushState('denied'); return; }
    navigator.serviceWorker.getRegistration('/service-worker.js')
      .then(reg => reg ? reg.pushManager.getSubscription() : null)
      .then(sub => setPushState(sub ? 'enabled' : 'idle'))
      .catch(() => setPushState('idle'));
  }, []);

  const enablePush = async () => {
    setPushBusy(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') { setPushState('denied'); return; }
      const reg = await navigator.serviceWorker.register('/service-worker.js');
      await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });
      const j = sub.toJSON();
      await apiFetch('/push/subscribe', token, {
        method: 'POST',
        body: JSON.stringify({ endpoint: j.endpoint, keys: j.keys, userAgent: navigator.userAgent }),
      });
      setPushState('enabled');
      setTestMsg('✅ Push notifications enabled');
      setTimeout(() => setTestMsg(''), 3000);
    } catch(e) {
      console.error('Push enable failed:', e);
      setPushState('idle');
      setTestMsg('❌ Could not enable push — check browser permissions');
      setTimeout(() => setTestMsg(''), 4000);
    } finally { setPushBusy(false); }
  };

  const disablePush = async () => {
    setPushBusy(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration('/service-worker.js');
      if (reg) { const s = await reg.pushManager.getSubscription(); if (s) await s.unsubscribe(); }
      await apiFetch('/push/subscribe', token, { method: 'DELETE' });
      setPushState('idle');
    } catch(e) {
      console.error('Push disable failed:', e);
    } finally { setPushBusy(false); }
  };

  const testPush = async () => {
    setTestMsg('Sending…');
    try {
      await apiFetch('/push/test', token, { method: 'POST' });
      setTestMsg('✅ Test push sent — check your device');
    } catch(e) { setTestMsg(`❌ ${e.message}`); }
    setTimeout(() => setTestMsg(''), 4000);
  };

  useEffect(() => {
    setLoading(true);
    apiFetch("/alerts", token)
      .then(d => setAlerts(Array.isArray(d) ? d : []))
      .catch(() => setAlerts([]))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const deleteAlert = async (id) => {
    try {
      await apiFetch(`/alerts/${id}`, token, { method:"DELETE" });
      setAlerts(prev => prev.filter(a => a.id !== id));
    } catch(e) { setTestMsg(`❌ ${e.message}`); }
  };

  const saveEdit = async (a) => {
    const val = parseFloat(editVal);
    if (!editVal || isNaN(val) || val <= 0) {
      setTestMsg("❌ Enter a valid number"); return;
    }
    try {
      await apiFetch(`/alerts/${a.id}`, token, {
        method:"PATCH",
        body: JSON.stringify({ target_value: val }),
      });
      setAlerts(prev => prev.map(x => x.id===a.id ? {...x, target_value:val} : x));
      setEditId(null); setEditVal("");
    } catch(e) { setTestMsg(`❌ ${e.message}`); }
  };

  const reactivate = async (a) => {
    // Delete old triggered alert and let user create a new one on the stock screen
    await deleteAlert(a.id);
    setTestMsg("✅ Cleared — open the stock to set a new alert");
  };

  const testEmail = async () => {
    setTestMsg("Sending…");
    try {
      const d = await apiFetch("/alerts/test-email", token, { method:"POST" });
      setTestMsg(`✅ ${d.detail}`);
    } catch(e) { setTestMsg(`❌ ${e.message}`); }
    setTimeout(() => setTestMsg(""), 4000);
  };

  const alertLabel = (a) => {
    const t = a.target_value;
    if (a.alert_type==="price_above")  return `📈 Price above $${t}`;
    if (a.alert_type==="price_below")  return `📉 Price below $${t}`;
    if (a.alert_type==="stop_loss")    return `🛑 Stop loss $${t}`;
    if (a.alert_type==="swing_buy")    return `⚡ Swing score ≥ ${t}`;
    if (a.alert_type==="gap_scanner") {
      try {
        const n = JSON.parse(a.notes || "{}");
        const parts = [`📊 Gap ≥${t}%`];
        if (n.sectors?.length) parts.push(n.sectors.join(", "));
        if (n.rsi_filter) parts.push({oversold:"RSI <30",neutral:"RSI 45–55",overbought:"RSI >70"}[n.rsi_filter]||n.rsi_filter);
        // Score filter was added in v7.9.39; surface it here so users can
        // tell at a glance which alerts require a high swing / LT score.
        if (n.score_filter) parts.push({
          swing_buy:"Swing ≥ Buy", lt_buy:"LT ≥ Buy",
          either_buy:"Swing or LT ≥ Buy", both_buy:"Swing & LT ≥ Buy",
        }[n.score_filter] || n.score_filter);
        return parts.join(" · ");
      } catch(e) { return `📊 Gap ≥${t}%`; }
    }
    return `${a.alert_type} ${t}`;
  };

  const alertTicker = (a) => {
    if (a.alert_type === "gap_scanner") return "Gap Scanner";
    return a.ticker;
  };

  const active  = alerts.filter(a =>  a.is_active);
  const history = alerts.filter(a => !a.is_active);

  return (
    <Card style={{marginBottom:16}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
        <SLabel>🔔 My alerts ({active.length} active)</SLabel>
        <button onClick={testEmail} style={{background:"none",border:"none",
          color:C.accent,fontSize:12,cursor:"pointer",fontFamily:"inherit"}}>
          Test email
        </button>
      </div>

      {/* Push notifications — account-wide toggle, covers all alert types */}
      <div style={{background:C.surface,borderRadius:10,border:`1px solid ${C.border}`,
        padding:"11px 14px",marginBottom:14}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10}}>
          <div>
            <div style={{fontSize:12,fontWeight:700,color:C.text}}>Push notifications</div>
            <div style={{fontSize:10,color:C.muted,marginTop:2,lineHeight:1.4}}>
              {pushState === 'enabled'
                ? 'Gap alerts + price alerts sent to this device'
                : pushState === 'denied'
                ? 'Blocked — enable in browser/OS settings then reload'
                : pushState === 'unsupported'
                ? 'Not supported in this browser'
                : 'Get alerts on this device even when the app is closed'}
            </div>
            {pushState === 'idle' && /iPhone|iPad/.test(navigator.userAgent) && !window.navigator.standalone && (
              <div style={{fontSize:9,color:C.muted,marginTop:4,lineHeight:1.5}}>
                iOS: add GemPulse to your Home Screen first
              </div>
            )}
          </div>
          <div style={{display:"flex",gap:6,flexShrink:0}}>
            {pushState === 'enabled' && (
              <button onClick={testPush} disabled={pushBusy}
                style={{padding:"5px 9px",borderRadius:6,border:`1px solid ${C.border}`,
                  background:"none",color:C.muted,fontFamily:"inherit",
                  fontSize:10,fontWeight:600,cursor:"pointer"}}>
                Test
              </button>
            )}
            {pushState === 'unsupported' || pushState === 'denied' ? null : (
              <button
                onClick={pushState === 'enabled' ? disablePush : enablePush}
                disabled={pushBusy || pushState === 'checking'}
                style={{padding:"6px 12px",borderRadius:7,border:"none",cursor:"pointer",
                  fontFamily:"inherit",fontSize:11,fontWeight:700,
                  background: pushState === 'enabled'
                    ? "rgba(255,82,82,0.12)" : "rgba(0,212,255,0.15)",
                  color: pushState === 'enabled' ? C.red : C.accent}}>
                {pushBusy ? "…"
                  : pushState === 'checking' ? "…"
                  : pushState === 'enabled'  ? "Disable"
                  : "Enable"}
              </button>
            )}
          </div>
        </div>
      </div>

      {testMsg && (
        <div style={{fontSize:12,marginBottom:10,padding:"6px 10px",borderRadius:7,
          background:testMsg.startsWith("✅")?"rgba(0,230,118,0.08)":"rgba(255,82,82,0.08)",
          color:testMsg.startsWith("✅")?C.green:C.red}}>
          {testMsg}
        </div>
      )}

      {loading && <div style={{fontSize:12,color:C.muted}}>Loading…</div>}

      {/* Active alerts */}
      {!loading && active.length === 0 && history.length === 0 && (
        <div style={{fontSize:12,color:C.muted,textAlign:"center",padding:"12px 0",lineHeight:1.7}}>
          No alerts yet.{" "}Open any stock → 🔔 Alerts tab to set one.
        </div>
      )}

      {active.map(a=>(
        <div key={a.id} style={{background:C.surface,borderRadius:10,
          border:`1px solid ${C.border}`,marginBottom:8,overflow:"hidden"}}>
          {/* Main row */}
          <div style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px"}}>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:13,fontWeight:700,color:C.text,fontFamily:"monospace"}}>
                {alertTicker(a)}
              </div>
              {/* lineHeight + wordBreak let long gap_scanner labels with
                  multiple filters wrap cleanly instead of truncating. */}
              <div style={{fontSize:11,color:C.muted,marginTop:2,
                lineHeight:1.4,wordBreak:"break-word"}}>{alertLabel(a)}</div>
            </div>
            <div style={{fontSize:10,color:C.green,flexShrink:0}}>● Active</div>
            {/* Edit button */}
            <button onClick={()=>{ setEditId(editId===a.id?null:a.id); setEditVal(String(a.target_value)); }}
              style={{padding:"5px 10px",borderRadius:7,border:"none",cursor:"pointer",
                fontFamily:"inherit",fontSize:11,fontWeight:600,flexShrink:0,
                background:editId===a.id?"rgba(0,212,255,0.2)":"rgba(0,212,255,0.08)",
                color:C.accent}}>
              {editId===a.id?"Cancel":"Edit"}
            </button>
            {/* Delete button */}
            <button onClick={()=>deleteAlert(a.id)}
              style={{padding:"5px 10px",borderRadius:7,border:"none",cursor:"pointer",
                fontFamily:"inherit",fontSize:11,fontWeight:600,flexShrink:0,
                background:"rgba(255,82,82,0.1)",color:C.red}}>
              ×
            </button>
          </div>
          {/* Inline edit row */}
          {editId===a.id && (
            <div style={{display:"flex",gap:8,padding:"0 14px 12px"}}>
              <input
                type="number" step="0.01" min="0"
                value={editVal}
                onChange={e=>setEditVal(e.target.value)}
                placeholder="New target value"
                style={{flex:1,background:C.card,border:`1px solid ${C.accent}44`,
                  borderRadius:8,padding:"8px 12px",color:C.text,fontSize:13,
                  fontFamily:"monospace",outline:"none"}}
              />
              <button onClick={()=>saveEdit(a)}
                style={{padding:"8px 14px",borderRadius:8,border:"none",cursor:"pointer",
                  background:C.accent,color:C.bg,fontSize:12,fontWeight:700,
                  fontFamily:"inherit",whiteSpace:"nowrap"}}>
                Save
              </button>
            </div>
          )}
        </div>
      ))}

      {/* Triggered history */}
      {history.length > 0 && (
        <div style={{marginTop:active.length>0?12:0}}>
          <div style={{fontSize:10,fontWeight:700,color:C.muted,letterSpacing:"0.08em",
            textTransform:"uppercase",marginBottom:6}}>
            Triggered ({history.length})
          </div>
          {history.map(a=>(
            <div key={a.id} style={{display:"flex",alignItems:"center",gap:10,
              background:C.surface,borderRadius:10,padding:"8px 14px",
              border:`1px solid ${C.border}`,marginBottom:6}}>
              <div style={{flex:1,minWidth:0,opacity:0.6}}>
                <span style={{fontSize:12,fontWeight:700,fontFamily:"monospace",
                  color:C.text}}>{alertTicker(a)}</span>
                <span style={{fontSize:11,color:C.muted,marginLeft:8}}>{alertLabel(a)}</span>
                {a.triggered_at && (
                  <div style={{fontSize:10,color:C.muted,marginTop:2}}>
                    Triggered {new Date(a.triggered_at+"Z").toLocaleDateString()}
                  </div>
                )}
              </div>
              {/* Clear / dismiss button */}
              <button onClick={()=>reactivate(a)}
                style={{padding:"5px 10px",borderRadius:7,border:"none",cursor:"pointer",
                  fontFamily:"inherit",fontSize:11,fontWeight:600,flexShrink:0,
                  background:"rgba(255,82,82,0.1)",color:C.red}}>
                Clear
              </button>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

// --- Admin Tools Panel --------------------------------------------------------
function AdminToolsPanel({ token, externalStats }) {
  // If the parent (AdminScreen) has already fetched stats, use it directly
  // instead of firing a second identical request.
  const [stats,        setStats]        = React.useState(externalStats || null);
  const [statsLoading, setStatsLoading] = React.useState(!externalStats);
  const [logoMsg,      setLogoMsg]      = React.useState("");
  const [logoLoading,  setLogoLoading]  = React.useState(false);
  const [logoStatus,   setLogoStatus]   = React.useState(null);
  const [logoStatLoading, setLogoStatLoading] = React.useState(false);
  const [logoFlushTicker, setLogoFlushTicker] = React.useState("");
  const [logoFlushMsg,    setLogoFlushMsg]    = React.useState("");
  const [univMsg,      setUnivMsg]      = React.useState("");
  const [univLoading,  setUnivLoading]  = React.useState(false);
  const [dynUniv,      setDynUniv]      = React.useState(null);   // dynamic-universe list
  const [dynLoading,   setDynLoading]   = React.useState(false);
  const [cacheMsg,     setCacheMsg]     = React.useState("");
  const [cacheLoading, setCacheLoading] = React.useState(false);
  const [discMsg,      setDiscMsg]      = React.useState("");
  const [discLoading,  setDiscLoading]  = React.useState(false);
  const [emailMsg,     setEmailMsg]     = React.useState("");
  const [emailLoading, setEmailLoading] = React.useState(false);
  const [earnWarmMsg,  setEarnWarmMsg]  = React.useState("");
  const [earnWarmLoad,  setEarnWarmLoad]  = React.useState(false);
  const [earnCapMsg,   setEarnCapMsg]   = React.useState("");
  const [earnCapLoad,  setEarnCapLoad]  = React.useState(false);

  // Sync when parent passes fresh stats (avoids duplicate /admin/stats fetch).
  // Falls back to own fetch only when no external stats provided.
  React.useEffect(() => {
    if (externalStats != null) { setStats(externalStats); setStatsLoading(false); return; }
    setStatsLoading(true);
    apiFetch("/admin/stats", token)
      .then(d => setStats(d))
      .catch(() => {})
      .finally(() => setStatsLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalStats]);

  const fetchLogoStatus = async () => {
    setLogoStatLoading(true);
    try {
      const d = await apiFetch("/admin/logo-status", token);
      setLogoStatus(d);
    } catch(e) { setLogoStatus(null); }
    finally { setLogoStatLoading(false); }
  };

  const refreshLogos = async (clear) => {
    setLogoLoading(true); setLogoMsg(""); setLogoStatus(null);
    try {
      const d = await apiFetch(`/admin/refresh-logos?clear=${clear}`, token, { method:"POST" });
      setLogoMsg(`✅ ${d.detail} (${d.tickers} tickers${d.placeholders_deleted ? `, ${d.placeholders_deleted} placeholders cleared` : ""})`);
    } catch(e) { setLogoMsg(`❌ ${e.message}`); }
    finally { setLogoLoading(false); }
  };

  const flushOneLogo = async () => {
    const t = logoFlushTicker.trim().toUpperCase();
    if (!t) return;
    setLogoFlushMsg("Flushing…");
    try {
      const d = await apiFetch(`/admin/logo/${t}`, token, { method:"DELETE" });
      setLogoFlushMsg(`✅ ${d.detail}`);
      setLogoFlushTicker("");
    } catch(e) { setLogoFlushMsg(`❌ ${e.message}`); }
  };

  const refreshUniverse = async () => {
    setUnivLoading(true); setUnivMsg("");
    try {
      const d = await apiFetch("/universe/trigger", token, { method:"POST" });
      setUnivMsg(`✅ ${d.detail}`);
    } catch(e) { setUnivMsg(`❌ ${e.message}`); }
    finally { setUnivLoading(false); }
  };

  const flushCache = async () => {
    setCacheLoading(true); setCacheMsg("");
    try {
      const d = await apiFetch("/admin/flush-cache", token, { method:"POST" });
      setCacheMsg(`✅ ${d.detail}`);
    } catch(e) { setCacheMsg(`❌ ${e.message}`); }
    finally { setCacheLoading(false); }
  };

  const loadDynamicUniverse = async () => {
    setDynLoading(true);
    try {
      const d = await apiFetch("/admin/dynamic-universe", token);
      setDynUniv(d);
    } catch(e) { setDynUniv({ dynamic: [], count: 0, error: e.message }); }
    finally { setDynLoading(false); }
  };

  const clearDiscovery = async () => {
    setDiscLoading(true); setDiscMsg("");
    try {
      await apiFetch("/discover/cache", token, { method:"DELETE" });
      setDiscMsg("✅ Discovery cache cleared");
    } catch(e) { setDiscMsg(`❌ ${e.message}`); }
    finally { setDiscLoading(false); }
  };

  const testGapEmail = async () => {
    setEmailLoading(true); setEmailMsg("");
    try {
      await apiFetch("/admin/test-gap-email", token, { method:"POST" });
      setEmailMsg("✅ Gap scanner email triggered — check inboxes");
    } catch(e) { setEmailMsg(`❌ ${e.message}`); }
    finally { setEmailLoading(false); }
  };

  // v8.4.325 — earnings maintenance triggers (background jobs; run in the api).
  const warmEarnings = async () => {
    setEarnWarmLoad(true); setEarnWarmMsg("");
    try {
      const d = await apiFetch("/admin/warm-earnings-calendar", token, { method:"POST" });
      setEarnWarmMsg(`✅ ${d.note || d.status || "warming started"} (~1-2 min → E chips populate)`);
    } catch(e) { setEarnWarmMsg(`❌ ${e.message}`); }
    finally { setEarnWarmLoad(false); }
  };
  const captureEarnings = async () => {
    setEarnCapLoad(true); setEarnCapMsg("");
    try {
      const d = await apiFetch("/admin/capture-earnings-results", token, { method:"POST" });
      setEarnCapMsg(`✅ ${d.note || d.status || "capture started"} (re-check the window in ~15 min)`);
    } catch(e) { setEarnCapMsg(`❌ ${e.message}`); }
    finally { setEarnCapLoad(false); }
  };

  const Btn = ({ onClick, disabled, danger, children }) => (
    <button onClick={onClick} disabled={disabled} style={{
      flex:1, padding:"9px 10px", borderRadius:8, border:`1px solid ${C.border}`,
      background: danger ? "rgba(255,82,82,0.1)" : "rgba(0,212,255,0.08)",
      color: disabled ? C.muted : C.text,
      fontSize:11, fontWeight:600, cursor: disabled ? "default" : "pointer",
      fontFamily:"inherit", textAlign:"center", transition:"all 0.15s",
    }}>{children}</button>
  );

  const Msg = ({ msg }) => msg ? (
    <div style={{fontSize:11,marginTop:5,color:msg.startsWith("✅") ? C.green : C.red}}>{msg}</div>
  ) : null;

  const Section = ({ title, children }) => (
    <div style={{marginBottom:14,paddingBottom:14,borderBottom:`1px solid ${C.divider}`}}>
      <div style={{fontSize:11,fontWeight:700,color:C.muted,letterSpacing:"0.07em",
        textTransform:"uppercase",marginBottom:8}}>{title}</div>
      {children}
    </div>
  );

  return (
    <div style={{background:C.surface,borderRadius:12,padding:"14px 16px",
      marginBottom:12,border:"1px solid rgba(255,202,40,0.25)"}}>
      <div style={{fontSize:10,fontWeight:700,color:"rgba(255,202,40,0.8)",
        letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:14}}>
        🛠 Admin Tools
      </div>

      {/* Stats dashboard */}
      <Section title="Platform Stats">
        {statsLoading ? (
          <div style={{fontSize:11,color:C.muted}}>Loading…</div>
        ) : stats ? (
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8}}>
            {[
              {label:"Total Users",   value: stats.users?.total},
              {label:"Active Today",  value: stats.users?.active_today},
              {label:"Active 7d",     value: stats.users?.active_7d},
              {label:"Active Alerts", value: stats.alerts?.active},
              {label:"Triggered 7d",  value: stats.alerts?.triggered_7d},
              {label:"Tickers Scored",value: stats.scores_today?.tickers_scored},
            ].map(({label,value}) => (
              <div key={label} style={{background:C.bg,borderRadius:8,padding:"8px 10px",
                border:`1px solid ${C.border}`}}>
                <div style={{fontSize:9,color:C.muted,textTransform:"uppercase",
                  letterSpacing:"0.06em",marginBottom:3}}>{label}</div>
                <div style={{fontSize:16,fontWeight:800,fontFamily:"monospace",
                  color:C.text}}>{value ?? "—"}</div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{fontSize:11,color:C.muted}}>Stats unavailable</div>
        )}
      </Section>

      {/* Universe */}
      <Section title="Universe & Scores">
        <div style={{fontSize:11,color:C.muted,marginBottom:8}}>
          Trigger an immediate full re-score of all ~810 tickers (Russell 1000
          + TSX 100 + ETFs) plus any dynamic-universe additions. Takes ~10 min
          to complete with 8 parallel workers.
        </div>
        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
          <Btn onClick={refreshUniverse} disabled={univLoading}>
            {univLoading ? "Triggering…" : "⚡ Refresh Universe"}
          </Btn>
          <Btn onClick={async () => {
            // Force-reload the app to the latest Vercel build.
            // Unregisters any service workers and clears browser caches so
            // PWA home-screen installs pick up new deployments immediately.
            try {
              if ('serviceWorker' in navigator) {
                const regs = await navigator.serviceWorker.getRegistrations();
                await Promise.all(regs.map(r => r.unregister()));
              }
              if ('caches' in window) {
                const names = await caches.keys();
                await Promise.all(names.map(n => caches.delete(n)));
              }
            } catch(_) {}
            window.location.reload(true);
          }}>
            🔁 Reload App
          </Btn>
        </div>
        <Msg msg={univMsg}/>
      </Section>

      {/* Dynamic Universe */}
      <Section title="Dynamic Universe">
        <div style={{fontSize:11,color:C.muted,marginBottom:8}}>
          Tickers auto-promoted on demand (searched / clicked / watchlisted but not
          in the static base list). 200-cap set — names worth keeping should be
          added to <code style={{color:C.text}}>ticker_universe.py</code> permanently.
        </div>
        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
          <Btn onClick={loadDynamicUniverse} disabled={dynLoading}>
            {dynLoading ? "Loading…" : "📋 List Dynamic Universe"}
          </Btn>
        </div>
        {dynUniv && (
          <div style={{marginTop:10}}>
            {dynUniv.error ? (
              <div style={{fontSize:11,color:C.red}}>❌ {dynUniv.error}</div>
            ) : (
              <>
                <div style={{fontSize:11,color:C.muted,marginBottom:6}}>
                  {dynUniv.count} ticker{dynUniv.count===1?"":"s"} in the dynamic set
                </div>
                <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                  {(dynUniv.dynamic||[]).length === 0 ? (
                    <span style={{fontSize:11,color:C.muted}}>— empty —</span>
                  ) : (dynUniv.dynamic||[]).map(t => (
                    <span key={t} style={{fontSize:11,fontFamily:"monospace",fontWeight:700,
                      color:C.text,background:C.bg,border:`1px solid ${C.border}`,
                      borderRadius:6,padding:"3px 8px"}}>{t}</span>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </Section>

      {/* Cache */}
      <Section title="Cache Management">
        <div style={{fontSize:11,color:C.muted,marginBottom:8}}>
          Flush all API in-memory caches: price · yfinance history · gap scanner
          · sentiment · logo-negative-cache (v8.3.6) · Polygon dividends /
          Form-4 / short-volume (v8.3.x). Use after deploying sector overrides
          or when stale data is suspected. "Clear Discovery" wipes just the
          /discover route's Redis cache (Speculation / Zombie / Trending).
        </div>
        <div style={{display:"flex",gap:8}}>
          <Btn onClick={flushCache} disabled={cacheLoading}>
            {cacheLoading ? "Flushing…" : "🗑 Flush API Cache"}
          </Btn>
          <Btn onClick={clearDiscovery} disabled={discLoading}>
            {discLoading ? "Clearing…" : "🔄 Clear Discovery"}
          </Btn>
        </div>
        <Msg msg={cacheMsg || discMsg}/>
      </Section>

      {/* Earnings maintenance (v8.4.325) */}
      <Section title="Earnings Data">
        <div style={{fontSize:11,color:C.muted,marginBottom:8}}>
          <b>Warm Calendar</b> rebuilds the whole-universe upcoming-earnings cache that
          feeds the Scanner/Discover “E” chips (runs the per-symbol scan; ~1-2 min to start
          populating). <b>Capture Results</b> stores the day’s reporters’ beat/miss into the
          DB and self-heals legacy quarter-end window dates (e.g. a report mis-dated to 06/30).
          Both also run on a daily schedule; use these to run them now.
        </div>
        <div style={{display:"flex",gap:8}}>
          <Btn onClick={warmEarnings} disabled={earnWarmLoad}>
            {earnWarmLoad ? "Warming…" : "🗓 Warm Calendar"}
          </Btn>
          <Btn onClick={captureEarnings} disabled={earnCapLoad}>
            {earnCapLoad ? "Capturing…" : "📥 Capture Results"}
          </Btn>
        </div>
        <Msg msg={earnWarmMsg || earnCapMsg}/>
      </Section>

      {/* Emails */}
      <Section title="Email & Alerts">
        <div style={{fontSize:11,color:C.muted,marginBottom:8}}>
          Manually fire the 9:45 AM ET gap scanner digest to all subscribed
          users. Bypasses the throttle but still respects each user's saved
          filters — so if your filters are strict, no email lands. Pending
          bug-fix session may change this flow (see memory file).
        </div>
        <div style={{display:"flex",gap:8}}>
          <Btn onClick={testGapEmail} disabled={emailLoading}>
            {emailLoading ? "Sending…" : "📧 Send Gap Alert Email"}
          </Btn>
        </div>
        <Msg msg={emailMsg}/>
      </Section>

      {/* Company Logos */}
      <div>
        <div style={{fontSize:11,fontWeight:700,color:C.muted,letterSpacing:"0.07em",
          textTransform:"uppercase",marginBottom:8}}>Company Logos</div>
        <div style={{fontSize:11,color:C.muted,marginBottom:8}}>
          Fill missing / replace Parqet placeholders (safe anytime) or wipe and re-download all.
        </div>
        <div style={{display:"flex",gap:8,marginBottom:6}}>
          <Btn onClick={fetchLogoStatus} disabled={logoStatLoading}>
            {logoStatLoading ? "Checking…" : "🔍 Check Status"}
          </Btn>
          <Btn onClick={()=>refreshLogos(false)} disabled={logoLoading}>
            {logoLoading ? "Running…" : "⬇ Fill / Fix"}
          </Btn>
          <Btn onClick={()=>refreshLogos(true)} disabled={logoLoading} danger>
            {logoLoading ? "Running…" : "♻ Wipe All"}
          </Btn>
        </div>
        {logoStatus && (
          <div style={{background:C.bg,borderRadius:8,padding:"8px 10px",
            border:`1px solid ${C.border}`,fontSize:11,marginBottom:6}}>
            <div style={{display:"flex",gap:12,flexWrap:"wrap",marginBottom:4}}>
              <span style={{color:C.green}}>✅ {logoStatus.cached_real} real logos</span>
              <span style={{color:C.yellow}}>⚠ {logoStatus.cached_placeholder} placeholders</span>
              <span style={{color:C.muted}}>❌ {logoStatus.missing_from_disk} missing</span>
              <span style={{color:C.muted}}>🚫 {logoStatus.negative_cache_active} neg-cached</span>
            </div>
            {logoStatus.placeholder_tickers?.length > 0 && (
              <div style={{color:C.yellow,fontSize:10,marginTop:2}}>
                Placeholder: {logoStatus.placeholder_tickers.map(p=>p.ticker).join(", ")}
              </div>
            )}
            {logoStatus.missing_sample?.length > 0 && (
              <div style={{color:C.muted,fontSize:10,marginTop:2}}>
                Missing: {logoStatus.missing_sample.join(", ")}
              </div>
            )}
          </div>
        )}
        <Msg msg={logoMsg}/>
        {/* Single-ticker logo flush — useful when a wrong logo was cached (e.g. FMP placeholder) */}
        <div style={{marginTop:10,paddingTop:10,borderTop:`1px solid ${C.border}`}}>
          <div style={{fontSize:11,color:C.muted,marginBottom:6}}>Flush one ticker's cached logo (re-fetches on next request)</div>
          <div style={{display:"flex",gap:6,alignItems:"center"}}>
            <input
              value={logoFlushTicker}
              onChange={e=>setLogoFlushTicker(e.target.value.toUpperCase())}
              onKeyDown={e=>e.key==="Enter"&&flushOneLogo()}
              placeholder="e.g. VRT"
              maxLength={10}
              style={{
                background:C.bg,border:`1px solid ${C.border}`,borderRadius:6,
                color:C.text,fontSize:12,padding:"5px 8px",width:80,
                fontFamily:"monospace",textTransform:"uppercase"
              }}
            />
            <Btn onClick={flushOneLogo} disabled={!logoFlushTicker.trim()}>🗑 Flush</Btn>
          </div>
          {logoFlushMsg && <div style={{fontSize:10,color:logoFlushMsg.startsWith("✅")?C.green:C.red,marginTop:4}}>{logoFlushMsg}</div>}
        </div>
      </div>
    </div>
  );
}


// --- Member referral / invite panel (P1-3) ----------------------------------
// Each member gets a few founder-tier, single-use invite codes to share.
// Backend lazily provisions them on first GET /me/invite-codes.
function InvitePanel({ token, onAuthError }) {
  const [data, setData]       = React.useState(null);   // { target, codes:[] }
  const [loading, setLoading] = React.useState(true);
  const [copied,  setCopied]  = React.useState(null);   // code string just copied

  React.useEffect(() => {
    if (!token) return;
    apiFetch("/me/invite-codes", token)
      .then(d => setData(d))
      .catch(e => {
        if (e?.name === "AuthError" && onAuthError) onAuthError();
        setData({ target: 0, codes: [] });
      })
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const copyLink = async (c) => {
    try {
      await navigator.clipboard.writeText(c.invite_url);
      setCopied(c.code);
      setTimeout(() => setCopied(prev => (prev === c.code ? null : prev)), 1800);
    } catch {}
  };

  const codes    = data?.codes || [];
  const total    = codes.length;
  const redeemed = codes.filter(c => c.status === "redeemed").length;

  return (
    <Card style={{marginBottom:16, borderColor:"rgba(0,212,255,0.2)"}}>
      <SLabel>Invite friends · Founder tier</SLabel>
      <div style={{fontSize:12,color:C.muted,marginBottom:14,lineHeight:1.6}}>
        You have founder-tier invites to share. Anyone who joins with your link
        locks in <strong style={{color:C.text}}>permanent founder pricing</strong> before
        Stripe billing launches — only transferable now.
      </div>

      {loading ? (
        <div style={{fontSize:12,color:C.muted}}>Loading your invites…</div>
      ) : total === 0 ? (
        <div style={{fontSize:12,color:C.muted}}>No invites available yet.</div>
      ) : (
        <>
          <div style={{fontSize:11,color:C.muted,marginBottom:10}}>
            {redeemed} of {total} redeemed
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            {codes.map(c => {
              const used = c.status === "redeemed";
              return (
                <div key={c.code} style={{
                  display:"flex",alignItems:"center",gap:10,padding:"10px 12px",
                  borderRadius:10,border:`1px solid ${C.border}`,
                  background:C.surface, opacity: used ? 0.55 : 1,
                }}>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:13,fontWeight:700,color:C.text,
                      fontFamily:"monospace",letterSpacing:0.5,overflow:"hidden",
                      textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.code}</div>
                    <div style={{fontSize:11,color: used ? C.green : C.muted,marginTop:2}}>
                      {used ? "✓ Redeemed" : "Available"}
                    </div>
                  </div>
                  {!used && (
                    <button onClick={()=>copyLink(c)} style={{
                      padding:"7px 12px",borderRadius:8,border:"none",cursor:"pointer",
                      background: copied===c.code ? C.green : C.accent,
                      color:C.bg,fontSize:12,fontWeight:700,fontFamily:"inherit",
                      whiteSpace:"nowrap",
                    }}>{copied===c.code ? "Copied ✓" : "Copy link"}</button>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </Card>
  );
}

// --- Settings / Profile screen ------------------------------------------------
function SettingsScreen({ user, onProfileChange, onLogout, token, onAuthError, onAdmin, currency, onCurrencyChange, tradePref, onTradePrefChange, alertsRefreshKey, textScale, onTextScaleChange }) {
  const [apiVersion, setApiVersion] = React.useState(null);
  React.useEffect(() => {
    fetch(API + "/health")
      .then(r => r.json())
      .then(d => setApiVersion(d.version || null))
      .catch(() => {});
  }, []);
  const [showPwForm,      setShowPwForm]      = React.useState(false);
  const [pwMsg,           setPwMsg]           = React.useState("");
  const [pwLoading,       setPwLoading]       = React.useState(false);
  const [curPw,           setCurPw]           = React.useState("");
  const [newPw,           setNewPw]           = React.useState("");
  const [confirmPw,       setConfirmPw]       = React.useState("");
  // v8.4.0 — daily market digest opt-in
  const [digestSaving,    setDigestSaving]    = React.useState(false);

  // Initialise from localStorage immediately — no flash on re-mount.
  // localStorage key is per-user so multi-account devices stay isolated.
  const _digestKey = `digestOptIn_${user?.id || "guest"}`;
  const _localDigest = localStorage.getItem(_digestKey);
  const [digestOptIn, setDigestOptIn] = React.useState(
    _localDigest !== null ? _localDigest === "true" : null
  );

  const _setDigest = (val) => {
    setDigestOptIn(val);
    localStorage.setItem(_digestKey, String(val));
  };

  React.useEffect(() => {
    if (!token) return;
    apiFetch("/user/market-digest", token)
      .then(d => {
        const serverVal = !!d.opt_in;
        const localVal  = _localDigest !== null ? _localDigest === "true" : null;
        if (localVal !== null && localVal !== serverVal) {
          // Local preference differs from server (e.g. toggled before VM was deployed).
          // Trust local — sync server silently to match.
          apiFetch("/user/market-digest", token, {
            method: "PUT",
            body: JSON.stringify({ opt_in: localVal }),
          }).catch(() => {});
          // State is already correct from the useState initializer — no update needed.
        } else {
          _setDigest(serverVal);
        }
      })
      .catch(() => {
        // Endpoint unavailable — keep localStorage value; default false if none.
        if (_localDigest === null) setDigestOptIn(false);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleDigestToggle = async () => {
    if (digestSaving) return;
    const newVal = !digestOptIn;
    _setDigest(newVal);          // persist locally immediately — survives re-mount
    setDigestSaving(true);
    try {
      await apiFetch("/user/market-digest", token, {
        method: "PUT",
        body: JSON.stringify({ opt_in: newVal }),
      });
    } catch(e) {
      // Don't revert — user's explicit choice is persisted in localStorage.
      // Once the VM is deployed with this endpoint the next PUT will sync it.
      // Only revert for auth errors (account deactivated / session expired).
      if (e?.name === "AuthError" || e?.message?.includes("Session expired")) {
        _setDigest(!newVal);
      }
    } finally {
      setDigestSaving(false);
    }
  };

  // ── Weekly recap opt-in (Option B) — mirrors the daily toggle ──────────────
  const _weeklyKey = `weeklyOptIn_${user?.id || "guest"}`;
  const _localWeekly = localStorage.getItem(_weeklyKey);
  const [weeklyOptIn, setWeeklyOptIn] = React.useState(
    _localWeekly !== null ? _localWeekly === "true" : null
  );
  const [weeklySaving, setWeeklySaving] = React.useState(false);
  const _setWeekly = (val) => { setWeeklyOptIn(val); localStorage.setItem(_weeklyKey, String(val)); };
  React.useEffect(() => {
    if (!token) return;
    apiFetch("/user/weekly-digest", token)
      .then(d => {
        const serverVal = !!d.opt_in;
        const localVal  = _localWeekly !== null ? _localWeekly === "true" : null;
        if (localVal !== null && localVal !== serverVal) {
          apiFetch("/user/weekly-digest", token, { method:"PUT", body: JSON.stringify({ opt_in: localVal }) }).catch(()=>{});
        } else { _setWeekly(serverVal); }
      })
      .catch(() => { if (_localWeekly === null) setWeeklyOptIn(false); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);
  const handleWeeklyToggle = async () => {
    if (weeklySaving) return;
    const newVal = !weeklyOptIn;
    _setWeekly(newVal);
    setWeeklySaving(true);
    try {
      await apiFetch("/user/weekly-digest", token, { method:"PUT", body: JSON.stringify({ opt_in: newVal }) });
    } catch(e) {
      if (e?.name === "AuthError" || e?.message?.includes("Session expired")) _setWeekly(!newVal);
    } finally { setWeeklySaving(false); }
  };

  // Feedback form state
  const [fbCategory,  setFbCategory]  = React.useState("bug");
  const [fbMessage,   setFbMessage]   = React.useState("");
  const [fbSending,   setFbSending]   = React.useState(false);
  const [fbMsg,       setFbMsg]       = React.useState("");

  const handleFeedback = async () => {
    if (!fbMessage.trim()) { setFbMsg("❌ Please enter a message"); return; }
    setFbSending(true); setFbMsg("");
    try {
      await apiFetch("/feedback", token, {
        method: "POST",
        body: JSON.stringify({ category: fbCategory, message: fbMessage.trim() }),
      });
      setFbMsg("✅ Sent! Thanks for the feedback.");
      setFbMessage("");
    } catch(e) { setFbMsg(`❌ ${e.message}`); }
    finally { setFbSending(false); }
  };

  const inp = {
    background:C.surface, border:`1px solid ${C.border}`,
    borderRadius:10, padding:"10px 12px", color:C.text,
    fontSize:16, fontFamily:"inherit", outline:"none", width:"100%",
    boxSizing:"border-box",
  };

  const handleChangePassword = async () => {
    if (newPw !== confirmPw) { setPwMsg("❌ Passwords don't match"); return; }
    if (newPw.length < 8)    { setPwMsg("❌ Minimum 8 characters");   return; }
    setPwLoading(true); setPwMsg("");
    try {
      await apiFetch("/auth/change-password", token, {
        method:"POST",
        body: JSON.stringify({ current_password:curPw, new_password:newPw }),
      });
      setPwMsg("✅ Password updated");
      setCurPw(""); setNewPw(""); setConfirmPw(""); setShowPwForm(false);
    } catch(e) { setPwMsg(`❌ ${e.message}`); }
    finally { setPwLoading(false); }
  };

  return (
    <div style={{padding:"0 16px 100px"}}>
      <FrozenHeader padTop={16}>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <AppLogo size={32}/>
          <div style={{fontSize:20,fontWeight:900,color:C.text,letterSpacing:"-0.02em",lineHeight:1}}>
            Settings
          </div>
        </div>
      </FrozenHeader>
      <div style={{height:16}}/>
      {/* Admin tools moved to Admin Dashboard — see 🛡 Admin Dashboard button below */}

      {/* Member referral / founder invites (P1-3) */}
      <InvitePanel token={token} onAuthError={onAuthError} />

      {/* Beta feedback */}
      <Card style={{marginBottom:16,borderColor:"rgba(0,212,255,0.2)"}}>
        <SLabel>Beta feedback</SLabel>
        <div style={{fontSize:12,color:C.muted,marginBottom:14,lineHeight:1.6}}>
          Found a bug or have a suggestion? Let us know — every report goes
          directly to the GemPulse team.
        </div>
        {/* Category pills */}
        <div style={{display:"flex",gap:8,marginBottom:12,flexWrap:"wrap"}}>
          {[
            {key:"bug",     label:"🐛 Bug report"},
            {key:"feature", label:"💡 Feature idea"},
            {key:"general", label:"💬 General"},
          ].map(o => (
            <button key={o.key} onClick={()=>setFbCategory(o.key)} style={{
              padding:"6px 12px",borderRadius:20,border:"none",cursor:"pointer",
              fontFamily:"inherit",fontSize:12,fontWeight:600,
              background: fbCategory===o.key ? C.accent : C.surface,
              color:       fbCategory===o.key ? C.bg     : C.muted,
              transition:"background 0.15s",
            }}>{o.label}</button>
          ))}
        </div>
        <textarea
          rows={4}
          placeholder={
            fbCategory === "bug"     ? "Describe what happened and how to reproduce it…" :
            fbCategory === "feature" ? "What would you like to see in GemPulse?" :
                                       "Share your thoughts…"
          }
          value={fbMessage}
          onChange={e => setFbMessage(e.target.value)}
          style={{...inp, resize:"vertical", marginBottom:10}}
        />
        {fbMsg && (
          <div style={{fontSize:12,color:fbMsg.startsWith("✅")?C.green:C.red,
            marginBottom:8}}>{fbMsg}</div>
        )}
        <button onClick={handleFeedback} disabled={fbSending} style={{
          width:"100%",padding:"10px",borderRadius:10,border:"none",cursor:"pointer",
          background:C.accent,color:C.bg,fontSize:13,fontWeight:700,
          fontFamily:"inherit",opacity:fbSending?0.7:1,
        }}>{fbSending ? "Sending…" : "Send feedback"}</button>
      </Card>

      {/* Change password */}
      <Card style={{marginBottom:16}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <SLabel>Change password</SLabel>
          <button onClick={()=>{setShowPwForm(!showPwForm);setPwMsg("");}} style={{
            background:"none",border:"none",color:C.accent,
            fontSize:12,cursor:"pointer",fontFamily:"inherit",
          }}>{showPwForm?"Cancel":"Change"}</button>
        </div>
        {showPwForm && (
          <div style={{display:"flex",flexDirection:"column",gap:8,marginTop:8}}>
            <input style={inp} type="password" placeholder="Current password"
              value={curPw} onChange={e=>setCurPw(e.target.value)}/>
            <input style={inp} type="password" placeholder="New password (min 6 chars)"
              value={newPw} onChange={e=>setNewPw(e.target.value)}/>
            <input style={inp} type="password" placeholder="Confirm new password"
              value={confirmPw} onChange={e=>setConfirmPw(e.target.value)}/>
            {pwMsg && <div style={{fontSize:12,
              color:pwMsg.startsWith("✅")?C.green:C.red}}>{pwMsg}</div>}
            <button onClick={handleChangePassword} disabled={pwLoading} style={{
              padding:"10px",borderRadius:10,border:"none",cursor:"pointer",
              background:C.accent,color:C.bg,fontSize:13,fontWeight:700,
              fontFamily:"inherit",opacity:pwLoading?0.7:1,
            }}>{pwLoading?"Updating…":"Update Password"}</button>
          </div>
        )}
      </Card>

      {/* Risk profile */}
      <Card style={{marginBottom:16}}>
        <SLabel>Your risk profile</SLabel>
        <div style={{fontSize:12,color:C.muted,marginBottom:16,lineHeight:1.6}}>
          This setting personalises every score and recommendation to match your investing style.
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          {PROFILES.map(p=>(
            <button key={p.key} onClick={()=>onProfileChange(p.key)} style={{
              display:"flex",alignItems:"center",gap:14,padding:"14px 16px",
              borderRadius:12,border:`2px solid ${user.risk_profile===p.key?C.accent:C.border}`,
              background:user.risk_profile===p.key?"rgba(0,212,255,0.08)":C.surface,
              cursor:"pointer",textAlign:"left",fontFamily:"inherit",
            }}>
              <span style={{fontSize:28}}>{p.emoji}</span>
              <div style={{flex:1}}>
                <div style={{fontSize:14,fontWeight:700,color:C.text}}>{p.label}</div>
                <div style={{fontSize:12,color:C.muted,marginTop:2}}>{p.desc}</div>
              </div>
              {user.risk_profile===p.key &&
                <div style={{width:20,height:20,borderRadius:"50%",
                  background:C.accent,display:"flex",alignItems:"center",justifyContent:"center"}}>
                  <span style={{color:C.bg,fontSize:12,fontWeight:800}}>✓</span>
                </div>}
            </button>
          ))}
        </div>
      </Card>



      {/* Trading preference */}
      <Card style={{marginBottom:16}}>
        <SLabel>Trading preference</SLabel>
        <div style={{fontSize:12,color:C.muted,marginBottom:12,lineHeight:1.6}}>
          Personalises the final Buy / Sell signal label to favour your preferred strategy.
          Both scores remain visible and unchanged.
        </div>
        <div style={{display:"flex",gap:8}}>
          {[
            {key:"swing",   label:"Swing",     desc:"Short-term momentum"},
            {key:"blended", label:"Blended",   desc:"Balanced approach"},
            {key:"lt",      label:"Long-Term", desc:"Fundamentals first"},
          ].map(opt => (
            <button key={opt.key}
              onClick={()=>onTradePrefChange && onTradePrefChange(opt.key)}
              style={{flex:1,padding:"10px 6px",borderRadius:10,textAlign:"center",
                fontFamily:"inherit",cursor:"pointer",
                border:`1px solid ${tradePref===opt.key?C.accent:C.border}`,
                background:tradePref===opt.key?"rgba(0,212,255,0.08)":C.surface}}>
              <div style={{fontSize:12,fontWeight:700,
                color:tradePref===opt.key?C.accent:C.text}}>{opt.label}</div>
              <div style={{fontSize:9,color:C.muted,marginTop:2,lineHeight:1.3}}>{opt.desc}</div>
            </button>
          ))}
        </div>
      </Card>

      {/* Currency display */}
      <Card style={{marginBottom:16}}>
        <SLabel>Currency display</SLabel>
        <div style={{fontSize:12,color:C.muted,marginBottom:12,lineHeight:1.6}}>
          Prices shown in native currency by default. Switch to convert all prices using live FX rates.
        </div>
        <div style={{display:"flex",gap:8}}>
          {[
            {key:"native", label:"Native", desc:"CA$ / US$"},
            {key:"usd",    label:"USD",    desc:"All US$"},
            {key:"cad",    label:"CAD",    desc:"All CA$"},
          ].map(opt => (
            <button key={opt.key}
              onClick={()=>onCurrencyChange && onCurrencyChange(opt.key)}
              style={{flex:1,padding:"10px 8px",borderRadius:10,textAlign:"center",
                fontFamily:"inherit",cursor:"pointer",
                border:`1px solid ${currency===opt.key?C.accent:C.border}`,
                background:currency===opt.key?"rgba(0,212,255,0.08)":C.surface}}>
              <div style={{fontSize:13,fontWeight:700,
                color:currency===opt.key?C.accent:C.text}}>{opt.label}</div>
              <div style={{fontSize:10,color:C.muted,marginTop:2}}>{opt.desc}</div>
            </button>
          ))}
        </div>
      </Card>

      {/* v8.4.308: accessibility text size — scales the whole app via root zoom. */}
      <Card style={{marginBottom:16}}>
        <SLabel>Text size</SLabel>
        <div style={{fontSize:12,color:C.muted,marginBottom:12,lineHeight:1.6}}>
          Make everything larger and easier to read. Applies across the whole app.
        </div>
        <div style={{display:"flex",gap:8}}>
          {[
            {key:"1",    label:"Normal",  desc:"Default"},
            {key:"1.15", label:"Large",   desc:"+15%"},
            {key:"1.3",  label:"Larger",  desc:"+30%"},
          ].map(opt => (
            <button key={opt.key}
              onClick={()=>onTextScaleChange && onTextScaleChange(opt.key)}
              style={{flex:1,padding:"10px 8px",borderRadius:10,textAlign:"center",
                fontFamily:"inherit",cursor:"pointer",
                border:`1px solid ${textScale===opt.key?C.accent:C.border}`,
                background:textScale===opt.key?"rgba(0,212,255,0.08)":C.surface}}>
              <div style={{fontSize:13,fontWeight:700,
                color:textScale===opt.key?C.accent:C.text}}>{opt.label}</div>
              <div style={{fontSize:10,color:C.muted,marginTop:2}}>{opt.desc}</div>
            </button>
          ))}
        </div>
      </Card>

      <Card style={{marginBottom:16}}>
        <SLabel>Help &amp; tours</SLabel>
        <div style={{fontSize:12,color:C.muted,marginBottom:12,lineHeight:1.6}}>
          Missed one of the guided walkthroughs, or want a refresher? Replay any of them below.
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          {TOUR_REGISTRY.map(t => (
            <div key={t.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,
              padding:"9px 10px",borderRadius:10,background:C.surface,border:`1px solid ${C.border}`}}>
              <div>
                <div style={{fontSize:12,fontWeight:700,color:C.text}}>{t.label}</div>
                <div style={{fontSize:10,color:C.muted,marginTop:1}}>{t.desc}</div>
              </div>
              <button onClick={()=>{ _resetTour(t.id, t.version); alert(`"${t.label}" will show again the next time you open that screen.`); }}
                style={{padding:"6px 12px",borderRadius:9,border:`1px solid ${C.accent}`,background:"rgba(0,212,255,0.1)",
                  color:C.accent,fontWeight:700,fontSize:11,cursor:"pointer",fontFamily:"inherit",flexShrink:0}}>Replay</button>
            </div>
          ))}
        </div>
        <button onClick={()=>{ _resetAllTours(); alert("All tours will show again as you revisit each screen."); }}
          style={{marginTop:10,width:"100%",padding:"9px",borderRadius:10,border:`1px solid ${C.border}`,
            background:"none",color:C.muted,fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>
          Replay all tours
        </button>
      </Card>

      <Card style={{marginBottom:16}}>
        <SLabel>About the scores</SLabel>
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          {[
            ["📈","Swing Score","Days to weeks. Based on RSI, MACD, chart patterns, volume and momentum."],
            ["📊","Long-Term Score","Months to years. Based on fundamentals: P/E, earnings growth, financial stability."],
            ["📰","Sentiment Score","AI-scored news headlines from −1.0 (very bearish) to +1.0 (very bullish)."],
          ].map(([icon,title,desc])=>(
            <div key={title} style={{display:"flex",gap:12,alignItems:"flex-start"}}>
              <span style={{fontSize:20,marginTop:1}}>{icon}</span>
              <div>
                <div style={{fontSize:13,fontWeight:600,color:C.text}}>{title}</div>
                <div style={{fontSize:12,color:C.muted,marginTop:3,lineHeight:1.6}}>{desc}</div>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Daily Market Digest opt-in (v8.4.0) */}
      <Card style={{marginBottom:16}}>
        <SLabel>📊 Daily Market Wrap</SLabel>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:12}}>
          <div style={{flex:1}}>
            <div style={{fontSize:13,fontWeight:600,color:C.text,marginBottom:3}}>
              AI Market Digest — 4:30pm ET
            </div>
            <div style={{fontSize:12,color:C.muted,lineHeight:1.6}}>
              Daily email after market close. Top movers, sector rotation, macro context,
              and what to watch tomorrow — powered by Gemini + live market data.
            </div>
          </div>
          {/* Toggle switch */}
          <button
            onClick={handleDigestToggle}
            disabled={digestOptIn === null || digestSaving}
            style={{
              flexShrink:0, width:48, height:26, borderRadius:13, border:"none",
              cursor: digestOptIn===null||digestSaving ? "not-allowed" : "pointer",
              background: digestOptIn ? C.accent : "rgba(255,255,255,0.12)",
              position:"relative", transition:"background 0.2s",
              opacity: digestOptIn===null ? 0.5 : 1,
            }}
          >
            <div style={{
              position:"absolute", top:3,
              left: digestOptIn ? 25 : 3,
              width:20, height:20, borderRadius:"50%",
              background: "#fff",
              transition:"left 0.2s",
              boxShadow:"0 1px 4px rgba(0,0,0,0.3)",
            }}/>
          </button>
        </div>
        {digestOptIn && (
          <div style={{marginTop:10,fontSize:11,color:C.accent,
            background:"rgba(0,212,255,0.06)",borderRadius:8,padding:"6px 10px"}}>
            ✓ You'll receive the daily market wrap at ~4:30pm ET on trading days.
          </div>
        )}
      </Card>

      {/* Weekly Recap opt-in (Option B) */}
      <Card style={{marginBottom:16}}>
        <SLabel>🗓 Weekly Recap</SLabel>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:12}}>
          <div style={{flex:1}}>
            <div style={{fontSize:13,fontWeight:600,color:C.text,marginBottom:3}}>
              Weekly Recap & Macro Outlook — Fri 4:45pm ET
            </div>
            <div style={{fontSize:12,color:C.muted,lineHeight:1.6}}>
              The week in review + the macro/regime arc + what to watch next week. If you
              also have the daily on, Friday sends the weekly instead (no double email).
            </div>
          </div>
          <button
            onClick={handleWeeklyToggle}
            disabled={weeklyOptIn === null || weeklySaving}
            style={{
              flexShrink:0, width:48, height:26, borderRadius:13, border:"none",
              cursor: weeklyOptIn===null||weeklySaving ? "not-allowed" : "pointer",
              background: weeklyOptIn ? C.accent : "rgba(255,255,255,0.12)",
              position:"relative", transition:"background 0.2s",
              opacity: weeklyOptIn===null ? 0.5 : 1,
            }}
          >
            <div style={{
              position:"absolute", top:3, left: weeklyOptIn ? 25 : 3,
              width:20, height:20, borderRadius:"50%", background:"#fff",
              transition:"left 0.2s", boxShadow:"0 1px 4px rgba(0,0,0,0.3)",
            }}/>
          </button>
        </div>
        {weeklyOptIn && (
          <div style={{marginTop:10,fontSize:11,color:C.accent,
            background:"rgba(0,212,255,0.06)",borderRadius:8,padding:"6px 10px"}}>
            ✓ You'll receive the weekly recap at ~4:45pm ET each Friday.
          </div>
        )}
      </Card>

      {/* All alerts */}
      <AlertsPanel token={token} user={user} refreshKey={alertsRefreshKey}/>

      {/* App version */}
      <div style={{background:C.surface,borderRadius:12,padding:"12px 16px",
        marginBottom:12,border:`1px solid ${C.border}`}}>
        <div style={{fontSize:10,fontWeight:700,color:C.muted,letterSpacing:"0.08em",
          textTransform:"uppercase",marginBottom:6}}>App Version</div>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <div>
            <div style={{fontSize:15,fontWeight:700,color:C.text,fontFamily:"monospace"}}>
              v{apiVersion || "—"}
            </div>
            <div style={{fontSize:10,color:C.muted,marginTop:2}}>
              {apiVersion ? "GemPulse API · live" : "Checking…"}
            </div>
            {/* v8.4.318: FRONTEND bundle hash so a stale UI cache is visible (the version
                above is the BACKEND; they update independently). Compare to the deployed
                asset-manifest — if they differ, hard-refresh / Reload App. */}
            <div style={{fontSize:10,color:C.muted,marginTop:4,fontFamily:"monospace"}}>
              UI build: {(() => {
                try {
                  const el = document.querySelector('script[src*="/static/js/main."]');
                  const m = el && el.src.match(/main\.([a-f0-9]+)\.js/);
                  return m ? m[1] : "—";
                } catch (_) { return "—"; }
              })()}
            </div>
          </div>
          <div style={{width:8,height:8,borderRadius:"50%",
            background:apiVersion ? C.green : C.muted,
            boxShadow:apiVersion ? `0 0 6px ${C.green}` : "none"}}/>
        </div>
      </div>

      {user?.role === "admin" && (
        <button onClick={onAdmin} style={{
          width:"100%",padding:"14px",borderRadius:12,marginBottom:10,
          background:"rgba(0,212,255,0.08)",border:"1px solid rgba(0,212,255,0.3)",
          color:C.accent,fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:"inherit",
        }}>🛡 Admin Dashboard</button>
      )}
      <button onClick={onLogout} style={{
        width:"100%",padding:"14px",borderRadius:12,
        background:C.redBg,border:`1px solid ${C.red}`,
        color:C.red,fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:"inherit",
      }}>Sign Out</button>

      <div style={{textAlign:"center",marginTop:20,fontSize:10,color:C.muted,lineHeight:1.6}}>
        ⚠️ Educational / paper-trading only · Not financial advice
      </div>
    </div>
  );
}

// --- Stock detail screen ------------------------------------------------------
// --- Score History Chart -------------------------------------------------------
function ScoreHistoryChart({ ticker, token }) {
  const [history, setHistory] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [days,    setDays]    = React.useState(30);

  React.useEffect(() => {
    if (!ticker || !token) return;
    setLoading(true);
    apiFetch(`/score-history/${ticker}?days=${days}`, token)
      .then(d => setHistory(d.history || []))
      .catch(() => setHistory([]))
      .finally(() => setLoading(false));
  }, [ticker, token, days]);

  if (loading) return (
    <div style={{height:80,background:"rgba(255,255,255,0.03)",borderRadius:8,
      display:"flex",alignItems:"center",justifyContent:"center"}}>
      <span style={{fontSize:10,color:C.muted}}>Loading score history…</span>
    </div>
  );

  if (!history || history.length < 2) return (
    <div style={{padding:"10px 0",textAlign:"center"}}>
      <div style={{fontSize:10,color:C.muted}}>
        Score history builds over time as you use the app.
      </div>
      <div style={{fontSize:9,color:"rgba(255,255,255,0.2)",marginTop:4}}>
        Logged automatically each day by the universe refresh.
      </div>
    </div>
  );

  const W = 320, H = 80, PAD = {t:8,r:8,b:20,l:32};
  const innerW = W - PAD.l - PAD.r;
  const innerH = H - PAD.t - PAD.b;

  // Use swing_score for chart
  const scores = history.map(h => h.swing_score ?? 0);
  const minS   = Math.min(...scores, 0);
  const maxS   = Math.max(...scores, 100);
  const range  = maxS - minS || 1;

  const pts = history.map((h, i) => {
    const x = PAD.l + (i / (history.length - 1)) * innerW;
    const y = PAD.t + innerH - ((h.swing_score ?? 0) - minS) / range * innerH;
    return [x, y];
  });

  const polyline = pts.map(([x,y]) => `${x},${y}`).join(' ');
  // Area fill path
  const areaPath = `M${pts[0][0]},${PAD.t+innerH} ` +
    pts.map(([x,y]) => `L${x},${y}`).join(' ') +
    ` L${pts[pts.length-1][0]},${PAD.t+innerH} Z`;

  // Last score colour
  const last = history[history.length-1];
  const lineColor = (last?.swing_score||0) >= 70 ? C.green
                  : (last?.swing_score||0) >= 50 ? C.accent : C.red;

  // Y-axis labels
  const yLabels = [0, 50, 100].map(v => ({
    v,
    y: PAD.t + innerH - (v - minS) / range * innerH,
  }));

  // X-axis: first and last date
  const firstDate = history[0]?.date?.slice(5) || '';
  const lastDate  = history[history.length-1]?.date?.slice(5) || '';

  return (
    <div>
      {/* Period toggle */}
      <div style={{display:"flex",justifyContent:"space-between",
        alignItems:"center",marginBottom:6}}>
        <div style={{fontSize:9,color:C.muted,textTransform:"uppercase",
          letterSpacing:"0.06em"}}>Swing Score History</div>
        <div style={{display:"flex",gap:3}}>
          {[14,30,90].map(d => (
            <button key={d} onClick={()=>setDays(d)} style={{
              fontSize:9,padding:"1px 6px",borderRadius:4,border:"none",
              cursor:"pointer",fontFamily:"inherit",fontWeight:600,
              background:days===d?"rgba(0,212,255,0.15)":"rgba(255,255,255,0.05)",
              color:days===d?C.accent:C.muted,
            }}>{d}d</button>
          ))}
        </div>
      </div>

      {/* SVG chart */}
      <svg width="100%" viewBox={`0 0 ${W} ${H}`}
        style={{overflow:"visible",display:"block"}}>
        <defs>
          <linearGradient id="sgGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={lineColor} stopOpacity="0.25"/>
            <stop offset="100%" stopColor={lineColor} stopOpacity="0"/>
          </linearGradient>
        </defs>
        {/* Y-axis grid + labels */}
        {yLabels.map(({v,y}) => (
          <g key={v}>
            <line x1={PAD.l} y1={y} x2={W-PAD.r} y2={y}
              stroke="rgba(255,255,255,0.06)" strokeWidth="1"/>
            <text x={PAD.l-4} y={y+3} textAnchor="end"
              fontSize="7" fill="rgba(255,255,255,0.3)">{v}</text>
          </g>
        ))}
        {/* Area fill */}
        <path d={areaPath} fill="url(#sgGrad)"/>
        {/* Score line */}
        <polyline points={polyline} fill="none"
          stroke={lineColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        {/* Last point dot */}
        <circle cx={pts[pts.length-1][0]} cy={pts[pts.length-1][1]}
          r="3" fill={lineColor} stroke={C.surface} strokeWidth="1.5"/>
        {/* X-axis dates */}
        <text x={PAD.l} y={H-4} fontSize="7" fill="rgba(255,255,255,0.25)">{firstDate}</text>
        <text x={W-PAD.r} y={H-4} fontSize="7" fill="rgba(255,255,255,0.25)"
          textAnchor="end">{lastDate}</text>
        {/* Current score label */}
        <text x={pts[pts.length-1][0]+5} y={pts[pts.length-1][1]-3}
          fontSize="9" fontWeight="700" fill={lineColor}>
          {last?.swing_score?.toFixed(0)}
        </text>
      </svg>
    </div>
  );
}

// --- Entry-Zoom: a focused minute chart of TODAY's action against the model's
//     suggested entry zone + stop, so the user can see "is now a good entry?" at a
//     glance. Anchored to sw.entry/stop/target1 (the model's levels) — the zone is
//     fixed at the suggested trigger and the live price is shown relative to it.
//     Renders only when there's a real entry level + intraday bars.
function EntryZoomCard({ data, sw }) {
  if (!(sw?.entry > 0)) return null;
  const candles = (Array.isArray(data?.candles_intraday_15m) && data.candles_intraday_15m.length)
    ? data.candles_intraday_15m
    : (Array.isArray(data?.candles_intraday) ? data.candles_intraday : []);
  const _t2m = t => { const p = (t || "").split(":"); return (+p[0]) * 60 + (+p[1] || 0); };
  // Today's bars carry a bare "HH:MM" label; prior-day bars have a "Day HH:MM" prefix.
  const todayBars = candles.filter(c => /^\d{2}:\d{2}$/.test(c.d));
  const reg = todayBars.filter(c => { const m = _t2m(c.d); return m >= 570 && m <= 960; }); // 9:30–16:00
  const series = reg.length >= 3 ? reg
    : (todayBars.length >= 3 ? todayBars : candles.slice(-40));
  if (series.length < 3) return null;

  const entry  = sw.entry;
  const stop   = (sw.stop > 0)    ? sw.stop    : entry * 0.95;
  const target = (sw.target1 > 0) ? sw.target1 : entry * 1.10;
  const risk   = Math.max(entry - stop, entry * 0.005);
  const atr    = (data?.atr > 0)  ? data.atr   : risk * 0.6;
  // Entry zone = a band around the trigger (~half an ATR, skewed below to favour
  // buying into weakness rather than chasing).
  const zoneLo = entry - 0.5 * atr;
  const zoneHi = entry + 0.15 * atr;
  const cur    = (data?.price > 0) ? data.price : series[series.length - 1].c;

  // The model's "entry" is just the close at scoring time, so the live price almost
  // always sits inside this static band — "in the entry zone now" was therefore nearly
  // always green, even when the Overview "Swing Timing" card said WAIT (a direct
  // contradiction the user reported on AMAT). The single source of truth for the
  // GO / WAIT verdict is data.intradayTiming (the SAME composite the Overview shows);
  // the band below is only price context and must never override that verdict. v8.4.236
  const it     = data?.intradayTiming;
  const inBand = (cur >= zoneLo && cur <= zoneHi);
  const pctAbove = (((cur - entry) / entry) * 100).toFixed(1);
  const pctBelow = (((entry - cur) / entry) * 100).toFixed(1);

  let posLabel, posColor;
  if (it && it.label) {
    if (it.label === "GOOD ENTRY") {
      posColor = C.green;
      posLabel = inBand ? "In the entry zone now"
               : cur > zoneHi ? `Favorable timing · ${pctAbove}% above trigger`
               : `Favorable timing · ${pctBelow}% below trigger`;
    } else if (it.label === "WAIT") {
      posColor = C.yellow;
      posLabel = inBand ? "Near entry — but timing says wait"
               : cur > zoneHi ? `${pctAbove}% above entry — wait for a pullback`
               : `${pctBelow}% below the entry trigger — waiting`;
    } else { // EXTENDED
      posColor = C.red;
      posLabel = cur > zoneHi ? `Extended ${pctAbove}% above entry — consider waiting`
               : "Extended — consider waiting / trimming";
    }
  } else {
    // No intraday timing (market closed / no bars): fall back to the pure band check.
    if (inBand) { posLabel = "In the entry band"; posColor = C.green; }
    else if (cur > zoneHi) { posLabel = `${pctAbove}% above entry — wait for a pullback`; posColor = C.yellow; }
    else { posLabel = `${pctBelow}% below the entry trigger`; posColor = C.accent; }
  }

  // Y-domain frames the minute action + the zone + the live price; only pulls in
  // the stop if it's within ~7% (otherwise it squishes the intraday detail). Target
  // is intentionally left out of the frame (too far on a 3.5:1 setup) — shown as text.
  const lows  = series.map(c => c.l ?? c.c).filter(v => v > 0);
  const highs = series.map(c => c.h ?? c.c).filter(v => v > 0);
  let lo = Math.min(...lows, zoneLo, cur);
  let hi = Math.max(...highs, zoneHi, cur);
  const stopInRange = stop > entry * 0.93;
  if (stopInRange) lo = Math.min(lo, stop);
  const pad = (hi - lo) * 0.08 || entry * 0.01;
  const yDomain = [lo - pad, hi + pad];
  const pct = v => `${v >= 0 ? "+" : ""}${(v * 100).toFixed(1)}%`;

  return (
    <Card>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:4}}>
        <SLabel>Entry zoom · today</SLabel>
        <span style={{fontSize:10,fontWeight:700,color:posColor}}>{posLabel}</span>
      </div>
      <div style={{fontSize:10,color:C.muted,marginBottom:8}}>
        Zone ${zoneLo.toFixed(2)}–${zoneHi.toFixed(2)} · stop ${stop.toFixed(2)} ({pct(-risk / entry)}) · target ${target.toFixed(2)} ({pct((target - entry) / entry)})
      </div>
      {it && it.label && (
        <div style={{fontSize:10,color:C.muted,marginTop:-4,marginBottom:8}}>
          Swing Timing: <span style={{fontWeight:700,color:posColor}}>{it.label}</span> ({it.score}/100)
          {it.entryZone?.note ? ` — ${it.entryZone.note}` : ""}
        </div>
      )}
      <ResponsiveContainer width="100%" height={150}>
        <AreaChart data={series} margin={{top:4,right:6,left:-28,bottom:0}}>
          <defs>
            <linearGradient id="ezfill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%"  stopColor={C.accent} stopOpacity={0.3}/>
              <stop offset="95%" stopColor={C.accent} stopOpacity={0}/>
            </linearGradient>
          </defs>
          <XAxis dataKey="d" tick={{fill:C.muted,fontSize:9}}
            interval={Math.max(0, Math.floor(series.length / 5))} tickLine={false}
            axisLine={false} minTickGap={28}/>
          <YAxis tick={{fill:C.muted,fontSize:8}} tickLine={false} axisLine={false}
            domain={yDomain} tickFormatter={v=>`$${v.toFixed(0)}`}/>
          <Tooltip contentStyle={{background:C.card,border:`1px solid ${C.border}`,borderRadius:8,fontSize:11}}
            formatter={v=>[`$${parseFloat(v).toFixed(2)}`,"Price"]}/>
          {/* Entry zone band (drawn first → behind the price) */}
          <ReferenceArea y1={zoneLo} y2={zoneHi} ifOverflow="visible"
            fill="rgba(0,230,118,0.12)" stroke="rgba(0,230,118,0.3)" strokeDasharray="3 3"/>
          <Area type="monotone" dataKey="c" stroke={C.accent} strokeWidth={2}
            fill="url(#ezfill)" dot={false} isAnimationActive={false}/>
          {/* Entry trigger + stop on top */}
          <ReferenceLine y={entry} ifOverflow="visible" stroke="rgba(0,230,118,0.75)"
            strokeDasharray="4 2" strokeWidth={1}
            label={{value:`Entry $${entry.toFixed(2)}`,position:"insideRight",fill:C.green,fontSize:8}}/>
          {stopInRange && (
            <ReferenceLine y={stop} ifOverflow="visible" stroke={C.red}
              strokeDasharray="3 2" strokeWidth={1}
              label={{value:`Stop $${stop.toFixed(2)}`,position:"insideRight",fill:C.red,fontSize:8}}/>
          )}
        </AreaChart>
      </ResponsiveContainer>
    </Card>
  );
}


// --- Pattern Showcase: one focused mini-chart per detected multi-week chart
//     pattern (Double Top/Bottom, Asc/Desc Triangle), zoomed to the pattern's
//     window with its neckline (trigger) + measured target drawn, the two
//     peaks/troughs marked, and a plain-English read. Renders only when patterns
//     exist + enough daily candles. Data: data.chartPatterns + data.candles.
function PatternShowcaseCard({ data }) {
  const allPatterns = Array.isArray(data?.chartPatterns) ? data.chartPatterns : [];
  const candles  = Array.isArray(data?.candles) ? data.candles : [];
  if (candles.length < 12) return null;
  const _M = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const isoToD = iso => { try { const d = new Date(iso + "T12:00:00"); return `${_M[d.getMonth()]} ${String(d.getDate()).padStart(2,"0")}`; } catch { return ""; } };
  const cur = (data?.price > 0) ? data.price : (candles[candles.length - 1]?.c || 0);

  // Drop patterns the price has already invalidated / played out — a Double Bottom
  // targeting $455 is meaningless when the stock is $626. Stale = bullish target
  // already exceeded (or price ran >15% past the breakout level), or the bearish mirror.
  // v8.4.240: symmetric "price left the pattern zone" check — drops a top/bottom
  // invalidated in EITHER direction (was missing the upside-breakout case, so WYFI's
  // ~$11 double top still showed at $34.67). Uses structural levels, not the far target.
  const _pStale = (p) => {
    if (!cur) return false;
    const struct = [p.neckline, p.level].filter(v => v > 0);
    if (!struct.length) return false;
    const hi = Math.max(...struct), lo = Math.min(...struct);
    if (cur > hi * 1.15 || cur < lo * 0.85) return true;
    if (p.type === "bullish") return p.target > 0 && cur >= p.target;
    return p.target > 0 && cur <= p.target;
  };
  const patterns = allPatterns.filter(p => !_pStale(p));

  // v8.4.236: candlestick patterns (data.patterns) are single-bar signals detected on
  // the LATEST daily bar (detect_patterns reads df.iloc[-1]). The Overview surfaces them
  // (e.g. a 60% Hikkake) via patternsBias, so the showcase must be able to chart the
  // proof too — otherwise "no patterns" here contradicts the Overview. Directional
  // first, then by confidence, matching the Overview ChartPatternsCard ordering.
  const candlePats = (Array.isArray(data?.patterns) ? data.patterns : [])
    .filter(p => p && p.name)
    .slice()
    .sort((a, b) => {
      const dir = p => (p.type === "bullish" || p.type === "bearish") ? 0 : 1;
      return dir(a) - dir(b) || ((b.confidence || 0) - (a.confidence || 0));
    });

  if (patterns.length === 0 && candlePats.length === 0) {
    return (
      <Card>
        <SLabel>Pattern showcase</SLabel>
        <div style={{fontSize:12,color:C.muted,marginTop:6,lineHeight:1.5}}>
          No active chart or candlestick patterns right now
          {allPatterns.length > 0 ? " — detected multi-week patterns have already played out or been invalidated by price." : "."}
        </div>
      </Card>
    );
  }

  // Candlestick mini-chart frame — last ~16 daily bars, final bar = the signal bar.
  const cWin = candles.slice(-16);
  const cYs  = cWin.flatMap(c => [c.l ?? c.c, c.h ?? c.c, c.c]).filter(v => v > 0);
  const cLo  = cYs.length ? Math.min(...cYs) : 0;
  const cHi  = cYs.length ? Math.max(...cYs) : 1;
  const cPad = (cHi - cLo) * 0.1 || cHi * 0.01;
  const cDom = [cLo - cPad, cHi + cPad];
  const sigBar = cWin[cWin.length - 1] || {};
  const sigCol = candlePats.some(p => p.type === "bullish") ? C.green
               : candlePats.some(p => p.type === "bearish") ? C.red : C.muted;

  return (
    <Card>
      <SLabel>Pattern showcase</SLabel>
      {patterns.length > 0 && (
      <div style={{display:"flex",flexDirection:"column",gap:16,marginTop:8}}>
        {patterns.slice(0, 2).map((p, i) => {
          const isBull = p.type === "bullish";
          const accent = isBull ? C.green : C.yellow;
          const conf   = (p.confidence <= 1) ? Math.round(p.confidence * 100) : Math.round(p.confidence);
          const sKey = isoToD(p.start_date), eKey = isoToD(p.end_date);
          let eIdx = candles.findIndex(c => c.d === eKey); if (eIdx < 0) eIdx = candles.length - 1;
          let sIdx = candles.findIndex(c => c.d === sKey); if (sIdx < 0) sIdx = Math.max(0, eIdx - 30);
          const win = candles.slice(Math.max(0, sIdx - 8), Math.min(candles.length, eIdx + 12));
          if (win.length < 4) return null;
          const tgtNear = p.target > 0 && cur > 0 && Math.abs(p.target - cur) / cur < 0.25;
          const ys = win.flatMap(c => [c.l ?? c.c, c.h ?? c.c, c.c].filter(v => v > 0));
          let lo = Math.min(...ys, p.neckline || Infinity, p.level || Infinity);
          let hi = Math.max(...ys, p.neckline || 0, p.level || 0);
          if (tgtNear) { lo = Math.min(lo, p.target); hi = Math.max(hi, p.target); }
          const pad = (hi - lo) * 0.08 || (hi * 0.01);
          const yDomain = [lo - pad, hi + pad];
          const tgtPct = (p.target > 0 && cur > 0) ? ((p.target - cur) / cur * 100) : null;
          const isDouble = (p.name || "").startsWith("Double");
          const dots = isDouble ? [sKey, eKey]
            .filter(k => win.some(c => c.d === k))
            .map(k => ({ d: k, y: p.level })) : [];
          return (
            <div key={i}>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:3}}>
                <span style={{fontSize:11,fontWeight:700,color:accent}}>{isBull ? "▲" : "▼"} {p.name}</span>
                <span style={{fontSize:9,color:C.muted,background:"rgba(255,255,255,0.06)",padding:"1px 5px",borderRadius:4}}>{conf}% conf</span>
                <span style={{fontSize:9,color:C.muted,marginLeft:"auto"}}>{sKey} – {eKey}</span>
              </div>
              <ResponsiveContainer width="100%" height={130}>
                <AreaChart data={win} margin={{top:4,right:6,left:-28,bottom:0}}>
                  <defs>
                    <linearGradient id={`psfill${i}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor={accent} stopOpacity={0.25}/>
                      <stop offset="95%" stopColor={accent} stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="d" tick={{fill:C.muted,fontSize:8}}
                    interval={Math.max(0, Math.floor(win.length / 4))} tickLine={false}
                    axisLine={false} minTickGap={24}/>
                  <YAxis tick={{fill:C.muted,fontSize:8}} tickLine={false} axisLine={false}
                    domain={yDomain} tickFormatter={v=>`$${v.toFixed(0)}`}/>
                  <Tooltip contentStyle={{background:C.card,border:`1px solid ${C.border}`,borderRadius:8,fontSize:11}}
                    formatter={v=>[`$${parseFloat(v).toFixed(2)}`,"Close"]}/>
                  {p.neckline > 0 && (
                    <ReferenceLine y={p.neckline} ifOverflow="visible" stroke="rgba(255,255,255,0.45)"
                      strokeDasharray="5 3" strokeWidth={1}
                      label={{value:`Neckline $${p.neckline.toFixed(2)}`,position:"insideLeft",fill:"rgba(255,255,255,0.6)",fontSize:8}}/>
                  )}
                  {tgtNear && (
                    <ReferenceLine y={p.target} ifOverflow="visible" stroke={isBull ? C.green : C.red}
                      strokeDasharray="3 2" strokeWidth={1}
                      label={{value:`Target $${p.target.toFixed(2)}`,position:"insideLeft",fill:isBull ? C.green : C.red,fontSize:8}}/>
                  )}
                  <Area type="monotone" dataKey="c" stroke={accent} strokeWidth={2}
                    fill={`url(#psfill${i})`} dot={false} isAnimationActive={false}/>
                  {dots.map((dt, di) => (
                    <ReferenceDot key={di} x={dt.d} y={dt.y} r={4} fill={accent}
                      stroke="rgba(0,0,0,0.5)" strokeWidth={1} ifOverflow="visible"/>
                  ))}
                </AreaChart>
              </ResponsiveContainer>
              <div style={{fontSize:10,color:C.muted,marginTop:4,lineHeight:1.5}}>
                {p.description}
                {tgtPct != null && (
                  <span style={{color:accent,fontWeight:600}}> ({tgtPct >= 0 ? "+" : ""}{tgtPct.toFixed(1)}% to target)</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
      )}

      {/* v8.4.236: Candlestick proof — charts whatever single-bar signal the Overview
          surfaced (Hikkake/Inside/Engulfing…) so the two screens stay consistent. All
          candlestick patterns fire on the latest bar, so one zoomed candle chart with
          the final bar flagged is the shared proof. Neutral patterns included. */}
      {candlePats.length > 0 && (
        <div style={{marginTop: patterns.length > 0 ? 18 : 8,
          paddingTop:  patterns.length > 0 ? 14 : 0,
          borderTop:   patterns.length > 0 ? `1px solid ${C.border}` : "none"}}>
          <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:6,flexWrap:"wrap"}}>
            <span style={{fontSize:11,fontWeight:700,color:C.text}}>Latest candlestick</span>
            {candlePats.slice(0, 3).map((p, i) => {
              const col  = p.type==="bullish"?C.green:p.type==="bearish"?C.red:C.muted;
              const conf = (p.confidence <= 1) ? Math.round(p.confidence * 100) : Math.round(p.confidence);
              return (
                <span key={i} style={{fontSize:9,fontWeight:700,color:col,
                  background:`${col}1f`,padding:"1px 6px",borderRadius:4}}>
                  {p.type==="bullish"?"▲":p.type==="bearish"?"▼":"●"} {p.name} {conf}%
                </span>
              );
            })}
          </div>
          <ResponsiveContainer width="100%" height={130}>
            <ComposedChart data={cWin} margin={{top:10,right:6,left:-28,bottom:0}}>
              <XAxis dataKey="d" tick={{fill:C.muted,fontSize:8}}
                interval={Math.max(0, Math.floor(cWin.length / 4))} tickLine={false}
                axisLine={false} minTickGap={24}/>
              <YAxis tick={{fill:C.muted,fontSize:8}} tickLine={false} axisLine={false}
                domain={cDom} tickFormatter={v=>`$${v.toFixed(0)}`}/>
              <Tooltip contentStyle={{background:C.card,border:`1px solid ${C.border}`,borderRadius:8,fontSize:11}}
                formatter={v=>[`$${parseFloat(v).toFixed(2)}`,"Close"]}/>
              <Bar dataKey="c" baseValue={cDom[0]} isAnimationActive={false}
                shape={(props)=><CandleBar {...props} yDomain={cDom}/>}/>
              {sigBar.d && (
                <ReferenceDot x={sigBar.d} y={sigBar.h ?? sigBar.c} r={4} fill={sigCol}
                  stroke="rgba(0,0,0,0.5)" strokeWidth={1} ifOverflow="visible"
                  label={{value:"signal",position:"top",fill:C.muted,fontSize:8}}/>
              )}
            </ComposedChart>
          </ResponsiveContainer>
          <div style={{fontSize:10,color:C.muted,marginTop:4,lineHeight:1.5}}>
            {candlePats[0].description}{" "}
            <span style={{opacity:0.8}}>The % is how reliable this candle has been historically (not the odds your trade works). It's a single-bar, short-term clue on the latest close — confirm with the trend, not on its own.</span>
          </div>
        </div>
      )}
    </Card>
  );
}


// Plain-English Fibonacci interpretation for the average user (v8.4.239). Translates
// the ladder + golden pocket + retracement % into "what happened / what the zone means /
// what to do" — keyed on where price actually sits. Honest: never a prediction.
function fibPlainRead(fib, price) {
  if (!fib || !(price > 0)) return null;
  const f$ = v => `$${Number(v).toFixed(2)}`;
  const isUp = fib.direction === "up";
  const SL = fib.swingLow.price, SH = fib.swingHigh.price;
  const r  = fib.currentRetracement, rp = Math.round(r * 100);
  const gp = fib.goldenPocket;
  const l382 = (fib.levels.find(l => l.ratio === 0.382) || {}).price;
  // nearest level
  let near = null, nd = Infinity;
  for (const l of fib.levels) { const d = Math.abs(price - l.price) / (l.price || 1); if (d < nd) { nd = d; near = l; } }
  const onLevel = near && nd < 0.015;

  const move  = isUp ? `${f$(SL)} up to ${f$(SH)}` : `${f$(SH)} down to ${f$(SL)}`;
  const eased = isUp ? "pulled back" : "bounced";
  const summary = r < 0.12
    ? `${isUp ? "It rallied" : "It fell"} from ${move} and has barely ${eased} (${rp}%) — so it's still trading near the ${isUp ? "top" : "bottom"} of that run.`
    : `${isUp ? "It rallied" : "It fell"} from ${move} and has since ${eased} ${rp}% of that move.`;

  const pocket = isUp
    ? `The "golden pocket" (${f$(gp[0])}–${f$(gp[1])}) is the pullback zone where uptrends most often find a floor and resume — the spot swing traders watch for a lower-risk entry.`
    : `The "golden pocket" (${f$(gp[0])}–${f$(gp[1])}) is where downtrend bounces tend to stall — overhead resistance, not a buy zone.`;

  let action, tone; // tone: good | wait | caution
  if (fib.inGoldenPocket) {
    action = isUp ? `Price is in that zone right now — if your other signals agree, this is a higher-odds entry area.`
                  : `Price is in that resistance zone now — rallies here often fade, so it's not a clean entry.`;
    tone = isUp ? "good" : "caution";
  } else if (onLevel) {
    action = `Price is sitting right on the ${near.label} level (${f$(near.price)}) — a common ${isUp ? "bounce" : "stall"} point; watch how it reacts here before acting.`;
    tone = "wait";
  } else if (isUp && r < 0.236) {
    action = `Buying here means chasing strength near the high. Patient buyers wait for a dip toward the 38.2% level (${f$(l382)}) or the golden pocket for a better risk-defined entry.`;
    tone = "wait";
  } else if (isUp && r <= 0.618) {
    action = `It's mid-pullback. A deeper dip into the golden pocket would be the classic lower-risk entry.`;
    tone = "wait";
  } else if (isUp && r <= 0.786) {
    action = `It's pulled back deep — below the 78.6% line the uptrend is in question, so it's better to wait for it to stabilise.`;
    tone = "caution";
  } else if (isUp) {
    action = `Almost the entire up-move has been given back — the prior uptrend is largely broken.`;
    tone = "caution";
  } else {
    action = `This is a downtrend bounce — the levels above are resistance where rallies tend to stall, not a swing-buy setup.`;
    tone = "caution";
  }
  return { summary, pocket, action, tone, near, onLevel };
}

// --- Fibonacci card (v8.4.238): the dominant-swing retracement ladder + golden
//     pocket drawn on the recent price action, with a plain-English read of where
//     price sits + what to do. Honest framing: levels are confluence/support-resistance
//     ZONES, not predictions (Phase 2 scores only fib∩VWAP∩S-R confluence in the Swing
//     Timing card). Data: data.fibLevels (deterministic swing) + data.candles.
function FibCard({ data }) {
  const fib = data?.fibLevels;
  const candles = Array.isArray(data?.candles) ? data.candles : [];
  if (!fib || candles.length < 12) {
    return (
      <Card>
        <SLabel>Fibonacci</SLabel>
        <div style={{fontSize:12,color:C.muted,marginTop:6,lineHeight:1.5}}>
          No clear swing to anchor Fibonacci levels right now — price hasn't made a big
          enough up- or down-move recently for the levels to mean anything.
        </div>
      </Card>
    );
  }
  const _M = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const isoToD = iso => { try { const d = new Date(iso + "T12:00:00"); return `${_M[d.getMonth()]} ${String(d.getDate()).padStart(2,"0")}`; } catch { return ""; } };
  const isUp   = fib.direction === "up";
  const accent = isUp ? C.green : C.red;
  const cur    = (data?.price > 0) ? data.price : (candles[candles.length - 1]?.c || 0);
  const read   = fibPlainRead(fib, cur);
  const TONE   = { good: C.green, wait: C.yellow, caution: C.red };
  const toneCol = read ? (TONE[read.tone] || C.muted) : C.muted;

  // Window: from the earlier swing extreme to now (a little context padding).
  const sHiD = isoToD(fib.swingHigh.date), sLoD = isoToD(fib.swingLow.date);
  let iHi = candles.findIndex(c => c.d === sHiD); if (iHi < 0) iHi = candles.length - 1;
  let iLo = candles.findIndex(c => c.d === sLoD); if (iLo < 0) iLo = candles.length - 1;
  const start = Math.max(0, Math.min(iHi, iLo) - 4);
  const win = candles.slice(start);
  if (win.length < 4) return null;
  const ys = win.flatMap(c => [c.l ?? c.c, c.h ?? c.c, c.c]).filter(v => v > 0);
  const levelPrices = fib.levels.map(l => l.price);
  const lo = Math.min(...ys, ...levelPrices, fib.goldenPocket[0]);
  const hi = Math.max(...ys, ...levelPrices, fib.goldenPocket[1]);
  const pad = (hi - lo) * 0.06 || hi * 0.01;
  const yDomain = [lo - pad, hi + pad];
  const roleWord = isUp ? "support" : "resistance";

  return (
    <Card>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:4}}>
        <SLabel>Fibonacci</SLabel>
        <span style={{fontSize:10,fontWeight:700,color:toneCol}}>
          {Math.round(fib.currentRetracement*100)}% pulled back
        </span>
      </div>

      {/* Plain-English read FIRST — the average user reads this, not the ladder. */}
      {read && (
        <div style={{fontSize:11.5,color:C.text,lineHeight:1.55,marginBottom:10}}>
          {read.summary}{" "}
          <span style={{color:C.muted}}>{read.pocket}</span>
          <div style={{marginTop:6,padding:"7px 9px",borderRadius:8,
            background:`${toneCol}14`,border:`1px solid ${toneCol}38`,
            color:toneCol,fontWeight:600}}>
            {read.tone==="good"?"✓ ":read.tone==="caution"?"⚠ ":"⏳ "}{read.action}
          </div>
        </div>
      )}

      <ResponsiveContainer width="100%" height={200}>
        <AreaChart data={win} margin={{top:4,right:6,left:-28,bottom:0}}>
          <defs>
            <linearGradient id="fibfill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%"  stopColor={accent} stopOpacity={0.18}/>
              <stop offset="95%" stopColor={accent} stopOpacity={0}/>
            </linearGradient>
          </defs>
          <XAxis dataKey="d" tick={{fill:C.muted,fontSize:8}}
            interval={Math.max(0, Math.floor(win.length / 5))} tickLine={false}
            axisLine={false} minTickGap={24}/>
          <YAxis tick={{fill:C.muted,fontSize:8}} tickLine={false} axisLine={false}
            domain={yDomain} tickFormatter={v=>`$${v.toFixed(0)}`}/>
          <Tooltip contentStyle={{background:C.card,border:`1px solid ${C.border}`,borderRadius:8,fontSize:11}}
            formatter={v=>[`$${parseFloat(v).toFixed(2)}`,"Close"]}/>
          {/* Golden pocket band (behind price) */}
          <ReferenceArea y1={fib.goldenPocket[0]} y2={fib.goldenPocket[1]} ifOverflow="visible"
            fill="rgba(255,202,40,0.14)" stroke="rgba(255,202,40,0.4)" strokeDasharray="3 3"
            label={{value:"golden pocket",position:"insideTopLeft",fill:"#ffca28",fontSize:8}}/>
          {/* Retracement ladder — % only on the line (price is in the table below, so
              the chart doesn't look like it has a confusing second axis). */}
          {fib.levels.map((l, i) => (
            <ReferenceLine key={i} y={l.price} ifOverflow="visible"
              stroke="rgba(255,255,255,0.22)" strokeDasharray="4 3" strokeWidth={1}
              label={{value:l.label,position:"insideLeft",fill:C.muted,fontSize:8}}/>
          ))}
          <Area type="monotone" dataKey="c" stroke={accent} strokeWidth={2}
            fill="url(#fibfill)" dot={false} isAnimationActive={false}/>
          {/* Swing extremes */}
          <ReferenceDot x={sHiD} y={fib.swingHigh.price} r={3.5} fill={C.green}
            stroke="rgba(0,0,0,0.5)" strokeWidth={1} ifOverflow="visible"
            label={{value:"high",position:"top",fill:C.green,fontSize:8}}/>
          <ReferenceDot x={sLoD} y={fib.swingLow.price} r={3.5} fill={C.red}
            stroke="rgba(0,0,0,0.5)" strokeWidth={1} ifOverflow="visible"
            label={{value:"low",position:"bottom",fill:C.red,fontSize:8}}/>
          {/* v8.4.253: today's LIVE price against the ladder */}
          {cur > 0 && (
            <ReferenceLine y={cur} ifOverflow="visible" stroke={C.accent} strokeWidth={1.5}
              label={{value:`now $${cur.toFixed(2)}`,position:"insideRight",fill:C.accent,fontSize:8}}/>
          )}
        </AreaChart>
      </ResponsiveContainer>

      {/* Levels table — tells the user exactly what the lines/prices are. */}
      <div style={{marginTop:8}}>
        <div style={{fontSize:9,color:C.muted,marginBottom:4}}>
          Pullback levels (potential {roleWord}) — closest to today's price is highlighted:
        </div>
        <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
          {fib.levels.map((l,i)=>{
            const isNear = read?.near && read.near.ratio === l.ratio;
            const inGP   = l.price >= fib.goldenPocket[0] && l.price <= fib.goldenPocket[1];
            const col = isNear ? C.accent : inGP ? "#ffca28" : C.muted;
            return (
              <span key={i} style={{fontSize:9.5,fontWeight:600,color:col,
                background:isNear?"rgba(91,140,255,0.16)":inGP?"rgba(255,202,40,0.12)":"rgba(255,255,255,0.05)",
                padding:"2px 7px",borderRadius:4,whiteSpace:"nowrap"}}>
                {l.label} · ${l.price.toFixed(2)}
              </span>
            );
          })}
        </div>
        <div style={{fontSize:9,color:C.muted,marginTop:8,opacity:0.85,lineHeight:1.4}}>
          If the move keeps going, projected targets: {fib.extensions.map(e=>`$${e.price.toFixed(2)}`).join(" · ")}.
          These levels are zones traders watch — not guarantees.
        </div>
      </div>
    </Card>
  );
}


// --- Signal Strip: plain-English, ranked list of recent TA signal events +
//     detected patterns — replaces the cryptic on-chart dots. Dedupes to the most
//     recent of each signal type (a 90-day window repeats crossovers) and
//     translates the terse backend labels ("MACD ↑", "RSI ↑30") into what they MEAN.
function SignalStrip({ data }) {
  const signals  = Array.isArray(data?.chartSignals)  ? data.chartSignals  : [];
  const _allPats = Array.isArray(data?.chartPatterns) ? data.chartPatterns : [];
  // Hide patterns price has already invalidated (same rule as PatternShowcaseCard).
  const _cur = (data?.price > 0) ? data.price : 0;
  const patterns = _allPats.filter(p => {
    if (!_cur) return true;
    // v8.4.240: symmetric zone-exit (same rule as PatternShowcaseCard._pStale).
    const struct = [p.neckline, p.level].filter(v => v > 0);
    if (!struct.length) return true;
    const hi = Math.max(...struct), lo = Math.min(...struct);
    if (_cur > hi * 1.15 || _cur < lo * 0.85) return false;
    if (p.type === "bullish") return !(p.target > 0 && _cur >= p.target);
    return !(p.target > 0 && _cur <= p.target);
  });
  const _M = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const fmtD = iso => { try { const d = new Date(iso + "T12:00:00"); return `${_M[d.getMonth()]} ${d.getDate()}`; } catch { return ""; } };
  const isBull = d => d === "bull" || d === "up" || d === "bullish";
  const meaning = (s) => ({
    "macd_cross:bull": "MACD bullish crossover — momentum turning up",
    "macd_cross:bear": "MACD bearish crossover — momentum turning down",
    "ema_cross:bull":  "EMA 9/21 golden cross — short-term uptrend begun",
    "ema_cross:bear":  "EMA 9/21 death cross — short-term downtrend begun",
    "rsi_zone:bull":   "RSI reclaimed 30 — exiting oversold (bounce setup)",
    "rsi_zone:bear":   "RSI lost 70 — exiting overbought (pullback risk)",
    "vol_spike:bull":  "Volume spike on an up day — accumulation",
    "vol_spike:bear":  "Volume spike on a down day — distribution",
  }[`${s.type}:${s.dir}`] || s.label || "Signal");

  // Dedupe to the most recent of each type (crossovers repeat over 90 days).
  const byType = {};
  for (const s of signals) {
    const cur = byType[s.type];
    if (!cur || (s.date || "") > (cur.date || "")) byType[s.type] = s;
  }
  const ranked = Object.values(byType).sort((a, b) => (b.date || "").localeCompare(a.date || ""));

  if (ranked.length === 0 && patterns.length === 0) {
    return <Card><SLabel>Signals</SLabel>
      <div style={{fontSize:12,color:C.muted,marginTop:6}}>No recent technical signals or patterns detected.</div></Card>;
  }
  return (
    <Card>
      <SLabel>Signals · plain-English</SLabel>
      <div style={{fontSize:10,color:C.muted,margin:"2px 0 10px",lineHeight:1.4}}>
        Most recent of each signal type over ~90 days — these describe what just happened, not predictions.
      </div>
      <div style={{display:"flex",flexDirection:"column",gap:6}}>
        {ranked.map((s, i) => {
          const bull = isBull(s.dir);
          return (
            <div key={`s${i}`} style={{display:"flex",alignItems:"center",gap:8,padding:"7px 9px",background:C.surface,borderRadius:8}}>
              <span style={{fontSize:12,color:bull?C.green:C.red,fontWeight:800,width:12,textAlign:"center",flexShrink:0}}>{bull?"▲":"▼"}</span>
              <span style={{fontSize:11,color:C.text,flex:1,lineHeight:1.4}}>{meaning(s)}</span>
              <span style={{fontSize:9,color:C.muted,whiteSpace:"nowrap",flexShrink:0}}>{fmtD(s.date)}{s.price?` · $${(+s.price).toFixed(2)}`:""}</span>
            </div>
          );
        })}
        {patterns.map((p, i) => {
          const bull = p.type === "bullish";
          const conf = p.confidence <= 1 ? Math.round(p.confidence * 100) : Math.round(p.confidence);
          return (
            <div key={`p${i}`} style={{display:"flex",alignItems:"flex-start",gap:8,padding:"7px 9px",
              background:bull?"rgba(0,230,118,0.07)":"rgba(255,202,40,0.07)",borderRadius:8}}>
              <span style={{fontSize:12,color:bull?C.green:C.yellow,fontWeight:800,width:12,textAlign:"center",flexShrink:0}}>{bull?"▲":"▼"}</span>
              <div style={{flex:1}}>
                <div style={{fontSize:11,color:C.text,fontWeight:600}}>{p.name}
                  <span style={{fontSize:9,color:C.muted,fontWeight:400}}> · {conf}% conf</span></div>
                {p.description && <div style={{fontSize:10,color:C.muted,marginTop:1,lineHeight:1.4}}>{p.description}</div>}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}


// v8.4.309: collapsible Buy/Hold/Sell distribution inside the analyst consensus
// card — the mean alone hid the split (a "Buy" mean is often a Buy/Hold mix).
function AnalystDistribution({ counts }) {
  const [open, setOpen] = React.useState(false);
  if (!counts) return null;
  const rows = [
    ["Strong Buy",  counts.strongBuy  || 0, "#00e676"],
    ["Buy",         counts.buy        || 0, "#69f0ae"],
    ["Hold",        counts.hold       || 0, C.yellow],
    ["Sell",        counts.sell       || 0, "#ff8a65"],
    ["Strong Sell", counts.strongSell || 0, C.red],
  ];
  const total = rows.reduce((s, r) => s + r[1], 0);
  if (total <= 0) return null;
  return (
    <div style={{marginBottom:12}}>
      <button onClick={()=>setOpen(o=>!o)} style={{width:"100%",display:"flex",
        alignItems:"center",justifyContent:"space-between",background:C.surface,
        border:`1px solid ${C.border}`,borderRadius:10,padding:"9px 12px",
        cursor:"pointer",fontFamily:"inherit",color:C.text}}>
        <span style={{fontSize:12,fontWeight:600}}>Rating breakdown ({total} analysts)</span>
        <span style={{fontSize:11,color:C.muted}}>{open?"▲ hide":"▼ show"}</span>
      </button>
      {open && (
        <div style={{marginTop:8,display:"flex",flexDirection:"column",gap:6,
          padding:"4px 2px"}}>
          {rows.map(([label,n,col])=>(
            <div key={label} style={{display:"flex",alignItems:"center",gap:8}}>
              <span style={{fontSize:11,color:C.muted,width:78,flexShrink:0}}>{label}</span>
              <div style={{flex:1,height:8,background:"rgba(255,255,255,0.06)",
                borderRadius:4,overflow:"hidden"}}>
                <div style={{height:"100%",borderRadius:4,background:col,
                  width:`${Math.round(n/total*100)}%`,transition:"width 0.4s"}}/>
              </div>
              <span style={{fontSize:11,fontFamily:"monospace",color:C.text,
                width:18,textAlign:"right"}}>{n}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// v8.4.319: the single canonical earnings glyph — a calendar with an "E" (replaces the
// 📅 emoji, which iOS renders frozen at "JUL 17" and reads like a wrong date). Reused
// everywhere earnings is indicated so the whole app is consistent.
function EarningsGlyph({ size = 13, color = C.yellow }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-label="Earnings"
      style={{flexShrink:0}}>
      <rect x="3" y="5" width="18" height="16" rx="2.5" stroke={color} strokeWidth="2"/>
      <line x1="3" y1="9" x2="21" y2="9" stroke={color} strokeWidth="2"/>
      <line x1="8" y1="2.5" x2="8" y2="6" stroke={color} strokeWidth="2" strokeLinecap="round"/>
      <line x1="16" y1="2.5" x2="16" y2="6" stroke={color} strokeWidth="2" strokeLinecap="round"/>
      <text x="12" y="18.5" fontSize="10" fontWeight="800" fill={color} textAnchor="middle">E</text>
    </svg>
  );
}

// v8.4.316: subtle earnings badge on a list tile when the name reports within ~7d.
function EarningsSoonBadge({ soon, ticker }) {
  const info = soon && soon[String(ticker || "").toUpperCase()];
  if (!info) return null;
  const label = info.daysUntil === 0 ? "Reports earnings today"
    : info.daysUntil === 1 ? "Reports earnings tomorrow"
    : `Reports earnings in ${info.daysUntil}d`;
  return (
    <span title={`${label}${info.hour ? ` (${info.hour})` : ""}`}
      style={{display:"inline-flex",alignItems:"center",lineHeight:1,cursor:"help"}}>
      <EarningsGlyph size={13} color={C.yellow}/>
    </span>
  );
}

// v8.4.316: on-demand AI read of an earnings reaction — overreaction (dip to buy)
// vs justified re-rating (falling knife). Hangs off the earnings-driven drop badge.
function EarningsReactionCard({ ticker, token }) {
  const [data, setData]       = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [err, setErr]         = React.useState("");
  const run = async () => {
    if (!token || !ticker) return;
    setLoading(true); setErr("");
    try {
      const d = await _aiAgentFetch(() =>
        apiFetch(`/ai/earnings-reaction?ticker=${ticker}`, token, { method: "POST" }));
      if (d?.status === "no_recent_earnings") setErr(d.message || "No recent earnings found.");
      else setData(d);
    } catch (e) { setErr(e.message || "AI agent unavailable"); }
    finally { setLoading(false); }
  };
  if (!data && !loading && !err) {
    return (
      <button onClick={run} style={{marginTop:8,width:"100%",padding:"9px 12px",borderRadius:10,
        border:`1px solid ${C.accent}`,background:"rgba(0,212,255,0.1)",color:C.accent,
        fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
        🔍 Analyze this earnings reaction (AI)
      </button>
    );
  }
  return (
    <div style={{marginTop:8,border:`1px solid ${C.border}`,borderRadius:10,padding:12,background:C.surface}}>
      {loading && <div style={{fontSize:12,color:C.muted,textAlign:"center",padding:"8px 0"}}>Reading results + news…</div>}
      {err && !loading && <div style={{fontSize:12,color:C.red}}>{err}</div>}
      {data && (() => {
        // v8.4.342 — direction-aware: "overreaction"/"justified" flip meaning by the move's
        // sign. On a DROP overreaction = dip-to-buy (green); on a POP overreaction = overdone
        // rally to fade (red). Derive from changePct so a +34% gain reads correctly.
        const up = (data.changePct ?? 0) >= 0;
        const V = up ? {
          overreaction: { label: "Overreaction — pop may fade",      color: C.red,    icon: "🔴" },
          justified:    { label: "Justified — rally warranted",      color: C.green,  icon: "🟢" },
          mixed:        { label: "Mixed / unclear",                  color: C.yellow, icon: "🟡" },
        } : {
          overreaction: { label: "Overreaction — possible bounce",   color: C.green,  icon: "🟢" },
          justified:    { label: "Justified — falling-knife risk",   color: C.red,    icon: "🔴" },
          mixed:        { label: "Mixed / unclear",                  color: C.yellow, icon: "🟡" },
        };
        const v = V[data.verdict] || V.mixed;
        return (
          <>
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6,flexWrap:"wrap"}}>
              <span style={{fontSize:13}}>{v.icon}</span>
              <span style={{fontSize:13,fontWeight:800,color:v.color}}>{v.label}</span>
              <span style={{fontSize:10,color:C.muted}}>· {data.confidence} confidence</span>
              <div style={{flex:1}}/>
              <button onClick={run} style={{background:"none",border:"none",color:C.muted,
                fontSize:10,cursor:"pointer",fontFamily:"inherit"}} title="Re-run">↻</button>
            </div>
            {data.summary && <div style={{fontSize:12,color:C.text,lineHeight:1.5,marginBottom:8}}>{data.summary}</div>}
            {Array.isArray(data.factors) && data.factors.length > 0 && (
              <ul style={{margin:0,paddingLeft:16,fontSize:11,color:C.muted,lineHeight:1.6}}>
                {data.factors.map((f,i)=><li key={i}>{f}</li>)}
              </ul>
            )}
            <div style={{fontSize:9,color:C.muted,marginTop:8,fontStyle:"italic"}}>
              Probability read from reported results + news — not financial advice. Verify guidance yourself.
            </div>
          </>
        );
      })()}
    </div>
  );
}

// v8.4.443 — honest quote freshness. The Polygon plan here is 15-min DELAYED; US detail/portfolio prices now come from a real-time source
// when available. This stamp says exactly which one you are looking at and what time the number is from.
function _clockOf(iso, withSeconds) {
  if (!iso) return null;
  const t = new Date(iso);
  if (isNaN(t)) return null;
  return t.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", ...(withSeconds ? { second: "2-digit" } : {}) });
}
// v8.4.444 — list rows (Discovery / Watchlist / picks) show prices from the ~30-minute scored cache on top of a ~15-minute delayed feed,
// so they must never say "Live". Open the stock for the real-time quote.
function ListQuoteDot({ isLive, style }) {
  const open = isMarketOpen() && isLive;
  return open
    ? <span title="List prices refresh about every 30 minutes from a ~15-minute delayed feed. Open the stock for a live quote."
        style={{color:"rgba(255,202,40,0.8)",...(style||{})}}>● Delayed</span>
    : <span title="Market closed — last close." style={{color:"rgba(255,255,255,0.2)",...(style||{})}}>● EOD</span>;
}
function FreshnessStamp({ asOf, ageSec, delayed, marketOpen, style }) {
  const clock = _clockOf(asOf, ageSec != null && ageSec < 3600);
  let txt, clr, tip;
  if (!marketOpen) {
    txt = "Market closed"; clr = "rgba(255,255,255,0.35)"; tip = "Market closed — showing the last available price.";
  } else if (delayed) {
    txt = `Delayed ~15 min${clock ? " · " + clock : ""}`; clr = C.yellow;
    tip = "This quote is ~15 minutes behind the market (data-plan limit; Canadian stocks are always delayed). Real-time prices are used for US stocks where available.";
  } else if (ageSec != null && ageSec > 180) {
    txt = `${Math.round(ageSec / 60)} min old${clock ? " · " + clock : ""}`; clr = C.yellow;
    tip = "Real-time source, but this stock's last trade was a few minutes ago (thinly traded).";
  } else {
    txt = `Live${clock ? " · " + clock : ""}`; clr = "#00e676"; tip = "Real-time quote.";
  }
  return <span title={tip} style={{fontSize:10,color:clr,whiteSpace:"nowrap",...(style || {})}}>● {txt}</span>;
}
function PortfolioFreshness({ fr, generatedAt }) {
  if (!fr) return null;
  const pc = _clockOf(fr.pricesAsOf, true), sc = _clockOf(fr.scoresAsOf, false), up = _clockOf(generatedAt ? generatedAt + "Z" : null, true);
  let priceTxt, clr;
  if (!fr.marketOpen) { priceTxt = "Market closed — prices as of the last close"; clr = "rgba(255,255,255,0.4)"; }
  else if (fr.pricesDelayed) { priceTxt = `Some prices ~15 min delayed${pc ? " (oldest " + pc + ")" : ""}`; clr = C.yellow; }
  else { priceTxt = `Prices live${pc ? " · oldest quote " + pc : ""}`; clr = "#00e676"; }
  return (
    <div title="US prices are real-time; Canadian (TSX) prices are ~15 min delayed. Scores and optimizer verdicts refresh about every 30 minutes during market hours."
      style={{fontSize:9,lineHeight:1.5,marginTop:3,color:"rgba(255,255,255,0.4)"}}>
      <span style={{color:clr}}>● {priceTxt}</span>
      {sc && <span> · Scores &amp; verdicts as of {sc}</span>}
      {up && <span> · refreshed {up}</span>}
    </div>
  );
}

function StockScreen({ ticker, user, horizon, token, onBack, watchlist, setWatchlist, upsertUniverse }) {
  // Reset scroll to the top on open / ticker change — otherwise navigating in
  // from a scrolled-down list lands the detail view scrolled past the header,
  // so the ticker is cut off until you scroll up.
  React.useEffect(() => {
    try { window.scrollTo(0, 0); } catch (_) {}
  }, [ticker]);
  // Stable string dep for useEffect — avoids referencing `user` object inside hook
  const userProfile = user?.risk_profile || "moderate";
  const [data,        setData]       = useState(null);
  const [rtMeta,      setRtMeta]     = useState(null);   // v8.4.443 — {asOf, ageSec, delayed, source} of the latest live quote
  const [loading,     setLoading]    = useState(true);
  const [error,       setError]      = useState("");
  const [tab,         setTab]        = useState("overview");
  // Chart preference — persisted to localStorage so the user's chosen
  // horizon and chart type (line/candle) survive navigation between stocks.
  const _chartPrefKey = "gp_chartPref";
  const _loadChartPref = () => {
    try { return JSON.parse(localStorage.getItem(_chartPrefKey)) || {}; } catch { return {}; }
  };
  const [chartHz,   setChartHzState]   = useState(() => _loadChartPref().hz   || "2M");
  const [chartType, setChartTypeState] = useState(() => _loadChartPref().type || "line");
  // Chart-tab mode: price (the trend chart) / entry / patterns / signals.
  const [chartMode, setChartMode]      = useState("price");
  const setChartHz = (hz) => {
    setChartHzState(hz);
    try { localStorage.setItem(_chartPrefKey, JSON.stringify({..._loadChartPref(), hz})); } catch {}
  };
  const setChartType = (type) => {
    setChartTypeState(type);
    try { localStorage.setItem(_chartPrefKey, JSON.stringify({..._loadChartPref(), type})); } catch {}
  };
  // Show auto-detected support/resistance overlay on the price chart.
  // Default off — only shown on horizons where the levels are meaningful
  // (the bands are derived from daily candles, so 1D/1W charts only see a
  // tiny slice and the lines look out of place). User can toggle anyway.
  // Persists per session via the same authDurable approach so it doesn't
  // surprise the user on reopen, but defaults to off for new tabs.
  const [showSR,       setShowSR]      = useState(false);
  const [showSignals,  setShowSignals] = useState(false);
  const [showEarnings, setShowEarnings]= useState(false);
  const [earningsWindowData, setEarningsWindowData] = useState(null);
  const [earningsWindowLoading, setEarningsWindowLoading] = useState(false);
  const [alerts,      setAlerts]     = useState([]);
  const [alertType,   setAlertType]  = useState("price_above");
  const [alertTarget, setAlertTarget]= useState("");
  const [alertMsg,    setAlertMsg]   = useState("");
  const [alertSaving, setAlertSaving]= useState(false);
  const [optData,     setOptData]    = useState(null);
  const [optLoading,  setOptLoading] = useState(false);
  const [optError,    setOptError]   = useState("");
  const [optRationaleOpen, setOptRationaleOpen] = useState(false);
  const [wlAdding,    setWlAdding]   = useState(false);
  const [scoreHistory, setScoreHistory] = React.useState([]);
  const [retryMsg,    setRetryMsg]   = useState("");
  const [retryKey,    setRetryKey]   = useState(0);
  const [swCollapsed, setSwCollapsed]= useState(true);
  const [ltCollapsed, setLtCollapsed]= useState(true);
  const [swCtxCollapsed, setSwCtxCollapsed]= useState(true);
  const [ltCtxCollapsed, setLtCtxCollapsed]= useState(true);
  const [aiReadText, setAiReadText]  = useState(null);
  const [aiReadLoading,setAiReadLoading]=useState(false);
  const [aiReadErr, setAiReadErr]    = useState("");
  const [aiReadCollapsed, setAiReadCollapsed] = useState(false); // minimize after reading

  const inWatchlist = watchlist?.includes(ticker) || false;
  const addToWatchlist = async () => {
    if (inWatchlist || !setWatchlist) return;
    setWlAdding(true);
    try {
      // v8.4.88: atomic add — server merges with authoritative DB list,
      // eliminates race where watchlist state is still [] on first load.
      const d = await apiFetch("/watchlist/add", token, {
        method:"POST", body:JSON.stringify({ticker})
      });
      if (d?.tickers) setWatchlist(d.tickers);
    } catch(e) {}
    finally { setWlAdding(false); }
  };

  const fetchOptions = async () => {
    setOptLoading(true); setOptError(""); setOptData(null); setOptRationaleOpen(false);
    try {
      const d = await apiFetch(`/options/${ticker}?horizon=${horizon}`, token);
      setOptData(d);
    } catch(e) {
      setOptError(e.message || "Failed to load options data.");
    } finally {
      setOptLoading(false);
    }
  };

  // v8.4.443 — keep the header price within seconds while the market is open (US = real-time Finnhub; TSX stays ~15 min delayed and says so).
  // Only a NON-delayed quote overwrites the price the rest of the page uses, so a delayed feed can never replace a fresher number.
  const _dataLoaded = !!data;
  useEffect(() => {
    if (!token || !ticker || !_dataLoaded) return;
    let stop = false;
    const tick = async () => {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
      try {
        const r = await apiFetch(`/quotes/live?tickers=${encodeURIComponent(ticker)}`, token);
        const q = r?.quotes?.[String(ticker).toUpperCase()];
        if (stop || !q || !q.price) return;
        setRtMeta({ asOf: q.asOf || null, ageSec: q.ageSec ?? null, delayed: !!q.delayed, source: q.source || null });
        if (!q.delayed) setData(prev => prev ? { ...prev, price: q.price, changePct: q.changePct ?? prev.changePct, isLive: true } : prev);
      } catch (_) {}
    };
    tick();
    if (!isMarketOpen()) return () => { stop = true; };
    const id = setInterval(tick, 15000);
    return () => { stop = true; clearInterval(id); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, ticker, _dataLoaded]);
  useEffect(() => { setRtMeta(null); }, [ticker]);

  const fetchEarningsWindow = React.useCallback(async () => {
    if (!token || !ticker || earningsWindowLoading) return;
    setEarningsWindowLoading(true);
    try {
      const d = await apiFetch(`/earnings-window/${ticker}`, token);
      setEarningsWindowData(d);
    } catch (_) {}
    finally { setEarningsWindowLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, ticker]);


  const fetchAiRead = async () => {
    if (!token || !ticker) return;
    setAiReadLoading(true); setAiReadErr("");
    try {
      // Pass the page's CURRENT live price/change so the read matches the donuts +
      // header (and the levels strip) instead of drifting from a stale snapshot.
      const _pq = (data?.price != null ? `&price=${data.price}` : "")
                + (data?.changePct != null ? `&changePct=${data.changePct}` : "");
      const d = await _aiAgentFetch(
        () => apiFetch(`/ai/stock-read?ticker=${ticker}&horizon=${horizon}${_pq}`, token, { method: "POST" })
      );
      setAiReadText(d.read || "");
      setAiReadCollapsed(false);   // auto-expand a freshly generated read
    } catch (e) {
      setAiReadErr(e.message || "AI unavailable");
    } finally {
      setAiReadLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(""); setData(null); setRetryMsg("");
    setAiReadText(null); setAiReadErr(""); setAiReadLoading(false);
    // v8.3.27: extended auto-retry with exponential backoff (5 attempts).
    // After attempt 3 show "taking longer than usual" message so the user
    // knows the app is still working. User can also tap "Try again" from
    // the error state to force a fresh retry cycle.
    const delays = [0, 1000, 2500, 5000, 10000];
    (async () => {
      let lastErr = null;
      for (let attempt = 0; attempt < delays.length; attempt++) {
        if (cancelled) return;
        if (attempt > 0) {
          if (attempt >= 2 && !cancelled) {
            setRetryMsg(`Taking longer than usual… (attempt ${attempt + 1} of ${delays.length})`);
          }
          await new Promise(r => setTimeout(r, delays[attempt]));
        }
        if (cancelled) return;
        try {
          const raw = await apiFetch(`/analyze/${ticker}?horizon=${horizon}`, token);
          if (cancelled) return;
          // Guard: ensure profile is always present (universe cache hits may omit it)
          const p = PROFILES.find(x => x.key === userProfile) || PROFILES[1];
          const d = raw.profile ? raw : {
            ...raw,
            profile: { key: p.key, label: p.label, emoji: p.emoji, description: p.desc }
          };
          setData({...d, candles: normaliseCandles(d.candles)});
          setLoading(false);
          // v8.4.39: write the FRESH /analyze result back to universeData so
          // the Watchlist donut for this ticker stays aligned with what the
          // user just saw on the detail screen. Previously StockScreen was
          // the only /analyze entry point that didn't upsert — meaning
          // tapping a ticker showed (say) swing=78 on detail while the
          // Watchlist kept rendering 75 from a stale Celery cycle.
          // Score shape is normalized to flat numbers (the canonical wire
          // shape for universeData — same as scoreUniverseBackground emits).
          if (upsertUniverse && d) {
            const _intra = extractIntradaySparkline(d.candles_intraday_15m, d.candles_intraday);
            const _daily = (Array.isArray(d.sparkline) && d.sparkline.length >= 2)
              ? d.sparkline
              : (Array.isArray(d.candles) ? d.candles.slice(-14)
                  .map(c => typeof c === "object" ? c.c : c)
                  .filter(v => typeof v === "number" && !isNaN(v)) : []);
            upsertUniverse(ticker, {
              ticker,
              shortName:     d.shortName || ticker,
              sector:        d.fundamentals?.sector || "",
              price:         d.price || 0,
              changePct:     d.changePct ?? 0,
              isLive:        !!d.isLive,
              swingScore:    d.swingScore?.score ?? null,
              swingLabel:    d.swingScore?.label || "",
              swingType:     d.swingScore?.labelType || "watch",
              ltScore:       d.longTermScore?.score ?? null,
              ltLabel:       d.longTermScore?.label || "",
              ltType:        d.longTermScore?.labelType || "watch",
              horizonReturn: d.longTermScore?.horizonReturn ?? null,
              beta:          d.beta || 1.0,
              rsi:           d.rsi || 50,
              sentiment:     d.sentiment?.label || "",
              rsRating:      d.rsRating ?? null,
              isLeader:      !!d.isLeader,
              patterns:      Array.isArray(d.patterns) ? d.patterns : [],
              sparkline:     _daily,
              sparklineIntraday: _intra,
              scoredAt:      Date.now(),
            });
          }
          return; // success
        } catch(e) {
          lastErr = e;
        }
      }
      if (cancelled) return;
      setError(lastErr?.message || "Failed to load data");
      setLoading(false);
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticker, horizon, token, userProfile, retryKey]);

  // Fetch score history for confidence pill and overview chart
  React.useEffect(() => {
    if (!ticker || !token) return;
    apiFetch(`/score-history/${ticker}?days=30`, token)
      .then(d => setScoreHistory(d.history || []))
      .catch(() => setScoreHistory([]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticker, token]);

  const loadAlerts = () => {
    apiFetch("/alerts", token)
      .then(d => setAlerts(Array.isArray(d) ? d.filter(a=>a.ticker===ticker) : []))
      .catch(() => setAlerts([]));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (tab==="alerts") loadAlerts(); }, [tab]);

  useEffect(() => {
    if (tab === "trade" && !earningsWindowData && !earningsWindowLoading) fetchEarningsWindow();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, fetchEarningsWindow]);

  const createAlert = async () => {
    if (!alertTarget || isNaN(parseFloat(alertTarget))) {
      setAlertMsg("❌ Enter a valid target value"); return;
    }
    setAlertSaving(true); setAlertMsg("");
    try {
      await apiFetch("/alerts", token, {
        method: "POST",
        body: JSON.stringify({
          ticker, alert_type: alertType,
          target_value: parseFloat(alertTarget),
        }),
      });
      setAlertMsg("✅ Alert created! You'll get an email when it triggers.");
      setAlertTarget("");
      loadAlerts();
    } catch(e) { setAlertMsg(`❌ ${e.message}`); }
    finally { setAlertSaving(false); }
  };

  // Quick-set alert directly from trade level buttons
  const [quickAlertId,  setQuickAlertId]  = useState(null);  // which level bell is active
  const [quickAlertMsg, setQuickAlertMsg] = useState({});     // {labelKey: "✅"|"❌"}

  const quickSetAlert = async (label, price, alertType) => {
    setQuickAlertId(label);
    try {
      await apiFetch("/alerts", token, {
        method: "POST",
        body: JSON.stringify({ ticker, alert_type: alertType, target_value: price }),
      });
      setQuickAlertMsg(prev => ({...prev, [label]: "✅"}));
      loadAlerts();
      setTimeout(() => {
        setQuickAlertMsg(prev => ({...prev, [label]: null}));
        setQuickAlertId(null);
      }, 2000);
    } catch(e) {
      setQuickAlertMsg(prev => ({...prev, [label]: "❌"}));
      setTimeout(() => {
        setQuickAlertMsg(prev => ({...prev, [label]: null}));
        setQuickAlertId(null);
      }, 2000);
    }
  };

  const deleteAlert = async (id) => {
    try {
      await apiFetch(`/alerts/${id}`, token, { method:"DELETE" });
      setAlerts(prev => prev.filter(a => a.id !== id));
    } catch(e) { setAlertMsg(`❌ ${e.message}`); }
  };

  const reactivateAlert = async (id) => {
    try {
      await apiFetch(`/alerts/${id}/toggle`, token, { method:"PATCH" });
      setAlerts(prev => prev.map(a => a.id===id ? {...a, is_active:1, triggered_at:null} : a));
      setAlertMsg("✅ Alert reactivated.");
    } catch(e) { setAlertMsg(`❌ ${e.message}`); }
  };

  if (loading) return (
    <div style={{display:"flex",flexDirection:"column",alignItems:"center",
      justifyContent:"center",minHeight:"60vh",gap:16}}>
      <div style={{width:40,height:40,border:`3px solid ${C.accent}`,
        borderTopColor:"transparent",borderRadius:"50%",
        animation:"spin 0.8s linear infinite"}}/>
      <div style={{color:C.muted,fontSize:13}}>Analyzing {ticker}…</div>
      <div style={{fontSize:11,color:C.muted}}>Fetching live data -- takes ~10s</div>
      {retryMsg && (
        <div style={{fontSize:11,color:C.accent,textAlign:"center",maxWidth:220}}>
          {retryMsg}
        </div>
      )}
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}@keyframes blink{0%,100%{opacity:1}50%{opacity:0}}@keyframes shimmer{0%{left:-100%}100%{left:200%}}@keyframes pulse{0%,100%{opacity:0.4}50%{opacity:0.8}}
    @keyframes logoPulse{
      0%{opacity:0;transform:scale(0.85)}
      30%{opacity:1;transform:scale(1.08)}
      60%{opacity:0.5;transform:scale(1.0)}
      100%{opacity:0;transform:scale(0.85)}
    }`}</style>
    </div>
  );

  if (error) return (
    <div style={{padding:"40px 24px",textAlign:"center"}}>
      <div style={{fontSize:32,marginBottom:12}}>⚠️</div>
      <div style={{fontSize:14,color:C.red,marginBottom:16}}>{error}</div>
      <div style={{display:"flex",gap:12,justifyContent:"center",flexWrap:"wrap"}}>
        <button onClick={onBack} style={{background:C.surface,border:`1px solid ${C.border}`,
          borderRadius:10,padding:"10px 20px",color:C.muted,cursor:"pointer",fontFamily:"inherit"}}>
          ← Back
        </button>
        <button onClick={() => { setError(""); setRetryKey(k => k + 1); }}
          style={{background:C.accent,border:"none",
          borderRadius:10,padding:"10px 20px",color:"#000",fontWeight:700,
          cursor:"pointer",fontFamily:"inherit",fontSize:13}}>
          Try again
        </button>
      </div>
    </div>
  );

  if (!data) return null;

  const { swingScore: sw, longTermScore: lt, sentiment, patterns } = data;
  const tabs = [
    {id:"overview",label:"Overview"},
    {id:"chart",   label:"Chart"},
    {id:"trade",   label:"Trade Setup"},
    {id:"news",    label:"News"},
    {id:"alerts",  label:"◉ Alerts"},
  ];

  return (
    <div>
      {/* Sticky header. Pins to the very top of the viewport on scroll. The app
          root reserves the safe-area (notch) inset as paddingTop, but that strip
          scrolls away once the header is pinned — leaving the Back/★ row to slide
          up under the status bar / clock and become untappable at the bottom of
          the page. Fix: pull the header up by the inset (negative marginTop) so its
          background covers the notch strip, and add the inset back as paddingTop so
          the Back/★ row stays below the clock at every scroll position. */}
      <div style={{position:"sticky",top:0,zIndex:10,
        background:C.bg,borderBottom:`1px solid ${C.border}`,
        marginTop:"calc(-1 * env(safe-area-inset-top, 0px))",
        padding:"calc(10px + env(safe-area-inset-top, 0px)) 16px 10px"}}>
        {/* Row 1 — back (left) + watchlist star toggle (right). v8.4.x compact header */}
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
          <button onClick={onBack} style={{background:C.surface,border:`1px solid ${C.border}`,
            borderRadius:10,padding:"6px 12px",color:C.muted,fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>
            ← Back
          </button>
          {setWatchlist && (
            inWatchlist
              ? <span title="On your watchlist" style={{fontSize:18,lineHeight:1,
                  color:C.green,padding:"4px 8px",flexShrink:0}}>
                  ★
                </span>
              : <button onClick={addToWatchlist} disabled={wlAdding} title="Add to watchlist" style={{
                  fontSize:18,lineHeight:1,background:"transparent",color:C.accent,
                  borderRadius:8,padding:"4px 9px",flexShrink:0,
                  border:`1px solid rgba(0,212,255,0.25)`,cursor:"pointer",fontFamily:"inherit"}}>
                  {wlAdding ? "…" : "☆"}
                </button>
          )}
        </div>
        {/* Rows 2-3 — identity. Row 2: logo + ticker (left), price/change/status (right,
            right-aligned). Row 3: full-width company name. Bid/Ask removed (never
            populated intraday) + ticker/price merged onto one row to keep the frozen
            header tight. */}
        <div style={{marginBottom:12}}>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <TickerLogo ticker={ticker} size={36}/>
            <div style={{fontSize:22,fontWeight:900,color:C.text,letterSpacing:"-0.02em"}}>{ticker}</div>
            <div style={{marginLeft:"auto",textAlign:"right",whiteSpace:"nowrap"}}>
              <span style={{fontSize:17,fontWeight:800,color:C.text}}>
                {data.price!=null?`$${data.price.toFixed(2)}`:"—"}
              </span>
              <span style={{marginLeft:6,fontSize:13,fontWeight:700,
                color:(data.changePct||0)>=0?C.green:C.red}}>
                {(data.changePct||0)>=0?"+":""}{data.changePct??0}%
              </span>
              <FreshnessStamp style={{marginLeft:6}}
                asOf={rtMeta?.asOf} ageSec={rtMeta?.ageSec}
                delayed={rtMeta ? rtMeta.delayed : true}
                marketOpen={isMarketOpen()}/>
            </div>
          </div>
          {data.shortName && data.shortName !== ticker && (
            <div style={{fontSize:12,color:C.muted,marginTop:4,
              whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
              {data.shortName}
            </div>
          )}
        </div>
        {/* Tab bar */}
        <div style={{display:"flex",gap:4}}>
          {tabs.map(t=>(
            <button key={t.id} onClick={()=>setTab(t.id)} style={{
              flex:1,padding:"7px 4px",borderRadius:8,border:"none",cursor:"pointer",
              fontFamily:"inherit",fontSize:11,fontWeight:600,
              background:tab===t.id?C.accent:C.surface,
              color:tab===t.id?C.bg:C.muted,transition:"all 0.15s",
            }}>{t.label}</button>
          ))}
        </div>
      </div>

      <div style={{padding:"14px 16px 100px"}}>
        {/* -- OVERVIEW -- */}
        {tab==="overview" && (
          <div style={{display:"flex",flexDirection:"column",gap:14}}>
            {/* Profile badge */}
            <div style={{display:"flex",alignItems:"center",gap:8,
              background:C.surface,borderRadius:12,padding:"10px 14px",
              border:`1px solid ${C.border}`}}>
              <span style={{fontSize:20}}>{data.profile.emoji}</span>
              <div style={{fontSize:12,color:C.muted}}>
                Scored for your <strong style={{color:C.text}}>{data.profile.label}</strong> profile
              </div>
            </div>

            {/* Dual score cards */}
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
              {/* Swing */}
              <Card style={{textAlign:"center",padding:"18px 12px"}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"center",
                  gap:6,fontSize:10,fontWeight:700,color:C.muted,
                  letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:10}}>
                  <SwingIcon size={13} color={C.muted}/> Swing Score
                </div>
                <ScoreDonut score={sw.score} size={80}/>
                <div style={{marginTop:10}}>
                  <SignalPill type={sw.labelType} label={sw.label} size="sm"/>
                </div>
                {/* Gap-up streak warning — caution when stock has run hard recently */}
                {sw.gapUpStreak >= 2 && (
                  <div style={{marginTop:7,display:"inline-flex",alignItems:"center",
                    gap:4,borderRadius:20,padding:"3px 9px",
                    background:"rgba(255,202,40,0.12)",
                    border:"1px solid rgba(255,202,40,0.3)"}}>
                    <span style={{fontSize:11}}>⚠️</span>
                    <span style={{fontSize:10,fontWeight:700,color:"#ffca28"}}>
                      {sw.gapUpStreak} consecutive days +3% — may be extended
                    </span>
                  </div>
                )}
                {/* Regime-gated profit-taking flag (v8.4.278) — sits with the
                    gap-up/confidence signals under the swing score. Only shows
                    when overextended AND the market regime is weak. */}
                {data.overextension && data.overextension.profitTakeFlag
                  && !(data.pullback && data.pullback.pullbackFlag) && (
                  <div style={{marginTop:7,display:"inline-flex",alignItems:"center",
                    gap:4,borderRadius:20,padding:"3px 9px",
                    background:"rgba(245,158,11,0.15)",
                    border:"1px solid rgba(245,158,11,0.42)"}}>
                    <span style={{fontSize:11}}>⚠️</span>
                    <span style={{fontSize:10,fontWeight:700,color:"#f59e0b"}}>
                      Extended — consider trimming
                    </span>
                    <InfoTip text={data.overextension.reason
                      || `Overextended (${data.overextension.extCount}/5 stretched signals) in a weak market regime — historically these underperform over the next month.`}/>
                  </div>
                )}
                {/* v8.4.283 — per-stock pullback-risk / bounce caution (ml/pullback_risk_signal.py).
                    Pullback supersedes the older "Extended" badge above when it fires (richer,
                    quantified odds + rotation-signal gate). Bounce is additive (green). */}
                {data.pullback && data.pullback.pullbackFlag && (
                  <div style={{marginTop:7,display:"inline-flex",alignItems:"center",
                    gap:4,borderRadius:20,padding:"3px 9px",
                    background:"rgba(245,158,11,0.15)",
                    border:"1px solid rgba(245,158,11,0.42)"}}>
                    <span style={{fontSize:11}}>⚠️</span>
                    <span style={{fontSize:10,fontWeight:700,color:"#f59e0b"}}>
                      Pullback risk{data.pullback.dropOdds5d ? ` — ~${data.pullback.dropOdds5d}% odds of a >5% drop` : ""}
                    </span>
                    <InfoTip text={data.pullback.reason
                      || `Stretched (${Math.round(data.pullback.stretchScore)}/100) into a weak tape — historically an elevated chance of a sharp pullback this week.`}/>
                  </div>
                )}
                {data.pullback && data.pullback.bounceFlag && (
                  <div style={{marginTop:7,display:"inline-flex",alignItems:"center",
                    gap:4,borderRadius:20,padding:"3px 9px",
                    background:"rgba(0,230,118,0.13)",
                    border:"1px solid rgba(0,230,118,0.4)"}}>
                    <span style={{fontSize:11}}>🔄</span>
                    <span style={{fontSize:10,fontWeight:700,color:"#00e676"}}>
                      Oversold bounce setup
                    </span>
                    <InfoTip text={data.pullback.reason
                      || "Sharp drop in an uptrend while oversold — historically an above-average bounce setup."}/>
                  </div>
                )}
                {/* v8.4.295 — FOMC calendar heads-up (informational only; NOT a score/label
                    change). Backtest showed pre-FOMC volatility is NOT elevated, so this is a
                    neutral date reminder, not a caution. Shows within ~7 trading days. */}
                {data.fed && data.fed.daysToMeeting != null && data.fed.daysToMeeting <= 7 && (
                  <div style={{marginTop:7,display:"inline-flex",alignItems:"center",
                    gap:4,borderRadius:20,padding:"3px 9px",
                    background:"rgba(96,140,210,0.15)",
                    border:"1px solid rgba(96,140,210,0.42)"}}>
                    <span style={{fontSize:11}}>🏛️</span>
                    <span style={{fontSize:10,fontWeight:700,color:"#7aa6dd"}}>
                      FOMC {data.fed.daysToMeeting === 0 ? "today" : `in ${data.fed.daysToMeeting}d`}
                      {data.fed.nextMeeting ? ` · ${data.fed.nextMeeting}` : ""}
                    </span>
                    <InfoTip text={`Fed policy decision ${data.fed.nextMeeting || "soon"}. Informational only — it does NOT change the score. In testing the market tends to drift sideways into the meeting; the decision day itself runs only slightly above normal volatility (~+5%), and the days before are typical.`}/>
                  </div>
                )}
                {/* v8.4.297 — sharp-drop bounce vs rotation (ml/sharp_drop_recovery.py). Fires
                    after a -8% day / -10% 3d flush; classified by breadth+regime. Informational. */}
                {data.dropSignal && data.dropSignal.bounceCandidate && (
                  <div style={{marginTop:7,display:"inline-flex",alignItems:"center",
                    gap:4,borderRadius:20,padding:"3px 9px",
                    background:"rgba(0,230,118,0.13)",
                    border:"1px solid rgba(0,230,118,0.4)"}}>
                    <span style={{fontSize:11}}>🟢</span>
                    <span style={{fontSize:10,fontWeight:700,color:"#00e676"}}>
                      Oversold bounce candidate
                    </span>
                    <InfoTip text={data.dropSignal.reason
                      || "Sharp drop, isolated dip in a steady tape — historically recovered ~+3-5% over the next week."}/>
                  </div>
                )}
                {data.dropSignal && data.dropSignal.rotationRisk && (
                  <div style={{marginTop:7,display:"inline-flex",alignItems:"center",
                    gap:4,borderRadius:20,padding:"3px 9px",
                    background:"rgba(255,82,82,0.14)",
                    border:"1px solid rgba(255,82,82,0.42)"}}>
                    <span style={{fontSize:11}}>⚠️</span>
                    <span style={{fontSize:10,fontWeight:700,color:"#ff7a7a"}}>
                      Rotation risk — may keep sliding
                    </span>
                    <InfoTip text={data.dropSignal.reason
                      || "Sharp drop with a broad, weak-market selloff — historically recovers less and tends to keep sliding near-term."}/>
                  </div>
                )}
                {/* v8.4.314 — earnings-driven drop caveat. A drop on an earnings report is a
                    fundamental re-rating, so it qualifies the technical bounce/rotation read. */}
                {data.dropSignal && data.dropSignal.earningsDriven && (
                  <div style={{marginTop:7,marginLeft:6,display:"inline-flex",alignItems:"center",
                    gap:4,borderRadius:20,padding:"3px 9px",
                    background:"rgba(255,202,40,0.14)",
                    border:"1px solid rgba(255,202,40,0.45)"}}>
                    <EarningsGlyph size={12} color={C.yellow}/>
                    <span style={{fontSize:10,fontWeight:700,color:C.yellow}}>
                      On earnings{data.dropSignal.earningsOutcome ? ` — ${data.dropSignal.earningsOutcome}` : ""}
                    </span>
                    <InfoTip text={"This drop landed on an EARNINGS report — a fundamental re-rating, not just a technical flush. An oversold-bounce read is LOWER-confidence here: a miss or weak guidance can keep re-pricing for several sessions. Check the guidance and analyst reaction before treating the drop as a dip to buy."}/>
                  </div>
                )}
                {/* v8.4.324 — recent-earnings pill on the overview, regardless of drop. The
                    "On earnings" badge above only fires on an earnings-DRIVEN drop, so a name
                    that reported and is FLAT/UP (QBTS up post-earnings) showed nothing here.
                    Show a small "Reported Nd ago · OUTCOME" pill so the overview flags the
                    fresh report; gated off when the drop badge already conveys it (no dupe).
                    The full AI overreaction read stays in the Trade Setup tab. */}
                {data.recentEarnings && !(data.dropSignal && data.dropSignal.earningsDriven) && (
                  <div style={{marginTop:7,marginLeft:6,display:"inline-flex",alignItems:"center",
                    gap:4,borderRadius:20,padding:"3px 9px",
                    background:"rgba(255,202,40,0.14)",
                    border:"1px solid rgba(255,202,40,0.45)"}}>
                    <EarningsGlyph size={12} color={C.yellow}/>
                    <span style={{fontSize:10,fontWeight:700,color:C.yellow}}>
                      Reported {fmtEarnDate(data.recentEarnings.date)}{data.recentEarnings.hour ? ` (${hourLabel(data.recentEarnings.hour)})` : ""}
                      {data.recentEarnings.outcome ? ` · ${String(data.recentEarnings.outcome).toUpperCase()}` : ""}
                    </span>
                    <InfoTip text={"This name reported earnings within the last few sessions. A post-earnings move is a fundamental re-rating — see the Trade Setup tab for the ±3-day price window and an AI overreaction-vs-justified read before acting."}/>
                  </div>
                )}
                {/* Confidence bubble from score history */}
                {(() => {
                  const hist = scoreHistory;
                  if (!hist.length) return null;
                  const recent = hist.slice(0, 5);
                  // With 1 entry: always "new" until variance can be computed.
                  // With 2+ entries: use mean-absolute-deviation of swing scores.
                  let conf, cColor, tipText;
                  if (recent.length < 2) {
                    conf = "new"; cColor = C.muted;
                    tipText = "Score history just started — confidence improves as more daily data accumulates.";
                  } else {
                    const avgSw = recent.reduce((a,h) => a + (h.swing_score||0), 0) / recent.length;
                    const variance = recent.reduce((a,h) => a + Math.abs((h.swing_score||0) - avgSw), 0) / recent.length;
                    conf = variance < 5 ? "high" : variance < 12 ? "medium" : "low";
                    cColor = conf==="high" ? C.green : conf==="medium" ? C.yellow : C.red;
                    tipText = `Signal stability based on last ${recent.length} daily scores (swing score variance). High = consistent signal, Low = volatile/changing signal.`;
                  }
                  return (
                    <div style={{marginTop:6,display:"flex",alignItems:"center",
                      justifyContent:"center",gap:4}}>
                      <span style={{width:7,height:7,borderRadius:"50%",
                        background:cColor,display:"inline-block",
                        boxShadow: conf !== "new" ? `0 0 5px ${cColor}` : "none"}}/>
                      <span style={{fontSize:9,color:cColor,fontWeight:700,textTransform:"uppercase"}}>
                        {conf} confidence
                      </span>
                      <InfoTip text={tipText}/>
                    </div>
                  );
                })()}
              </Card>
              {/* Long-term */}
              <Card style={{textAlign:"center",padding:"18px 12px"}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"center",
                  gap:6,fontSize:10,fontWeight:700,color:C.muted,
                  letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:10}}>
                  <LTIcon size={13} color={C.muted}/> Long-Term
                </div>
                <ScoreDonut score={lt.score} size={80}/>
                <div style={{marginTop:10}}>
                  <SignalPill type={lt.labelType} label={lt.label} size="sm"/>
                </div>
              </Card>
            </div>

            {/* 2. AI Read — consolidated score + catalyst + TA synthesis (v8.4.201) */}
            <Card>
              {/* Deterministic 1-liner: always visible instantly from cached data */}
              <div style={{fontSize:11,color:C.muted,marginBottom:10,lineHeight:1.5,
                padding:"8px 10px",background:C.surface,borderRadius:8,
                border:`1px solid ${C.border}`}}>
                <span style={{color:sw.score>=70?C.green:sw.score>=45?C.yellow:C.red,fontWeight:700}}>
                  Swing {sw.score}
                </span>
                <span style={{color:C.muted}}> ({sw.label}) · </span>
                <span style={{color:lt.score>=70?C.green:lt.score>=45?C.yellow:C.red,fontWeight:700}}>
                  LT {lt.score}
                </span>
                <span style={{color:C.muted}}> ({lt.label})</span>
                {data.technicalConfirmation && (
                  <span style={{color:C.muted}}>
                    {" — TA: "}
                    <span style={{fontWeight:700,
                      color:data.technicalConfirmation.label==="CONFIRMED"?C.green:
                           data.technicalConfirmation.label==="WEAK"?C.red:C.yellow}}>
                      {data.technicalConfirmation.label}
                    </span>
                  </span>
                )}
                {data.patternsBias && (
                  <span style={{color:C.muted}}>
                    {" · Patterns: "}
                    <span style={{fontWeight:700,
                      color:data.patternsBias.color==="green"?C.green:
                           data.patternsBias.color==="red"?C.red:C.yellow}}>
                      {data.patternsBias.label}
                    </span>
                    {` (${data.patternsBias.bullishCount}B/${data.patternsBias.bearishCount}Bear)`}
                  </span>
                )}
              </div>
              <AIFeatureHeader
                title="AI Read"
                subtitle={aiReadText ? null : "Score rationale · today's catalyst · TA signals"}
                action={
                  !aiReadText && !aiReadLoading ? (
                    <AIActionButton onClick={fetchAiRead} loading={aiReadLoading}>
                      Generate
                    </AIActionButton>
                  ) : aiReadText ? (
                    <div style={{display:"flex",alignItems:"center",gap:6}}>
                      <AIActionButton onClick={fetchAiRead} variant="secondary">
                        Refresh
                      </AIActionButton>
                      <button onClick={()=>setAiReadCollapsed(c=>!c)}
                        title={aiReadCollapsed?"Expand AI Read":"Minimize AI Read"}
                        style={{background:"none",border:"none",cursor:"pointer",
                          color:C.muted,fontSize:10,padding:"4px 6px",fontFamily:"inherit",
                          flexShrink:0}}>
                        {aiReadCollapsed?"▼":"▲"}
                      </button>
                    </div>
                  ) : null
                }
              />
              {aiReadLoading && !aiReadText && (
                <div style={{fontSize:12,color:C.muted,padding:"8px 0",textAlign:"center"}}>
                  Synthesizing scores, news, and TA signals… (5-15s)
                </div>
              )}
              <AgentErrorBox message={aiReadErr} onRetry={fetchAiRead}/>
              {aiReadText && !aiReadCollapsed && (
                <div style={{marginTop:8}}>
                  {aiReadText.split(/\n\s*\n/).filter(p=>p.trim()).map((p,i)=>(
                    <p key={i} style={{color:i===0?C.text:"#c2cbdd",
                      fontSize:13,lineHeight:i===0?1.6:1.7,
                      fontWeight:i===0?600:400,margin:"0 0 10px 0",
                      whiteSpace:"pre-wrap"}}>{p}</p>
                  ))}
                </div>
              )}
            </Card>

            {/* 3. Technical Confirmation — weighted RAG card */}
            <TechnicalConfirmationCard tc={data.technicalConfirmation} rsi={data.rsi}/>

            {/* 3b. Swing Timing — intraday entry/exit overlay */}
            <IntradayTimingCard it={data.intradayTiming}/>

            {/* 4. Chart Patterns — RAG card */}
            <ChartPatternsCard pb={data.patternsBias}/>

            {/* 5. News sentiment */}
            <Card>
              <SLabel>News sentiment</SLabel>
              <div style={{display:"flex",alignItems:"center",gap:14}}>
                <div style={{textAlign:"center",minWidth:70}}>
                  <div style={{fontSize:28,fontWeight:800,fontFamily:"monospace",
                    color: sentiment.score>0.02?C.green:sentiment.score<-0.02?C.red:C.yellow}}>
                    {sentiment.score>0?"+":""}{sentiment.score.toFixed(2)}
                  </div>
                  <div style={{fontSize:11,color:C.muted,marginTop:2}}>{sentiment.labelEmoji} {sentiment.label}</div>
                </div>
                <div style={{flex:1}}>
                  <div style={{height:4,background:"rgba(255,255,255,0.06)",borderRadius:2,marginBottom:8}}>
                    <div style={{height:"100%",borderRadius:2,width:`${Math.abs(sentiment.score)*100}%`,
                      marginLeft: sentiment.score>=0?"50%":`${50-Math.abs(sentiment.score)*50}%`,
                      background:sentiment.score>0?C.green:C.red,maxWidth:"50%"}}/>
                  </div>
                  <div style={{fontSize:11,color:C.muted}}>{sentiment.count} headlines analysed</div>
                  <div style={{fontSize:11,color:C.muted}}>Source: {sentiment.source}</div>
                </div>
              </div>
            </Card>

            {/* 6. Trade levels — CONCISE strip on the Overview (the full entry/stop/
                target table + one-tap alerts lives on the Trade Setup tab, so this
                isn't a duplicate). Adds an ATR-velocity estimated time-to-target. */}
            <Card>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
                <SLabel>Swing trade levels</SLabel>
                <button onClick={()=>setTab("trade")} style={{background:"none",border:"none",
                  color:C.accent,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit",padding:0}}>
                  Full setup →
                </button>
              </div>
              {(() => {
                const liveEntry = data.price && data.price > 0 ? data.price : sw.entry;
                if (!liveEntry) return <div style={{fontSize:12,color:C.muted}}>Levels unavailable.</div>;
                const cachedRisk = sw.entry && sw.stop ? (sw.entry - sw.stop) : null;
                const cachedRiskPct = cachedRisk && sw.entry ? cachedRisk / sw.entry : null;
                const liveRisk = cachedRiskPct ? liveEntry * cachedRiskPct : cachedRisk || (liveEntry * 0.05);
                const liveStop = liveEntry - liveRisk;
                const liveT2   = liveEntry + 3.5 * liveRisk;   // main target — matches the Trade Setup tab
                const pts = [
                  {label:"Stop",   price:liveStop,  color:C.red},
                  {label:"Entry",  price:liveEntry, color:C.text},
                  {label:"Target", price:liveT2,    color:C.green},
                ];
                const atr = data.atr;
                const eta = (atr && atr > 0) ? Math.max(1, Math.round((liveT2 - liveEntry) / atr)) : null;
                return (
                  <>
                    <div style={{display:"flex",gap:8}}>
                      {pts.map(p=>(
                        <div key={p.label} style={{flex:1,textAlign:"center",background:C.surface,
                          borderRadius:10,padding:"8px 6px",border:`1px solid ${C.border}`}}>
                          <div style={{fontSize:9,color:C.muted,textTransform:"uppercase",letterSpacing:"0.06em"}}>{p.label}</div>
                          <div style={{fontSize:15,fontWeight:800,fontFamily:"monospace",color:p.color,marginTop:2}}>
                            ${p.price.toFixed(2)}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div style={{fontSize:10,color:C.muted,marginTop:8,display:"flex",
                      justifyContent:"space-between",flexWrap:"wrap",gap:6}}>
                      <span>3.5:1 R:R · risk ${liveRisk.toFixed(2)}/sh</span>
                      {eta != null && (
                        <span title="Estimate: target distance ÷ current ATR. Velocity estimate, not a prediction.">
                          est. ~{eta} session{eta===1?"":"s"} to target @ ATR ${atr.toFixed(2)}/day
                        </span>
                      )}
                    </div>
                  </>
                );
              })()}
            </Card>

            {/* Score breakdown */}
            <Card>
              <SLabel>Score breakdown</SLabel>
              {/* ── Swing components (collapsible) ── */}
              <div style={{marginBottom:16}}>
                <button onClick={()=>setSwCollapsed(p=>!p)}
                  style={{display:"flex",alignItems:"center",gap:6,width:"100%",
                    background:"none",border:"none",cursor:"pointer",padding:"0 0 8px",
                    textAlign:"left"}}>
                  <SwingIcon size={13} color={C.muted}/>
                  <span style={{fontSize:11,fontWeight:600,color:C.muted,flex:1}}>
                    Swing components
                  </span>
                  {/* Stoplight bubble matching swing score */}
                  <span style={{
                    fontSize:9,fontWeight:800,borderRadius:6,padding:"2px 7px",
                    color: sw.score>=70?C.green:sw.score>=45?C.yellow:C.red,
                    background: sw.score>=70?"rgba(0,230,118,0.12)":sw.score>=45?"rgba(255,202,40,0.12)":"rgba(255,82,82,0.12)",
                  }}>{sw.score}</span>
                  <span style={{fontSize:11,color:C.muted,marginLeft:4}}>{swCollapsed?"▼":"▲"}</span>
                </button>
                {!swCollapsed && (
                  <>
                    {Object.entries(sw.componentScores).map(([k,v])=>{
                      const color=v>=70?C.green:v>=45?C.yellow:C.red;
                      return (
                        <div key={k} style={{marginBottom:8}}>
                          <div style={{display:"flex",justifyContent:"space-between",fontSize:12,marginBottom:3}}>
                            <span style={{color:C.muted,textTransform:"capitalize"}}>{k}</span>
                            <span style={{color,fontFamily:"monospace",fontWeight:600}}>{v}</span>
                          </div>
                          <div style={{height:3,background:"rgba(255,255,255,0.06)",borderRadius:2}}>
                            <div style={{height:"100%",borderRadius:2,background:color,width:`${v}%`,
                              transition:"width 0.6s cubic-bezier(.4,0,.2,1)"}}/>
                          </div>
                        </div>
                      );
                    })}
                  </>
                )}
                {/* Swing context adjustments — collapsible */}
                {sw.ctxAdjustments && Object.keys(sw.ctxAdjustments).length > 0 && (
                  <div style={{marginTop:swCollapsed?0:10,paddingTop:10,
                    borderTop:swCollapsed?`1px solid ${C.divider}`:`1px solid ${C.divider}`}}>
                    <button onClick={()=>setSwCtxCollapsed(p=>!p)}
                      style={{display:"flex",alignItems:"center",gap:6,width:"100%",
                        background:"none",border:"none",cursor:"pointer",padding:"0 0 4px",
                        textAlign:"left"}}>
                      <span style={{fontSize:10,color:C.muted,flex:1,
                        letterSpacing:"0.08em",textTransform:"uppercase"}}>
                        Market context adjustments
                      </span>
                      <span style={{fontSize:10,color:C.muted}}>{swCtxCollapsed?"▼":"▲"}</span>
                    </button>
                    {!swCtxCollapsed && Object.entries(sw.ctxAdjustments).map(([k,v])=>(
                      <div key={k} style={{display:"flex",justifyContent:"space-between",
                        fontSize:11,marginBottom:4}}>
                        <span style={{color:C.muted,textTransform:"capitalize"}}>
                          {k.replace(/_/g," ")}
                        </span>
                        <span style={{fontFamily:"monospace",fontWeight:600,
                          color:v>0?C.green:v<0?C.red:C.muted}}>
                          {v>0?"+":""}{v}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {/* ── Long-term components (collapsible) ── */}
              <div>
                <button onClick={()=>setLtCollapsed(p=>!p)}
                  style={{display:"flex",alignItems:"center",gap:6,width:"100%",
                    background:"none",border:"none",cursor:"pointer",padding:"0 0 8px",
                    textAlign:"left"}}>
                  <LTIcon size={13} color={C.muted}/>
                  <span style={{fontSize:11,fontWeight:600,color:C.muted,flex:1}}>
                    Long-term components
                  </span>
                  {/* Stoplight bubble matching LT score */}
                  <span style={{
                    fontSize:9,fontWeight:800,borderRadius:6,padding:"2px 7px",
                    color: lt.score>=70?C.green:lt.score>=45?C.yellow:C.red,
                    background: lt.score>=70?"rgba(0,230,118,0.12)":lt.score>=45?"rgba(255,202,40,0.12)":"rgba(255,82,82,0.12)",
                  }}>{lt.score}</span>
                  <span style={{fontSize:11,color:C.muted,marginLeft:4}}>{ltCollapsed?"▼":"▲"}</span>
                </button>
                {!ltCollapsed && (
                  <>
                    {Object.entries(lt.componentScores).map(([k,v])=>{
                      const color=v>=70?C.green:v>=45?C.yellow:C.red;
                      return (
                        <div key={k} style={{marginBottom:8}}>
                          <div style={{display:"flex",justifyContent:"space-between",fontSize:12,marginBottom:3}}>
                            <span style={{color:C.muted,textTransform:"capitalize"}}>{k}</span>
                            <span style={{color,fontFamily:"monospace",fontWeight:600}}>{v}</span>
                          </div>
                          <div style={{height:3,background:"rgba(255,255,255,0.06)",borderRadius:2}}>
                            <div style={{height:"100%",borderRadius:2,background:color,width:`${v}%`}}/>
                          </div>
                        </div>
                      );
                    })}
                  </>
                )}
                {/* LT context adjustments — collapsible */}
                {lt.ctxAdjustments && Object.keys(lt.ctxAdjustments).length > 0 && (
                  <div style={{marginTop:ltCollapsed?0:10,paddingTop:10,
                    borderTop:`1px solid ${C.divider}`}}>
                    <button onClick={()=>setLtCtxCollapsed(p=>!p)}
                      style={{display:"flex",alignItems:"center",gap:6,width:"100%",
                        background:"none",border:"none",cursor:"pointer",padding:"0 0 4px",
                        textAlign:"left"}}>
                      <span style={{fontSize:10,color:C.muted,flex:1,
                        letterSpacing:"0.08em",textTransform:"uppercase"}}>
                        Market context adjustments
                      </span>
                      <span style={{fontSize:10,color:C.muted}}>{ltCtxCollapsed?"▼":"▲"}</span>
                    </button>
                    {!ltCtxCollapsed && Object.entries(lt.ctxAdjustments).map(([k,v])=>(
                      <div key={k} style={{display:"flex",justifyContent:"space-between",
                        fontSize:11,marginBottom:4}}>
                        <span style={{color:C.muted,textTransform:"capitalize"}}>
                          {k.replace(/_/g," ")}
                        </span>
                        <span style={{fontFamily:"monospace",fontWeight:600,
                          color:v>0?C.green:v<0?C.red:C.muted}}>
                          {v>0?"+":""}{v}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Market context summary */}
              {data.marketContext && (
                <div style={{marginTop:12,paddingTop:12,borderTop:`1px solid ${C.divider}`}}>
                  <div style={{fontSize:10,color:C.muted,marginBottom:8,
                    letterSpacing:"0.08em",textTransform:"uppercase"}}>
                    Market context signals
                  </div>
                  {[
                    data.marketContext.insiderLabel !== "Neutral" && {
                      label:"Insider activity", value:data.marketContext.insiderLabel,
                      positive: data.marketContext.insiderSignal > 0
                    },
                    data.marketContext.shortLabel !== "Normal" && {
                      label:"Short interest", value:data.marketContext.shortLabel,
                      positive: data.marketContext.shortSignal >= 0
                    },
                    data.marketContext.relStrengthLabel && data.marketContext.relStrengthLabel !== "Neutral" && {
                      label:"Relative strength", value:data.marketContext.relStrengthLabel,
                      positive: data.marketContext.relStrengthSignal >= 0
                    },
                    data.marketContext.twoYrYield && {
                      label:"2yr Treasury yield",
                      value:`${data.marketContext.twoYrYield}% -- ${data.marketContext.rateCutExpected?"cuts expected":"no cuts priced in"}`,
                      positive: data.marketContext.rateCutExpected
                    },
                    data.marketContext.fomc_days !== null && data.marketContext.fomc_days <= 14 && {
                      label:"FOMC proximity",
                      value:`Meeting in ${data.marketContext.fomc_days} day${data.marketContext.fomc_days!==1?"s":""}`,
                      positive: false
                    },
                    data.marketContext.regimeLabel && data.marketContext.regimeLabel !== "UNKNOWN" && {
                      label:"Market regime",
                      value:`${data.marketContext.regimeLabel} (${data.marketContext.regimeScore ?? "—"}/100) — swing ${data.marketContext.regimeAdj_swing > 0 ? "+" : ""}${data.marketContext.regimeAdj_swing ?? 0} pts, LT ${data.marketContext.regimeAdj_lt > 0 ? "+" : ""}${data.marketContext.regimeAdj_lt ?? 0} pts`,
                      positive: (data.marketContext.regimeAdj_swing ?? 0) >= 0
                    },
                  ].filter(Boolean).map((item,i)=>(
                    <div key={i} style={{display:"flex",gap:8,alignItems:"flex-start",
                      marginBottom:6}}>
                      <span style={{fontSize:12,color:item.positive?C.green:C.red,flexShrink:0}}>
                        {item.positive?"▲":"▼"}
                      </span>
                      <div style={{flex:1}}>
                        <span style={{fontSize:11,color:C.muted}}>{item.label}: </span>
                        <span style={{fontSize:11,color:C.text}}>{item.value}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            {/* 8. Fundamentals — collapsible block of static company stats */}
            <FundamentalsCard data={data}/>
            {/* 8b. ETF top-holdings — renders only for ETFs/funds (data.etfHoldings) */}
            <EtfHoldingsCard data={data} token={token}/>

            {/* 8c. Federal contracts — renders only when USAspending has awards for this company */}
            <FederalContractsCard data={data} token={token}/>
            {/* 8c2. Insider activity — Form 4 open-market buys/sales, last 90d; renders only when there is any */}
            <InsiderActivityCard data={data} token={token}/>
            {/* 8d. Ownership filings — Schedule 13D/13G from SEC EDGAR; renders only when filings exist */}
            <OwnershipFilingsCard data={data} token={token}/>

            {/* 9. Score History — compact chart */}
            <Card>
              <SLabel>📈 Score History</SLabel>
              <ScoreHistoryChart ticker={ticker} token={token}/>
            </Card>

            {/* 10. About — bottom of overview */}
            {data.fundamentals?.description && (
              <Card>
                <SLabel>About</SLabel>
                <CompanyDescription text={data.fundamentals.description}/>
              </Card>
            )}
          </div>
        )}

        {/* -- CHART -- */}
        {tab==="chart" && (
          <div style={{display:"flex",flexDirection:"column",gap:12}}>
            {/* Chart mode: Price (trend) · Entry · Patterns · Signals */}
            <div style={{display:"flex",gap:4}}>
              {[["price","Price"],["entry","Entry"],["fib","Fib"],["patterns","Patterns"],["signals","Signals"]].map(([m,l])=>(
                <button key={m} onClick={()=>setChartMode(m)} style={{
                  flex:1,padding:"7px 4px",borderRadius:8,border:"none",cursor:"pointer",
                  fontFamily:"inherit",fontSize:11,fontWeight:700,
                  background:chartMode===m?C.accent:C.surface,
                  color:chartMode===m?C.bg:C.muted,transition:"all 0.15s",
                }}>{l}</button>
              ))}
            </div>
            {chartMode==="price" && (() => {
          // Chart horizon filter.
          // 1D uses intraday 1-min candles (today + 15-min prior context).
          // 1W uses intraday 30-min candles for trend detail (yfinance 30m fallback
          //   gives full 5-day week; x-axis labels by day — "Mon", "Tue" etc.)
          // 1M+ uses daily candles.
          const CHART_HORIZONS = [
            {key:"1D",  label:"1D",  days:1,   intraday:true},
            {key:"1W",  label:"1W",  days:5,   intraday:true},
            {key:"1M",  label:"1M",  days:21,  intraday:false},
            {key:"2M",  label:"2M",  days:42,  intraday:false},
            {key:"6M",  label:"6M",  days:126, intraday:false},
            {key:"1Y",  label:"1Y",  days:252, intraday:false},
          ];
          const selHz = CHART_HORIZONS.find(h => h.key === chartHz) || CHART_HORIZONS[3];

          // Decide which candle set to use.
          // 1D  → candles_intraday_15m (1-min Polygon or 15m yfinance)
          // 1W  → candles_intraday (30-min yfinance; ~65 bars for 5 trading days)
          // 1M+ → daily candles (data.candles)
          // Fall back: 1D uses candles_intraday when 15m payload missing.
          const candles60m = data.candles_intraday || [];
          const candles15m = data.candles_intraday_15m || [];
          const intradayCandles =
            selHz.key === "1D" && candles15m.length > 0 ? candles15m : candles60m;
          const useIntraday = selHz.intraday && intradayCandles.length > 0;

          // Split 1D intraday into prior-session ("Day HH:MM") and today ("HH:MM").
          // Backend labels today's bars "HH:MM" only when the most recent session is
          // genuinely today (US/Eastern). On weekends/holidays every bar is "Day HH:MM".
          const priorIntraday = intradayCandles.filter(c => /^\w{3} \d{2}:\d{2}$/.test(c.d));
          const todayIntraday = intradayCandles.filter(c => /^\d{2}:\d{2}$/.test(c.d));

          // 1D prior-context: last 15 1-min bars of the previous session, shown muted
          // to give an anchor/scale reference. 15 bars = 15 minutes of prior close activity.
          const PRIOR_CONTEXT = 15;
          const priorContext = priorIntraday.slice(-PRIOR_CONTEXT);

          // show1DPrior: true when we have both prior context bars and today's bars.
          // Used to render the dual-series line chart (muted prior + accent today).
          const hasTodayBars = todayIntraday.length > 1;
          const show1DPrior  = selHz.key === "1D" && hasTodayBars && priorContext.length > 0;

          // After-hours / regular-session split for today's 1D bars.
          // Regular session: 09:30–16:00 ET. After-hours: 16:01+.
          const _t2m = t => { const [hh,mm] = t.split(":"); return +hh*60 + +mm; };
          const MKTOPEN = 9*60+30, MKTCLOSE = 16*60;
          const _todayAH = todayIntraday.filter(c => {
            const t = c.d.split(" ").pop(); return _t2m(t) > MKTCLOSE;
          });
          const hasAfterHours = _todayAH.length > 0;

          // Today's regular-session close: last intraday bar at or before 16:00.
          // Shown as a dotted reference line so after-hours movement is contextualised.
          const _todayRegular = todayIntraday.filter(c => {
            const t = c.d.split(" ").pop(); const m = _t2m(t);
            return m >= MKTOPEN && m <= MKTCLOSE;
          });
          // v8.4.226: anchor to the data-derived regular-session close
          // (prevClose × (1+changePct)) so the chart's Close line matches the
          // overview's "today close" exactly, instead of the last 1-min bar (which
          // is off by a few cents and drifts in after-hours). Falls back to the last
          // regular bar when change data is unavailable.
          const todayRegularClose = (data.prevClose > 0 && data.changePct != null)
            ? data.prevClose * (1 + data.changePct / 100)
            : (_todayRegular.length > 0 ? _todayRegular[_todayRegular.length - 1].c : null);

          // After-hours change %: last after-hours bar vs the last regular-session
          // bar, BOTH taken from the intraday feed. v8.4.306 — the old formula used
          // (data.price − todayRegularClose), but todayRegularClose is
          // prevClose*(1+changePct/100) and changePct is itself derived from
          // data.price, so those two operands are algebraically identical → AH%
          // was structurally ~0 for every ticker (AMD/IONQ showed 0% even when down
          // hard AH). Using the intraday bars gives a real, independent delta.
          const _regCloseBar = _todayRegular.length > 0 ? _todayRegular[_todayRegular.length - 1].c : null;
          const _ahLastBar   = hasAfterHours ? _todayAH[_todayAH.length - 1].c : null;
          const ahChangePct = (hasAfterHours && _regCloseBar > 0 && _ahLastBar > 0)
            ? (_ahLastBar - _regCloseBar) / _regCloseBar * 100
            : null;

          // WINDOW_1D_BARS: fallback used when today's bars are absent (pre-market /
          // weekend / holiday) — shows the most recent session's last N bars.
          const is1MinData   = intradayCandles === candles15m && intradayCandles.length > 100;
          const WINDOW_1D_BARS = is1MinData ? 390 : (intradayCandles === candles15m ? 26 : 8);

          // Prev-session close — horizontal reference for where yesterday closed.
          // v8.4.226: anchor to the authoritative data.prevClose (the same value the
          // overview shows) instead of the last prior-context bar, which is an
          // after-hours print (e.g. 19:45) that drifts from the official 16:00 close.
          const prevSessionClose = (data.prevClose && data.prevClose > 0)
            ? data.prevClose
            : (priorContext.length > 0
                ? priorContext[priorContext.length - 1].c
                : (priorIntraday.length > 0 ? priorIntraday[priorIntraday.length - 1].c : null));
          // v8.4.226: explicit session verticals at the 9:30 open + 16:00 close bars,
          // anchored to the regular-session bars so they line up with the overview's
          // open/close (replaces the single pre-market "open-bell" vertical that landed
          // at the wrong time).
          const openBarD    = _todayRegular.length > 0 ? _todayRegular[0].d : null;
          const closeBarD   = _todayRegular.length > 0 ? _todayRegular[_todayRegular.length - 1].d : null;
          const todayOpenPx = (data.fundamentals?.openPrice ?? data.openPrice ?? data.open
                               ?? (_todayRegular[0] ? (_todayRegular[0].o ?? _todayRegular[0].c) : null));

          let candleSlice;
          if (useIntraday && selHz.key === "1D") {
            if (hasTodayBars) {
              // Tag each today bar by SESSION so each can be styled distinctly:
              //   pre-market (<9:30) → preMarketC, regular (9:30–16:00) → todayC,
              //   after-hours (>16:00) → afterHoursC. Previously pre-market bars were
              //   lumped into todayC and drawn as the solid regular-session line.
              const todayTagged = todayIntraday.map(c => {
                const t = c.d.split(" ").pop(); const m = _t2m(t);
                const isPre = m < MKTOPEN;
                const isAH  = m > MKTCLOSE;
                const isReg = !isPre && !isAH;
                return { ...c, isAH, isPre,
                  preMarketC:  isPre ? c.c : null,
                  todayC:      isReg ? c.c : null,
                  afterHoursC: isAH  ? c.c : null };
              });
              // Bridges so adjacent session lines connect without a visible gap:
              //   pre-market → open (first regular bar also carries preMarketC),
              //   close → after-hours (last regular bar also carries afterHoursC).
              const firstRegIdx = todayTagged.findIndex(c => c.todayC != null);
              if (firstRegIdx > 0 && todayTagged.some(c => c.preMarketC != null)) {
                todayTagged[firstRegIdx] = { ...todayTagged[firstRegIdx],
                  preMarketC: todayTagged[firstRegIdx].c };
              }
              const lastRegIdx = todayTagged.reduce((last, c, i) => c.todayC != null ? i : last, -1);
              if (lastRegIdx >= 0 && hasAfterHours) {
                todayTagged[lastRegIdx] = { ...todayTagged[lastRegIdx],
                  afterHoursC: todayTagged[lastRegIdx].c };
              }
              // Combine: muted prior-day tail (15 bars) + today's full session
              candleSlice = [
                ...priorContext.map(c => ({...c, isPrior: true, priorC: c.c})),
                ...todayTagged,
              ];
            } else {
              // Pre-market / weekend / holiday: show last N bars of most recent session
              candleSlice = intradayCandles.slice(-WINDOW_1D_BARS);
            }
          } else if (useIntraday && selHz.key === "1W") {
            // Full 5-day 30-min dataset (yfinance or Polygon)
            candleSlice = intradayCandles;
          } else {
            // Daily candles — slice to the selected horizon's day count (1M=21, …)
            candleSlice = (data.candles || []).slice(-selHz.days);
          }

          // XAxis tick density.
          // 1D 1-min: ~405 bars → every 120 = one label per 2 hours.
          // 1D 15-min: ~26 bars → every 8 = one label per 2 hours.
          // 1W: explicit ticks (first bar of each trading day) — prevents the
          //   "Wed Wed Today Today Today" duplication from interval-based approach.
          // Daily (1M …): auto-space by data length.
          const xInterval = (useIntraday && selHz.key === "1D")
            ? (is1MinData ? 120 : 8)
            : (candleSlice.length <= 10 ? 1 : candleSlice.length <= 21 ? 3 : candleSlice.length <= 63 ? 7 : candleSlice.length <= 150 ? 21 : 42);

          // 1W: one tick per trading day (first bar of each day).
          // Recharts `ticks` prop accepts exact dataKey values to display.
          const xTicks1W = (() => {
            if (!(useIntraday && selHz.key === "1W")) return undefined;
            const seen = new Set();
            return candleSlice
              .filter(c => {
                // Day key: prefix word ("Mon", "Tue"…) for prior days; "__today__" for today
                const dayKey = c.d && c.d.includes(" ") ? c.d.split(" ")[0] : "__today__";
                if (seen.has(dayKey)) return false;
                seen.add(dayKey); return true;
              })
              .map(c => c.d);
          })();

          // 1D tick formatter: strip "Day " prefix ("Fri 14:00" → "14:00")
          // so prior-day and today bars both show bare "HH:MM" on the axis.
          const tickFormatterIntraday = (v) => {
            if (!v || typeof v !== "string") return v || "";
            const parts = v.split(" ");
            return parts.length > 1 ? parts[parts.length - 1] : v;
          };
          // 1W tick formatter: extract just the day abbreviation ("Mon 09:30" → "Mon";
          // "09:30" i.e. today's bars → "Today").
          const tickFormatter1W = (v) => {
            if (!v || typeof v !== "string") return v || "";
            const parts = v.split(" ");
            return parts.length > 1 ? parts[0] : "Today";
          };
          const xTickFormatter = (useIntraday && selHz.key === "1D")
            ? tickFormatterIntraday
            : (useIntraday && selHz.key === "1W")
              ? tickFormatter1W
              : candleSlice.length > 150
                ? (d => d ? d.slice(0, 3) : d)
                : undefined;

          // YAxis domain
          // Include close, high, AND low from every bar so candle wicks don't clip.
          // priorC / todayC are used on the 1D dual-series view.
          const prices = candleSlice.flatMap(c =>
            [c.priorC, c.todayC, c.c, c.h, c.l].filter(v => v != null && v > 0)
          );
          const priceMin = prices.length ? Math.min(...prices) : 0;
          const priceMax = prices.length ? Math.max(...prices) : 100;

          let yDomain;
          if (selHz.key === "1D" || (useIntraday && selHz.key === "1W")) {
            // Tight domain for short intraday views — fit the visible price action
            // with a small % pad. Do NOT expand for stop/target1 here: on a 5-day
            // 30-min chart, the stop (e.g. -12%) and target1 (e.g. +15%) would
            // massively expand the y-axis and make the week's movement look flat.
            // Stop/target1 reference lines will still render (clipped by the domain).
            const pad = Math.max((priceMax - priceMin) * 0.04, priceMax * 0.005);
            let lo = Math.max(0, priceMin - pad);
            let hi = priceMax + pad;
            // Always include live price in case of intraday gap-up/halt
            if (data?.price > 0) {
              hi = Math.max(hi, data.price * 1.01);
              lo = Math.min(lo, data.price * 0.99);
            }
            yDomain = [lo, hi];
          } else {
            // v8.3.28: data-driven tight y-axis for longer horizons (1M+).
            // Stop/target1 nudged in only when close to the data range.
            const yRange = priceMax - priceMin || priceMax * 0.1;
            const yPad   = Math.max(yRange * 0.06, priceMax * 0.01);
            let yDomMin = Math.max(0, priceMin - yPad);
            let yDomMax = priceMax + yPad;
            if (sw?.stop   > 0 && sw.stop   > priceMin * 0.80) yDomMin = Math.min(yDomMin, sw.stop   * 0.98);
            if (sw?.target1 > 0 && sw.target1 < priceMax * 1.30) yDomMax = Math.max(yDomMax, sw.target1 * 1.02);
            if (data?.price > 0) {
              yDomMax = Math.max(yDomMax, data.price * 1.02);
              yDomMin = Math.min(yDomMin, data.price * 0.98);
            }
            yDomain = [Math.max(0, yDomMin), yDomMax];
          }

          // ── Support/Resistance overlay (Pass 3 Track A, v7.9.53) ─────
          // Bands are derived from daily candles; on intraday horizons (1D/1W)
          // the lines compress against a tiny price range and add visual noise
          // without much info, so we only render when the chart is showing 1M+.
          // The user can still toggle on intraday — the levels are still valid,
          // they just look weird against ~1-day movement.
          const sr_levels = Array.isArray(data?.srLevels) ? data.srLevels : [];
          const srEligible = !["1D","1W"].includes(chartHz);
          const visibleSrLevels = (showSR && sr_levels.length > 0)
            ? sr_levels.filter(l => {
                // Clamp to the chart's y-domain so off-screen levels don't
                // squish the y-axis. yDomain is computed earlier in this
                // closure from the visible candle slice.
                if (!Array.isArray(yDomain) || yDomain.length !== 2) return true;
                return l.level >= yDomain[0] && l.level <= yDomain[1];
              })
            : [];

          const _MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
          const _isoToD = iso => { try { const d = new Date(iso+"T12:00:00"); return `${_MONTHS[d.getMonth()]} ${String(d.getDate()).padStart(2,'0')}`; } catch { return ""; } };

          const chart_signals  = Array.isArray(data?.chartSignals)  ? data.chartSignals  : [];
          const chart_patterns = Array.isArray(data?.chartPatterns) ? data.chartPatterns : [];

          // Map signals → candle slice (only for non-intraday charts where dates align)
          const signalDots = (!useIntraday && showSignals) ? chart_signals.map(sig => {
            const dKey = _isoToD(sig.date);
            const candle = candleSlice.find(c => c.d === dKey);
            return candle ? { ...sig, d: dKey, y: candle.c } : null;
          }).filter(Boolean) : [];

          // Earnings date markers — vertical lines at each past announcement date.
          // Dedupe near-adjacent dates: Polygon's announce date and yfinance's
          // calendar date for the SAME quarter land 1-2 days apart, which drew two
          // overlapping "E" lines per event. Collapse anything within 7 days into one.
          const earningsDatesRaw = Array.isArray(data?.earnings?.earningsDates) ? data.earnings.earningsDates : [];
          const earningsDates = (() => {
            const ts = earningsDatesRaw
              .map(iso => ({ iso, t: new Date(iso + "T12:00:00").getTime() }))
              .filter(x => !isNaN(x.t))
              .sort((a, b) => a.t - b.t);
            const out = [];
            for (const e of ts) {
              if (out.length && Math.abs(e.t - out[out.length - 1].t) < 60 * 864e5) continue; // ≤60d = same quarter (announce/calendar variants)
              out.push(e);
            }
            return out.map(x => x.iso);
          })();
          // v8.4.255: match earnings→candle by the FULL ISO date (not the year-ambiguous
          // "%b %d"), which fixes the duplicate 'E' + wrong-year/vanishing markers on 1Y.
          // Dedup by the candle's iso so one quarter never emits two dots. Nearest-
          // trading-day fallback (≤4d) covers earnings that land on a holiday/weekend.
          // v8.4.257: build the SET of earnings candle-isos, and render markers as
          // DATA-POINT dots via an invisible <Line dot={...}> overlay (below) instead of
          // <ReferenceDot x={category}>. ReferenceDot x-resolution fails on the 1Y axis
          // where the year-less "%b %d" category is DUPLICATED across years → the "E dots
          // vanish on 1Y" bug. A series dot positions by the actual candle (cx,cy), so it's
          // immune to axis density / duplicate categories.
          const earningsIsoSet = (() => {
            if (useIntraday || !showEarnings) return new Set();
            const _t = s => new Date(s + "T12:00:00").getTime();
            const set = new Set();
            for (const iso of earningsDates) {
              let c = candleSlice.find(cc => cc.iso === iso);
              if (!c) {
                const et = _t(iso); let best = null, bestD = Infinity;
                for (const cc of candleSlice) {
                  if (!cc.iso) continue;
                  const dd = Math.abs(_t(cc.iso) - et);
                  if (dd < bestD) { bestD = dd; best = cc; }
                }
                if (best && bestD <= 4 * 864e5) c = best;
              }
              if (c && c.iso) set.add(c.iso);
            }
            return set;
          })();
          const renderEarningsDot = (props) => {
            const { cx, cy, payload } = props;
            if (cx == null || cy == null || !payload || !earningsIsoSet.has(payload.iso)) return null;
            return (
              <g key={`ed-${payload.iso}`} style={{pointerEvents:"none"}}>
                <circle cx={cx} cy={cy} r={5} fill="rgba(255,202,40,0.9)" stroke="#0b0f17" strokeWidth={1}/>
                <text x={cx} y={cy - 9} textAnchor="middle" fill="#ffca28" fontSize={10} fontWeight={800}>E</text>
              </g>
            );
          };

          // Chart pattern horizontal levels clamped to y-domain
          const patternLines = (!useIntraday && showSignals) ? chart_patterns.flatMap(p => {
            const items = [];
            if (p.level)    items.push({ price: p.level,    label: p.name,           type: p.type });
            if (p.neckline) items.push({ price: p.neckline, label: `${p.name} neck`, type: p.type });
            return items;
          }).filter(p => !Array.isArray(yDomain) || yDomain.length !== 2 ||
            (p.price >= yDomain[0] && p.price <= yDomain[1])) : [];

          // ── Horizon change: $ and % over the visible window ───────────────
          // 1D MODE: use the backend's authoritative changePct/changeAmt
          // (computed live as price − prevClose). v8.4.43 fix: previous code
          // derived 1D change from the chart's prior-context bars, but those
          // include pre+after-hours bars. The "last prior bar" was often an
          // after-hours rally print at $X+, not the actual regular-session
          // 4 PM close, so a +2.1% real move rendered as +0.97% in the header.
          // For longer horizons, the chart's first/last bars ARE the right
          // reference, so keep the old computation there.
          let _hzFirst, _hzLast, hzChangeAmt, hzChangePct;
          if (selHz.key === "1D" && data?.changePct != null) {
            hzChangePct = data.changePct;
            hzChangeAmt = (data?.changeAmt != null && data.changeAmt !== 0)
              ? data.changeAmt
              : (data?.price && data.changePct != null
                  ? (data.price * data.changePct / (100 + data.changePct))
                  : null);
            _hzLast = data.price ?? null;
            _hzFirst = (data.price && hzChangeAmt != null)
              ? (data.price - hzChangeAmt) : null;
          } else {
            _hzFirst = (selHz.key === "1D")
              ? (prevSessionClose != null
                  ? prevSessionClose
                  : (todayIntraday.length > 0 ? (todayIntraday[0].o ?? todayIntraday[0].c) : null))
              : (candleSlice.length > 0 ? candleSlice[0].c : null);
            _hzLast = (data?.price > 0) ? data.price
              : (candleSlice.length > 0 ? candleSlice[candleSlice.length - 1].c : null);
            hzChangeAmt = (_hzFirst && _hzLast && _hzFirst > 0)
              ? (_hzLast - _hzFirst) : null;
            hzChangePct = hzChangeAmt != null
              ? (hzChangeAmt / _hzFirst * 100) : null;
          }
          // Formatted strings: "+$4.23  +6.12%" or "-$2.10  -1.87%"
          const _fmtHzAmt = v => v >= 0 ? `+$${v.toFixed(2)}` : `-$${Math.abs(v).toFixed(2)}`;
          const _fmtHzPct = v => v >= 0 ? `+${v.toFixed(2)}%` : `${v.toFixed(2)}%`;

          return (
          <div style={{display:"flex",flexDirection:"column",gap:12}}>
            {/* Horizon selector */}
            <div style={{display:"flex",gap:4,background:C.surface,borderRadius:10,
              padding:"4px",border:`1px solid ${C.border}`}}>
              {CHART_HORIZONS.map(h => (
                <button key={h.key} onClick={()=>setChartHz(h.key)} style={{
                  flex:1, padding:"5px 0", borderRadius:7, border:"none",
                  fontFamily:"inherit", fontSize:11, fontWeight:700, cursor:"pointer",
                  background: chartHz===h.key ? C.accent : "transparent",
                  color:       chartHz===h.key ? C.bg : C.muted,
                  transition:"all 0.15s",
                }}>{h.label}</button>
              ))}
            </div>
            {/* S/R toggle row — only shown when the backend returned levels.
                Stays out of the way for tickers where detection found nothing
                (cold-cache, recent IPO, very flat history, etc). */}
            {sr_levels.length > 0 && (
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",
                padding:"6px 10px",background:C.surface,borderRadius:8,
                border:`1px solid ${C.border}`}}>
                <div style={{display:"flex",alignItems:"center",gap:6,
                  fontSize:11,color:C.muted}}>
                  <span style={{fontSize:10,fontWeight:700,letterSpacing:"0.05em",
                    textTransform:"uppercase"}}>S/R Levels</span>
                  <InfoTip text={
                    "Auto-detected support and resistance bands.\n\n" +
                    "Algorithm: identifies fractal pivots in the daily candles " +
                    "(swing highs / lows isolated by ≥3 bars on each side), " +
                    "clusters nearby pivots within 1×ATR into a single band, " +
                    "and ranks by touch count × recency.\n\n" +
                    "Bands need ≥2 confirmed touches to qualify (single pokes " +
                    "filtered out as noise). Levels further than ±25% from the " +
                    "current price are dropped. Top 5 by strength are surfaced.\n\n" +
                    "Green = support, red = resistance. Intensity scales with " +
                    "touch count + recency."
                  }/>
                </div>
                <button onClick={()=>setShowSR(v=>!v)} style={{
                  padding:"4px 11px",borderRadius:14,border:"none",cursor:"pointer",
                  fontFamily:"inherit",fontSize:10,fontWeight:700,
                  background: showSR ? "rgba(0,212,255,0.15)" : "rgba(255,255,255,0.06)",
                  color:      showSR ? C.accent : C.muted,
                  outline:    showSR ? `1px solid ${C.accent}` : "none",
                }}>
                  {showSR
                    ? `● Showing ${visibleSrLevels.length}/${sr_levels.length}`
                    : `Show ${sr_levels.length}`}
                </button>
              </div>
            )}
            {(chart_signals.length > 0 || chart_patterns.length > 0) && !useIntraday && (
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",
                padding:"6px 10px",background:C.surface,borderRadius:8,
                border:`1px solid ${C.border}`}}>
                <div style={{display:"flex",alignItems:"center",gap:6,fontSize:11,color:C.muted}}>
                  <span style={{fontSize:10,fontWeight:700,letterSpacing:"0.05em",textTransform:"uppercase"}}>
                    Signals
                  </span>
                  <InfoTip text={
                    "Auto-detected technical signals and chart patterns on daily candles.\n\n" +
                    "Signals: MACD crossovers, EMA 9/21 crosses, RSI zone exits (30/70), " +
                    "volume spikes ≥2.5× 20-day average.\n\n" +
                    "Patterns: Double Top/Bottom, Ascending/Descending Triangle.\n\n" +
                    "Green dots = bullish signals; red dots = bearish signals. " +
                    "Pattern necklines shown as dashed lines."
                  }/>
                </div>
                <button onClick={()=>setShowSignals(v=>!v)} style={{
                  padding:"4px 11px",borderRadius:14,border:"none",cursor:"pointer",
                  fontFamily:"inherit",fontSize:10,fontWeight:700,
                  background: showSignals ? "rgba(0,212,255,0.15)" : "rgba(255,255,255,0.06)",
                  color:      showSignals ? C.accent : C.muted,
                  outline:    showSignals ? `1px solid ${C.accent}` : "none",
                }}>
                  {showSignals
                    ? `● Showing ${chart_signals.length + chart_patterns.length}`
                    : `Show ${chart_signals.length + chart_patterns.length}`}
                </button>
              </div>
            )}
            {/* Earnings toggle — only on daily-candle horizons (1M+) */}
            {!useIntraday && (data?.earnings?.earningsDates?.length > 0) && (
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",
                padding:"6px 10px",background:C.surface,borderRadius:8,
                border:`1px solid ${C.border}`}}>
                <div style={{display:"flex",alignItems:"center",gap:6,fontSize:11,color:C.muted}}>
                  <span style={{fontSize:10,fontWeight:700,letterSpacing:"0.05em",textTransform:"uppercase"}}>
                    Earnings
                  </span>
                  <InfoTip text={"Vertical markers on the chart showing past earnings announcement dates.\nToggle to see how the stock moved around each earnings event."}/>
                </div>
                <button onClick={()=>setShowEarnings(v=>!v)} style={{
                  padding:"4px 11px",borderRadius:14,border:"none",cursor:"pointer",
                  fontFamily:"inherit",fontSize:10,fontWeight:700,
                  background: showEarnings ? "rgba(255,202,40,0.15)" : "rgba(255,255,255,0.06)",
                  color:      showEarnings ? C.yellow : C.muted,
                  outline:    showEarnings ? "1px solid rgba(255,202,40,0.5)" : "none",
                }}>
                  {showEarnings
                    ? `● Showing ${data.earnings.earningsDates.length}`
                    : `Show ${data.earnings.earningsDates.length}`}
                </button>
              </div>
            )}
            {/* Line / Candle toggle — available on all horizons including 1D/1W */}
            {(
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",
                padding:"6px 10px",background:C.surface,borderRadius:8,
                border:`1px solid ${C.border}`}}>
                <span style={{fontSize:10,fontWeight:700,color:C.muted,
                  letterSpacing:"0.05em",textTransform:"uppercase"}}>Chart type</span>
                <div style={{display:"flex",gap:4}}>
                  {[{k:"line",l:"Line"},{k:"candle",l:"Candle"}].map(({k,l}) => (
                    <button key={k} onClick={()=>setChartType(k)} style={{
                      padding:"3px 12px",borderRadius:12,border:"none",cursor:"pointer",
                      fontFamily:"inherit",fontSize:10,fontWeight:700,
                      background: chartType===k ? "rgba(0,212,255,0.15)" : "rgba(255,255,255,0.06)",
                      color:      chartType===k ? C.accent : C.muted,
                      outline:    chartType===k ? `1px solid ${C.accent}` : "none",
                    }}>{l}</button>
                  ))}
                </div>
              </div>
            )}
            <Card>
              <SLabel>
                <div style={{display:"flex",alignItems:"baseline",
                  justifyContent:"space-between",gap:6}}>
                  <span>
                    Price · {selHz.label}
                    {useIntraday && selHz.key === "1D" && (
                      <span style={{fontSize:9,color:C.muted,marginLeft:4,fontWeight:400}}>
                        (1-min{show1DPrior ? ", +15min prev" : ", today"})
                      </span>
                    )}
                    {useIntraday && selHz.key === "1W" && (
                      <span style={{fontSize:9,color:C.muted,marginLeft:4,fontWeight:400}}>
                        (30-min, 5-day)
                      </span>
                    )}
                  </span>
                  <div style={{display:"flex",alignItems:"baseline",gap:8,flexShrink:0}}>
                    {hzChangePct != null && (
                      <span style={{
                        fontSize:11, fontWeight:700, letterSpacing:0,
                        textTransform:"none", fontFamily:"monospace",
                        color: hzChangePct >= 0 ? C.green : C.red,
                      }}>
                        {_fmtHzAmt(hzChangeAmt)}&nbsp;&nbsp;{_fmtHzPct(hzChangePct)}
                      </span>
                    )}
                    {ahChangePct != null && selHz.key === "1D" && (
                      <span style={{
                        fontSize:10, fontWeight:600, fontFamily:"monospace",
                        color: ahChangePct >= 0 ? "rgba(0,230,118,0.7)" : "rgba(255,82,82,0.7)",
                      }}
                        title="After-hours change vs today's 4 PM close">
                        AH {ahChangePct >= 0 ? "+" : ""}{ahChangePct.toFixed(2)}%
                      </span>
                    )}
                  </div>
                </div>
              </SLabel>
              <ResponsiveContainer width="100%" height={200}>
                {(show1DPrior && chartType !== "candle") ? (
                  /* 1D line chart — muted prior-day tail + regular session + after-hours */
                  <ComposedChart data={candleSlice} margin={{top:4,right:4,left:-28,bottom:0}}>
                    <defs>
                      <linearGradient id="pgPrior" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%"  stopColor="rgba(120,140,170,0.2)"/>
                        <stop offset="95%" stopColor="rgba(120,140,170,0)"/>
                      </linearGradient>
                      <linearGradient id="pg" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%"  stopColor={C.accent} stopOpacity={0.3}/>
                        <stop offset="95%" stopColor={C.accent} stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="d" tick={{fill:C.muted,fontSize:9}} interval={xInterval} tickLine={false} axisLine={false} tickFormatter={xTickFormatter} minTickGap={40}/>
                    <YAxis tick={{fill:C.muted,fontSize:8}} tickLine={false} axisLine={false} domain={yDomain} tickFormatter={v=>`$${v.toFixed(0)}`}/>
                    <Tooltip contentStyle={{background:C.card,border:`1px solid ${C.border}`,borderRadius:8,fontSize:11}}
                      formatter={(v,name)=>v!=null?[`$${parseFloat(v).toFixed(2)}`,name==="priorC"?"Prev Close":name==="preMarketC"?"Pre-market":name==="afterHoursC"?"After-hours":"Today"]:["—",""]}/>
                    {/* Prior-day tail — muted grey area */}
                    <Area type="monotone" dataKey="priorC" stroke="rgba(140,155,180,0.45)" strokeWidth={1.5}
                      fill="url(#pgPrior)" dot={false} connectNulls={false}/>
                    {/* Pre-market (before 9:30) — muted grey-blue, dotted, no fill */}
                    <Line type="monotone" dataKey="preMarketC" stroke="rgba(160,170,195,0.45)"
                      strokeWidth={1.5} strokeDasharray="2 3" dot={false} connectNulls={false}/>
                    {/* Today regular session — solid accent (the focus) */}
                    <Area type="monotone" dataKey="todayC" stroke={C.accent} strokeWidth={2}
                      fill="url(#pg)" dot={false} connectNulls={false}/>
                    {/* After-hours — muted dimmed line, no fill */}
                    {hasAfterHours && (
                      <Line type="monotone" dataKey="afterHoursC" stroke="rgba(0,212,255,0.35)"
                        strokeWidth={1.5} strokeDasharray="3 2" dot={false} connectNulls={false}/>
                    )}
                    {/* Vertical session markers — 9:30 open + 4:00 close */}
                    {openBarD && (
                      <ReferenceLine x={openBarD} stroke="rgba(255,255,255,0.18)"
                        strokeWidth={1} strokeDasharray="3 3"
                        label={{value: todayOpenPx ? `9:30 $${todayOpenPx.toFixed(2)}` : "9:30",
                          position:"insideTopLeft", fill:"rgba(255,255,255,0.5)", fontSize:8}}/>
                    )}
                    {closeBarD && closeBarD !== openBarD && (
                      <ReferenceLine x={closeBarD} stroke="rgba(255,255,255,0.18)"
                        strokeWidth={1} strokeDasharray="3 3"
                        label={{value:"4pm", position:"insideTopRight",
                          fill:"rgba(255,255,255,0.5)", fontSize:8}}/>
                    )}
                    {/* Prev-session close — horizontal reference */}
                    {prevSessionClose != null && (
                      <ReferenceLine y={prevSessionClose} stroke="rgba(200,200,200,0.35)"
                        strokeDasharray="4 3" strokeWidth={1}
                        label={{value:`Prev $${prevSessionClose.toFixed(2)}`,position:"insideRight",
                          fill:"rgba(200,200,200,0.6)",fontSize:8}}/>
                    )}
                    {/* Today's regular-session close — dotted line, visible in after-hours */}
                    {todayRegularClose != null && todayRegularClose !== prevSessionClose && (
                      <ReferenceLine y={todayRegularClose} stroke="rgba(0,212,255,0.45)"
                        strokeDasharray="4 3" strokeWidth={1}
                        label={{value:`Close $${todayRegularClose.toFixed(2)}`,position:"insideLeft",
                          fill:"rgba(0,212,255,0.65)",fontSize:8}}/>
                    )}
                    {(!useIntraday && showSignals) && (
                      <ReferenceLine y={sw.stop}    stroke={C.red}   strokeDasharray="3 2" strokeWidth={1}/>
                    )}
                    {(!useIntraday && showSignals) && (
                      <ReferenceLine y={sw.target1} stroke={C.green} strokeDasharray="3 2" strokeWidth={1}/>
                    )}
                    {visibleSrLevels.map((l, i) => {
                      const isSupport = l.type === "support";
                      const baseColor = isSupport ? "0,230,118" : "255,82,82";
                      const opacity = Math.min(0.85, 0.35 + l.strength * 0.12);
                      return (
                        <ReferenceLine key={`sr-${i}`} y={l.level}
                          stroke={`rgba(${baseColor},${opacity})`}
                          strokeWidth={i < 3 ? 1.5 : 1}
                          strokeDasharray="2 4"
                          label={{
                            value: `${isSupport ? "S" : "R"} $${l.level} · ${l.touches}×`,
                            position: "insideRight",
                            fill: `rgba(${baseColor},${Math.min(1, opacity + 0.15)})`,
                            fontSize: 9, fontWeight: 600,
                          }}/>
                      );
                    })}
                    {patternLines.map((p, i) => (
                      <ReferenceLine key={`pat-${i}`} y={p.price}
                        stroke={p.type === "bullish" ? "rgba(0,230,118,0.6)" : "rgba(255,150,50,0.6)"}
                        strokeWidth={1.5} strokeDasharray="4 2"
                        label={{ value: p.label, position: "insideLeft",
                          fill: p.type === "bullish" ? C.green : C.yellow,
                          fontSize: 8, fontWeight: 600 }}/>
                    ))}
                    {signalDots.map((sig, i) => {
                      const isBull = sig.dir === "bull" || sig.dir === "up" || sig.dir === "bullish";
                      return (
                        <ReferenceDot key={`sig-${i}`} x={sig.d} y={sig.y}
                          r={4} fill={isBull ? C.green : C.red}
                          stroke="rgba(0,0,0,0.5)" strokeWidth={1}/>
                      );
                    })}
                  </ComposedChart>
                ) : (chartType === "candle") ? (
                  /* Candlestick chart — daily (1W/1M+) and intraday (1D).
                     1D: candleSlice has isPrior:true on the 15 prior-day bars → CandleBar renders them muted. */
                  <ComposedChart data={candleSlice} margin={{top:4,right:4,left:-28,bottom:0}}>
                    <XAxis dataKey="d" tick={{fill:C.muted,fontSize:9}} interval={xTicks1W ? 0 : xInterval} ticks={xTicks1W} tickLine={false} axisLine={false} tickFormatter={xTickFormatter} minTickGap={40}/>
                    <YAxis tick={{fill:C.muted,fontSize:8}} tickLine={false} axisLine={false} domain={yDomain} tickFormatter={v=>`$${v.toFixed(0)}`}/>
                    <Tooltip contentStyle={{background:C.card,border:`1px solid ${C.border}`,borderRadius:8,fontSize:11}}
                      content={({ active, payload }) => {
                        if (!active || !payload?.[0]) return null;
                        const d = payload[0].payload;
                        const isBull = d.c >= (d.o ?? d.c);
                        return (
                          <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:8,
                            padding:"8px 10px",fontSize:11,lineHeight:1.7}}>
                            <div style={{color:d.isPrior?C.muted:"rgba(255,255,255,0.5)",marginBottom:3,fontSize:10}}>
                              {d.isPrior ? "Prev " : ""}{d.d}
                            </div>
                            {d.o != null && <div>O <span style={{color:C.text,fontFamily:"monospace"}}>${d.o.toFixed(2)}</span></div>}
                            {d.h != null && <div>H <span style={{color:"#00e676",fontFamily:"monospace"}}>${d.h.toFixed(2)}</span></div>}
                            {d.l != null && <div>L <span style={{color:"#ff5252",fontFamily:"monospace"}}>${d.l.toFixed(2)}</span></div>}
                            <div>C <span style={{color:isBull?"#00e676":"#ff5252",fontFamily:"monospace",fontWeight:700}}>${d.c.toFixed(2)}</span></div>
                          </div>
                        );
                      }}/>
                    {/* Candle bars — Bar provides x/width; CandleBar draws wick+body.
                        isPrior bars render at 30% opacity via CandleBar's muting logic. */}
                    <Bar dataKey="c" baseValue={yDomain[0]} isAnimationActive={false}
                      shape={(props) => <CandleBar {...props} yDomain={yDomain}/>}/>
                    {/* Moving averages — daily charts only; intraday (1D) lacks SMA columns */}
                    {!useIntraday && <Line type="monotone" dataKey="s20" stroke={C.yellow} strokeWidth={1} dot={false} strokeDasharray="3 3"/>}
                    {!useIntraday && <Line type="monotone" dataKey="s50" stroke="#ff7043" strokeWidth={1} dot={false} strokeDasharray="5 3"/>}
                    {/* Prev-session close — only on 1D intraday candle view */}
                    {(useIntraday && selHz.key === "1D" && prevSessionClose != null) && (
                      <ReferenceLine y={prevSessionClose} stroke="rgba(200,200,200,0.35)"
                        strokeDasharray="4 3" strokeWidth={1}
                        label={{value:`Prev $${prevSessionClose.toFixed(2)}`,position:"insideRight",
                          fill:"rgba(200,200,200,0.6)",fontSize:8}}/>
                    )}
                    {/* Today's regular-session close — dotted reference, visible in after-hours */}
                    {(useIntraday && selHz.key === "1D" && todayRegularClose != null && todayRegularClose !== prevSessionClose) && (
                      <ReferenceLine y={todayRegularClose} stroke="rgba(0,212,255,0.45)"
                        strokeDasharray="4 3" strokeWidth={1}
                        label={{value:`Close $${todayRegularClose.toFixed(2)}`,position:"insideLeft",
                          fill:"rgba(0,212,255,0.65)",fontSize:8}}/>
                    )}
                    {/* Session verticals — 9:30 open + 4:00 close (1D) */}
                    {(useIntraday && selHz.key === "1D" && openBarD) && (
                      <ReferenceLine x={openBarD} stroke="rgba(255,255,255,0.18)"
                        strokeWidth={1} strokeDasharray="3 3"
                        label={{value: todayOpenPx ? `9:30 $${todayOpenPx.toFixed(2)}` : "9:30",
                          position:"insideTopLeft", fill:"rgba(255,255,255,0.5)", fontSize:8}}/>
                    )}
                    {(useIntraday && selHz.key === "1D" && closeBarD && closeBarD !== openBarD) && (
                      <ReferenceLine x={closeBarD} stroke="rgba(255,255,255,0.18)"
                        strokeWidth={1} strokeDasharray="3 3"
                        label={{value:"4pm", position:"insideTopRight",
                          fill:"rgba(255,255,255,0.5)", fontSize:8}}/>
                    )}
                    {(!useIntraday && showSignals) && (
                      <ReferenceLine y={sw.stop}    stroke={C.red}   strokeDasharray="3 2" strokeWidth={1}/>
                    )}
                    {(!useIntraday && showSignals) && (
                      <ReferenceLine y={sw.target1} stroke={C.green} strokeDasharray="3 2" strokeWidth={1}/>
                    )}
                    {visibleSrLevels.map((l, i) => {
                      const isSupport = l.type === "support";
                      const baseColor = isSupport ? "0,230,118" : "255,82,82";
                      const opacity = Math.min(0.85, 0.35 + l.strength * 0.12);
                      return (
                        <ReferenceLine key={`sr-${i}`} y={l.level}
                          stroke={`rgba(${baseColor},${opacity})`}
                          strokeWidth={i < 3 ? 1.5 : 1} strokeDasharray="2 4"
                          label={{ value:`${isSupport?"S":"R"} $${l.level} · ${l.touches}×`,
                            position:"insideRight",
                            fill:`rgba(${baseColor},${Math.min(1, opacity+0.15)})`,
                            fontSize:9, fontWeight:600 }}/>
                      );
                    })}
                    {signalDots.map((sig, i) => {
                      const isBull = sig.dir === "bull" || sig.dir === "up" || sig.dir === "bullish";
                      return (
                        <ReferenceDot key={`sig-${i}`} x={sig.d} y={sig.y}
                          r={4} fill={isBull ? C.green : C.red}
                          stroke="rgba(0,0,0,0.5)" strokeWidth={1}/>
                      );
                    })}
                    {(!useIntraday && showEarnings) && (
                      <Line dataKey="c" stroke="transparent" dot={renderEarningsDot}
                        isAnimationActive={false} legendType="none" activeDot={false}/>
                    )}
                  </ComposedChart>
                ) : (
                  /* Line chart — accent area + SMA overlays */
                  <AreaChart data={candleSlice} margin={{top:4,right:4,left:-28,bottom:0}}>
                    <defs>
                      <linearGradient id="pg" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%"  stopColor={C.accent} stopOpacity={0.3}/>
                        <stop offset="95%" stopColor={C.accent} stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="d" tick={{fill:C.muted,fontSize:9}} interval={xTicks1W ? 0 : xInterval} ticks={xTicks1W} tickLine={false} axisLine={false} tickFormatter={xTickFormatter} minTickGap={40}/>
                    <YAxis tick={{fill:C.muted,fontSize:8}} tickLine={false} axisLine={false} domain={yDomain} tickFormatter={v=>`$${v.toFixed(0)}`}/>
                    <Tooltip contentStyle={{background:C.card,border:`1px solid ${C.border}`,borderRadius:8,fontSize:11}}
                      formatter={v=>[`$${parseFloat(v).toFixed(2)}`,"Close"]}/>
                    <Area type="monotone" dataKey="c" stroke={C.accent} strokeWidth={2} fill="url(#pg)" dot={false}/>
                    {!useIntraday && <Line type="monotone" dataKey="s20" stroke={C.yellow} strokeWidth={1} dot={false} strokeDasharray="3 3"/>}
                    {!useIntraday && <Line type="monotone" dataKey="s50" stroke="#ff7043" strokeWidth={1} dot={false} strokeDasharray="5 3"/>}
                    {(!useIntraday && showSignals) && (
                      <ReferenceLine y={sw.stop}    stroke={C.red}   strokeDasharray="3 2" strokeWidth={1}/>
                    )}
                    {(!useIntraday && showSignals) && (
                      <ReferenceLine y={sw.target1} stroke={C.green} strokeDasharray="3 2" strokeWidth={1}/>
                    )}
                    {visibleSrLevels.map((l, i) => {
                      const isSupport = l.type === "support";
                      const baseColor = isSupport ? "0,230,118" : "255,82,82";
                      const opacity = Math.min(0.85, 0.35 + l.strength * 0.12);
                      return (
                        <ReferenceLine key={`sr-${i}`} y={l.level}
                          stroke={`rgba(${baseColor},${opacity})`}
                          strokeWidth={i < 3 ? 1.5 : 1}
                          strokeDasharray="2 4"
                          label={{
                            value: `${isSupport ? "S" : "R"} $${l.level} · ${l.touches}×`,
                            position: "insideRight",
                            fill: `rgba(${baseColor},${Math.min(1, opacity + 0.15)})`,
                            fontSize: 9, fontWeight: 600,
                          }}/>
                      );
                    })}
                    {patternLines.map((p, i) => (
                      <ReferenceLine key={`pat-${i}`} y={p.price}
                        stroke={p.type === "bullish" ? "rgba(0,230,118,0.6)" : "rgba(255,150,50,0.6)"}
                        strokeWidth={1.5} strokeDasharray="4 2"
                        label={{ value: p.label, position: "insideLeft",
                          fill: p.type === "bullish" ? C.green : C.yellow,
                          fontSize: 8, fontWeight: 600 }}/>
                    ))}
                    {signalDots.map((sig, i) => {
                      const isBull = sig.dir === "bull" || sig.dir === "up" || sig.dir === "bullish";
                      return (
                        <ReferenceDot key={`sig-${i}`} x={sig.d} y={sig.y}
                          r={4} fill={isBull ? C.green : C.red}
                          stroke="rgba(0,0,0,0.5)" strokeWidth={1}/>
                      );
                    })}
                    {(!useIntraday && showEarnings) && (
                      <Line dataKey="c" stroke="transparent" dot={renderEarningsDot}
                        isAnimationActive={false} legendType="none" activeDot={false}/>
                    )}
                  </AreaChart>
                )}
              </ResponsiveContainer>
              <div style={{display:"flex",gap:14,marginTop:8,flexWrap:"wrap"}}>
                {(chartType === "candle"
                    ? (useIntraday
                        ? [{l:"▲ Bull",c:"#00e676"},{l:"▼ Bear",c:"#ff5252"},{l:"Prev",c:"rgba(140,155,180,0.5)"},{l:"Stop",c:C.red},{l:"T1",c:C.green}]
                        : [{l:"▲ Bull",c:"#00e676"},{l:"▼ Bear",c:"#ff5252"},{l:"SMA20",c:C.yellow},{l:"SMA50",c:"#ff7043"},{l:"Stop",c:C.red},{l:"T1",c:C.green}])
                    : (show1DPrior
                        ? [
                            {l:"Prev Close",c:"rgba(140,155,180,0.6)"},
                            {l:"Today",c:C.accent},
                            ...(hasAfterHours ? [{l:"After-hrs",c:"rgba(0,212,255,0.45)"}] : []),
                            {l:"Stop",c:C.red},{l:"Target 1",c:C.green},
                          ]
                        : (!useIntraday
                            ? [{l:"SMA20",c:C.yellow},{l:"SMA50",c:"#ff7043"},{l:"Stop",c:C.red},{l:"Target 1",c:C.green}]
                            : [{l:"Stop",c:C.red},{l:"Target 1",c:C.green}]))
                ).map(({l,c})=>(
                  <div key={l} style={{display:"flex",alignItems:"center",gap:5,fontSize:11}}>
                    <div style={{width:18,height:2,background:c,borderRadius:1}}/>
                    <span style={{color:C.muted}}>{l}</span>
                  </div>
                ))}
              </div>
            </Card>
            <Card>
              <SLabel>Volume ({useIntraday && selHz.key === "1D" ? "1-min" : useIntraday && selHz.key === "1W" ? "30-min" : "daily"}, last {Math.min(candleSlice.length, 30)} bars)</SLabel>
              <ResponsiveContainer width="100%" height={90}>
                <BarChart data={candleSlice.slice(-30)} margin={{top:0,right:4,left:-28,bottom:0}}>
                  <XAxis dataKey="d" tick={{fill:C.muted,fontSize:9}} interval={xTicks1W ? 0 : xInterval} ticks={xTicks1W} tickLine={false} axisLine={false} tickFormatter={xTickFormatter} minTickGap={40}/>
                  <YAxis tick={false} axisLine={false} tickLine={false}/>
                  <Tooltip contentStyle={{background:C.card,border:`1px solid ${C.border}`,borderRadius:8,fontSize:11}}
                    formatter={v=>[v>=1e6?(v/1e6).toFixed(1)+"M":v>=1e3?(v/1e3).toFixed(0)+"K":v,"Vol"]}/>
                  <Bar dataKey="vol" fill={C.accent} opacity={0.6} radius={[2,2,0,0]}/>
                </BarChart>
              </ResponsiveContainer>
            </Card>
            {showSignals && (chart_signals.length > 0 || chart_patterns.length > 0) && !useIntraday && (
              <Card>
                <SLabel>Technical Signals</SLabel>
                <div style={{display:"flex",flexDirection:"column",gap:6}}>
                  {chart_signals.map((sig, i) => {
                    const isBull = sig.dir === "up" || sig.dir === "bullish";
                    return (
                      <div key={i} style={{display:"flex",alignItems:"center",justifyContent:"space-between",
                        padding:"5px 8px",background:C.surface,borderRadius:6}}>
                        <div style={{display:"flex",alignItems:"center",gap:6}}>
                          <span style={{fontSize:10,color:isBull?C.green:C.red,fontWeight:700}}>
                            {isBull ? "▲" : "▼"}
                          </span>
                          <span style={{fontSize:11,color:C.text,fontWeight:600}}>{sig.label}</span>
                        </div>
                        <span style={{fontSize:10,color:C.muted}}>{_isoToD(sig.date)}</span>
                      </div>
                    );
                  })}
                  {chart_patterns.map((p, i) => {
                    const isBull = p.type === "bullish";
                    return (
                      <div key={`p${i}`} style={{display:"flex",alignItems:"center",justifyContent:"space-between",
                        padding:"5px 8px",background:C.surface,borderRadius:6}}>
                        <div style={{display:"flex",flexDirection:"column",gap:2}}>
                          <div style={{display:"flex",alignItems:"center",gap:6}}>
                            <span style={{fontSize:10,color:isBull?C.green:C.yellow,fontWeight:700}}>
                              {isBull ? "▲" : "▼"}
                            </span>
                            <span style={{fontSize:11,color:C.text,fontWeight:600}}>{p.name}</span>
                            <span style={{fontSize:9,color:C.muted,background:`rgba(255,255,255,0.06)`,
                              padding:"1px 5px",borderRadius:4}}>
                              {p.confidence <= 1 ? Math.round(p.confidence * 100) : Math.round(p.confidence)}%
                            </span>
                          </div>
                          {p.description && (
                            <span style={{fontSize:10,color:C.muted,paddingLeft:16}}>{p.description}</span>
                          )}
                        </div>
                        <span style={{fontSize:10,color:C.muted}}>{_isoToD(p.end_date || p.start_date)}</span>
                      </div>
                    );
                  })}
                </div>
              </Card>
            )}
          </div>
          )})()}
            {chartMode==="entry"    && <EntryZoomCard data={data} sw={sw}/>}
            {chartMode==="fib"      && <FibCard data={data}/>}
            {chartMode==="patterns" && <PatternShowcaseCard data={data}/>}
            {chartMode==="signals"  && <SignalStrip data={data}/>}
          </div>
        )}

        {/* -- TRADE SETUP -- */}
        {tab==="trade" && (
          <div style={{display:"flex",flexDirection:"column",gap:12}}>
            {/* Entry-Zoom — today's action vs the suggested entry, above the levels table */}
            <EntryZoomCard data={data} sw={sw}/>
            {/* v8.4.239: compact Fib read — plain-English "what to do" + level chips;
                full ladder/chart lives in the Chart > Fib mode. */}
            {data.fibLevels && (() => {
              const read = fibPlainRead(data.fibLevels, data.price);
              const TONE = { good: C.green, wait: C.yellow, caution: C.red };
              const tc = read ? (TONE[read.tone] || C.muted) : C.muted;
              return (
                <Card>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:6}}>
                    <SLabel>Fib retracement</SLabel>
                    <span style={{fontSize:10,fontWeight:700,color:tc}}>
                      {Math.round(data.fibLevels.currentRetracement*100)}% pulled back
                    </span>
                  </div>
                  {read && (
                    <>
                      <div style={{fontSize:11,color:C.text,lineHeight:1.5,marginBottom:6}}>
                        {read.summary}
                      </div>
                      <div style={{fontSize:11,padding:"7px 9px",borderRadius:8,
                        background:`${tc}14`,border:`1px solid ${tc}38`,color:tc,fontWeight:600,lineHeight:1.45}}>
                        {read.tone==="good"?"✓ ":read.tone==="caution"?"⚠ ":"⏳ "}{read.action}
                      </div>
                    </>
                  )}
                  <div style={{display:"flex",flexWrap:"wrap",gap:6,marginTop:8}}>
                    {data.fibLevels.levels.map((l,i)=>{
                      const isNear = read?.near && read.near.ratio === l.ratio;
                      return (
                        <span key={i} style={{fontSize:9,fontWeight:600,
                          color:isNear?C.accent:C.muted,
                          background:isNear?"rgba(91,140,255,0.14)":"rgba(255,255,255,0.05)",
                          padding:"2px 6px",borderRadius:4}}>
                          {l.label} ${l.price.toFixed(2)}
                        </span>
                      );
                    })}
                  </div>
                </Card>
              );
            })()}
            <Card>
              <SLabel>Swing trade levels</SLabel>
              {(() => {
                // Use live price as entry and recompute stop/targets preserving R:R ratios
                const liveEntry = data.price && data.price > 0 ? data.price : sw.entry;
                const cachedRisk = sw.entry && sw.stop ? (sw.entry - sw.stop) : null;
                const cachedRiskPct = cachedRisk && sw.entry ? cachedRisk / sw.entry : null;
                const liveRisk = cachedRiskPct ? liveEntry * cachedRiskPct : cachedRisk || (liveEntry * 0.05);
                const liveStop   = parseFloat((liveEntry - liveRisk).toFixed(2));
                const liveT1     = parseFloat((liveEntry + 2.0 * liveRisk).toFixed(2));
                const liveT2     = parseFloat((liveEntry + 3.5 * liveRisk).toFixed(2));
                const liveT3     = parseFloat((liveEntry + 5.0 * liveRisk).toFixed(2));
                return [
                {label:"Entry",    price:liveEntry,  note:"Live market price",        color:C.text,    alertType:"price_above"},
                {label:"Stop Loss",price:liveStop,   note:`−$${liveRisk.toFixed(2)} risk per share`, color:C.red, alertType:"stop_loss"},
                {label:"Target 1", price:liveT1,     note:"2:1 reward-to-risk",      color:C.green,   alertType:"price_above"},
                {label:"Target 2", price:liveT2,     note:"3.5:1 — main target",     color:C.green,   alertType:"price_above"},
                {label:"Target 3", price:liveT3,     note:"5:1 — let it run",        color:"#69f0ae", alertType:"price_above"},
              ].map(row=>{
                const qMsg    = quickAlertMsg[row.label];
                const isBusy  = quickAlertId === row.label;
                const isDone  = qMsg === "✅";
                const isFail  = qMsg === "❌";
                // Check if this exact price already has an active alert
                const already = alerts.some(a =>
                  a.is_active && a.alert_type === row.alertType &&
                  Math.abs(a.target_value - row.price) < 0.005
                );
                return (
                  <div key={row.label} style={{display:"flex",alignItems:"center",
                    background:C.surface,borderRadius:10,padding:"10px 14px",
                    border:`1px solid ${C.border}`,marginBottom:8}}>
                    <div style={{width:3,height:36,borderRadius:2,background:row.color,marginRight:12,flexShrink:0}}/>
                    <div style={{flex:1}}>
                      <div style={{fontSize:12,color:C.muted}}>{row.label}</div>
                      <div style={{fontSize:10,color:C.muted,marginTop:1}}>{row.note}</div>
                    </div>
                    <div style={{fontSize:18,fontWeight:700,fontFamily:"monospace",color:row.color,marginRight:10}}>
                      ${row.price.toFixed(2)}
                    </div>
                    {/* Quick alert bell */}
                    <button
                      onClick={()=>!already && !isBusy && quickSetAlert(row.label, row.price, row.alertType)}
                      title={already ? "Alert already set" : `Set ${row.label} alert at $${row.price.toFixed(2)}`}
                      style={{
                        width:32, height:32, borderRadius:8, flexShrink:0,
                        border:"none", cursor: already||isBusy ? "default" : "pointer",
                        display:"flex", alignItems:"center", justifyContent:"center",
                        fontSize:15, transition:"all 0.15s",
                        background: isDone ? "rgba(0,230,118,0.2)"
                                  : isFail ? "rgba(255,82,82,0.15)"
                                  : already ? "rgba(0,212,255,0.08)"
                                  : "rgba(255,255,255,0.06)",
                      }}>
                      {isBusy ? "…"
                       : isDone ? "✅"
                       : isFail ? "❌"
                       : already ? <span style={{color:C.accent,fontSize:13,fontWeight:700}}>◉</span>
                       : <span style={{color:"rgba(255,255,255,0.25)",fontSize:13}}>◎</span>}
                    </button>
                  </div>
                );
              });
              })()}
            </Card>

            {/* Analyst price targets */}
            {data.analyst && (
              <Card>
                <SLabel>Analyst price targets</SLabel>
                {data.analyst.numAnalysts === 0 ? (
                  <div style={{fontSize:12,color:C.muted,textAlign:"center",padding:"12px 0"}}>
                    No analyst coverage available for {ticker}
                  </div>
                ) : (
                  <>
                    {/* Consensus rating */}
                    <div style={{display:"flex",alignItems:"center",gap:12,
                      background:C.surface,borderRadius:10,padding:"12px 14px",
                      border:`1px solid ${C.border}`,marginBottom:12}}>
                      <div style={{flex:1}}>
                        <div style={{fontSize:11,color:C.muted,marginBottom:4}}>
                          Analyst consensus
                        </div>
                        <div style={{fontSize:16,fontWeight:700,color:
                          data.analyst.recType==="strong_buy"||data.analyst.recType==="buy"
                            ? C.green : data.analyst.recType==="pass" ? C.red : C.yellow}}>
                          {data.analyst.recLabel}
                        </div>
                        <div style={{fontSize:10,color:C.muted,marginTop:2}}>
                          {data.analyst.coverage}
                          {" · "}{data.analyst.numAnalysts} analyst{data.analyst.numAnalysts!==1?"s":""}
                        </div>
                      </div>
                      {/* Rating bar 1=Strong Buy to 5=Strong Sell */}
                      {data.analyst.recMean && (
                        <div style={{textAlign:"center",flexShrink:0}}>
                          <div style={{fontSize:22,fontWeight:800,fontFamily:"monospace",
                            color: data.analyst.recMean<=2?C.green:
                                   data.analyst.recMean<=3?C.yellow:C.red}}>
                            {data.analyst.recMean.toFixed(1)}
                          </div>
                          <div style={{fontSize:9,color:C.muted}}>
                            1=Buy · 5=Sell
                          </div>
                          <div style={{width:60,height:4,background:"rgba(255,255,255,0.06)",
                            borderRadius:2,marginTop:4}}>
                            <div style={{
                              height:"100%",borderRadius:2,
                              width:`${Math.min(100,((data.analyst.recMean-1)/4)*100)}%`,
                              background: data.analyst.recMean<=2?C.green:
                                          data.analyst.recMean<=3?C.yellow:C.red,
                            }}/>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* v8.4.309: collapsible Buy/Hold/Sell breakdown */}
                    <AnalystDistribution counts={data.analyst.recCounts}/>

                    {/* Price targets */}
                    <div style={{display:"flex",flexDirection:"column",gap:8}}>
                      {[
                        {label:"High target",   price:data.analyst.targetHigh,  color:"#69f0ae"},
                        {label:"Mean target",   price:data.analyst.targetMean,  color:C.green,  main:true},
                        {label:"Median target", price:data.analyst.targetMedian,color:C.accent},
                        {label:"Low target",    price:data.analyst.targetLow,   color:C.yellow},
                      ].filter(r=>r.price).map(row=>{
                        const dist = data.price
                          ? round2((row.price-data.price)/data.price*100) : null;
                        return (
                          <div key={row.label} style={{display:"flex",alignItems:"center",
                            background:row.main?"rgba(0,230,118,0.05)":C.surface,
                            borderRadius:10,padding:"10px 14px",
                            border:`1px solid ${row.main?"rgba(0,230,118,0.2)":C.border}`}}>
                            <div style={{width:3,height:28,borderRadius:2,
                              background:row.color,marginRight:12,flexShrink:0}}/>
                            <div style={{flex:1}}>
                              <div style={{fontSize:12,color:C.muted}}>{row.label}</div>
                            </div>
                            <div style={{textAlign:"right"}}>
                              <div style={{fontSize:16,fontWeight:700,
                                fontFamily:"monospace",color:row.color}}>
                                ${row.price.toFixed(2)}
                              </div>
                              {dist!==null && (
                                <div style={{fontSize:10,
                                  color:dist>=0?C.green:C.red,marginTop:1}}>
                                  {dist>=0?"+":""}{dist}%
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Upside summary */}
                    {data.analyst.upsidePct !== null && (
                      <div style={{marginTop:10,padding:"10px 14px",borderRadius:10,
                        background:data.analyst.upsidePct>=0
                          ?"rgba(0,230,118,0.08)":"rgba(255,82,82,0.08)",
                        border:`1px solid ${data.analyst.upsidePct>=0
                          ?"rgba(0,230,118,0.2)":"rgba(255,82,82,0.2)"}`}}>
                        <div style={{fontSize:12,color:C.muted,marginBottom:2}}>
                          Analyst mean upside from current price
                        </div>
                        <div style={{fontSize:22,fontWeight:800,fontFamily:"monospace",
                          color:data.analyst.upsidePct>=0?C.green:C.red}}>
                          {data.analyst.upsidePct>=0?"+":""}{data.analyst.upsidePct}%
                        </div>
                      </div>
                    )}
                  </>
                )}
              </Card>
            )}

            <Card>
              <SLabel>Position size calculator (1% portfolio risk)</SLabel>
              <div style={{fontSize:12,color:C.muted,marginBottom:12}}>
                How many shares you could buy while risking only 1% of your portfolio:
              </div>
              <div style={{display:"flex",gap:8}}>
                {[5000,10000,25000,50000].map(cap=>(
                  <div key={cap} style={{flex:1,background:C.surface,borderRadius:10,
                    padding:"12px 6px",textAlign:"center",border:`1px solid ${C.border}`}}>
                    <div style={{fontSize:9,color:C.muted,marginBottom:4}}>
                      ${(cap/1000).toFixed(0)}K
                    </div>
                    <div style={{fontSize:18,fontWeight:700,color:C.accent,fontFamily:"monospace"}}>
                      {Math.max(1,Math.floor(cap*0.01/Math.max(sw.riskPerShare,0.01)))}
                    </div>
                    <div style={{fontSize:9,color:C.muted,marginTop:2}}>shares</div>
                  </div>
                ))}
              </div>
            </Card>
            {/* -- Options section -- */}
            {(() => {
              const isTSX = ticker.endsWith(".TO") || ticker.endsWith(".V");
              if (isTSX) return (
                <Card style={{borderColor:"rgba(255,255,255,0.06)"}}>
                  <SLabel>📊 Options analysis</SLabel>
                  <div style={{fontSize:12,color:C.muted,lineHeight:1.7,textAlign:"center",padding:"8px 0"}}>
                    Options data is not available for TSX-listed stocks.{"\n"}
                    If this stock trades on a US exchange, search the US ticker instead.
                  </div>
                </Card>
              );
              return (
                <Card style={{borderColor:"rgba(0,212,255,0.15)"}}>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",
                    marginBottom: optData||optLoading||optError ? 14 : 0}}>
                    <SLabel style={{marginBottom:0}}>📊 Options analysis</SLabel>
                    {!optData && !optLoading && (
                      <button onClick={fetchOptions} style={{
                        padding:"6px 14px",borderRadius:8,border:`1px solid ${C.accent}`,
                        background:"rgba(0,212,255,0.08)",color:C.accent,fontSize:12,
                        fontWeight:700,cursor:"pointer",fontFamily:"inherit",
                      }}>Analyze</button>
                    )}
                    {optData && !optLoading && (
                      <button onClick={fetchOptions} style={{
                        background:"none",border:`1px solid ${C.border}`,borderRadius:6,
                        padding:"4px 10px",color:C.muted,fontSize:11,cursor:"pointer",
                        fontFamily:"inherit",
                      }}>
              {isMarketOpen() ? "↻ Refresh" : "↻ Refresh (EOD)"}
            </button>
                    )}
                  </div>

                  {optLoading && (
                    <div style={{display:"flex",alignItems:"center",gap:10,padding:"12px 0"}}>
                      <div style={{width:14,height:14,border:`2px solid ${C.accent}`,
                        borderTopColor:"transparent",borderRadius:"50%",
                        animation:"spin 0.7s linear infinite"}}/>
                      <span style={{fontSize:12,color:C.muted}}>
                        Fetching option chain for {ticker}…
                      </span>
                    </div>
                  )}

                  {optError && (
                    <div style={{fontSize:12,color:C.red,padding:"8px 0"}}>{optError}</div>
                  )}

                  {optData && !optData.supported && (
                    <div style={{fontSize:12,color:C.muted,lineHeight:1.7,padding:"4px 0"}}>
                      {optData.note}
                    </div>
                  )}

                  {optData?.supported && (() => {
                    const o   = optData;
                    const rec = o.recommendation;
                    const dirColor = o.direction==="call" ? C.green : C.red;
                    return (
                      <>
                        {/* Direction banner */}
                        <div style={{display:"flex",alignItems:"center",gap:10,
                          background:o.direction==="call"?"rgba(0,230,118,0.08)":"rgba(255,82,82,0.08)",
                          borderRadius:10,padding:"10px 14px",marginBottom:12,
                          border:`1px solid ${o.direction==="call"?"rgba(0,230,118,0.2)":"rgba(255,82,82,0.2)"}`}}>
                          <span style={{fontSize:24}}>{o.directionEmoji}</span>
                          <div style={{flex:1}}>
                            <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                              <span style={{fontSize:14,fontWeight:700,color:dirColor,textTransform:"uppercase"}}>
                                {o.direction} option
                              </span>
                              <span style={{fontSize:10,fontWeight:700,
                                background:dirColor+"22",color:dirColor,
                                borderRadius:4,padding:"1px 6px",border:`1px solid ${dirColor}44`}}>
                                {o.moneyness}
                              </span>
                              {rec.inTheMoney && (
                                <span style={{fontSize:10,fontWeight:700,
                                  background:"rgba(255,202,40,0.15)",color:C.yellow,
                                  borderRadius:4,padding:"1px 6px",border:"1px solid rgba(255,202,40,0.3)"}}>
                                  ITM
                                </span>
                              )}
                            </div>
                            <div style={{fontSize:11,color:C.muted,marginTop:2,lineHeight:1.5}}>
                              {o.rationale}
                            </div>
                          </div>
                          <div style={{textAlign:"right",flexShrink:0}}>
                            <div style={{fontSize:10,color:C.muted}}>Score</div>
                            <div style={{fontSize:16,fontWeight:700,color:dirColor}}>
                              {o.swingScore}
                            </div>
                          </div>
                        </div>

                        {/* Recommended contract */}
                        <div style={{fontSize:10,fontWeight:700,color:C.muted,
                          letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:8}}>
                          Recommended contract
                        </div>
                        <div style={{background:C.surface,borderRadius:10,
                          padding:"12px 14px",border:`1px solid ${C.border}`,marginBottom:12}}>
                          <div style={{display:"flex",justifyContent:"space-between",marginBottom:8}}>
                            <div>
                              <div style={{fontSize:13,fontWeight:700,color:C.text}}>
                                {ticker} ${rec.strike} {o.direction.toUpperCase()}
                              </div>
                              <div style={{fontSize:11,color:C.muted,marginTop:2}}>
                                Exp: {o.expiry} · {o.dte} days · {o.profileLabel} profile
                              </div>
                            </div>
                            <div style={{textAlign:"right"}}>
                              <div style={{fontSize:18,fontWeight:800,
                                fontFamily:"monospace",color:C.accent}}>
                                ${rec.mid}
                              </div>
                              <div style={{fontSize:10,color:C.muted}}>
                                {rec.bid}/{rec.ask} bid/ask
                              </div>
                            </div>
                          </div>

                          {/* Key metrics row */}
                          <div style={{display:"flex",gap:6}}>
                            {[
                              {label:"IV",  val:`${rec.iv}%`,  tip:"Implied Volatility — the market's expectation of future price movement. Higher IV = more expensive options. Above 50% is elevated."},
                              {label:"OI",  val:(rec.openInterest||0).toLocaleString(), tip:"Open Interest — total open contracts. Higher OI means better liquidity and easier to fill your order at a fair price."},
                              {label:"Vol", val:(rec.volume||0).toLocaleString(), tip:"Daily trading volume for this contract. Low or zero volume means the contract may be hard to fill at the mid price — check your broker before entering.", color:(rec.volume||0)===0?C.red:(rec.volume||0)<50?C.yellow:C.text},
                              {label:"B/E", val:`$${rec.breakeven}`, tip:"Breakeven price — the stock price where you neither profit nor lose at expiry. For a Call: Strike + Premium paid."},
                            ].map(({label,val,tip,color})=>(
                              <div key={label} style={{flex:1,background:C.card,
                                borderRadius:8,padding:"6px 4px",textAlign:"center",
                                border:`1px solid ${C.border}`}}>
                                <div style={{fontSize:9,color:C.muted,display:"flex",
                                  alignItems:"center",justifyContent:"center",gap:3}}>
                                  {label} <InfoTip text={tip}/>
                                </div>
                                <div style={{fontSize:12,fontWeight:600,color:color||C.text,
                                  marginTop:2}}>{val}</div>
                              </div>
                            ))}
                          </div>

                          {/* Liquidity warning */}
                          {o.liquidityWarning && (
                            <div style={{display:"flex",alignItems:"center",gap:6,
                              marginTop:8,padding:"6px 10px",borderRadius:7,
                              background:"rgba(255,202,40,0.08)",
                              border:"1px solid rgba(255,202,40,0.25)"}}>
                              <span style={{fontSize:13}}>⚠️</span>
                              <span style={{fontSize:11,color:C.yellow,lineHeight:1.4}}>
                                Thin liquidity — OI or volume is low. Verify fills at your broker before entering.
                              </span>
                            </div>
                          )}

                          {/* Contract rationale — collapsible */}
                          {o.contractRationale && (
                            <div style={{marginTop:8}}>
                              <button
                                onClick={()=>setOptRationaleOpen(v=>!v)}
                                style={{display:"flex",alignItems:"center",gap:5,
                                  background:"none",border:"none",padding:0,cursor:"pointer",
                                  color:C.muted,fontSize:11,fontFamily:"inherit"}}>
                                <span style={{fontSize:12,
                                  transform:optRationaleOpen?"rotate(90deg)":"none",
                                  transition:"transform 0.15s",display:"inline-block"}}>▶</span>
                                Why this contract?
                              </button>
                              {optRationaleOpen && (
                                <div style={{fontSize:11,color:C.muted,lineHeight:1.7,
                                  marginTop:6,padding:"8px 10px",borderRadius:7,
                                  background:C.surface,border:`1px solid ${C.border}`}}>
                                  {o.contractRationale}
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Risk / Reward */}
                        <div style={{display:"flex",gap:8,marginBottom:12}}>
                          <div style={{flex:1,background:C.redBg,borderRadius:10,
                            padding:"10px 12px",border:"1px solid rgba(255,82,82,0.2)",
                            textAlign:"center"}}>
                            <div style={{fontSize:10,color:C.muted,marginBottom:4}}>Max loss</div>
                            <div style={{fontSize:16,fontWeight:700,color:C.red,
                              fontFamily:"monospace"}}>${rec.maxLoss}</div>
                            <div style={{fontSize:9,color:C.muted,marginTop:2}}>per contract (100 shares)</div>
                          </div>
                          {rec.rrRatio && (
                            <div style={{flex:1,background:"rgba(0,212,255,0.06)",borderRadius:10,
                              padding:"10px 12px",border:`1px solid rgba(0,212,255,0.2)`,
                              textAlign:"center"}}>
                              <div style={{fontSize:10,color:C.muted,marginBottom:4}}>R/R ratio</div>
                              <div style={{fontSize:16,fontWeight:700,color:C.accent,
                                fontFamily:"monospace"}}>{rec.rrRatio}:1</div>
                              <div style={{fontSize:9,color:C.muted,marginTop:2}}>at price target</div>
                            </div>
                          )}
                        </div>

                        {/* Top 3 contracts table */}
                        <div style={{fontSize:10,fontWeight:700,color:C.muted,
                          letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:8}}>
                          Top 3 contracts
                        </div>
                        {o.top3.map((c,i)=>(
                          <div key={i} style={{display:"flex",alignItems:"center",gap:8,
                            background:i===0?`rgba(0,212,255,0.06)`:C.surface,
                            borderRadius:8,padding:"8px 12px",marginBottom:6,
                            border:`1px solid ${i===0?`rgba(0,212,255,0.2)`:C.border}`}}>
                            {i===0 && (
                              <span style={{fontSize:9,background:C.accent,color:C.bg,
                                borderRadius:4,padding:"1px 5px",fontWeight:700,flexShrink:0}}>
                                REC
                              </span>
                            )}
                            <div style={{flex:1}}>
                              <div style={{display:"flex",alignItems:"center",gap:6}}>
                                <span style={{fontSize:12,fontWeight:600,color:C.text,
                                  fontFamily:"monospace"}}>${c.strike}</span>
                                {c.liquidityWeak && (
                                  <span style={{fontSize:9,color:C.yellow,
                                    border:"1px solid rgba(255,202,40,0.35)",
                                    borderRadius:3,padding:"0 4px"}}>low liq</span>
                                )}
                              </div>
                              <span style={{fontSize:10,color:C.muted}}>
                                IV {c.iv}% · OI {(c.openInterest||0).toLocaleString()} · Vol {(c.volume||0).toLocaleString()}
                              </span>
                            </div>
                            <div style={{textAlign:"right",flexShrink:0}}>
                              <div style={{fontSize:13,fontWeight:700,fontFamily:"monospace",
                                color:C.accent}}>${c.mid}</div>
                              <div style={{fontSize:9,color:C.muted}}>
                                {c.bid}/{c.ask}
                              </div>
                            </div>
                          </div>
                        ))}


                        <div style={{fontSize:10,color:C.muted,marginTop:10,lineHeight:1.5}}>
                          ⚠️ Options carry significant risk including total loss of premium.
                          This is educational only -- not financial advice. Verify all data with your broker.
                        </div>
                      </>
                    );
                  })()}
                </Card>
              );
            })()}

            {/* v8.4.319 — Earnings reaction: pill + AI overreaction read, ABOVE the ±3-day
                graphs (moved here from the overview per request). Shows whenever the backend
                detected a recent report (data.recentEarnings, bulk-aware) or an earnings-driven
                drop. */}
            {(data.recentEarnings || data.dropSignal?.earningsDriven) && (
              <Card>
                <SLabel>Earnings reaction</SLabel>
                {data.recentEarnings && (
                  <div style={{display:"inline-flex",alignItems:"center",gap:5,marginBottom:8,
                    borderRadius:20,padding:"3px 10px",background:"rgba(255,202,40,0.14)",
                    border:"1px solid rgba(255,202,40,0.45)"}}>
                    <EarningsGlyph size={12} color={C.yellow}/>
                    <span style={{fontSize:11,fontWeight:700,color:C.yellow}}>
                      Reported {fmtEarnDate(data.recentEarnings.date)}{data.recentEarnings.hour ? ` (${hourLabel(data.recentEarnings.hour)})` : ""}
                      {data.recentEarnings.outcome ? ` · ${String(data.recentEarnings.outcome).toUpperCase()}` : ""}
                      {data.changePct != null ? ` · ${data.changePct>=0?"+":""}${data.changePct}% since` : ""}
                    </span>
                  </div>
                )}
                <div style={{fontSize:11,color:C.muted,lineHeight:1.5,marginBottom:2}}>
                  Was the move an overreaction (mean-reversion candidate) or a justified re-rating
                  (falling knife)? The AI weighs the reported results + guidance/analyst reaction.
                </div>
                <EarningsReactionCard ticker={ticker} token={token}/>
              </Card>
            )}

            {/* Earnings Window Card */}
            {(() => {
              const ewWindows = earningsWindowData?.windows;
              const hasUpcoming = data?.earnings?.daysUntil != null && data.earnings.daysUntil <= 3;
              // v8.4.314 — detect a JUST-REPORTED earnings (most recent announce date within
              // ~3 sessions) to show a "reported / expect volatility" banner before the ±3-day
              // post-earnings window has enough sessions to render.
              const recentEarn = (() => {
                const eds = data?.earnings?.earningsDates || [];
                const eba = data?.earnings?.earningsByAnnounce || {};
                const now = new Date();
                let best = null;
                for (const iso of eds) {
                  const dt = new Date(iso + "T00:00:00");
                  const days = Math.floor((now - dt) / 86400000);
                  if (days >= 0 && days <= 5 && (!best || days < best.days)) {  // 5d: survive weekend
                    const eps = eba[iso] || {};
                    const outcome = eps.beat === true ? "BEAT"
                      : eps.beat === false ? ((eps.surprise != null && Math.abs(eps.surprise) <= 2) ? "IN-LINE" : "MISS")
                      : null;
                    best = { iso, days, outcome };
                  }
                }
                return best;
              })();
              if (!ewWindows && !earningsWindowLoading && !recentEarn) return null;
              return (
                <Card>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
                    <SLabel style={{marginBottom:0}}>📈 Earnings Price Action</SLabel>
                    {earningsWindowLoading && <span style={{fontSize:10,color:C.muted}}>Loading…</span>}
                    {!earningsWindowLoading && !ewWindows && (
                      <button onClick={fetchEarningsWindow} style={{fontSize:10,color:C.accent,
                        background:"none",border:"none",cursor:"pointer",padding:0}}>Load</button>
                    )}
                  </div>

                  {earningsWindowLoading && (
                    <div style={{textAlign:"center",padding:"20px 0",color:C.muted,fontSize:12}}>Fetching price windows…</div>
                  )}

                  {/* v8.4.314 — just-reported banner: the ±3-day window needs a few
                      post-earnings sessions before it can chart, so surface the event now. */}
                  {recentEarn && (
                    <div style={{fontSize:11,lineHeight:1.5,padding:"9px 11px",borderRadius:8,marginBottom:12,
                      background:"rgba(255,202,40,0.10)",border:"1px solid rgba(255,202,40,0.35)"}}>
                      <EarningsGlyph size={12} color={C.yellow}/>{" "}
                      <strong style={{color:C.text}}>Reported {fmtEarnDate(recentEarn.iso)}</strong>
                      {recentEarn.outcome && (
                        <span style={{color: recentEarn.outcome==="BEAT"?C.green:recentEarn.outcome==="MISS"?C.red:C.yellow,
                          fontWeight:700}}> · {recentEarn.outcome}</span>
                      )}
                      {data.changePct != null && (
                        <span style={{color:C.muted}}> · {data.changePct>=0?"+":""}{data.changePct}% since</span>
                      )}
                      <div style={{color:C.muted,marginTop:2}}>
                        The ±3-day price-action window below fills in over the next few sessions — expect elevated volatility.
                      </div>
                    </div>
                  )}

                  {ewWindows && ewWindows.length === 0 && !recentEarn && (
                    <div style={{fontSize:11,color:C.muted,padding:"8px 0"}}>
                      {(() => {
                        const r = earningsWindowData?.reason;
                        const msg = r === "no_price_history" ? "Price history is temporarily unavailable — check back shortly."
                                  : (r === "no_dates" || r === "no_windows_built")
                                      ? "Confirmed historical earnings dates aren't available from our data sources for this ticker yet — the window will populate after its next report."
                                  : (r && r.startsWith("exception")) ? "Earnings window temporarily unavailable."
                                  : "No historical earnings windows available yet.";
                        return <>{msg}{r ? <span style={{opacity:0.4,fontSize:9}}> ({r})</span> : null}</>;
                      })()}
                    </div>
                  )}

                  {ewWindows && ewWindows.map((win, wi) => {
                    const { eps, postDrift3d, preDrift3d, bars, date, hour, reactionMove, postDrift1m, postDrift3m, mktDrift1m, mktDrift3m } = win;
                    const outcome  = eps?.outcome;
                    const badgeClr = outcome === "beat" ? C.green : outcome === "miss" ? C.red : C.yellow;
                    const badgeLbl = outcome ? outcome.toUpperCase() : "?";
                    const drift    = postDrift3d;
                    const driftClr = drift == null ? C.muted : drift >= 0 ? C.green : C.red;
                    const rxnClr   = reactionMove == null ? C.muted : reactionMove >= 0 ? C.green : C.red;
                    const minC = bars.length ? Math.min(...bars.map(b=>b.l)) : 0;
                    const maxC = bars.length ? Math.max(...bars.map(b=>b.h)) : 1;
                    const pad  = (maxC - minC) * 0.08 || 0.5;
                    // v8.4.325 — bell-aware: index the bars so the boundary line can sit
                    // at the LEFT edge of the reaction bar (between D-1 and D0) via a numeric
                    // x-axis, and label it by when the report landed vs the session.
                    const barsIdx     = bars.map((b,i)=>({...b, idx:i}));
                    const reactionIdx = barsIdx.findIndex(b => b.isEarningsDay);
                    const bellLabel   = hour === "bmo" ? "before open" : hour === "amc" ? "after close"
                                      : hour === "dmh" ? "during hours" : null;
                    return (
                      <div key={wi} style={{marginBottom: wi < ewWindows.length-1 ? 18 : 0}}>
                        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
                          <span style={{fontSize:10,color:C.muted}}>{date}</span>
                          <span style={{fontSize:10,fontWeight:700,color:badgeClr,
                            border:`1px solid ${badgeClr}`,borderRadius:4,
                            padding:"1px 6px",letterSpacing:"0.05em"}}>
                            {badgeLbl}
                          </span>
                          {bellLabel && (
                            <span style={{fontSize:9,fontWeight:700,color:C.muted,
                              border:`1px solid ${C.border}`,borderRadius:4,padding:"1px 5px",
                              textTransform:"uppercase",letterSpacing:"0.04em"}}>
                              {bellLabel}
                            </span>
                          )}
                          {eps?.actual != null && (
                            <span style={{fontSize:10,color:C.muted}}>
                              EPS {eps.actual >= 0 ? "+" : ""}{eps.actual}
                              {eps.estimate != null && ` vs est ${eps.estimate}`}
                            </span>
                          )}
                          <div style={{flex:1}}/>
                          {reactionMove != null && (
                            <span style={{fontSize:11,fontWeight:700,fontFamily:"monospace",color:rxnClr}}
                              title="Reaction: prior close → the first session that traded the news">
                              {reactionMove >= 0 ? "+" : ""}{reactionMove}%
                            </span>
                          )}
                        </div>

                        {/* Mini 7-day price chart */}
                        <ResponsiveContainer width="100%" height={80}>
                          <ComposedChart data={barsIdx} margin={{top:4,right:4,left:-32,bottom:0}}>
                            {/* numeric x keyed on bar index so the boundary line can sit at a
                                HALF-index (left edge of the reaction bar). Half-band padding
                                on each side mirrors the old category spacing. */}
                            <XAxis dataKey="idx" type="number" domain={[-0.5, barsIdx.length-0.5]}
                              tick={false} axisLine={false} tickLine={false} hide/>
                            <YAxis tick={{fill:C.muted,fontSize:8}} tickLine={false} axisLine={false}
                              domain={[minC-pad, maxC+pad]} tickFormatter={v=>`$${v.toFixed(0)}`}/>
                            {/* v8.4.326 — no per-point earnings dot: the reaction session is
                                marked by the dashed boundary line (at the reaction bar's left
                                edge) + the D0 label below. The old yellow dot sat ON the D0 data
                                point, half a bar right of the moved line → looked misaligned. */}
                            <Line type="monotone" dataKey="c" stroke={C.accent} strokeWidth={2} dot={false}/>
                            {reactionIdx >= 0 && (
                              <ReferenceLine x={reactionIdx - 0.5}
                                stroke="rgba(255,202,40,0.6)" strokeDasharray="3 2" strokeWidth={1.5}
                                label={{ value: bellLabel ? `E · ${bellLabel}` : "E", position:"insideTopRight", offset:-2, fill:C.yellow, fontSize:9, fontWeight:700 }}/>
                            )}
                          </ComposedChart>
                        </ResponsiveContainer>

                        {/* Day labels below mini chart */}
                        <div style={{display:"flex",justifyContent:"space-between",
                          fontSize:9,color:C.muted,marginTop:2,paddingInline:4}}>
                          {bars.map((b,bi) => (
                            <span key={bi} style={{
                              color: b.isEarningsDay ? C.yellow : C.muted,
                              fontWeight: b.isEarningsDay ? 700 : 400,
                            }}>
                              {/* reaction session = D0 (the "E" lives on the dashed boundary
                                  line now; a second "E" here read as a misaligned x-axis mark) */}
                              {b.dayOffset < 0 ? `D${b.dayOffset}` : b.dayOffset === 0 ? "D0" : `+${b.dayOffset}`}
                            </span>
                          ))}
                        </div>

                        <div style={{display:"flex",gap:14,flexWrap:"wrap",fontSize:10,color:C.muted,marginTop:4}}>
                          {reactionMove != null && (
                            <span>Reaction: <b style={{color:rxnClr}}>{reactionMove>=0?"+":""}{reactionMove}%</b></span>
                          )}
                          {postDrift3d != null && (
                            <span>3d drift: <b style={{color:driftClr}}>{postDrift3d>=0?"+":""}{postDrift3d}%</b></span>
                          )}
                          {preDrift3d != null && (
                            <span>Run-up: <b style={{color:preDrift3d>=0?C.green:C.red}}>{preDrift3d>=0?"+":""}{preDrift3d}%</b></span>
                          )}
                          {/* v8.4.434 — what the stock did over the following ~1 and ~3 months (history only; S&P over the same dates alongside) */}
                          {[["1m", postDrift1m, mktDrift1m], ["3m", postDrift3m, mktDrift3m]].map(([lbl, v, m]) => v != null && (
                            <span key={lbl}>{lbl} after: <b style={{color:v>=0?C.green:C.red}}>{v>=0?"+":""}{v}%</b>
                              {m != null && <span style={{opacity:0.65}}> (S&P {m>=0?"+":""}{m}%)</span>}</span>
                          ))}
                        </div>
                        {bellLabel && (
                          <div style={{fontSize:9,color:C.muted,opacity:0.75,marginTop:3,lineHeight:1.4}}>
                            Reported <b>{bellLabel}</b> — the dashed line marks when the news first traded; D0 is the reaction session.
                          </div>
                        )}
                        {wi < ewWindows.length - 1 && (
                          <div style={{borderBottom:`1px solid ${C.border}`,marginTop:12}}/>
                        )}
                      </div>
                    );
                  })}

                  {ewWindows && ewWindows.some(w => w.postDrift1m != null || w.postDrift3m != null) && (
                    <div style={{fontSize:9,color:C.muted,opacity:0.75,marginTop:10,lineHeight:1.4}}>
                      "1m / 3m after" = price change from the reaction-session close to ~21 / ~63 trading days later, with the S&P over the same dates.
                      Past results for this stock — not a forecast.
                    </div>
                  )}

                  {hasUpcoming && (
                    <div style={{marginTop:12,padding:"8px 12px",background:"rgba(255,202,40,0.06)",
                      borderRadius:8,border:"1px solid rgba(255,202,40,0.2)",fontSize:11,color:C.yellow}}>
                      ⏰ Upcoming earnings in {data.earnings.daysUntil}d — this window will update live.
                    </div>
                  )}
                </Card>
              );
            })()}
          </div>
        )}

        {/* -- NEWS -- */}
        {tab==="news" && (
          <div style={{display:"flex",flexDirection:"column",gap:12}}>
            <Card>
              <SLabel>Sentiment score</SLabel>
              <div style={{textAlign:"center",padding:"16px 0"}}>
                <div style={{fontSize:48,fontWeight:800,fontFamily:"monospace",
                  color:sentiment.score>0.02?C.green:sentiment.score<-0.02?C.red:C.yellow}}>
                  {sentiment.score>=0?"+":""}{sentiment.score.toFixed(3)}
                </div>
                <div style={{fontSize:16,fontWeight:600,marginTop:4,
                  color:sentiment.score>0.02?C.green:sentiment.score<-0.02?C.red:C.yellow}}>
                  {sentiment.labelEmoji} {sentiment.label}
                </div>
                <div style={{fontSize:11,color:C.muted,marginTop:4}}>
                  {sentiment.count > 0
                    ? `Based on ${sentiment.count} headlines · Source: ${sentiment.source}`
                    : "No headlines found for this ticker"}
                </div>
              </div>
            </Card>
            <Card>
              <SLabel>Recent headlines</SLabel>
              {(!sentiment.headlines || sentiment.headlines.length === 0) ? (
                <div style={{textAlign:"center",padding:"16px 0"}}>
                  <div style={{fontSize:13,color:C.muted,lineHeight:1.7}}>
                    No recent headlines found.{"\n"}
                    Add a NewsAPI key in Railway environment variables for better coverage.
                  </div>
                  <div style={{fontSize:11,color:C.muted,marginTop:8,
                    background:C.surface,borderRadius:8,padding:"8px 12px",
                    fontFamily:"monospace"}}>
                    NEWSAPI_KEY = your_key_here
                  </div>
                  <div style={{fontSize:11,color:C.muted,marginTop:6}}>
                    Free key at newsapi.org · 100 requests/day
                  </div>
                </div>
              ) : [...sentiment.headlines].sort((a,b)=>(new Date(b.published_at||0))-(new Date(a.published_at||0))).map((h,i)=>{
                // Display sorted latest→oldest (agents keep the catalyst-tier
                // ranking server-side; this is display-only). Undated → bottom.
                // v8.4.89: format published_at as relative/short date label
                let dateLabel = "";
                if (h.published_at) {
                  try {
                    const d   = new Date(h.published_at);
                    const now = new Date();
                    const diffMs  = now - d;
                    const diffH   = diffMs / 3600000;
                    const diffD   = diffMs / 86400000;
                    if (diffH < 1)        dateLabel = "Just now";
                    else if (diffH < 24)  dateLabel = `${Math.floor(diffH)}h ago`;
                    else if (diffD < 2)   dateLabel = "Yesterday";
                    else if (diffD < 7)   dateLabel = `${Math.floor(diffD)}d ago`;
                    else dateLabel = d.toLocaleDateString("en-US", {month:"short", day:"numeric"});
                  } catch(_) {}
                }
                return (
                <div key={i} style={{display:"flex",gap:10,alignItems:"flex-start",
                  paddingBottom: i<sentiment.headlines.length-1?12:0,
                  marginBottom:  i<sentiment.headlines.length-1?12:0,
                  borderBottom:  i<sentiment.headlines.length-1?`1px solid ${C.divider}`:"none"}}>
                  <span style={{fontSize:14,marginTop:1}}>{h.emoji}</span>
                  <div style={{flex:1,minWidth:0}}>
                    {/* v8.4.435: the headline opens the original article in a new tab when the news API gave us a link */}
                    {h.url ? (
                      <a href={h.url} target="_blank" rel="noopener noreferrer"
                        style={{fontSize:12,color:C.text,lineHeight:1.6,textDecoration:"none",display:"block"}}>
                        {h.text}<span style={{color:C.muted,fontSize:10}}> ↗</span>
                      </a>
                    ) : (
                      <div style={{fontSize:12,color:C.text,lineHeight:1.6}}>{h.text}</div>
                    )}
                    {(dateLabel || h.source) && (
                      <div style={{fontSize:10,color:"rgba(255,255,255,0.28)",
                        marginTop:2,fontFamily:"monospace"}}>
                        {[h.source, dateLabel].filter(Boolean).join(" · ")}
                      </div>
                    )}
                    {h.summary && (
                      <details style={{marginTop:3}}>
                        <summary style={{fontSize:10,color:C.muted,cursor:"pointer",userSelect:"none"}}>Summary</summary>
                        <div style={{fontSize:11,color:C.muted,lineHeight:1.55,marginTop:3}}>
                          {h.summary}
                          {h.source && <span style={{opacity:0.6}}> — {h.source}</span>}
                        </div>
                      </details>
                    )}
                  </div>
                  <div style={{fontSize:11,fontFamily:"monospace",flexShrink:0,marginTop:2,
                    color:h.polarity>0.05?C.green:h.polarity<-0.05?C.red:C.yellow}}>
                    {h.polarity>=0?"+":""}{h.polarity.toFixed(2)}
                  </div>
                </div>
                );
              })}
            </Card>

            {/* Earnings section */}
            <Card style={{borderColor: data.earnings?.isSoon ? "rgba(255,202,40,0.4)" : C.border}}>
              <SLabel><span style={{display:"inline-flex",alignItems:"center",gap:5}}><EarningsGlyph size={13} color={C.muted}/> Earnings</span></SLabel>

              {/* v8.4.320: recent-earnings pill above the next date — same message as the
                  Trade Setup "Earnings reaction" pill, for consistency. */}
              {data.recentEarnings && (
                <div style={{display:"inline-flex",alignItems:"center",gap:5,marginBottom:10,
                  borderRadius:20,padding:"3px 10px",background:"rgba(255,202,40,0.14)",
                  border:"1px solid rgba(255,202,40,0.45)"}}>
                  <EarningsGlyph size={12} color={C.yellow}/>
                  <span style={{fontSize:11,fontWeight:700,color:C.yellow}}>
                    Reported {fmtEarnDate(data.recentEarnings.date)}{data.recentEarnings.hour ? ` (${hourLabel(data.recentEarnings.hour)})` : ""}
                    {data.recentEarnings.outcome ? ` · ${String(data.recentEarnings.outcome).toUpperCase()}` : ""}
                  </span>
                </div>
              )}

              {/* Earnings soon warning */}
              {data.earnings?.isSoon && (
                <div style={{display:"flex",alignItems:"center",gap:10,
                  background:"rgba(255,202,40,0.08)",borderRadius:10,
                  padding:"10px 14px",border:"1px solid rgba(255,202,40,0.3)",
                  marginBottom:12}}>
                  <span style={{fontSize:20}}>⚠️</span>
                  <div style={{fontSize:12,color:C.yellow,lineHeight:1.6}}>
                    <strong>Earnings in {data.earnings.daysUntil} day{data.earnings.daysUntil!==1?"s":""}!</strong>
                    {" "}Price can move sharply. Consider waiting until after the report.
                  </div>
                </div>
              )}

              {/* Next earnings date row — shown when available */}
              {data.earnings?.nextDate ? (
                <div style={{display:"flex",alignItems:"center",
                  background:C.surface,borderRadius:10,padding:"10px 14px",
                  border:`1px solid ${C.border}`,marginBottom:12}}>
                  <div style={{flex:1}}>
                    <div style={{fontSize:11,color:C.muted,marginBottom:2}}>
                      Next earnings{data.earnings.nextDateEstimated ? " (estimated)" : ""}
                    </div>
                    <div style={{fontSize:15,fontWeight:700,color:C.text}}>
                      {data.earnings.nextDateEstimated ? "~ " : ""}{data.earnings.nextDate}
                    </div>
                    {data.earnings.nextDateEstimated && (
                      <div style={{fontSize:9,color:C.muted,opacity:0.75,marginTop:2}}>
                        Projected from SEC filing cadence — not yet confirmed
                      </div>
                    )}
                  </div>
                  <div style={{textAlign:"right"}}>
                    <div style={{fontSize:20,fontWeight:800,fontFamily:"monospace",
                      color:data.earnings.isSoon?C.yellow:C.muted}}>
                      {data.earnings.daysUntil === 0 ? "Today"
                       : data.earnings.daysUntil === 1 ? "Tomorrow"
                       : `${data.earnings.daysUntil}d`}
                    </div>
                    <div style={{fontSize:10,color:C.muted}}>away</div>
                  </div>
                </div>
              ) : (
                <div style={{fontSize:11,color:C.muted,marginBottom:12,
                  padding:"6px 0",borderBottom:`1px solid ${C.border}`}}>
                  Next earnings date not available
                </div>
              )}

              {/* Beat streak */}
              {data.earnings?.beatStreak > 0 && (
                <div style={{fontSize:12,color:C.green,marginBottom:12,
                  background:"rgba(0,230,118,0.06)",borderRadius:8,
                  padding:"8px 12px",border:"1px solid rgba(0,230,118,0.15)"}}>
                  🔥 Beat EPS estimates {data.earnings.beatStreak} quarter{data.earnings.beatStreak!==1?"s":""} in a row
                </div>
              )}

              {/* Quarterly EPS & Revenue chart — shown whenever we have data */}
              {data.earnings?.quarters?.length > 0 ? (
                <EarningsChart quarters={data.earnings.quarters}/>
              ) : (
                <div style={{padding:"12px 0",textAlign:"center"}}>
                  <div style={{fontSize:24,marginBottom:6}}>📊</div>
                  <div style={{fontSize:12,color:C.muted,lineHeight:1.6}}>
                    Quarterly earnings data not available for this ticker.
                  </div>
                  <div style={{fontSize:10,color:"rgba(255,255,255,0.2)",marginTop:4}}>
                    May be unavailable for ETFs, new listings, or international stocks.
                  </div>
                </div>
              )}
            </Card>

            {/* Fed Calendar */}
            {data.fed && (
              <Card style={{borderColor: data.fed.isMeetingSoon ? "rgba(0,212,255,0.3)" : C.border}}>
                <SLabel>🏦 Federal Reserve</SLabel>

                {/* Rate display */}
                <div style={{display:"flex",alignItems:"center",gap:12,
                  background:C.surface,borderRadius:10,padding:"12px 14px",
                  border:`1px solid ${C.border}`,marginBottom:10}}>
                  <div style={{flex:1}}>
                    <div style={{fontSize:11,color:C.muted,marginBottom:3}}>
                      Fed Funds Rate
                    </div>
                    <div style={{fontSize:22,fontWeight:900,fontFamily:"monospace",
                      color: data.fed.fedRate >= 5 ? C.red :
                             data.fed.fedRate >= 3 ? C.yellow : C.green}}>
                      {data.fed.fedRate !== null ? `${data.fed.fedRate}%` : "N/A"}
                    </div>
                    <div style={{fontSize:11,color:C.muted,marginTop:2}}>
                      {data.fed.rateLabel}
                      {data.fed.rateTrend !== "unknown" && (
                        <span style={{marginLeft:6,
                          color: data.fed.rateTrend==="rising"?C.red:
                                 data.fed.rateTrend==="falling"?C.green:C.muted}}>
                          {data.fed.rateTrend==="rising"?"↑ Rising":
                           data.fed.rateTrend==="falling"?"↓ Falling":"→ Stable"}
                        </span>
                      )}
                    </div>
                  </div>
                  {/* Next meeting */}
                  <div style={{textAlign:"right",flexShrink:0}}>
                    <div style={{fontSize:10,color:C.muted,marginBottom:3}}>Next FOMC</div>
                    <div style={{fontSize:13,fontWeight:700,
                      color:data.fed.isMeetingSoon?C.accent:C.text}}>
                      {data.fed.nextMeeting || "N/A"}
                    </div>
                    {data.fed.daysToMeeting !== null && (
                      <div style={{fontSize:11,
                        color:data.fed.isMeetingSoon?C.yellow:C.muted,marginTop:2}}>
                        {data.fed.daysToMeeting === 0 ? "Today!" :
                         data.fed.daysToMeeting === 1 ? "Tomorrow" :
                         `${data.fed.daysToMeeting}d away`}
                      </div>
                    )}
                  </div>
                </div>

                {/* Meeting soon warning */}
                {data.fed.isMeetingSoon && (
                  <div style={{display:"flex",gap:10,alignItems:"flex-start",
                    background:"rgba(0,212,255,0.06)",borderRadius:10,
                    padding:"10px 14px",border:"1px solid rgba(0,212,255,0.2)",
                    marginBottom:10}}>
                    <span style={{fontSize:18,flexShrink:0}}>📢</span>
                    <div style={{fontSize:12,color:C.accent,lineHeight:1.6}}>
                      <strong>FOMC meeting in {data.fed.daysToMeeting} day{data.fed.daysToMeeting!==1?"s":""}.</strong>
                      {" "}Rate decisions can cause sharp market moves. Consider reducing position size until after the announcement.
                    </div>
                  </div>
                )}

                {/* Market impact analysis */}
                <div style={{fontSize:12,color:C.muted,lineHeight:1.75,
                  marginBottom:10}}>
                  {data.fed.marketImpact}
                </div>

                {/* Swing note */}
                {data.fed.swingNote && (
                  <div style={{fontSize:12,color:C.yellow,lineHeight:1.6,
                    background:"rgba(255,202,40,0.06)",borderRadius:8,
                    padding:"8px 12px",border:"1px solid rgba(255,202,40,0.15)"}}>
                    📈 {data.fed.swingNote}
                  </div>
                )}

                {/* Upcoming meetings */}
                {data.fed.nextMeetings?.length > 1 && (
                  <div style={{marginTop:12}}>
                    <div style={{fontSize:10,fontWeight:700,color:C.muted,
                      letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:8}}>
                      Upcoming FOMC meetings
                    </div>
                    {data.fed.nextMeetings.map((m,i)=>(
                      <div key={i} style={{display:"flex",justifyContent:"space-between",
                        alignItems:"center",padding:"7px 12px",
                        background:C.surface,borderRadius:8,marginBottom:5,
                        border:`1px solid ${C.border}`}}>
                        <div style={{fontSize:12,color:C.text}}>{m.date}</div>
                        <div style={{fontSize:11,fontFamily:"monospace",
                          color:m.daysAway<=14?C.accent:C.muted}}>
                          {m.daysAway === 0 ? "Today" :
                           m.daysAway === 1 ? "Tomorrow" :
                           `${m.daysAway}d`}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div style={{fontSize:10,color:C.muted,marginTop:10,lineHeight:1.5}}>
                  ⚠️ Rate analysis is educational only. Past Fed decisions do not predict future policy.
                </div>
              </Card>
            )}
          </div>
        )}

        {/* -- ALERTS -- */}
        {tab==="alerts" && (
          <div style={{display:"flex",flexDirection:"column",gap:12}}>
            {/* Create alert */}
            <Card>
              <SLabel>Set a new alert for {ticker}</SLabel>
              <div style={{display:"flex",flexDirection:"column",gap:10}}>
                {/* Alert type */}
                <div style={{display:"flex",flexDirection:"column",gap:6}}>
                  {[
                    {key:"price_above", label:"📈 Price rises above",   unit:"$"},
                    {key:"price_below", label:"📉 Price falls below",   unit:"$"},
                    {key:"stop_loss",   label:"🛑 Stop loss triggered", unit:"$"},
                    {key:"swing_buy",   label:"⚡ Swing score hits",    unit:"pts"},
                  ].map(t=>(
                    <button key={t.key} onClick={()=>setAlertType(t.key)} style={{
                      display:"flex",alignItems:"center",justifyContent:"space-between",
                      padding:"10px 14px",borderRadius:10,border:"none",cursor:"pointer",
                      fontFamily:"inherit",fontSize:13,textAlign:"left",
                      background:alertType===t.key?"rgba(0,212,255,0.12)":C.surface,
                      color:alertType===t.key?C.accent:C.muted,
                      borderLeft:`3px solid ${alertType===t.key?C.accent:"transparent"}`,
                    }}>
                      <span>{t.label}</span>
                      {alertType===t.key&&<span style={{fontSize:11,fontFamily:"monospace"}}>
                        {t.unit}___
                      </span>}
                    </button>
                  ))}
                </div>

                {/* Target value */}
                <div style={{display:"flex",gap:8,alignItems:"center"}}>
                  <div style={{fontSize:13,color:C.muted,flexShrink:0}}>
                    {alertType==="swing_buy" ? "Score ≥" : "Price"}
                  </div>
                  <input
                    style={{flex:1,background:C.surface,border:`1px solid ${C.border}`,
                      borderRadius:8,padding:"10px 14px",color:C.text,
                      fontSize:15,fontFamily:"monospace",outline:"none"}}
                    type="number"
                    placeholder={alertType==="swing_buy" ? "e.g. 70" : `e.g. ${data?.price?.toFixed(0)||"200"}`}
                    value={alertTarget}
                    onChange={e=>setAlertTarget(e.target.value)}
                  />
                </div>

                {/* Hint */}
                <div style={{fontSize:11,color:C.muted,lineHeight:1.6}}>
                  {alertType==="price_above" && `Alert when ${ticker} rises above your target price.`}
                  {alertType==="price_below" && `Alert when ${ticker} falls below your target price.`}
                  {alertType==="stop_loss"   && `Alert when ${ticker} hits your stop loss level.`}
                  {alertType==="swing_buy"   && `Alert when ${ticker}'s swing score reaches your target (0-100). BUY threshold is typically 60+.`}
                </div>

                {alertMsg && (
                  <div style={{fontSize:12,color:alertMsg.startsWith("✅")?C.green:C.red,
                    lineHeight:1.6}}>{alertMsg}</div>
                )}

                <button onClick={(e)=>{Haptics.success(e.currentTarget);createAlert();}} disabled={alertSaving} style={{
                  padding:"12px",borderRadius:10,border:"none",cursor:"pointer",
                  background:C.accent,color:C.bg,fontSize:14,fontWeight:700,
                  fontFamily:"inherit",opacity:alertSaving?0.7:1,
                }}>
                  {alertSaving?"Creating…":"◉ Create Alert"}
                </button>
              </div>
            </Card>

            {/* Existing alerts for this ticker */}
            {alerts.length > 0 && (
              <Card>
                <SLabel>Alerts for {ticker}</SLabel>
                {alerts.map(a=>(
                  <div key={a.id} style={{display:"flex",alignItems:"center",gap:8,
                    background:C.surface,borderRadius:10,padding:"10px 14px",
                    border:`1px solid ${a.is_active?C.border:"rgba(255,202,40,0.2)"}`,
                    marginBottom:8,opacity:a.is_active?1:0.75}}>
                    <div style={{flex:1}}>
                      <div style={{fontSize:12,fontWeight:600,color:C.text}}>
                        {a.alert_type==="price_above"&&"📈 Price above "}
                        {a.alert_type==="price_below"&&"📉 Price below "}
                        {a.alert_type==="stop_loss"  &&"🛑 Stop loss at "}
                        {a.alert_type==="swing_buy"  &&"⚡ Score hits "}
                        <span style={{fontFamily:"monospace",color:C.accent}}>
                          {a.alert_type==="swing_buy"?"":"$"}{a.target_value}
                          {a.alert_type==="swing_buy"?" pts":""}
                        </span>
                      </div>
                      <div style={{fontSize:10,marginTop:2,
                        color:a.is_active?C.green:C.yellow}}>
                        {a.is_active?"● Active":"● Triggered"}
                        {a.triggered_at&&` · ${new Date(a.triggered_at).toLocaleDateString()}`}
                      </div>
                    </div>
                    {!a.is_active && (
                      <button onClick={()=>reactivateAlert(a.id)} style={{
                        background:"rgba(0,212,255,0.08)",border:`1px solid ${C.accent}`,
                        borderRadius:8,padding:"5px 9px",cursor:"pointer",
                        color:C.accent,fontSize:11,fontFamily:"inherit",fontWeight:600,
                      }}>↺ Reset</button>
                    )}
                    <button onClick={()=>deleteAlert(a.id)} style={{
                      background:C.redBg,border:"none",borderRadius:8,
                      padding:"6px 10px",cursor:"pointer",color:C.red,
                      fontSize:12,fontFamily:"inherit",fontWeight:600,
                    }}>Delete</button>
                  </div>
                ))}
              </Card>
            )}

            <Card style={{borderColor:"rgba(255,202,40,0.2)"}}>
              <div style={{fontSize:12,color:C.yellow,lineHeight:1.7}}>
                ◉ Alerts are checked every 30 minutes on weekdays during market hours.
                You'll receive an email at <strong style={{color:C.text}}>{user.email}</strong> when triggered.
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}

// ── SwipeableWatchlistCard ────────────────────────────────────────────────────
// Left-swipe on a watchlist row reveals a red Delete panel. Commit at 40% card
// width OR swipe velocity ≥0.5 px/ms (fast flick). Spring-back otherwise.
// No right-swipe action — tapping the card already opens stock detail.
function SwipeableWatchlistCard({ onDelete, children }) {
  const containerRef   = React.useRef(null);
  const startXRef      = React.useRef(null);
  const startTimeRef   = React.useRef(null);
  const dragOffsetRef  = React.useRef(0);
  const [renderOffset, setRenderOffset] = React.useState(0);
  const [animating,    setAnimating]    = React.useState(false);

  const handleTouchStart = (e) => {
    startXRef.current    = e.touches[0].clientX;
    startTimeRef.current = Date.now();
    dragOffsetRef.current = 0;
    setAnimating(false);
  };

  const handleTouchMove = (e) => {
    if (startXRef.current === null) return;
    const dx = e.touches[0].clientX - startXRef.current;
    if (dx >= 0) return;
    const w = containerRef.current?.offsetWidth ?? 400;
    dragOffsetRef.current = Math.max(dx, -w);
    setRenderOffset(dragOffsetRef.current);
  };

  const handleTouchEnd = () => {
    if (startXRef.current === null) return;
    const w        = containerRef.current?.offsetWidth ?? 300;
    const dx       = dragOffsetRef.current;
    const dt       = Date.now() - (startTimeRef.current ?? Date.now());
    const velocity = Math.abs(dx) / Math.max(dt, 1);
    const commit   = Math.abs(dx) >= w * 0.4 || velocity >= 0.5;
    startXRef.current = null;
    setAnimating(true);
    if (commit) {
      Haptics.heavy();
      setRenderOffset(-w);
      setTimeout(onDelete, 260);
    } else {
      setRenderOffset(0);
    }
  };

  const transition = animating
    ? (renderOffset < -10
        ? "transform 0.24s ease-in"
        : "transform 0.32s cubic-bezier(0.34,1.56,0.64,1)")
    : "none";

  return (
    <div ref={containerRef}
      style={{position:"relative",overflow:"hidden",borderRadius:16}}>
      {/* Full-width red delete panel — only render while card is being swiped left */}
      {renderOffset < 0 && (
        <div aria-hidden style={{
          position:"absolute",inset:0,
          background:"#e53935",
          display:"flex",alignItems:"center",justifyContent:"flex-end",
          paddingRight:22,borderRadius:16,
          userSelect:"none",pointerEvents:"none",
        }}>
          <span style={{color:"#fff",fontSize:12,fontWeight:700,letterSpacing:"0.06em"}}>
            DELETE
          </span>
        </div>
      )}
      {/* Slideable card */}
      <div
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{
          transform:   `translateX(${renderOffset}px)`,
          transition,
          willChange:  "transform",
          position:    "relative",
          touchAction: "pan-y",
        }}
      >
        {children}
      </div>
    </div>
  );
}

// --- Scanner / home screen ----------------------------------------------------
function ScannerScreen({ user, token, onSelectTicker, onProfileChange,
  scanData, setScanData, scanLoading, setScanLoading, scanError, setScanError, setMarketData,
  runScanRef, onScanComplete, universeData, upsertUniverse,
  watchlist, setWatchlist, onAuthError, globalHorizon, tradePref, currency, fxRates, earningsSoon, visible=true }) {
  const horizon = globalHorizon || "6m";  // driven by Landing screen global
  const activistMap = useActivistFilings(watchlist, token);   // v8.4.418 — 13D chip on watchlist rows
  const insiderMap  = useInsiderClusters(watchlist, token);   // v8.4.420 — insider buy-cluster chip
  const [filter,       setFilter]     = useState("all");
  const [showManage,   setShowManage] = useState(false);
  const [showHorizonInfo,  setShowHorizonInfo]  = useState(false);
  const [showProfileInfo,  setShowProfileInfo]  = useState(false);
  const [newTicker,    setNewTicker]  = useState("");
  const [wlSaving,     setWlSaving]   = useState(false);
  const [wlMsg,        setWlMsg]      = useState("");
  // v8.4.35: per-ticker sparkline mode. Default unset → render intraday when
  // available, else fall back to 14-day daily. Tap any sparkline to flip
  // between intraday (today's 15-min closes) and 3W (14 daily closes).
  const [sparklineMode, setSparklineMode] = useState({});
  const toggleSparklineMode = (t) => setSparklineMode(p => {
    const cur = p[t] || "intraday";
    return { ...p, [t]: cur === "intraday" ? "daily" : "intraday" };
  });

  const [bgRefreshing, setBgRefreshing] = useState(false);
  const [bgUpdating,   setBgUpdating]   = useState({}); // ticker -> bool skeleton

  // ── Recently-deleted ticker recovery (v8.4.28) ───────────────────────────
  // Persisted in localStorage per-user. Each entry: { ticker, deletedAt (ms) }.
  // Entries older than 7 days are silently pruned on load and after each change.
  const _RD_TTL   = 7 * 24 * 60 * 60 * 1000; // 7 days in ms
  const _rdKey    = `recentlyDeleted_${user?.id || "guest"}`;
  const _loadRd   = () => {
    try {
      const raw = localStorage.getItem(_rdKey);
      if (!raw) return [];
      const now = Date.now();
      return JSON.parse(raw).filter(e => (now - e.deletedAt) < _RD_TTL);
    } catch { return []; }
  };
  const [recentlyDeleted, setRecentlyDeleted] = useState(_loadRd);
  // Persist + prune whenever the list changes
  const _saveRd = (list) => {
    const now     = Date.now();
    const pruned  = list.filter(e => (now - e.deletedAt) < _RD_TTL);
    setRecentlyDeleted(pruned);
    try { localStorage.setItem(_rdKey, JSON.stringify(pruned)); } catch {}
  };

  // Always-current mirror of the universeData prop — used inside runScan (a
  // useCallback) so it never reads a stale closure snapshot of universeData.
  // Without this, runScan captures universeData at [token, risk_profile] creation
  // time and never sees subsequent universe refreshes, causing scoredAt to look
  // stale → cacheUsable = false → /analyze called for every ticker even when
  // the universe cache is warm. On any API hiccup that would yield 0 results.
  const universeDataRef = React.useRef(universeData);
  useEffect(() => { universeDataRef.current = universeData; }, [universeData]);

  // Fetch regime (VIX + SPY trend) immediately on mount so pills show before scan completes
  useEffect(() => {
    apiFetch("/regime", token)
      .then(regime => {
        if (regime?.vix > 0) {
          setScanData(prev => prev
            ? {...prev, regime}
            : {results:[], count:0, regime, profile:{key:user.risk_profile}});
        }
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Load watchlist on mount, then silently refresh scores in background
  useEffect(() => {
    apiFetch("/watchlist", token)
      .then(async d => {
        const tickers = d.tickers || [];
        setWatchlist(tickers);
        if (!tickers.length) return;

        // Seed scanData immediately from universeData so user sees instant results
        // before the full /analyze scan completes (~60s)
        //
        // v8.4.36: track seeding with a local flag instead of re-reading
        // scanData after setScanData. The next two conditionals were reading
        // scanData from a STALE closure (the value at useEffect-entry, before
        // setScanData had applied) — so even when seed populated, the code
        // saw scanData empty and fell through to runScan(). runScan then
        // wholesale-replaced scanData with results that included _stubRow
        // null-score entries for any ticker whose /analyze timed out, wiping
        // the seeded valid scores → donuts briefly populated, then went grey.
        let didSeed = false;
        if (!scanData?.results?.length && universeData) {
          const seeded = tickers.map((t, i) => {
            const u = universeData[t.toUpperCase()] || {};
            if (!u.swingScore && !u.price) return null; // skip if no data
            return {
              ticker: t.toUpperCase(), shortName: u.shortName || t,
              price: u.price || 0, changePct: u.changePct || 0,
              changeAmt: u.changeAmt || 0, isLive: u.isLive || false,
              swingScore: u.swingScore ?? null, swingLabel: u.swingLabel || "",
              swingType: u.swingType || "watch",
              ltScore: u.ltScore ?? null, ltLabel: u.ltLabel || "", ltType: u.ltType || "watch",
              rsi: u.rsi || 0, beta: u.beta || 1.0,
              sentiment: u.sentiment || "", sentimentEmoji: u.sentimentEmoji || "",
              horizonReturn: u.horizonReturn || 0, sector: u.sector || "",
              sparkline: u.sparkline || [],
              sparklineIntraday: u.sparklineIntraday || [],
              patterns: "", addedOrder: i, _fromCache: true,
            };
          }).filter(Boolean);
          if (seeded.length > 0) {
            setScanData(prev => {
              if (prev?.results?.length) return prev; // already have real results
              // v8.4.215: immediately sync scores against current universe cache.
              // scoreUniverseBackground may have already loaded fresh Redis scores
              // into universeDataRef before this seed runs — override stale
              // localStorage scores with whatever is fresher in the ref.
              const synced = seeded.map(r => {
                const u = universeDataRef.current?.[r.ticker];
                if (!u || !(u.scoredAt > 0)) return r;
                return {
                  ...r,
                  swingScore: (u.swingScore != null && u.swingScore > 0) ? u.swingScore : r.swingScore,
                  swingLabel: u.swingLabel || r.swingLabel,
                  swingType:  u.swingType  || r.swingType,
                  ltScore:    (u.ltScore   != null && u.ltScore   > 0) ? u.ltScore   : r.ltScore,
                  ltLabel:    u.ltLabel    || r.ltLabel,
                  ltType:     u.ltType     || r.ltType,
                  _uTs: u.scoredAt,
                };
              });
              return {results: synced, count: synced.length,
                      regime: prev?.regime || {spyTrend:"unknown",vix:0,fearLabel:"N/A"},
                      profile: {key:user.risk_profile}};
            });
            didSeed = true;
          }
        }

        const _haveRows = didSeed || (scanData?.results?.length > 0);

        // If we already have cached results, show them immediately
        // then silently refresh in background one by one
        if (_haveRows && isMarketOpen()) {
          // Market open + cached results — silently refresh each ticker
          setBgRefreshing(true);
          const h = horizonRef.current || "6m";
          for (const t of tickers) {
            setBgUpdating(p => ({...p, [t]: true}));
            try {
              // Check universeData first — if scored <35min ago reuse it.
              // Aligns with Celery beat market-hours-refresh (every 30 min) + 5 min grace
              // so we don't trigger a `/analyze` rescore on every Watchlist open.
              const STALE_MS = 35 * 60 * 1000;
              const cached_u = universeData?.[t];
              // Only reuse cache when scores are VALID (non-null AND > 0).
              // score = 0 is written by upsertUniverse's || 0 fallback when
              // /analyze returns null scores (scoring glitch). ScoreDonut treats
              // 0 identically to null (shows "—"), so treat 0 as a cache miss
              // and fall through to a fresh /analyze call.
              // v8.4.41: revert v8.4.38's intraday requirement — it forced an
              // /analyze call for EVERY watchlist ticker on every visit
              // (universe cache never has intraday from Celery), bursting the
              // sequential loop to ~3s × ticker_count and leaving rows
              // empty until /analyze finished. Now cache hits are fast again;
              // tickers without intraday hydrate in the BACKGROUND pass below
              // (parallel, fire-and-forget). User sees rows with 3W tab
              // immediately, and 1D lights up over the next few seconds.
              const bgCacheOk = cached_u
                && cached_u.swingScore != null && cached_u.swingScore > 0
                && cached_u.ltScore    != null && cached_u.ltScore    > 0
                && cached_u.price      != null
                && (Date.now() - (cached_u.scoredAt||0)) < STALE_MS;
              let d2_raw = null;
              if (bgCacheOk) {
                // Reuse universe cache — synthesise the shape runScan expects
                d2_raw = cached_u;
              } else {
                d2_raw = await apiFetch(`/analyze/${t}?horizon=${h}`, token);
                // Also upsert into universeData so other tabs benefit
                if (upsertUniverse && d2_raw) {
                  const _sec2 = d2_raw.fundamentals?.sector || SECTOR_HINTS[t] || "";
                  // v8.4.38: also persist BOTH sparkline series to
                  // universeData so the NEXT cache-hit visit has intraday
                  // data and the 1D · 3W toggle works without a re-fetch.
                  const _bgSparkDaily = (Array.isArray(d2_raw.sparkline) && d2_raw.sparkline.length >= 2)
                    ? d2_raw.sparkline
                    : (d2_raw.candles || []).slice(-14)
                        .map(c => typeof c === "object" ? c.c : c)
                        .filter(v => typeof v === "number" && !isNaN(v));
                  const _bgSparkIntraday = extractIntradaySparkline(d2_raw.candles_intraday_15m, d2_raw.candles_intraday);
                  upsertUniverse(t, {
                    ticker:t, shortName:d2_raw.shortName||t,
                    sector:_sec2,
                    price:d2_raw.price||0, changePct:d2_raw.changePct||0,
                    isLive:d2_raw.isLive||false,
                    // Use ?? null (not || 0) so a failed/null score doesn't get
                    // cached as 0 and permanently block future re-fetches.
                    // bgCacheOk requires score > 0 so a null score triggers retry.
                    swingScore:d2_raw.swingScore?.score ?? null,
                    swingLabel:d2_raw.swingScore?.label||"",
                    swingType:d2_raw.swingScore?.labelType||"watch",
                    ltScore:d2_raw.longTermScore?.score ?? null,
                    ltLabel:d2_raw.longTermScore?.label||"",
                    ltType:d2_raw.longTermScore?.labelType||"watch",
                    horizonReturn:d2_raw.longTermScore?.horizonReturn??null,
                    beta:d2_raw.beta||1.0, rsi:d2_raw.rsi||50,
                    sentiment:d2_raw.sentiment?.label||"",
                    sparkline:_bgSparkDaily,
                    sparklineIntraday:_bgSparkIntraday,
                    scoredAt:Date.now(),
                  });
                }
              }
              // Normalise to the shape scanData.results expects
              const isFromCache = bgCacheOk;
              const d2 = isFromCache ? null : d2_raw;
              const updated = {
                ticker:t,
                shortName:   isFromCache ? cached_u.shortName  : d2?.shortName,
                price:       isFromCache ? cached_u.price       : d2?.price,
                changePct:   isFromCache ? cached_u.changePct   : d2?.changePct,
                changeAmt:   isFromCache ? 0                    : (d2?.changeAmt||0),
                isLive:      isFromCache ? cached_u.isLive      : d2?.isLive,
                swingScore:  isFromCache ? cached_u.swingScore  : d2?.swingScore?.score,
                swingLabel:  isFromCache ? cached_u.swingLabel  : d2?.swingScore?.label,
                swingType:   isFromCache ? cached_u.swingType   : d2?.swingScore?.labelType,
                ltScore:     isFromCache ? cached_u.ltScore     : d2?.longTermScore?.score,
                ltLabel:     isFromCache ? cached_u.ltLabel     : d2?.longTermScore?.label,
                ltType:      isFromCache ? cached_u.ltType      : d2?.longTermScore?.labelType,
                trendDir:    isFromCache ? null : d2?.swingScore?.trendDir,
                rsi:         isFromCache ? cached_u.rsi         : d2?.rsi,
                beta:        isFromCache ? cached_u.beta        : d2?.beta,
                sentiment:   isFromCache ? cached_u.sentiment   : d2?.sentiment?.label,
                sentimentEmoji: isFromCache ? null : d2?.sentiment?.labelEmoji,
                horizonReturn: isFromCache ? cached_u.horizonReturn : d2?.longTermScore?.horizonReturn,
                entry:       isFromCache ? null : d2?.swingScore?.entry,
                stop:        isFromCache ? null : d2?.swingScore?.stop,
                target1:     isFromCache ? null : d2?.swingScore?.target1,
                patterns:    isFromCache ? "" : `▲${(d2?.patterns||[]).filter(p=>p.type==="bullish").length} ▼${(d2?.patterns||[]).filter(p=>p.type==="bearish").length}`,
                sector:      isFromCache ? cached_u.sector : (d2?.fundamentals?.sector||""),
                // v8.4.38: `sparkline` = 14 daily closes (3W tab), `sparklineIntraday`
                // = today's 15-min closes (1D tab). Renderer picks one based on
                // sparklineMode. Prefer d2.sparkline (backend writes it for /analyze
                // since v8.4.29) over slicing d2.candles — the stale-cache fallback
                // path on /analyze omits candles but always emits sparkline.
                sparkline:   isFromCache
                  ? (cached_u.sparkline || [])
                  : (() => {
                      if (Array.isArray(d2?.sparkline) && d2.sparkline.length >= 2) return d2.sparkline;
                      const src = d2?.candles || [];
                      return src.slice(-14).map(c => typeof c === "object" ? c.c : c).filter(v => typeof v === "number" && !isNaN(v));
                    })(),
                sparklineIntraday: isFromCache
                  ? (cached_u.sparklineIntraday || [])
                  : extractIntradaySparkline(d2?.candles_intraday_15m, d2?.candles_intraday),
                addedOrder:  tickers.indexOf(t),
              };
              setScanData(prev => {
                if (!prev) return prev;
                const exists = prev.results.find(r=>r.ticker===t);
                const newResults = exists
                  ? prev.results.map(r=>r.ticker===t?updated:r)
                  : [...prev.results, updated];
                return {...prev, results:newResults, count:newResults.length};
              });
            } catch(e) { /* skip on error */ }
            setBgUpdating(p => ({...p, [t]: false}));
          }
          scanTimeRef.current = Date.now();
          setBgRefreshing(false);

          // v8.4.215: post-loop score sync. The v8.4.39 effect fires when
          // universeData changes, but if scoreUniverseBackground completed and
          // called setUniverseData BEFORE scanData rows were populated (bgCacheOk
          // loop hadn't run yet), the sync saw empty results and nooped. scanData
          // is now populated but stuck on stale localStorage scores. One explicit
          // sync pass against universeDataRef (always current) fixes it.
          setScanData(prev => {
            if (!prev?.results?.length) return prev;
            let _synced = false;
            const _synced_results = prev.results.map(r => {
              const u = universeDataRef.current?.[r.ticker];
              if (!u) return r;
              const uTs = u.scoredAt || 0;
              const rTs = r._uTs || 0;
              if (uTs <= rTs) return r;
              _synced = true;
              return {
                ...r,
                swingScore: (u.swingScore != null && u.swingScore > 0) ? u.swingScore : r.swingScore,
                swingLabel: u.swingLabel || r.swingLabel,
                swingType:  u.swingType  || r.swingType,
                ltScore:    (u.ltScore   != null && u.ltScore   > 0) ? u.ltScore   : r.ltScore,
                ltLabel:    u.ltLabel    || r.ltLabel,
                ltType:     u.ltType     || r.ltType,
                price:      u.price      || r.price,
                changePct:  u.changePct  ?? r.changePct,
                isLive:     u.isLive     ?? r.isLive,
                sparklineIntraday:
                  (Array.isArray(u.sparklineIntraday) && u.sparklineIntraday.length >= 2)
                    ? u.sparklineIntraday : r.sparklineIntraday,
                _uTs: uTs,
              };
            });
            return _synced ? {...prev, results: _synced_results} : prev;
          });

          // v8.4.216: live-price patch runs AFTER the bgCacheOk loop settles.
          // The v8.4.214 approach (fire-and-forget BEFORE the loop) was a race:
          // bgCacheOk overwrote each row with cached_u.price (stale, up to 30 min)
          // after the live-price patch had already landed. Moving it here guarantees
          // no subsequent setScanData can clobber the fresh price/changePct/isLive.
          if (tickers.length) {
            try {
              const _lpData = await apiFetch(
                `/live-prices?tickers=${tickers.join(",")}`, token);
              if (_lpData?.prices) {
                setScanData(prev => {
                  if (!prev?.results?.length) return prev;
                  let _lp_changed = false;
                  const _lp_results = prev.results.map(r => {
                    const lp = _lpData.prices[r.ticker];
                    if (!lp || lp.price == null) return r;
                    _lp_changed = true;
                    return {
                      ...r,
                      price:     lp.price,
                      changePct: lp.changePct ?? r.changePct,
                      changeAmt: lp.prevClose != null
                        ? lp.price - lp.prevClose
                        : r.changeAmt,
                      isLive: lp.isLive ?? r.isLive,
                    };
                  });
                  return _lp_changed ? {...prev, results: _lp_results} : prev;
                });
              }
            } catch (_) {}
          }

          // v8.4.41: background intraday hydration. After the main score-
          // refresh loop completes, sweep tickers whose universe entry has
          // a valid daily sparkline but no intraday series, and fire
          // /analyze in parallel (concurrency 4) just to populate intraday.
          // upsertUniverse → the sync effect → scanData row updates → the
          // "1D" tab lights up for each ticker as its hydration lands.
          // Fire-and-forget — no UI blocking.
          (async () => {
            const ud = universeDataRef.current || {};
            const needsIntra = tickers.filter(t => {
              const u = ud[t.toUpperCase()];
              if (!u) return false;
              return !(Array.isArray(u.sparklineIntraday) && u.sparklineIntraday.length >= 2);
            });
            if (!needsIntra.length) return;
            const CONCURRENCY = 4;
            let cursor = 0;
            const worker = async () => {
              while (cursor < needsIntra.length) {
                const my = cursor++;
                const t = needsIntra[my];
                try {
                  const dd = await apiFetch(`/analyze/${t}?horizon=${h}`, token);
                  if (!dd || !upsertUniverse) continue;
                  const _intra = extractIntradaySparkline(dd.candles_intraday_15m, dd.candles_intraday);
                  const _daily = (Array.isArray(dd.sparkline) && dd.sparkline.length >= 2)
                    ? dd.sparkline
                    : (Array.isArray(dd.candles) ? dd.candles.slice(-14)
                        .map(c => typeof c === "object" ? c.c : c)
                        .filter(v => typeof v === "number" && !isNaN(v)) : []);
                  upsertUniverse(t, {
                    ticker: t,
                    sparkline: _daily,
                    sparklineIntraday: _intra,
                    scoredAt: Date.now(),
                  });
                } catch (e) { /* skip silent */ }
              }
            };
            await Promise.all(Array.from({length: CONCURRENCY}, worker));
          })();
        } else if (!_haveRows) {
          // No seed succeeded (universe cache cold/sparse) — fall back to
          // runScan regardless of market session. runScan's cache-first
          // logic per-ticker (universeData → /analyze on miss) keeps this
          // cheap when the cache IS warm and only hits /analyze for the
          // missing tickers. Previously closed-market would silently leave
          // the watchlist empty until manual refresh; this guarantees the
          // list always populates.
          // v8.4.36: gated on _haveRows (local flag) instead of scanData
          // (stale closure) so a successful seed doesn't trigger runScan.
          runScan(tickers);
        }
      })
      .catch(() => {
        // Transient failure (e.g. backend mid-restart / momentary auth blip) used to
        // silently strand the watchlist empty + stuck "loading". Retry the fetch once
        // so the saved tickers repopulate; scores then refill from the background scan.
        setTimeout(() => {
          apiFetch("/watchlist", token)
            .then(d => { if (d?.tickers?.length) setWatchlist(d.tickers); })
            .catch(() => {});
        }, 2500);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // ─── Continuous live-price refresh while watchlist is open ─────────────
  // Polls /live-prices every 60 s during market hours. Uses watchlistRef
  // (always-current) so the interval closure never reads a stale ticker list.
  // isMarketOpen() is checked at call time — if the user opens the screen
  // pre-market and holds it through the open bell, prices go live at 9:30.
  useEffect(() => {
    if (!token) return;
    const patchPrices = () => {
      const wl = watchlistRef.current;
      if (!wl?.length || !isMarketOpen()) return;
      apiFetch(`/live-prices?tickers=${wl.join(",")}`, token)
        .then(lpData => {
          if (!lpData?.prices) return;
          setScanData(prev => {
            if (!prev?.results?.length) return prev;
            let changed = false;
            const newResults = prev.results.map(r => {
              const lp = lpData.prices[r.ticker];
              if (!lp || lp.price == null) return r;
              changed = true;
              return {
                ...r,
                price:     lp.price,
                changePct: lp.changePct ?? r.changePct,
                changeAmt: lp.prevClose != null
                  ? lp.price - lp.prevClose
                  : r.changeAmt,
                isLive: lp.isLive ?? r.isLive,
              };
            });
            return changed ? {...prev, results: newResults} : prev;
          });
        })
        .catch(() => {});
    };
    const _id = setInterval(patchPrices, 60_000);
    return () => clearInterval(_id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, setScanData]); // setScanData is a stable setter; watchlist read from ref

  // ─── Single source of truth: ensure every watchlist ticker has a row ────
  // Fires whenever universeData or watchlist changes. Walks the watchlist,
  // seeds rows from universeData where available, and triggers a per-ticker
  // /analyze fetch (upserting back into universeData so other surfaces
  // benefit) for any ticker missing from the universe cache. This is the
  // canonical pattern: all surfaces read from universeData; missing tickers
  // get filled lazily and the result is written back so the next surface
  // doesn't have to re-fetch.
  useEffect(() => {
    if (!watchlist?.length || !universeData || !token) return;
    const ud = universeData;

    // Build seed rows from universeData (whatever is available)
    const seeded = watchlist.map((t, i) => {
      const u = ud[t.toUpperCase()] || {};
      const hasData = (u.swingScore != null || u.price != null);
      if (!hasData) return null;
      return {
        ticker: t.toUpperCase(), shortName: u.shortName || t,
        price: u.price || 0, changePct: u.changePct || 0,
        changeAmt: u.changeAmt || 0, isLive: u.isLive || false,
        swingScore: u.swingScore ?? null, swingLabel: u.swingLabel || "",
        swingType:  u.swingType || "watch",
        ltScore: u.ltScore ?? null, ltLabel: u.ltLabel || "", ltType: u.ltType || "watch",
        rsi: u.rsi || 0, beta: u.beta || 1.0,
        sentiment: u.sentiment || "", sentimentEmoji: u.sentimentEmoji || "",
        horizonReturn: u.horizonReturn || 0, sector: u.sector || "",
        sparkline: u.sparkline || [],
        sparklineIntraday: u.sparklineIntraday || [],
        patterns: "", addedOrder: i, _fromCache: true,
      };
    }).filter(Boolean);

    // Merge fresh universe scores into scanData rows.
    // Strategy:
    //   • If no rows yet → replace wholesale with seeded.
    //   • If /analyze rows exist (!_fromCache) → don't touch (richer data).
    //   • If only cache rows exist → merge: update null/0 scores with fresh
    //     values from seeded. This is the fix for TSLA/NVDA etc. that were
    //     previously stuck showing "—" because an earlier cycle wrote score=0
    //     and the length-match guard prevented the score from being updated.
    if (seeded.length > 0) {
      setScanData(prev => {
        // Never downgrade /analyze rows (they have entry/stop/target/patterns)
        if (prev?.results?.some(r => !r._fromCache)) return prev;

        // No rows yet — just populate from seeded
        if (!prev?.results?.length) {
          return { results: seeded, count: seeded.length,
                   regime: prev?.regime || {spyTrend:"unknown", vix:0, fearLabel:"N/A"},
                   profile: {key: user.risk_profile} };
        }

        // Has cache rows: merge seeded scores in (null/0 → fresh value)
        const seededMap = new Map(seeded.map(r => [r.ticker, r]));
        const merged = prev.results.map(r => {
          const fresh = seededMap.get(r.ticker);
          if (!fresh) return r;
          return {
            ...r,
            // Only overwrite if fresh has a better (non-null, >0) value
            swingScore: (fresh.swingScore != null && fresh.swingScore > 0)
              ? fresh.swingScore : r.swingScore,
            swingLabel: fresh.swingLabel || r.swingLabel,
            swingType:  fresh.swingType  || r.swingType,
            ltScore:    (fresh.ltScore != null && fresh.ltScore > 0)
              ? fresh.ltScore : r.ltScore,
            ltLabel:    fresh.ltLabel    || r.ltLabel,
            ltType:     fresh.ltType     || r.ltType,
            price:      fresh.price      || r.price,
            changePct:  fresh.changePct  ?? r.changePct,
            sparkline:  fresh.sparkline?.length ? fresh.sparkline : r.sparkline,
            sparklineIntraday: fresh.sparklineIntraday?.length
              ? fresh.sparklineIntraday
              : r.sparklineIntraday,
          };
        });
        // Also add any tickers in seeded that aren't in prev yet
        const prevTickers = new Set(prev.results.map(r => r.ticker));
        const additions   = seeded.filter(s => !prevTickers.has(s.ticker));
        const final       = [...merged, ...additions];
        return { ...prev, results: final, count: final.length };
      });
    }

    // Lazy-fill missing tickers via /analyze (one-by-one, low priority).
    // Each result is upserted into universeData so the next surface that
    // reads this ticker doesn't have to re-fetch.
    //
    // v8.4.32: a ticker counts as "filled" only when both scores are present
    // and > 0. Earlier logic treated any ticker with EITHER a price or a
    // swingScore as filled — so a partial Redis entry (price only, null
    // scores — happens when Celery wrote one profile×horizon combo but not
    // the user's) was seeded with null scores AND skipped by lazy-fill,
    // leaving the donut as "—" permanently. Symptom on user accounts whose
    // risk_profile differs from the profile Celery managed to write for a
    // given ticker (e.g. SMTC/LUNR/RKLB on aggressive while only moderate
    // was in Redis). Force-include those in the lazy-fill set so /analyze
    // computes the missing combo and back-fills the universe cache.
    const _filled = new Set(
      seeded
        .filter(r => (r.swingScore ?? 0) > 0 && (r.ltScore ?? 0) > 0)
        .map(r => r.ticker)
    );
    const missing = watchlist
      .map(t => t.toUpperCase())
      .filter(t => !_filled.has(t));
    if (missing.length === 0) return;
    let cancelled = false;
    (async () => {
      const h = horizonRef.current || "6m";
      for (const t of missing) {
        if (cancelled) break;
        try {
          const d = await apiFetch(`/analyze/${t}?horizon=${h}`, token);
          if (cancelled || !d) continue;
          // Upsert into universeData — single source of truth.
          // v8.4.37: prefer backend's d.sparkline (14 closes) — works even
          // when /analyze returns its stale-cache fallback (no `candles`).
          const _lzSparkDaily = (Array.isArray(d.sparkline) && d.sparkline.length >= 2)
            ? d.sparkline
            : (d.candles || []).slice(-14)
                .map(c => typeof c === "object" ? c.c : c)
                .filter(v => typeof v === "number" && !isNaN(v));
          const _lzSparklineIntraday = extractIntradaySparkline(d.candles_intraday_15m, d.candles_intraday);
          // Back-compat alias used by other call sites in this file.
          const _lzSparkline = _lzSparkDaily;
          if (upsertUniverse) {
            upsertUniverse(t, {
              ticker: t, shortName: d.shortName || t,
              sector: d.fundamentals?.sector || "",
              price: d.price || 0, changePct: d.changePct || 0,
              isLive: !!d.isLive,
              swingScore: d.swingScore?.score ?? null,
              swingLabel: d.swingScore?.label || "",
              swingType:  d.swingScore?.labelType || "watch",
              ltScore:    d.longTermScore?.score ?? null,
              ltLabel:    d.longTermScore?.label || "",
              ltType:     d.longTermScore?.labelType || "watch",
              horizonReturn: d.longTermScore?.horizonReturn ?? null,
              beta: d.beta || 1.0, rsi: d.rsi || 50,
              sentiment: d.sentiment?.label || "",
              sparkline: _lzSparkline,
              sparklineIntraday: _lzSparklineIntraday,
              scoredAt: Date.now(),
            });
          }
          // Inject this ticker into scanData
          const newRow = {
            ticker: t, shortName: d.shortName || t,
            price: d.price || 0, changePct: d.changePct || 0,
            changeAmt: d.changeAmt || 0, isLive: !!d.isLive,
            swingScore: d.swingScore?.score ?? null,
            swingLabel: d.swingScore?.label || "",
            swingType:  d.swingScore?.labelType || "watch",
            ltScore:    d.longTermScore?.score ?? null,
            ltLabel:    d.longTermScore?.label || "",
            ltType:     d.longTermScore?.labelType || "watch",
            rsi: d.rsi || 0, beta: d.beta || 1.0,
            sentiment: d.sentiment?.label || "",
            sentimentEmoji: d.sentiment?.labelEmoji || "",
            horizonReturn: d.longTermScore?.horizonReturn || 0,
            sector: d.fundamentals?.sector || "",
            sparkline: _lzSparkline,
            sparklineIntraday: _lzSparklineIntraday,
            patterns: "", addedOrder: watchlist.indexOf(t.toLowerCase()) >= 0
              ? watchlist.indexOf(t.toLowerCase())
              : watchlist.indexOf(t),
          };
          setScanData(prev => {
            if (!prev) {
              return { results: [newRow], count: 1,
                       regime: {spyTrend:"unknown", vix:0, fearLabel:"N/A"},
                       profile: {key: user.risk_profile} };
            }
            const exists = prev.results.find(r => r.ticker === t);
            const newResults = exists
              ? prev.results.map(r => r.ticker === t ? newRow : r)
              : [...prev.results, newRow];
            return { ...prev, results: newResults, count: newResults.length };
          });
        } catch (e) {
          // Skip on error; the user can pull-to-refresh to retry
        }
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [universeData, watchlist]);

  // v8.4.62: standalone background intraday hydration that runs regardless
  // of market session. The bgRefresh-tail hydration (v8.4.41) only fires
  // when (haveRows && isMarketOpen), so on weekends/overnight every row
  // stayed on 3W-only with no 1D toggle. This effect runs once per session
  // for each watchlist ticker that lacks sparklineIntraday in universeData,
  // including when market is closed — Polygon snapshot returns Friday's
  // last-session 15-min bars over the weekend, which is still useful.
  // Persistent ref so the same ticker isn't re-fetched on every render.
  const intraHydrateAttemptedRef = React.useRef(new Set());
  useEffect(() => {
    if (!watchlist?.length || !token || !upsertUniverse) return;
    let cancelled = false;
    (async () => {
      // Give universeData a beat to load on cold start.
      await new Promise(r => setTimeout(r, 800));
      if (cancelled) return;
      const ud = universeDataRef.current || {};
      const attempted = intraHydrateAttemptedRef.current;
      const needs = watchlist
        .map(t => t.toUpperCase())
        .filter(t => !attempted.has(t))
        .filter(t => {
          const u = ud[t];
          // Already populated? skip.
          if (u && Array.isArray(u.sparklineIntraday) && u.sparklineIntraday.length >= 2) {
            attempted.add(t);
            return false;
          }
          return true;
        });
      if (!needs.length) return;
      const h = horizonRef.current || "6m";
      const CONCURRENCY = 4;
      let cursor = 0;
      const worker = async () => {
        while (!cancelled && cursor < needs.length) {
          const my = cursor++;
          const t = needs[my];
          // Mark attempted NOW so concurrent workers don't double-fetch the same ticker
          attempted.add(t);
          try {
            const dd = await apiFetch(`/analyze/${t}?horizon=${h}`, token);
            if (cancelled || !dd) continue;
            const _intra = extractIntradaySparkline(dd.candles_intraday_15m, dd.candles_intraday);
            const _daily = (Array.isArray(dd.sparkline) && dd.sparkline.length >= 2)
              ? dd.sparkline
              : (Array.isArray(dd.candles) ? dd.candles.slice(-14)
                  .map(c => typeof c === "object" ? c.c : c)
                  .filter(v => typeof v === "number" && !isNaN(v)) : []);
            if (_intra.length >= 2 || _daily.length >= 2) {
              upsertUniverse(t, {
                ticker: t,
                sparkline: _daily.length >= 2 ? _daily : undefined,
                sparklineIntraday: _intra,
                scoredAt: Date.now(),
              });
            }
            // If intraday came back empty, remove from attempted so it retries
            // next time watchlist changes (e.g., after market opens)
            if (_intra.length < 2) {
              attempted.delete(t);
            }
          } catch (e) {
            attempted.delete(t); // failed fetch — allow retry on next render cycle
          }
        }
      };
      await Promise.all(Array.from({length: CONCURRENCY}, worker));
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchlist, token]);

  // v8.4.39: keep scanData rows in sync with universeData updates.
  // When StockScreen calls upsertUniverse with fresh /analyze data, this
  // effect detects the newer scoredAt and re-projects the matching row's
  // score fields. Without this the Watchlist would render stale Celery
  // scores after the user looked at the detail screen — the seed effect
  // above bails out as soon as any scanData row is non-cache.
  useEffect(() => {
    if (!universeData) return;
    setScanData(prev => {
      if (!prev?.results?.length) return prev;
      let changed = false;
      const newResults = prev.results.map(r => {
        const u = universeData[r.ticker];
        if (!u) return r;
        const uTs = u.scoredAt || 0;
        const rTs = r._uTs || 0;
        if (uTs <= rTs) return r; // no newer data
        changed = true;
        return {
          ...r,
          swingScore: (u.swingScore != null && u.swingScore > 0) ? u.swingScore : r.swingScore,
          swingLabel: u.swingLabel || r.swingLabel,
          swingType:  u.swingType  || r.swingType,
          ltScore:    (u.ltScore != null && u.ltScore > 0) ? u.ltScore : r.ltScore,
          ltLabel:    u.ltLabel || r.ltLabel,
          ltType:     u.ltType  || r.ltType,
          price:      u.price || r.price,
          changePct:  u.changePct ?? r.changePct,
          isLive:     u.isLive ?? r.isLive,
          rsi:        u.rsi || r.rsi,
          sector:     u.sector || r.sector,
          sentiment:  u.sentiment || r.sentiment,
          sparkline:  (Array.isArray(u.sparkline) && u.sparkline.length >= 2) ? u.sparkline : r.sparkline,
          sparklineIntraday:
            (Array.isArray(u.sparklineIntraday) && u.sparklineIntraday.length >= 2)
              ? u.sparklineIntraday : r.sparklineIntraday,
          // Clear stale failure flag once a real score lands.
          _failed: ((u.swingScore || 0) > 0 || (u.ltScore || 0) > 0) ? false : r._failed,
          _uTs: uTs,
        };
      });
      if (!changed) return prev;
      return { ...prev, results: newResults };
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [universeData]);

  // v8.4.300: remember the watchlist sort across sessions (was resetting to "recent").
  const [sort, setSort] = useState(() => {
    try { return localStorage.getItem("gempulse_wl_sort") || "recent"; } catch (_) { return "recent"; }
  });

  const WATCHLIST_LIMIT = 50;
  const overLimit = watchlist.length > WATCHLIST_LIMIT;

  const saveWatchlist = async (tickers) => {
    setWlSaving(true); setWlMsg("");
    try {
      await apiFetch("/watchlist", token, {
        method: "PUT",
        body: JSON.stringify({ tickers }),
      });
      setWatchlist(tickers);
      setWlMsg("✅ Saved");
      setTimeout(() => setWlMsg(""), 2000);
    } catch(e) {
      setWlMsg("❌ Failed to save");
    } finally {
      setWlSaving(false);
    }
  };

  const addTicker = async () => {
    const t = newTicker.trim().toUpperCase().replace(/\s/g,"");
    if (!t) return;
    if (watchlist.includes(t)) { setWlMsg("Already in watchlist"); return; }
    if (watchlist.length >= WATCHLIST_LIMIT) {
      setWlMsg(`⚠️ Over ${WATCHLIST_LIMIT} tickers — refresh may be slower`);
    }
    const updated = [t, ...watchlist];
    setNewTicker("");

    // 1. Save to API + update watchlist immediately (v8.4.88: atomic add)
    try {
      const d = await apiFetch("/watchlist/add", token, {
        method:"POST", body:JSON.stringify({ticker:t})
      });
      if (d?.tickers) setWatchlist(d.tickers);
    } catch(e) { setWlMsg("❌ Failed to save"); return; }

    // 2. Show placeholder card at top immediately (addedOrder: -1 = always top)
    const placeholder = {
      ticker:t, shortName:"Scoring…", price:null,
      swingScore:null, ltScore:null, swingType:"watch",
      addedOrder: -1, isPlaceholder: true,
    };
    setScanData(prev => prev
      ? {...prev, results:[placeholder,...prev.results.filter(r=>r.ticker!==t)], count:(prev.count||0)+1}
      : {results:[placeholder],count:1,regime:{spyTrend:"unknown",vix:0,fearLabel:"N/A"},profile:{key:user.risk_profile}});

    // 3. Score in background -- replace placeholder when done.
    //    Fast path: if the ticker is already in the universe cache (Celery
    //    has pre-scored 514 tickers × 15 horizon×profile combos), use that
    //    directly instead of burning a /analyze rescore.  Falls through to
    //    /analyze when the cache is missing, stale, or not the right profile.
    try {
      const h = horizonRef.current;
      const cachedU = universeData && universeData[t];
      const cachedUsable = cachedU
        && cachedU.swingScore != null
        && cachedU.ltScore != null
        && cachedU.price != null;
      let scored;
      if (cachedUsable) {
        // Universe cache stores swingScore/ltScore as flat numbers (not the
        // {score,label,labelType} object /analyze returns), plus parallel
        // swingLabel/swingType/ltLabel/ltType strings.  swingScoreFull carries
        // entry/stop/target1 when present.
        const swf = cachedU.swingScoreFull || {};
        const pats = cachedU.patterns || [];
        scored = {
          ticker:t, shortName:cachedU.shortName||t,
          price: cachedU.price,
          changePct: cachedU.changePct, changeAmt: cachedU.changeAmt || 0,
          isLive:  !!cachedU.isLive,
          swingScore: cachedU.swingScore, swingLabel: cachedU.swingLabel, swingType: cachedU.swingType,
          ltScore:    cachedU.ltScore,    ltLabel:    cachedU.ltLabel,    ltType:    cachedU.ltType,
          trendDir: swf.trendDir, rsi: cachedU.rsi, beta: cachedU.beta,
          sentiment: cachedU.sentiment?.label, sentimentEmoji: cachedU.sentiment?.labelEmoji,
          horizonReturn: cachedU.horizonReturn,
          entry: swf.entry, stop: swf.stop, target1: swf.target1,
          patterns: `▲${pats.filter(p=>p.type==="bullish").length} ▼${pats.filter(p=>p.type==="bearish").length}`,
          sector: cachedU.sector || cachedU.fundamentals?.sector || "", addedOrder: -1,
        };
        setScanData(prev => prev
          ? {...prev, results:prev.results.map(r=>r.ticker===t?scored:r)}
          : {results:[scored],count:1,regime:{spyTrend:"unknown",vix:0,fearLabel:"N/A"},profile:{key:user.risk_profile}});
        return;
      }
      const d = await apiFetch(`/analyze/${t}?horizon=${h}`, token);
      scored = {
        ticker:t, shortName:d.shortName, price:d.price,
        changePct:d.changePct, changeAmt:d.changeAmt||0, isLive:d.isLive,
        swingScore:d.swingScore?.score, swingLabel:d.swingScore?.label, swingType:d.swingScore?.labelType,
        ltScore:d.longTermScore?.score, ltLabel:d.longTermScore?.label, ltType:d.longTermScore?.labelType,
        trendDir:d.swingScore?.trendDir, rsi:d.rsi, beta:d.beta,
        sentiment:d.sentiment?.label, sentimentEmoji:d.sentiment?.labelEmoji,
        horizonReturn:d.longTermScore?.horizonReturn,
        entry:d.swingScore?.entry, stop:d.swingScore?.stop, target1:d.swingScore?.target1,
        patterns:`▲${(d.patterns||[]).filter(p=>p.type==="bullish").length} ▼${(d.patterns||[]).filter(p=>p.type==="bearish").length}`,
        sector:d.fundamentals?.sector||"", addedOrder: -1,
      };
      setScanData(prev => prev
        ? {...prev, results:prev.results.map(r=>r.ticker===t?scored:r)}
        : {results:[scored],count:1,regime:{spyTrend:"unknown",vix:0,fearLabel:"N/A"},profile:{key:user.risk_profile}});
    } catch(e) {
      // Remove placeholder on failure -- user can full-refresh
      setScanData(prev => prev
        ? {...prev, results:prev.results.filter(r=>r.ticker!==t || !r.isPlaceholder), count:Math.max(0,(prev.count||1)-1)}
        : prev);
      setWlMsg("⚠️ " + t + " saved but scoring failed -- pull to refresh");
      setTimeout(()=>setWlMsg(""),4000);
    }
  };

  const removeTicker = (t) => {
    if (watchlist.length <= 1) { setWlMsg("❌ Need at least 1 ticker"); return; }
    const updated = watchlist.filter(x => x !== t);
    setScanData(prev => prev ? {...prev, results:prev.results.filter(r=>r.ticker!==t), count:Math.max(0,(prev.count||1)-1)} : prev);
    // v8.4.88: atomic remove — server deletes from authoritative DB list
    apiFetch("/watchlist/"+encodeURIComponent(t), token, {method:"DELETE"})
      .then(d => { if (d?.tickers) setWatchlist(d.tickers); else setWatchlist(updated); })
      .catch(()=>{});
    // Log to recently-deleted so user can recover accidental removals
    _saveRd([{ticker:t, deletedAt:Date.now()}, ..._loadRd().filter(e=>e.ticker!==t)]);
  };

  const restoreTicker = (t) => {
    // Remove from recently-deleted list immediately
    _saveRd(recentlyDeleted.filter(e => e.ticker !== t));
    // Re-add to watchlist (reuse addTicker flow if ticker isn't already there)
    if (watchlist.includes(t)) return;
    setNewTicker(t);
    // v8.4.88: atomic add for restore path
    apiFetch("/watchlist/add", token, {method:"POST", body:JSON.stringify({ticker:t})})
      .then(d => {
        if (d?.tickers) setWatchlist(d.tickers);
        setNewTicker("");
        setWlMsg(`✓ ${t} restored`);
        setTimeout(() => setWlMsg(""), 3000);
      })
      .catch(() => setWlMsg(`❌ Failed to restore ${t}`));
  };

  const [scanProgress, setScanProgress] = useState({completed:0, total:0});
  const scanTimeRef    = React.useRef(null);
  const scanAbortRef   = React.useRef(false);
  const watchlistRef   = React.useRef(watchlist);
  const horizonRef     = React.useRef(horizon);
  const hasScanedOnce  = React.useRef(false);

  useEffect(() => { watchlistRef.current = watchlist; }, [watchlist]);
  useEffect(() => { horizonRef.current = horizon; }, [horizon]);

  // Same approach as Discovery -- call /analyze per ticker in parallel batches of 4
  // This is proven to work and avoids the broken /scan endpoint dependency
  const runScan = useCallback(async (wl) => {
    const tickers = wl || watchlistRef.current;
    if (!tickers || !tickers.length) return;
    scanAbortRef.current = false;

    setScanLoading(true);
    setScanError("");
    setScanData(null);
    setScanProgress({completed:0, total:tickers.length});

    const h       = horizonRef.current;
    const out     = [];
    const BATCH   = 4;
    const TICKER_TIMEOUT = 90000; // 90s per ticker -- prevents one slow ticker blocking all
    let   done    = 0;

    // Request wake lock to prevent screen sleep during scan
    let wakeLock = null;
    try {
      if ("wakeLock" in navigator) {
        wakeLock = await navigator.wakeLock.request("screen");
      }
    } catch(e) { /* wake lock not supported or denied -- continue without it */ }

    const _stubRow = (ticker) => ({
      ticker, shortName: ticker,
      price: null, changePct: null, changeAmt: 0, isLive: false,
      swingScore: null, swingLabel: null, swingType: null,
      ltScore: null, ltLabel: null, ltType: null,
      trendDir: null, rsi: null, beta: null,
      sentiment: null, sentimentEmoji: null,
      horizonReturn: null, entry: null, stop: null, target1: null,
      sparkline: [], sparklineIntraday: [], patterns: "—", _failed: true,
    });

    const scoreOne = async (ticker) => {
      // Wrap each ticker in its own timeout so one slow stock can't block the batch
      return new Promise(async (resolve) => {
        // On timeout: keep the row visible with null scores rather than dropping it
        const timer = setTimeout(() => resolve(_stubRow(ticker)), TICKER_TIMEOUT);
        try {
          // ── Cache-first path ──────────────────────────────────────────
          // Always check universeData before hitting /analyze. When market
          // is closed scores don't change overnight so the cache is the
          // authoritative source; when open, accept cache up to 35min old
          // (matches Celery's 30-min refresh + 5min grace, identical to
          // the bg-refresh loop above so manual + auto refreshes stay in
          // sync). This prevents the manual refresh button from hammering
          // /analyze on Saturday/Sunday for nothing.
          const STALE_MS = 35 * 60 * 1000;
          // Use the always-current ref (not the stale useCallback closure)
          const cached_u = universeDataRef.current?.[ticker];
          // Require actual scores present — a cached entry with scoredAt but
          // null swingScore/ltScore (e.g. Celery failed to score that ticker)
          // must fall through to /analyze rather than re-serving null scores.
          const cacheUsable = cached_u
            && cached_u.swingScore != null
            && cached_u.ltScore    != null
            && cached_u.price      != null
            && (
              !isMarketOpen()
                ? !!cached_u.scoredAt
                : (Date.now() - (cached_u.scoredAt || 0)) < STALE_MS
            );

          let d;
          if (cacheUsable) {
            // Synthesise the /analyze-shaped object that the rest of this
            // function expects, sourced from universeData. The runScan
            // result object below reads .swingScore?.score etc, so we wrap
            // the flat numeric scores back into nested form.
            // v8.4.41: properly forward BOTH sparkline series. Previous code
            // mapped cached_u.sparkline (daily 14 closes) into
            // candles_intraday_15m which made downstream extraction:
            //   (a) set sparkline=[] (no d.sparkline, no d.candles)
            //   (b) populate sparklineIntraday with daily data mislabeled
            // → row had no daily series, intraday tab showed daily data.
            // Now: pass through cached_u.sparkline as d.sparkline and
            // cached_u.sparklineIntraday as candles_intraday_15m so both
            // tabs work and represent the right timeframe.
            d = {
              ticker, shortName: cached_u.shortName || ticker,
              price: cached_u.price, changePct: cached_u.changePct,
              changeAmt: cached_u.changeAmt || 0,
              isLive: cached_u.isLive,
              rsi:   cached_u.rsi,    beta: cached_u.beta,
              swingScore:   { score: cached_u.swingScore, label: cached_u.swingLabel, labelType: cached_u.swingType, trendDir: cached_u.trendDir },
              longTermScore:{ score: cached_u.ltScore,    label: cached_u.ltLabel,    labelType: cached_u.ltType,    horizonReturn: cached_u.horizonReturn },
              sentiment: { label: cached_u.sentiment, labelEmoji: cached_u.sentimentEmoji || "" },
              patterns: Array.isArray(cached_u.patterns) ? cached_u.patterns : [],
              sparkline: Array.isArray(cached_u.sparkline) ? cached_u.sparkline : [],
              candles_intraday_15m: Array.isArray(cached_u.sparklineIntraday)
                ? cached_u.sparklineIntraday.map(c => ({c}))
                : [],
              candles_intraday: [],
            };
          } else {
            d = await apiFetch(`/analyze/${ticker}?horizon=${h}`, token);
          }
          clearTimeout(timer);
          // v8.4.37: dual sparkline data — 14-day daily series (3W toggle) +
          // today's 15-min intraday (default render).
          // Daily preference order: d.sparkline (backend-written 14 closes,
          // always present after v8.4.29) → slice from d.candles → empty.
          // Using d.sparkline first means rows served via /analyze's
          // stale-cache fallback (which omits candles) still get a daily
          // series, so the 3W toggle has something to flip to.
          const _sparkDaily = (Array.isArray(d.sparkline) && d.sparkline.length >= 2)
            ? d.sparkline
            : (d.candles || []).slice(-14)
                .map(c => (typeof c === "object" ? c.c : c))
                .filter(v => typeof v === "number" && !isNaN(v));
          const _sparklineIntraday = extractIntradaySparkline(d.candles_intraday_15m, d.candles_intraday);
          // Back-compat: existing call sites read `_sparkline` as the daily series.
          const _sparkline = _sparkDaily;

          resolve({
            ticker:        d.ticker,
            shortName:     d.shortName,
            price:         d.price,
            changePct:     d.changePct,
            changeAmt:     d.changeAmt || 0,
            isLive:        d.isLive,
            swingScore:    d.swingScore?.score,
            swingLabel:    d.swingScore?.label,
            swingType:     d.swingScore?.labelType,
            ltScore:       d.longTermScore?.score,
            ltLabel:       d.longTermScore?.label,
            ltType:        d.longTermScore?.labelType,
            trendDir:      d.swingScore?.trendDir,
            rsi:           d.rsi,
            beta:          d.beta,
            sentiment:     d.sentiment?.label,
            sentimentEmoji:d.sentiment?.labelEmoji,
            horizonReturn: d.longTermScore?.horizonReturn,
            entry:         d.swingScore?.entry,
            stop:          d.swingScore?.stop,
            target1:       d.swingScore?.target1,
            sparkline:     _sparkline,
            sparklineIntraday: _sparklineIntraday,
            patterns:      `▲${(d.patterns||[]).filter(p=>p.type==="bullish").length} ▼${(d.patterns||[]).filter(p=>p.type==="bearish").length}`,
          });
        } catch(e) {
          clearTimeout(timer);
          if (e.name === "AuthError") {
            setScanError("Session may have expired — results may be incomplete. Try refreshing.");
          }
          // Keep the row visible with null scores rather than dropping it entirely
          resolve(_stubRow(ticker));
        }
      });
    };

    for (let i = 0; i < tickers.length; i += BATCH) {
      if (scanAbortRef.current) break;
      const batch   = tickers.slice(i, i + BATCH);
      const settled = await Promise.allSettled(batch.map(scoreOne));
      for (const r of settled) {
        if (r.status === "fulfilled" && r.value) out.push(r.value);
      }
      done = Math.min(i + BATCH, tickers.length);
      setScanProgress({completed:done, total:tickers.length});
    }

    if (scanAbortRef.current) {
      try { wakeLock?.release(); } catch(e) {}
      return;
    }

    // Tag each result with its watchlist position for "recent" sort
    // tickers[0] = most recently added (we prepend new tickers)
    const orderMap = {};
    tickers.forEach((t, i) => { orderMap[t] = i; });
    out.forEach(r => { r.addedOrder = orderMap[r.ticker] ?? 999; });

    // Default sort by combined score (user can change via sort pills)
    out.sort((a,b) => ((b.swingScore||0)+(b.ltScore||0)) - ((a.swingScore||0)+(a.ltScore||0)));

    // v8.4.36: merge with existing scanData instead of wholesale replace so
    // a `_stubRow` from a /analyze timeout doesn't nuke a previously-valid
    // seeded score. For each result row, keep the score from `prev` if the
    // new row's score is null/0 (failed) but prev had a real value.
    // v8.4.38: also clear row._failed when we restored prev's score — the
    // _stubRow flag would otherwise paint "tap to retry" over a row whose
    // score is actually fine from the previous seed.
    setScanData(prev => {
      const prevMap = new Map((prev?.results || []).map(r => [r.ticker, r]));
      const merged = out.map(row => {
        const p = prevMap.get(row.ticker);
        if (!p) return row;
        const _keepSw = (row.swingScore == null || row.swingScore <= 0) && (p.swingScore != null && p.swingScore > 0);
        const _keepLt = (row.ltScore    == null || row.ltScore    <= 0) && (p.ltScore    != null && p.ltScore    > 0);
        const _restored = _keepSw || _keepLt;
        return {
          ...row,
          swingScore: _keepSw ? p.swingScore : row.swingScore,
          swingLabel: _keepSw ? (p.swingLabel || row.swingLabel) : row.swingLabel,
          swingType:  _keepSw ? (p.swingType  || row.swingType)  : row.swingType,
          ltScore:    _keepLt ? p.ltScore    : row.ltScore,
          ltLabel:    _keepLt ? (p.ltLabel || row.ltLabel) : row.ltLabel,
          ltType:     _keepLt ? (p.ltType  || row.ltType)  : row.ltType,
          price:      (row.price != null && row.price > 0) ? row.price : (p.price ?? row.price),
          changePct:  row.changePct ?? p.changePct,
          sparkline:          row.sparkline?.length         ? row.sparkline         : (p.sparkline || []),
          sparklineIntraday:  row.sparklineIntraday?.length ? row.sparklineIntraday : (p.sparklineIntraday || []),
          // Clear the stub-failure flag when we have valid restored scores —
          // otherwise the row paints "⚠ tap to retry" on top of a real score.
          _failed: _restored ? false : row._failed,
        };
      });
      return {
        results: merged,
        count:   merged.length,
        regime:  prev?.regime || {spyTrend:"unknown", vix:0, fearLabel:"N/A"},
        profile: {key:user.risk_profile},
      };
    });
    setScanLoading(false);
    setScanProgress({completed:tickers.length, total:tickers.length});
    scanTimeRef.current   = Date.now();
    hasScanedOnce.current = true;

    // Release wake lock now scan is done
    try { wakeLock?.release(); } catch(e) {}

    // Fetch regime + market indices after scan completes
    // Feed full market data up to root so Landing screen uses real data, not a dummy fetch
    try {
      const spy = await apiFetch("/analyze/SPY?horizon=6m", token);
      if (spy?.regime) setScanData(prev => prev ? {...prev, regime:spy.regime} : prev);
      // Fetch the other indices in parallel and push to root market state
      const [qq, dia, gld] = await Promise.allSettled([
        apiFetch("/analyze/QQQ?horizon=6m", token),
        apiFetch("/analyze/DIA?horizon=6m", token),
        apiFetch("/analyze/GLD?horizon=6m", token),
      ]);
      if (setMarketData) {
        setMarketData({
          spy,
          regime: spy.regime,
          qqq: qq.status === "fulfilled" ? qq.value : null,
          dia: dia.status === "fulfilled" ? dia.value : null,
          gld: gld.status === "fulfilled" ? gld.value : null,
        });
      }
      if (onScanComplete) onScanComplete(Date.now());
    } catch(e) {}

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, user.risk_profile]);


  // Expose runScan to parent via ref so Home pull-to-refresh can trigger it
  React.useEffect(() => {
    if (runScanRef) runScanRef.current = () => runScan(watchlistRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runScan]);
  // Re-scan when horizon or profile changes
  const prevHorizon = React.useRef(horizon);
  const prevProfile = React.useRef(user.risk_profile);
  useEffect(() => {
    const horizonChanged = prevHorizon.current !== horizon;
    const profileChanged = prevProfile.current !== user.risk_profile;
    prevHorizon.current  = horizon;
    prevProfile.current  = user.risk_profile;
    if ((horizonChanged || profileChanged) && hasScanedOnce.current
        && watchlistRef.current.length > 0 && !scanLoading
        && isMarketOpen()) {
      // Only rescan on setting changes during market hours — scores are live.
      // When closed, cached scores are the same regardless of horizon/profile UI;
      // the backend cache key includes both so a re-open will get fresh data.
      runScan(watchlistRef.current);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [horizon, user.risk_profile]);


    const REFRESH_COOLDOWN = isMarketOpen()
    ? 3 * 60 * 1000       // 3 min during market hours
    : 8 * 60 * 60 * 1000; // 8 hours when EOD

  const results  = scanData?.results || [];
  const filtered = (filter==="all" ? results
    : filter==="buy"   ? results.filter(r=>r.swingType==="buy"||r.swingType==="strong_buy")
    : filter==="watch" ? results.filter(r=>r.swingType==="watch")
    : results.filter(r=>r.swingType==="pass")
  ).slice().sort((a,b) => {
    if (sort==="recent")  return (a.addedOrder??999) - (b.addedOrder??999);
    if (sort==="alpha")   return (a.ticker||"").localeCompare(b.ticker||"");
    if (sort==="swing")   return (b.swingScore||0) - (a.swingScore||0);
    if (sort==="lt")      return (b.ltScore||0) - (a.ltScore||0);
    if (sort==="gap")     return Math.abs(b.changePct||0) - Math.abs(a.changePct||0);
    return 0;
  });
  const loading = scanLoading;
  const error   = scanError;

  return (
    <div style={{padding:"0 16px 100px"}}>
      <FrozenHeader padTop={16} padBottom={10}>
      {/* Title row — identity (left); refresh/manage moved to their own row below */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,paddingRight:38}}>
        <div style={{display:"flex",alignItems:"center",gap:10,minWidth:0}}>
          <AppLogo size={32}/>
          <div style={{minWidth:0}}>
            <div style={{fontSize:20,fontWeight:900,color:C.text,letterSpacing:"-0.02em",lineHeight:1}}>
              Watchlist
            </div>
            <div style={{fontSize:10,color:C.muted,marginTop:2,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
              {PROFILES.find(p=>p.key===user.risk_profile)?.emoji}{" "}
              {PROFILES.find(p=>p.key===user.risk_profile)?.label}{" · "}
              {HORIZONS.find(h=>h.key===horizon)?.label}
            </div>
          </div>
        </div>
      </div>
      {/* v8.4.313: status (left) + refresh/manage (right) share ONE row to compress
          the header; the fixed search magnifier owns the top-right corner. */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:8,marginTop:8}}>
        <div style={{flex:1,minWidth:0}}>
          {scanLoading ? (
            <span style={{fontSize:10,color:C.muted,display:"inline-flex",alignItems:"center",gap:6}}>
              <span style={{width:9,height:9,border:`2px solid ${C.accent}`,
                borderTopColor:"transparent",borderRadius:"50%",display:"inline-block",
                animation:"spin 0.7s linear infinite"}}/>
              Analyzing {scanProgress.completed}/{scanProgress.total}…
            </span>
          ) : scanError ? (
            <span style={{fontSize:10,color:C.red}}>⚠️ {scanError}</span>
          ) : scanData ? (
            <span style={{fontSize:10,color:C.muted}}>
              <span style={{color:bgRefreshing?"#ffca28":C.green}}>●</span>
              {" "}
              {bgRefreshing
                ? <span style={{color:"#ffca28"}}>Refreshing…</span>
                : <>
                    {scanData.count}/{watchlist.length} updated
                    {scanData.count < watchlist.length &&
                      <span style={{color:C.yellow}}> · {watchlist.length - scanData.count} missing</span>}
                    {scanTimeRef.current &&
                      <span> · {Math.round((Date.now()-scanTimeRef.current)/60000)||"<1"}m ago
                        {!isMarketOpen() && <span style={{color:"rgba(255,255,255,0.3)"}}> · EOD</span>}
                      </span>}
                  </>}
            </span>
          ) : (
            <span style={{fontSize:10,color:C.muted}}>Loading…</span>
          )}
        </div>
        <div style={{display:"flex",alignItems:"center",gap:6,flexShrink:0}}>
          {!scanLoading && (
            <button onClick={()=>{
              if (scanError) { setScanError(""); runScan(watchlistRef.current); return; }
              const now = Date.now(), lastScan = scanTimeRef.current || 0;
              const hasGaps = scanData && scanData.count < watchlistRef.current.length;
              if (hasGaps || now - lastScan >= REFRESH_COOLDOWN) runScan(watchlistRef.current);
              else {
                const waitSecs = Math.ceil((REFRESH_COOLDOWN-(now-lastScan))/1000);
                setScanError(`Updated recently — try again in ${waitSecs}s`);
                setTimeout(()=>setScanError(""),3000);
              }
            }} disabled={bgRefreshing} style={{
              background:"none",border:`1px solid ${C.border}`,borderRadius:8,
              padding:"4px 10px",color:C.muted,fontSize:11,cursor:"pointer",
              fontFamily:"inherit",flexShrink:0,
            }}>{isMarketOpen() ? "↻" : "↻ EOD"}</button>
          )}
          <button onClick={()=>setShowManage(s=>!s)} style={{
            fontSize:11,color:C.muted,background:"none",border:`1px solid ${C.border}`,
            borderRadius:8,padding:"4px 12px",cursor:"pointer",fontFamily:"inherit",flexShrink:0,
          }}>{showManage ? "▲ close" : "▼ manage"}</button>
        </div>
      </div>

      {/* Filter pills */}
      <div data-tour="wl-filter" style={{display:"flex",gap:6,marginTop:10,marginBottom:8}}>
        {[["all","All"],["buy","Buy"],["watch","Watch"],["pass","Pass"]].map(([v,l])=>(
          <button key={v} onClick={()=>setFilter(v)} style={{
            flex:1,padding:"6px 4px",borderRadius:8,border:"none",cursor:"pointer",
            fontFamily:"inherit",fontSize:12,fontWeight:600,
            background:filter===v?C.accent:C.surface,
            color:filter===v?C.bg:C.muted,transition:"all 0.15s",
          }}>{l}</button>
        ))}
      </div>

      {/* Sort pills */}
      <div data-tour="wl-sort" style={{display:"flex",gap:5,marginBottom:2,overflowX:"auto",
        paddingBottom:2}}>
        <span style={{fontSize:10,color:C.muted,alignSelf:"center",
          whiteSpace:"nowrap",marginRight:2}}>Sort:</span>
        {[
          ["recent","added"],
          ["swing","swing"],
          ["lt","lt"],
          ["gap","%▲"],
          ["alpha","A-Z"],
        ].map(([v,l])=>(
          <button key={v} onClick={()=>{ setSort(v); try { localStorage.setItem("gempulse_wl_sort", v); } catch(_){} }} style={{
            display:"flex",alignItems:"center",gap:4,
            padding:"4px 10px",borderRadius:20,border:`1px solid ${sort===v?C.accent:C.border}`,
            cursor:"pointer",fontFamily:"inherit",fontSize:11,fontWeight:600,
            background:sort===v?"rgba(0,212,255,0.12)":"none",
            color:sort===v?C.accent:C.muted,transition:"all 0.15s",
            whiteSpace:"nowrap",flexShrink:0,
          }}>
            {v==="swing" && <SwingIcon size={12} color={sort===v?C.accent:"rgba(255,255,255,0.4)"}/>}
            {v==="lt"    && <LTIcon size={12} color={sort===v?C.accent:"rgba(255,255,255,0.4)"}/>}
            {v==="recent" && (
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="9" stroke={sort===v?C.accent:"rgba(255,255,255,0.4)"} strokeWidth="2"/>
                <path d="M12 7v5l3 3" stroke={sort===v?C.accent:"rgba(255,255,255,0.4)"} strokeWidth="2" strokeLinecap="round"/>
              </svg>
            )}
            <span>{l}</span>
          </button>
        ))}
      </div>
      </FrozenHeader>
      <div style={{height:12}}/>

      {/* Search + Add — always exposed */}
      <div data-tour="wl-add" style={{marginBottom:10}}>
        <FeatureTour tourId="watchlist" version={1} steps={WATCHLIST_STEPS} active={visible}/>
        <div style={{display:"flex",gap:8}}>
          <div style={{flex:1}}>
            <TickerSearch
              value={newTicker}
              onChange={setNewTicker}
              onSelect={(sym)=>{ setNewTicker(sym); }}
              placeholder="Search to add a ticker…"
              size="sm"
              token={token}
            />
          </div>
          <button onClick={(e)=>{Haptics.medium(e.currentTarget);addTicker();}} disabled={wlSaving} style={{
            padding:"0 16px",borderRadius:10,border:"none",
            background:C.accent,color:C.bg,fontSize:13,fontWeight:700,
            cursor:"pointer",fontFamily:"inherit",opacity:wlSaving?0.7:1,flexShrink:0,
          }}>{wlSaving ? "…" : "+"}</button>
        </div>
        {wlMsg && (
          <div style={{fontSize:11,marginTop:6,
            color:wlMsg.startsWith("✅")?C.green:wlMsg.startsWith("⏳")?C.accent:C.yellow}}>
            {wlMsg}
          </div>
        )}
      </div>

      {/* Manage panel — ticker chips with remove buttons */}
      {showManage && (
        <div style={{background:C.surface,borderRadius:12,padding:"12px",
          border:`1px solid ${C.border}`,marginBottom:10}}>
          {overLimit && (
            <div style={{fontSize:11,color:C.yellow,marginBottom:8,lineHeight:1.6}}>
              ⚠️ {watchlist.length} tickers — refresh may be slower above {WATCHLIST_LIMIT}
            </div>
          )}
          <div style={{fontSize:10,color:C.muted,marginBottom:8}}>
            {watchlist.length} tickers · tap × to remove
          </div>
          <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
            {watchlist.map(t=>(
              <div key={t} style={{display:"flex",alignItems:"center",gap:4,
                background:C.card,borderRadius:20,padding:"4px 10px",
                border:`1px solid ${C.border}`}}>
                <TickerLogo ticker={t} size={16}/>
                <span style={{fontSize:12,fontWeight:600,color:C.text}}>{t}</span>
                <button onClick={(e)=>{Haptics.light(e.currentTarget);removeTicker(t);}} style={{
                  background:"none",border:"none",color:C.muted,cursor:"pointer",
                  fontSize:14,lineHeight:1,padding:"0 0 0 2px",fontFamily:"inherit",
                }}>×</button>
              </div>
            ))}
          </div>

          {/* Recently deleted — shows for up to 7 days, lets user undo accidental removes */}
          {recentlyDeleted.length > 0 && (
            <div style={{marginTop:14,paddingTop:12,
              borderTop:`1px solid ${C.border}`}}>
              <div style={{fontSize:10,color:C.muted,marginBottom:8,
                display:"flex",alignItems:"center",gap:6}}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
                  <path d="M12 8v4l3 3" stroke={C.muted} strokeWidth="2" strokeLinecap="round"/>
                  <circle cx="12" cy="12" r="9" stroke={C.muted} strokeWidth="2"/>
                </svg>
                Recently removed · tap ↩ to restore (clears after 7 days)
              </div>
              <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                {recentlyDeleted.map(({ticker:t, deletedAt})=>{
                  const daysAgo = Math.floor((Date.now()-deletedAt)/(24*60*60*1000));
                  const label   = daysAgo === 0 ? "today" : daysAgo === 1 ? "1d ago" : `${daysAgo}d ago`;
                  const alreadyIn = watchlist.includes(t);
                  return (
                    <div key={t} style={{display:"flex",alignItems:"center",gap:4,
                      background:"rgba(255,255,255,0.04)",borderRadius:20,
                      padding:"4px 10px",
                      border:`1px solid rgba(255,255,255,0.1)`,
                      opacity: alreadyIn ? 0.45 : 1}}>
                      <TickerLogo ticker={t} size={16}/>
                      <span style={{fontSize:12,fontWeight:600,
                        color:"rgba(255,255,255,0.55)"}}>{t}</span>
                      <span style={{fontSize:9,color:C.muted,marginLeft:1}}>{label}</span>
                      {!alreadyIn && (
                        <button
                          onClick={(e)=>{ Haptics.light(e.currentTarget); restoreTicker(t); }}
                          title={`Restore ${t}`}
                          style={{
                            background:"none",border:"none",
                            color:C.accent,cursor:"pointer",
                            fontSize:13,lineHeight:1,
                            padding:"0 0 0 2px",fontFamily:"inherit",
                          }}>↩</button>
                      )}
                      <button
                        onClick={()=>_saveRd(recentlyDeleted.filter(e=>e.ticker!==t))}
                        title="Dismiss"
                        style={{
                          background:"none",border:"none",color:C.muted,cursor:"pointer",
                          fontSize:12,lineHeight:1,padding:"0 0 0 1px",fontFamily:"inherit",
                        }}>×</button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}


          {/* Results */}
          <div style={{display:"flex",flexDirection:"column",gap:10}}>
            {filtered.filter(s=>s&&s.ticker).map((s,i)=>{
              // Placeholder card while new ticker is being scored
              if (s.isPlaceholder) return (
                <Card key={s.ticker} style={{opacity:0.6}}>
                  <div style={{display:"flex",alignItems:"center",gap:12}}>
                    <div style={{width:30,height:30,borderRadius:"50%",
                      background:"rgba(0,212,255,0.1)",flexShrink:0,
                      animation:"pulse 1.2s ease-in-out infinite"}}/>
                    <div style={{flex:1}}>
                      <div style={{fontSize:15,fontWeight:800,color:C.accent,
                        fontFamily:"monospace"}}>{s.ticker}</div>
                      <div style={{display:"flex",alignItems:"center",gap:6,marginTop:3}}>
                        <div style={{width:12,height:12,border:`2px solid ${C.accent}`,
                          borderTopColor:"transparent",borderRadius:"50%",
                          animation:"spin 0.7s linear infinite"}}/>
                        <span style={{fontSize:11,color:C.muted}}>Scoring…</span>
                      </div>
                    </div>
                  </div>
                </Card>
              );
              return (
              <div key={s.ticker} data-tour={i===0 ? "wl-card" : undefined}>
              <SwipeableWatchlistCard onDelete={()=>removeTicker(s.ticker)}>
              <Card onClick={()=>onSelectTicker(s.ticker)}
                style={{cursor:"pointer", position:"relative",
                  transition:"opacity 0.3s",
                  opacity: bgUpdating[s.ticker] ? 0.65 : 1}}>
                {bgUpdating[s.ticker] && (
                  <div style={{position:"absolute",top:0,left:0,right:0,bottom:0,
                    borderRadius:14,overflow:"hidden",pointerEvents:"none",zIndex:2}}>
                    <div style={{
                      position:"absolute",top:0,left:"-100%",width:"50%",height:"100%",
                      background:"linear-gradient(90deg,transparent,rgba(0,212,255,0.06),transparent)",
                      animation:"shimmer 1.4s ease-in-out infinite",
                    }}/>
                  </div>
                )}
                <div style={{display:"flex",alignItems:"center",gap:9}}>
                  {/* Dual donuts — shrunk from 46→40 (v7.9.54) so the ticker
                      logo + name + recommendation pill row in the middle
                      column has more breathing room and doesn't push the
                      pill behind the right-side price column on narrow
                      viewports. Gap also tightened 12→9. */}
                  <div style={{display:"flex",gap:6,flexShrink:0}}>
                    <ScoreDonut score={s.swingScore} size={40} label="Swing"/>
                    <ScoreDonut score={s.ltScore}    size={40} label="LT"/>
                  </div>
                  <div style={{flex:1,minWidth:0,overflow:"hidden"}}>
                    <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:2,
                      flexWrap:"wrap"}}>
                      <TickerLogo ticker={s.ticker} size={20}/>
                      <span style={{fontSize:16,fontWeight:800,color:C.text}}>{s.ticker}</span>
                      <EarningsSoonBadge soon={earningsSoon} ticker={s.ticker}/>
                      {/* v8.4.296: ONE pill = the tradePref-selected canonical backend label
                          (swing/blended → swingType, lt → ltType). No more dual/divergent pills. */}
                      <SignalPill type={prefSignal(s.swingScore,s.swingType,s.ltScore,s.ltType,tradePref).type} size="sm"/>
                      {s.pullbackFlag ? <PullbackFlag/> : (s.profitTakeFlag && <ExtendedFlag/>)}
                      {s.bounceFlag && <BounceFlag/>}
                      <SetupQualityFlag q={universeData?.[(s.ticker||"").toUpperCase()]?.setupQuality}/>
                      {insiderMap[(s.ticker||"").toUpperCase()] && <InsiderClusterFlag info={insiderMap[(s.ticker||"").toUpperCase()]}/>}
                      {activistMap[(s.ticker||"").toUpperCase()] && <ActivistFilingFlag info={activistMap[(s.ticker||"").toUpperCase()]}/>}
                    </div>
                    {/* company name stays directly under the ticker */}
                    {s.shortName && s.shortName !== s.ticker && (
                      <div style={{fontSize:11,color:C.muted,marginBottom:4,
                        whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
                        {s.shortName}
                      </div>
                    )}
                    {/* v8.4.298: the wider sharp-drop chips get their OWN line, BELOW the name,
                        so they don't spill over the price/sparkline column on narrow tiles. */}
                    {(s.bounceCandidate || s.rotationRisk) && (
                      <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:4}}>
                        {s.bounceCandidate && <DropBounceFlag/>}
                        {s.rotationRisk && <DropRotationFlag/>}
                      </div>
                    )}
                    <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                      {s.rsi != null && <span style={{display:"inline-flex",alignItems:"center",gap:4}}><span style={{fontSize:9,color:"rgba(255,255,255,0.35)"}}>RSI</span><RSIPill rsi={s.rsi} size="sm"/></span>}
                      {s.rsi != null && s.horizonReturn != null && <span style={{fontSize:10,color:C.muted}}>·</span>}
                      {s.horizonReturn != null && (
                        <span style={{fontSize:10,color:s.horizonReturn>=0?C.green:C.red}}>
                          {s.horizonReturn>=0?"+":""}{s.horizonReturn}% past {HORIZONS.find(h=>h.key===horizon)?.label.toLowerCase()}
                        </span>
                      )}
                      {s.sentiment && <><span style={{fontSize:10,color:C.muted}}>·</span><span style={{fontSize:10,color:C.muted}}>{sentimentIcon(s.sentiment)} {s.sentiment}</span></>}
                    </div>
                  </div>
                  <div style={{textAlign:"right",flexShrink:0,
                    display:"flex",flexDirection:"column",alignItems:"flex-end",gap:1}}>
                    {s.price > 0 && (
                      <div style={{fontSize:15,fontWeight:700,fontFamily:"monospace",color:C.text}}>
                        {fmtCurrency(s.price, s.ticker, currency, fxRates)}
                      </div>
                    )}
                    {/* v8.4.40: layout
                        Price → sparkline → % change → $ change → Live/EOD → 1D · 3W tabs
                        % and $ change are scoped to the active timeframe:
                          1D mode → today's change (s.changePct / changeAmt)
                          3W mode → 3-week return from sparkline endpoints
                        Tabs moved below the live/EOD bubble per user request. */}
                    {(() => {
                      const requested = sparklineMode[s.ticker] || "intraday";
                      const intraday  = Array.isArray(s.sparklineIntraday) ? s.sparklineIntraday : [];
                      const daily     = Array.isArray(s.sparkline) ? s.sparkline : [];
                      const hasI = intraday.length >= 2;
                      const hasD = daily.length >= 2;
                      const showIntra = (requested === "intraday" && hasI) || (!hasD);
                      const arr  = showIntra ? intraday : daily;
                      const canToggle = hasI && hasD;
                      // Mode-scoped change values.
                      // 1D: backend-supplied (live during market hours).
                      // 3W: anchor 14-day-old close from daily sparkline to
                      //     current live price so the % matches "what's my
                      //     return if I'd bought 14 sessions ago and held."
                      let pct, amt;
                      if (showIntra) {
                        pct = s.changePct;
                        amt = (s.changeAmt != null && s.changeAmt !== 0)
                          ? s.changeAmt
                          : (s.price && s.changePct != null
                              ? (s.price * s.changePct / (100 + s.changePct))
                              : null);
                      } else if (hasD) {
                        const anchor = daily[0];
                        const tip    = s.price || daily[daily.length-1];
                        if (anchor && tip) {
                          amt = tip - anchor;
                          pct = (amt / anchor) * 100;
                        }
                      }
                      const pctColor = (pct == null) ? C.muted
                        : (pct >= 0 ? C.green : C.red);
                      return (
                        <>
                          {(hasI || hasD) && (
                            <div style={{marginTop:2,marginBottom:1,height:14,
                              display:"flex",alignItems:"center"}}>
                              <Sparkline key={showIntra ? "i" : "d"} data={arr}
                                up={pct == null ? null : pct >= 0}/>
                            </div>
                          )}
                          {pct != null && (
                            <div style={{fontSize:11,color:pctColor,fontFamily:"monospace"}}>
                              {pct >= 0 ? "+" : ""}{pct.toFixed(2)}%
                            </div>
                          )}
                          {(amt != null && isFinite(amt)) && (
                            <div style={{fontSize:10,fontFamily:"monospace",color:pctColor}}>
                              {amt >= 0 ? "+" : ""}{amt >= 0 ? "$" : "-$"}{Math.abs(amt).toFixed(2)}
                            </div>
                          )}
                          {s._failed ? (
                            <div style={{fontSize:9,marginTop:2,color:C.yellow}}>
                              ⚠ tap to retry
                            </div>
                          ) : (
                            <div style={{fontSize:9,marginTop:2}}>
                              <ListQuoteDot isLive={s.isLive}/>
                            </div>
                          )}
                          {(hasI || hasD) && (
                            <div
                              onClick={canToggle ? (e) => { e.stopPropagation(); toggleSparklineMode(s.ticker); } : undefined}
                              title={canToggle ? "Tap to switch between today (1D) and the last 3 weeks" : ""}
                              style={{marginTop:3,
                                display:"inline-flex",alignItems:"center",gap:3,
                                fontSize:8,letterSpacing:"0.06em",
                                fontFamily:"monospace",lineHeight:1,userSelect:"none",
                                cursor: canToggle ? "pointer" : "default"}}>
                              {hasI && (
                                <span style={{
                                  color: showIntra ? C.text : "rgba(255,255,255,0.28)",
                                  fontWeight: showIntra ? 800 : 600}}>1D</span>
                              )}
                              {hasI && hasD && (
                                <span style={{color:"rgba(255,255,255,0.18)"}}>·</span>
                              )}
                              {hasD && (
                                <span style={{
                                  color: !showIntra ? C.text : "rgba(255,255,255,0.28)",
                                  fontWeight: !showIntra ? 800 : 600}}>3W</span>
                              )}
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>
              </Card>
              </SwipeableWatchlistCard>
              </div>
            );
            })}
          </div>
    </div>
  );
}

// --- Discover screen ---------------------------------------------------------
// --- Error boundary -- catches render crashes and shows fallback --------------
class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { crashed: false, error: null }; }
  static getDerivedStateFromError(e) { return { crashed: true, error: e }; }
  componentDidCatch(e, info) { console.error("Render crash:", e, info); }
  render() {
    if (this.state.crashed) return (
      <div style={{padding:"40px 24px",textAlign:"center"}}>
        <div style={{fontSize:36,marginBottom:12}}>⚠️</div>
        <div style={{fontSize:15,fontWeight:600,color:"#ff5252",marginBottom:8}}>
          Something went wrong
        </div>
        <div style={{fontSize:12,color:"rgba(255,255,255,0.4)",marginBottom:20,lineHeight:1.6}}>
          {this.state.error?.message || "An unexpected error occurred."}
        </div>
        <button
          onClick={() => this.setState({crashed:false, error:null})}
          style={{padding:"10px 24px",borderRadius:10,border:"none",
            background:"#00d4ff",color:"#080c14",fontSize:14,
            fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
          Try again
        </button>
      </div>
    );
    return this.props.children;
  }
}

// --- Curated lists for quick discovery ---------------------------------------
// ── Scoring Universe ────────────────────────────────────────────────────────
// Tiered: Tier 1 scored first (covers most watchlists), Tier 2 in background.
// This is the single source of truth for all tabs — Discovery, Watchlist,
// Optimizer, and Builder all read from the same scored universe cache.
const UNIVERSE_T1 = [
  // Mega-cap US + top watchlist candidates (~40)
  "AAPL","MSFT","NVDA","AMZN","GOOGL","META","TSLA","AVGO","JPM","V",
  "LLY","UNH","XOM","MA","COST","NFLX","AMD","CRM","ORCL","ADBE",
  "NOW","PLTR","MRVL","PANW","CRWD","APP","ANET","ARM","TSM","QCOM",
  "BAC","GS","MS","AXP","BLK","WFC","SCHW","JNJ","ABBV","MRK",
  // Key semiconductors often searched
  "MU","INTC","TXN","LRCX","AMAT","KLAC","ON","MCHP",
];
const UNIVERSE_T2 = [
  // Healthcare
  "PFE","TMO","ABT","MDT","ISRG","REGN","VRTX","GILD",
  // Energy
  "CVX","COP","EOG","SLB","OXY","PSX","VLO","MPC",
  // Consumer
  "HD","MCD","NKE","SBUX","TGT","WMT","PG","KO","PEP",
  // Communication
  "DIS","CMCSA","T","VZ","TMUS",
  // Financials
  "BRK-B","SPGI","MCO","ICE","CME","PYPL","SQ","COIN",
  // Industrials
  "CAT","DE","HON","RTX","LMT","GE","UPS","FDX",
  // AI/Growth
  "SMCI","RKLB","AXON","DKNG","MSTR","HOOD","SOFI",
  // TSX 60
  "RY.TO","TD.TO","ENB.TO","BN.TO","CNR.TO","CP.TO","BMO.TO",
  "SHOP.TO","SU.TO","MFC.TO","ABX.TO","ATD.TO","CM.TO","AEM.TO","IFC.TO",
  "TRP.TO","CNQ.TO","CVE.TO","FFH.TO","WPM.TO","FM.TO","FTS.TO","QSR.TO",
  // Materials/Mining
  "NEM","GOLD","FCX","HL","NUE","AA",
  // ETFs
  "SPY","QQQ","IWM","GLD","SLV","TLT","XLK","XLF","XLE","XLV","XLI",
  "VTI","VOO","SCHD","JEPI","TQQQ",
];
// Specialty — scored on-demand when user browses these categories
const UNIVERSE_T3 = [
  "MRNA","BNTX","BIIB","BMRN","ALNY",       // Biotech
  "HIMS","CELH","DUOL","CAVA","BROS",        // Small-cap growth
  "MARA","RIOT","CLSK","HUT","CIFR",         // Crypto miners
  "PLD","AMT","EQIX","CCI","WELL","SPG",     // REITs
  "IONQ","QBTS","ASTS",                       // Quantum/Space
];
const SCORING_UNIVERSE = [...new Set([...UNIVERSE_T1, ...UNIVERSE_T2])];
const ALL_UNIVERSE     = [...new Set([...SCORING_UNIVERSE, ...UNIVERSE_T3])];

// Sector hints for common tickers — mirrors backend _KNOWN_SECTORS
// Used by Optimizer to send correct sector for non-universe holdings
const SECTOR_HINTS = {
  // Technology / Semiconductors
  AAPL:"Technology",MSFT:"Technology",NVDA:"Technology",
  AVGO:"Technology",AMD:"Technology",CRM:"Technology",
  ORCL:"Technology",ADBE:"Technology",NOW:"Technology",PLTR:"Technology",
  MRVL:"Technology",PANW:"Technology",CRWD:"Technology",APP:"Technology",
  ANET:"Technology",ARM:"Technology",TSM:"Technology",QCOM:"Technology",
  MU:"Technology",INTC:"Technology",TXN:"Technology",LRCX:"Technology",
  AMAT:"Technology",KLAC:"Technology",ON:"Technology",MCHP:"Technology",
  SMCI:"Technology",ASML:"Technology",INTU:"Technology",SNPS:"Technology",
  CDNS:"Technology",FTNT:"Technology",ZBRA:"Technology",KEYS:"Technology",
  VRT:"Industrials",CIEN:"Technology",FLEX:"Technology",
  IONQ:"Technology",QBTS:"Technology",ASTS:"Technology",
  RGTI:"Technology",QUBT:"Technology",KULR:"Technology",
  FBTC:"ETF",IBIT:"ETF",ARKK:"ETF",ARKG:"ETF",
  ACHR:"Industrials",JOBY:"Industrials",
  TWLO:"Technology",DDOG:"Technology",SNOW:"Technology",MDB:"Technology",
  ESTC:"Technology",ZS:"Technology",OKTA:"Technology",NET:"Technology",
  HUBS:"Technology",TEAM:"Technology",WDAY:"Technology",VEEV:"Technology",
  SHOP:"Technology","SHOP.TO":"Technology",
  // Communication Services
  NFLX:"Communication Services",DIS:"Communication Services",
  CMCSA:"Communication Services",T:"Communication Services",
  VZ:"Communication Services",TMUS:"Communication Services",
  GOOG:"Communication Services",GOOGL:"Communication Services",
  META:"Communication Services",
  SNAP:"Communication Services",PINS:"Communication Services",
  RDDT:"Communication Services",TTD:"Communication Services",
  // Consumer
  AMZN:"Consumer Cyclical",TSLA:"Consumer Cyclical",HD:"Consumer Cyclical",
  MCD:"Consumer Cyclical",NKE:"Consumer Cyclical",SBUX:"Consumer Cyclical",
  TGT:"Consumer Cyclical",BKNG:"Consumer Cyclical",ABNB:"Consumer Cyclical",
  UBER:"Consumer Cyclical",LYFT:"Consumer Cyclical",ETSY:"Consumer Cyclical",
  DKNG:"Consumer Cyclical",CAVA:"Consumer Cyclical",BROS:"Consumer Cyclical",
  COST:"Consumer Defensive",WMT:"Consumer Defensive",PG:"Consumer Defensive",
  KO:"Consumer Defensive",PEP:"Consumer Defensive",MDLZ:"Consumer Defensive",
  CL:"Consumer Defensive",GIS:"Consumer Defensive",
  // Financials
  JPM:"Financials",BAC:"Financials",GS:"Financials",MS:"Financials",
  WFC:"Financials",C:"Financials",USB:"Financials",PNC:"Financials",
  BLK:"Financial Services",SCHW:"Financial Services",
  V:"Financial Services",MA:"Financial Services",AXP:"Financial Services",
  PYPL:"Financial Services",SQ:"Financial Services",
  COIN:"Financial Services",HOOD:"Financial Services",SOFI:"Financial Services",
  SPGI:"Financial Services",MCO:"Financial Services",
  ICE:"Financial Services",CME:"Financial Services",
  "RY.TO":"Financials","TD.TO":"Financials","BMO.TO":"Financials",
  "CM.TO":"Financials","MFC.TO":"Financials","IFC.TO":"Financials",
  "FFH.TO":"Financials","BN.TO":"Financial Services",
  // Healthcare
  LLY:"Healthcare",UNH:"Healthcare",JNJ:"Healthcare",ABBV:"Healthcare",
  MRK:"Healthcare",PFE:"Healthcare",TMO:"Healthcare",ABT:"Healthcare",
  MDT:"Healthcare",ISRG:"Healthcare",REGN:"Healthcare",VRTX:"Healthcare",
  GILD:"Healthcare",HIMS:"Healthcare",MRNA:"Healthcare",BNTX:"Healthcare",
  DXCM:"Healthcare",ALGN:"Healthcare",EW:"Healthcare",ZBH:"Healthcare",
  IDXX:"Healthcare",MTD:"Healthcare",BIO:"Healthcare",
  AMGN:"Healthcare",BIIB:"Healthcare",BMRN:"Healthcare",ALNY:"Healthcare",
  INCY:"Healthcare",SRPT:"Healthcare",EXAS:"Healthcare",
  // Energy
  XOM:"Energy",CVX:"Energy",COP:"Energy",EOG:"Energy",SLB:"Energy",
  OXY:"Energy",PSX:"Energy",VLO:"Energy",MPC:"Energy",HAL:"Energy",
  DVN:"Energy",FANG:"Energy",HES:"Energy",MRO:"Energy",APA:"Energy",
  BKR:"Energy",NOG:"Energy",SM:"Energy",MGY:"Energy",VTLE:"Energy",
  CTRA:"Energy",OVV:"Energy",
  "ENB.TO":"Energy","TRP.TO":"Energy","SU.TO":"Energy","CNQ.TO":"Energy",
  "CVE.TO":"Energy","ARX.TO":"Energy","PPL.TO":"Energy","PKI.TO":"Energy",
  "TVE.TO":"Energy","PEY.TO":"Energy","VET.TO":"Energy",
  // Industrials
  CAT:"Industrials",DE:"Industrials",HON:"Industrials",RTX:"Industrials",
  LMT:"Industrials",GE:"Industrials",UPS:"Industrials",FDX:"Industrials",
  BA:"Industrials",NOC:"Industrials",GD:"Industrials",LHX:"Industrials",
  RKLB:"Industrials",AXON:"Industrials",CARR:"Industrials",OTIS:"Industrials",
  "CNR.TO":"Industrials","CP.TO":"Industrials",
  // Materials / Mining
  NEM:"Materials",GOLD:"Materials",FCX:"Materials",NUE:"Materials",AA:"Materials",
  PAAS:"Materials",HL:"Materials",AG:"Materials",WPM:"Materials",
  FNV:"Materials",OR:"Materials",RGLD:"Materials",
  CLF:"Materials",STLD:"Materials",RS:"Materials",EXK:"Materials",
  "AEM.TO":"Materials","ABX.TO":"Materials","WPM.TO":"Materials",
  "FM.TO":"Materials","K.TO":"Materials","NGT.TO":"Materials",
  "PAAS.TO":"Materials","IMG.TO":"Materials","MAG.TO":"Materials",
  // Real Estate
  PLD:"Real Estate",AMT:"Real Estate",EQIX:"Real Estate",
  CCI:"Real Estate",WELL:"Real Estate",SPG:"Real Estate",
  O:"Real Estate",VICI:"Real Estate",AVB:"Real Estate",
  EQR:"Real Estate",PSA:"Real Estate",EXR:"Real Estate",
  WY:"Real Estate",ARE:"Real Estate",BXP:"Real Estate",
  VTR:"Real Estate",KIM:"Real Estate",REG:"Real Estate",
  SUI:"Real Estate",ELS:"Real Estate",CPT:"Real Estate",
  PEAK:"Real Estate",DRE:"Real Estate",UDR:"Real Estate",IRT:"Real Estate",
  // Utilities
  NEE:"Utilities",DUK:"Utilities",SO:"Utilities",D:"Utilities",
  AEP:"Utilities",EXC:"Utilities",XEL:"Utilities",
  SRE:"Utilities",WEC:"Utilities",ES:"Utilities",ETR:"Utilities",
  FE:"Utilities",CMS:"Utilities",NI:"Utilities",PNW:"Utilities",
  AES:"Utilities",NRG:"Utilities",VST:"Utilities",CEG:"Utilities",
  "FTS.TO":"Utilities","EMA.TO":"Utilities","AQN.TO":"Utilities",
  "H.TO":"Utilities","BEP-UN.TO":"Utilities",
  // Canadian
  "ATD.TO":"Consumer Cyclical","QSR.TO":"Consumer Cyclical",
  "POW.TO":"Financial Services","L.TO":"Consumer Defensive",
  // ETFs
  SPY:"ETF",QQQ:"ETF",IWM:"ETF",GLD:"ETF",SLV:"ETF",TLT:"ETF",
  XLK:"ETF",XLF:"ETF",XLE:"ETF",XLV:"ETF",XLI:"ETF",
  VTI:"ETF",VOO:"ETF",SCHD:"ETF",JEPI:"ETF",TQQQ:"ETF",
};

// ─── Trending Carousel (StockTwits) ──────────────────────────────────────────
// Muted trending-pulse icon — matches SwingIcon / LTIcon style
function TrendingIcon({ size=12, color="currentColor" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      style={{display:"inline-block",verticalAlign:"middle",flexShrink:0}}>
      <path d="M2 18 L7 10 L11 14 L16 6 L22 2M22 2h-5M22 2v5"
        stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function TrendingCarousel({ token, horizon, profile, session, onSelectTicker }) {
  const [items,   setItems]   = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [active,  setActive]  = React.useState(0);
  const timerRef  = React.useRef(null);
  const dragRef   = React.useRef({ startX: null, dragging: false });
  const VISIBLE   = 3;

  // v8.4.240: the backend hydrates LIVE prices on every /trending call, but the
  // carousel used to fetch only once on mount — so at the open it kept showing the
  // pre-open snapshot (while the watchlist polled live). Refetch on an interval during
  // active sessions so trending prices track the open like everything else; keep the
  // last good list if a refresh comes back empty (transient open-bell miss).
  React.useEffect(() => {
    if (!token) return;
    let alive = true;
    const fetchTrending = () => {
      apiFetch(`/trending?horizon=${horizon||"6m"}&profile=${profile||"moderate"}`, token)
        .then(d => {
          if (!alive) return;
          const t = d?.tickers || [];
          if (t.length) setItems(t);   // v8.4.250: keep last good; NEVER blank on empty
          setLoading(false);
        })
        .catch(() => { if (alive) setLoading(false); });
    };
    fetchTrending();
    // v8.4.250: refresh ALWAYS (was gated to active sessions), so a cold-cache/empty
    // first load on reopen — including when markets are CLOSED, e.g. right after a
    // redeploy — self-heals instead of the carousel vanishing with no recovery.
    const iv = setInterval(fetchTrending, 90 * 1000);
    return () => { alive = false; clearInterval(iv); };
  }, [token, horizon, profile]);

  const maxActive = Math.max(0, items.length - VISIBLE);

  const startAutoAdvance = React.useCallback(() => {
    clearInterval(timerRef.current);
    if (items.length <= VISIBLE) return;
    timerRef.current = setInterval(() => {
      setActive(a => a >= maxActive ? 0 : a + 1);
    }, 3500);
  }, [items.length, maxActive]);

  React.useEffect(() => {
    startAutoAdvance();
    return () => clearInterval(timerRef.current);
  }, [startAutoAdvance]);

  const nudge = React.useCallback((dir) => {
    setActive(a => Math.max(0, Math.min(maxActive, a + dir)));
    // Auto-advance interval keeps running uninterrupted
  }, [maxActive]);

  // Touch handlers
  const onTouchStart = e => { dragRef.current.startX = e.touches[0].clientX; };
  const onTouchEnd   = e => {
    if (dragRef.current.startX == null) return;
    const delta = e.changedTouches[0].clientX - dragRef.current.startX;
    dragRef.current.startX = null;
    if (Math.abs(delta) > 40) nudge(delta < 0 ? 1 : -1);
  };
  // Mouse drag handlers (desktop)
  const onMouseDown = e => { dragRef.current = { startX: e.clientX, dragging: true }; };
  const onMouseUp   = e => {
    if (!dragRef.current.dragging) return;
    const delta = e.clientX - dragRef.current.startX;
    dragRef.current = { startX: null, dragging: false };
    if (Math.abs(delta) > 40) nudge(delta < 0 ? 1 : -1);
  };

  if (loading || items.length === 0) return null;

  const visible = items.slice(active, active + VISIBLE);

  return (
    <div style={{background:C.surface,borderRadius:14,padding:"12px 14px",
      border:`1px solid ${C.border}`,marginBottom:14}}>

      {/* Header — matches "◆ Top Swing Scores" style */}
      <div style={{display:"flex",justifyContent:"space-between",
        alignItems:"center",marginBottom:10}}>
        <div style={{display:"flex",alignItems:"center",gap:5,
          fontSize:10,fontWeight:700,color:C.muted,
          letterSpacing:"0.1em",textTransform:"uppercase"}}>
          <TrendingIcon size={11} color={C.muted}/>
          Trending
        </div>
        <span style={{fontSize:9,color:C.muted}}>
          {active + 1}–{Math.min(active + VISIBLE, items.length)} of {items.length}
        </span>
      </div>

      {/* Cards — touch + mouse drag to swipe */}
      <div style={{display:"flex",gap:8,userSelect:"none",cursor:"grab"}}
        onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}
        onMouseDown={onMouseDown}   onMouseUp={onMouseUp}
        onMouseLeave={e => {
          if (dragRef.current.dragging) {
            dragRef.current = { startX: null, dragging: false };
          }
        }}>
        {visible.map(item => (
          <button key={item.ticker}
            onClick={e => {
              // Suppress click when the user was dragging
              if (Math.abs((dragRef.current.startX || e.clientX) - e.clientX) > 5) return;
              onSelectTicker(item.ticker);
            }}
            style={{flex:1,background:C.card,borderRadius:10,
              padding:"10px 6px 8px",border:`1px solid ${C.border}`,
              cursor:"pointer",fontFamily:"inherit",minWidth:0,
              display:"flex",flexDirection:"column",alignItems:"center",
              transition:"border-color 0.15s",position:"relative"}}
            onMouseEnter={e=>e.currentTarget.style.borderColor="rgba(0,212,255,0.3)"}
            onMouseLeave={e=>e.currentTarget.style.borderColor=C.border}>

            {/* Rank badge — top-right */}
            <span style={{position:"absolute",top:5,right:6,fontSize:8,
              color:C.muted,fontWeight:700,fontFamily:"monospace"}}>
              #{item.rank}
            </span>

            {/* Logo */}
            <div style={{marginBottom:5,marginTop:2}}>
              <TickerLogo ticker={item.ticker} size={28}/>
            </div>

            {/* Ticker */}
            <div style={{fontSize:12,fontWeight:800,color:C.text,
              fontFamily:"monospace",letterSpacing:"-0.01em"}}>
              {item.ticker}
            </div>

            {/* Company name */}
            {item.shortName && item.shortName !== item.ticker && (
              <div style={{fontSize:9,color:C.muted,marginTop:2,
                textAlign:"center",width:"100%",
                overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",
                paddingLeft:2,paddingRight:2}}>
                {item.shortName}
              </div>
            )}

            {/* Price */}
            {item.price != null && (
              <div style={{fontSize:11,fontWeight:700,fontFamily:"monospace",
                color:C.text,marginTop:5}}>
                ${item.price.toFixed(2)}
              </div>
            )}

            {/* Change */}
            {item.changePct != null && (
              <div style={{fontSize:11,fontWeight:700,fontFamily:"monospace",
                color:item.changePct>=0?C.green:C.red}}>
                {item.changePct>=0?"+":""}{item.changePct.toFixed(2)}%
              </div>
            )}

            {/* Swing + LT scores side by side — SwingIcon/LTIcon glyphs */}
            <div style={{marginTop:5,display:"flex",alignItems:"center",
              gap:8,justifyContent:"center"}}>
              <div style={{display:"flex",alignItems:"center",gap:2}}>
                <SwingIcon size={10} color={C.muted}/>
                <span style={{fontSize:10,fontFamily:"monospace",fontWeight:700,
                  color:item.swingScore>=70?C.green
                       :item.swingScore>=45?C.muted
                       :item.swingScore>0?"rgba(255,82,82,0.75)"
                       :"rgba(255,255,255,0.2)"}}>
                  {item.swingScore>0?Math.round(item.swingScore):"—"}
                </span>
              </div>
              <div style={{display:"flex",alignItems:"center",gap:2}}>
                <LTIcon size={10} color={C.muted}/>
                <span style={{fontSize:10,fontFamily:"monospace",fontWeight:700,
                  color:item.ltScore>=70?C.green
                       :item.ltScore>=45?C.muted
                       :item.ltScore>0?"rgba(255,82,82,0.75)"
                       :"rgba(255,255,255,0.2)"}}>
                  {item.ltScore>0?Math.round(item.ltScore):"—"}
                </span>
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Progress bar */}
      {maxActive > 0 && (
        <div style={{marginTop:10,height:2,borderRadius:1,
          background:"rgba(255,255,255,0.07)"}}>
          <div style={{height:"100%",borderRadius:1,background:C.muted,
            width:`${((active / maxActive) * 100).toFixed(1)}%`,
            transition:"width 0.3s ease"}}/>
        </div>
      )}
    </div>
  );
}

const CURATED = {
  "S&P 100":    ["AAPL","MSFT","NVDA","AMZN","GOOGL","META","TSLA","AVGO","JPM",
                 "V","LLY","XOM","MA","COST","NFLX"],
  "TSX 60":     ["RY.TO","TD.TO","ENB.TO","BN.TO","CNR.TO","CP.TO","BMO.TO",
                 "SHOP.TO","SU.TO","MFC.TO","ABX.TO","ATD.TO","CM.TO","AEM.TO","IFC.TO"],
  "Tech":       ["AAPL","MSFT","NVDA","AMD","AVGO","TSM","ORCL","CRM","NOW","PLTR",
                 "MRVL","ANET","PANW","CRWD","APP","MU","INTC","TXN","LRCX"],
  "AI & Chips": ["NVDA","AMD","AVGO","MRVL","ARM","TSM","AMAT","LRCX","ASML","SMCI",
                 "MU","INTC","TXN","KLAC","ON","IONQ","QBTS","ASTS","PLTR","AI"],
  "Finance":    ["JPM","BAC","GS","V","MA","AXP","BLK","WFC",
                 "RY.TO","TD.TO","BMO.TO","CM.TO","MFC.TO","IFC.TO","FFH.TO"],
  "Energy":     ["XOM","CVX","COP","EOG","SLB","OXY","PSX","VLO","MPC","HAL",
                 "DVN","FANG","HES","MRO","APA","NOG","SM","MGY","VTLE",
                 "ENB.TO","CNQ.TO","SU.TO","CVE.TO","TRP.TO","ARX.TO","PPL.TO",
                 "TVE.TO","PEY.TO","VET.TO"],
  "ETFs":       ["SPY","QQQ","IWM","GLD","SLV","TLT","XLK","XLF","XLE",
                 "XLV","VTI","VOO","SCHD","JEPI","TQQQ"],
  "Momentum":   ["NVDA","META","AMZN","TSLA","PLTR","COIN","APP","MSTR","CRWD",
                 "PANW","AXON","SMCI","DKNG","RKLB","HOOD"],
  "Materials":  ["NEM","GOLD","FCX","AA","NUE","PAAS","HL","AG","WPM",
                 "FNV","OR","RGLD","CLF","STLD","RS",
                 "AEM.TO","ABX.TO","WPM.TO","FM.TO","K.TO","PAAS.TO","IMG.TO"],
  "Healthcare": ["LLY","UNH","JNJ","ABBV","MRK","PFE","TMO","ABT",
                 "MDT","ISRG","REGN","VRTX","GILD","AMGN","BIIB",
                 "DXCM","EW","HIMS","MRNA","ZBH"],
  "Industrials":["CAT","DE","HON","RTX","LMT","GE","UPS","FDX","BA",
                 "NOC","GD","LHX","RKLB","AXON","CARR","OTIS",
                 "CNR.TO","CP.TO"],
  "Utilities":  ["NEE","DUK","SO","D","AEP","SRE","EXC","XEL","WEC","ES",
                 "ETR","FE","CMS","NI","PNW","AES","NRG","VST","CEG",
                 "FTS.TO","H.TO","EMA.TO","AQN.TO","BEP-UN.TO"],
  "Real Estate":["PLD","AMT","EQIX","CCI","WELL","SPG","O","VICI","AVB",
                 "EQR","DRE","PSA","EXR","WY","ARE","BXP","VTR","PEAK",
                 "KIM","REG","SUI","ELS","CPT","UDR","IRT"],
};

function DiscoverScreen({ user, token, onSelectTicker, onAuthError, globalHorizon, watchlist, setWatchlist, lastScanTime, universeData, upsertUniverse, tradePref, currency, fxRates, earningsSoon, visible=true }) {
  const [market,      setMarket]    = useState("US");   // US | CA
  const [query,       setQuery]     = useState("");
  const [list,        setList]      = useState("S&P 100");
  const [results,     setResults]   = useState([]);
  const activistMap = useActivistFilings(results.map(r => r && r.ticker), token);   // v8.4.419 — 13D chip on Discovery rows
  const insiderMap  = useInsiderClusters(results.map(r => r && r.ticker), token);   // v8.4.420 — insider buy-cluster chip
  // v8.4.447 — list rows are built once from the ~30-min scored cache, so a row could sit on a stale/EOD price all session (MU on the
  // Speculation list). While the market is open, refresh the visible rows' prices every minute from the delayed batch (rt=0: no Finnhub
  // budget spent on list rows). Overlay price/change only — scores, chips and sparklines are untouched.
  const _rowKey = results.slice(0, 40).map(r => r && r.ticker).filter(Boolean).join(",");
  useEffect(() => {
    if (!token || !visible || !_rowKey) return;
    let stop = false;
    const tick = async () => {
      if (!isMarketOpen() || (typeof document !== "undefined" && document.visibilityState === "hidden")) return;
      try {
        const r = await apiFetch(`/quotes/live?tickers=${encodeURIComponent(_rowKey)}&rt=0`, token);
        const qs = r?.quotes || {};
        if (stop) return;
        setResults(prev => prev.map(row => {
          const q = row && qs[String(row.ticker).toUpperCase()];
          return (q && q.price) ? { ...row, price: q.price, changePct: q.changePct ?? row.changePct, changeAmt: null, isLive: true } : row;
        }));
      } catch (_) {}
    };
    tick();
    const id = setInterval(tick, 60000);
    return () => { stop = true; clearInterval(id); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, visible, _rowKey]);
  const [heatmapKey,  setHeatmapKey]= useState(0); // increments to clear heatmap selection
  const [heatmapView, setHeatmapView] = useState("sectors"); // v8.4.309: "sectors" | "movers"
  const [scanning,    setScanning]  = useState(false);
  const [progress,    setProgress]  = useState({done:0, total:0, current:""});
  // v8.4.35: per-ticker sparkline mode (same UX as Watchlist).
  const [sparklineMode, setSparklineMode] = useState({});
  const toggleSparklineMode = (t) => setSparklineMode(p => {
    const cur = p[t] || "intraday";
    return { ...p, [t]: cur === "intraday" ? "daily" : "intraday" };
  });

  // v8.4.41: background intraday hydration. Discovery's curated lists and
  // cache-hit scoreOne path return rows with only the daily sparkline (from
  // /universe). Without this, the 1D tab never appears on Discovery rows.
  // Whenever the results array changes, find rows lacking intraday and fire
  // parallel /analyze in the background to populate it. Upsert into
  // universeData + patch the matching row so the 1D tab lights up. */
  const [error,       setError]     = useState("");
  const horizon = globalHorizon || "6m";  // always use global from landing
  const [searched,    setSearched]  = useState(false);
  const [lastScored,  setLastScored] = useState(0);
  // v8.3.7: removed the "scores may be stale" banner. With the centralized
  // universeData architecture (v8.1.5 + v8.3.x), Discovery / Watchlist /
  // Trending / Speculation / Zombie / quick-lists all read the same Redis
  // universe cache via /universe (refreshed every 30 min by Celery beat
  // and hydrated with live prices on every call). Showing a "stale" banner
  // here implied inconsistency with the Watchlist; the architecture no
  // longer has that inconsistency.

  // v8.4.41 / v8.4.43: background intraday hydration.
  // Persistent set of tickers we've ALREADY tried to hydrate so a hydration
  // success doesn't bounce the effect into re-fetching forever. Cleared
  // implicitly when ScannerScreen remounts.
  const hydrationAttemptedRef = React.useRef(new Set());
  // Stable string key derived from the ticker list so the effect re-fires
  // when the user switches lists (e.g. S&P 100 → NASDAQ 100) even if the
  // length matches.
  const _tickerListKey = results.map(r => r?.ticker || "").join("|");
  useEffect(() => {
    if (!results.length || !token) return;
    const attempted = hydrationAttemptedRef.current;
    const needsIntra = results
      .filter(r => r && r.ticker
        && !attempted.has(r.ticker)
        && !(Array.isArray(r.sparklineIntraday) && r.sparklineIntraday.length >= 2))
      .map(r => r.ticker);
    if (!needsIntra.length) return;
    let cancelled = false;
    (async () => {
      const CONCURRENCY = 4;
      let cursor = 0;
      const worker = async () => {
        while (!cancelled && cursor < needsIntra.length) {
          const my = cursor++;
          const t = needsIntra[my];
          // v8.4.79: mark attempted per-ticker just before its own fetch.
          // Pre-marking all tickers before any fetch (old behaviour) meant
          // a ticker that returned empty intraday was stuck in attempted
          // forever and never retried. Now we delete on empty/error so the
          // next results change triggers a retry.
          attempted.add(t);
          try {
            const dd = await apiFetch(`/analyze/${t}?horizon=${horizon}`, token);
            if (cancelled || !dd) { attempted.delete(t); continue; }
            const _intra = extractIntradaySparkline(dd.candles_intraday_15m, dd.candles_intraday);
            const _daily = (Array.isArray(dd.sparkline) && dd.sparkline.length >= 2)
              ? dd.sparkline
              : (Array.isArray(dd.candles) ? dd.candles.slice(-14)
                  .map(c => typeof c === "object" ? c.c : c)
                  .filter(v => typeof v === "number" && !isNaN(v)) : []);
            if (_intra.length < 2) {
              attempted.delete(t);  // empty intraday — allow retry next render
            }
            if (upsertUniverse) {
              upsertUniverse(t, {
                ticker: t,
                sparkline: _daily,
                sparklineIntraday: _intra,
                scoredAt: Date.now(),
              });
            }
            // Patch the matching results row so 1D appears immediately.
            setResults(prev => prev.map(r =>
              r && r.ticker === t
                ? { ...r,
                    sparkline:         _daily.length >= 2 ? _daily : r.sparkline,
                    sparklineIntraday: _intra.length >= 2 ? _intra : r.sparklineIntraday }
                : r
            ));
          } catch (e) { attempted.delete(t); /* allow retry on error */ }
        }
      };
      await Promise.all(Array.from({length: CONCURRENCY}, worker));
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_tickerListKey, token]);

  const profile = PROFILES.find(p => p.key === user.risk_profile) || PROFILES[1];

  const [addingToWl, setAddingToWl] = React.useState({});
  const addToWatchlist = async (ticker) => {
    if (!setWatchlist || (watchlist||[]).includes(ticker)) return;
    setAddingToWl(p=>({...p,[ticker]:true}));
    try {
      // v8.4.88: atomic add
      const d = await apiFetch("/watchlist/add", token, {method:"POST", body:JSON.stringify({ticker})});
      if (d?.tickers) setWatchlist(d.tickers);
    } catch(e){}
    finally { setAddingToWl(p=>({...p,[ticker]:false})); }
  };

  // Score tickers — checks universeData cache first, only fetches missing/stale
  const scoreTickers = async (tickers) => {
    setScanning(true); setError(""); setResults([]);
    setProgress({done:0, total:tickers.length, current:""});
    const out = [];
    const BATCH = 4;

    const scoreOne = async (ticker) => {
      setProgress(p => ({...p, current:ticker}));
      try {
        // Use the shared universeData cache when it has a complete entry (scores
        // + sparkline). The background /universe refresh pre-populates all 514
        // tickers with scores and prices but NO intraday candles, so uc.sparkline
        // is empty on those skeleton entries. Requiring a sparkline here ensures
        // we fall through to /analyze on first Discovery visit to compute one.
        // After upsertUniverse writes the sparkline back, subsequent visits take
        // the fast path. Scores stay consistent because /analyze also reads from
        // the Redis universe cache first.
        const uc = universeData?.[ticker.toUpperCase()];
        // v8.4.30: only take the universe-cache fast path when the price is
        // FRESH. /universe is fetched with hydrate_prices=false, so uc.price
        // reflects the last Celery scoring cycle (up to 30 min old). After
        // v8.4.29 made sparklines persist through /analyze, every cached entry
        // had a sparkline → this path served stale EOD-looking prices during
        // market hours. During an open session, fall through to /analyze when
        // the cache is >5 min stale so prices stay live. When closed, the
        // cached price IS the latest available — keep the fast path.
        const STALE_MS = 5 * 60 * 1000;
        const _ucFresh = uc && (!isMarketOpen() ||
                                (Date.now() - (uc.scoredAt || 0)) < STALE_MS);
        if (uc && Array.isArray(uc.sparkline) && uc.sparkline.length >= 2 && _ucFresh) {
          return {
            ticker: uc.ticker || ticker.toUpperCase(),
            shortName:uc.shortName||ticker,
            price:uc.price||0, changePct:uc.changePct||0, isLive:uc.isLive||false,
            swingScore:uc.swingScore ?? null, swingLabel:uc.swingLabel||"",
            swingType:uc.swingType||"watch", ltScore:uc.ltScore ?? null,
            ltLabel:uc.ltLabel||"", ltType:uc.ltType||"watch",
            horizonReturn:uc.horizonReturn??null, beta:uc.beta||1.0,
            rsi:uc.rsi||50, sentiment:uc.sentiment||"",
            sector:(uc.sector && uc.sector !== "Other") ? uc.sector : (SECTOR_HINTS[ticker.toUpperCase()] || ""), fromCache:true,
            rsRating:  uc.rsRating  ?? null,
            isLeader:  uc.isLeader  || false,
            profitTakeFlag: uc.profitTakeFlag || false,
            pullbackFlag: uc.pullbackFlag || false,
            bounceFlag: uc.bounceFlag || false,
            bounceCandidate: uc.bounceCandidate || false,
            rotationRisk: uc.rotationRisk || false,
            patterns:  Array.isArray(uc.patterns) ? uc.patterns : [],
            sparkline: uc.sparkline,
            sparklineIntraday: uc.sparklineIntraday || [],
          };
        }
        // Auto-retry: same pattern as StockScreen and AI agents (v8.3.31).
        // A cold backend cache or transient network blip causes a silent null
        // return, leaving the user with a blank screen that requires a second
        // Enter press. Three attempts (0 / 1s / 2.5s) cover the common cold-
        // cache case without adding noticeable latency on success.
        const d = await _aiAgentFetch(
          () => apiFetch(`/analyze/${ticker}?horizon=${horizon}`, token),
          { maxAttempts: 3, delayMs: 1000 }
        );
        // v8.4.37: capture BOTH the 14-day daily series and today's intraday
        // 15-min closes — same dual-source pattern the Watchlist uses so the
        // sparkline tap-to-toggle works identically on both screens. Prefer
        // backend's d.sparkline (always 14 closes) over slicing d.candles —
        // the latter is empty on /analyze stale-cache fallback responses.
        const _sparkDaily = (Array.isArray(d.sparkline) && d.sparkline.length >= 2)
          ? d.sparkline
          : (d.candles || []).slice(-14)
              .map(c => (typeof c === "object" ? c.c : c))
              .filter(v => typeof v === "number" && !isNaN(v));
        const _sparklineIntraday = extractIntradaySparkline(d.candles_intraday_15m, d.candles_intraday);
        const _sparkline = _sparkDaily;
        // Upsert into universeData so other tabs benefit
        if (upsertUniverse && d) {
          const _secD = (d.fundamentals?.sector && d.fundamentals.sector !== "Other")
            ? d.fundamentals.sector : (SECTOR_HINTS[ticker.toUpperCase()] || "");
          upsertUniverse(ticker.toUpperCase(), {
            ticker:ticker.toUpperCase(), shortName:d.shortName||ticker,
            sector:_secD, price:d.price||0,
            changePct:d.changePct||0, isLive:d.isLive||false,
            swingScore:d.swingScore?.score??null, swingLabel:d.swingScore?.label||"",
            swingType:d.swingScore?.labelType||"watch",
            ltScore:d.longTermScore?.score??null, ltLabel:d.longTermScore?.label||"",
            ltType:d.longTermScore?.labelType||"watch",
            horizonReturn:d.longTermScore?.horizonReturn??null,
            beta:d.beta||1.0, rsi:d.rsi||50,
            sentiment:d.sentiment?.label||"", scoredAt:Date.now(),
            rsRating: d.rsRating  ?? null,
            isLeader: d.isLeader  || false,
            profitTakeFlag: d.profitTakeFlag || false,
            pullbackFlag: d.pullbackFlag || false,
            bounceFlag: d.bounceFlag || false,
            bounceCandidate: d.bounceCandidate || false,
            rotationRisk: d.rotationRisk || false,
            patterns: Array.isArray(d.patterns) ? d.patterns : [],
            sparkline: _sparkline,
            sparklineIntraday: _sparklineIntraday,
          });
        }

        return {
          ticker:         d.ticker,
          shortName:      d.shortName,
          price:          d.price,
          changePct:      d.changePct,
          changeAmt:      d.changeAmt || 0,
          isLive:         d.isLive,
          swingScore:     d.swingScore?.score,
          swingLabel:     d.swingScore?.label,
          swingType:      d.swingScore?.labelType,
          ltScore:        d.longTermScore?.score,
          ltLabel:        d.longTermScore?.label,
          ltType:         d.longTermScore?.labelType,
          rsi:            d.rsi,
          trendDir:       d.swingScore?.trendDir,
          sentiment:      d.sentiment?.label,
          sentimentEmoji: d.sentiment?.labelEmoji,
          horizonReturn:  d.longTermScore?.horizonReturn,
          beta:           d.beta,
          sector:         (d.fundamentals?.sector && d.fundamentals.sector !== "Other") ? d.fundamentals.sector : (SECTOR_HINTS[ticker.toUpperCase()] || ""),
          rsRating:       d.rsRating    ?? null,
          isLeader:       d.isLeader    || false,
          profitTakeFlag: d.profitTakeFlag || false,
          pullbackFlag:   d.pullbackFlag || false,
          bounceFlag:     d.bounceFlag || false,
          bounceCandidate: d.bounceCandidate || false,
          rotationRisk:   d.rotationRisk || false,
          patterns:       Array.isArray(d.patterns) ? d.patterns : [],
          sparkline:      _sparkline,
          sparklineIntraday: _sparklineIntraday,
        };
      } catch(e) {
        if (e.name === "AuthError") { onAuthError?.(e); return null; }
        // After all retries exhausted: surface an error for single-ticker
        // searches so the user sees "Couldn't load X" rather than a blank
        // screen that looks like a successful empty result.
        if (tickers.length === 1) {
          setError(`Couldn't load ${ticker} — please try again`);
        }
        return null;
      }
    };

    for (let i = 0; i < tickers.length; i += BATCH) {
      const batch   = tickers.slice(i, i + BATCH);
      const settled = await Promise.allSettled(batch.map(scoreOne));
      for (const r of settled) {
        if (r.status === "fulfilled" && r.value) out.push(r.value);
      }
      setProgress(p => ({...p, done: Math.min(i + BATCH, tickers.length)}));
      setResults([...out].sort((a,b) =>
        ((b.swingScore||0)+(b.ltScore||0)) - ((a.swingScore||0)+(a.ltScore||0))));
    }
    setScanning(false);
    setSearched(true);
    setLastScored(Date.now());
  };

  const handleSearch = () => {
    const t = query.trim().toUpperCase().replace(/[^A-Z0-9.]/g,"");
    if (!t) return;
    scoreTickers([t]);
  };

  const [speculationFootnote, setSpeculationFootnote] = React.useState("");
  // v8.4.424 — Insider Buys quick list controls (window in days; minimum distinct officer/director buyers)
  const [insWin, setInsWin] = React.useState(30);
  const [insMin, setInsMin] = React.useState(2);
  const [insMode, setInsMode] = React.useState("insiders");   // "insiders" | "funds" (funds & 10% owners)
  const [zombieFootnote, setZombieFootnote]           = React.useState("");
  const [contrarianFootnote, setContrarianFootnote]   = React.useState("");
  const [valueFootnote, setValueFootnote]             = React.useState("");

  const handleList = (name) => {
    setList(name);
    setHeatmapKey(k => k + 1);
    setError("");   // v8.4.298: clear any prior list's message (e.g. Bounce/Rotation
                    // empty-state) so it doesn't linger when switching lists.
    setSpeculationFootnote("");
    setZombieFootnote("");
    setContrarianFootnote("");
    setValueFootnote("");

    if (name === "⚡ Speculation") {
      setScanning(true); setError(""); setResults([]);
      setProgress({done:0, total:0, current:""});
      apiFetch(`/discover/speculation?horizon=${horizon}&profile=${user.risk_profile||"moderate"}`, token)
        .then(d => {
          const mapped = (d.results || []).map(r => ({
            ticker:       r.ticker,
            shortName:    r.shortName || r.ticker,
            price:        r.price || 0,
            changePct:    r.changePct || 0,
            // v8.3.21: backend used to set isLive:true after live-price
            // hydration (Speculation hits _batch_live_prices). User asked
            // for consistency with other quick lists which all show EOD.
            // Keep the live PRICE (more useful) but force the badge to
            // EOD-style so the dot/label matches other curated lists.
            isLive:       false,
            swingScore:   r.swingScore ?? null,
            swingLabel:   "",
            swingType:    r.swingType || (r.swingScore >= 75 ? "strong_buy" : r.swingScore >= 60 ? "buy" : r.swingScore >= 42 ? "watch" : "pass"),
            ltScore:      r.ltScore ?? null,
            ltLabel:      "",
            ltType:       r.ltType || (r.ltScore >= 75 ? "strong_buy" : r.ltScore >= 60 ? "buy" : r.ltScore >= 42 ? "watch" : "pass"),
            // v8.3.21: pull through row fields (RSI/sentiment/patterns/sparkline/RS)
            // so Speculation rows look identical to other quick lists.
            horizonReturn: r.horizonReturn ?? null,
            beta:         r.beta || null,
            rsi:          r.rsi ?? null,
            sentiment:    r.sentimentLabel || "",
            sentimentEmoji: r.sentimentEmoji || "",
            sector:       r.sector || "",
            rsRating:     r.rsRating ?? null,
            isLeader:     r.isLeader || false,
            profitTakeFlag: r.profitTakeFlag || false,
            pullbackFlag: r.pullbackFlag || false,
            bounceFlag: r.bounceFlag || false,
            bounceCandidate: r.bounceCandidate || false,
            rotationRisk: r.rotationRisk || false,
            patterns:     Array.isArray(r.patterns) ? r.patterns : [],
            sparkline:    Array.isArray(r.sparkline) ? r.sparkline : [],
            sparklineIntraday: Array.isArray(r.sparklineIntraday) ? r.sparklineIntraday : [],
            isTrending:   r.isTrending || false,
            trendingRank: r.trendingRank || null,
          }));
          setResults(mapped);
          setSearched(true);
          setScanning(false);
          setSpeculationFootnote(d.footnote || "");
        })
        .catch(e => {
          if (e.name === "AuthError") { onAuthError?.(e); return; }
          setError("Could not load Speculation list. Try again shortly.");
          setScanning(false);
        });
      return;
    }

    if (name === "💎 Contrarian") {
      setScanning(true); setError(""); setResults([]);
      setProgress({done:0, total:0, current:""});
      apiFetch(`/discover/contrarian?horizon=${horizon}&profile=${user.risk_profile||"moderate"}`, token)
        .then(d => {
          const mapped = (d.results || []).map(r => ({
            ticker:       r.ticker,
            shortName:    r.shortName || r.ticker,
            price:        r.price || 0,
            changePct:    r.changePct || 0,
            isLive:       false,    // match other quick-list badge style
            swingScore:   r.swingScore ?? null,
            swingLabel:   "",
            swingType:    r.swingType || (r.swingScore >= 75 ? "strong_buy" : r.swingScore >= 60 ? "buy" : r.swingScore >= 42 ? "watch" : "pass"),
            ltScore:      r.ltScore ?? null,
            ltLabel:      "",
            ltType:       r.ltType || (r.ltScore >= 75 ? "strong_buy" : r.ltScore >= 60 ? "buy" : r.ltScore >= 42 ? "watch" : "pass"),
            horizonReturn: r.horizonReturn ?? null,
            beta:         r.beta || null,
            rsi:          r.rsi ?? null,
            sentiment:    r.sentimentLabel || "",
            sentimentEmoji: r.sentimentEmoji || "",
            sector:       r.sector || "",
            rsRating:     r.rsRating ?? null,
            isLeader:     r.isLeader || false,
            profitTakeFlag: r.profitTakeFlag || false,
            pullbackFlag: r.pullbackFlag || false,
            bounceFlag: r.bounceFlag || false,
            bounceCandidate: r.bounceCandidate || false,
            rotationRisk: r.rotationRisk || false,
            patterns:     Array.isArray(r.patterns) ? r.patterns : [],
            sparkline:    Array.isArray(r.sparkline) ? r.sparkline : [],
            // v8.4.66: forward intraday so the 1D toggle has data to render.
            sparklineIntraday: Array.isArray(r.sparklineIntraday) ? r.sparklineIntraday : [],
            drawdown5d:   r.drawdown5d ?? null,
          }));
          setResults(mapped);
          setSearched(true);
          setScanning(false);
          setContrarianFootnote(d.footnote || "");
        })
        .catch(e => {
          if (e.name === "AuthError") { onAuthError?.(e); return; }
          setError("Could not load Contrarian list. Try again shortly.");
          setScanning(false);
        });
      return;
    }

    if (name === "🪙 Value") {
      setScanning(true); setError(""); setResults([]);
      setProgress({done:0, total:0, current:""});
      apiFetch(`/discover/value?horizon=${horizon}&profile=${user.risk_profile||"moderate"}`, token)
        .then(d => {
          // v8.4.70: surface backend diagnostics for empty-result cases so
          // we know which gate is firing instead of guessing.
          if (d?.diagnostics) {
            console.log("[discover/value] diagnostics:", d.diagnostics);
          }
          const mapped = (d.results || []).map(r => ({
            ticker:       r.ticker,
            shortName:    r.shortName || r.ticker,
            price:        r.price || 0,
            changePct:    r.changePct || 0,
            isLive:       false,
            swingScore:   r.swingScore ?? null,
            swingLabel:   "",
            swingType:    r.swingType || (r.swingScore >= 75 ? "strong_buy" : r.swingScore >= 60 ? "buy" : r.swingScore >= 42 ? "watch" : "pass"),
            ltScore:      r.ltScore ?? null,
            ltLabel:      "",
            ltType:       r.ltType || (r.ltScore >= 75 ? "strong_buy" : r.ltScore >= 60 ? "buy" : r.ltScore >= 42 ? "watch" : "pass"),
            horizonReturn: r.horizonReturn ?? null,
            beta:         r.beta || null,
            rsi:          r.rsi ?? null,
            sentiment:    r.sentimentLabel || "",
            sentimentEmoji: r.sentimentEmoji || "",
            sector:       r.sector || "",
            rsRating:     r.rsRating ?? null,
            isLeader:     r.isLeader || false,
            profitTakeFlag: r.profitTakeFlag || false,
            pullbackFlag: r.pullbackFlag || false,
            bounceFlag: r.bounceFlag || false,
            bounceCandidate: r.bounceCandidate || false,
            rotationRisk: r.rotationRisk || false,
            patterns:     Array.isArray(r.patterns) ? r.patterns : [],
            sparkline:    Array.isArray(r.sparkline) ? r.sparkline : [],
            sparklineIntraday: Array.isArray(r.sparklineIntraday) ? r.sparklineIntraday : [],
            forwardPE:    r.forwardPE ?? null,
            fcfYield:     r.fcfYield ?? null,
            dividendYield:r.dividendYield ?? null,
            valueScore:   r.valueScore ?? null,
          }));
          setResults(mapped);
          setSearched(true);
          setScanning(false);
          setValueFootnote(d.footnote || "");
        })
        .catch(e => {
          if (e.name === "AuthError") { onAuthError?.(e); return; }
          setError("Could not load Value list. Try again shortly.");
          setScanning(false);
        });
      return;
    }

    if (name === "🧟 Zombie") {
      setScanning(true); setError(""); setResults([]);
      setProgress({done:0, total:0, current:""});
      apiFetch(`/discover/zombie?horizon=${horizon}&profile=${user.risk_profile||"moderate"}`, token)
        .then(d => {
          const mapped = (d.results || []).map(r => ({
            ticker:       r.ticker,
            shortName:    r.shortName || r.ticker,
            price:        r.price || 0,
            changePct:    r.changePct || 0,
            // v8.3.21: same as Speculation — force EOD badge for visual
            // consistency with other quick lists.
            isLive:       false,
            swingScore:   r.swingScore ?? null,
            swingLabel:   "",
            swingType:    "pass",
            ltScore:      r.ltScore ?? null,
            ltLabel:      "",
            ltType:       "pass",
            // v8.3.21: pull through row fields from backend so Zombie rows
            // match other quick lists.
            horizonReturn: r.horizonReturn ?? null,
            beta:         r.beta || null,
            rsi:          r.rsi ?? null,
            sentiment:    r.sentimentLabel || "",
            sentimentEmoji: r.sentimentEmoji || "",
            sector:       r.sector || "",
            rsRating:     r.rsRating ?? null,
            isLeader:     r.isLeader || false,
            profitTakeFlag: r.profitTakeFlag || false,
            pullbackFlag: r.pullbackFlag || false,
            bounceFlag: r.bounceFlag || false,
            bounceCandidate: r.bounceCandidate || false,
            rotationRisk: r.rotationRisk || false,
            patterns:     Array.isArray(r.patterns) ? r.patterns : [],
            sparkline:    Array.isArray(r.sparkline) ? r.sparkline : [],
            zombieFlags:  r.zombieFlags || [],
          }));
          setResults(mapped);
          setSearched(true);
          setScanning(false);
          setZombieFootnote(d.footnote || "");
        })
        .catch(e => {
          if (e.name === "AuthError") { onAuthError?.(e); return; }
          setError("Could not load Zombie list. Try again shortly.");
          setScanning(false);
        });
      return;
    }

    // v8.4.297/298 — sharp-drop quick lists (Bounce/Rotation) are built REACTIVELY by
    // the dropListEffect below (setList(name) above triggers it), so they re-filter when
    // universeData loads/updates and a click before the universe is ready can't leave a
    // stale empty snapshot. Just stop here so we don't fall through to the curated path.
    if (name === "🟢 Bounce" || name === "⚠️ Rotation" || name === "💰 Dividend" || name === "👥 Insider Buys") {
      return;
    }

    const tickers = CURATED[name] || [];
    const filtered = market === "CA"
      ? tickers.filter(t => t.endsWith(".TO"))
      : market === "US"
      ? tickers.filter(t => !t.endsWith(".TO"))
      : tickers;
    const allTickers = filtered.length ? filtered : tickers;

    // Universe-first path — read scores directly from universeData (same source as
    // Trending, Speculation, and Zombie). Bypasses the sparkline gate in scoreTickers()
    // which was causing curated lists to call /analyze live and produce scores
    // inconsistent with every other surface on the app.
    // v8.4.79: require swingScore > 0. Tickers with null swingScore (scored for
    // LT but not swing, or Celery scoring failed) showed "—" in the donut with
    // no useful momentum info — confusing next to tickers that have a full score.
    // Tickers that fail this filter fall through to scoreTickers() → live /analyze.
    const fromUniverse = allTickers
      .map(t => universeData?.[t.toUpperCase()])
      .filter(uc => uc && (uc.swingScore ?? 0) > 0);

    if (fromUniverse.length >= Math.min(3, allTickers.length)) {
      // Good coverage — display instantly from universe cache
      const mapped = fromUniverse.map(uc => ({
        ticker:        uc.ticker,
        shortName:     uc.shortName || uc.ticker,
        price:         uc.price || 0,
        changePct:     uc.changePct || 0,
        isLive:        uc.isLive || false,
        swingScore:    uc.swingScore ?? null,
        swingLabel:    uc.swingLabel || "",
        swingType:     uc.swingType || "watch",
        ltScore:       uc.ltScore ?? null,
        ltLabel:       uc.ltLabel || "",
        ltType:        uc.ltType || "watch",
        horizonReturn: uc.horizonReturn ?? null,
        beta:          uc.beta || 1.0,
        rsi:           uc.rsi || 50,
        sentiment:     uc.sentiment || "",
        sector:        uc.sector || "",
        rsRating:      uc.rsRating ?? null,
        isLeader:      uc.isLeader || false,
        patterns:      Array.isArray(uc.patterns) ? uc.patterns : [],
        sparkline:     Array.isArray(uc.sparkline) ? uc.sparkline : [],
        // v8.4.79: forward sparklineIntraday so 1D toggle works on curated lists
        // without waiting for the discoverIntraHydrateRef effect to fire.
        sparklineIntraday: Array.isArray(uc.sparklineIntraday) ? uc.sparklineIntraday : [],
        fromCache:     true,
      })).sort((a, b) =>
        ((b.swingScore || 0) + (b.ltScore || 0)) - ((a.swingScore || 0) + (a.ltScore || 0))
      );
      setResults(mapped);
      setSearched(true);
      setScanning(false);
    } else {
      // Universe cold or sparse — fall back to live scoring
      scoreTickers(allTickers.slice(0, 20));
    }
  };

  // v8.4.66: background intraday hydration for Discovery results so the 1D
  // sparkline toggle has data on most rows. Mirrors the Watchlist hydration
  // effect (v8.4.62) — runs once per ticker per session, hits /analyze in
  // small parallel batches, and upserts the intraday closes into universeData
  // so the row picks them up via the existing sparklineIntraday plumbing.
  //
  // Why this is needed: the universe Celery scoring loop only writes the
  // daily 14-close sparkline. Intraday data lives in the per-ticker /analyze
  // cache which the Discovery quicklist endpoints don't hit. Symptom user
  // reported on LBRT: only 3W tab available, no 1D toggle.
  const discoverIntraHydrateRef = React.useRef(new Set());
  React.useEffect(() => {
    if (!results?.length || !token || !upsertUniverse) return;
    let cancelled = false;
    (async () => {
      await new Promise(r => setTimeout(r, 500));  // let initial render settle
      if (cancelled) return;
      const attempted = discoverIntraHydrateRef.current;
      const ud = universeData || {};
      const needs = results
        .map(r => r.ticker?.toUpperCase())
        .filter(Boolean)
        .filter(t => !attempted.has(t))
        .filter(t => {
          const cached = ud[t];
          if (cached && Array.isArray(cached.sparklineIntraday) &&
              cached.sparklineIntraday.length >= 2) {
            attempted.add(t);
            return false;
          }
          return true;
        })
        .slice(0, 12);  // cap per scan — don't hammer /analyze on every chip tap
      if (!needs.length) return;
      const CONCURRENCY = 3;
      let cursor = 0;
      const worker = async () => {
        while (!cancelled && cursor < needs.length) {
          const t = needs[cursor++];
          // v8.4.79: mark per-ticker just before its fetch (same fix as Watchlist
          // intraHydrate and hydrationAttemptedRef). Pre-marking all tickers before
          // any fetch means empty-intraday returns lock the ticker out forever.
          attempted.add(t);
          try {
            const dd = await apiFetch(`/analyze/${t}?horizon=${horizon}`, token);
            if (cancelled || !dd) { attempted.delete(t); continue; }
            const _intra = extractIntradaySparkline(dd.candles_intraday_15m, dd.candles_intraday);
            const _daily = (Array.isArray(dd.sparkline) && dd.sparkline.length >= 2)
              ? dd.sparkline
              : (Array.isArray(dd.candles) ? dd.candles.slice(-14)
                  .map(c => typeof c === "object" ? c.c : c)
                  .filter(v => typeof v === "number" && !isNaN(v)) : []);
            // v8.4.69: ALSO pull the authoritative changePct + price from
            // /analyze so the Discovery tile matches the stock detail page
            // exactly. This makes us resilient to the backend snapshot
            // override (v8.4.66) failing — /analyze is what the detail
            // page itself uses, so the two CAN'T disagree once this fires.
            // Reported case: LBRT showed -12.34% on Contrarian tile but
            // +0.72% on detail page.
            const _liveChangePct = (typeof dd.changePct === "number")
              ? dd.changePct : null;
            const _livePrice     = (typeof dd.price === "number" && dd.price > 0)
              ? dd.price : null;
            const _liveAmt       = (typeof dd.changeAmt === "number")
              ? dd.changeAmt : null;
            // Allow retry if intraday came back empty (market may be pre-open)
            if (_intra.length < 2) attempted.delete(t);
            if (_intra.length >= 2 || _daily.length >= 2 || _liveChangePct !== null) {
              upsertUniverse(t, {
                ticker: t,
                sparkline: _daily.length >= 2 ? _daily : undefined,
                sparklineIntraday: _intra,
                changePct: _liveChangePct !== null ? _liveChangePct : undefined,
                price:     _livePrice !== null ? _livePrice : undefined,
                scoredAt: Date.now(),
              });
              // Patch the row in-place so the tile updates without waiting
              // for universeData propagation — pushes both sparkline and
              // (critically) the corrected changePct.
              if (!cancelled) {
                setResults(prev => prev.map(r => {
                  if (r.ticker?.toUpperCase() !== t) return r;
                  return {
                    ...r,
                    sparklineIntraday: _intra.length >= 2 ? _intra : r.sparklineIntraday,
                    sparkline:         _daily.length >= 2 ? _daily : r.sparkline,
                    changePct:         _liveChangePct !== null ? _liveChangePct : r.changePct,
                    price:             _livePrice     !== null ? _livePrice     : r.price,
                    changeAmt:         _liveAmt       !== null ? _liveAmt       : r.changeAmt,
                    isLive:            dd.isLive ?? r.isLive,
                  };
                }));
              }
            }
          } catch (e) { attempted.delete(t); /* allow retry on error */ }
        }
      };
      await Promise.all(Array.from({length: CONCURRENCY}, worker));
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results, token]);

  // Sector heatmap click — instantly filter universeData by sector, sort by swing score
  const handleSectorFilter = React.useCallback((sectorName) => {
    // Step 1: find all already-scored tickers in this sector from universeData — within the SELECTED region
    // (v8.4.437: Canada = .TO names, US = everything else, All = both) so a Canadian sector tap can't show U.S. stocks.
    const inRegion = (t) => {
      const isCa = String(t || "").toUpperCase().endsWith(".TO");
      return market === "CA" ? isCa : market === "US" ? !isCa : true;
    };
    const regionWord = market === "CA" ? "Canadian " : market === "US" ? "U.S. " : "";
    const fromUniverse = Object.values(universeData || {})
      .filter(r => r?.sector && r.sector.toLowerCase() === sectorName.toLowerCase()
                && r.swingScore > 0 && inRegion(r.ticker))
      .sort((a, b) => b.swingScore - a.swingScore);

    // Step 2: route sectors with a curated list directly to that list (instant + complete)
    const SECTOR_TO_LIST = {
      "Technology":             "Tech",
      "Financials":             "Finance",
      "Financial Services":     "Finance",
      "Energy":                 "Energy",
      "Healthcare":             "Healthcare",
      "Industrials":            "Industrials",
      "Materials":              "Materials",
      "Utilities":              "Utilities",
      "Real Estate":            "Real Estate",
      "Consumer Cyclical":      null,
      "Consumer Defensive":     null,
      "Communication Services": null,
    };
    const curatedName = SECTOR_TO_LIST[sectorName];

    // If a curated list exists for this sector AND it has names in the selected region, use it — gives full coverage.
    // (handleList falls back to the other market's names when a region has none — wrong for a sector tap.)
    if (curatedName && CURATED[curatedName] && CURATED[curatedName].some(inRegion)) {
      handleList(curatedName);
      return;
    }

    // No curated list — fall back to universeData filter
    const curatedTickers = [];
    const scoredSet = new Set(fromUniverse.map(r => r.ticker.toUpperCase()));
    const missing   = curatedTickers.filter(t => !scoredSet.has(t.toUpperCase()));

    if (fromUniverse.length === 0 && missing.length === 0) {
      setError(`No ${regionWord}stocks found for ${sectorName}. ` + (market === "All"
        ? "Run the universe scan first or check back shortly."
        : "Try the All tab, or check back shortly."));
      return;
    }

    setList(sectorName);

    if (fromUniverse.length > 0) {
      // Show universe results immediately — already sorted by score
      setResults(fromUniverse.slice(0, 20).map(r => ({
        ticker:       r.ticker,
        shortName:    r.shortName || r.ticker,
        price:        r.price     || 0,
        changePct:    r.changePct || 0,
        isLive:       r.isLive    || false,
        swingScore:   r.swingScore || 0,
        swingLabel:   r.swingLabel || "",
        swingType:    r.swingType  || "watch",
        ltScore:      r.ltScore   || 0,
        ltLabel:      r.ltLabel   || "",
        ltType:       r.ltType    || "watch",
        horizonReturn:r.horizonReturn ?? null,
        beta:         r.beta      || 1.0,
        rsi:          r.rsi       || 50,
        sentiment:    r.sentiment || "",
        sector:       r.sector    || "",
        rsRating:     r.rsRating  ?? null,
        isLeader:     r.isLeader  || false,
        patterns:     r.patterns  || [],
        fromCache:    true,
      })));
      setSearched(true);
      setScanning(false);
    }

    // Score any missing curated tickers in the background (fills gaps)
    if (missing.length > 0) {
      scoreTickers(missing.length < 8 ? missing : missing.slice(0, 8));
    } else if (fromUniverse.length === 0) {
      // No universe data at all — fall back to scoring the full curated list
      scoreTickers(curatedTickers.slice(0, 15));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [universeData, market]);

  // v8.4.298 — reactive Bounce/Rotation lists: re-filter universeData by the flag whenever
  // it (or the selected list) changes, so the list is never a stale empty snapshot from a
  // click made before /universe finished loading, and it refreshes as new scores arrive.
  React.useEffect(() => {
    const isDiv = list === "💰 Dividend";
    if (list !== "🟢 Bounce" && list !== "⚠️ Rotation" && !isDiv) return;
    const flag = list === "🟢 Bounce" ? "bounceCandidate" : "rotationRisk";
    // Dividend list: yield >= 3% (dividendYield is a decimal, 0.03 = 3%), sorted high→low.
    const src = isDiv
      ? Object.values(universeData || {})
          .filter(r => r && (r.dividendYield || 0) >= 0.03)
          .sort((a, b) => (b.dividendYield || 0) - (a.dividendYield || 0))
      : Object.values(universeData || {})
          .filter(r => r && r[flag])
          .sort((a, b) => (b.swingScore || 0) - (a.swingScore || 0));
    const rows = src.map(r => ({
      ticker: r.ticker, shortName: r.shortName || r.ticker,
      price: r.price || 0, changePct: r.changePct || 0, isLive: r.isLive || false,
      swingScore: r.swingScore ?? null, swingLabel: r.swingLabel || "", swingType: r.swingType || "watch",
      ltScore: r.ltScore ?? null, ltLabel: r.ltLabel || "", ltType: r.ltType || "watch",
      horizonReturn: r.horizonReturn ?? null, beta: r.beta || null, rsi: r.rsi ?? null,
      sector: r.sector || "", rsRating: r.rsRating ?? null, isLeader: r.isLeader || false,
      patterns: Array.isArray(r.patterns) ? r.patterns : [],
      sparkline: Array.isArray(r.sparkline) ? r.sparkline : [],
      bounceCandidate: r.bounceCandidate || false, rotationRisk: r.rotationRisk || false,
      dividendYield: r.dividendYield ?? null,
      fromCache: true,
    }));
    setResults(rows);
    setSearched(true);
    setScanning(false);
    setError(rows.length ? "" : (isDiv
      ? "No 3%+ dividend payers scored yet — dividend yields fill in as the universe re-scores."
      : list === "🟢 Bounce"
        ? "No bounce candidates right now — this fires after a stock drops ~8%+ as an isolated dip in a steady tape. Check back on a red day."
        : "No rotation-risk names right now — this fires when a stock drops ~8%+ during a broad, weak-market selloff."));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [universeData, list]);

  // v8.4.424 — Insider Buys: market-wide officer/director open-market purchases (SEC Form 4 via Polygon),
  // ranked server-side by conviction. Reactive on the controls so changing the window/min-buyers re-ranks.
  React.useEffect(() => {
    if (list !== "👥 Insider Buys" || !token) return;
    let alive = true;
    setScanning(true); setError("");
    apiFetch(`/insider-buys?days=${insWin}&min_buyers=${insMin}&mode=${insMode}${insMode === "funds" ? "&min_value=250000" : ""}`, token)
      .then(d => {
        if (!alive) return;
        if (d && d.unavailable) {
          setResults([]); setSearched(true); setScanning(false);
          setError("Insider data is unavailable right now — try again shortly.");
          return;
        }
        const rows = ((d && d.rows) || []).map(r => {
          const u = (universeData || {})[String(r.ticker).toUpperCase()] || {};   // reuse universe sparkline/RSI etc.
          return {
            ticker: r.ticker, shortName: r.shortName || r.ticker,
            price: r.price || 0, changePct: r.changePct ?? 0, isLive: u.isLive || false,
            swingScore: r.swingScore ?? null, swingLabel: u.swingLabel || "", swingType: r.swingType || "watch",
            ltScore: r.ltScore ?? null, ltLabel: u.ltLabel || "", ltType: r.ltType || "watch",
            horizonReturn: u.horizonReturn ?? null, beta: u.beta || null, rsi: u.rsi ?? null,
            sector: r.sector || "", rsRating: u.rsRating ?? null, isLeader: u.isLeader || false,
            patterns: Array.isArray(u.patterns) ? u.patterns : [],
            sparkline: Array.isArray(u.sparkline) ? u.sparkline : [],
            sparklineIntraday: Array.isArray(u.sparklineIntraday) ? u.sparklineIntraday : [],
            profitTakeFlag: u.profitTakeFlag || false, pullbackFlag: u.pullbackFlag || false,
            bounceFlag: u.bounceFlag || false, bounceCandidate: u.bounceCandidate || false,
            rotationRisk: u.rotationRisk || false, fromCache: true,
            insider: {kind: insMode, buyers: r.buyers ?? r.holders, value: r.buyValue, sells: r.sellValue, who: r.who || [], avgPrice: r.avgPrice,
                      vsPct: r.vsInsiderPct, latest: r.latest, cSuite: r.cSuite, score: r.score, pctOfCap: r.pctOfCap,
                      quality: r.setupQuality, offeringRisk: !!r.offeringRisk,
                      holders: r.holders, tenPct: !!r.tenPct, priceLabel: r.priceLabel, openPct: r.openPct, activist13d: !!r.activist13d},
          };
        });
        setResults(rows); setSearched(true); setScanning(false);
        setError(rows.length ? "" : (insMode === "funds"
          ? `No scored stocks have $250K+ of fund or 10%-owner buying in the last ${insWin} days — try a longer window.`
          : `No scored stocks have ${insMin}+ officers/directors buying on the open market in the last ${insWin} days — try a longer window or fewer buyers.`));
      })
      .catch(e => {
        if (!alive) return;
        if (e && e.name === "AuthError") { onAuthError?.(e); return; }
        setError("Could not load insider buys. Try again shortly."); setScanning(false);
      });
    return () => { alive = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list, insWin, insMin, insMode, token]);

  // v8.4.300: case/whitespace-tolerant watchlist membership. A strict
  // Array.includes(s.ticker) was leaving a "+ Watchlist" button on names already
  // watchlisted (user caught MRVL on the Bounce list). Normalize both sides.
  const _wlSet = React.useMemo(
    () => new Set((watchlist || []).map(w => String(w).trim().toUpperCase())),
    [watchlist]
  );

  const listsByMarket = market === "CA"
    ? ["TSX 60","Finance","Energy","Materials","Healthcare","Industrials"]
    : market === "US"
    ? ["S&P 100","Tech","AI & Chips","Finance","Energy","Healthcare","Materials","Industrials","ETFs","Momentum","🟢 Bounce","⚠️ Rotation","💰 Dividend","👥 Insider Buys","💎 Contrarian","🪙 Value","⚡ Speculation","🧟 Zombie"]
    : [...Object.keys(CURATED), "🟢 Bounce","⚠️ Rotation","💰 Dividend","👥 Insider Buys","💎 Contrarian","🪙 Value","⚡ Speculation","🧟 Zombie"];

  return (
    <div style={{padding:"0 16px 100px"}}>
      {/* Header (frozen) */}
      <FrozenHeader padTop={16} padBottom={12}>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <AppLogo size={32}/>
          <div>
            <div style={{fontSize:20,fontWeight:900,color:C.text,letterSpacing:"-0.02em",lineHeight:1}}>
              Discover
            </div>
            <div style={{fontSize:10,color:C.muted,marginTop:2}}>
              Score any ticker or scan a curated list
            </div>
          </div>
        </div>
      </FrozenHeader>
      <div style={{height:14}}/>

      {/* AI Agent: "What should I buy today?" — promoted to the TOP (was wedged
          between the quick lists/sector selectors and the results they produce,
          which split the manual flow). Now it's the hero entry point; the manual
          tools (heatmap → search → quick lists) sit below, with their results
          directly under them. */}
      <FeatureTour tourId="discover" version={1} steps={DISCOVER_STEPS} active={visible}/>
      <div data-tour="disc-agent">
      <BuyTodayAgentCard
        token={token}
        horizon={horizon}
        profile={user?.risk_profile || "moderate"}
        onSelectTicker={onSelectTicker}
      />
      </div>

      {/* Controls card */}
      <div style={{background:C.surface,borderRadius:14,padding:"12px 14px",
        border:`1px solid ${C.border}`,marginBottom:12}}>

        {/* Market toggle */}
        <div style={{display:"flex",gap:6,marginBottom:12}}>
          {[["US","🇺🇸 US"],["CA","🇨🇦 Canada"],["All","🌐 All"]].map(([key,label])=>(
            <button key={key} onClick={()=>setMarket(key)} style={{
              flex:1,padding:"7px 4px",borderRadius:8,border:"none",
              cursor:"pointer",fontFamily:"inherit",fontSize:12,fontWeight:600,
              transition:"all 0.15s",
              background:market===key?"rgba(0,212,255,0.15)":C.card,
              color:market===key?C.accent:C.muted,
              borderBottom:`2px solid ${market===key?C.accent:"transparent"}`,
            }}>{label}</button>
          ))}
        </div>

        {/* Sector RS heatmap vs top-movers bubble chart — toggle (v8.4.309) */}
        {Object.keys(universeData||{}).length >= 20 && (
          <div style={{marginBottom:12}}>
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
              <div style={{display:"flex",gap:6}}>
                {[["sectors","Sectors"],["movers","Movers"]].map(([v,l])=>(
                  <button key={v} onClick={()=>setHeatmapView(v)} style={{
                    padding:"4px 12px",borderRadius:20,fontSize:11,fontWeight:700,
                    border:`1px solid ${heatmapView===v?C.accent:C.border}`,
                    background:heatmapView===v?"rgba(0,212,255,0.12)":"none",
                    color:heatmapView===v?C.accent:C.muted,cursor:"pointer",
                    fontFamily:"inherit",transition:"all 0.15s"}}>{l}</button>
                ))}
              </div>
              {heatmapView==="movers" && (
                <InfoTip text={"TOP MOVERS BUBBLE CHART — a snapshot of where today's money and momentum are.\n\n• BUBBLE SIZE = today's DOLLAR VOLUME (price × shares traded) — bigger bubble = more money changing hands = more liquid/active.\n\n• COLOR = daily % move: green = up, red = down. Brighter/more saturated = bigger move.\n\n• Shows the ~50 biggest movers by % change. Tap any bubble to open the stock.\n\nNot a signal or a recommendation — it's a live map of activity, useful for spotting where the action is right now."}/>
              )}
            </div>
            <div data-tour="disc-heatmap">
            {heatmapView==="sectors"
              ? <SectorHeatmap token={token} universeData={universeData} lastScanTime={lastScanTime} onSelectTicker={onSelectTicker} onSectorClick={handleSectorFilter} resetKey={heatmapKey} market={market}/>
              : <MoversBubbleChart universeData={universeData} onSelectTicker={onSelectTicker} market={market}/>}
            </div>
          </div>
        )}

        {/* Search input */}
        <div data-tour="disc-search" style={{marginBottom:12}}>
          <TickerSearch
            value={query}
            onChange={setQuery}
            onSelect={(t) => { setQuery(t); if(t) scoreTickers([t]); }}
            placeholder={market==="CA"?"Search e.g. SHOP.TO or Shopify":"Search e.g. NVDA or Nvidia"}
            token={token}
          />
        </div>

        {/* Curated list pills */}
        <div data-tour="disc-lists">
        <div style={{fontSize:10,fontWeight:700,color:C.muted,
          letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:7}}>
          Quick lists
        </div>
        <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
          {listsByMarket.map(name=>(
            <button key={name} onClick={()=>handleList(name)}
              disabled={scanning}
              style={{padding:"5px 10px",borderRadius:20,border:`1px solid ${C.border}`,
                cursor:"pointer",fontFamily:"inherit",fontSize:11,fontWeight:600,
                background:list===name&&searched?"rgba(0,212,255,0.12)":C.card,
                color:list===name&&searched?C.accent:C.muted,
                opacity:scanning?0.5:1,transition:"all 0.15s",
              }}>{name}</button>
          ))}
        </div>
        </div>
      </div>

      {/* Horizon indicator - read only, change on Home screen */}
      <div style={{display:"flex",gap:4,marginBottom:12}}>
        {HORIZONS.map(h=>(
          <div key={h.key} style={{
            flex:1,padding:"6px 2px",borderRadius:8,textAlign:"center",
            fontSize:11,fontWeight:600,
            background:horizon===h.key?"rgba(0,212,255,0.15)":"transparent",
            color:horizon===h.key?C.accent:"rgba(255,255,255,0.2)",
            borderBottom:`2px solid ${horizon===h.key?C.accent:"transparent"}`,
          }}>{h.label.replace(" ","")}</div>
        ))}
      </div>

      {/* Progress */}
      {scanning && (
        <div style={{marginBottom:14}}>
          <div style={{display:"flex",justifyContent:"space-between",
            fontSize:11,color:C.muted,marginBottom:5}}>
            <span>
              {progress.done} / {progress.total}
              {progress.current &&
                <span style={{marginLeft:6,color:C.accent,fontFamily:"monospace"}}>
                  {progress.current}
                </span>}
            </span>
            <span>{progress.total>0?Math.round((progress.done/progress.total)*100):0}%</span>
          </div>
          <div style={{height:4,background:"rgba(255,255,255,0.06)",
            borderRadius:2,overflow:"hidden"}}>
            <div style={{height:"100%",borderRadius:2,
              background:`linear-gradient(90deg,${C.accent},#00ff88)`,
              width:`${progress.total>0?(progress.done/progress.total)*100:0}%`,
              transition:"width 0.3s ease"}}/>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <Card style={{borderColor:"rgba(255,82,82,0.3)",marginBottom:10}}>
          <div style={{fontSize:13,color:C.red,textAlign:"center"}}>⚠️ {error}</div>
        </Card>
      )}

      {/* Empty state */}
      {!scanning && !searched && (
        <Card style={{textAlign:"center",padding:"32px 16px"}}>
          <div style={{fontSize:36,marginBottom:12}}>🔭</div>
          <div style={{fontSize:15,fontWeight:600,color:C.text,marginBottom:8}}>
            Search or pick a list
          </div>
          <div style={{fontSize:12,color:C.muted,lineHeight:1.7}}>
            Type any ticker above, or tap a quick list to score a curated set of stocks
            with live data. Results appear as each stock is analysed.
          </div>
        </Card>
      )}

      {/* v8.3.7: stale-banner removed — universeData is the centralized source
          of truth, refreshed by Celery beat every 30 min during market hours
          (less frequently off-hours). Discovery / Watchlist / Builder /
          Optimizer / Gap Scanner all read the same cache, so a "scores may be
          stale" message implied an inconsistency that no longer exists. If
          users want a manual refresh, the global progress card on Landing has
          a refresh button. */}

      {/* Speculation disclaimer */}
      {list === "⚡ Speculation" && speculationFootnote && (
        <div style={{background:"rgba(255,202,40,0.08)",border:"1px solid rgba(255,202,40,0.25)",
          borderRadius:10,padding:"10px 14px",marginBottom:12,
          display:"flex",alignItems:"flex-start",gap:8}}>
          <span style={{fontSize:16,flexShrink:0}}>⚠️</span>
          <span style={{fontSize:11,color:"#ffca28",lineHeight:1.5}}>{speculationFootnote}</span>
        </div>
      )}

      {/* Contrarian disclaimer (v8.4.63) */}
      {list === "💎 Contrarian" && contrarianFootnote && (
        <div style={{background:"rgba(0,212,255,0.06)",border:"1px solid rgba(0,212,255,0.25)",
          borderRadius:10,padding:"10px 14px",marginBottom:12,
          display:"flex",alignItems:"flex-start",gap:8}}>
          <span style={{fontSize:16,flexShrink:0}}>💎</span>
          <span style={{fontSize:11,color:"#7fd6f5",lineHeight:1.5}}>{contrarianFootnote}</span>
        </div>
      )}

      {/* Value disclaimer (v8.4.64) — gold/amber to differentiate from Contrarian cyan */}
      {list === "🪙 Value" && valueFootnote && (
        <div style={{background:"rgba(255,193,7,0.06)",border:"1px solid rgba(255,193,7,0.25)",
          borderRadius:10,padding:"10px 14px",marginBottom:12,
          display:"flex",alignItems:"flex-start",gap:8}}>
          <span style={{fontSize:16,flexShrink:0}}>🪙</span>
          <span style={{fontSize:11,color:"#ffc107",lineHeight:1.5}}>{valueFootnote}</span>
        </div>
      )}

      {/* v8.4.424 — Insider Buys controls + explainer */}
      {list === "👥 Insider Buys" && (
        <div style={{background:"rgba(0,230,118,0.05)",border:"1px solid rgba(0,230,118,0.22)",
          borderRadius:10,padding:"10px 14px",marginBottom:12}}>
          <div style={{display:"flex",gap:6,flexWrap:"wrap",alignItems:"center",marginBottom:8}}>
            {[["insiders","👥 Insiders"],["funds","🏦 Funds & 10% owners"]].map(([m,l]) => (
              <button key={m} onClick={()=>setInsMode(m)} style={{padding:"4px 12px",borderRadius:16,fontSize:11,fontWeight:800,
                border:`1px solid ${insMode===m?"#00e676":C.border}`,background:insMode===m?"rgba(0,230,118,0.14)":"none",
                color:insMode===m?"#00e676":C.muted,cursor:"pointer",fontFamily:"inherit"}}>{l}</button>))}
          </div>
          <div style={{display:"flex",gap:6,flexWrap:"wrap",alignItems:"center",marginBottom:8}}>
            <span style={{fontSize:10,color:C.muted,fontWeight:700,letterSpacing:"0.06em",textTransform:"uppercase"}}>Window</span>
            {[7,14,30].map(d => (
              <button key={d} onClick={()=>setInsWin(d)} style={{padding:"3px 10px",borderRadius:16,fontSize:11,fontWeight:700,
                border:`1px solid ${insWin===d?"#00e676":C.border}`,background:insWin===d?"rgba(0,230,118,0.14)":"none",
                color:insWin===d?"#00e676":C.muted,cursor:"pointer",fontFamily:"inherit"}}>{d}d</button>))}
            {insMode === "insiders" && <span style={{fontSize:10,color:C.muted,fontWeight:700,letterSpacing:"0.06em",textTransform:"uppercase",marginLeft:8}}>Min buyers</span>}
            {insMode === "insiders" && [2,3,4].map(n => (
              <button key={n} onClick={()=>setInsMin(n)} style={{padding:"3px 10px",borderRadius:16,fontSize:11,fontWeight:700,
                border:`1px solid ${insMin===n?"#00e676":C.border}`,background:insMin===n?"rgba(0,230,118,0.14)":"none",
                color:insMin===n?"#00e676":C.muted,cursor:"pointer",fontFamily:"inherit"}}>{n}+</button>))}
          </div>
          <div style={{fontSize:11,color:"#7fe6a8",lineHeight:1.5}}>
            {insMode === "funds"
              ? "Funds, 10% owners and other non-officer holders buying (SEC Form 4, $250K+). Each is tagged open-market priced vs offering-like by comparing the purchase price to that day's trading range — a buy well below the market is usually a private placement, not accumulation. A 13D badge means an activist filing too. Public filings: context, not a guarantee. Backtest note (past year, ~440 events): fund and 10%-owner buying showed no reliable edge over 1 week to 3 months. A research lead, not a buy signal."
              : "Officers and directors buying their own stock on the open market (SEC Form 4). Ranked by conviction — number of insiders, dollars, C-suite involvement and recency, minus any insider selling. Fund and 10%-owner buying is on the other tab; pre-planned (10b5-1) purchases are excluded. Public filings: context, not a guarantee. Backtest note (past year, ~850 clusters): after officer and director buying clusters, stocks beat similar stocks by about 1% over the first week; gains over 1-3 months were small and not statistically reliable, and the typical cluster only matched the market. No edge in large caps. A research lead, not a buy signal."}
          </div>
        </div>
      )}

      {/* Zombie disclaimer */}
      {list === "🧟 Zombie" && zombieFootnote && (
        <div style={{background:"rgba(255,82,82,0.07)",border:"1px solid rgba(255,82,82,0.25)",
          borderRadius:10,padding:"10px 14px",marginBottom:12,
          display:"flex",alignItems:"flex-start",gap:8}}>
          <span style={{fontSize:16,flexShrink:0}}>☠️</span>
          <span style={{fontSize:11,color:"#ff5252",lineHeight:1.5}}>{zombieFootnote}</span>
        </div>
      )}

      {/* Results -- appear as they stream in */}
      {results.length > 0 && (
        <ErrorBoundary>
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          {results.filter(s=>s&&s.ticker).map((s,i)=>{
            const inWl = _wlSet.has(String(s.ticker).trim().toUpperCase());
            return (
            <Card key={s.ticker} onClick={()=>onSelectTicker(s.ticker)}
              style={{cursor:"pointer",opacity: scanning&&i>2?0.7:1}}>
              <div style={{display:"flex",alignItems:"center",gap:10}}>
                {/* Rank */}
                <div style={{width:22,height:22,borderRadius:"50%",flexShrink:0,
                  background:"rgba(0,212,255,0.1)",display:"flex",
                  alignItems:"center",justifyContent:"center"}}>
                  <span style={{fontSize:10,fontWeight:800,color:C.accent}}>#{i+1}</span>
                </div>
                {/* Logo */}
                <TickerLogo ticker={s.ticker} size={30}/>
                {/* Name & sector */}
                <div style={{flex:1,minWidth:0}}>
                  <div style={{display:"flex",alignItems:"center",flexWrap:"wrap",columnGap:7,rowGap:3,marginBottom:1}}>
                    <span style={{fontSize:15,fontWeight:800,color:C.text,fontFamily:"monospace"}}>
                      {s.ticker}
                    </span>
                    <EarningsSoonBadge soon={earningsSoon} ticker={s.ticker}/>
                    {/* v8.4.296: ONE pill = the tradePref-selected canonical backend label. */}
                    <SignalPill type={prefSignal(s.swingScore,s.swingType,s.ltScore,s.ltType,tradePref).type} size="sm"/>
                    {s.pullbackFlag ? <PullbackFlag/> : (s.profitTakeFlag && <ExtendedFlag/>)}
                    {s.bounceFlag && <BounceFlag/>}
                    {insiderMap[String(s.ticker).toUpperCase()] && <InsiderClusterFlag info={insiderMap[String(s.ticker).toUpperCase()]}/>}
                    {activistMap[String(s.ticker).toUpperCase()] && <ActivistFilingFlag info={activistMap[String(s.ticker).toUpperCase()]}/>}
                  </div>
                  {/* company name stays directly under the ticker */}
                  {s.shortName && s.shortName !== s.ticker && (
                    <div style={{fontSize:11,color:C.muted,
                      overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                      {s.shortName}
                    </div>
                  )}
                  {s.sector && (
                    <div style={{fontSize:10,color:C.muted,marginTop:1}}>{s.sector}</div>
                  )}
                  {/* v8.4.299: dividend yield — shown on the Dividend list (and any name paying >=1%) */}
                  {(s.dividendYield || 0) >= 0.01 && (
                    <div style={{fontSize:10,fontWeight:700,color:"#00e676",marginTop:1}}>
                      💰 {(s.dividendYield * 100).toFixed(2)}% yield
                    </div>
                  )}
                  {/* v8.4.426 — Funds & 10% owners variant */}
                  {s.insider && s.insider.kind === "funds" && (
                    <div style={{marginTop:4,fontSize:10,lineHeight:1.55}}>
                      <div style={{color:"#7ab8ff",fontWeight:700}}>
                        🏦 {s.insider.holders} holder{s.insider.holders === 1 ? "" : "s"} · {_usdShort(s.insider.value)}{s.insider.tenPct ? " · 10% owner" : ""} · {_isoAgo(s.insider.latest)}
                        <span style={{color:C.muted,fontWeight:600}}> · score {Math.round(s.insider.score)}</span>
                      </div>
                      <div style={{display:"flex",gap:6,flexWrap:"wrap",margin:"2px 0"}}>
                        {s.insider.priceLabel && s.insider.priceLabel !== "unknown" && (
                          <span title={s.insider.openPct != null ? `${s.insider.openPct}% of the dollars were priced inside that day's trading range` : ""}
                            style={{padding:"1px 6px",borderRadius:12,fontWeight:700,
                              color: s.insider.priceLabel === "open-market" ? "#00e676" : s.insider.priceLabel === "offering-like" ? "#ff7a7a" : C.yellow,
                              background: s.insider.priceLabel === "open-market" ? "rgba(0,230,118,0.13)" : s.insider.priceLabel === "offering-like" ? "rgba(255,82,82,0.14)" : "rgba(255,202,40,0.13)"}}>
                            {s.insider.priceLabel === "open-market" ? "Open-market priced" : s.insider.priceLabel === "offering-like" ? "Offering-like (off-market price)" : "Mixed pricing"}
                          </span>)}
                        {s.insider.activist13d && (
                          <span style={{padding:"1px 6px",borderRadius:12,fontWeight:700,color:"#ffca28",background:"rgba(255,202,40,0.13)"}}>📄 13D filed</span>)}
                      </div>
                      {(s.insider.who || []).length > 0 && (
                        <div style={{color:C.muted}}>
                          {s.insider.who.map(w => `${w.name}${w.ten ? " (10%)" : ""} ${_usdShort(w.value)}`).join(" · ")}
                        </div>)}
                      {s.insider.avgPrice != null && s.insider.vsPct != null && (
                        <div style={{color:C.muted}}>
                          {"Holders' avg $"}{Number(s.insider.avgPrice).toFixed(2)} → now{" "}
                          <span style={{fontWeight:700,color:s.insider.vsPct <= 0 ? C.green : C.yellow}}>
                            {s.insider.vsPct >= 0 ? "+" : ""}{s.insider.vsPct}%
                          </span>
                        </div>)}
                    </div>
                  )}
                  {/* v8.4.424 — Insider Buys list: who bought, how much, at what price vs now */}
                  {s.insider && s.insider.kind !== "funds" && (
                    <div style={{marginTop:4,fontSize:10,lineHeight:1.55}}>
                      <div style={{color:"#00e676",fontWeight:700}}>
                        👥 {s.insider.buyers} insiders · {_usdShort(s.insider.value)}{s.insider.cSuite ? " · C-suite" : ""} · {_isoAgo(s.insider.latest)}
                        <span style={{color:C.muted,fontWeight:600}}> · conviction {Math.round(s.insider.score)}</span>
                      </div>
                      {(s.insider.who || []).length > 0 && (
                        <div style={{color:C.muted}}>
                          {s.insider.who.map(w => `${w.name}${w.title ? " (" + w.title + ")" : ""} ${_usdShort(w.value)}`).join(" · ")}
                        </div>)}
                      {s.insider.avgPrice != null && s.insider.vsPct != null && (
                        <div style={{color:C.muted}}>
                          Insiders' avg ${Number(s.insider.avgPrice).toFixed(2)} → now{" "}
                          <span style={{fontWeight:700,color:s.insider.vsPct <= 0 ? C.green : C.yellow}}>
                            {s.insider.vsPct >= 0 ? "+" : ""}{s.insider.vsPct}%
                          </span>{s.insider.vsPct <= 0 ? " (at or below what insiders paid)" : " above insiders' price"}
                        </div>)}
                      {s.insider.sells > 0 && (
                        <div style={{color:C.yellow}}>Insiders also sold {_usdShort(s.insider.sells)} in this window</div>)}
                      {s.insider.offeringRisk && (
                        <div style={{color:C.yellow}}>⚠ Very large vs. the company's market cap — often a financing or PIPE, not open-market buying</div>)}
                    </div>
                  )}
                  {/* v8.4.298: sharp-drop chips on their own line, BELOW the name/sector. */}
                  {(s.bounceCandidate || s.rotationRisk) && (
                    <div style={{display:"flex",gap:6,flexWrap:"wrap",marginTop:3}}>
                      {s.bounceCandidate && <DropBounceFlag/>}
                      {s.rotationRisk && <DropRotationFlag/>}
                    </div>
                  )}
                  {/* Watchlist badge or + button */}
                  <div style={{marginTop:4}}>
                    {inWl
                      ? <span style={{fontSize:9,background:"rgba(0,230,118,0.12)",
                          color:C.green,borderRadius:4,padding:"1px 6px",
                          border:"1px solid rgba(0,230,118,0.3)",fontWeight:700}}>
                          ★ Watching
                        </span>
                      : <button onClick={e=>{e.stopPropagation();addToWatchlist(s.ticker);}}
                          disabled={addingToWl[s.ticker]} style={{
                          fontSize:9,background:"rgba(0,212,255,0.08)",color:C.accent,
                          borderRadius:4,padding:"2px 6px",fontWeight:700,
                          border:"1px solid rgba(0,212,255,0.2)",cursor:"pointer",
                          fontFamily:"inherit"}}>
                          {addingToWl[s.ticker]?"…":"+ Watchlist"}
                        </button>
                    }
                  </div>
                </div>
                {/* Price + 24h sparkline (added v7.9.54 — same component as
                    Watchlist row). Sparkline source: row's `sparkline` array
                    captured during scoreOne, which prefers the universeData
                    entry's sparkline (warm) and falls back to /analyze candles. */}
                <div style={{textAlign:"right",flexShrink:0,
                  display:"flex",flexDirection:"column",alignItems:"flex-end",gap:1}}>
                  <div style={{fontSize:15,fontWeight:700,fontFamily:"monospace",color:C.text}}>
                    {fmtCurrency(s.price, s.ticker, currency, fxRates)}
                  </div>
                  {/* v8.4.40: sparkline + mode-scoped change + tabs-below-bubble.
                      Matches the Watchlist layout exactly. */}
                  {(() => {
                    const requested = sparklineMode[s.ticker] || "intraday";
                    // v8.4.256: fall back to the shared universeData sparklines when THIS
                    // list's rows are missing one — some Discovery lists carry intraday-only
                    // (1D, no 3W toggle) or daily-only. The background scorer fills
                    // universeData with the 14d daily `sparkline`, so this restores the
                    // 1D·3W toggle consistently across every quicklist.
                    const _uni = (universeData && universeData[s.ticker]) || {};
                    const intraday  = (Array.isArray(s.sparklineIntraday) && s.sparklineIntraday.length >= 2)
                      ? s.sparklineIntraday
                      : (Array.isArray(_uni.sparklineIntraday) ? _uni.sparklineIntraday : []);
                    const daily     = (Array.isArray(s.sparkline) && s.sparkline.length >= 2)
                      ? s.sparkline
                      : (Array.isArray(_uni.sparkline) ? _uni.sparkline : []);
                    const hasI = intraday.length >= 2;
                    const hasD = daily.length >= 2;
                    const showIntra = (requested === "intraday" && hasI) || (!hasD);
                    const arr  = showIntra ? intraday : daily;
                    const canToggle = hasI && hasD;
                    // Mode-scoped % / $ change.
                    let pct, amt;
                    if (showIntra) {
                      pct = s.changePct;
                      amt = (s.changeAmt != null && s.changeAmt !== 0)
                        ? s.changeAmt
                        : (s.price && s.changePct != null
                            ? (s.price * s.changePct / (100 + s.changePct))
                            : null);
                    } else if (hasD) {
                      const anchor = daily[0];
                      const tip    = s.price || daily[daily.length-1];
                      if (anchor && tip) {
                        amt = tip - anchor;
                        pct = (amt / anchor) * 100;
                      }
                    }
                    const pctColor = (pct == null) ? C.muted
                      : (pct >= 0 ? C.green : C.red);
                    return (
                      <>
                        {(hasI || hasD) && (
                          <div style={{marginTop:2,marginBottom:1,height:14,
                            display:"flex",alignItems:"center"}}>
                            <Sparkline key={showIntra ? "i" : "d"} data={arr}
                              up={pct == null ? null : pct >= 0}/>
                          </div>
                        )}
                        {pct != null && (
                          <div style={{fontSize:11,color:pctColor,fontFamily:"monospace"}}>
                            {pct >= 0 ? "+" : ""}{pct.toFixed(2)}%
                          </div>
                        )}
                        {(amt != null && isFinite(amt)) && (
                          <div style={{fontSize:10,fontFamily:"monospace",color:pctColor}}>
                            {amt >= 0 ? "+" : ""}{amt >= 0 ? "$" : "-$"}{Math.abs(amt).toFixed(2)}
                          </div>
                        )}
                        <div style={{fontSize:9,marginTop:2}}>
                          <ListQuoteDot isLive={s.isLive}/>
                        </div>
                        {(hasI || hasD) && (
                          <div
                            onClick={canToggle ? (e) => { e.stopPropagation(); toggleSparklineMode(s.ticker); } : undefined}
                            title={canToggle ? "Tap to switch between today (1D) and the last 3 weeks" : ""}
                            style={{marginTop:3,
                              display:"inline-flex",alignItems:"center",gap:3,
                              fontSize:8,letterSpacing:"0.06em",
                              fontFamily:"monospace",lineHeight:1,userSelect:"none",
                              cursor: canToggle ? "pointer" : "default"}}>
                            {hasI && (
                              <span style={{
                                color: showIntra ? C.text : "rgba(255,255,255,0.28)",
                                fontWeight: showIntra ? 800 : 600}}>1D</span>
                            )}
                            {hasI && hasD && (
                              <span style={{color:"rgba(255,255,255,0.18)"}}>·</span>
                            )}
                            {hasD && (
                              <span style={{
                                color: !showIntra ? C.text : "rgba(255,255,255,0.28)",
                                fontWeight: !showIntra ? 800 : 600}}>3W</span>
                            )}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>

              {/* v8.4.441 — setup-quality chip first, then IBD Leader flag + RS Rating + Trending badge (moved out of the ticker row, where it overlapped the price) */}
              {(["strong","weak"].includes(universeData?.[String(s.ticker).toUpperCase()]?.setupQuality) || s.isLeader || s.rsRating >= 80 || s.isTrending) && (
                <div style={{display:"flex",alignItems:"center",flexWrap:"wrap",gap:6,marginTop:6}}>
                  <SetupQualityFlag q={universeData?.[String(s.ticker).toUpperCase()]?.setupQuality}/>
                  {s.isLeader && (
                    <span style={{display:"inline-flex",alignItems:"center",gap:4,
                      background:"rgba(255,202,40,0.12)",border:"1px solid rgba(255,202,40,0.35)",
                      borderRadius:20,padding:"2px 9px",fontSize:10,fontWeight:800,
                      color:"#ffca28"}}>
                      ⭐ IBD Leader
                    </span>
                  )}
                  {s.rsRating != null && (
                    <RSRatingPill rating={s.rsRating}/>
                  )}
                  {s.isTrending && (
                    <span style={{display:"inline-flex",alignItems:"center",gap:4,
                      background:"rgba(255,82,82,0.1)",border:"1px solid rgba(255,82,82,0.3)",
                      borderRadius:20,padding:"2px 9px",fontSize:10,fontWeight:800,
                      color:C.red}}>
                      🔥 #{s.trendingRank} StockTwits
                    </span>
                  )}
                </div>
              )}

              {/* Pattern badges */}
              {s.patterns?.length > 0 && (
                <div style={{display:"flex",flexWrap:"wrap",gap:4,marginTop:6}}>
                  {s.patterns.slice(0,3).map((p,i) => (
                    <span key={i} style={{
                      display:"inline-flex",alignItems:"center",gap:3,
                      background:p.type==="bullish"?"rgba(0,230,118,0.1)":
                                 p.type==="bearish"?"rgba(255,82,82,0.1)":"rgba(255,255,255,0.06)",
                      border:`1px solid ${p.type==="bullish"?"rgba(0,230,118,0.3)":
                                          p.type==="bearish"?"rgba(255,82,82,0.3)":"rgba(255,255,255,0.12)"}`,
                      borderRadius:20,padding:"2px 8px",fontSize:10,fontWeight:600,
                      color:p.type==="bullish"?C.green:p.type==="bearish"?C.red:C.muted}}>
                      {p.type==="bullish"?"▲":p.type==="bearish"?"▼":"●"} {p.name}
                    </span>
                  ))}
                </div>
              )}

              {/* Zombie flags — only shown on 🧟 Zombie list */}
              {s.zombieFlags?.length > 0 && (
                <div style={{display:"flex",flexWrap:"wrap",gap:4,marginTop:6}}>
                  {s.zombieFlags.map((flag,fi) => (
                    <span key={fi} style={{
                      display:"inline-flex",alignItems:"center",gap:3,
                      background:"rgba(255,82,82,0.08)",
                      border:"1px solid rgba(255,82,82,0.25)",
                      borderRadius:20,padding:"2px 8px",fontSize:9,fontWeight:700,
                      color:"#ff5252",letterSpacing:"0.02em"}}>
                      ✕ {flag}
                    </span>
                  ))}
                </div>
              )}

              {/* Altman Z-Score badge — shown when backend has balance-sheet data
                  (Zombie + Speculation lists). Zones: ≥3 Safe (green), 1.81-3 Grey (amber), <1.81 Distress (red) */}
              {s.altmanZ != null && (
                <div style={{marginTop:6}}>
                  <span style={{
                    display:"inline-flex",alignItems:"center",gap:5,
                    borderRadius:20,padding:"2px 9px",fontSize:9,fontWeight:700,
                    letterSpacing:"0.03em",
                    ...(s.altmanZZone === "safe"
                      ? {background:"rgba(0,230,118,0.10)",border:"1px solid rgba(0,230,118,0.30)",color:"#00e676"}
                      : s.altmanZZone === "grey"
                      ? {background:"rgba(255,202,40,0.10)",border:"1px solid rgba(255,202,40,0.30)",color:"#ffca28"}
                      : {background:"rgba(255,82,82,0.10)",border:"1px solid rgba(255,82,82,0.30)",color:"#ff5252"}),
                  }}>
                    Z {s.altmanZ.toFixed(1)} ·{" "}
                    {s.altmanZZone === "safe" ? "Safe zone"
                     : s.altmanZZone === "grey" ? "Grey zone"
                     : "Distress"}
                  </span>
                </div>
              )}

              {/* Scores */}
              <div style={{display:"flex",gap:8,marginTop:10,
                background:C.surface,borderRadius:10,padding:"8px 12px",
                alignItems:"center"}}>
                <ScoreDonut score={s.swingScore} size={38} label="Swing"/>
                <ScoreDonut score={s.ltScore}    size={38} label="LT"/>
                <div style={{flex:1,display:"flex",flexWrap:"wrap",gap:6,marginLeft:4}}>
                  {s.rsi != null && (
                    <span style={{display:"inline-flex",alignItems:"center",gap:4}}>
                      <span style={{fontSize:9,color:"rgba(255,255,255,0.35)"}}>RSI</span>
                      <RSIPill rsi={s.rsi} size="sm"/>
                    </span>
                  )}
                  {s.beta != null && (
                    <span style={{fontSize:10,color:s.beta>=2?C.red:s.beta>=1.5?"#ffca28":C.muted,
                      fontWeight:s.beta>=1.5?700:400}}>
                      β {s.beta.toFixed(1)}
                    </span>
                  )}
                  {s.horizonReturn != null && (
                    <>
                      <span style={{fontSize:10,color:C.muted}}>·</span>
                      <span style={{fontSize:10,
                        color:s.horizonReturn>=0?C.green:C.red}}>
                        {s.horizonReturn>=0?"+":""}{s.horizonReturn}% past {HORIZONS.find(h=>h.key===horizon)?.label.toLowerCase()}
                      </span>
                    </>
                  )}
                  {s.sentiment && (
                    <>
                      <span style={{fontSize:10,color:C.muted}}>·</span>
                      <span style={{fontSize:10,color:C.muted}}>
                        {sentimentIcon(s.sentiment)} {s.sentiment}
                      </span>
                    </>
                  )}
                </div>
                <div style={{fontSize:16,color:C.muted}}>›</div>
              </div>
            </Card>
            );
          })}
          {scanning && (
            <div style={{textAlign:"center",padding:"16px 0",fontSize:12,color:C.muted}}>
              Scoring more tickers…
            </div>
          )}
        </div>
        </ErrorBoundary>
      )}
    </div>
  );
}

// --- Update banner ------------------------------------------------------------
function useUpdateAvailable() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [serverVersion,   setServerVersion]   = useState(null);

  // v8.4.298: detect updates by comparing the RUNNING frontend bundle to the DEPLOYED
  // one (CRA asset-manifest.json → the hashed main.js). This fires on EVERY frontend
  // deploy — including frontend-only Vercel deploys that don't bump the backend /health
  // version, and patch bumps — which the old major.minor /health check silently missed
  // (why the banner never showed and users had to hunt for a manual reload). No stale
  // baseline race: we read the ACTUAL loaded main.[hash].js from the DOM, not a first
  // sample. cache:no-store bypasses the HTTP/PWA cache that pins old code on the device.
  useEffect(() => {
    let cancelled = false;

    const runningBundle = (() => {
      try {
        const src = Array.from(document.querySelectorAll("script[src]"))
          .map(s => s.getAttribute("src") || "")
          .find(s => /static\/js\/main\.[A-Za-z0-9]+\.js/.test(s));
        return src ? src.replace(/^https?:\/\/[^/]+/, "") : null;   // "/static/js/main.<hash>.js"
      } catch (_) { return null; }
    })();

    const check = async () => {
      if (!runningBundle || cancelled) return;
      try {
        const r = await fetch(`/asset-manifest.json?_=${Date.now()}`, { cache: "no-store" });
        if (!r.ok || cancelled) return;
        const m = await r.json();
        const deployed = m && m.files && m.files["main.js"];
        if (deployed && deployed !== runningBundle) {
          let sv = "latest";
          try { const h = await fetch(`${API}/health?_=${Date.now()}`); sv = (await h.json()).version || "latest"; } catch (_) {}
          if (!cancelled) { setServerVersion(sv); setUpdateAvailable(true); }
        }
      } catch (_) { /* silently skip */ }
    };

    const initial  = setTimeout(check, 8000);           // ~8s after load
    const interval = setInterval(check, 5 * 60 * 1000);  // re-check every 5 min
    const onFocus  = () => check();                      // and when the app regains focus
    window.addEventListener("focus", onFocus);
    return () => { cancelled = true; clearTimeout(initial); clearInterval(interval);
                   window.removeEventListener("focus", onFocus); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { updateAvailable, serverVersion };
}

function UpdateBanner({ serverVersion }) {
  const [dismissed, setDismissed] = useState(false);
  // v8.4.298: reload the way the admin "Reload App" button does — unregister the (push-
  // only) service worker + clear the Cache API + reload — so a home-screen PWA / stale
  // HTTP cache actually picks up the new build instead of re-serving old code.
  const reload = async () => {
    try {
      if ("serviceWorker" in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map(r => r.unregister()));
      }
      if ("caches" in window) {
        const names = await caches.keys();
        await Promise.all(names.map(n => caches.delete(n)));
      }
    } catch (_) {}
    window.location.reload();
  };
  const dismiss = () => setDismissed(true);
  if (dismissed) return null;
  return (
    <div style={{
      position:"fixed", top:0, left:"50%", transform:"translateX(-50%)",
      width:"100%", maxWidth:430, zIndex:9999,
      background:"linear-gradient(90deg,#0a1628,#0d1f3c)",
      borderBottom:"1px solid rgba(0,212,255,0.3)",
      // v8.4.68: pad above content with the iOS safe-area inset so the
      // Reload button isn't hidden under the notch / Dynamic Island /
      // status bar (clock + battery). The gradient still fills behind
      // the status bar; only the interactive row is pushed down.
      // Fallback 12px keeps a sensible gap on non-iOS browsers.
      padding:"calc(env(safe-area-inset-top, 12px) + 10px) 16px 10px",
      display:"flex", alignItems:"center", gap:10,
    }}>
      <span style={{fontSize:16,flexShrink:0}}>🆕</span>
      <div style={{flex:1}}>
        <div style={{fontSize:12,fontWeight:700,color:"#00d4ff"}}>
          Update available (v{serverVersion})
        </div>
        <div style={{fontSize:11,color:"rgba(255,255,255,0.5)",marginTop:1}}>
          Reload for the latest version, or dismiss to continue
        </div>
      </div>
      <div style={{display:"flex",gap:8,flexShrink:0}}>
        <button onClick={reload} style={{
          padding:"6px 12px",borderRadius:8,border:"none",cursor:"pointer",
          background:"#00d4ff",color:"#080c14",fontSize:12,
          fontWeight:700,fontFamily:"inherit",
        }}>Reload</button>
        <button onClick={dismiss} style={{
          padding:"6px 10px",borderRadius:8,
          border:"1px solid rgba(255,255,255,0.1)",
          cursor:"pointer",background:"none",color:"rgba(255,255,255,0.4)",
          fontSize:12,fontFamily:"inherit",
        }}>✕</button>
      </div>
    </div>
  );
}


// --- NamePromptBanner (v8.4.17) -----------------------------------------------
// Shown once per session when the logged-in user has no name set.
// Non-blocking: user can dismiss. Saves via PATCH /auth/profile {name}.
function NamePromptBanner({ token, user, onNameSaved }) {
  const [name,      setName]      = React.useState("");
  const [saving,    setSaving]    = React.useState(false);
  const [dismissed, setDismissed] = React.useState(false);
  if (dismissed) return null;
  const save = async () => {
    const n = name.trim();
    if (n.length < 2) return;
    setSaving(true);
    try {
      const updated = await apiFetch("/auth/profile", token, {
        method: "PATCH",
        body: JSON.stringify({ name: n }),
      });
      if (onNameSaved) onNameSaved(updated);
      setDismissed(true);
    } catch(e) {
      // silent — user can retry later
    } finally {
      setSaving(false);
    }
  };
  return (
    <div style={{
      position:"fixed", top:0, left:"50%", transform:"translateX(-50%)",
      width:"100%", maxWidth:430, zIndex:9998,
      background:"linear-gradient(90deg,#0a1628,#0d1f3c)",
      borderBottom:"1px solid rgba(255,165,0,0.4)",
      padding:"10px 16px",
      display:"flex", alignItems:"center", gap:10,
    }}>
      <span style={{fontSize:16,flexShrink:0}}>👤</span>
      <div style={{flex:1,minWidth:0}}>
        <div style={{fontSize:11,fontWeight:700,color:"#ffa500",marginBottom:4}}>
          What should we call you?
        </div>
        <div style={{display:"flex",gap:6}}>
          <input
            value={name}
            onChange={e => setName(e.target.value)}
            onKeyDown={e => e.key === "Enter" && save()}
            placeholder="Your name"
            style={{
              flex:1, background:"rgba(255,255,255,0.06)",
              border:"1px solid rgba(255,165,0,0.3)", borderRadius:6,
              padding:"5px 10px", color:"#fff", fontSize:12,
              fontFamily:"inherit", outline:"none",
            }}
          />
          <button onClick={save} disabled={saving || name.trim().length < 2} style={{
            padding:"5px 12px", borderRadius:6, border:"none",
            background: name.trim().length < 2 ? "rgba(255,165,0,0.3)" : "#ffa500",
            color:"#080c14", fontSize:12, fontWeight:700,
            fontFamily:"inherit", cursor: name.trim().length < 2 ? "default" : "pointer",
          }}>{saving ? "…" : "Save"}</button>
        </div>
      </div>
      <button onClick={() => setDismissed(true)} style={{
        background:"none", border:"none", color:"rgba(255,255,255,0.4)",
        fontSize:18, cursor:"pointer", flexShrink:0, padding:"4px",
      }}>✕</button>
    </div>
  );
}


// --- Metals & Crypto Widget ---------------------------------------------------
function MetalsCryptoWidget({ token, currency, fxRates }) {
  // v8.4.251: resilient fetch — keep the last good prices instead of blanking every
  // value to "—" on a cold/empty fetch; already refreshed every 5 min.
  const { data, loading, stale, updatedAt } = useResilientFetch("/market/metals-crypto", token, {
    isEmpty: (d) => !d || Object.keys(d).length === 0,
    refreshMs: 5 * 60 * 1000,
  });
  const [expanded, setExpanded] = React.useState({});

  const fmtPrice = (price, sym) => {
    if (price == null) return "—";
    // Metals and crypto are priced in USD natively
    if (currency && currency !== "native" && fxRates) {
      return fmtCurrency(price, "US", currency, fxRates); // "US" = not .TO = USD asset
    }
    if (sym === "BTC") return `$${price.toLocaleString("en", {maximumFractionDigits:0})}`;
    if (["XRP","SOL","ETH"].includes(sym)) return `$${price.toLocaleString("en", {minimumFractionDigits:2,maximumFractionDigits:2})}`;
    if (price >= 1000) return `$${price.toLocaleString("en", {maximumFractionDigits:0})}`;
    return `$${price.toFixed(2)}`;
  };

  const SIGNAL_STYLES = {
    strong_buy:  { bg:"rgba(0,230,118,0.15)",  color:"#00e676",  border:"rgba(0,230,118,0.3)"  },
    buy:         { bg:"rgba(0,230,118,0.08)",  color:"#69f0ae",  border:"rgba(0,230,118,0.2)"  },
    neutral:     { bg:"rgba(255,255,255,0.06)",color:"rgba(255,255,255,0.4)", border:"rgba(255,255,255,0.1)" },
    sell:        { bg:"rgba(255,82,82,0.08)",  color:"#ff7043",  border:"rgba(255,82,82,0.2)"  },
    strong_sell: { bg:"rgba(255,82,82,0.15)",  color:"#ff5252",  border:"rgba(255,82,82,0.3)"  },
  };
  const signalStyle = (type) => SIGNAL_STYLES[type] || SIGNAL_STYLES.neutral;

  const chgColor = pct => pct == null ? C.muted : pct >= 0 ? C.green : C.red;
  const chgLabel = pct => pct == null ? "" : `${pct >= 0 ? "+" : ""}${pct.toFixed(2)}%`;
  const trendIcon = t => t === "bullish" ? "↑" : t === "bearish" ? "↓" : "→";
  const trendColor = t => t === "bullish" ? C.green : t === "bearish" ? C.red : C.muted;

  // v8.3.22: cleaner precious-metals icons — stylised bullion bar SVG
  // instead of medal emoji. Same metallic gradient idea, no kitschy podium.
  const MetalBarIcon = ({ size = 22, color = "#d4af37", accent = "#fff5d0" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      style={{flexShrink:0, display:"block"}}>
      <defs>
        <linearGradient id={`mb-${color.slice(1)}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor={accent}/>
          <stop offset="40%"  stopColor={color}/>
          <stop offset="100%" stopColor={color} stopOpacity="0.6"/>
        </linearGradient>
      </defs>
      {/* Bar body — slight perspective via skewed quad */}
      <path d="M3 8 L21 7 L20 17 L4 18 Z"
            fill={`url(#mb-${color.slice(1)})`}
            stroke={color} strokeWidth="0.6" strokeOpacity="0.6"/>
      {/* Top highlight */}
      <path d="M3 8 L21 7" stroke={accent} strokeWidth="1.4" strokeLinecap="round"/>
      {/* Brand mark line */}
      <path d="M7 12 L17 11" stroke="rgba(0,0,0,0.18)" strokeWidth="0.6"/>
    </svg>
  );

  // v8.3.21: crypto icon URLs via CoinCap (no auth, returns transparent PNG).
  // Falls back to a tinted initials avatar if the image fails to load.
  const CRYPTO_ICON_MAP = {
    BTC: "btc", ETH: "eth", XRP: "xrp", SOL: "sol",
    DOGE: "doge", ADA: "ada", AVAX: "avax", DOT: "dot",
    MATIC: "matic", LINK: "link", LTC: "ltc",
  };
  const ASSET_ICON_BG = {
    BTC: "#f7931a", ETH: "#627eea", XRP: "#23292f", SOL: "#9945ff",
    GOLD: "#d4af37", SILVER: "#c0c0c0",
  };
  const AssetIcon = ({ sym, size = 22 }) => {
    const [errored, setErrored] = React.useState(false);
    const coinId = CRYPTO_ICON_MAP[sym];
    const bg = ASSET_ICON_BG[sym] || "rgba(255,255,255,0.08)";
    // Precious metals: stylised bullion bar instead of medal emoji (v8.3.22)
    if (sym === "GOLD") {
      return <MetalBarIcon size={size} color="#d4af37" accent="#fff5d0"/>;
    }
    if (sym === "SILVER") {
      return <MetalBarIcon size={size} color="#b8b8b8" accent="#f5f5f5"/>;
    }
    if (coinId && !errored) {
      return (
        <img src={`https://assets.coincap.io/assets/icons/${coinId}@2x.png`}
          alt={sym}
          onError={() => setErrored(true)}
          style={{width:size, height:size, borderRadius:"50%",
                  flexShrink:0, background:"#fff", objectFit:"cover"}}/>
      );
    }
    // Initials fallback (same look as TickerLogo for unknowns)
    return (
      <div style={{width:size, height:size, borderRadius:"50%",
                   background:`${bg}22`, border:`1px solid ${bg}55`,
                   display:"flex", alignItems:"center", justifyContent:"center",
                   fontSize:size*0.38, fontWeight:800, color:bg,
                   fontFamily:"monospace", flexShrink:0}}>
        {sym.slice(0,3)}
      </div>
    );
  };

  const AssetRow = ({ sym, item }) => {
    if (!item) return null;
    const ss   = signalStyle(item.signal_type);
    const open = expanded[sym];
    return (
      <div style={{marginBottom:6}}>
        {/* Main row */}
        <div onClick={()=>setExpanded(p=>({...p,[sym]:!p[sym]}))}
          style={{display:"flex",alignItems:"center",gap:10,
            background:C.surface,borderRadius:open?"12px 12px 0 0":12,
            padding:"9px 12px",border:`1px solid ${C.border}`,
            cursor:"pointer",transition:"border-radius 0.15s"}}>
          {/* Asset icon (v8.3.21) — crypto logo via CoinCap, metals via emoji */}
          <AssetIcon sym={sym} size={22}/>
          {/* Symbol */}
          <div style={{width:44,flexShrink:0}}>
            <div style={{fontSize:12,fontWeight:800,fontFamily:"monospace",
              color:C.text}}>{sym}</div>
            <div style={{fontSize:9,color:C.muted,marginTop:1}}>{item.label}</div>
          </div>
          {/* Price + change */}
          <div style={{flex:1}}>
            <div style={{fontSize:14,fontWeight:700,fontFamily:"monospace",
              color:C.text,lineHeight:1}}>
              {loading ? "—" : fmtPrice(item.price, sym)}
            </div>
            <div style={{fontSize:10,color:chgColor(item.chg_pct),marginTop:1}}>
              {chgLabel(item.chg_pct)}
              {item.perf_30d != null && (
                <span style={{color:C.muted,marginLeft:6}}>
                  30d: {item.perf_30d >= 0?"+":""}{item.perf_30d}%
                </span>
              )}
            </div>
          </div>
          {/* Signal pill */}
          <span style={{fontSize:9,fontWeight:700,borderRadius:20,
            padding:"2px 8px",flexShrink:0,
            background:ss.bg,color:ss.color,
            border:`1px solid ${ss.border}`}}>
            {item.signal || "—"}
          </span>
          {/* Chevron */}
          <span style={{fontSize:10,color:C.muted,flexShrink:0}}>
            {open?"▲":"▼"}
          </span>
        </div>

        {/* Expanded detail */}
        {open && (
          <div style={{background:"rgba(0,0,0,0.2)",borderRadius:"0 0 12px 12px",
            padding:"10px 12px",border:`1px solid ${C.border}`,borderTop:"none"}}>
            <div style={{display:"flex",gap:16,flexWrap:"wrap"}}>
              {/* RSI */}
              <div>
                <div style={{display:"flex",alignItems:"center",gap:3,fontSize:8,color:C.muted,
                  textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:2}}>
                  RSI
                  <InfoTip text="Relative Strength Index (0–100). Below 30 = oversold — asset may be due for a bounce. Above 70 = overbought — momentum may be fading. Most useful when diverging from price direction."/>
                </div>
                <div style={{fontSize:12,fontWeight:700,fontFamily:"monospace",
                  color:item.rsi<30?C.green:item.rsi>70?C.red:C.text}}>
                  {item.rsi ?? "—"}
                  <span style={{fontSize:9,color:C.muted,marginLeft:3}}>
                    {item.rsi<30?"Oversold":item.rsi>70?"Overbought":"Neutral"}
                  </span>
                </div>
              </div>
              {/* Trend */}
              <div>
                <div style={{display:"flex",alignItems:"center",gap:3,fontSize:8,color:C.muted,
                  textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:2}}>
                  Trend
                  <InfoTip text="Price vs 50-day and 200-day moving averages. Bullish = price above both MAs (uptrend). Bearish = price below both (downtrend). The 200-day MA is a key long-term support/resistance level watched by institutions."/>
                </div>
                <div style={{fontSize:12,fontWeight:700,
                  color:trendColor(item.trend)}}>
                  {trendIcon(item.trend)} {item.trend ?? "—"}
                  {item.above_50d != null && (
                    <span style={{fontSize:9,color:C.muted,marginLeft:4}}>
                      {item.above_50d?"above":"below"} 50d
                    </span>
                  )}
                </div>
              </div>
              {/* MACD */}
              <div>
                <div style={{display:"flex",alignItems:"center",gap:3,fontSize:8,color:C.muted,
                  textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:2}}>
                  MACD
                  <InfoTip text="Moving Average Convergence Divergence. Bullish = the fast EMA is above the slow EMA and momentum is accelerating (histogram rising). Bearish = opposite. Best used as a momentum confirmation alongside trend direction."/>
                </div>
                <div style={{fontSize:12,fontWeight:700,
                  color:trendColor(item.macd)}}>
                  {trendIcon(item.macd)} {item.macd ?? "—"}
                </div>
              </div>
              {/* Key levels */}
              {item.support && (
                <div>
                  <div style={{fontSize:8,color:C.muted,textTransform:"uppercase",
                    letterSpacing:"0.06em",marginBottom:2}}>Support / Resist</div>
                  <div style={{fontSize:11,fontFamily:"monospace",color:C.text}}>
                    <span style={{color:C.green}}>{fmtPrice(item.support,sym)}</span>
                    <span style={{color:C.muted}}> / </span>
                    <span style={{color:C.red}}>{fmtPrice(item.resistance,sym)}</span>
                  </div>
                </div>
              )}
              {/* Volume surge */}
              {item.vol_surge && (
                <div style={{alignSelf:"center"}}>
                  <span style={{fontSize:9,fontWeight:700,borderRadius:20,
                    padding:"2px 8px",background:"rgba(0,212,255,0.1)",
                    color:C.accent,border:"1px solid rgba(0,212,255,0.2)"}}>
                    ⚡ Vol surge
                  </span>
                </div>
              )}
              {/* DXY context for metals — color-coded dollar chip (value + trend) */}
              {item.dxy_context && (() => {
                const dxyVal = data?.dxy?.value;
                // Absolute-level headwind: strong dollar = metals headwind
                const lvlRed   = dxyVal != null && dxyVal >= 106;
                const lvlAmber = dxyVal != null && dxyVal >= 102 && dxyVal < 106;
                // Trend reinforces/overrides: falling USD = tailwind (bullish for metals)
                const bullish = item.dxy_context === "falling" && !lvlRed;
                const bearish = item.dxy_context === "rising" || lvlRed;
                const col = bullish ? C.green : bearish ? C.red : (lvlAmber ? "#ffca28" : C.muted);
                const arrow = item.dxy_context === "falling" ? "↓"
                            : item.dxy_context === "rising" ? "↑" : "→";
                const tail = bullish ? "tailwind" : bearish ? "headwind" : "neutral";
                return (
                  <div>
                    <div style={{fontSize:8,color:C.muted,textTransform:"uppercase",
                      letterSpacing:"0.06em",marginBottom:2}}>US Dollar (DXY)</div>
                    <span style={{display:"inline-flex",alignItems:"center",gap:5,
                      fontSize:11,fontWeight:700,borderRadius:20,padding:"3px 9px",
                      color:col,
                      background:col===C.green?"rgba(0,230,118,0.1)":
                                 col===C.red?"rgba(255,82,82,0.1)":
                                 col==="#ffca28"?"rgba(255,202,40,0.1)":"rgba(255,255,255,0.06)",
                      border:`1px solid ${col===C.green?"rgba(0,230,118,0.25)":
                              col===C.red?"rgba(255,82,82,0.25)":
                              col==="#ffca28"?"rgba(255,202,40,0.25)":"rgba(255,255,255,0.1)"}`}}>
                      {arrow} {dxyVal != null ? dxyVal.toFixed(1) : "USD"}
                      <span style={{fontWeight:600,opacity:0.85}}>· metals {tail}</span>
                    </span>
                  </div>
                );
              })()}
            </div>

            {/* Macro pills row — metals only */}
            {(item.real_rate != null || item.inflation != null ||
              item.uncertainty != null || item.supply_label != null) && (
              <div style={{marginTop:10,paddingTop:8,
                borderTop:"1px solid rgba(255,255,255,0.06)"}}>
                <div style={{fontSize:8,color:C.muted,textTransform:"uppercase",
                  letterSpacing:"0.06em",marginBottom:6,display:"flex",
                  alignItems:"center",gap:4}}>
                  Macro context
                  <InfoTip text="Macro factors that historically drive precious metals. Real Rates: negative real interest rates boost gold. Inflation: rising inflation expectations increase metals appeal as a store of value. Uncertainty: geopolitical stress and de-dollarization drive safe-haven demand. Supply: physical gold/silver demand vs ETF redemption activity as a COMEX inventory proxy."/>
                </div>
                <div style={{display:"flex",flexWrap:"wrap",gap:5}}>
                  {/* Real rates */}
                  {item.real_rate != null && (
                    <span style={{fontSize:9,fontWeight:600,borderRadius:20,
                      padding:"2px 8px",
                      background:item.real_rate_sig>0?"rgba(0,230,118,0.1)":
                                 item.real_rate_sig<0?"rgba(255,82,82,0.1)":"rgba(255,255,255,0.06)",
                      color:item.real_rate_sig>0?C.green:
                            item.real_rate_sig<0?C.red:C.muted,
                      border:`1px solid ${item.real_rate_sig>0?"rgba(0,230,118,0.25)":
                              item.real_rate_sig<0?"rgba(255,82,82,0.25)":"rgba(255,255,255,0.1)"}`}}>
                      {item.real_rate_sig>0?"📉":"📈"} Real Rates
                    </span>
                  )}
                  {/* Inflation */}
                  {item.inflation != null && (
                    <span style={{fontSize:9,fontWeight:600,borderRadius:20,
                      padding:"2px 8px",
                      background:item.inflation_sig>0?"rgba(255,202,40,0.1)":
                                 item.inflation_sig<0?"rgba(255,82,82,0.1)":"rgba(255,255,255,0.06)",
                      color:item.inflation_sig>0?"#ffca28":
                            item.inflation_sig<0?C.red:C.muted,
                      border:`1px solid ${item.inflation_sig>0?"rgba(255,202,40,0.25)":
                              item.inflation_sig<0?"rgba(255,82,82,0.25)":"rgba(255,255,255,0.1)"}`}}>
                      🔥 Inflation
                    </span>
                  )}
                  {/* Uncertainty / safe haven */}
                  {item.uncertainty != null && (
                    <span style={{fontSize:9,fontWeight:600,borderRadius:20,
                      padding:"2px 8px",
                      background:item.uncertainty_sig>0?"rgba(171,71,188,0.12)":
                                 item.uncertainty_sig<0?"rgba(255,82,82,0.08)":"rgba(255,255,255,0.06)",
                      color:item.uncertainty_sig>0?"#ce93d8":
                            item.uncertainty_sig<0?C.red:C.muted,
                      border:`1px solid ${item.uncertainty_sig>0?"rgba(171,71,188,0.3)":
                              item.uncertainty_sig<0?"rgba(255,82,82,0.2)":"rgba(255,255,255,0.1)"}`}}>
                      🌐 Safe Haven
                    </span>
                  )}
                  {/* COMEX Supply proxy */}
                  {item.supply_label != null && (
                    <span title={item.supply_pct!=null?`Volume vs 90d avg: ${item.supply_pct>0?"+":""}${item.supply_pct}%`:""}
                      style={{fontSize:9,fontWeight:600,borderRadius:20,
                      padding:"2px 8px",
                      background:item.supply_sig>0?"rgba(0,230,118,0.12)":
                                 item.supply_sig<0?"rgba(255,82,82,0.1)":"rgba(255,202,40,0.08)",
                      color:item.supply_sig>0?C.green:
                            item.supply_sig<0?C.red:"#ffca28",
                      border:`1px solid ${item.supply_sig>0?"rgba(0,230,118,0.3)":
                              item.supply_sig<0?"rgba(255,82,82,0.2)":"rgba(255,202,40,0.2)"}`}}>
                      ⛏ Supply: {item.supply_label}
                    </span>
                  )}
                  {/* Gold/Silver ratio */}
                  {item.gold_silver_ratio != null && (
                    <span style={{fontSize:9,fontWeight:600,borderRadius:20,
                      padding:"2px 8px",
                      background:item.gs_ratio_sig>0?"rgba(0,230,118,0.08)":"rgba(255,255,255,0.06)",
                      color:item.gs_ratio_sig>0?C.green:C.muted,
                      border:`1px solid ${item.gs_ratio_sig>0?"rgba(0,230,118,0.2)":"rgba(255,255,255,0.1)"}`}}>
                      G/S {item.gold_silver_ratio}x{item.gs_ratio_sig>0?" (silver cheap)":""}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const metals = data?.metals || {};
  const crypto = data?.crypto || {};

  // Don't render empty section headers when data fetch failed
  if (!loading && !data) return null;

  return (
    <div style={{marginBottom:14}}>
      {/* Precious Metals */}
      <div style={{fontSize:10,fontWeight:700,color:C.muted,
        letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:8}}>
        Precious Metals
        {data?.dxy_trend && (
          <span style={{marginLeft:8,fontSize:9,fontWeight:600,
            color:data.dxy_trend==="falling"?C.green:
                  data.dxy_trend==="rising"?C.red:C.muted,
            textTransform:"none",letterSpacing:0}}>
            · USD {data.dxy_trend}
          </span>
        )}
        {/* v8.4.332 — stale indicator: live feed unavailable, showing last known
            prices. Two triggers: (a) backend flagged data.stale (its cascade fell
            back to last-known), or (b) v8.4.356 the widget is showing module-cached
            data that hasn't confirmed-refreshed this view and is older than a refresh
            cycle (survives navigation instead of vanishing). */}
        {(data?.stale || (stale && updatedAt && (Date.now() - updatedAt > 6 * 60 * 1000))) && (() => {
          let ago = "";
          try {
            const raw = data?.staleAsOf || "";
            const t = raw ? new Date(raw + (!/[Zz]$/.test(raw) ? "Z" : "")) : new Date(updatedAt);
            const m = Math.max(0, Math.round((Date.now() - t.getTime()) / 60000));
            ago = m < 60 ? `${m}m ago` : m < 1440 ? `${Math.round(m/60)}h ago` : `${Math.round(m/1440)}d ago`;
          } catch (_) {}
          return (
            <span title="Live metals/crypto feed unavailable — showing last known prices"
              style={{marginLeft:8,fontSize:9,fontWeight:700,color:C.yellow,
                background:"rgba(255,202,40,0.14)",border:"1px solid rgba(255,202,40,0.4)",
                borderRadius:6,padding:"1px 6px",textTransform:"none",letterSpacing:0}}>
              ⚠ stale{ago ? ` · ${ago}` : ""}
            </span>
          );
        })()}
      </div>
      {["GOLD","SILVER"].map(sym => (
        <AssetRow key={sym} sym={sym} item={metals[sym]}/>
      ))}

      {/* Crypto */}
      <div style={{fontSize:10,fontWeight:700,color:C.muted,
        letterSpacing:"0.1em",textTransform:"uppercase",
        marginBottom:8,marginTop:14}}>
        Crypto
      </div>
      {["BTC","ETH","XRP","SOL"].map(sym => (
        <AssetRow key={sym} sym={sym} item={crypto[sym]}/>
      ))}

      {data?.ts && (
        <div style={{fontSize:8,color:"rgba(255,255,255,0.18)",
          textAlign:"right",marginTop:6}}>
          Technical signals · updated {typeof data.ts === "string" ? data.ts.slice(11,16) : ""} UTC
        </div>
      )}
    </div>
  );
}


// --- Profile Divergence Signal Card -------------------------------------------
// ─── GemPulse Gem Picks ───────────────────────────────────────────────────────
// A higher-conviction shortlist DISTINCT from "Top Swing Scores" (a raw swing
// sort). Backend /scores/gem-picks ranks by a multi-factor QUALITY composite —
// (swing+LT)/2 Combined, risk-adjusted by Beta, rewarded for analyst Coverage —
// gated to the user's risk profile. Surfaces the criteria that earned each pick
// its spot, with a final green Quality bubble.
function GemPicksCard({ token, horizon, onSelectTicker, currency, fxRates }) {
  const [data,    setData]    = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  // v8.4.250: was fetch-once + setData(anything) + render-null-on-empty, so a cold cache
  // (right after a redeploy, before the universe re-scores) left Gem Picks vanished with
  // no recovery. Now: keep the last good picks on an empty/failed fetch, and refresh so a
  // transient-empty first load self-heals. (Same class as gap scanner / trending / polymarket.)
  React.useEffect(() => {
    if (!token) return;
    let alive = true;
    const load = (isBg) => {
      if (!isBg) setLoading(true);
      apiFetch(`/scores/gem-picks?horizon=${horizon || "6m"}`, token)
        .then(r => { if (alive) { if (r?.picks?.length) setData(r); setLoading(false); } })
        .catch(() => { if (alive) setLoading(false); });
    };
    load(false);
    const iv = setInterval(() => load(true), 90 * 1000);   // recover cold-cache + refresh
    return () => { alive = false; clearInterval(iv); };
  }, [token, horizon]);

  if (loading || !data?.picks?.length) return null;
  const picks = data.picks;

  // Color helpers — Combined: higher better; Beta: LOWER better (risk); Coverage: more better.
  const cmbColor  = v => v >= 72 ? C.green : v >= 58 ? "#69f0ae" : v >= 42 ? C.yellow : C.red;
  const betaColor = b => b <= 1.0 ? C.green : b <= 1.6 ? "#69f0ae" : b <= 2.5 ? C.yellow : C.red;
  const covColor  = n => n >= 10 ? C.green : n >= 5 ? "#69f0ae" : n >= 1 ? C.yellow : C.muted;
  const qColor    = q => q >= 55 ? C.green : q >= 46 ? "#69f0ae" : C.yellow;
  const qBg       = q => q >= 55 ? "rgba(0,230,118,0.12)" : q >= 46 ? "rgba(105,240,174,0.12)" : "rgba(255,202,40,0.12)";
  const qBorder   = q => q >= 55 ? "rgba(0,230,118,0.32)" : q >= 46 ? "rgba(105,240,174,0.32)" : "rgba(255,202,40,0.32)";

  return (
    <div style={{background:C.surface, borderRadius:14, padding:"12px 14px",
      border:`1px solid ${C.border}`, marginBottom:14}}>
      {/* Header — matches Top Swing Scores / Profile Divergence, with SVG diamond */}
      <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10}}>
        <div style={{display:"flex", alignItems:"center", gap:6}}>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" style={{flexShrink:0}}>
            <path d="M6 3 H18 L22 9 L12 22 L2 9 Z" fill={C.accent}/>
            <path d="M2 9 H22 M6 3 L9.5 9 M18 3 L14.5 9 M9.5 9 L12 22 M14.5 9 L12 22"
              stroke="rgba(6,10,17,0.40)" strokeWidth="0.8" fill="none"/>
          </svg>
          <span style={{fontSize:10, fontWeight:700, color:C.muted,
            letterSpacing:"0.1em", textTransform:"uppercase"}}>
            Gem Picks
          </span>
          <InfoTip text="GemPulse Gem Picks rank by a multi-factor QUALITY composite, not raw swing score. It blends the Swing + Long-Term scores (Combined), risk-adjusts by Beta (lower is better), and rewards real analyst Coverage — then filters to your risk profile's thresholds. A high-swing but flimsy name (sky-high beta, no coverage, weak long-term) is excluded here, so only durable, risk-adjusted, higher-conviction setups make the list." />
        </div>
        <span style={{fontSize:9, color:C.muted, textTransform:"uppercase", letterSpacing:"0.05em"}}>
          {data.profile || ""} · quality-ranked
        </span>
      </div>

      {picks.map((p, i) => (
        <button key={p.ticker} onClick={() => onSelectTicker && onSelectTicker(p.ticker)}
          style={{width:"100%", display:"flex", alignItems:"center", gap:8, padding:"9px 0",
            background:"none", border:"none", cursor:"pointer", fontFamily:"inherit", textAlign:"left",
            borderBottom: i < picks.length-1 ? `1px solid ${C.border}` : "none"}}>
          <span style={{fontSize:10, color:C.muted, width:14, textAlign:"right", flexShrink:0}}>#{i+1}</span>
          <TickerLogo ticker={p.ticker} size={24}/>
          <div style={{flex:1, minWidth:0}}>
            <div style={{display:"flex", alignItems:"center", gap:6}}>
              <span style={{fontSize:13, fontWeight:800, fontFamily:"monospace", color:C.text}}>{p.ticker}</span>
              {p.isLeader && (
                <span style={{fontSize:8, color:"#ffca28", fontWeight:700,
                  background:"rgba(255,202,40,0.12)", borderRadius:8, padding:"1px 5px"}}>IBD</span>
              )}
              {p.price != null && (
                <span style={{fontSize:10, color:C.muted, fontFamily:"monospace"}}>
                  {fmtCurrency(p.price, p.ticker, currency, fxRates)}
                </span>
              )}
            </div>
            {/* Criteria that earned the pick — color-coded */}
            <div style={{display:"flex", gap:9, marginTop:3, fontFamily:"monospace", fontSize:10}}>
              <span style={{color:cmbColor(p.combinedScore)}}>CMB {p.combinedScore?.toFixed(0)}</span>
              <span style={{color:betaColor(p.beta)}}>β {p.beta?.toFixed(2)}</span>
              <span style={{color:covColor(p.numAnalysts)}}>
                {p.numAnalysts > 0 ? `COV ${p.numAnalysts}` : "COV —"}
              </span>
            </div>
          </div>
          {/* Final Quality bubble */}
          <span style={{fontSize:12, fontWeight:800, fontFamily:"monospace",
            color:qColor(p.quality), background:qBg(p.quality),
            border:`1px solid ${qBorder(p.quality)}`, borderRadius:10,
            padding:"3px 9px", minWidth:34, textAlign:"center", flexShrink:0}}>
            {p.quality?.toFixed(0)}
          </span>
        </button>
      ))}
    </div>
  );
}


function ProfileDivergenceCard({ token, horizon, onSelectTicker }) {
  const [data,     setData]     = React.useState(null);
  const [loading,  setLoading]  = React.useState(true);
  const [expanded, setExpanded] = React.useState(false);
  const [showInfo, setShowInfo] = React.useState(false);

  React.useEffect(() => {
    setLoading(true);
    apiFetch(`/ml/divergence?horizon=${horizon || "6m"}&min_divergence=10&limit=10`, token)
      .then(r => { setData(r); setLoading(false); })
      .catch(() => setLoading(false));
  }, [token, horizon]);

  if (loading || !data?.data?.length) return null;

  const top3 = data.data.slice(0, 3);
  const rest = data.data.slice(3);
  const maxDiv = data.data[0]?.divergence || 1;

  const Row = ({ item, isLast }) => (
    <button
      onClick={() => onSelectTicker && onSelectTicker(item.ticker)}
      style={{
        width:"100%", display:"flex", alignItems:"center", gap:8,
        padding:"8px 0", background:"none", border:"none", cursor:"pointer",
        fontFamily:"inherit", textAlign:"left",
        borderBottom: isLast ? "none" : `1px solid ${C.border}`,
      }}
    >
      <TickerLogo ticker={item.ticker} size={22} />
      <span style={{
        fontSize:13, fontWeight:800, fontFamily:"monospace",
        color:C.text, width:52, flexShrink:0,
      }}>{item.ticker}</span>
      <div style={{flex:1, minWidth:0}}>
        <div style={{display:"flex", gap:6, marginBottom:3}}>
          <span style={{fontSize:10, color:C.accent}}>AGG {item.aggressive_score.toFixed(0)}</span>
          <span style={{fontSize:10, color:C.muted}}>MOD {item.moderate_score.toFixed(0)}</span>
          <span style={{fontSize:10, color:"#ff8080"}}>CON {item.conservative_score.toFixed(0)}</span>
        </div>
        <div style={{height:3, borderRadius:2, background:"rgba(255,255,255,0.07)", overflow:"hidden"}}>
          <div style={{
            height:"100%", borderRadius:2,
            width:`${Math.round((item.divergence / maxDiv) * 100)}%`,
            background:"linear-gradient(90deg,#00d4ff,#7b61ff)",
          }}/>
        </div>
      </div>
      <span style={{
        fontWeight:700, fontSize:13, fontFamily:"monospace",
        minWidth:28, textAlign:"right",
        color: item.divergence > 30 ? C.red : item.divergence > 20 ? C.yellow : C.accent,
      }}>+{item.divergence.toFixed(0)}</span>
    </button>
  );

  return (
    <>
      {/* Info modal */}
      {showInfo && (
        <div
          onClick={() => setShowInfo(false)}
          style={{
            position:"fixed", inset:0, zIndex:1000,
            background:"rgba(0,0,0,0.72)",
            display:"flex", alignItems:"center", justifyContent:"center",
            padding:"0 20px", boxSizing:"border-box",
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background:C.surface, border:`1px solid ${C.border}`,
              borderRadius:16, padding:"22px 20px",
              width:"100%", maxWidth:380, boxSizing:"border-box",
            }}
          >
            <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14}}>
              <div style={{display:"flex", alignItems:"center", gap:8}}>
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <circle cx="7" cy="7" r="6.5" stroke={C.muted} strokeWidth="1"/>
                  <path d="M7 3.5v4M7 9.5v1" stroke={C.muted} strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                <span style={{fontWeight:700, fontSize:10, color:C.muted,
                  letterSpacing:"0.1em", textTransform:"uppercase"}}>
                  Profile Divergence
                </span>
              </div>
              <button
                onClick={() => setShowInfo(false)}
                style={{background:"none", border:"none", color:C.muted,
                  fontSize:16, cursor:"pointer", padding:"0 2px", lineHeight:1}}
              >✕</button>
            </div>
            <p style={{fontSize:13, color:"rgba(255,255,255,0.75)", lineHeight:1.65, margin:"0 0 12px"}}>
              Each stock is scored three ways — <strong style={{color:C.accent}}>Aggressive</strong>,{" "}
              <strong style={{color:C.text}}>Moderate</strong>, and{" "}
              <strong style={{color:"#ff8080"}}>Conservative</strong> — weighting growth, risk,
              and stability differently based on the selected risk profile.
            </p>
            <p style={{fontSize:13, color:"rgba(255,255,255,0.75)", lineHeight:1.65, margin:"0 0 12px"}}>
              The <strong style={{color:C.text}}>divergence score</strong> is the gap between
              Aggressive and Conservative. A large gap means the two profiles strongly disagree
              on the stock's merit — flagging higher-risk, higher-conviction plays.
            </p>
            <div style={{fontSize:12, color:"rgba(255,255,255,0.55)", lineHeight:1.8,
              paddingLeft:12, borderLeft:`2px solid rgba(0,212,255,0.25)`}}>
              <div><span style={{color:C.red, fontWeight:700}}>Red 30+</span> — high disagreement, elevated risk</div>
              <div><span style={{color:C.yellow, fontWeight:700}}>Yellow 20–30</span> — notable gap, proceed carefully</div>
              <div><span style={{color:C.accent, fontWeight:700}}>Blue &lt;20</span> — mild divergence, more consensus</div>
            </div>
            <p style={{fontSize:11, color:C.muted, lineHeight:1.6, margin:"12px 0 0"}}>
              Tap any ticker to open its full analysis. Updates daily with the universe scan.
            </p>
          </div>
        </div>
      )}

      <div style={{background:C.surface, borderRadius:14, padding:"12px 14px",
        border:`1px solid ${C.border}`, marginBottom:14}}>
        {/* Header — matches Top Swing Scores style exactly */}
        <div style={{display:"flex", justifyContent:"space-between",
          alignItems:"center", marginBottom:10}}>
          <div style={{display:"flex", alignItems:"center", gap:6}}>
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none" style={{flexShrink:0}}>
              <path d="M5 0L6.12 3.45H9.76L6.82 5.59L7.94 9.04L5 6.9L2.06 9.04L3.18 5.59L0.24 3.45H3.88L5 0Z"
                fill={C.muted}/>
            </svg>
            <span style={{fontSize:10, fontWeight:700, color:C.muted,
              letterSpacing:"0.1em", textTransform:"uppercase"}}>
              Profile Divergence
            </span>
            <button
              onClick={e => { e.stopPropagation(); setShowInfo(true); }}
              style={{
                background:"none", border:"none", cursor:"pointer", padding:"0 2px",
                display:"flex", alignItems:"center", justifyContent:"center",
              }}
            >
              <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                <circle cx="6.5" cy="6.5" r="6" stroke={C.muted} strokeWidth="1"/>
                <path d="M6.5 3.5v3.5M6.5 9v1" stroke={C.muted} strokeWidth="1.4" strokeLinecap="round"/>
              </svg>
            </button>
          </div>
          <button
            onClick={() => setExpanded(p => !p)}
            style={{background:"none", border:"none", cursor:"pointer",
              color:C.muted, fontSize:9, fontFamily:"inherit", padding:0}}
          >
            {expanded ? "▲ less" : `▼ +${rest.length} more`}
          </button>
        </div>

        {/* Rows */}
        {top3.map((item, i) => (
          <Row key={item.ticker} item={item} isLast={!expanded && i === top3.length - 1} />
        ))}
        {expanded && rest.map((item, i) => (
          <Row key={item.ticker} item={item} isLast={i === rest.length - 1} />
        ))}
      </div>
    </>
  );
}

// --- ML Score Accuracy Card ---------------------------------------------------
function MLScoreAccuracyCard({ token, horizon }) {
  const [data,     setData]    = React.useState(null);
  const [loading,  setLoading] = React.useState(true);
  const [showInfo, setShowInfo] = React.useState(false);

  React.useEffect(() => {
    setLoading(true);
    apiFetch(`/ml/accuracy?horizon=${horizon || "1m"}&min_score=0`, token)
      .then(r => { setData(r); setLoading(false); })
      .catch(() => setLoading(false));
  }, [token, horizon]);

  if (loading || !data || data.status !== "ok" || !data.buckets?.length) return null;

  const buckets = data.buckets.filter(b => b.n >= 3);
  if (!buckets.length) return null;

  const hLabel = { "1m":"1-month","3m":"3-month","6m":"6-month","1y":"1-year" }[data.horizon] || data.horizon;

  return (
    <>
      {showInfo && (
        <div onClick={() => setShowInfo(false)} style={{
          position:"fixed",inset:0,zIndex:1000,background:"rgba(0,0,0,0.72)",
          display:"flex",alignItems:"center",justifyContent:"center",
          padding:"0 20px",boxSizing:"border-box",
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            background:C.surface,border:`1px solid ${C.border}`,
            borderRadius:16,padding:"22px 20px",
            width:"100%",maxWidth:380,boxSizing:"border-box",
          }}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <circle cx="7" cy="7" r="6.5" stroke={C.muted} strokeWidth="1"/>
                  <path d="M7 3.5v4M7 9.5v1" stroke={C.muted} strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                <span style={{fontWeight:700,fontSize:10,color:C.muted,
                  letterSpacing:"0.1em",textTransform:"uppercase"}}>Score Accuracy</span>
              </div>
              <button onClick={() => setShowInfo(false)} style={{
                background:"none",border:"none",color:C.muted,
                fontSize:16,cursor:"pointer",padding:"0 2px",lineHeight:1,
              }}>✕</button>
            </div>
            <p style={{fontSize:13,color:"rgba(255,255,255,0.75)",lineHeight:1.65,margin:"0 0 12px"}}>
              Each row shows how stocks in that score range actually performed over a{" "}
              {hLabel} holding period. Based on {data.total_rows.toLocaleString()} historical entries
              since scoring began April 2026.
            </p>
            <p style={{fontSize:13,color:"rgba(255,255,255,0.75)",lineHeight:1.65,margin:"0 0 12px"}}>
              <strong style={{color:C.text}}>Win Rate</strong> is the % of entries that were
              positive after the holding period. <strong style={{color:C.text}}>Avg Return</strong>{" "}
              includes losers.
            </p>
            <div style={{fontSize:12,color:"rgba(255,255,255,0.55)",lineHeight:1.8,
              paddingLeft:12,borderLeft:`2px solid rgba(0,212,255,0.25)`}}>
              <div>Past results do not guarantee future returns.</div>
              <div>Accuracy improves as more history accumulates.</div>
            </div>
          </div>
        </div>
      )}
      <div style={{background:C.surface,borderRadius:14,padding:"12px 14px",
        border:`1px solid ${C.border}`,marginBottom:14}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
          <div style={{display:"flex",alignItems:"center",gap:6}}>
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none" style={{flexShrink:0}}>
              <rect x="0" y="5" width="2" height="5" fill={C.muted}/>
              <rect x="3" y="3" width="2" height="7" fill={C.muted}/>
              <rect x="6" y="0" width="2" height="10" fill={C.muted}/>
            </svg>
            <span style={{fontSize:10,fontWeight:700,color:C.muted,
              letterSpacing:"0.1em",textTransform:"uppercase"}}>Score Accuracy</span>
            <span style={{fontSize:10,color:C.muted}}>· {hLabel}</span>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <span style={{fontSize:10,color:C.muted}}>{data.total_rows.toLocaleString()} trades</span>
            <button onClick={() => setShowInfo(true)} style={{
              background:"none",border:`1px solid ${C.border}`,
              borderRadius:"50%",width:18,height:18,
              display:"flex",alignItems:"center",justifyContent:"center",
              cursor:"pointer",padding:0,flexShrink:0,
            }}>
              <svg width="9" height="9" viewBox="0 0 14 14" fill="none">
                <circle cx="7" cy="7" r="6.5" stroke={C.muted} strokeWidth="1"/>
                <path d="M7 3.5v4M7 9.5v1" stroke={C.muted} strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </button>
          </div>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"60px 1fr 1fr 1fr",
          gap:4,paddingBottom:6,marginBottom:4,borderBottom:`1px solid ${C.border}`}}>
          {["Score","Trades","Avg Ret","Win %"].map(h => (
            <span key={h} style={{fontSize:9,color:C.muted,textTransform:"uppercase",
              letterSpacing:"0.07em",textAlign:h==="Score"?"left":"right"}}>{h}</span>
          ))}
        </div>
        {buckets.map((b, i) => {
          const retColor = b.avg_return > 2 ? C.green
            : b.avg_return > 0 ? "#69f0ae"
            : b.avg_return > -2 ? C.yellow : C.red;
          const winColor = b.win_rate_pct >= 60 ? C.green
            : b.win_rate_pct >= 50 ? C.yellow : C.red;
          return (
            <div key={b.bucket_floor} style={{
              display:"grid",gridTemplateColumns:"60px 1fr 1fr 1fr",
              gap:4,alignItems:"center",
              paddingBottom:i < buckets.length-1 ? 6 : 0,
              marginBottom:i < buckets.length-1 ? 6 : 0,
              borderBottom:i < buckets.length-1 ? `1px solid ${C.divider}` : "none",
            }}>
              <span style={{fontSize:11,fontWeight:700,fontFamily:"monospace",color:C.text}}>
                {b.bucket_floor}–{b.bucket_floor + data.bucket_size}
              </span>
              <span style={{fontSize:11,color:C.muted,fontFamily:"monospace",textAlign:"right"}}>
                {b.n}
              </span>
              <span style={{fontSize:12,fontWeight:700,fontFamily:"monospace",
                textAlign:"right",color:retColor}}>
                {b.avg_return > 0 ? "+" : ""}{b.avg_return.toFixed(1)}%
              </span>
              <span style={{fontSize:12,fontWeight:700,fontFamily:"monospace",
                textAlign:"right",color:winColor}}>
                {b.win_rate_pct.toFixed(0)}%
              </span>
            </div>
          );
        })}
      </div>
    </>
  );
}

// --- ML Backtest Card ---------------------------------------------------------
function MLBacktestCard({ token, horizon, profile }) {
  const [data,     setData]     = React.useState(null);
  const [loading,  setLoading]  = React.useState(true);
  const [expanded, setExpanded] = React.useState(false);
  const [showInfo, setShowInfo] = React.useState(false);

  React.useEffect(() => {
    setLoading(true);
    apiFetch(
      `/ml/backtest?horizon=${horizon || "1m"}&min_score=70&profile=${profile || "moderate"}`,
      token,
    )
      .then(r => { setData(r); setLoading(false); })
      .catch(() => setLoading(false));
  }, [token, horizon, profile]);

  if (loading || !data || data.status !== "ok") return null;

  const s = data.summary;
  const hLabel = { "1m":"1 Month","3m":"3 Months","6m":"6 Months","1y":"1 Year" }[data.horizon] || data.horizon;

  const Tile = ({ label, value, color }) => (
    <div style={{
      background:C.card,borderRadius:10,padding:"8px 6px",
      textAlign:"center",flex:1,minWidth:0,
    }}>
      <div style={{fontSize:14,fontWeight:800,fontFamily:"monospace",
        color:color||C.text,marginBottom:2}}>{value}</div>
      <div style={{fontSize:9,color:C.muted,textTransform:"uppercase",
        letterSpacing:"0.07em",lineHeight:1.3}}>{label}</div>
    </div>
  );

  return (
    <>
      {showInfo && (
        <div onClick={() => setShowInfo(false)} style={{
          position:"fixed",inset:0,zIndex:1000,background:"rgba(0,0,0,0.72)",
          display:"flex",alignItems:"center",justifyContent:"center",
          padding:"0 20px",boxSizing:"border-box",
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            background:C.surface,border:`1px solid ${C.border}`,
            borderRadius:16,padding:"22px 20px",
            width:"100%",maxWidth:380,boxSizing:"border-box",
          }}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <circle cx="7" cy="7" r="6.5" stroke={C.muted} strokeWidth="1"/>
                  <path d="M7 3.5v4M7 9.5v1" stroke={C.muted} strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                <span style={{fontWeight:700,fontSize:10,color:C.muted,
                  letterSpacing:"0.1em",textTransform:"uppercase"}}>Score Backtest</span>
              </div>
              <button onClick={() => setShowInfo(false)} style={{
                background:"none",border:"none",color:C.muted,
                fontSize:16,cursor:"pointer",padding:"0 2px",lineHeight:1,
              }}>✕</button>
            </div>
            <p style={{fontSize:13,color:"rgba(255,255,255,0.75)",lineHeight:1.65,margin:"0 0 12px"}}>
              Simulates buying every stock that scored ≥70 and holding for {hLabel}.
              Alpha is the strategy's avg return minus SPY's avg return over the same windows.
            </p>
            <p style={{fontSize:13,color:"rgba(255,255,255,0.75)",lineHeight:1.65,margin:"0 0 12px"}}>
              Results use actual historical closes adjusted for splits and dividends.
              Based on scoring data since April 2026 — accuracy improves as more periods complete.
            </p>
            <div style={{fontSize:12,color:"rgba(255,255,255,0.55)",lineHeight:1.8,
              paddingLeft:12,borderLeft:`2px solid rgba(0,212,255,0.25)`}}>
              <div>Past results do not guarantee future returns.</div>
            </div>
          </div>
        </div>
      )}
      <div style={{background:C.surface,borderRadius:14,padding:"12px 14px",
        border:`1px solid ${C.border}`,marginBottom:14}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
          <div style={{display:"flex",alignItems:"center",gap:6}}>
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none" style={{flexShrink:0}}>
              <path d="M1 8L4 4L7 5.5L9 1" stroke={C.muted} strokeWidth="1.5"
                strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span style={{fontSize:10,fontWeight:700,color:C.muted,
              letterSpacing:"0.1em",textTransform:"uppercase"}}>Score ≥70 Backtest</span>
            <span style={{fontSize:10,color:C.muted}}>· {hLabel}</span>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <span style={{fontSize:10,color:C.muted}}>{s.trades} trades</span>
            <button onClick={() => setShowInfo(true)} style={{
              background:"none",border:`1px solid ${C.border}`,
              borderRadius:"50%",width:18,height:18,
              display:"flex",alignItems:"center",justifyContent:"center",
              cursor:"pointer",padding:0,flexShrink:0,
            }}>
              <svg width="9" height="9" viewBox="0 0 14 14" fill="none">
                <circle cx="7" cy="7" r="6.5" stroke={C.muted} strokeWidth="1"/>
                <path d="M7 3.5v4M7 9.5v1" stroke={C.muted} strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </button>
          </div>
        </div>
        <div style={{display:"flex",gap:6,marginBottom:10}}>
          <Tile
            label="Strategy"
            value={`${s.avg_return_pct > 0 ? "+" : ""}${s.avg_return_pct.toFixed(1)}%`}
            color={s.avg_return_pct > 0 ? C.green : C.red}
          />
          <Tile
            label="vs SPY"
            value={s.avg_spy_return_pct != null
              ? `${s.avg_spy_return_pct > 0 ? "+" : ""}${s.avg_spy_return_pct.toFixed(1)}%`
              : "—"}
            color={s.avg_spy_return_pct != null
              ? (s.avg_spy_return_pct > 0 ? C.green : C.red) : C.muted}
          />
          <Tile
            label="Alpha"
            value={s.alpha_pct != null
              ? `${s.alpha_pct > 0 ? "+" : ""}${s.alpha_pct.toFixed(1)}%`
              : "—"}
            color={s.alpha_pct != null
              ? (s.alpha_pct > 0 ? C.accent : C.red) : C.muted}
          />
          <Tile
            label="Win Rate"
            value={`${s.win_rate_pct.toFixed(0)}%`}
            color={s.win_rate_pct >= 60 ? C.green : s.win_rate_pct >= 50 ? C.yellow : C.red}
          />
        </div>
        {data.sector_breakdown?.length > 0 && (
          <>
            <button onClick={() => setExpanded(e => !e)} style={{
              background:"none",border:"none",width:"100%",
              display:"flex",alignItems:"center",justifyContent:"space-between",
              padding:"6px 0 0",cursor:"pointer",borderTop:`1px solid ${C.divider}`,
              fontFamily:"inherit",
            }}>
              <span style={{fontSize:10,color:C.muted,textTransform:"uppercase",
                letterSpacing:"0.07em"}}>Sector Breakdown</span>
              <span style={{fontSize:11,color:C.muted}}>{expanded ? "▲" : "▼"}</span>
            </button>
            {expanded && (
              <div style={{marginTop:8}}>
                <div style={{display:"grid",gridTemplateColumns:"1fr 44px 60px 50px",
                  gap:4,paddingBottom:4,marginBottom:4,borderBottom:`1px solid ${C.border}`}}>
                  {["Sector","N","Avg","Win%"].map(h => (
                    <span key={h} style={{fontSize:9,color:C.muted,textTransform:"uppercase",
                      letterSpacing:"0.07em",textAlign:h==="Sector"?"left":"right"}}>{h}</span>
                  ))}
                </div>
                {data.sector_breakdown.map((sec, i) => {
                  const retColor = sec.avg_return > 2 ? C.green
                    : sec.avg_return > 0 ? "#69f0ae"
                    : sec.avg_return > -2 ? C.yellow : C.red;
                  return (
                    <div key={sec.sector} style={{
                      display:"grid",gridTemplateColumns:"1fr 44px 60px 50px",
                      gap:4,padding:"4px 0",alignItems:"center",
                      borderBottom:i < data.sector_breakdown.length-1
                        ? `1px solid ${C.divider}` : "none",
                    }}>
                      <span style={{fontSize:11,color:C.muted,overflow:"hidden",
                        textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{sec.sector}</span>
                      <span style={{fontSize:11,color:C.muted,
                        fontFamily:"monospace",textAlign:"right"}}>{sec.trades}</span>
                      <span style={{fontSize:12,fontWeight:700,fontFamily:"monospace",
                        textAlign:"right",color:retColor}}>
                        {sec.avg_return > 0 ? "+" : ""}{sec.avg_return.toFixed(1)}%
                      </span>
                      <span style={{fontSize:12,fontWeight:700,fontFamily:"monospace",
                        textAlign:"right",
                        color:sec.win_rate_pct>=60?C.green:sec.win_rate_pct>=50?C.yellow:C.red}}>
                        {sec.win_rate_pct.toFixed(0)}%
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}

// --- Technical Confirmation Card (v8.4.190) ------------------------------------
const TC_SIGNAL_INFO = {
  "RSI": {
    title: "RSI — Relative Strength Index",
    body: "Relative Strength Index (0-100). Below 30 = oversold (potential buy). Above 70 = overbought (potential pullback). 45-55 = neutral momentum. Weight shifts by profile: aggressive accepts RSI 70-80 as strong momentum; conservative treats RSI >65 as elevated risk.",
  },
  "ATR %": {
    title: "ATR % — Daily Volatility",
    body: "Average True Range as a % of price — the average daily price movement. Higher ATR = bigger daily swings. For aggressive profiles, ATR 1-8% is ideal (more profit potential). For conservative, 0.5-3% is preferred. Also useful for setting stop-loss distances.",
  },
  "Beta": {
    title: "Beta — Market Sensitivity",
    body: "Beta measures volatility vs the S&P 500. Beta 1.0 = moves with the market. >1.5 = amplified swings. <0.5 = low volatility stock or ETF. Threshold shifts by profile: conservative max β=1.0, moderate max β=1.4, aggressive allows up to β=3.0.",
  },
  "RS Rating": {
    title: "RS Rating — Relative Strength",
    body: "Relative Strength Rating (1-99). Measures price performance vs the S&P 500 universe over the past 12 months. 80+ = market leader. 60-79 = above average. Below 40 = underperforming the market. Factored into your Swing Score as a momentum signal.",
  },
  "Trend": {
    title: "Trend Direction",
    body: "Price vs 50-day and 200-day moving averages. Bullish = price above both MAs (uptrend). Bearish = price below both (downtrend). Neutral = mixed. Strong alignment (all three: price > 50d > 200d) is a textbook uptrend confirmation.",
  },
  "P/E": {
    title: "P/E — Price-to-Earnings",
    body: "Forward Price-to-Earnings ratio. Compares stock price to expected earnings per share. Below 15 = potentially cheap. 15-25 = fairly valued. Above 40 = priced for high growth — more risk if growth disappoints. Weighs 3× more for LT horizons than swing.",
  },
  "PEG": {
    title: "PEG — Price/Earnings-to-Growth",
    body: "PEG = forward P/E ÷ expected earnings growth %. Below 1 = potentially undervalued for the growth on offer (classic Lynch). 1–1.5 = fair. 1.5–2 = getting expensive. Above 2 = priced for perfection. Feeds into your Long-Term score (±8 max) and Swing score (±3 max).",
  },
  "EPS Growth": {
    title: "EPS Growth Rate",
    body: "Earnings Per Share growth rate — how fast the company's profit per share is growing. Above 20% = high growth. 5-20% = moderate. Below 0% = earnings are shrinking. For aggressive LT profiles, the green threshold is 30%+.",
  },
  "FCF/share": {
    title: "Free Cash Flow per Share",
    body: "Free Cash Flow per share (trailing 12 months). Positive = generating real cash after capex. $2+/share = strong. Negative = cash-burning. Computed as operating cash flow minus capex, divided by shares outstanding. Weighs 2× more for LT horizons.",
  },
  "Debt/Equity": {
    title: "Debt / Equity Ratio",
    body: "Debt / Equity ratio — how much long-term debt relative to stockholders equity. Below 0.5 = conservative, low-leverage. 0.5-1.0 = moderate. Above 2 = heavily leveraged. Weighs 2× more for LT horizons where debt service risk compounds.",
  },
  "Current Ratio": {
    title: "Current Ratio — Liquidity",
    body: "Current Ratio = current assets / current liabilities. Below 1.0 = potential liquidity risk. 1.5-2.0 = adequate. Above 2.0 = well-capitalized short-term. Most healthy companies sit between 1.5 and 3. Weighs 2× more for LT horizons.",
  },
  "composite": {
    title: "Technical Confirmation Score",
    body: "A weighted 0–100 composite across up to 11 signals (momentum + fundamentals). Weights shift by your risk profile and horizon: momentum signals (RSI, ATR, Beta, RS, Trend) weigh more for swing; fundamental signals (P/E, PEG, EPS, FCF, D/E, CR) weigh more for LT. CONFIRMED (≥70) / MIXED (45–69) / WEAK (<45). Complements your Swing and LT scores — does NOT replace them.",
  },
};

const TC_COLOR = { CONFIRMED: "#00e676", MIXED: "#ffca28", WEAK: "#ff5252" };
const TC_SIG_COLOR = { green: "#00e676", yellow: "#ffca28", orange: "#ff7043", red: "#ff5252" };

function TechnicalConfirmationCard({ tc, rsi }) {
  const [collapsed, setCollapsed] = React.useState(true);
  const [expanded,  setExpanded]  = React.useState({});
  const [infoModal, setInfoModal] = React.useState(null);

  if (!tc) return null;

  const { score, label, signals = [] } = tc;
  const tColor = TC_COLOR[label] || "#5a6a88";
  const tBg    = `${tColor}18`;

  const InfoBtn = ({ infoKey, size = 11 }) => {
    const info = TC_SIGNAL_INFO[infoKey];
    if (!info) return null;
    return (
      <button
        onClick={e => { e.stopPropagation(); setInfoModal(info); }}
        style={{ background:"none", border:"none", cursor:"pointer",
          padding:"0 3px", color:C.muted, fontSize:size,
          lineHeight:1, flexShrink:0, opacity:0.7 }}
        aria-label={`Info about ${info.title}`}>
        ⓘ
      </button>
    );
  };

  return (
    <>
      {infoModal && (
        <div onClick={() => setInfoModal(null)}
          style={{ position:"fixed", inset:0, zIndex:9999,
            background:"rgba(0,0,0,0.72)",
            display:"flex", alignItems:"center", justifyContent:"center",
            padding:"0 20px" }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background:"#1a2236", borderRadius:18,
              padding:"22px 20px 18px", maxWidth:340, width:"100%",
              border:"1px solid rgba(255,255,255,0.12)",
              boxShadow:"0 20px 60px rgba(0,0,0,0.6)" }}>
            <div style={{ fontSize:13, fontWeight:700, color:C.text, marginBottom:10 }}>
              {infoModal.title}
            </div>
            <div style={{ fontSize:12, color:"#8a9ab8", lineHeight:1.55 }}>
              {infoModal.body}
            </div>
            <button onClick={() => setInfoModal(null)}
              style={{ marginTop:18, width:"100%", padding:"10px",
                background:"rgba(255,255,255,0.07)", border:"1px solid rgba(255,255,255,0.1)",
                borderRadius:10, color:C.muted, fontSize:12,
                fontWeight:600, cursor:"pointer" }}>
              Got it
            </button>
          </div>
        </div>
      )}

      <div style={{ background:C.surface, borderRadius:14, marginBottom:12,
        border:`1px solid ${tColor}30`, overflow:"hidden" }}>

        {/* ── Header row ─────────────────────────────── */}
        <div onClick={() => setCollapsed(p => !p)}
          style={{ padding:"12px 14px 10px", display:"flex",
            alignItems:"center", gap:8, cursor:"pointer" }}>

          <div style={{ flex:1, minWidth:0 }}>
            {/* Title + RAG bubble */}
            <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:6 }}>
              <span style={{ fontSize:9, fontWeight:700, letterSpacing:"0.07em",
                color:C.muted, textTransform:"uppercase" }}>Technical Confirmation</span>
              <span style={{ fontSize:10, fontWeight:800, color:tColor,
                background:tBg, borderRadius:6, padding:"1px 7px",
                letterSpacing:"0.04em" }}>{label}</span>
              <InfoBtn infoKey="composite" size={12} />
            </div>

            {/* Score bar + RSI inline */}
            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
              <div style={{ flex:1, height:5, background:"rgba(255,255,255,0.07)",
                borderRadius:3, overflow:"hidden" }}>
                <div style={{ width:`${Math.max(2, score)}%`, height:"100%",
                  background:tColor, borderRadius:3, transition:"width 0.6s ease" }} />
              </div>
              <span style={{ fontSize:16, fontWeight:800, color:tColor,
                fontFamily:"monospace", flexShrink:0 }}>{score}</span>
              <span style={{ fontSize:9, color:C.muted, flexShrink:0 }}>/100</span>
            </div>
          </div>

          {/* RSI inline on summary — always visible, with a small muted label */}
          {rsi != null && (
            <div style={{ display:"flex", flexDirection:"column", alignItems:"center",
              gap:2, flexShrink:0 }}>
              <span style={{ fontSize:8, fontWeight:700, color:C.muted,
                letterSpacing:"0.06em", textTransform:"uppercase" }}>RSI</span>
              <RSIPill rsi={rsi} size="sm" />
            </div>
          )}

          {/* Expand/collapse arrow — matched to the metals/crypto/gap-scanner
              chevron (▲/▼ at fontSize 10) for consistency across cards. */}
          <button onClick={e => { e.stopPropagation(); setCollapsed(p => !p); }}
            style={{ background:"none", border:"none", cursor:"pointer",
              padding:"4px 6px", color:C.muted, fontSize:10, flexShrink:0 }}>
            {collapsed ? "▼" : "▲"}
          </button>
        </div>

        {/* ── Expanded content ────────────────────────── */}
        {!collapsed && (
          <>
            {/* Legend */}
            <div style={{ display:"flex", gap:4, padding:"0 14px 10px",
              overflowX:"auto" }}>
              {[["≥70","CONFIRMED","#00e676"],["45–69","MIXED","#ffca28"],["<45","WEAK","#ff5252"]]
                .map(([rng, lbl, clr]) => (
                <div key={lbl} style={{ display:"flex", alignItems:"center", gap:4,
                  background:`${clr}12`, borderRadius:6, padding:"3px 8px",
                  border: label === lbl ? `1px solid ${clr}55` : "1px solid transparent",
                  flexShrink:0 }}>
                  <div style={{ width:5, height:5, borderRadius:"50%", background:clr }} />
                  <span style={{ fontSize:8, fontWeight:700, color:clr,
                    letterSpacing:"0.04em" }}>{rng} {lbl}</span>
                </div>
              ))}
            </div>

            {/* Signal rows */}
            <div style={{ borderTop:`1px solid ${C.border}` }}>
              {signals.map((sig, i) => {
                const sc   = TC_SIG_COLOR[sig.status] || C.muted;
                const isExp = !!expanded[i];
                const pctW  = sig.max_pts > 0 ? Math.round((sig.pts / sig.max_pts) * 100) : 0;
                // Short classification from the backend detail (the phrase after the
                // em-dash, e.g. "P/E 8.6 — potentially undervalued" → "Potentially
                // undervalued") so the user sees what the value MEANS upfront without
                // opening the ⓘ. Blank (skipped) for "no data" / undashed details.
                const meaning = (() => {
                  const d = sig.detail || "";
                  const dash = d.indexOf("—");
                  if (dash < 0) return "";
                  const m = d.slice(dash + 1).split(/[;.]/)[0].trim();
                  return m ? m.charAt(0).toUpperCase() + m.slice(1) : "";
                })();
                return (
                  <div key={sig.name}
                    style={{ borderBottom: i < signals.length - 1
                      ? `1px solid ${C.border}` : "none" }}>
                    <div onClick={() => setExpanded(p => ({ ...p, [i]: !p[i] }))}
                      style={{ display:"flex", alignItems:"center", gap:7,
                        padding:"9px 14px", cursor:"pointer" }}>
                      <div style={{ width:7, height:7, borderRadius:"50%",
                        background:sc, flexShrink:0,
                        boxShadow:`0 0 5px ${sc}80` }} />
                      <span style={{ fontSize:11, color:C.text, fontWeight:500,
                        flexShrink:0, whiteSpace:"nowrap" }}>{sig.name}</span>
                      <InfoBtn infoKey={sig.name} />
                      {/* Plain-English classification (color-coded to status) */}
                      <span style={{ fontSize:10, color:sc, flex:1, minWidth:0,
                        textAlign:"right", overflow:"hidden", textOverflow:"ellipsis",
                        whiteSpace:"nowrap", opacity:0.9 }}>{meaning}</span>
                      <span style={{ fontSize:11, fontWeight:700, color:sc,
                        fontFamily:"monospace", flexShrink:0,
                        marginLeft: meaning ? 0 : "auto" }}>{sig.value}</span>
                      <div style={{ display:"flex", alignItems:"center",
                        gap:5, flexShrink:0 }}>
                        <div style={{ width:28, height:3,
                          background:"rgba(255,255,255,0.08)", borderRadius:2, overflow:"hidden" }}>
                          <div style={{ width:`${pctW}%`, height:"100%",
                            background:sc, borderRadius:2 }} />
                        </div>
                        <span style={{ fontSize:9, color:C.muted, fontFamily:"monospace" }}>
                          {sig.pts}/{sig.max_pts}
                        </span>
                      </div>
                      <span style={{ fontSize:9, color:C.muted, flexShrink:0 }}>
                        {isExp ? "▴" : "▾"}
                      </span>
                    </div>
                    {isExp && (
                      <div style={{ padding:"0 14px 10px 28px",
                        display:"flex", flexDirection:"column", gap:3 }}>
                        <div style={{ fontSize:10, color:C.muted, lineHeight:1.45 }}>
                          {sig.detail}
                        </div>
                        <div style={{ fontSize:9, color:`${C.muted}80` }}>
                          Threshold: {sig.threshold} · Weight: {sig.max_pts} pts
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Score summary footer */}
              <div style={{ padding:"12px 14px", background:`${tColor}08`,
                borderTop:`1px solid ${tColor}20` }}>
                <div style={{ display:"flex", alignItems:"center",
                  justifyContent:"space-between", marginBottom:6 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                    <span style={{ fontSize:10, color:C.muted, fontWeight:600 }}>
                      Weighted composite score
                    </span>
                    <InfoBtn infoKey="composite" />
                  </div>
                  <span style={{ fontSize:14, fontWeight:800, color:tColor,
                    fontFamily:"monospace" }}>{score}/100</span>
                </div>
                <div style={{ fontSize:10, color:`${C.muted}99`, lineHeight:1.4 }}>
                  Complements your Swing &amp; LT scores. Momentum signals weigh
                  more for swing horizons; fundamentals weigh more for LT.
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}

// --- Intraday Timing Card (v8.4.212) ------------------------------------------
// Honest framing: surfaces statistically FAVORABLE entry/exit ZONES.
// Does NOT predict the top — short-horizon price is near-random.
const IT_SIGNAL_INFO = {
  "VWAP Distance": {
    title: "VWAP Distance — Core Signal",
    body: "Volume-Weighted Average Price distance, measured in ATR units. VWAP is the average price weighted by volume throughout the session — a key institutional reference. Prices below or at VWAP represent a favorable entry zone; prices far above VWAP are extended and at higher reversal risk. Expressed in ATR units so the scale is the same across stocks.",
  },
  "Intraday RSI": {
    title: "Intraday RSI (14-period on 5-min bars)",
    body: "RSI computed on 5-minute bar closes — a faster read than the daily RSI used in Technical Confirmation. Oversold (<30) on the intraday chart often flags a mean-reversion bounce setup. Overbought (>75) warns of a short-term extension and potential trimming zone. This is a DIFFERENT calculation from the daily RSI shown in the Technical Confirmation card.",
  },
  "Day Range Position": {
    title: "Position in Day's Range",
    body: "(Price − Day Low) ÷ (Day High − Day Low). Lower third (<33%) puts price near the session's low — historically a favorable entry zone on trend days. Upper third (>67%) means you're buying near the day's high, which carries a higher fade risk. Mid-range (33–67%) is neutral — wait for a directional push before committing.",
  },
  "Relative Volume": {
    title: "Relative Volume (RVOL)",
    body: "Today's cumulative volume vs the stock's average daily volume, scaled by the fraction of the session elapsed. RVOL >1.5× confirms that today's move has real institutional participation. Very high RVOL (>5×) can signal a climax bar — high emotion, potential exhaustion. Low RVOL (<0.5×) suggests thin tape; price moves may not sustain.",
  },
  "Open Extension": {
    title: "Extension from Day Open",
    body: "Price distance from the opening print, measured in ATR units. Stocks that have already run 2+ ATR from their open are extended — chasing that gap historically underperforms. Stocks that have pulled back from the open (negative extension) often offer a cleaner risk-defined entry, especially if VWAP is nearby.",
  },
  "Fib Confluence": {
    title: "Fibonacci Confluence",
    body: "Fibonacci levels alone aren't reliable, so this only counts CONFLUENCE — when price is sitting on a Fib retracement level that ALSO lines up with VWAP and/or an auto-detected support/resistance level (within ~0.4 ATR). When several of those stack at the same price, it's a genuine support zone and scores green (favorable entry). If the Fib level is overhead resistance (a downtrend bounce), it counts against entering. When price isn't near any Fib level, this stays neutral and doesn't sway the score. See the full ladder in the chart's Fib tab.",
  },
  "Time of Day": {
    title: "Time-of-Day Context",
    body: "The first 30 minutes of US session (9:30–10:00 ET) have elevated volatility and wide spreads — experienced traders often wait for the market to 'settle in' before taking new entries. Mid-session (10:00–15:30 ET) is the normal liquidity window. The last 30 minutes (power hour, 15:30–16:00 ET) see elevated activity driven by institutional rebalancing — be mindful of market-on-close (MOC) order flow.",
  },
  "composite": {
    title: "Swing Timing Score",
    body: "A rule-based 0–100 composite across up to 6 intraday signals. HONEST FRAMING: this does NOT predict tops or bottoms — short-horizon price is near-random. It surfaces statistically FAVORABLE entry/exit ZONES: Green (GOOD ENTRY ≥65) = multiple signals align for a favorable entry; Amber (WAIT 40–64) = wait for a better setup; Red (EXTENDED <40) = price extended, consider trimming. Distinct from your Swing and LT horizon scores, which measure WHAT and which direction over days-months — this measures WHEN intraday.",
  },
};

const IT_COLOR = { "GOOD ENTRY": "#00e676", "WAIT": "#ffca28", "EXTENDED": "#ff5252" };
const IT_SIG_COLOR = { green: "#00e676", yellow: "#ffca28", orange: "#ff7043", red: "#ff5252" };

function IntradayTimingCard({ it }) {
  const [collapsed,  setCollapsed]  = React.useState(true);
  const [expanded,   setExpanded]   = React.useState({});
  const [infoModal,  setInfoModal]  = React.useState(null);

  if (!it) return null;

  const { score, label, signals = [], entryZone, exitPlan } = it;
  const tColor = IT_COLOR[label] || "#5a6a88";
  const tBg    = `${tColor}18`;

  const InfoBtn = ({ infoKey, size = 11 }) => {
    const info = IT_SIGNAL_INFO[infoKey];
    if (!info) return null;
    return (
      <button onClick={e => { e.stopPropagation(); setInfoModal(info); }}
        style={{ background:"none", border:"none", cursor:"pointer",
          padding:"0 3px", color:C.muted, fontSize:size,
          lineHeight:1, flexShrink:0, opacity:0.7 }}
        aria-label={`Info about ${info.title}`}>
        ⓘ
      </button>
    );
  };

  return (
    <>
      {infoModal && (
        <div onClick={() => setInfoModal(null)}
          style={{ position:"fixed", inset:0, zIndex:9999,
            background:"rgba(0,0,0,0.72)",
            display:"flex", alignItems:"center", justifyContent:"center",
            padding:"0 20px" }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background:"#1a2236", borderRadius:18,
              padding:"22px 20px 18px", maxWidth:340, width:"100%",
              border:"1px solid rgba(255,255,255,0.12)",
              boxShadow:"0 20px 60px rgba(0,0,0,0.6)" }}>
            <div style={{ fontSize:13, fontWeight:700, color:C.text, marginBottom:10 }}>
              {infoModal.title}
            </div>
            <div style={{ fontSize:12, color:"#8a9ab8", lineHeight:1.55 }}>
              {infoModal.body}
            </div>
            <button onClick={() => setInfoModal(null)}
              style={{ marginTop:18, width:"100%", padding:"10px",
                background:"rgba(255,255,255,0.07)", border:"1px solid rgba(255,255,255,0.1)",
                borderRadius:10, color:C.muted, fontSize:12,
                fontWeight:600, cursor:"pointer" }}>
              Got it
            </button>
          </div>
        </div>
      )}

      <div style={{ background:C.surface, borderRadius:14, marginBottom:12,
        border:`1px solid ${tColor}30`, overflow:"hidden" }}>

        {/* ── Header row ─────────────────────────────── */}
        <div onClick={() => setCollapsed(p => !p)}
          style={{ padding:"12px 14px 10px", display:"flex",
            alignItems:"center", gap:8, cursor:"pointer" }}>

          <div style={{ flex:1, minWidth:0 }}>
            {/* Title + RAG bubble */}
            <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:6 }}>
              <span style={{ fontSize:9, fontWeight:700, letterSpacing:"0.07em",
                color:C.muted, textTransform:"uppercase" }}>Swing Timing</span>
              <span style={{ fontSize:10, fontWeight:800, color:tColor,
                background:tBg, borderRadius:6, padding:"1px 7px",
                letterSpacing:"0.04em" }}>{label}</span>
              <InfoBtn infoKey="composite" size={12} />
            </div>

            {/* Score bar + one-line summary */}
            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
              <div style={{ flex:1, height:5, background:"rgba(255,255,255,0.07)",
                borderRadius:3, overflow:"hidden" }}>
                <div style={{ width:`${Math.max(2, score)}%`, height:"100%",
                  background:tColor, borderRadius:3, transition:"width 0.6s ease" }} />
              </div>
              <span style={{ fontSize:16, fontWeight:800, color:tColor,
                fontFamily:"monospace", flexShrink:0 }}>{score}</span>
              <span style={{ fontSize:9, color:C.muted, flexShrink:0 }}>/100</span>
            </div>
            {/* Inline summary — first signal detail when collapsed */}
            {collapsed && signals.length > 0 && (
              <div style={{ fontSize:10, color:C.muted, marginTop:4,
                overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                {(() => {
                  const top = signals.find(s => s.name === "VWAP Distance") || signals[0];
                  return top ? top.detail : "";
                })()}
              </div>
            )}
          </div>

          <button onClick={e => { e.stopPropagation(); setCollapsed(p => !p); }}
            style={{ background:"none", border:"none", cursor:"pointer",
              padding:"4px 6px", color:C.muted, fontSize:10, flexShrink:0 }}>
            {collapsed ? "▼" : "▲"}
          </button>
        </div>

        {/* ── Expanded content ────────────────────────── */}
        {!collapsed && (
          <>
            {/* Legend */}
            <div style={{ display:"flex", gap:4, padding:"0 14px 10px",
              overflowX:"auto" }}>
              {[["≥65","GOOD ENTRY","#00e676"],["40–64","WAIT","#ffca28"],["<40","EXTENDED","#ff5252"]]
                .map(([rng, lbl, clr]) => (
                <div key={lbl} style={{ display:"flex", alignItems:"center", gap:4,
                  background:`${clr}12`, borderRadius:6, padding:"3px 8px",
                  border: label === lbl ? `1px solid ${clr}55` : "1px solid transparent",
                  flexShrink:0 }}>
                  <div style={{ width:5, height:5, borderRadius:"50%", background:clr }} />
                  <span style={{ fontSize:8, fontWeight:700, color:clr,
                    letterSpacing:"0.04em" }}>{rng} {lbl}</span>
                </div>
              ))}
            </div>

            {/* Signal rows */}
            <div style={{ borderTop:`1px solid ${C.border}` }}>
              {signals.map((sig, i) => {
                const sc   = IT_SIG_COLOR[sig.status] || C.muted;
                const isExp = !!expanded[i];
                const pctW  = sig.max_pts > 0 ? Math.round((sig.pts / sig.max_pts) * 100) : 0;
                const meaning = (() => {
                  const d = sig.detail || "";
                  const dash = d.indexOf("—");
                  if (dash < 0) return "";
                  const m = d.slice(dash + 1).split(/[;.]/)[0].trim();
                  return m ? m.charAt(0).toUpperCase() + m.slice(1) : "";
                })();
                return (
                  <div key={sig.name}
                    style={{ borderBottom: i < signals.length - 1
                      ? `1px solid ${C.border}` : "none" }}>
                    <div onClick={() => setExpanded(p => ({ ...p, [i]: !p[i] }))}
                      style={{ display:"flex", alignItems:"center", gap:7,
                        padding:"9px 14px", cursor:"pointer" }}>
                      <div style={{ width:7, height:7, borderRadius:"50%",
                        background:sc, flexShrink:0, boxShadow:`0 0 5px ${sc}80` }} />
                      <span style={{ fontSize:11, color:C.text, fontWeight:500,
                        flexShrink:0, whiteSpace:"nowrap" }}>{sig.name}</span>
                      <InfoBtn infoKey={sig.name} />
                      <span style={{ fontSize:10, color:sc, flex:1, minWidth:0,
                        textAlign:"right", overflow:"hidden", textOverflow:"ellipsis",
                        whiteSpace:"nowrap", opacity:0.9 }}>{meaning}</span>
                      <span style={{ fontSize:11, fontWeight:700, color:sc,
                        fontFamily:"monospace", flexShrink:0,
                        marginLeft: meaning ? 0 : "auto" }}>{sig.value}</span>
                      <div style={{ display:"flex", alignItems:"center", gap:5, flexShrink:0 }}>
                        <div style={{ width:28, height:3,
                          background:"rgba(255,255,255,0.08)", borderRadius:2, overflow:"hidden" }}>
                          <div style={{ width:`${pctW}%`, height:"100%",
                            background:sc, borderRadius:2 }} />
                        </div>
                        <span style={{ fontSize:9, color:C.muted, fontFamily:"monospace" }}>
                          {sig.pts}/{sig.max_pts}
                        </span>
                      </div>
                      <span style={{ fontSize:9, color:C.muted, flexShrink:0 }}>
                        {isExp ? "▴" : "▾"}
                      </span>
                    </div>
                    {isExp && (
                      <div style={{ padding:"0 14px 10px 28px",
                        display:"flex", flexDirection:"column", gap:3 }}>
                        <div style={{ fontSize:10, color:C.muted, lineHeight:1.45 }}>
                          {sig.detail}
                        </div>
                        <div style={{ fontSize:9, color:`${C.muted}80` }}>
                          Threshold: {sig.threshold} · Weight: {sig.max_pts} pts
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Entry zone + exit plan */}
              {(entryZone?.vwap || exitPlan?.stop) && (
                <div style={{ padding:"12px 14px", background:`${tColor}08`,
                  borderTop:`1px solid ${tColor}20` }}>
                  {entryZone?.vwap && (
                    <div style={{ marginBottom: exitPlan?.stop ? 8 : 0 }}>
                      <div style={{ fontSize:9, fontWeight:700, color:C.muted,
                        letterSpacing:"0.06em", textTransform:"uppercase", marginBottom:4 }}>
                        Entry Zone
                      </div>
                      <div style={{ fontSize:11, color:C.text }}>{entryZone.note}</div>
                      <div style={{ fontSize:10, color:C.muted, marginTop:2 }}>
                        VWAP ${entryZone.vwap} · Current ${entryZone.current}
                      </div>
                    </div>
                  )}
                  {exitPlan?.stop && (
                    <div>
                      <div style={{ fontSize:9, fontWeight:700, color:C.muted,
                        letterSpacing:"0.06em", textTransform:"uppercase", marginBottom:4 }}>
                        Exit Plan (rules-based estimate)
                      </div>
                      <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                        <span style={{ fontSize:10, color:"#ff5252" }}>
                          Stop ${exitPlan.stop}
                        </span>
                        <span style={{ fontSize:10, color:"#ffca28" }}>
                          {exitPlan.trail}
                        </span>
                        <span style={{ fontSize:10, color:"#00e676" }}>
                          Target ${exitPlan.target}
                        </span>
                      </div>
                      <div style={{ fontSize:10, color:C.muted, marginTop:4 }}>
                        {exitPlan.timeStop}
                      </div>
                    </div>
                  )}
                  <div style={{ fontSize:9, color:`${C.muted}80`, marginTop:8, lineHeight:1.4 }}>
                    Complements Swing &amp; LT scores. Timing is intraday — not a prediction.
                    Adjust to your thesis and risk tolerance.
                  </div>
                </div>
              )}

              {/* Score summary footer */}
              <div style={{ padding:"10px 14px", background:`${tColor}05`,
                borderTop:`1px solid ${tColor}15` }}>
                <div style={{ display:"flex", alignItems:"center",
                  justifyContent:"space-between" }}>
                  <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                    <span style={{ fontSize:10, color:C.muted, fontWeight:600 }}>
                      Timing composite score
                    </span>
                    <InfoBtn infoKey="composite" />
                  </div>
                  <span style={{ fontSize:14, fontWeight:800, color:tColor,
                    fontFamily:"monospace" }}>{score}/100</span>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}

// --- Market Regime Card --------------------------------------------------------
const REGIME_SIGNAL_INFO = {
  "10yr Treasury": {
    title: "10-Year Treasury Yield",
    body: "The 10-year US Treasury yield is the benchmark discount rate for the economy. Higher yields raise borrowing costs and compress stock valuations by reducing the present value of future earnings. Yields above 4% start to make bonds more competitive relative to equities. Scores best when below 4%, worst above 5%.",
  },
  "30yr Treasury": {
    title: "30-Year Treasury Yield",
    body: "The 30-year yield anchors long-duration asset pricing including mortgages and real estate. Yields above 4.5% signal meaningful long-end rate pressure, historically weighing on growth stocks, REITs, and rate-sensitive sectors. Scores best below 4.5%, worst above 5.5%.",
  },
  "CPI Inflation": {
    title: "CPI Inflation (YoY)",
    body: "Year-over-year change in the US Consumer Price Index, sourced from the Federal Reserve (FRED). The Fed targets 2% inflation. When CPI runs above 3%, the Fed maintains restrictive policy — keeping rates elevated, compressing equity multiples, and increasing corporate borrowing costs. Scores best below 3%, worst above 5%.",
  },
  "Equity Risk Premium": {
    title: "Equity Risk Premium (ERP)",
    body: "The extra return demanded for owning stocks vs risk-free bonds. Calculated as the S&P 500 earnings yield (1/PE ratio) minus the 10-year Treasury yield. A negative ERP means bonds yield more than equities — historically a headwind for stock prices. A high ERP signals stocks are attractively valued vs bonds. Scores best above 2.5%, worst below 0%.",
  },
  "Yield Curve (10yr–3mo)": {
    title: "Yield Curve (10yr – 3mo)",
    body: "The spread between the 10-year and 3-month Treasury yields. When this inverts (10yr yield drops below 3mo), it has preceded every US recession since 1968 — typically with a 12–18 month lag. A deeply inverted curve (-0.5% or worse) signals the market is pricing in meaningful economic deterioration. A healthy positive slope (>1%) signals normal credit conditions and growth expectations.",
  },
  "VIX Volatility": {
    title: "VIX — The Fear Index",
    body: "The CBOE Volatility Index measures the market's expected 30-day volatility, derived from S&P 500 options pricing. VIX above 35 signals extreme panic — but historically, these spikes often mark near-term market BOTTOMS (see: March 2020, October 2022, Q4 2018). Low VIX (<18) = complacency and calm. Note: extreme fear can be a contrarian buy signal — see the Contrarian Alert section if active.",
  },
  "SPY 50/200 MA": {
    title: "SPY 50/200 Moving Average Cross",
    body: "Compares SPY's 50-day moving average vs its 200-day moving average. A 'death cross' (50d below 200d by 2%+) signals trend deterioration and has preceded extended bear markets. A 'golden cross' (50d above 200d by 3%+) confirms a healthy uptrend. The cross is a lagging indicator — by the time it fires, much of the move has already happened. Most useful as a regime confirmation signal.",
  },
  "Credit Spreads (HYG)": {
    title: "Credit Spreads — HYG Proxy",
    body: "HYG (iShares High Yield Corporate Bond ETF) 30-day performance is used as a proxy for credit spread changes. When high-yield bonds sell off, credit spreads widen — signaling financial stress, tightening lending conditions, and risk-off sentiment. Stable or rising HYG indicates healthy credit markets and risk appetite. Scores best when HYG 30d return is flat or positive, worst when down 3%+.",
  },
  "composite": {
    title: "Composite Regime Score",
    body: "A weighted 0–100 score across 8 macro signals (total max = 17 pts mapped to 100). CAUTION (45–69) is the neutral baseline — no adjustment to stock scores. CLEAR (≥70) adds a small bonus to all swing and LT scores. HIGH ALERT (20–44) and CRISIS (<20) apply graduated penalties. The score updates hourly and propagates automatically to every stock in your universe.",
  },
};

// Chart Patterns RAG card (v8.4.201) — collapsed RAG bubble + "+N more" expander
function ChartPatternsCard({ pb }) {
  const [expanded,  setExpanded]  = React.useState(false);
  const [showMore,  setShowMore]  = React.useState(false);

  if (!pb || !pb.patterns || pb.patterns.length === 0) return null;

  const DOT_COLOR = { green: "#00e676", red: "#ff5252", amber: "#ffca28" };
  const dotColor  = DOT_COLOR[pb.color] || C.muted;

  const confLabel = pb.score >= 70 ? "strong" : pb.score >= 45 ? "moderate" : "weak";
  const parts = [];
  if (pb.bullishCount > 0) parts.push(`${pb.bullishCount} bullish`);
  if (pb.bearishCount > 0) parts.push(`${pb.bearishCount} bearish`);
  if (pb.neutralCount > 0) parts.push(`${pb.neutralCount} neutral`);
  // v8.4.236: when only non-directional candlesticks are present, don't imply a bias.
  const hasDir = (pb.directionalCount ?? ((pb.bullishCount||0) + (pb.bearishCount||0))) > 0;
  const netStr = pb.net > 0 ? "net bullish" : pb.net < 0 ? "net bearish"
    : hasDir ? "neutral" : "no directional bias";
  const summaryLine = `${parts.join(" · ")} → ${netStr}` + (hasDir ? ` · ${confLabel} confidence` : "");

  const patterns = pb.patterns || [];
  const top2 = patterns.slice(0, 2);
  const rest  = patterns.slice(2);

  const PatternRow = ({ p }) => {
    const isNeutral = p.type !== "bullish" && p.type !== "bearish";
    return (
    <div style={{display:"flex",alignItems:"center",gap:12,
      background:C.surface,borderRadius:10,padding:"10px 12px",
      border:`1px solid ${C.border}`,marginBottom:8}}>
      <span style={{fontSize:18}}>{p.type==="bullish"?"🟢":p.type==="bearish"?"🔴":"⚪"}</span>
      <div style={{flex:1}}>
        {/* v8.4.236: timeframe chip (Candlestick / Multi-week) + neutral tag so a
            single-bar Inside Day reads as a different lens from multi-week patterns,
            not a contradiction with the Pattern Showcase. */}
        <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
          <span style={{fontSize:13,fontWeight:600,color:C.text}}>{p.name}</span>
          {p.timeframeLabel && (
            <span style={{fontSize:9,color:C.muted,background:"rgba(255,255,255,0.06)",
              padding:"1px 5px",borderRadius:4}}>{p.timeframeLabel}</span>
          )}
          {isNeutral && (
            <span style={{fontSize:9,color:C.muted,fontStyle:"italic"}}>neutral</span>
          )}
        </div>
        <div style={{fontSize:11,color:C.muted,marginTop:2}}>{p.description}</div>
      </div>
      <div style={{fontSize:11,color:C.muted,textAlign:"right"}}>
        <div style={{fontSize:14,fontWeight:700,fontFamily:"monospace",
          color:p.type==="bullish"?C.green:p.type==="bearish"?C.red:C.muted}}>
          {p.confidence}%
        </div>
        confidence
      </div>
    </div>
    );
  };

  return (
    <Card>
      <div onClick={()=>setExpanded(e=>!e)}
        style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer"}}>
        <span style={{width:12,height:12,borderRadius:"50%",flexShrink:0,
          background:dotColor,boxShadow:`0 0 6px ${dotColor}`}}/>
        <div style={{flex:1}}>
          <div style={{fontSize:12,fontWeight:700,color:C.text}}>Chart Patterns</div>
          {!expanded && (
            <div style={{fontSize:11,color:C.muted,marginTop:2}}>{summaryLine}</div>
          )}
        </div>
        <span style={{fontSize:11,fontWeight:700,color:dotColor,marginRight:4}}>{pb.label}</span>
        <span style={{fontSize:11,color:C.muted}}>{expanded?"▲":"▼"}</span>
      </div>

      {expanded && (
        <div style={{marginTop:12}}>
          <div style={{fontSize:11,color:C.muted,marginBottom:10}}>{summaryLine}</div>
          {top2.map((p,i)=><PatternRow key={i} p={p}/>)}
          {rest.length > 0 && (
            <>
              {showMore && rest.map((p,i)=><PatternRow key={i} p={p}/>)}
              <button onClick={e=>{e.stopPropagation();setShowMore(m=>!m)}}
                style={{width:"100%",background:"none",border:`1px solid ${C.border}`,
                  borderRadius:8,padding:"7px 0",fontSize:11,color:C.accent,
                  cursor:"pointer",marginTop:4}}>
                {showMore?"▲ less":`▼ +${rest.length} more`}
              </button>
            </>
          )}
          {/* v8.4.239: plain-English explainer so the % isn't a mystery grade. */}
          <div style={{fontSize:10,color:C.muted,marginTop:10,lineHeight:1.5,
            paddingTop:10,borderTop:`1px solid ${C.border}`}}>
            <b style={{color:C.text}}>How to read this:</b> the % is how <i>reliable</i> each
            pattern has been historically — not the odds your trade works. A 🟢 leans bullish,
            🔴 bearish, ⚪ is neutral (no clear direction). "Candlestick" = a 1-bar move;
            "Multi-week" = a larger formation. Treat them as supporting clues, not a signal on
            their own — confirm with the score and trend.
          </div>
        </div>
      )}
    </Card>
  );
}

// v8.4.282 — Fast rotation / risk-off early-warning banner + view. Reads the compact
// `rotation` summary from /market-pulse; expands (fetching /rotation-risk) into a
// component breakdown + score history. Informational: a complement to the macro
// regime, NOT a trade recommendation and NOT tied to any score. Silent when calm.
function RotationSparkline({ history, col }) {
  const pts = (history || []).slice(-40).filter(p => p && typeof p.score === "number");
  if (pts.length < 2) return null;
  const W = 280, H = 44, pad = 3;
  const xs = i => pad + (i / (pts.length - 1)) * (W - 2 * pad);
  const ys = v => H - pad - (Math.max(0, Math.min(100, v)) / 100) * (H - 2 * pad);
  const path = pts.map((p, i) => `${i ? "L" : "M"}${xs(i).toFixed(1)} ${ys(p.score).toFixed(1)}`).join(" ");
  return (
    <div style={{ marginTop: 4 }}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} preserveAspectRatio="none">
        {[45, 60, 75].map(t => (
          <line key={t} x1={0} x2={W} y1={ys(t)} y2={ys(t)}
            stroke="rgba(255,255,255,0.12)" strokeWidth="0.5" strokeDasharray="3 3" />
        ))}
        <path d={path} fill="none" stroke={col} strokeWidth="1.5" />
      </svg>
      <div style={{ fontSize: 8, color: C.muted, textAlign: "right" }}>rotation score · last {pts.length}d</div>
    </div>
  );
}

function RotationBanner({ token, rotation }) {
  const [expanded, setExpanded] = useState(false);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  if (!rotation || rotation.score == null || rotation.label === "Low") return null;

  const score = rotation.score;
  const label = rotation.label;
  const COLOR = { Watch: "#ffca28", Elevated: "#ff7043", High: "#ff5252" };
  const col = COLOR[label] || "#ffca28";
  const bg = `${col}18`;
  const DRIVER_LABEL = {
    credit: "Credit spreads", defensive_rs: "Defensive leadership",
    bonds: "Bonds vs stocks", breadth: "Breadth",
  };

  const toggle = async () => {
    const next = !expanded;
    setExpanded(next);
    if (next && !detail) {
      setLoadingDetail(true);
      try { const d = await apiFetch("/rotation-risk", token); setDetail(d); } catch (e) { /* keep banner */ }
      setLoadingDetail(false);
    }
  };

  return (
    <div style={{ marginBottom: 12, borderRadius: 12, border: `1px solid ${col}55`,
      background: bg, overflow: "hidden" }}>
      <button onClick={toggle} style={{ width: "100%", background: "none", border: "none",
        cursor: "pointer", padding: "11px 12px", display: "flex", alignItems: "center",
        gap: 10, textAlign: "left" }}>
        <span style={{ fontSize: 16, flexShrink: 0 }}>{label === "High" ? "🔴" : "⚠️"}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: C.text }}>
            Rotation risk: {label}{rotation.favoring ? ` — favoring ${rotation.favoring}` : ""}
          </div>
          <div style={{ fontSize: 10.5, color: C.muted, marginTop: 2, whiteSpace: "nowrap",
            overflow: "hidden", textOverflow: "ellipsis" }}>{rotation.reason}</div>
        </div>
        <div style={{ textAlign: "right", flexShrink: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: col, fontFamily: "monospace" }}>{Math.round(score)}</div>
          <div style={{ fontSize: 8, color: C.muted }}>/100</div>
        </div>
        <span style={{ fontSize: 10, color: C.muted, flexShrink: 0,
          transform: expanded ? "rotate(180deg)" : "none", transition: "transform 0.15s" }}>▾</span>
      </button>

      {expanded && (
        <div style={{ padding: "0 12px 12px" }}>
          {loadingDetail && <div style={{ fontSize: 10, color: C.muted, paddingBottom: 8 }}>Loading…</div>}
          {detail && detail.components && (
            <>
              <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 8 }}>
                {Object.entries(detail.components).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
                  <div key={k} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{ width: 108, fontSize: 10, color: C.muted, flexShrink: 0 }}>{DRIVER_LABEL[k] || k}</div>
                    <div style={{ flex: 1, height: 6, background: "rgba(255,255,255,0.08)",
                      borderRadius: 3, overflow: "hidden" }}>
                      <div style={{ height: "100%", width: `${Math.max(2, Math.min(100, v))}%`,
                        background: v >= 60 ? col : C.accent, borderRadius: 3 }} />
                    </div>
                    <div style={{ width: 26, fontSize: 9.5, color: C.text, textAlign: "right",
                      fontFamily: "monospace" }}>{Math.round(v)}</div>
                  </div>
                ))}
              </div>
              <RotationSparkline history={detail.history} col={col} />
              <div style={{ fontSize: 9.5, color: C.muted, marginTop: 8, lineHeight: 1.5 }}>
                Fast risk-off signal from credit spreads, defensive-vs-cyclical leadership, bonds
                and breadth. In testing it turned risk-off a median ~45 days before market
                drawdowns — a complement to the macro regime, not a trade recommendation.
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function MarketRegimeCard({ token }) {
  // v8.4.251: resilient fetch — keeps the last good regime + refreshes so a cold-cache
  // miss (e.g. right after a redeploy) self-heals instead of the card staying vanished.
  const { data: regime, loading } = useResilientFetch("/market/regime", token, {
    isEmpty: (d) => !d || d.score == null,
    refreshMs: 3 * 60 * 1000,
  });
  const [collapsed, setCollapsed] = useState(true);
  const [expanded, setExpanded]   = useState({});
  const [infoModal, setInfoModal] = useState(null); // { title, body }

  if (loading || !regime) return null;

  const { score, label, signals = [], swing_adj, lt_adj, fear_signal } = regime;

  const REGIME_COLOR = {
    CLEAR:        "#00e676",
    CAUTION:      "#ffca28",
    "HIGH ALERT": "#ff7043",
    CRISIS:       "#ff5252",
  };
  const SIGNAL_COLOR = { green: "#00e676", yellow: "#ffca28", orange: "#ff7043", red: "#ff5252" };
  const rColor = REGIME_COLOR[label] || "#5a6a88";
  const rBg    = `${rColor}18`;

  // ── Info button ──────────────────────────────────────────
  const InfoBtn = ({ infoKey, size = 11 }) => {
    const info = REGIME_SIGNAL_INFO[infoKey];
    if (!info) return null;
    return (
      <button
        onClick={e => { e.stopPropagation(); setInfoModal(info); }}
        style={{ background: "none", border: "none", cursor: "pointer",
          padding: "0 3px", color: C.muted, fontSize: size,
          lineHeight: 1, flexShrink: 0, opacity: 0.7 }}
        aria-label={`Info about ${info.title}`}>
        ⓘ
      </button>
    );
  };

  return (
    <>
      {/* ── Centered info modal ────────────────────────────────── */}
      {infoModal && (
        <div
          onClick={() => setInfoModal(null)}
          style={{ position: "fixed", inset: 0, zIndex: 9999,
            background: "rgba(0,0,0,0.72)",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: "0 20px" }}>
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: "#1a2236", borderRadius: 18,
              padding: "22px 20px 18px", maxWidth: 340, width: "100%",
              border: "1px solid rgba(255,255,255,0.12)",
              boxShadow: "0 20px 60px rgba(0,0,0,0.6)" }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.text,
              marginBottom: 10 }}>{infoModal.title}</div>
            <div style={{ fontSize: 12, color: "#8a9ab8", lineHeight: 1.55 }}>
              {infoModal.body}
            </div>
            <button
              onClick={() => setInfoModal(null)}
              style={{ marginTop: 18, width: "100%", padding: "10px",
                background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: 10, color: C.muted, fontSize: 12,
                fontWeight: 600, cursor: "pointer" }}>
              Got it
            </button>
          </div>
        </div>
      )}

      {/* ── Card ────────────────────────────────────────────────── */}
      <div style={{ background: C.surface, borderRadius: 14, marginBottom: 12,
        border: `1px solid ${rColor}30`, overflow: "hidden" }}>

        {/* ── Contrarian fear callout — shown even when collapsed ── */}
        {fear_signal && (
          <div style={{
            padding: "10px 14px",
            background: fear_signal.tier === "extreme"
              ? "rgba(255,193,7,0.12)"
              : "rgba(255,152,0,0.10)",
            borderBottom: `1px solid ${fear_signal.tier === "extreme" ? "#ffc107" : "#ff9800"}30`,
            display: "flex", alignItems: "flex-start", gap: 10 }}>
            <span style={{ fontSize: 18, flexShrink: 0, lineHeight: 1 }}>
              {fear_signal.tier === "extreme" ? "⚡" : "📡"}
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 11, fontWeight: 800,
                color: fear_signal.tier === "extreme" ? "#ffc107" : "#ff9800",
                marginBottom: 4 }}>{fear_signal.label}</div>
              <div style={{ fontSize: 10, color: "#8a9ab8", lineHeight: 1.45 }}>
                {fear_signal.detail}
              </div>
              {fear_signal.vix && (
                <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
                  <span style={{ fontSize: 9, fontWeight: 700,
                    background: "rgba(255,82,82,0.15)",
                    color: "#ff5252", borderRadius: 5, padding: "2px 6px" }}>
                    VIX {fear_signal.vix}
                  </span>
                  {fear_signal.spy_rsi && (
                    <span style={{ fontSize: 9, fontWeight: 700,
                      background: "rgba(255,152,0,0.15)",
                      color: "#ff9800", borderRadius: 5, padding: "2px 6px" }}>
                      SPY RSI {fear_signal.spy_rsi}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Header row ──────────────────────────────────────────── */}
        <div style={{ padding: "12px 14px 10px", display: "flex",
          alignItems: "center", gap: 8 }}>

          <div style={{ flex: 1, minWidth: 0 }}>
            {/* Title row */}
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
              <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.07em",
                color: C.muted, textTransform: "uppercase" }}>Market Regime</span>
              <span style={{ fontSize: 10, fontWeight: 800, color: rColor,
                background: rBg, borderRadius: 6, padding: "1px 7px",
                letterSpacing: "0.04em" }}>{label}</span>
              <InfoBtn infoKey="composite" size={12} />
            </div>

            {/* Score bar */}
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ flex: 1, height: 5, background: "rgba(255,255,255,0.07)",
                borderRadius: 3, overflow: "hidden" }}>
                <div style={{ width: `${Math.max(2, score)}%`, height: "100%",
                  background: rColor, borderRadius: 3, transition: "width 0.6s ease" }} />
              </div>
              <span style={{ fontSize: 16, fontWeight: 800, color: rColor,
                fontFamily: "monospace", flexShrink: 0 }}>{score}</span>
              <span style={{ fontSize: 9, color: C.muted, flexShrink: 0 }}>/100</span>
            </div>
          </div>

          {/* Impact pills — only show non-zero */}
          <div style={{ display: "flex", flexDirection: "column", gap: 4,
            alignItems: "flex-end", flexShrink: 0 }}>
            {swing_adj !== 0 ? (
              <div style={{ fontSize: 9, fontWeight: 700, borderRadius: 6, padding: "2px 7px",
                color: swing_adj > 0 ? "#00e676" : "#ff5252",
                background: swing_adj > 0 ? "rgba(0,230,118,0.12)" : "rgba(255,82,82,0.12)" }}>
                Swing {swing_adj > 0 ? `+${swing_adj}` : swing_adj}
              </div>
            ) : (
              <div style={{ fontSize: 9, fontWeight: 600, borderRadius: 6, padding: "2px 7px",
                color: C.muted, background: "rgba(255,255,255,0.05)" }}>
                Neutral
              </div>
            )}
            {lt_adj !== 0 && (
              <div style={{ fontSize: 9, fontWeight: 700, borderRadius: 6, padding: "2px 7px",
                color: lt_adj > 0 ? "#00e676" : "#ff5252",
                background: lt_adj > 0 ? "rgba(0,230,118,0.12)" : "rgba(255,82,82,0.12)" }}>
                LT {lt_adj > 0 ? `+${lt_adj}` : lt_adj}
              </div>
            )}
          </div>

          {/* Expand/collapse */}
          <button onClick={() => setCollapsed(p => !p)}
            style={{ background: "none", border: "none", cursor: "pointer",
              padding: "4px 6px", color: C.muted, fontSize: 14, flexShrink: 0 }}>
            {collapsed ? "▾" : "▴"}
          </button>
        </div>

        {/* ── Expanded content ──────────────────────────────────── */}
        {!collapsed && (
          <>
            {/* Regime legend */}
            <div style={{ display: "flex", gap: 4, padding: "0 14px 10px",
              overflowX: "auto" }}>
              {[["≥70","CLEAR","#00e676"],["45–69","CAUTION","#ffca28"],
                ["20–44","HIGH ALERT","#ff7043"],["<20","CRISIS","#ff5252"]]
                .map(([rng, lbl, clr]) => (
                <div key={lbl} style={{ display: "flex", alignItems: "center", gap: 4,
                  background: `${clr}12`, borderRadius: 6, padding: "3px 8px",
                  border: label === lbl ? `1px solid ${clr}55` : "1px solid transparent",
                  flexShrink: 0 }}>
                  <div style={{ width: 5, height: 5, borderRadius: "50%", background: clr }} />
                  <span style={{ fontSize: 8, fontWeight: 700, color: clr,
                    letterSpacing: "0.04em" }}>{rng} {lbl}</span>
                </div>
              ))}
            </div>

            {/* Signal rows */}
            <div style={{ borderTop: `1px solid ${C.border}` }}>
              {signals.map((sig, i) => {
                const sc    = SIGNAL_COLOR[sig.status] || C.muted;
                const isExp = !!expanded[i];
                const pctW  = sig.max_pts > 0 ? Math.round((sig.pts / sig.max_pts) * 100) : 0;
                return (
                  <div key={sig.name}
                    style={{ borderBottom: i < signals.length - 1
                      ? `1px solid ${C.border}` : "none" }}>

                    {/* Row — click anywhere to expand, ⓘ opens info modal */}
                    <div
                      onClick={() => setExpanded(p => ({ ...p, [i]: !p[i] }))}
                      style={{ display: "flex", alignItems: "center", gap: 7,
                        padding: "9px 14px", cursor: "pointer" }}>

                      {/* Status dot */}
                      <div style={{ width: 7, height: 7, borderRadius: "50%",
                        background: sc, flexShrink: 0,
                        boxShadow: `0 0 5px ${sc}80` }} />

                      {/* Name + info btn */}
                      <span style={{ fontSize: 11, color: C.text, fontWeight: 500,
                        flex: 1, minWidth: 0 }}>{sig.name}</span>
                      <InfoBtn infoKey={sig.name} />

                      {/* Value */}
                      <span style={{ fontSize: 11, fontWeight: 700, color: sc,
                        fontFamily: "monospace", flexShrink: 0 }}>{sig.value}</span>

                      {/* Mini pts bar */}
                      <div style={{ display: "flex", alignItems: "center",
                        gap: 5, flexShrink: 0 }}>
                        <div style={{ width: 28, height: 3,
                          background: "rgba(255,255,255,0.08)", borderRadius: 2, overflow: "hidden" }}>
                          <div style={{ width: `${pctW}%`, height: "100%",
                            background: sc, borderRadius: 2 }} />
                        </div>
                        <span style={{ fontSize: 9, color: C.muted, fontFamily: "monospace" }}>
                          {sig.pts}/{sig.max_pts}
                        </span>
                      </div>

                      <span style={{ fontSize: 9, color: C.muted, flexShrink: 0 }}>
                        {isExp ? "▴" : "▾"}
                      </span>
                    </div>

                    {/* Expanded detail */}
                    {isExp && (
                      <div style={{ padding: "0 14px 10px 28px",
                        display: "flex", flexDirection: "column", gap: 3 }}>
                        <div style={{ fontSize: 10, color: C.muted, lineHeight: 1.45 }}>
                          {sig.detail}
                        </div>
                        <div style={{ fontSize: 9, color: `${C.muted}80` }}>
                          Threshold: {sig.threshold} · Weight: {sig.max_pts} pts
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}

              {/* ── Score impact summary ─────────────────────────── */}
              <div style={{ padding: "12px 14px", background: `${rColor}08`,
                borderTop: `1px solid ${rColor}20` }}>

                {/* Row: label + score + info btn */}
                <div style={{ display: "flex", alignItems: "center",
                  justifyContent: "space-between", marginBottom: 10 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontSize: 10, color: C.muted, fontWeight: 600 }}>
                      Weighted score &amp; impact
                    </span>
                    <InfoBtn infoKey="composite" />
                  </div>
                  <span style={{ fontSize: 14, fontWeight: 800, color: rColor,
                    fontFamily: "monospace" }}>{score}/100</span>
                </div>

                {/* Impact tiles */}
                <div style={{ display: "flex", gap: 10 }}>
                  <div style={{ flex: 1, background: C.surface, borderRadius: 8,
                    padding: "8px 10px", border: `1px solid ${C.border}` }}>
                    <div style={{ fontSize: 8, color: C.muted, marginBottom: 3,
                      fontWeight: 700, textTransform: "uppercase",
                      letterSpacing: "0.05em" }}>Swing score impact</div>
                    <div style={{ fontSize: 15, fontWeight: 800, fontFamily: "monospace",
                      color: swing_adj > 0 ? "#00e676" : swing_adj < 0 ? "#ff5252" : C.muted }}>
                      {swing_adj > 0 ? `+${swing_adj}` : swing_adj === 0 ? "0 (neutral)" : swing_adj} pts
                    </div>
                  </div>
                  <div style={{ flex: 1, background: C.surface, borderRadius: 8,
                    padding: "8px 10px", border: `1px solid ${C.border}` }}>
                    <div style={{ fontSize: 8, color: C.muted, marginBottom: 3,
                      fontWeight: 700, textTransform: "uppercase",
                      letterSpacing: "0.05em" }}>LT score impact</div>
                    <div style={{ fontSize: 15, fontWeight: 800, fontFamily: "monospace",
                      color: lt_adj > 0 ? "#00e676" : lt_adj < 0 ? "#ff5252" : C.muted }}>
                      {lt_adj > 0 ? `+${lt_adj}` : lt_adj === 0 ? "0 (neutral)" : lt_adj} pts
                    </div>
                  </div>
                </div>
                <div style={{ fontSize: 9, color: C.muted, marginTop: 8, lineHeight: 1.4 }}>
                  Applied to every stock in your universe automatically.
                  CAUTION = neutral baseline (no adj). CLEAR adds a small bonus.
                  HIGH ALERT / CRISIS apply graduated penalties.
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}

// --- Earnings This Week card (v8.4.148) -----------------------------------------
// Cut-down list: holdings + watchlist always shown, filled out with top-score
// universe names, capped at 12. Backed by /calendar/earnings-week (Finnhub
// bulk earnings calendar intersected with our universe, server-side ranked).
function EarningsThisWeekCard({ token, horizon, onSelectTicker }) {
  // v8.4.299: stale-while-revalidate. Seed from a same-day localStorage cache so the card
  // populates INSTANTLY on open (was blank until the network fetch returned), then refresh
  // in the background. Cache is date-keyed so it auto-expires daily as the window shifts.
  const CACHE_KEY = "gempulse_earnings_week";
  const _today = () => new Date().toISOString().slice(0, 10);
  const [data, setData] = React.useState(() => {
    try {
      const c = JSON.parse(localStorage.getItem(CACHE_KEY) || "null");
      // v8.4.301: seed from ANY recent cache (even yesterday's) so the card never
      // blanks on the first open of a new day — the effect revalidates immediately
      // after. Earnings-this-week barely changes day-to-day, so a ~1s stale view
      // beats a blank. Cap at 4 days so a long absence doesn't show an old window.
      if (c && c.data && c.date) {
        const ageDays = (new Date(_today()) - new Date(c.date)) / 86400000;
        if (ageDays <= 4) return c.data;
      }
    } catch (_) {}
    return null;
  });
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (!data) setLoading(true);
    apiFetch("/calendar/earnings-week", token)
      .then(r => {
        setData(r); setLoading(false);
        try { localStorage.setItem(CACHE_KEY, JSON.stringify({ date: _today(), data: r })); } catch (_) {}
      })
      .catch(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (loading && !data) return null;   // only blank on the very first ever load (no cache yet)

  const HOUR_LABEL = { bmo: "Before Open", amc: "After Close", dmh: "During Hours" };

  if (!data?.earnings?.length) {
    return (
      <div style={{background:C.surface,borderRadius:14,padding:"12px 14px",
        border:`1px solid ${C.border}`,marginBottom:14}}>
        <div style={{fontSize:10,fontWeight:700,color:C.muted,letterSpacing:"0.1em",
          textTransform:"uppercase",marginBottom:6}}>
          <span style={{display:"inline-flex",alignItems:"center",gap:6}}><EarningsGlyph size={12} color={C.muted}/> Earnings This Week</span>
        </div>
        <div style={{fontSize:12,color:C.muted,opacity:0.7,padding:"4px 0"}}>
          No tracked earnings announced this week.
        </div>
      </div>
    );
  }
  const dayLabel = (dateStr, daysUntil) => {
    // v8.4.346 — "Tomorrow" only for a clearly-upcoming next-day report; for today or past
    // (daysUntil <= 0) show the DATE, not "Today" — an after-close print is read as
    // daysUntil 0 the next day and "Today" then misleads (the actual date is unambiguous).
    if (daysUntil === 1) return "Tomorrow";
    try {
      return new Date(dateStr + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
    } catch { return dateStr; }
  };

  return (
    <div style={{background:C.surface,borderRadius:14,padding:"12px 14px",
      border:`1px solid ${C.border}`,marginBottom:14}}>
      <div style={{fontSize:10,fontWeight:700,color:C.muted,letterSpacing:"0.1em",
        textTransform:"uppercase",marginBottom:10}}>
        <span style={{display:"inline-flex",alignItems:"center",gap:6}}><EarningsGlyph size={12} color={C.muted}/> Earnings This Week</span>
      </div>
      {data.earnings.map((e,i) => (
        <button key={e.ticker} onClick={()=>onSelectTicker && onSelectTicker(e.ticker)}
          style={{width:"100%",display:"flex",alignItems:"center",gap:8,
            padding:"8px 0",background:"none",border:"none",cursor:"pointer",
            fontFamily:"inherit",textAlign:"left",
            borderBottom:i<data.earnings.length-1?`1px solid ${C.border}`:"none"}}>
          <TickerLogo ticker={e.ticker} size={22}/>
          <div style={{flex:1,minWidth:0}}>
            <div style={{display:"flex",alignItems:"center",gap:6}}>
              <span style={{fontSize:13,fontWeight:800,fontFamily:"monospace",color:C.text}}>{e.ticker}</span>
              {e.isHolding && (
                <span style={{fontSize:9,color:C.accent,fontWeight:700,
                  background:"rgba(0,212,255,0.12)",borderRadius:10,padding:"1px 6px"}}>HOLDING</span>
              )}
              {!e.isHolding && e.isWatchlist && (
                <span style={{fontSize:9,color:"#ffca28",fontWeight:700,
                  background:"rgba(255,202,40,0.12)",borderRadius:10,padding:"1px 6px"}}>WATCHLIST</span>
              )}
            </div>
            <div style={{fontSize:10,color:C.muted,marginTop:2}}>
              {(e.shortName||"").slice(0,28)}{e.sector ? ` · ${e.sector}` : ""}
            </div>
          </div>
          <div style={{textAlign:"right",flexShrink:0}}>
            <div style={{fontSize:12,fontWeight:700,color:C.text}}>{dayLabel(e.date, e.daysUntil)}</div>
            {e.hour && (
              <div style={{fontSize:9,color:C.muted,marginTop:2}}>{HOUR_LABEL[e.hour] || e.hour}</div>
            )}
          </div>
        </button>
      ))}
    </div>
  );
}

// --- Upcoming IPOs card (v8.4.148) ------------------------------------------------
// "confirmed" — real, filed IPOs from Finnhub's /calendar/ipo (date, expected
// price range, share count). "anticipated" — small manually-curated list of
// rumored mega-IPOs (SpaceX, Anthropic...) with no filing, shown muted/TBD.
function UpcomingIPOsCard({ token }) {
  const [data,    setData]    = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    setLoading(true);
    apiFetch("/calendar/ipo", token)
      .then(r => { setData(r); setLoading(false); })
      .catch(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (loading) return null;
  const confirmed   = data?.confirmed   || [];
  const anticipated = data?.anticipated || [];
  if (!confirmed.length && !anticipated.length) return null;

  const fmtDate = (dateStr) => {
    try { return new Date(dateStr + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" }); }
    catch { return dateStr; }
  };

  return (
    <div style={{background:C.surface,borderRadius:14,padding:"12px 14px",
      border:`1px solid ${C.border}`,marginBottom:14}}>
      <div style={{fontSize:10,fontWeight:700,color:C.muted,letterSpacing:"0.1em",
        textTransform:"uppercase",marginBottom:10}}>
        🆕 Upcoming IPOs
      </div>
      {confirmed.map((ipo,i) => (
        <div key={ipo.ticker || i} style={{display:"flex",alignItems:"center",gap:8,
          padding:"8px 0",
          borderBottom: (i<confirmed.length-1 || anticipated.length>0) ? `1px solid ${C.border}` : "none"}}>
          <div style={{flex:1,minWidth:0}}>
            <div style={{display:"flex",alignItems:"center",gap:6}}>
              {ipo.ticker && (
                <span style={{fontSize:13,fontWeight:800,fontFamily:"monospace",color:C.text}}>${ipo.ticker}</span>
              )}
              <span style={{fontSize:12,color:C.text,fontWeight:ipo.ticker?400:700}}>
                {(ipo.name||"").slice(0,32)}
              </span>
            </div>
            <div style={{fontSize:10,color:C.muted,marginTop:2}}>
              {ipo.exchange ? `${ipo.exchange} · ` : ""}
              {ipo.priceFrom ? `Est. $${ipo.priceFrom}${ipo.priceTo && ipo.priceTo!==ipo.priceFrom ? `–$${ipo.priceTo}` : ""}` : ""}
              {ipo.numberOfShares ? ` · ${Number(ipo.numberOfShares).toLocaleString()} sh` : ""}
              {ipo.totalSharesValue ? ` · ~$${(Number(ipo.totalSharesValue)/1e6).toFixed(0)}M deal` : ""}
            </div>
          </div>
          <div style={{textAlign:"right",flexShrink:0}}>
            <div style={{fontSize:12,fontWeight:700,color:C.text}}>{fmtDate(ipo.date)}</div>
            {ipo.status && (
              <div style={{fontSize:9,color:C.muted,marginTop:2,textTransform:"capitalize"}}>{ipo.status}</div>
            )}
          </div>
        </div>
      ))}
      {anticipated.map((ipo,i) => {
        const isToday = ipo.status === "RUMORED_TODAY";
        const STATUS_LABEL = { RUMORED_TODAY: "Reported Today", TBD: "TBD" };
        return (
          <div key={`ant_${i}`} style={{display:"flex",alignItems:"center",gap:8,
            padding:"8px 0",opacity:isToday?0.85:0.55,
            borderBottom: i<anticipated.length-1 ? `1px solid ${C.border}` : "none"}}>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:12,color:C.text,fontWeight:700}}>{ipo.name}</div>
              <div style={{fontSize:10,color:C.muted,marginTop:2}}>{ipo.note}</div>
            </div>
            <div style={{fontSize:9,fontWeight:700,flexShrink:0,
              color: isToday ? C.accent : C.muted,
              border:`1px solid ${isToday ? C.accent : C.border}`,borderRadius:8,padding:"2px 8px"}}>
              {STATUS_LABEL[ipo.status] || ipo.status || "TBD"}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// --- Landing / Dashboard Screen -----------------------------------------------
const PULL_THRESH    = 180;   // px drag needed to trigger refresh — intentional large gesture
const PULL_COOLDOWN  = 60 * 1000; // 1 min between refreshes

function LandingScreen({ user, token, horizon, setHorizon, onProfileChange, onNavigate,
  marketData, setMarketData, onRefreshWatchlist, onCancelScan, watchlistSize,
  universePhase, universeScoredCount, universeData, lastScanTime, onSelectTicker,
  currency, fxRates, onAlertsChanged }) {
  // Use root-level market state (fed by scanner after watchlist scan)
  // Only self-fetch if root state is still empty (empty watchlist / first ever load)
  const market  = marketData;
  const setMarket = setMarketData;
  const [fgOpen, setFgOpen] = React.useState(false);     // v8.4.439 — Fear & Greed component breakdown
  const [fgLive, setFgLive] = React.useState(null);        // composite from /market-pulse (kept apart: other code paths overwrite marketData)
  const [loading,   setLoading]   = React.useState(!marketData);
  // marketReady: true only once we have a valid VIX reading this session
  const [marketReady, setMarketReady] = React.useState(
    !!(marketData?.regime?.vix > 0)
  );
  // Market session for Gap Scanner ("pre" | "open" | "post" | "closed")
  const [marketSession, setMarketSession] = React.useState("closed");

  // Compute session client-side (ET timezone)
  React.useEffect(() => {
    const computeSession = () => {
      try {
        const now = new Date();
        // Convert to ET
        const et = new Date(now.toLocaleString("en-US", {timeZone:"America/New_York"}));
        const day = et.getDay(); // 0=Sun, 6=Sat
        if (day === 0 || day === 6) { setMarketSession("closed"); return; }
        const h = et.getHours(), m = et.getMinutes();
        const mins = h * 60 + m;
        if      (mins >= 4*60  && mins < 9*60+30)  setMarketSession("pre");
        else if (mins >= 9*60+30 && mins < 16*60)  setMarketSession("open");
        else if (mins >= 16*60 && mins < 20*60)    setMarketSession("post");
        else                                        setMarketSession("closed");
      } catch(e) { setMarketSession("closed"); }
    };
    computeSession();
    const iv = setInterval(computeSession, 60 * 1000); // check every minute
    return () => clearInterval(iv);
  }, []);
  const [pullDelta, setPullDelta] = React.useState(0);
  const [lastRefreshedAt, setLastRefreshedAt] = React.useState(null);
  const [polyData, setPolyData] = React.useState(null);
  const pullRef    = React.useRef(null);
  const pullStartY = React.useRef(null);

  // Fetch Polymarket signals (2-hr backend cache). v8.4.243: the endpoint can return an
  // EMPTY object (Polymarket API down, or the 2-hr cache empty right after a rebuild), and
  // the old code did setPolyData(d) unconditionally — overwriting good data with {} so the
  // whole Prediction Markets section vanished (and it fetched only once on mount, so it
  // never recovered). Now: only replace polyData when the response actually has
  // probabilities (keep the last good render otherwise), and refresh periodically so a
  // transient-empty first load self-heals.
  React.useEffect(() => {
    if (!token) return;
    let alive = true;
    const _hasData = d => d && (d.cut_prob != null || d.recession_prob != null || d.bull_prob != null);
    const load = () => apiFetch("/polymarket", token)
      .then(d => { if (alive && _hasData(d)) setPolyData(d); })
      .catch(() => {}); // non-critical — keep prior data
    load();
    const iv = setInterval(load, 10 * 60 * 1000);   // 10 min; backend caches 2h
    return () => { alive = false; clearInterval(iv); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchMarket = React.useCallback(async () => {
    setLoading(true);
    try {
      // /market-pulse now returns regime + spy/qqq/dia/tsx all in one call,
      // supplemented from the universe cache on the backend side.
      // Fall back to individual /analyze calls for any missing ETF cards.
      const pulse = await apiFetch("/market-pulse", token);
      if (pulse && pulse.fearGreed) setFgLive(pulse.fearGreed);   // v8.4.439
      const md = {
        spy:    pulse.spy    || null,
        qqq:    pulse.qqq    || null,
        dia:    pulse.dia    || null,
        tsx:    pulse.tsx    || null,
        regime: pulse.regime || null,
        rotation: pulse.rotation || null,   // v8.4.282 fast risk-off / rotation signal
      };
      // If the pulse didn't include individual ETF data, fetch those independently
      const missing = [];
      if (!md.spy) missing.push(apiFetch("/analyze/SPY?horizon=6m", token).then(v => { md.spy = v; md.regime = md.regime || v.regime; }).catch(()=>{}));
      if (!md.qqq) missing.push(apiFetch("/analyze/QQQ?horizon=6m", token).then(v => { md.qqq = v; }).catch(()=>{}));
      if (!md.dia) missing.push(apiFetch("/analyze/DIA?horizon=6m", token).then(v => { md.dia = v; }).catch(()=>{}));
      if (!md.tsx) missing.push(apiFetch("/analyze/XIU.TO?horizon=6m", token).then(v => { md.tsx = v; }).catch(()=>{}));
      if (missing.length) await Promise.allSettled(missing);
      // v8.4.251: keep the last good market data — don't overwrite the index tiles (SPY/
      // QQQ/DIA/TSX) with an all-null response. A cold cache (post-redeploy) or a
      // closed-market miss returns nothing, and blindly setting it made the indexes
      // vanish then repopulate on the next poll. Only replace when we actually got data.
      if (md.spy || md.qqq || md.dia || md.tsx || (md.regime && md.regime.vix > 0)) {
        setMarket(md);
      }
      if (md?.regime?.vix > 0) setMarketReady(true);
      setLoading(false);
    } catch(e) {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Effect: respond to marketData changes (scanner feeds root state after scan)
  // Use ref to avoid running on every render
  const prevMarketRef = React.useRef(null);
  React.useEffect(() => {
    if (marketData && marketData !== prevMarketRef.current) {
      prevMarketRef.current = marketData;
      setLoading(false); // scanner provided fresh data — stop any spinner
      // Activate market indicators whenever VIX is valid — catches the case where
      // scoreUniverseBackground sets marketData before fetchMarket() completes.
      if (marketData?.regime?.vix > 0) setMarketReady(true);
    } else if (!marketData && !prevMarketRef.current) {
      fetchMarket(); // first load, no data yet — self-fetch
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marketData]);

  // v8.4.30: stale-while-revalidate the localStorage cache.
  // The root marketData state is hydrated from localStorage on app boot (30-min
  // TTL). If that cached blob was written by an older code path that didn't
  // include dxy / tenYrYield in regime, the homepage tiles would render "—"
  // for those fields forever — fetchMarket only runs when marketData is null,
  // so the cached partial blob blocks a refresh. Always fire one /market-pulse
  // call on mount so the UI catches up to the backend's current regime.
  React.useEffect(() => {
    if (!token) return;
    fetchMarket();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Top-scores fallback — read from score_history when universe cache is sparse
  const [topScoresHistory, setTopScoresHistory] = React.useState([]);
  React.useEffect(() => {
    if (!token) return;
    const universeCount = Object.keys(universeData || {}).filter(
      k => (universeData[k]?.swingScore || 0) > 0
    ).length;
    if (universeCount >= 5) return; // Redis cache healthy — no fallback needed
    fetch(`${API}/scores/top?limit=10`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.results?.length) setTopScoresHistory(d.results); })
      .catch(() => {});
  // re-run if universeData population changes materially
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, Object.keys(universeData || {}).length]);

  const lastRefresh   = React.useRef(0);
  const pullHoldTimer = React.useRef(null);
  const pullActive    = React.useRef(false);

  // Pull to refresh — requires deliberate sustained pull (not just scroll to top)
  React.useEffect(() => {
    const el = pullRef.current;
    if (!el) return;
    const onStart = e => {
      if (el.scrollTop > 2) return; // must be at the absolute top
      pullStartY.current = e.touches?.[0]?.clientY ?? null;
      pullActive.current = false;
    };
    const onMove = e => {
      if (pullStartY.current === null) return;
      const dy = (e.touches?.[0]?.clientY ?? 0) - pullStartY.current;
      if (dy > 16) {
        setPullDelta(Math.min(dy, PULL_THRESH + 50));
        if (!pullActive.current && dy > 120) {
          pullActive.current = true;
        }
      }
    };
    const onEnd = () => {
      clearTimeout(pullHoldTimer.current);
      const now = Date.now();
      const cooldownOk = now - lastRefresh.current >= PULL_COOLDOWN;
      if (pullActive.current && pullDelta >= PULL_THRESH && !loading && cooldownOk) {
        lastRefresh.current = now;
        // Pull-to-refresh only refreshes market pulse data (lightweight).
        // Full universe re-score is available via the ↻ button in the scan panel.
        fetchMarket();
        const t = new Date();
        setLastRefreshedAt(`${String(t.getHours()).padStart(2,"0")}:${String(t.getMinutes()).padStart(2,"0")}`);
      }
      pullStartY.current = null;
      pullActive.current = false;
      setPullDelta(0);
    };
    el.addEventListener("touchstart", onStart, {passive:true});
    el.addEventListener("touchmove",  onMove,  {passive:true});
    el.addEventListener("touchend",   onEnd);
    return () => {
      el.removeEventListener("touchstart", onStart);
      el.removeEventListener("touchmove",  onMove);
      el.removeEventListener("touchend",   onEnd);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pullDelta, loading, fetchMarket]);

  const profile = PROFILES.find(p => p.key === user.risk_profile) || PROFILES[1];

  // Fear & Greed from VIX — only compute when market data is loaded
  // Use marketReady (not just !loading) to prevent F&G showing Extreme Greed
  // while VIX is still 0 (default before regime data arrives)
  // v8.4.442: a real VIX is never below ~8.6 — ignore an implausible value (a stale/bogus cached 5.1 showed on the pill once)
  const vix    = (marketReady && market?.regime?.vix >= 8)
                 ? market.regime.vix : null;
  // VIX → Fear & Greed (recalibrated):
  //   40+  = Extreme Fear   (5)    30-40 = Fear        (20)
  //   25-30= Fear           (32)   20-25 = Mild Fear   (45)
  //   17-20= Neutral        (55) ← historical avg ~19  13-17= Greed      (68)
  //   10-13= High Greed     (82)   <10  = Extreme Greed(92)
  // v8.4.439 — GemPulse's own six-input composite (stored daily by the backend) replaces the VIX-only step function whenever it
  // exists; the old VIX mapping stays as the fallback so the gauge never goes blank.
  const _fgSrc = fgLive || market?.fearGreed || null;
  const fgd    = _fgSrc && _fgSrc.score != null ? _fgSrc : null;
  const fg     = fgd ? Math.round(fgd.score)
               : vix === null ? null
               : vix >= 40 ? 5  : vix >= 30 ? 20 : vix >= 25 ? 32
               : vix >= 20 ? 45 : vix >= 17 ? 55 : vix >= 13 ? 68
               : vix >= 10 ? 82 : 92;
  const fgLabel = fgd ? fgd.label
                : fg === null ? "Loading…"
                : fg >= 75 ? "Extreme Greed" : fg >= 60 ? "Greed"
                : fg >= 45 ? "Neutral" : fg >= 30 ? "Fear" : "Extreme Fear";
  // composite bands (backend): <25 Extreme Fear · <45 Fear · <55 Neutral · <75 Greed · 75+ Extreme Greed
  const _fgBandClr = (v) => v == null ? "rgba(255,255,255,0.3)"
                : v >= 75 ? "#00e676" : v >= 55 ? "#69f0ae" : v >= 45 ? "#ffca28" : v >= 25 ? "#ff7043" : "#ff5252";
  const fgColor = fg === null ? "rgba(255,255,255,0.3)"
                : fgd ? _fgBandClr(fg)
                : fg >= 75 ? "#00e676" : fg >= 60 ? "#69f0ae"
                : fg >= 45 ? "#ffca28" : fg >= 30 ? "#ff7043" : "#ff5252";
  const spyResult  = market?.spy;
  const qqResult   = market?.qqq;
  const diaResult  = market?.dia;
  const tsxResult  = market?.tsx;
  const trend      = market?.regime?.spyTrend || "unknown";
  const dxy        = market?.regime?.dxy ?? null;
  const tenYrYield = market?.regime?.tenYrYield ?? null;

  // DXY color: strong dollar (>104) hurts commodities/EM — red; weak (<98) — green
  const dxyColor = dxy === null ? C.muted
                 : dxy > 106 ? "#ff5252" : dxy > 102 ? "#ff7043"
                 : dxy > 98  ? "#ffca28" : "#00e676";
  const dxyLabel = dxy === null ? "" : dxy > 106 ? "Strong $" : dxy > 102 ? "Elevated"
                 : dxy > 98 ? "Neutral" : "Weak $";

  // 10yr color: high yield (>4.5%) = pressure on growth stocks — red; low = green
  const tnxColor = tenYrYield === null ? C.muted
                 : tenYrYield > 4.8 ? "#ff5252" : tenYrYield > 4.2 ? "#ff7043"
                 : tenYrYield > 3.5 ? "#ffca28" : "#00e676";
  const tnxLabel = tenYrYield === null ? ""
                 : tenYrYield > 4.8 ? "High pressure" : tenYrYield > 4.2 ? "Elevated"
                 : tenYrYield > 3.5 ? "Moderate" : "Low / supportive";

  return (
    <div ref={pullRef} style={{padding:"0 16px 100px",overflowY:"auto"}}>
      {pullDelta > 10 && (
        <div style={{textAlign:"center",padding:"8px 0",fontSize:11,
          color:pullDelta>=PULL_THRESH?C.accent:C.muted}}>
          {pullDelta>=PULL_THRESH
            ? "Release to refresh market pulse"
            : "Pull to refresh market pulse"}
        </div>
      )}
      {lastRefreshedAt && pullDelta === 0 && (
        <div style={{textAlign:"center",padding:"4px 0 0",fontSize:10,color:C.muted}}>
          ✓ Refreshed at {lastRefreshedAt}
        </div>
      )}
      {/* Header — wide GemPulse banner (1776×576, ~3.08:1) shown full so the
          frame isn't cropped; the wider/shorter aspect naturally takes less
          vertical real estate. Diamond glow pulses over the hex gem when scanning
          (gem sits ~22% from left, ~42% down in this crop). */}
      <div style={{position:"relative",marginBottom:12,overflow:"hidden",
        marginLeft:-16,marginRight:-16}}>
        <img src="/header.jpg" alt="GemPulse"
          style={{width:"100%",height:"auto",display:"block",maxHeight:150}}/>
        {universePhase > 0 && universePhase < 3 && (
          <>
            <div style={{position:"absolute",inset:0,pointerEvents:"none",
              background:"radial-gradient(circle at 24% 40%, rgba(0,212,255,0.55) 0%, rgba(0,212,255,0) 32%)",
              animation:"logoPulse 1.4s ease-in-out infinite"}}/>
            <div style={{position:"absolute",inset:0,pointerEvents:"none",
              background:"radial-gradient(circle at 24% 40%, rgba(0,212,255,0.3) 0%, rgba(0,212,255,0) 50%)",
              animation:"logoPulse 1.4s ease-in-out infinite 0.3s"}}/>
          </>
        )}
        <div style={{position:"absolute",bottom:10,right:12,fontSize:10,fontWeight:600,
          background:"rgba(8,12,20,0.75)",borderRadius:20,padding:"3px 10px",
          backdropFilter:"blur(4px)"}}>
          {currency && currency !== "native" && (
            <span style={{fontSize:9,color:"rgba(255,255,255,0.4)",marginRight:6}}>
              {currency === "usd" ? "US$" : "CA$"}
            </span>
          )}
          {isMarketOpen()
            ? <span style={{color:C.green}}>● Open</span>
            : <span style={{color:"rgba(255,255,255,0.3)"}}>● Closed</span>}
        </div>
      </div>

      {/* Universe scan status — compact single line, no progress bar */}
      {(() => {
        const TOTAL   = 810;
        const pct     = Math.min(100, Math.round((universeScoredCount / TOTAL) * 100));
        const done    = universePhase === 3;
        const scanning= universePhase > 0 && universePhase < 3;
        const lastUpdated = lastScanTime > 0
          ? (() => {
              const diff = Math.round((Date.now() - lastScanTime) / 1000);
              if (diff < 60)   return `${diff}s ago`;
              if (diff < 3600) return `${Math.round(diff/60)}m ago`;
              return new Date(lastScanTime).toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"});
            })()
          : null;
        if (universePhase === 0 && !lastScanTime) return null;
        return (
          <div style={{display:"flex",alignItems:"center",gap:8,
            marginBottom:10,marginTop:-4}}>
            {/* Left: score count + pct */}
            <div style={{display:"flex",alignItems:"center",gap:5,flex:1,minWidth:0}}>
              {scanning && (
                <div style={{width:5,height:5,borderRadius:"50%",flexShrink:0,
                  background:C.accent,animation:"blink 1s ease-in-out infinite"}}/>
              )}
              <span style={{fontSize:10,fontWeight:600,
                color:done?C.green:scanning?C.accent:C.muted,whiteSpace:"nowrap"}}>
                {scanning
                  ? `Scanning… ${universeScoredCount} / ${TOTAL} (${pct}%)`
                  : `${universeScoredCount} stocks scored (${pct}%)`}
              </span>
              {scanning && (
                <span style={{fontSize:9,color:"rgba(0,212,255,0.45)",
                  fontFamily:"monospace",background:"rgba(0,212,255,0.07)",
                  borderRadius:4,padding:"1px 4px",flexShrink:0}}>
                  {universePhase===1?"T1":"T2"}
                </span>
              )}
            </div>
            {/* Right: updated timestamp + action button */}
            <div style={{display:"flex",alignItems:"center",gap:6,flexShrink:0}}>
              {lastUpdated && !scanning && (
                <span style={{fontSize:9,color:C.muted}}>Updated {lastUpdated}</span>
              )}
              {scanning ? (
                <button
                  onClick={()=>{ if(onCancelScan) onCancelScan(); }}
                  title="Cancel scoring"
                  style={{background:"none",border:`1px solid rgba(255,82,82,0.3)`,
                    borderRadius:6,padding:"1px 7px",cursor:"pointer",
                    color:C.red,fontSize:10,fontFamily:"inherit"}}>
                  ✕
                </button>
              ) : (
                <button
                  onClick={()=>{ if(onRefreshWatchlist) onRefreshWatchlist(); }}
                  title="Refresh all scores"
                  style={{background:"none",border:`1px solid ${C.border}`,
                    borderRadius:6,padding:"1px 7px",cursor:"pointer",
                    color:C.accent,fontSize:11,fontFamily:"inherit"}}>
                  ↻
                </button>
              )}
            </div>
          </div>
        );
      })()}

      {/* Market Pulse */}
      <div style={{marginBottom:16}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
          <div style={{fontSize:10,fontWeight:700,color:C.muted,letterSpacing:"0.1em",
            textTransform:"uppercase"}}>Market Pulse</div>
          {loading && (
            <div style={{display:"flex",alignItems:"center",gap:6}}>
              <div style={{width:10,height:10,border:`2px solid ${C.accent}`,
                borderTopColor:"transparent",borderRadius:"50%",
                animation:"spin 0.7s linear infinite"}}/>
              <span style={{fontSize:9,color:C.muted}}>Loading…</span>
            </div>
          )}
        </div>

        {/* v8.4.282 fast rotation / risk-off banner (silent when calm) */}
        <RotationBanner token={token} rotation={market?.rotation} />

        {/* Progress bar while loading */}
        {loading && (
          <div style={{height:3,background:"rgba(255,255,255,0.06)",borderRadius:2,
            overflow:"hidden",marginBottom:10}}>
            <div style={{height:"100%",borderRadius:2,
              background:`linear-gradient(90deg,${C.accent},#00e5aa)`,
              animation:"shimmer 1.4s ease-in-out infinite",
              width:"60%"}}/>
          </div>
        )}

        {loading ? (
          <div style={{display:"flex",gap:8}}>
            {[1,2,3,4].map(i=>(
              <div key={i} style={{flex:1,height:64,borderRadius:12,
                background:"rgba(255,255,255,0.04)",
                animation:"pulse 1.2s ease-in-out infinite"}}/>
            ))}
          </div>
        ) : (
          <>
            {/* Top row: indices */}
            <div style={{display:"flex",gap:8,marginBottom:8}}>
              {[
                {label:"S&P 500 (SPY)", r:spyResult, sym:"SPY"},
                {label:"NASDAQ (QQQ)",  r:qqResult,  sym:"QQQ"},
                {label:"DOW (DIA)",     r:diaResult, sym:"DIA"},
                {label:"TSX (XIU)",     r:tsxResult, sym:"XIU.TO"},
              ].map(({label,r,sym})=>(
                <div key={sym} style={{flex:1,background:C.surface,borderRadius:12,
                  padding:"10px 8px",border:`1px solid ${C.border}`,textAlign:"center"}}>
                  <div style={{fontSize:9,color:C.muted,marginBottom:3}}>{label}</div>
                  <div style={{fontSize:13,fontWeight:700,color:C.text,
                    fontFamily:"monospace"}}>
                    {(r && r.price != null) ? fmtCurrency(r.price, sym, currency, fxRates) : "—"}
                  </div>
                  <div style={{fontSize:10,fontFamily:"monospace",marginTop:2,
                    color: (r && r.changePct != null && r.changePct >= 0) ? C.green
                         : (r && r.changePct != null) ? C.red
                         : C.muted}}>
                    {(r && r.changePct != null)
                      ? `${r.changePct >= 0 ? "+" : ""}${Number(r.changePct).toFixed(2)}%`
                      : "—"}
                  </div>
                </div>
              ))}
            </div>

            {/* Market indicators — 2 rows of tiles */}
            {(!marketReady || vix === null) ? (
              <div style={{display:"flex",flexDirection:"column",gap:8}}>
                <div style={{display:"flex",gap:8}}>
                  {[1,2,3].map(i=>(
                    <div key={i} style={{flex:i===2?2:1,height:72,borderRadius:12,
                      background:"rgba(255,255,255,0.04)",
                      animation:"pulse 1.2s ease-in-out infinite"}}/>
                  ))}
                </div>
                <div style={{display:"flex",gap:8}}>
                  {[1,2].map(i=>(
                    <div key={i} style={{flex:1,height:64,borderRadius:12,
                      background:"rgba(255,255,255,0.04)",
                      animation:"pulse 1.2s ease-in-out infinite"}}/>
                  ))}
                </div>
              </div>
            ) : (
              <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:8}}>

                {/* Row 1: Fear & Greed + Trend — compact single-height row,
                    matched to the Market Regime / metals card height (v8.4.190) */}
                <div style={{display:"flex",gap:8}}>

                  {/* Fear & Greed — number+label, gauge, VIX pill all on one line */}
                  <div onClick={fgd ? () => setFgOpen(o => !o) : undefined}
                    style={{flex:2,background:C.surface,borderRadius:12,
                    padding:"8px 12px",border:`1px solid ${fgOpen ? "rgba(0,212,255,0.4)" : C.border}`,
                    cursor: fgd ? "pointer" : "default",
                    display:"flex",flexDirection:"column",justifyContent:"center",gap:6}}>
                    <div style={{display:"flex",alignItems:"center",gap:3,
                      fontSize:9,color:C.muted,fontWeight:700,
                      letterSpacing:"0.06em",textTransform:"uppercase"}}>
                      Fear &amp; Greed {fgd && <span style={{fontSize:15,lineHeight:1,color:C.accent,fontWeight:900,marginLeft:3,marginRight:2}}>{fgOpen ? "▴" : "▾"}</span>}
                      <InfoTip text={fgd
                        ? "GemPulse's own 0–100 mood gauge, built from six inputs: VIX vs its 50-day average, S&P 500 vs its 125-day average, the share of tracked U.S. stocks above their 50-day average, new 52-week highs vs lows, junk-bond demand (HYG vs IEF) and stocks vs bonds. Each input is ranked against its own last two years, so 0 = unusually fearful and 100 = unusually greedy for that input. Tap to see each input. It describes the mood — it is not a buy or sell signal, and it is not CNN's index. The VIX chip beside it is the volatility reading alone."
                        : "Derived from VIX — a 0–100 sentiment gauge. 0 = Extreme Fear, 100 = Extreme Greed. The VIX reading it's built from is shown in the chip beside it."}/>
                    </div>
                    <div style={{display:"flex",alignItems:"center",gap:10}}>
                      <div style={{textAlign:"center",lineHeight:1,flexShrink:0}}>
                        <div style={{fontSize:20,fontWeight:800,color:fgColor,
                          fontFamily:"monospace"}}>{fg != null ? fg : "—"}</div>
                        <div style={{fontSize:8,fontWeight:700,color:fgColor,marginTop:2}}>{fgLabel}</div>
                      </div>
                      <svg width="46" height="26" viewBox="0 0 56 32" style={{flexShrink:0}}>
                        <path d="M4 28 A24 24 0 0 1 52 28" fill="none"
                          stroke="rgba(255,255,255,0.08)" strokeWidth="5" strokeLinecap="round"/>
                        {fg != null && (
                          <path d="M4 28 A24 24 0 0 1 52 28" fill="none"
                            stroke={fgColor} strokeWidth="5" strokeLinecap="round"
                            strokeDasharray={`${fg * 0.754} 100`}/>
                        )}
                        {fg != null && <>
                          <line x1="28" y1="28"
                            x2={28+22*Math.cos(Math.PI-(fg/100)*Math.PI)}
                            y2={28-22*Math.sin((fg/100)*Math.PI)}
                            stroke={fgColor} strokeWidth="2.5" strokeLinecap="round"/>
                          <circle cx="28" cy="28" r="3" fill={fgColor}/>
                        </>}
                      </svg>
                      {vix != null && (() => {
                        // Thresholds aligned with Market Regime VIX signal (18 / 25 / 35)
                        // so 17.1 reads green in both places — no more yellow/green split.
                        const vc = vix>=35?"#ff5252":vix>=25?"#ff7043":vix>=18?"#ffca28":C.green;
                        return (
                          <span style={{fontSize:10,fontWeight:700,fontFamily:"monospace",
                            borderRadius:7,padding:"3px 8px",whiteSpace:"nowrap",
                            background:`${vc}1a`,border:`1px solid ${vc}40`,color:vc}}>
                            VIX {vix.toFixed(1)}
                          </span>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Market trend — arrow + label; SPY-vs-50MA detail moved to info bubble */}
                  <div style={{flex:1,background:C.surface,borderRadius:12,
                    padding:"8px 12px",border:`1px solid ${C.border}`,
                    display:"flex",flexDirection:"column",justifyContent:"center",gap:6}}>
                    <div style={{display:"flex",alignItems:"center",gap:3,
                      fontSize:9,color:C.muted,fontWeight:700,
                      letterSpacing:"0.06em",textTransform:"uppercase"}}>
                      Trend <InfoTip text="Market trend = SPY price relative to its 50-day moving average. Above the 50-day = bullish, below = bearish, right at it = neutral. Feeds the regime and LT score adjustments."/>
                    </div>
                    <div style={{display:"flex",alignItems:"center",gap:6}}>
                      <span style={{fontSize:18,fontWeight:800,lineHeight:1,
                        color:trend==="bullish"?C.green:trend==="bearish"?C.red:C.yellow}}>
                        {trend==="bullish"?"▲":trend==="bearish"?"▼":"--"}
                      </span>
                      <span style={{fontSize:12,fontWeight:700,
                        color:trend==="bullish"?C.green:trend==="bearish"?C.red:C.yellow}}>{trend}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {fgOpen && fgd && (
              <div style={{background:C.surface,borderRadius:12,padding:"10px 12px",marginBottom:8,
                border:`1px solid ${C.border}`}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:8}}>
                  <div style={{fontSize:11,fontWeight:700,color:C.text}}>
                    What's behind the {fg}
                    {fgd.change5d != null && (
                      <span style={{fontSize:10,fontWeight:600,marginLeft:8,color:fgd.change5d >= 0 ? C.green : C.red}}>
                        {fgd.change5d >= 0 ? "▲" : "▼"} {Math.abs(fgd.change5d)} vs 5 sessions ago
                      </span>
                    )}
                  </div>
                  <div style={{fontSize:9,color:C.muted}}>as of {fgd.asof}</div>
                </div>
                {(fgd.components || []).map(c => (
                  <div key={c.key} style={{marginBottom:6}}>
                    <div style={{display:"flex",justifyContent:"space-between",fontSize:10,color:C.muted,marginBottom:2}}>
                      <span>{c.label}{c.weight < 1 ? " · ½ weight" : ""}</span>
                      <span style={{fontFamily:"monospace",color:c.score == null ? C.muted : _fgBandClr(c.score)}}>
                        {c.score == null ? "n/a" : Math.round(c.score)}
                      </span>
                    </div>
                    <div style={{height:5,borderRadius:3,background:"rgba(255,255,255,0.08)",overflow:"hidden"}}>
                      {c.score != null && <div style={{width:`${Math.max(2, Math.min(100, c.score))}%`,height:"100%",
                        background:_fgBandClr(c.score),opacity:0.85}}/>}
                    </div>
                  </div>
                ))}
                <div style={{fontSize:9,color:C.muted,opacity:0.75,marginTop:6,lineHeight:1.5}}>
                  Each bar ranks that input against its own last two years (0 = unusually fearful, 100 = unusually greedy). GemPulse's own
                  composite, updated after each close — it describes market mood and is not a buy/sell signal or CNN's index.
                </div>
              </div>
            )}

            {/* Market Regime — directly beneath the Fear & Greed row */}
            {token && <MarketRegimeCard token={token} />}

            {/* Prediction Markets (Polymarket) — directly below Market Regime */}
            {polyData && (polyData.cut_prob != null || polyData.recession_prob != null || polyData.bull_prob != null) && (() => {
                  const cutProb  = polyData.cut_prob  != null ? Math.round(polyData.cut_prob  * 100) : null;
                  const holdProb = polyData.hold_prob != null ? Math.round(polyData.hold_prob * 100) : null;
                  const recProb  = polyData.recession_prob != null ? Math.round(polyData.recession_prob * 100) : null;
                  const bullProb = polyData.bull_prob != null ? Math.round(polyData.bull_prob * 100) : null;
                  const hikeProb = polyData.hike_prob != null ? Math.round(polyData.hike_prob * 100) : null;
                  const mtgDate  = polyData.meeting_date || "";

                  // Fed card: colour by expected action
                  const fedAction = hikeProb && hikeProb > 20 ? "hike"
                                  : cutProb  && cutProb  > 30 ? "cut"
                                  : "hold";
                  const fedColor  = fedAction === "cut"  ? C.green
                                  : fedAction === "hike" ? C.red : C.yellow;
                  const fedLabel  = fedAction === "cut"  ? `Cut ${cutProb}%`
                                  : fedAction === "hike" ? `Hike ${hikeProb}%`
                                  : `Hold ${holdProb != null ? holdProb : 100-(cutProb||0)-(hikeProb||0)}%`;

                  // Recession card
                  const recColor = recProb == null ? C.muted
                                 : recProb >= 40 ? C.red : recProb >= 20 ? C.yellow : C.green;
                  const recLabel = recProb == null ? "" : recProb >= 40 ? "High risk" : recProb >= 20 ? "Moderate" : "Low risk";

                  // S&P EOY card
                  const spxBullColor = bullProb == null ? C.muted
                                     : bullProb >= 60 ? C.green : bullProb >= 40 ? C.yellow : C.red;
                  // v8.4.133: threshold is now dynamic (whichever "above $N"
                  // strike Polymarket is actively pricing), not hardcoded 6K.
                  const spxThreshold = polyData.spx_threshold;
                  const spxThresholdLabel = spxThreshold >= 1000
                    ? `${Math.round(spxThreshold / 1000)}K` : spxThreshold;

                  return (
                    <div style={{marginTop:8}}>
                      <div style={{fontSize:9,fontWeight:700,color:C.muted,letterSpacing:"0.08em",
                        textTransform:"uppercase",marginBottom:6,display:"flex",alignItems:"center",gap:4}}>
                        Prediction Markets
                        <InfoTip text="Live probabilities from Polymarket — a real-money prediction market. Fed = probability of rate action at the next FOMC meeting. Recession = probability of US recession by end-2026. S&P EOY = probability of S&P 500 above the market's currently-priced threshold at year end. Updated every 2 hours."/>
                      </div>
                      <div style={{display:"flex",gap:8}}>

                        {/* Fed action */}
                        {cutProb != null && (
                          <div style={{flex:1,background:C.surface,borderRadius:12,
                            padding:"10px 12px",border:`1px solid ${C.border}`}}>
                            <div style={{fontSize:9,color:C.muted,marginBottom:3,fontWeight:700,
                              letterSpacing:"0.06em",textTransform:"uppercase"}}>
                              Fed {mtgDate}
                            </div>
                            <div style={{fontSize:18,fontWeight:800,fontFamily:"monospace",
                              color:fedColor}}>
                              {fedAction === "cut"  ? `${cutProb}%`
                               : fedAction === "hike" ? `${hikeProb}%`
                               : `${holdProb != null ? holdProb : 100-(cutProb||0)-(hikeProb||0)}%`}
                            </div>
                            <div style={{fontSize:9,color:fedColor,fontWeight:600}}>{fedLabel}</div>
                          </div>
                        )}

                        {/* Recession */}
                        {recProb != null && (
                          <div style={{flex:1,background:C.surface,borderRadius:12,
                            padding:"10px 12px",border:`1px solid ${C.border}`}}>
                            <div style={{fontSize:9,color:C.muted,marginBottom:3,fontWeight:700,
                              letterSpacing:"0.06em",textTransform:"uppercase"}}>Recession</div>
                            <div style={{fontSize:18,fontWeight:800,fontFamily:"monospace",
                              color:recColor}}>{recProb}%</div>
                            <div style={{fontSize:9,color:recColor,fontWeight:600}}>{recLabel}</div>
                          </div>
                        )}

                        {/* S&P 500 EOY */}
                        {bullProb != null && (
                          <div style={{flex:1,background:C.surface,borderRadius:12,
                            padding:"10px 12px",border:`1px solid ${C.border}`}}>
                            <div style={{fontSize:9,color:C.muted,marginBottom:3,fontWeight:700,
                              letterSpacing:"0.06em",textTransform:"uppercase"}}>S&amp;P EOY</div>
                            <div style={{fontSize:18,fontWeight:800,fontFamily:"monospace",
                              color:spxBullColor}}>{bullProb}%</div>
                            <div style={{fontSize:9,color:spxBullColor,fontWeight:600}}>
                              {bullProb >= 60 ? `Bull ↑${spxThresholdLabel}` : bullProb >= 40 ? "Uncertain" : `Bear ↓${spxThresholdLabel}`}
                            </div>
                          </div>
                        )}

                      </div>
                    </div>
                  );
                })()}
          </>
        )}
      </div>

      {/* Precious Metals + Crypto */}
      {token && (
        <MetalsCryptoWidget token={token} currency={currency} fxRates={fxRates}/>
      )}

      {/* Trending Carousel — StockTwits top movers.
          Positioned directly under the Crypto section, above the Gap Scanner. */}
      {token && (
        <TrendingCarousel token={token} horizon={horizon} session={marketSession}
          profile={user?.risk_profile || "moderate"} onSelectTicker={onSelectTicker}/>
      )}

      {/* Gap Scanner — always visible when logged in; shows empty state during closed hours.
          horizon/profile make swing+LT scores match the user's current settings
          (same universe-cache entry as watchlist / discovery / portfolio). */}
      {token && (
        <GapScannerCard token={token} session={marketSession}
          horizon={horizon} profile={user?.risk_profile || "moderate"}
          onNavigate={onSelectTicker} onAlertsChanged={onAlertsChanged}/>
      )}

      {/* Top Swing Scores — live from Redis universe cache; falls back to
          score_history when cache is sparse (e.g. post-restart) */}
      {(() => {
        const liveEntries = Object.values(universeData || {}).filter(d => (d.swingScore || 0) > 0);
        const useFallback = liveEntries.length < 5 && topScoresHistory.length > 0;

        // Prefer live universe data; use score_history fallback only when sparse
        const top = useFallback
          ? topScoresHistory.slice(0, 10)
          : liveEntries.sort((a,b) => b.swingScore - a.swingScore).slice(0, 5);

        if (!top.length) return null;

        // Normalise shape — history rows use camelCase already, universe rows too
        const rows = top.map(d => ({
          ticker:     d.ticker,
          swingScore: d.swingScore,
          price:      d.price,
          isLeader:   d.isLeader || d.is_leader || false,
        }));

        return (
          <div style={{background:C.surface,borderRadius:14,padding:"12px 14px",
            border:`1px solid ${C.border}`,marginBottom:14}}>
            <div style={{display:"flex",justifyContent:"space-between",
              alignItems:"center",marginBottom:10}}>
              <div style={{display:"flex",alignItems:"center",gap:6}}>
                <div style={{fontSize:10,fontWeight:700,color:C.muted,
                  letterSpacing:"0.1em",textTransform:"uppercase"}}>
                  ◆ Top Swing Scores
                </div>
                <InfoTip text="Stocks ranked by GemPulse Swing Score (0–100) from the shared universe cache, refreshed every 30 min. Scores here may differ slightly from the stock detail view — the detail score is freshly computed on demand while this uses the last cached value. IBD Leader = RS Rating ≥80 with strong price/volume action, based on Investor's Business Daily methodology." />
              </div>
              <span style={{fontSize:9,color:C.muted}}>
                {useFallback
                  ? <span style={{color:"rgba(255,202,40,0.7)"}}>from history · cache loading…</span>
                  : <>{Object.keys(universeData).length} cached
                      {universePhase < 3 && <span style={{color:C.accent}}> · loading…</span>}
                    </>
                }
              </span>
            </div>
            {rows.map((d,i) => (
              <button key={d.ticker} onClick={()=>onSelectTicker(d.ticker)}
                style={{width:"100%",display:"flex",alignItems:"center",gap:8,
                  padding:"8px 0",background:"none",border:"none",cursor:"pointer",
                  fontFamily:"inherit",textAlign:"left",
                  borderBottom:i<rows.length-1?`1px solid ${C.border}`:"none"}}>
                <span style={{fontSize:10,color:C.muted,width:16,
                  textAlign:"right",flexShrink:0}}>#{i+1}</span>
                <TickerLogo ticker={d.ticker} size={22}/>
                <span style={{fontSize:13,fontWeight:800,fontFamily:"monospace",
                  color:C.text,flex:1}}>{d.ticker}</span>
                {d.isLeader && (
                  <span style={{fontSize:9,color:"#ffca28",fontWeight:700,
                    background:"rgba(255,202,40,0.12)",borderRadius:10,
                    padding:"1px 6px"}}>IBD</span>
                )}
                <span style={{fontSize:11,color:C.muted,fontFamily:"monospace"}}>
                  {fmtCurrency(d.price,d.ticker,currency,fxRates)}
                </span>
                <span style={{fontSize:13,fontWeight:700,fontFamily:"monospace",
                  color:d.swingScore>=72?C.green:d.swingScore>=58?"#69f0ae":
                       d.swingScore>=42?C.yellow:C.red,
                  minWidth:28,textAlign:"right"}}>
                  {d.swingScore?.toFixed(0)}
                </span>
              </button>
            ))}
          </div>
        );
      })()}

      {/* GemPulse Gem Picks — multi-factor quality composite (distinct from raw swing) */}
      <GemPicksCard token={token} horizon={horizon} onSelectTicker={onSelectTicker}
        currency={currency} fxRates={fxRates} />

      {/* Profile Divergence Signal — beneath Top Swing Scores */}
      <ProfileDivergenceCard token={token} horizon={horizon} onSelectTicker={onSelectTicker} />

      {/* ML Score Accuracy — score bucket → avg return + win rate (visible once 1m data fills) */}
      <MLScoreAccuracyCard token={token} horizon={horizon} />

      {/* ML Backtest — score ≥70 strategy vs SPY (same data-maturity gate) */}
      <MLBacktestCard token={token} horizon={horizon} profile={user?.risk_profile} />

      {/* Unusual Activity — Polygon dark-pool short + insider Form 4 + rel volume.
          Positioned beneath Gem Picks, directly above Sector Performance. */}
      {token && (
        <UnusualActivityCard token={token} horizon={horizon}
          profile={user?.risk_profile || "moderate"} onNavigate={onSelectTicker}/>
      )}

      {/* Sector Heatmap — live ETF performance */}
      {token && (
        <SectorHeatmap token={token} universeData={universeData}/>
      )}

      {/* Earnings This Week — holdings/watchlist/top-score, cut to 12 */}
      {token && (
        <EarningsThisWeekCard token={token} horizon={horizon} onSelectTicker={onSelectTicker}/>
      )}

      {/* Upcoming IPOs — confirmed (Finnhub) + anticipated (manual, TBD) */}
      {token && (
        <UpcomingIPOsCard token={token}/>
      )}

      {/* Global Controls */}
      <div style={{background:C.surface,borderRadius:16,padding:"16px",
        border:`1px solid ${C.border}`,marginBottom:20}}>
        <div style={{fontSize:10,fontWeight:700,color:C.muted,letterSpacing:"0.1em",
          textTransform:"uppercase",marginBottom:14}}>Your Settings</div>

        {/* Risk Profile */}
        <div style={{marginBottom:14}}>
          <div style={{fontSize:11,color:C.muted,marginBottom:8}}>Risk profile</div>
          <div style={{display:"flex",gap:6}}>
            {PROFILES.map(p=>(
              <button key={p.key} onClick={()=>onProfileChange(p.key)} style={{
                flex:1,padding:"8px 4px",borderRadius:10,cursor:"pointer",
                fontFamily:"inherit",fontSize:11,fontWeight:700,
                border:`1px solid ${user.risk_profile===p.key?C.accent:C.border}`,
                background:user.risk_profile===p.key?"rgba(0,212,255,0.1)":C.card,
                color:user.risk_profile===p.key?C.accent:C.muted,
                transition:"all 0.15s",
              }}>
                <div style={{fontSize:14,marginBottom:2}}>{p.emoji}</div>
                <div>{p.label}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Horizon */}
        <div>
          <div style={{fontSize:11,color:C.muted,marginBottom:8}}>Time horizon</div>
          <div style={{display:"flex",gap:4}}>
            {HORIZONS.map(h=>(
              <button key={h.key} onClick={()=>setHorizon(h.key)} style={{
                flex:1,padding:"7px 2px",borderRadius:8,cursor:"pointer",
                fontFamily:"inherit",fontSize:11,fontWeight:600,border:"none",
                background:horizon===h.key?"rgba(0,212,255,0.15)":C.card,
                color:horizon===h.key?C.accent:C.muted,
                borderBottom:`2px solid ${horizon===h.key?C.accent:"transparent"}`,
                transition:"all 0.15s",
              }}>{h.label.replace(" ","")}</button>
            ))}
          </div>
        </div>
      </div>


      {/* Legal Disclaimer */}
      <div style={{
        marginTop:24,padding:"14px 16px",
        background:"rgba(255,255,255,0.02)",
        border:"1px solid rgba(255,255,255,0.06)",
        borderRadius:12,
      }}>
        <div style={{fontSize:10,fontWeight:700,color:"rgba(255,255,255,0.25)",
          letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:6}}>
          ⚠️ Disclaimer
        </div>
        <div style={{fontSize:10,color:"rgba(255,255,255,0.25)",lineHeight:1.7}}>
          GemPulse is for <strong style={{color:"rgba(255,255,255,0.35)"}}>
          educational and informational purposes only</strong> and does not constitute
          financial, investment, or trading advice. All scores, signals, and portfolio
          suggestions are generated algorithmically and may not reflect current market
          conditions. Past performance is not indicative of future results.
          Always conduct your own due diligence and consult a qualified financial
          advisor before making any investment decisions. Trading involves significant
          risk, including the potential loss of principal.
        </div>
      </div>
    </div>
  );
}

// --- Portfolio Builder --------------------------------------------------------
// ─── Portfolio AI Briefing (Stage C) ──────────────────────────────────────────
// "What should I do today?" — personalized 5-6 bullet briefing powered by
// Gemini. Reads the user's optimizer portfolios, watchlist scores, active
// alerts, upcoming earnings, and market regime. Cached 4h per user.
function PortfolioBriefing({ user, token, setTab }) {
  const [briefing,           setBriefing]           = React.useState(null);
  const [loading,            setLoading]            = React.useState(false);
  const [error,              setError]              = React.useState("");
  const [fetchedAt,          setFetchedAt]          = React.useState(null);
  // Portfolio selector — loaded once from /portfolio/optimizer on mount
  const [portfolioList,      setPortfolioList]      = React.useState([]); // [{id,name}]
  const [selectedPortfolio,  setSelectedPortfolio]  = React.useState(null); // null = "all"

  // Load portfolio names for selector (lightweight — name+id only needed)
  React.useEffect(() => {
    if (!token) return;
    apiFetch("/portfolio/optimizer", token)
      .then(data => {
        if (Array.isArray(data) && data.length > 1) {
          setPortfolioList(data.map(p => ({
            id:   String(p.id || ""),
            name: p.name || "Portfolio",
          })));
        }
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Clear stale briefing when the user switches portfolio selection, then
  // re-fetch for the newly selected scope. v8.4.136: this effect previously
  // only cleared state and never re-fetched, so switching to a specific
  // portfolio left the screen blank (or showing stale "Briefing unavailable"
  // error text from a prior failed load) until the user hit Refresh.
  const _briefingMounted = React.useRef(false);
  React.useEffect(() => {
    setBriefing(null);
    setError("");
    if (!_briefingMounted.current) { _briefingMounted.current = true; return; }
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPortfolio]);

  const load = async (force = false) => {
    setLoading(true); setError("");
    const params = new URLSearchParams();
    if (force) params.set("force", "true");
    if (selectedPortfolio) params.set("portfolio_id", selectedPortfolio);
    const qs = params.toString();
    // v8.4.386: async submit→poll (was a synchronous POST). A cold-cache briefing
    // runs context assembly + TWO LLM calls (writer + fact-check) and could exceed
    // Cloudflare's ~100s edge timeout → the browser threw "Load failed" and showed
    // "Briefing unavailable". The submit returns a request_id instantly and each poll
    // is fast, so the edge timeout can't fire; a warm cache resolves on the first poll.
    const subUrl = "/ai/portfolio-briefing-async" + (qs ? "?" + qs : "");
    try {
      const sub = await apiFetch(subUrl, token, { method: "POST" });
      const reqId = sub?.request_id;
      if (!reqId) throw new Error("no request_id returned");
      let finalInfo = null;
      const start = Date.now();
      while (Date.now() - start < 180000) {   // poll up to 3 min; first poll is immediate
        const info = await apiFetch(`/ai/portfolio-briefing-async/${reqId}`, token);
        if (info?.status === "done")  { finalInfo = info; break; }
        if (info?.status === "error") { finalInfo = info; break; }
        setError("Agent working…");
        await new Promise(r => setTimeout(r, 3000));
      }
      if (!finalInfo) throw new Error("timed out after 3 min");
      if (finalInfo.status === "error") throw new Error(finalInfo.error || "AI agent temporarily unavailable");
      setError("");
      setBriefing(finalInfo);
      setFetchedAt(new Date());
      // Back-fill portfolio list from response if the mount fetch returned only 1
      if ((finalInfo.portfolioNames || []).length > 1) setPortfolioList(finalInfo.portfolioNames);
    } catch(e) {
      setError(e.message || "AI agent temporarily unavailable — try again in a moment");
    }
    setLoading(false);
  };

  React.useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Render markdown-ish briefing text. Handles **bold** and line breaks.
  const renderBriefing = (text) => {
    if (!text) return null;
    return text.split("\n").map((line, i) => {
      if (!line.trim()) return <div key={i} style={{height:8}}/>;
      // Bold headers like **🎯 Top Signal:**
      const parts = line.split(/(\*\*[^*]+\*\*)/g);
      const rendered = parts.map((p, j) => {
        if (p.startsWith("**") && p.endsWith("**")) {
          return (
            <strong key={j} style={{color: C.accent, fontWeight:700}}>
              {p.slice(2, -2)}
            </strong>
          );
        }
        return p;
      });
      return (
        <div key={i} style={{
          fontSize:13, lineHeight:1.7, color:C.text,
          marginBottom:4,
          paddingLeft: line.trim().startsWith("**") ? 0 : 4,
        }}>
          {rendered}
        </div>
      );
    });
  };

  const cacheAge = fetchedAt
    ? Math.round((Date.now() - fetchedAt.getTime()) / 60000)
    : null;

  return (
    <div style={{padding:"0 16px"}}>
      {/* Header — only shown when a briefing already exists */}
      {briefing && (
        <div style={{marginBottom:16,display:"flex",alignItems:"center",
          justifyContent:"space-between"}}>
          <div>
            <div style={{fontSize:16,fontWeight:800,color:C.text,marginBottom:2}}>
              🤖 AI Portfolio Briefing
            </div>
            <div style={{fontSize:12,color:C.muted}}>
              "What should I do today?" · Powered by Gemini
            </div>
          </div>
          <button onClick={()=>load(true)} disabled={loading} style={{
            background:"rgba(0,212,255,0.10)",border:`1px solid ${C.accent}`,
            borderRadius:8,padding:"7px 14px",color:C.accent,fontFamily:"inherit",
            fontSize:12,fontWeight:700,cursor:loading?"not-allowed":"pointer",
            opacity:loading?0.5:1,
          }}>
            {loading ? "…" : "↻ Refresh"}
          </button>
        </div>
      )}

      {/* Portfolio selector — shown when user has 2+ portfolios */}
      {portfolioList.length > 1 && (
        <div style={{background:C.surface,borderRadius:12,padding:"12px 14px",
          border:`1px solid ${C.border}`,marginBottom:14}}>
          <div style={{fontSize:10,fontWeight:700,color:C.muted,letterSpacing:"0.08em",
            textTransform:"uppercase",marginBottom:8}}>
            Briefing scope
          </div>
          <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
            {/* "All portfolios" option */}
            {(() => {
              const sel = selectedPortfolio === null;
              return (
                <button onClick={()=>setSelectedPortfolio(null)} style={{
                  padding:"6px 12px",borderRadius:20,border:`1px solid ${sel?C.accent:C.border}`,
                  background:sel?"rgba(0,212,255,0.12)":"rgba(255,255,255,0.04)",
                  color:sel?C.accent:C.muted,
                  fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit",
                }}>
                  📊 All portfolios
                </button>
              );
            })()}
            {/* Individual portfolio pills */}
            {portfolioList.map(p => {
              const sel = selectedPortfolio === p.id;
              return (
                <button key={p.id} onClick={()=>setSelectedPortfolio(p.id)} style={{
                  padding:"6px 12px",borderRadius:20,border:`1px solid ${sel?C.accent:C.border}`,
                  background:sel?"rgba(0,212,255,0.12)":"rgba(255,255,255,0.04)",
                  color:sel?C.accent:C.muted,
                  fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit",
                }}>
                  {p.name}
                </button>
              );
            })}
          </div>
          {selectedPortfolio && (
            <div style={{fontSize:11,color:C.muted,marginTop:8,lineHeight:1.5}}>
              Briefing will focus on <strong style={{color:C.text}}>
                {portfolioList.find(p=>p.id===selectedPortfolio)?.name}
              </strong> holdings only. Switch to <em>All portfolios</em> for a combined view.
            </div>
          )}
        </div>
      )}

      {/* Context pills */}
      {briefing && !loading && (
        <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:14}}>
          {briefing.regimeLabel && (
            <span style={{background:"rgba(0,212,255,0.08)",
              border:"1px solid rgba(0,212,255,0.2)",
              borderRadius:20,padding:"3px 10px",fontSize:11,
              fontWeight:700,color:C.accent}}>
              {briefing.regimeLabel}
            </span>
          )}
          {/* Active portfolio pill — shows which scope was used */}
          {briefing.activePortfolioName ? (
            <span style={{background:"rgba(255,202,40,0.08)",
              border:"1px solid rgba(255,202,40,0.25)",
              borderRadius:20,padding:"3px 10px",fontSize:11,
              fontWeight:700,color:"#ffca28"}}>
              📁 {briefing.activePortfolioName}
            </span>
          ) : portfolioList.length > 1 ? (
            <span style={{background:C.surface,border:`1px solid ${C.border}`,
              borderRadius:20,padding:"3px 10px",fontSize:11,color:C.muted}}>
              📊 All {portfolioList.length} portfolios
            </span>
          ) : null}
          {briefing.watchlistCount > 0 && (
            <span style={{background:C.surface,border:`1px solid ${C.border}`,
              borderRadius:20,padding:"3px 10px",fontSize:11,color:C.muted}}>
              {briefing.watchlistCount} watchlist tickers
            </span>
          )}
          {briefing.earningsCount > 0 && (
            <span style={{background:"rgba(255,202,40,0.08)",
              border:"1px solid rgba(255,202,40,0.25)",
              borderRadius:20,padding:"3px 10px",fontSize:11,
              fontWeight:700,color:"#ffca28"}}>
              ⚠ {briefing.earningsCount} earnings this week
            </span>
          )}
          {cacheAge !== null && (
            <span style={{background:C.surface,border:`1px solid ${C.border}`,
              borderRadius:20,padding:"3px 10px",fontSize:11,color:C.muted}}>
              Updated {cacheAge}m ago
            </span>
          )}
        </div>
      )}

      {/* Loading state */}
      {loading && !briefing && (
        <div style={{textAlign:"center",padding:"48px 0",color:C.muted}}>
          <div style={{width:36,height:36,border:`3px solid rgba(0,212,255,0.2)`,
            borderTopColor:C.accent,borderRadius:"50%",
            animation:"spin 0.8s linear infinite",margin:"0 auto 14px"}}/>
          <div style={{fontSize:14,fontWeight:700,color:C.text,marginBottom:6}}>
            Analysing your portfolio…
          </div>
          <div style={{fontSize:12,color:C.muted}}>
            Reading watchlist · checking optimizer · reviewing earnings
          </div>
        </div>
      )}

      {/* Error state */}
      {error && !loading && (
        <div style={{background:"rgba(255,82,82,0.08)",
          border:"1px solid rgba(255,82,82,0.25)",
          borderRadius:12,padding:"14px 16px",marginBottom:12}}>
          <div style={{fontSize:13,color:"#ff5252",fontWeight:600,marginBottom:4}}>
            ❌ Briefing unavailable
          </div>
          <div style={{fontSize:12,color:C.muted}}>{error}</div>
        </div>
      )}

      {/* Holdings prompt banner — shown when no optimizer holdings detected */}
      {briefing && !loading && (briefing.portfolioCount || 0) === 0 && (
        <div style={{
          background:"rgba(0,212,255,0.06)",
          border:"1px solid rgba(0,212,255,0.3)",
          borderRadius:14,padding:"18px 16px",marginBottom:16,
        }}>
          <div style={{fontSize:15,fontWeight:800,color:C.accent,marginBottom:6}}>
            📊 No holdings found in this briefing
          </div>
          <div style={{fontSize:13,color:C.text,lineHeight:1.65,marginBottom:6}}>
            The AI is currently working from your watchlist only.
          </div>
          <div style={{fontSize:12,color:C.muted,lineHeight:1.65,marginBottom:14,
            background:"rgba(0,0,0,0.2)",borderRadius:8,padding:"10px 12px"}}>
            <strong style={{color:C.text}}>Already have holdings in the Optimizer?</strong>
            {" "}Tap <em>Refresh with holdings</em> below — the briefing re-reads your saved
            positions automatically. No need to re-run the Optimizer first.
            <br/><br/>
            <strong style={{color:C.text}}>Haven't added holdings yet?</strong>
            {" "}Go to the Optimizer tab, add your positions, then return here and refresh.
            The briefing will then analyse your specific holdings, flag trim/exit signals,
            warn about earnings on stocks you own, and tailor picks to complement your portfolio.
          </div>
          <div style={{display:"flex",gap:8}}>
            <button onClick={()=>load(true)} style={{
              flex:2,padding:"10px",borderRadius:10,border:"none",
              background:C.accent,color:C.bg,
              fontFamily:"inherit",fontSize:13,fontWeight:700,cursor:"pointer",
            }}>
              ↻ Refresh with holdings
            </button>
            <button onClick={()=>setTab("optimizer")} style={{
              flex:1,padding:"10px",borderRadius:10,
              border:`1px solid ${C.accent}`,background:"rgba(0,212,255,0.08)",color:C.accent,
              fontFamily:"inherit",fontSize:12,fontWeight:700,cursor:"pointer",
            }}>
              Optimizer →
            </button>
          </div>
        </div>
      )}

      {/* Briefing content */}
      {briefing?.briefing && (
        <div style={{background:C.surface,borderRadius:14,padding:"18px 16px",
          border:`1px solid ${C.border}`,lineHeight:1.8}}>
          {renderBriefing(briefing.briefing)}
        </div>
      )}

      {/* Empty state — prominent generate button matching Portfolio Builder style */}
      {!loading && !error && !briefing && (
        <div style={{padding:"24px 0"}}>
          <div style={{textAlign:"center",marginBottom:20}}>
            <div style={{fontSize:36,marginBottom:12}}>🤖</div>
            <div style={{fontSize:16,fontWeight:800,color:C.text,marginBottom:6}}>
              AI Portfolio Briefing
            </div>
            <div style={{fontSize:13,color:C.muted,lineHeight:1.65,
              maxWidth:300,margin:"0 auto"}}>
              "What should I do today?" — personalized analysis of your watchlist,
              holdings, active alerts and upcoming earnings. Powered by Gemini.
            </div>
          </div>
          <button onClick={()=>load(true)} disabled={loading} style={{
            width:"100%",padding:"13px",borderRadius:12,border:"none",
            background:C.accent,color:C.bg,
            fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:"inherit",
            transition:"all 0.2s",
          }}>
            🤖 Generate Briefing
          </button>
        </div>
      )}

      {/* Footer note */}
      <div style={{marginTop:14,fontSize:11,color:C.muted,textAlign:"center",lineHeight:1.6}}>
        Briefings are cached for 4 hours · Powered by Gemini
      </div>
    </div>
  );
}


// ═════════════════════════════════════════════════════════════════════════════
//  PORTFOLIO DASHBOARD  (v8.4.391 — Phase 1 of the Portfolio-tab redesign)
//  Empty/populated state machine + [My Portfolio | Build]. The shell paints from
//  GET /portfolio/dashboard (cheap-sync, no LLM); the briefing is the only async card.
//  Ledger-based editing via TransactionSheet (never the optimizer confirm flow).
//  Gated by GET /portfolio/ledger/status — when the backend flag is off the legacy
//  3-tab screen renders unchanged.
// ═════════════════════════════════════════════════════════════════════════════
const _pdMoney = (v, d=0, sym="$") => (v==null || isNaN(v)) ? "—"
  : (v<0?"-":"") + sym + Math.abs(v).toLocaleString("en",{minimumFractionDigits:d,maximumFractionDigits:d});
const _pdCompact = (v, sym="$") => {
  if (v==null || isNaN(v)) return "—";
  const a = Math.abs(v);
  const s = a>=1e6 ? (a/1e6).toFixed(2)+"M" : a>=1e4 ? (a/1e3).toFixed(1)+"K"
          : a.toLocaleString("en",{maximumFractionDigits:0});
  return (v<0?"-":"") + sym + s;
};
const _pdPct = (v, d=1) => (v==null || isNaN(v)) ? "—" : `${v>0?"+":""}${v.toFixed(d)}%`;
const _pdColor = (v) => v==null ? C.muted : v>0 ? C.green : v<0 ? C.red : C.muted;
const _PD_PALETTE = ["#00d4ff","#448aff","#b388ff","#ff80ab","#ffca28","#69f0ae","#ff9e40"];
const _PD_VERDICT = {
  EXIT:{color:C.red,   bg:C.redBg,    label:"EXIT"},
  TRIM:{color:C.yellow,bg:C.yellowBg, label:"TRIM"},
  ADD: {color:C.green, bg:C.greenBg,  label:"ADD"},
  HOLD:{color:C.blue,  bg:C.blueBg,   label:"HOLD"},
  REVIEW:{color:C.yellow,bg:C.yellowBg,label:"REVIEW"},
};
const _PD_FLAG_ICON = {"profit-take":"💰","pullback":"⚠️","bounce":"🟢"};
const _PD_TONE = {risk:{c:C.red,bg:C.redBg}, watch:{c:C.yellow,bg:C.yellowBg}, info:{c:C.blue,bg:C.blueBg}, positive:{c:C.green,bg:C.greenBg}};
// Setup-quality rating (backend _holding_quality): swing label + technicals + patterns + timing.
const _PD_QUALITY = {strong:{c:C.green,bg:C.greenBg}, neutral:{c:C.muted,bg:"rgba(255,255,255,0.06)"}, weak:{c:C.red,bg:C.redBg}};

// Mirrors the backend's _portfolio_concentration on FX-REBASED values (the server's caps and
// benchmark table are authoritative; this only re-derives weights in the display currency).
function _pdConcentration(holdings, conc) {
  if (!conc) return conc;
  const hv = holdings.reduce((a,h) => a + (h.marketValue || 0), 0);
  if (!hv) return conc;
  const bySec = {}, byTk = {};
  holdings.forEach(h => { const mv = h.marketValue || 0;
    bySec[h.sectorNorm || h.sector || "Other"] = (bySec[h.sectorNorm || h.sector || "Other"] || 0) + mv;
    byTk[h.ticker] = (byTk[h.ticker] || 0) + mv; });
  const r1 = (v) => Math.round(v*10)/10;
  const flags = [], n = Object.keys(byTk).length, cap = conc.caps || {};
  Object.entries(bySec).sort((a,b) => b[1]-a[1]).forEach(([sec,mv]) => { const pct = r1(mv/hv*100);
    if (sec !== "Other" && pct > cap.sector) flags.push({kind:"sector", label:`${sec} is ${pct}% of your holdings`, pct, cap:cap.sector, severity: pct > cap.sector+15 ? "high" : "watch"}); });
  if (n >= 3) Object.entries(byTk).sort((a,b) => b[1]-a[1]).forEach(([tk,mv]) => { const pct = r1(mv/hv*100);
    if (pct > cap.position) flags.push({kind:"position", label:`${tk} is ${pct}% of your holdings`, pct, cap:cap.position, severity: pct > cap.position+10 ? "high" : "watch"}); });
  else flags.push({kind:"count", label:`Only ${n} position${n!==1?"s":""} — limited diversification`, pct:null, cap:null, severity:"watch"});
  const bm = conc.benchmarks || {};
  return {...conc, flags, positions:n,
    top3Pct: r1(Object.values(byTk).sort((a,b) => b-a).slice(0,3).reduce((a,v) => a+v, 0)/hv*100),
    sectors: Object.entries(bySec).sort((a,b) => b[1]-a[1]).map(([sec,mv]) => { const pct = r1(mv/hv*100);
      return {sector:sec, pct, benchmark: bm[sec] ?? null, diff: bm[sec] != null ? r1(pct - bm[sec]) : null}; })};
}
// Last-known server flag, persisted so a transient failure of /portfolio/ledger/status (deploy window,
// network blip) can never bounce you back to the legacy screen. The server stays authoritative: every
// session re-verifies (with retries) and only an explicit enabled:false switches screens.
const _LEDGER_LS = "gp_ledger_enabled";
let _ledgerEnabledCache = (() => {
  try { const v = localStorage.getItem(_LEDGER_LS); return v === "1" ? true : v === "0" ? false : null; } catch (_) { return null; }
})();
let _ledgerVerified = false;

// TSX/TSXV tickers are priced in CAD, everything else in USD. The backend sums raw native
// prices, so a mixed portfolio's totals are wrong until rebased. Everything the dashboard
// SUMS is converted into ONE display currency here (usd for "native"/"usd", cad for "cad"),
// reusing the app's fxRates + the same defaults as fmtCurrency. Cash is a single ledger
// number with no currency of its own, so it is shown as-is.
const _pdIsCad = (t) => /\.(TO|V)$/i.test(t || "");
const _pdSym = (currency) => currency === "usd" ? "US$" : currency === "cad" ? "CA$" : "$";

function _pdRebase(dash, currency, fxRates) {
  const toCad = currency === "cad";
  const rows = dash.holdings || [];
  const sn0 = dash.snapshot || {};
  const cadFlows = Math.abs(sn0.realizedByCcy?.cad || 0) + Math.abs(sn0.dividendsByCcy?.cad || 0);
  if (!toCad && !cadFlows && !rows.some(h => _pdIsCad(h.ticker))) return dash;   // all-USD: nothing to convert
  const usdToCad = fxRates?.USDCAD || 1.36, cadToUsd = fxRates?.CADUSD || 0.735;
  const f = (t) => toCad ? (_pdIsCad(t) ? 1 : usdToCad) : (_pdIsCad(t) ? cadToUsd : 1);
  const m = (v, k) => (v == null ? v : v * k);
  const hs = rows.map(h => { const k = f(h.ticker);
    return {...h, price:m(h.price,k), avgCost:m(h.avgCost,k), marketValue:m(h.marketValue,k), plDollar:m(h.plDollar,k),
      realizedPl:m(h.realizedPl,k), dividends:m(h.dividends,k), targetValue:m(h.targetValue,k), deltaValue:m(h.deltaValue,k)}; });
  const uK = toCad ? usdToCad : 1, cK = toCad ? 1 : cadToUsd;
  const conv = (o) => o ? (o.usd || 0) * uK + (o.cad || 0) * cK : null;
  const realized = conv(sn0.realizedByCcy), divs = conv(sn0.dividendsByCcy);

  const hv = hs.reduce((a,h) => a + (h.marketValue || 0), 0);
  const tracked = !!dash.cash?.tracked, cash = tracked ? (dash.cash.value || 0) : 0;
  const total = hv + cash;
  hs.forEach(h => { h.weightPct = (h.marketValue && hv) ? Math.round(h.marketValue / hv * 1000) / 10 : null; });

  let prev = 0, dayD = 0;
  hs.forEach(h => { const mv = h.marketValue || 0;
    if (h.changePct != null && mv) { const pv = mv / (1 + h.changePct/100); prev += pv; dayD += mv - pv; } else prev += mv; });
  prev += cash;
  const cost = hs.reduce((a,h) => a + (h.avgCost ? h.avgCost * h.quantity : 0), 0);
  const unreal = hs.reduce((a,h) => a + (h.plDollar || 0), 0);
  const bn = hs.reduce((a,h) => a + (h.beta ? h.beta * (h.marketValue||0) : 0), 0);
  const bd = hs.reduce((a,h) => a + (h.beta ? (h.marketValue||0) : 0), 0);

  const sec = {}; hs.forEach(h => { sec[h.sector] = (sec[h.sector] || 0) + (h.marketValue || 0); });
  const sectorBreakdown = {};
  Object.entries(sec).sort((a,b) => b[1]-a[1]).forEach(([k,v]) => { sectorBreakdown[k] = hv ? Math.round(v/hv*1000)/10 : 0; });

  const byT = {}; hs.forEach(h => { byT[h.ticker] = (byT[h.ticker] || 0) + (h.marketValue || 0); });
  const ranked = Object.entries(byT).sort((a,b) => b[1]-a[1]);
  const base = total > 0 ? total : hv;
  const donut = base ? ranked.slice(0,7).filter(([,v]) => v > 0).map(([t,v]) => ({label:t, value:v, pct:Math.round(v/base*1000)/10})) : [];
  const other = ranked.slice(7).reduce((a,[,v]) => a + v, 0);
  if (other > 0 && base) donut.push({label:"Other", value:other, pct:Math.round(other/base*1000)/10, count:ranked.length-7});
  if (tracked && cash > 0 && base) donut.push({label:"Cash", value:cash, pct:Math.round(cash/base*1000)/10, isCash:true});

  const actions = (dash.actions || []).map(a => a.ticker ? {...a, deltaValue: m(a.deltaValue, f(a.ticker))} : a);
  const hero = actions[0] || dash.hero;
  return {...dash, hero, actions, holdings: hs, concentration: _pdConcentration(hs, dash.concentration), fxNote: `Values in ${toCad ? "CAD" : "USD"} · 1 USD = ${usdToCad.toFixed(3)} CAD`,
    cash: dash.cash ? {...dash.cash, pct: (tracked && total) ? Math.round(cash/total*1000)/10 : dash.cash.pct} : dash.cash,
    snapshot: {...dash.snapshot, totalValue: total, holdingsValue: hv,
      dayChangeDollar: prev ? dayD : null, dayChangePct: prev ? Math.round(dayD/prev*10000)/100 : null,
      realizedPl: realized, dividends: divs, unrealizedPl: unreal, unrealizedPlPct: cost ? Math.round(unreal/cost*1000)/10 : null,
      avgBeta: bd ? Math.round(bn/bd*100)/100 : dash.snapshot?.avgBeta, sectorBreakdown, donut}};
}

function ScorePair({ sw, lt, size=11 }) {
  const n = (v) => v != null ? Math.round(v) : "—";
  return (
    <span style={{display:"inline-flex",alignItems:"center",gap:10}}>
      <span title="Swing score" style={{display:"inline-flex",alignItems:"center",gap:4}}>
        <SwingIcon size={size} color={C.muted}/><span style={{fontFamily:"monospace"}}>{n(sw)}</span></span>
      <span title="Long-term score" style={{display:"inline-flex",alignItems:"center",gap:4}}>
        <LTIcon size={size} color={C.muted}/><span style={{fontFamily:"monospace"}}>{n(lt)}</span></span>
    </span>
  );
}

function _pdBold(text) {
  return String(text||"").split(/(\*\*[^*]+\*\*)/g).map((p,j) =>
    p.startsWith("**") && p.endsWith("**")
      ? <strong key={j} style={{color:C.accent,fontWeight:700}}>{p.slice(2,-2)}</strong> : p);
}

// ── Allocation donut (own SVG — segments incl. Other + muted Cash) ─────────────
function PortfolioDonut({ segments, total, size=132, sym="$" }) {
  const r = (size-16)/2, circ = 2*Math.PI*r;
  const sum = segments.reduce((a,s)=>a+(s.pct||0),0) || 1;
  let acc = 0;
  return (
    <svg width={size} height={size} style={{flexShrink:0}}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth={14}/>
      {segments.map((s,i) => {
        const len = Math.max(0,(s.pct/sum))*circ;
        const col = s.isCash ? "#3b4a63" : s.label==="Other" ? "#6b7a99" : _PD_PALETTE[i % _PD_PALETTE.length];
        const el = (
          <circle key={s.label+i} cx={size/2} cy={size/2} r={r} fill="none" stroke={col} strokeWidth={14}
            strokeDasharray={`${Math.max(0,len-1.5)} ${circ-Math.max(0,len-1.5)}`}
            strokeDashoffset={-acc} transform={`rotate(-90 ${size/2} ${size/2})`}/>
        );
        acc += len;
        return el;
      })}
      <text x={size/2} y={size/2-4} textAnchor="middle" fill={C.text} fontSize={size*0.16} fontWeight="800">{_pdCompact(total, sym)}</text>
      <text x={size/2} y={size/2+14} textAnchor="middle" fill={C.muted} fontSize={9} letterSpacing="0.08em">TOTAL VALUE</text>
    </svg>
  );
}

// ── Transaction sheet — Buy / Sell / Dividend / DRIP / Deposit / Withdraw / Set cash ──
const _TX_TYPES = [
  {id:"buy", label:"Buy"}, {id:"sell", label:"Sell"}, {id:"dividend", label:"Dividend"},
  {id:"drip", label:"DRIP"}, {id:"deposit", label:"Deposit"}, {id:"withdrawal", label:"Withdraw"},
];

function TransactionSheet({ token, portfolios, pid: pid0, holdings, cashTracked, cashValue, initial, universeData, onClose, onDone }) {
  const isEdit = !!initial?.editId;   // editing an existing row in place vs. adding a new one
  const [type,   setType]   = useState(initial?.type || "buy");
  const [pid,    setPid]    = useState(initial?.pid || pid0);
  const [ticker, setTicker] = useState((initial?.ticker || "").toUpperCase());
  const [shares, setShares] = useState(initial?.shares ? String(initial.shares) : "");
  const [price,  setPrice]  = useState(isEdit && initial?.price != null ? String(initial.price) : "");
  const [fees,   setFees]   = useState(isEdit && initial?.fees ? String(initial.fees) : "");
  const [amount, setAmount] = useState(isEdit && initial?.cash_amount != null ? String(initial.cash_amount) : "");
  const [balance,setBalance]= useState(cashValue != null ? String(Math.abs(cashValue)) : "");
  const [margin, setMargin] = useState(cashValue != null && cashValue < 0);   // iOS decimal keypad has no "−"
  const [date,   setDate]   = useState(() => (isEdit && initial?.ts) ? String(initial.ts).slice(0,10) : new Date().toISOString().slice(0,10));
  const [busy,   setBusy]   = useState(false);
  const [err,    setErr]    = useState("");
  const [followed, setFollowed] = useState(initial?.source === "recommendation");   // attribution (Phase 2b)
  const priceTouched = React.useRef(isEdit);   // don't let the live-price effect clobber an edited row's own price

  const T   = ticker.trim().toUpperCase();
  const pos = holdings.find(h => h.portfolioId === pid && h.ticker === T) || null;   // NATIVE-currency holdings
  const sy  = _pdIsCad(T) ? "CA$" : "$";                                             // prices/avg cost are native
  const isShareTx = ["buy","sell","drip"].includes(type);
  const isCashTx  = ["deposit","withdrawal","dividend"].includes(type);
  const isAdjust  = type === "adjust";
  const held = holdings.filter(h => h.portfolioId === pid);

  // Live price default (position price → shared universe price); user edits win.
  React.useEffect(() => {
    if (priceTouched.current || !(isShareTx || isAdjust)) return;
    const p = isAdjust ? pos?.avgCost : (pos?.price ?? universeData?.[T]?.price);
    setPrice(p ? String(Number(p).toFixed(2)) : "");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [T, type, pid]);

  const sh = parseFloat(shares), px = parseFloat(price), fe = parseFloat(fees) || 0, am = parseFloat(amount);
  let preview = null;
  if (isShareTx && sh > 0 && px >= 0) {
    const oq = pos?.quantity || 0, oa = pos?.avgCost || 0;
    if (type === "sell") {
      const realized = sh*(px-oa) - fe;
      preview = { lines:[
        ["Proceeds", _pdMoney(sh*px - fe, 2, sy)],
        ["Realized P&L", oa>0 ? _pdMoney(realized, 2, sy) : "—", oa>0 ? _pdColor(realized) : C.muted],
        ["Shares left", (oq-sh).toLocaleString("en",{maximumFractionDigits:4})],
        ...(cashTracked ? [["Cash after", _pdMoney(cashValue + sh*px - fe, 2)]] : []),
      ], warn: sh > oq + 1e-6 ? `You only hold ${oq.toLocaleString("en",{maximumFractionDigits:4})} ${T}` : "" };
    } else {
      const cost = sh*px + fe, na = (oq*oa + cost)/(oq+sh);
      preview = { lines:[
        [type==="buy" ? "Total cost" : "Reinvested", _pdMoney(cost, 2, sy)],
        ["New avg cost", _pdMoney(na, 2, sy)],
        ["Shares after", (oq+sh).toLocaleString("en",{maximumFractionDigits:4})],
        ...(cashTracked && type==="buy" ? [["Cash after", _pdMoney(cashValue - cost, 2), (cashValue-cost)<0 ? C.red : C.text]] : []),
      ], warn: "" };
    }
  }

  const valid = type === "setcash" ? balance !== "" && !isNaN(parseFloat(balance)) && parseFloat(balance) >= 0
    : isAdjust ? (T && px > 0 && !!pos)
    : isShareTx ? (T && sh > 0 && px >= 0 && price !== "" && !(preview && preview.warn))
    : type === "dividend" ? (T && am > 0) : am > 0;

  const submit = async () => {
    setBusy(true); setErr("");
    try {
      const today = new Date().toISOString().slice(0,10);
      if (type === "setcash") {
        const cbBody = { balance: (margin ? -1 : 1) * Math.abs(parseFloat(balance)) };
        if (date && date !== today) cbBody.ts = `${date}T12:00:00`;
        await apiFetch(`/portfolio/${pid}/cash-balance`, token, {method:"POST", body:JSON.stringify(cbBody)});
      } else if (isEdit) {
        const body = {};
        if (isShareTx) Object.assign(body, {shares:sh, price:px, fees:fe});
        if (isAdjust) body.price = px;
        if (type === "dividend" || type === "deposit" || type === "withdrawal") body.cash_amount = am;
        if (type === "buy" || type === "sell") body.source = followed ? "recommendation" : "manual";
        body.ts = date ? `${date}T12:00:00` : undefined;
        await apiFetch(`/portfolio/${pid}/transactions/${initial.editId}`, token, {method:"PATCH", body:JSON.stringify(body)});
      } else {
        const body = { type };
        if (isShareTx) Object.assign(body, {ticker:T, shares:sh, price:px, fees:fe});
        if (isAdjust) Object.assign(body, {ticker:T, price:px});
        if (type === "dividend") Object.assign(body, {ticker:T, cash_amount:am});
        if (type === "deposit" || type === "withdrawal") body.cash_amount = am;
        if (type === "buy" || type === "sell") body.source = followed ? "recommendation" : "manual";
        if (date && date !== today) body.ts = `${date}T12:00:00`;
        await apiFetch(`/portfolio/${pid}/transactions`, token, {method:"POST", body:JSON.stringify(body)});
      }
      onDone();
    } catch(e) { setErr(e.message || "Could not save transaction"); setBusy(false); }
  };

  const inp = {width:"100%",boxSizing:"border-box",padding:"11px 12px",borderRadius:10,
    background:C.surface,border:`1px solid ${C.border}`,color:C.text,fontSize:16,fontFamily:"inherit",outline:"none"};
  const lab = {fontSize:10,fontWeight:700,letterSpacing:"0.08em",color:C.muted,textTransform:"uppercase",marginBottom:5};

  return (
    <div onClick={onClose} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:9000,
      display:"flex",alignItems:"flex-end",justifyContent:"center"}}>
      <div onClick={e=>e.stopPropagation()} style={{width:"100%",maxWidth:520,maxHeight:"92vh",overflowY:"auto",
        background:C.card,borderRadius:"20px 20px 0 0",border:`1px solid ${C.border}`,
        padding:"16px 16px calc(20px + env(safe-area-inset-bottom))"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
          <div style={{fontSize:16,fontWeight:800,color:C.text}}>
            {isEdit ? "Edit transaction" : type==="setcash" ? "Set cash balance" : isAdjust ? "Edit cost basis" : "Add transaction"}
          </div>
          <button onClick={onClose} style={{background:"none",border:"none",color:C.muted,fontSize:22,cursor:"pointer"}}>×</button>
        </div>

        {isEdit && (
          <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.08em",color:C.muted,textTransform:"uppercase",marginBottom:14}}>
            {type}{T ? ` · ${T}` : ""} — type can't be changed; delete and re-add for that
          </div>
        )}

        {!isEdit && type !== "setcash" && !isAdjust && (
          <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:6,marginBottom:14}}>
            {_TX_TYPES.map(t => (
              <button key={t.id} onClick={()=>{setType(t.id); setErr("");}} style={{
                padding:"9px 4px",borderRadius:10,border:`1px solid ${type===t.id?C.accent:C.border}`,
                background:type===t.id?"rgba(0,212,255,0.14)":"transparent",
                color:type===t.id?C.accent:C.muted,fontWeight:700,fontSize:12,cursor:"pointer",fontFamily:"inherit"}}>{t.label}</button>
            ))}
          </div>
        )}

        {!isEdit && portfolios.length > 1 && (
          <div style={{marginBottom:12}}>
            <div style={lab}>Portfolio</div>
            <select value={pid} onChange={e=>setPid(e.target.value)} style={inp}>
              {portfolios.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
        )}

        {(isShareTx || type==="dividend" || isAdjust) && (
          <div style={{marginBottom:12}}>
            <div style={lab}>Ticker</div>
            <input value={ticker} list="pd-held" onChange={e=>{setTicker(e.target.value.toUpperCase()); priceTouched.current=false;}}
              placeholder="e.g. NVDA" autoCapitalize="characters" style={inp}/>
            <datalist id="pd-held">{held.map(h => <option key={h.ticker} value={h.ticker}/>)}</datalist>
            {type==="sell" && pos && (
              <div style={{fontSize:11,color:C.muted,marginTop:5}}>
                You hold {pos.quantity.toLocaleString("en",{maximumFractionDigits:4})} @ {_pdMoney(pos.avgCost,2,sy)} avg ·{" "}
                <span onClick={()=>setShares(String(pos.quantity))} style={{color:C.accent,cursor:"pointer",fontWeight:700}}>Sell all</span>
              </div>
            )}
          </div>
        )}

        {isAdjust && (
          <div style={{marginBottom:12}}>
            <div style={lab}>Average cost per share</div>
            <input value={price} onChange={e=>{priceTouched.current=true; setPrice(e.target.value);}} inputMode="decimal" placeholder="0.00" style={inp}/>
            <div style={{fontSize:11,color:C.muted,marginTop:5}}>
              {pos ? `Currently ${_pdMoney(pos.avgCost,2,sy)} on ${pos.quantity.toLocaleString("en",{maximumFractionDigits:4})} shares` : "Pick a ticker you hold"}
              {pos && px > 0 && pos.price ? ` · unrealized P&L at ${_pdMoney(pos.price,2,sy)} becomes ${_pdMoney((pos.price - px) * pos.quantity, 0, sy)}` : ""}
              . A cost-basis correction only — no cash moves.</div>
          </div>)}

        {isShareTx && (
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12}}>
            <div><div style={lab}>Shares</div>
              <input value={shares} onChange={e=>setShares(e.target.value)} inputMode="decimal" placeholder="Any quantity" style={inp}/></div>
            <div><div style={lab}>Price</div>
              <input value={price} onChange={e=>{priceTouched.current=true; setPrice(e.target.value);}} inputMode="decimal" placeholder="Live price" style={inp}/></div>
          </div>
        )}

        {isCashTx && (
          <div style={{marginBottom:12}}>
            <div style={lab}>{type==="dividend" ? "Dividend received ($)" : "Amount ($)"}</div>
            <input value={amount} onChange={e=>setAmount(e.target.value)} inputMode="decimal" placeholder="0.00" style={inp}/>
          </div>
        )}

        {type==="setcash" && (
          <div style={{marginBottom:12}}>
            <div style={{display:"flex",gap:6,marginBottom:10}}>
              {[[false,"Cash on hand"],[true,"Margin (I owe)"]].map(([m,l]) => (
                <button key={l} onClick={()=>setMargin(m)} style={{flex:1,padding:"9px 4px",borderRadius:10,fontWeight:700,fontSize:12,
                  cursor:"pointer",fontFamily:"inherit",border:`1px solid ${margin===m?(m?C.red:C.accent):C.border}`,
                  background:margin===m?(m?"rgba(255,82,82,0.12)":"rgba(0,212,255,0.14)"):"transparent",
                  color:margin===m?(m?C.red:C.accent):C.muted}}>{l}</button>))}
            </div>
            <div style={lab}>{margin ? "Margin balance owed ($)" : "Cash balance ($)"}</div>
            <input value={balance} onChange={e=>setBalance(e.target.value.replace(/[^0-9.]/g,""))} inputMode="decimal" placeholder="0.00" style={inp}/>
            <div style={{fontSize:11,color:C.muted,marginTop:5}}>
              {margin ? "Saved as a negative cash balance (borrowed against your holdings). " : ""}
              Recorded as a reconciling deposit/withdrawal so your ledger stays the source of truth.
              Backdate this below if the balance existed before today — e.g. set the date to your
              portfolio's opening entry to correct a pre-existing margin balance in past performance.
            </div>
          </div>
        )}

        {!isAdjust && (
          <div style={{display:"grid",gridTemplateColumns:isShareTx?"1fr 1fr":"1fr",gap:10,marginBottom:12}}>
            {isShareTx && <div><div style={lab}>Fees (optional)</div>
              <input value={fees} onChange={e=>setFees(e.target.value)} inputMode="decimal" placeholder="0" style={inp}/></div>}
            <div><div style={lab}>Date</div>
              <input type="date" value={date} max={new Date().toISOString().slice(0,10)} onChange={e=>setDate(e.target.value)} style={inp}/></div>
          </div>
        )}

        {(type === "buy" || type === "sell") && (
          <label style={{display:"flex",alignItems:"center",gap:8,fontSize:12,color:followed?C.accent:C.muted,marginBottom:12,cursor:"pointer"}}>
            <input type="checkbox" checked={followed} onChange={e=>setFollowed(e.target.checked)} style={{width:18,height:18,accentColor:C.accent}}/>
            This trade follows a GemPulse suggestion
          </label>)}

        {preview && (
          <div style={{background:C.surface,borderRadius:12,padding:"10px 12px",marginBottom:12,border:`1px solid ${C.border}`}}>
            {preview.lines.map(([k,v,col],i) => (
              <div key={i} style={{display:"flex",justifyContent:"space-between",fontSize:13,padding:"3px 0"}}>
                <span style={{color:C.muted}}>{k}</span><span style={{color:col||C.text,fontWeight:700}}>{v}</span>
              </div>
            ))}
            {preview.warn && <div style={{color:C.red,fontSize:12,marginTop:4}}>{preview.warn}</div>}
          </div>
        )}

        {err && <div style={{color:C.red,fontSize:12,marginBottom:10}}>{err}</div>}
        <button onClick={submit} disabled={!valid || busy} style={{
          width:"100%",padding:"14px",borderRadius:12,border:"none",fontWeight:800,fontSize:15,fontFamily:"inherit",
          cursor:(!valid||busy)?"default":"pointer",opacity:(!valid||busy)?0.45:1,
          background:C.accent,color:"#04121a"}}>
          {busy ? "Saving…" : type==="setcash" ? "Save balance" : isAdjust ? "Save cost basis" : `Save ${(_TX_TYPES.find(t=>t.id===type)||{}).label || ""}`}
        </button>
        {type !== "setcash" && (type==="deposit" || type==="withdrawal") && (
          <div onClick={()=>setType("setcash")} style={{textAlign:"center",marginTop:12,fontSize:12,color:C.accent,cursor:"pointer",fontWeight:700}}>
            Set cash balance instead
          </div>
        )}
      </div>
    </div>
  );
}

// ── Embedded briefing (async submit→poll; key takeaways up top, full prose folds in) ──
function PortfolioBriefingCard({ token, scope, horizon }) {
  const [briefing, setBriefing] = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState("");
  const [open,     setOpen]     = useState(false);
  const [fetchedAt,setFetchedAt]= useState(null);
  const reqRef = React.useRef(0);

  const load = async (force=false) => {
    const n = ++reqRef.current;
    setLoading(true); setError("");
    const qs = new URLSearchParams();
    if (force) qs.set("force","true");
    if (scope && scope !== "all") qs.set("portfolio_id", scope);
    if (horizon) qs.set("horizon", horizon);
    const q = qs.toString();
    try {
      const sub = await _aiAgentFetch(() => apiFetch("/ai/portfolio-briefing-async" + (q?"?"+q:""), token, {method:"POST"}), {maxAttempts: 2, delayMs: 1200});
      if (!sub?.request_id) throw new Error("no request_id returned");
      const start = Date.now(); let info = null;
      while (Date.now() - start < 180000) {
        info = await _aiAgentFetch(() => apiFetch(`/ai/portfolio-briefing-async/${sub.request_id}`, token), {maxAttempts: 2, delayMs: 800});
        if (n !== reqRef.current) return;
        if (info?.status === "done" || info?.status === "error") break;
        await new Promise(r => setTimeout(r, 3000));
      }
      if (n !== reqRef.current) return;
      if (!info || info.status !== "done") throw new Error(info?.error || "AI agent temporarily unavailable");
      setBriefing(info); setFetchedAt(new Date());
    } catch(e) {
      if (n === reqRef.current) setError(e.message || "Briefing unavailable — try again");
    }
    if (n === reqRef.current) setLoading(false);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => { setBriefing(null); load(false); }, [scope, horizon]);

  const lines = String(briefing?.briefing || "").split("\n").filter(l => l.trim());
  const ageMin = fetchedAt ? Math.max(0, Math.round((Date.now() - fetchedAt.getTime())/60000)) : null;

  return (
    <Card style={{padding:14}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
        <SLabel>🤖 Your briefing</SLabel>
        <button onClick={()=>load(true)} disabled={loading} title="Refresh" style={{background:"none",border:"none",
          color:C.accent,fontSize:16,cursor:loading?"default":"pointer",opacity:loading?0.4:1,marginTop:-10}}>↻</button>
      </div>
      {loading && !briefing && (
        <div style={{fontSize:12,color:C.muted,padding:"6px 0 4px"}}>Agent working… first open of the day can take up to a minute.</div>
      )}
      {error && !briefing && <div style={{fontSize:12,color:C.red}}>{error}</div>}
      {lines.slice(0, open ? lines.length : 2).map((l,i) => (
        <div key={i} style={{fontSize:13,lineHeight:1.65,color:C.text,marginBottom:6}}>{_pdBold(l)}</div>
      ))}
      {lines.length > 2 && (
        <div onClick={()=>setOpen(o=>!o)} style={{fontSize:12,color:C.accent,fontWeight:700,cursor:"pointer",marginTop:2}}>
          {open ? "Show less ▴" : `Read full briefing (${lines.length - 2} more) ▾`}
        </div>
      )}
      {briefing && (
        <div style={{fontSize:10,color:C.muted,marginTop:8}}>
          {briefing.regimeLabel ? `${briefing.regimeLabel} regime · ` : ""}Updated {ageMin===0?"just now":`${ageMin}m ago`} · tap ↻ for the latest
        </div>
      )}
    </Card>
  );
}

// ── Performance: return breakdown + reconstructed value curve vs "if held unchanged" ──
function PerformanceCard({ token, scope, view, currency, fxRates, sym, refreshKey }) {
  const [days, setDays]   = useState(180);
  const [curve, setCurve] = useState(null);
  const [busy, setBusy]   = useState(true);
  const s = view.snapshot || {};
  const toCad = currency === "cad";
  const uK = toCad ? (fxRates?.USDCAD || 1.36) : 1, cK = toCad ? 1 : (fxRates?.CADUSD || 0.735);

  useEffect(() => {
    let alive = true; setBusy(true);
    const q = new URLSearchParams({days:String(days)});
    if (scope !== "all") q.set("portfolio_id", scope);
    apiFetch("/portfolio/value-curve?" + q.toString(), token)
      .then(d => { if (alive) { setCurve(d); setBusy(false); } })
      .catch(() => { if (alive) { setCurve({points:[]}); setBusy(false); } });
    return () => { alive = false; };
  }, [token, scope, days, refreshKey]);

  const attr = !!curve?.hasAttribution;
  const pts = (curve?.points || []).map(p => ({d:p.d,
    a: p.usd*uK + p.cad*cK + p.cash, f: p.fu_usd*uK + p.fu_cad*cK + p.fu_cash,
    n: (p.nr_usd || 0)*uK + (p.nr_cad || 0)*cK + (p.nr_cash || 0)}));
  const W = 300, H = 96;
  let chart = null, delta = null, attrDelta = null;
  if (pts.length >= 2) {
    const all = pts.flatMap(p => attr ? [p.a, p.f, p.n] : [p.a, p.f]);
    const lo = Math.min(...all), hi = Math.max(...all), span = (hi - lo) || 1;
    const X = (i) => (i/(pts.length-1))*W, Y = (v) => H - 6 - ((v-lo)/span)*(H-12);
    const line = (k) => pts.map((p,i) => `${i?"L":"M"}${X(i).toFixed(1)},${Y(p[k]).toFixed(1)}`).join(" ");
    chart = (
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} preserveAspectRatio="none" style={{display:"block"}}>
        <path d={line("f")} fill="none" stroke="#6b7a99" strokeWidth="1.6" strokeDasharray="4 3" vectorEffect="non-scaling-stroke"/>
        {attr && <path d={line("n")} fill="none" stroke={C.yellow} strokeWidth="1.6" strokeDasharray="1 3" strokeLinecap="round" vectorEffect="non-scaling-stroke"/>}
        <path d={line("a")} fill="none" stroke={C.accent} strokeWidth="2" vectorEffect="non-scaling-stroke"/>
      </svg>);
    const last = pts[pts.length-1];
    delta = last.a - last.f;
    attrDelta = attr ? last.a - last.n : null;
  }
  const tile = (k, v, col) => (
    <div style={{flex:1,minWidth:0}}>
      <div style={{fontSize:10,color:C.muted,letterSpacing:"0.06em",textTransform:"uppercase"}}>{k}</div>
      <div style={{fontSize:14,fontWeight:800,color:col||C.text}}>{v}</div></div>);
  const haveFlows = s.realizedPl != null;
  const totalRet = (s.unrealizedPl || 0) + (s.realizedPl || 0) + (s.dividends || 0);

  return (
    <Card>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
        <SLabel>Performance</SLabel>
        <div style={{display:"flex",gap:4,marginTop:-10}}>
          {[[30,"1M"],[90,"3M"],[180,"6M"],[365,"1Y"]].map(([d,l]) => (
            <button key={d} onClick={()=>setDays(d)} style={{padding:"3px 8px",borderRadius:8,border:"none",fontSize:10,fontWeight:800,
              cursor:"pointer",fontFamily:"inherit",background:days===d?"rgba(0,212,255,0.16)":"transparent",color:days===d?C.accent:C.muted}}>{l}</button>))}
        </div>
      </div>
      <div style={{display:"flex",gap:8,marginBottom:12}}>
        {tile("Unrealized", _pdMoney(s.unrealizedPl,0,sym), _pdColor(s.unrealizedPl))}
        {haveFlows && tile("Realized", _pdMoney(s.realizedPl,0,sym), _pdColor(s.realizedPl))}
        {haveFlows && tile("Dividends", _pdMoney(s.dividends,0,sym), _pdColor(s.dividends))}
        {haveFlows && tile("Total", _pdMoney(totalRet,0,sym), _pdColor(totalRet))}
      </div>
      {busy && !chart && <div style={{fontSize:12,color:C.muted,padding:"14px 0"}}>Building your value curve…</div>}
      {!busy && !chart && (
        <div style={{fontSize:12,color:C.muted,lineHeight:1.5,padding:"6px 0"}}>
          Your value curve builds from your first transaction — opening positions are dated from when you started tracking.
          Log a buy or sell and it fills in.</div>)}
      {chart}
      {chart && (
        <>
          <div style={{display:"flex",gap:14,alignItems:"center",marginTop:8,fontSize:10,color:C.muted}}>
            <span><span style={{display:"inline-block",width:14,height:2,background:C.accent,marginRight:5,verticalAlign:"middle"}}/>Your portfolio</span>
            <span><span style={{display:"inline-block",width:14,height:0,borderTop:"2px dashed #6b7a99",marginRight:5,verticalAlign:"middle"}}/>If held unchanged</span>
            {attr && <span><span style={{display:"inline-block",width:14,height:0,borderTop:`2px dotted ${C.yellow}`,marginRight:5,verticalAlign:"middle"}}/>Without suggested trades</span>}
          </div>
          <div style={{fontSize:12,color:C.text,marginTop:8,lineHeight:1.5}}>
            {Math.abs(delta) < 0.5
              ? "Same as holding your opening positions — no trades since."
              : <>You're <b style={{color:_pdColor(delta)}}>{delta>0?"+":"-"}{_pdMoney(Math.abs(delta),0,sym)}</b> {delta>0?"ahead of":"behind"} simply holding your opening positions
                  <span style={{color:C.muted}}> (includes cash you've added or withdrawn)</span></>}
          </div>
          {curve?.start && (
            <div style={{fontSize:10,color:C.muted,marginTop:4}}>
              Opening basket locked in as of {curve.start} — only deposits/withdrawals/trades dated on or
              before that count toward it; anything dated after is real activity the comparison deliberately excludes.
            </div>)}
          {attr && attrDelta != null && (
            <div style={{fontSize:12,color:C.text,marginTop:8,lineHeight:1.5,padding:"8px 10px",borderRadius:10,background:"rgba(255,202,40,0.08)"}}>
              🤖 Acting on {curve.recCount} GemPulse suggestion{curve.recCount===1?"":"s"}: {Math.abs(attrDelta) < 0.5 ? "no net difference so far"
                : <><b style={{color:_pdColor(attrDelta)}}>{attrDelta>0?"+":"-"}{_pdMoney(Math.abs(attrDelta),0,sym)}</b> {attrDelta>0?"better":"worse"} than not acting on them</>}
              <div style={{fontSize:10,color:C.muted,marginTop:3}}>Based on the trades you've tagged as following a suggestion — reverse just those and compare. Past results don't predict future returns.</div>
            </div>)}
        </>)}
    </Card>
  );
}

// ── Holdings feed ─────────────────────────────────────────────────────────────
function HoldingsFeed({ holdings, onSelectTicker, onAdjust, onCore, onEditCost, sym="$", verdictNote, activistMap={}, insiderMap={} }) {
  const [openAll, setOpenAll] = useState(false);
  const [openSet, setOpenSet] = useState({});
  const [collapsed, setCollapsed] = useState(false);
  const [coreOv, setCoreOv] = useState({});     // optimistic 💎 toggles until the reload lands
  const [sort, setSort] = useState(() => {
    try {
      const mine = localStorage.getItem("gp_pd_sort");
      if (mine) return mine;
      const old = localStorage.getItem("gempulse_optpos_sort");            // preference saved in the old Optimizer tab
      return old === "alpha" ? "az" : (old === "swing" || old === "lt") ? old : "verdict";
    } catch (_) { return "verdict"; }
  });
  const pickSort = (v) => { setSort(v); try { localStorage.setItem("gp_pd_sort", v); } catch (_) {} };
  useEffect(() => { setCoreOv({}); }, [holdings]);
  const ORDER = {EXIT:0, TRIM:1, ADD:2, REVIEW:3, HOLD:4};
  const isCore = (h) => { const k = h.portfolioId + ":" + h.ticker; return k in coreOv ? coreOv[k] : !!h.coreHolding; };
  const cmp = {
    verdict: (a,b) => ((ORDER[a.action] ?? 5) - (ORDER[b.action] ?? 5)) || ((b.marketValue||0) - (a.marketValue||0)),
    swing:   (a,b) => ((b.swingScore ?? -1) - (a.swingScore ?? -1)) || String(a.ticker).localeCompare(String(b.ticker)),
    lt:      (a,b) => ((b.ltScore ?? -1) - (a.ltScore ?? -1)) || String(a.ticker).localeCompare(String(b.ticker)),
    value:   (a,b) => (b.marketValue||0) - (a.marketValue||0),
    az:      (a,b) => String(a.ticker).localeCompare(String(b.ticker)),
  }[sort] || (() => 0);
  const rows = [...holdings].sort(cmp);
  const isOpen = (k) => openAll || !!openSet[k];
  const toggleCore = async (h) => {
    const k = h.portfolioId + ":" + h.ticker, next = !isCore(h);
    setCoreOv(o => ({...o, [k]: next}));
    try { await onCore(h, next); } catch (_) { setCoreOv(o => ({...o, [k]: !next})); }
  };
  const tag = (txt, c, bg) => <span style={{fontSize:9,fontWeight:800,padding:"2px 6px",borderRadius:6,color:c,background:bg}}>{txt}</span>;
  return (
    <Card style={{padding:0,overflow:"visible"}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"12px 14px 6px"}}>
        <div onClick={()=>setCollapsed(c=>!c)} style={{cursor:"pointer"}}>
          <SLabel>Holdings · {holdings.length} {collapsed ? "▸" : "▾"}</SLabel>
        </div>
        {!collapsed && (
          <div onClick={()=>setOpenAll(o=>!o)} style={{fontSize:11,color:C.accent,fontWeight:700,cursor:"pointer",marginTop:-10}}>
            {openAll ? "Collapse all" : "Expand all"}
          </div>
        )}
      </div>
      {!collapsed && (
        <div style={{fontSize:10,color:C.muted,padding:"0 14px 8px",display:"flex",alignItems:"center",gap:4,flexWrap:"wrap"}}>
          Set core holdings using
          <span style={{display:"inline-flex",alignItems:"center",gap:2,background:"rgba(255,202,40,0.13)",border:"1px solid rgba(255,202,40,0.3)",
            borderRadius:6,padding:"1px 5px",fontSize:9,fontWeight:700,color:"#ffca28"}}>💎 Core</span>
          <InfoTip text="Mark a position as a Core Holding to protect it from trim or sell recommendations. The optimizer will keep it at its current weight (or higher) and only suggest adding — never reducing. Ideal for conviction positions you want to hold through volatility." />
        </div>)}
      {!collapsed && verdictNote && (
        <div style={{margin:"0 14px 8px",padding:"8px 10px",borderRadius:10,background:C.yellowBg,fontSize:11,color:C.yellow,lineHeight:1.5}}>
          Recommendations unavailable right now — {verdictNote}</div>)}
      {!collapsed && (
        <div style={{display:"flex",gap:4,padding:"0 14px 10px",flexWrap:"wrap"}}>
          {[["verdict","Verdict"],["swing","Swing"],["lt","LT"],["value","Value"],["az","A–Z"]].map(([k,l]) => (
            <button key={k} onClick={()=>pickSort(k)} style={{padding:"3px 9px",borderRadius:8,border:"none",fontSize:10,fontWeight:800,cursor:"pointer",
              fontFamily:"inherit",background:sort===k?"rgba(0,212,255,0.16)":"rgba(255,255,255,0.04)",color:sort===k?C.accent:C.muted}}>{l}</button>))}
        </div>)}
      {!collapsed && rows.map(h => {
        const k = h.portfolioId + ":" + h.ticker;
        const v = _PD_VERDICT[h.action];
        const core = isCore(h);
        const actionable = ["ADD","TRIM","EXIT"].includes(h.action) && h.targetPct != null;
        return (
          <div key={k} style={{borderTop:`1px solid ${C.divider}`}}>
            <div onClick={()=>setOpenSet(s=>({...s,[k]:!s[k]}))} style={{display:"flex",alignItems:"center",gap:10,padding:"11px 14px",cursor:"pointer"}}>
              <TickerLogo ticker={h.ticker} size={30}/>
              <div style={{flex:1,minWidth:0}}>
                <div style={{display:"flex",alignItems:"center",gap:6,minWidth:0}}>
                  <span style={{fontSize:14,fontWeight:800,color:C.text,flexShrink:0}}>{h.ticker}</span>
                  {h.scoring ? tag("SCORING…", C.muted, "rgba(255,255,255,0.06)") : (v && tag(v.label, v.color, v.bg))}
                  {h.urgency === "high" && tag("URGENT", C.red, C.redBg)}
                  {h.urgency === "medium" && tag("REVIEW", C.yellow, C.yellowBg)}
                  {h.isETF && tag("ETF", C.blue, C.blueBg)}
                  {h.shortName && h.shortName !== h.ticker && (
                    <span style={{fontSize:11,color:C.muted,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",minWidth:0,flex:1}}>{h.shortName}</span>)}
                </div>
                <div style={{fontSize:11,color:C.muted,marginTop:4,display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                  <ScorePair sw={h.swingScore} lt={h.ltScore}/>
                  {h.weightPct!=null && <span>{h.weightPct}%</span>}
                  <button onClick={(e)=>{e.stopPropagation(); toggleCore(h);}}
                    title={core ? "Core holding — trim/exit suggestions ignored" : "Mark as core holding"}
                    style={{background:core?"rgba(255,202,40,0.15)":"none",border:`1px solid ${core?"rgba(255,202,40,0.5)":C.border}`,
                      borderRadius:6,padding:"1px 5px",cursor:"pointer",fontSize:10,lineHeight:1.3,flexShrink:0,fontFamily:"inherit"}}>💎</button>
                  {!h.scoring && h.quality && (
                    <span title={`Setup quality: ${h.quality}${(h.qualityWhy||[]).length ? " — " + h.qualityWhy.join(", ") : ""}`}
                      style={{fontSize:9,fontWeight:800,padding:"2px 6px",borderRadius:6,textTransform:"uppercase",letterSpacing:"0.04em",
                        color:_PD_QUALITY[h.quality]?.c || C.muted, background:_PD_QUALITY[h.quality]?.bg || "rgba(255,255,255,0.06)"}}>
                      {h.quality === "strong" ? "Strong setup" : h.quality === "weak" ? "Weak setup" : "Mixed setup"}</span>)}
                  {insiderMap[(h.ticker||"").toUpperCase()] && <InsiderClusterFlag info={insiderMap[(h.ticker||"").toUpperCase()]}/>}
                  {activistMap[(h.ticker||"").toUpperCase()] && <ActivistFilingFlag info={activistMap[(h.ticker||"").toUpperCase()]}/>}
                  {h.risk
                    ? <span title={h.risk.why} style={{fontSize:9,fontWeight:800,padding:"2px 6px",borderRadius:6,
                        color:(_PD_TONE[h.risk.tone]||_PD_TONE.info).c,background:(_PD_TONE[h.risk.tone]||_PD_TONE.info).bg}}>{h.risk.label}</span>
                    : (h.flags||[]).map(f => <span key={f} title={f} style={{fontSize:11}}>{_PD_FLAG_ICON[f]}</span>)}
                </div>
              </div>
              <div style={{textAlign:"right"}}>
                <div style={{fontSize:13,fontWeight:700,color:C.text}}>{_pdCompact(h.marketValue, sym)}</div>
                <div style={{fontSize:12,fontWeight:700,color:_pdColor(h.changePct)}}>{_pdPct(h.changePct,2)}</div>
              </div>
            </div>
            {isOpen(k) && (
              <div style={{padding:"0 14px 12px 54px"}}>
                {actionable && (
                  <div style={{padding:"9px 11px",borderRadius:10,marginBottom:8,background:(v?v.bg:"transparent"),border:`1px solid ${(v?v.color:C.border)}33`}}>
                    <div style={{fontSize:12,color:C.text}}>Weight <b>{h.optCurrentPct ?? h.weightPct}%</b> → recommended <b style={{color:v?v.color:C.text}}>{h.targetPct}%</b></div>
                    {h.targetValue != null && (
                      <div style={{fontSize:11,color:C.muted,marginTop:3}}>
                        Target {_pdMoney(h.targetValue,0,sym)}{h.deltaShares != null ? ` · ${(h.quantity + h.deltaShares).toLocaleString("en",{maximumFractionDigits:4})} shares` : ""}</div>)}
                    {h.deltaShares != null && h.deltaShares !== 0 && (
                      <div style={{fontSize:12,fontWeight:800,marginTop:4,color:h.deltaShares>0?C.green:C.red}}>
                        {h.deltaShares>0?"Buy":"Sell"} {Math.abs(h.deltaShares).toLocaleString("en")} share{Math.abs(h.deltaShares)===1?"":"s"} (~{_pdMoney(Math.abs(h.deltaValue),0,sym)})</div>)}
                  </div>)}
                {h.trimReason === "sector_overweight" && (
                  <div style={{fontSize:11,color:C.yellow,marginBottom:6}}>⚠ {h.sector} is overweight{h.sectorPct != null ? ` (${h.sectorPct}% of this portfolio)` : ""} — trimming rebalances it</div>)}
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"4px 12px",fontSize:12,color:C.muted}}>
                  <div>Shares <span style={{color:C.text}}>{h.quantity.toLocaleString("en",{maximumFractionDigits:4})}</span></div>
                  <div>Avg cost <span style={{color:C.text}}>{_pdMoney(h.avgCost,2,sym)}</span>
                    {h.price != null && <div style={{marginTop:2}}>Mkt price <span style={{fontWeight:600,
                      color:h.avgCost ? _pdColor(h.price - h.avgCost) : C.text}}>{_pdMoney(h.price,2,sym)}</span></div>}
                  </div>
                  <div>Mkt value <span style={{color:C.text}}>{_pdMoney(h.marketValue,0,sym)}</span></div>
                  <div>P&amp;L <span style={{color:_pdColor(h.plDollar),fontWeight:700}}>{_pdMoney(h.plDollar,0,sym)} ({_pdPct(h.plPct)})</span></div>
                  <div>Horizon <span style={{color:_pdColor(h.horizonReturn)}}>{_pdPct(h.horizonReturn)}</span></div>
                  {!!h.realizedPl && <div>Realized <span style={{color:_pdColor(h.realizedPl),fontWeight:700}}>{_pdMoney(h.realizedPl,0,sym)}</span></div>}
                  {!!h.dividends && <div>Dividends <span style={{color:C.text}}>{_pdMoney(h.dividends,0,sym)}</span></div>}
                  {h.portfolio && <div>In <span style={{color:C.text}}>{h.portfolio}</span></div>}
                </div>
                {!h.scoring && h.quality && (
                  <div style={{fontSize:12,lineHeight:1.55,marginTop:8,color:_PD_QUALITY[h.quality]?.c || C.muted}}>
                    <b>Setup quality — {h.quality}:</b>{" "}
                    <span style={{color:C.muted}}>{(h.qualityWhy||[]).length
                      ? h.qualityWhy.join(" · ")
                      : "not enough technical/timing data yet — rated on the score alone"}</span></div>)}
                {h.risk && <div style={{fontSize:12,lineHeight:1.55,marginTop:8,color:(_PD_TONE[h.risk.tone]||_PD_TONE.info).c}}>
                  <b>{h.risk.label}:</b> <span style={{color:C.muted}}>{h.risk.why}</span></div>}
                {h.rationale && <div style={{fontSize:12,color:C.muted,lineHeight:1.55,marginTop:8}}>{h.rationale}</div>}
                <div style={{display:"flex",gap:8,marginTop:10,flexWrap:"wrap"}}>
                  <button onClick={()=>onAdjust(h)} style={{padding:"8px 14px",borderRadius:10,border:`1px solid ${C.accent}`,
                    background:"rgba(0,212,255,0.12)",color:C.accent,fontWeight:800,fontSize:12,cursor:"pointer",fontFamily:"inherit"}}>Adjust ▸</button>
                  <button onClick={()=>onEditCost(h)} style={{padding:"8px 14px",borderRadius:10,border:`1px solid ${C.border}`,
                    background:"transparent",color:C.muted,fontWeight:700,fontSize:12,cursor:"pointer",fontFamily:"inherit"}}>Edit cost</button>
                  <button onClick={()=>onSelectTicker && onSelectTicker(h.ticker)} style={{padding:"8px 14px",borderRadius:10,border:`1px solid ${C.border}`,
                    background:"transparent",color:C.muted,fontWeight:700,fontSize:12,cursor:"pointer",fontFamily:"inherit"}}>Details</button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </Card>
  );
}

// ── Tailored picks (async submit→poll): gem picks tagged held / sector-gap / heavy-sector ──
function useTailoredPicks(token, scope, refreshKey, horizon) {
  const [st, setSt] = useState({data:null, loading:true, error:""});
  useEffect(() => {
    let alive = true;
    setSt(prev => ({...prev, loading:true, error:""}));
    (async () => {
      try {
        const qp = new URLSearchParams();
        if (scope && scope !== "all") qp.set("portfolio_id", scope);
        if (horizon) qp.set("horizon", horizon);
        const q = qp.toString() ? "?" + qp.toString() : "";
        const sub = await _aiAgentFetch(() => apiFetch("/ai/tailored-picks-async" + q, token, {method:"POST"}), {maxAttempts: 2, delayMs: 1200});
        if (!sub?.request_id) throw new Error("no request_id");
        const start = Date.now(); let info = null;
        while (Date.now() - start < 120000) {
          info = await _aiAgentFetch(() => apiFetch(`/ai/tailored-picks-async/${sub.request_id}`, token), {maxAttempts: 2, delayMs: 800});
          if (!alive) return;
          if (info?.status === "done" || info?.status === "error") break;
          await new Promise(r => setTimeout(r, 2000));
        }
        if (!alive) return;
        if (info?.status !== "done") throw new Error(info?.error || "picks unavailable");
        setSt({data:info, loading:false, error:""});
      } catch(e) { if (alive) setSt({data:null, loading:false, error:e.message || "picks unavailable"}); }
    })();
    return () => { alive = false; };
  }, [token, scope, refreshKey, horizon]);
  return st;
}

const _pdNs = (t) => _pdIsCad(t) ? "CA$" : "$";   // ledger/plan figures are in each security's native currency

function CashCard({ dash, sym, onTx, token, onReload, horizon, onSelectTicker }) {
  const cash = dash.cash || {}, plans = dash.marginPlans || [], g = dash.cashGuidance;
  const [rateDraft, setRateDraft] = useState({});
  const [busy, setBusy] = useState(false);
  const saveRate = async (pid) => {
    setBusy(true);
    try {
      const v = parseFloat(rateDraft[pid]);
      await apiFetch(`/portfolio/${pid}/margin-rate`, token, {method:"PUT", body:JSON.stringify({rate: isNaN(v) ? null : v})});
      setRateDraft(d => { const n = {...d}; delete n[pid]; return n; });
      onReload();
    } catch(e) { /* surfaced by the unchanged value on reload */ }
    setBusy(false);
  };
  const postureTone = {hold:C.yellow, deploy:C.green, balanced:C.blue, invested:C.muted};

  return (
    <Card style={cash.negative ? {border:`1px solid ${C.red}66`} : {}}>
      <SLabel>{cash.negative ? "Margin balance" : "Cash"}</SLabel>
      {cash.tracked ? (
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <div style={{minWidth:0}}>
            <div style={{fontSize:22,fontWeight:900,color:cash.negative?C.red:C.text}}>{_pdMoney(cash.value,0,sym)}</div>
            <div style={{fontSize:11,color:C.muted}}>{cash.pct!=null ? `${cash.pct}% of portfolio` : ""}{cash.negative ? " · borrowed against your holdings" : ""}</div>
          </div>
          <div style={{display:"flex",gap:6}}>
            <button onClick={()=>onTx({type:"deposit"})} style={_pdMiniBtn}>＋ Deposit</button>
            <button onClick={()=>onTx({type:"setcash"})} style={_pdMiniBtn}>Set</button>
          </div>
        </div>
      ) : (
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10}}>
          <div style={{fontSize:12,color:C.muted,lineHeight:1.5}}>Track your uninvested cash — or a margin balance — to see it in your allocation and get cash-aware guidance.</div>
          <button onClick={()=>onTx({type:"setcash"})} style={_pdMiniBtn}>Set cash</button>
        </div>
      )}

      {/* ── Clear margin ───────────────────────────────────────────────────────── */}
      {plans.map(m => {
        const pl = m.plan || {};
        return (
          <div key={m.portfolioId} style={{marginTop:14,paddingTop:12,borderTop:`1px solid ${C.divider}`}}>
            {plans.length > 1 && <div style={{fontSize:11,color:C.muted,marginBottom:6}}>{m.name}</div>}
            <div style={{display:"flex",alignItems:"flex-end",justifyContent:"space-between",gap:10,flexWrap:"wrap"}}>
              <div>
                <div style={{fontSize:10,color:C.muted,letterSpacing:"0.06em",textTransform:"uppercase"}}>Estimated interest</div>
                <div style={{fontSize:13,fontWeight:800,color:C.text}}>
                  {m.interestAnnual != null ? `${_pdMoney(m.interestDaily,2,sym)}/day · ${_pdMoney(m.interestAnnual,0,sym)}/yr` : "Enter your rate →"}</div>
              </div>
              <div style={{display:"flex",alignItems:"center",gap:6}}>
                <input value={rateDraft[m.portfolioId] ?? (m.ratePct ?? "")} placeholder="APR %" inputMode="decimal"
                  onChange={e=>setRateDraft(d=>({...d,[m.portfolioId]:e.target.value.replace(/[^0-9.]/g,"")}))}
                  style={{width:64,padding:"7px 8px",borderRadius:8,background:C.surface,border:`1px solid ${C.border}`,color:C.text,fontSize:16,fontFamily:"inherit"}}/>
                <button onClick={()=>saveRate(m.portfolioId)} disabled={busy || rateDraft[m.portfolioId]===undefined} style={{..._pdMiniBtn,opacity:(busy||rateDraft[m.portfolioId]===undefined)?0.4:1}}>Save</button>
              </div>
            </div>
            {m.equityPct != null && (
              <div style={{fontSize:11,color:C.muted,marginTop:8}}>Equity cushion <b style={{color:m.equityPct<25?C.red:m.equityPct<35?C.yellow:C.text}}>{m.equityPct}%</b> of holdings</div>)}
            {m.caution && <div style={{fontSize:12,color:C.yellow,lineHeight:1.5,marginTop:6,padding:"8px 10px",borderRadius:10,background:C.yellowBg}}>{m.caution}</div>}

            <div style={{fontSize:12,fontWeight:800,color:C.text,margin:"14px 0 6px"}}>Clear margin — a way to raise {_pdMoney(pl.target,0,sym)}</div>
            {(pl.sells || []).length === 0 && <div style={{fontSize:12,color:C.muted}}>No eligible holdings to sell — add cash to reduce the balance.</div>}
            {(pl.sells || []).map(sl => (
              <div key={sl.ticker + sl.portfolioId} style={{display:"flex",alignItems:"center",gap:10,padding:"9px 0",borderTop:`1px solid ${C.divider}`}}>
                <div onClick={()=>onSelectTicker && onSelectTicker(sl.ticker)} style={{cursor:"pointer",display:"flex"}}><TickerLogo ticker={sl.ticker} size={28}/></div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:13,fontWeight:800,color:C.text}}>
                    {sl.partial ? "Trim" : "Sell"} {sl.shares.toLocaleString("en",{maximumFractionDigits:4})} {sl.ticker}
                    <span style={{color:C.muted,fontWeight:600}}> @ {_pdMoney(sl.price,2,_pdNs(sl.ticker))}</span>
                    {sl.tier === "last_resort" && <span title="Core holding — last resort" style={{marginLeft:6}}>💎</span>}
                  </div>
                  <div style={{fontSize:11,color:C.muted,lineHeight:1.4}}>{sl.reason}</div>
                </div>
                <div style={{textAlign:"right"}}>
                  <div style={{fontSize:13,fontWeight:800,color:C.green}}>+{_pdMoney(sl.proceeds,0,_pdNs(sl.ticker))}</div>
                  {sl.realizedPl != null && <div style={{fontSize:10,color:_pdColor(sl.realizedPl)}}>{_pdMoney(sl.realizedPl,0,_pdNs(sl.ticker))} P&amp;L</div>}
                  <div onClick={()=>onTx({type:"sell", ticker:sl.ticker, pid:sl.portfolioId, shares:sl.shares, source:"recommendation"})}
                    style={{fontSize:11,color:C.accent,fontWeight:800,cursor:"pointer",marginTop:2}}>Log sale ▸</div>
                </div>
              </div>))}
            {(pl.sells || []).length > 0 && (
              <div style={{fontSize:12,marginTop:8,color:pl.clears ? C.green : C.yellow,fontWeight:700}}>
                {pl.clears ? `Raises ≈ ${_pdMoney(pl.raised,0,sym)} — clears your ${_pdMoney(m.balance,0,sym)} margin balance`
                           : `Raises ≈ ${_pdMoney(pl.raised,0,sym)} — still ${_pdMoney(pl.shortfall,0,sym)} short; add cash to close the gap`}
                {pl.usesCore && " (includes a core holding as a last resort)"}
              </div>)}
            <div style={{fontSize:10,color:C.muted,lineHeight:1.5,marginTop:8}}>
              Ranked by lowest conviction, risk flags and overweight; core (💎) holdings only as a last resort and strong ADDs are never listed.
              Average-cost basis, no tax-lot optimization — informational, not advice or tax guidance.</div>
          </div>);
      })}

      {/* ── Cash-aware guidance: posture + hold / top up existing ─────────────────────────── */}
      {g && (
        <div style={{marginTop:14,paddingTop:12,borderTop:`1px solid ${C.divider}`}}>
          <div style={{display:"inline-block",fontSize:10,fontWeight:800,padding:"3px 9px",borderRadius:8,marginBottom:6,
            color:postureTone[g.posture]||C.muted,background:(postureTone[g.posture]||C.muted)+"22"}}>{g.label}</div>
          <div style={{fontSize:12,color:C.muted,lineHeight:1.55,marginBottom:10}}>{g.text}</div>
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            <div style={{padding:"10px 12px",borderRadius:12,background:g.posture==="hold"?C.yellowBg:C.surface,border:`1px solid ${C.border}`}}>
              <div style={{fontSize:12,fontWeight:800,color:C.text}}>⏸ Hold &amp; wait{g.posture==="hold" && <span style={{color:C.yellow,fontSize:10,marginLeft:6}}>SIGNAL-FAVORED</span>}</div>
              <div style={{fontSize:11,color:C.muted,marginTop:2}}>Preserve cash for a better entry — no action needed.</div>
            </div>
            <div style={{padding:"10px 12px",borderRadius:12,background:C.surface,border:`1px solid ${C.border}`}}>
              <div style={{fontSize:12,fontWeight:800,color:C.text}}>➕ Top up existing</div>
              {(g.add || []).length === 0 && <div style={{fontSize:11,color:C.muted,marginTop:2}}>None of your holdings carry an ADD verdict right now.</div>}
              {(g.add || []).map(a => (
                <div key={a.ticker+a.portfolioId} style={{display:"flex",alignItems:"center",gap:8,marginTop:6}}>
                  <div onClick={()=>onSelectTicker && onSelectTicker(a.ticker)} style={{display:"flex",alignItems:"center",gap:8,flex:1,minWidth:0,cursor:"pointer"}}>
                  <TickerLogo ticker={a.ticker} size={26}/>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:12,fontWeight:800,color:C.text,display:"flex",alignItems:"center",gap:8}}>{a.ticker} <span style={{color:C.muted,fontWeight:600,fontSize:11}}><ScorePair sw={a.swingScore} lt={a.ltScore} size={10}/></span></div>
                    <div style={{fontSize:11,color:C.muted}}>
                      {a.weightPct!=null && a.targetPct!=null ? `Weight ${a.weightPct}% → ${a.targetPct}%` : "ADD verdict"}
                      {a.suggestDollars ? ` · ≈ ${_pdMoney(a.suggestDollars,0,_pdNs(a.ticker))}` : ""}</div></div>
                  </div>
                  <button onClick={()=>onTx({type:"buy", ticker:a.ticker, pid:a.portfolioId, shares:a.suggestShares||undefined, source:"recommendation"})} style={_pdMiniBtn}>Buy ▸</button>
                </div>))}
            </div>
          </div>
          <div style={{fontSize:10,color:C.muted,marginTop:8}}>Informational — the product's read on conditions, not advice.</div>
        </div>)}

      {/* ── Deploy capital: cash + capital freed by TRIM/EXIT, after the profile's reserve ───── */}
      {(dash.deploy || []).map(d => <DeployCapital key={d.portfolioId} d={d} multi={(dash.deploy || []).length > 1}
        token={token} sym={sym} onTx={onTx} horizon={horizon} onSelectTicker={onSelectTicker}/>)}
    </Card>
  );
}

function DeployCapital({ d, multi, token, sym, onTx, horizon, onSelectTicker }) {
  const [extra, setExtra] = useState("");
  const [over, setOver] = useState(null);
  useEffect(() => {
    const v = parseFloat(extra);
    if (!(v > 0)) { setOver(null); return; }
    let alive = true;
    const t = setTimeout(() => {
      apiFetch(`/portfolio/${d.portfolioId}/deploy?extra=${encodeURIComponent(v)}${horizon ? `&horizon=${horizon}` : ""}`, token)
        .then(r => { if (alive && r && r.portfolioId) setOver(r); }).catch(() => {});
    }, 500);
    return () => { alive = false; clearTimeout(t); };
  }, [extra, d.portfolioId, token, horizon]);
  const x = over || d;
  const pct = Math.round((x.reservePct || 0) * 100);
  const box = (label, val, col, bg) => (
    <div style={{flex:1,minWidth:88,background:bg,borderRadius:8,padding:"6px 10px",border:`1px solid ${col}33`}}>
      <div style={{fontSize:9,color:col,marginBottom:2}}>{label}</div>
      <div style={{fontSize:13,fontWeight:700,color:col,fontFamily:"monospace"}}>{_pdMoney(val,0,sym)}</div></div>);
  if (!(x.totalDeployable > 0) && !(x.deficit > 0) && !parseFloat(extra) && !(x.builderPicks || []).length) return null;
  return (
    <div style={{marginTop:14,paddingTop:12,borderTop:`1px solid ${C.divider}`}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
        <div style={{fontSize:12,fontWeight:800,color:C.accent,letterSpacing:"0.04em",textTransform:"uppercase"}}>💎 Deploy capital{multi ? ` · ${d.name}` : ""}</div>
        {x.totalDeployable > 0 && <div style={{fontSize:18,fontWeight:800,color:C.green,fontFamily:"monospace"}}>{_pdMoney(x.totalDeployable,0,sym)}</div>}
      </div>
      {x.needsExtraCash && x.deficit > 0 && (
        <div style={{fontSize:11,color:C.yellow,lineHeight:1.5,padding:"8px 10px",borderRadius:8,marginBottom:8,background:C.yellowBg}}>
          ⚠ The ADD suggestions need <b>{_pdMoney(x.deficit,0,sym)}</b> more than your trims/exits free up. Add cash or skip some ADDs.</div>)}
      <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:8}}>
        {x.freed > 0 && box("Net freed (after ADDs)", x.freed, "#ff7043", "rgba(255,112,67,0.08)")}
        {(x.cash > 0 || x.extraCash > 0) && box("Cash", (x.cash || 0) + (x.extraCash || 0), C.green, "rgba(0,230,118,0.08)")}
        {x.totalDeployable > 0 && box(`Keep as reserve (${pct}%)`, x.reserveAmount, C.muted, "rgba(255,255,255,0.04)")}
      </div>
      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
        <span style={{fontSize:11,color:C.muted,whiteSpace:"nowrap"}}>What if I add</span>
        <input value={extra} onChange={e=>setExtra(e.target.value.replace(/[^0-9.]/g,""))} inputMode="decimal" placeholder="$0"
          style={{width:96,padding:"7px 9px",borderRadius:8,background:C.surface,border:`1px solid ${C.border}`,color:C.text,fontSize:16,fontFamily:"inherit"}}/>
        <span style={{fontSize:11,color:C.muted}}>in new cash?</span>
      </div>
      {(x.builderPicks || []).length > 0 && (
        <>
          <div style={{fontSize:10,color:"rgba(255,255,255,0.35)",lineHeight:1.5,marginBottom:6}}>
            New positions ranked by combined Swing + Long-term score; amounts split evenly after the {pct}% reserve.</div>
          {x.builderPicks.map((p,i) => (
            <div key={p.ticker} style={{display:"flex",alignItems:"center",gap:10,padding:"9px 0",borderTop:`1px solid ${C.divider}`}}>
              <div style={{width:20,height:20,borderRadius:"50%",flexShrink:0,background:"rgba(0,212,255,0.1)",border:"1px solid rgba(0,212,255,0.3)",
                display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:800,color:C.accent}}>{i+1}</div>
              <div onClick={()=>onSelectTicker && onSelectTicker(p.ticker)} style={{display:"flex",alignItems:"center",gap:10,flex:1,minWidth:0,cursor:"pointer"}}>
                <TickerLogo ticker={p.ticker} size={26}/>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                    <span style={{fontSize:13,fontWeight:800,color:C.text}}>{p.ticker}</span>
                    <SignalPill type={p.swingType} size="sm"/></div>
                  <div style={{fontSize:10,color:C.muted,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{p.shortName} · {p.sectorNorm}</div>
                </div>
              </div>
              <div style={{textAlign:"right"}}>
                {p.perPick ? <div style={{fontSize:12,fontWeight:700,color:C.green,fontFamily:"monospace"}}>~{_pdMoney(p.perPick,0,sym)}</div> : null}
                <div style={{fontSize:11,fontWeight:700,color:C.accent,fontFamily:"monospace"}}>{Math.round(p.combinedScore || 0)}/100</div>
              </div>
              <button onClick={()=>onTx({type:"buy", ticker:p.ticker, pid:d.portfolioId, source:"recommendation"})} style={_pdMiniBtn}>Buy ▸</button>
            </div>))}
        </>)}
    </div>
  );
}

function TailoredPicksCard({ tailored, onSelectTicker, onTx }) {
  const [lens, setLens] = useState("best");
  const d = tailored?.data;
  const best = (d?.bestOverall || d?.toExplore || []);
  const divs = (d?.diversifiers || []);
  const list = lens === "best" ? best : divs;
  const row = (r) => (
    <div key={r.ticker} style={{display:"flex",alignItems:"center",gap:10,padding:"9px 0",borderTop:`1px solid ${C.divider}`}}>
      <div onClick={()=>onSelectTicker && onSelectTicker(r.ticker)} style={{display:"flex",alignItems:"center",gap:10,flex:1,minWidth:0,cursor:"pointer"}}>
        <TickerLogo ticker={r.ticker} size={30}/>
        <div style={{minWidth:0}}>
          <div style={{fontSize:13,fontWeight:800,color:C.text}}>{r.ticker}
            {r.held && <span style={{color:C.green,marginLeft:6,fontSize:11}}>✓ held</span>}
            {r.gapFiller && <span style={{marginLeft:6,fontSize:9,fontWeight:800,padding:"2px 6px",borderRadius:6,color:C.green,background:C.greenBg}}>FILLS A GAP</span>}
            {r.heavySector && <span style={{marginLeft:6,fontSize:9,fontWeight:800,padding:"2px 6px",borderRadius:6,color:C.yellow,background:C.yellowBg}}>HEAVY SECTOR</span>}
          </div>
          <div style={{fontSize:11,color:C.muted,display:"flex",alignItems:"center",gap:8,minWidth:0}}>
            <ScorePair sw={r.swingScore} lt={r.ltScore} size={10}/>
            {r.note && <span style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",minWidth:0}}>· {r.note}</span>}</div>
        </div>
      </div>
      {!r.held && <button onClick={()=>onTx({type:"buy", ticker:r.ticker, source:"recommendation"})} style={_pdMiniBtn}>Buy ▸</button>}
    </div>);
  return (
    <Card>
      <SLabel>💎 Discover for you</SLabel>
      <div style={{display:"flex",gap:6,marginBottom:6}}>
        {[["best","Best scores"],["div","Best for diversification"]].map(([k,l]) => (
          <button key={k} onClick={()=>setLens(k)} style={{flex:1,padding:"8px 4px",borderRadius:10,fontSize:11,fontWeight:800,cursor:"pointer",fontFamily:"inherit",
            border:`1px solid ${lens===k?C.accent:C.border}`,background:lens===k?"rgba(0,212,255,0.14)":"transparent",color:lens===k?C.accent:C.muted}}>{l}</button>))}
      </div>
      <div style={{fontSize:10,color:C.muted,lineHeight:1.5,margin:"0 0 2px"}}>
        {lens === "best" ? "Highest-quality picks in the market right now, whatever the sector."
                         : "Best-scoring picks from sectors under 10% of your holdings — balance over pure return."}</div>
      {tailored?.loading && !d && <div style={{fontSize:12,color:C.muted,padding:"8px 0"}}>Matching today's top picks to your portfolio…</div>}
      {tailored?.error && !d && <div style={{fontSize:12,color:C.red,padding:"8px 0"}}>{tailored.error}</div>}
      {d && list.length === 0 && <div style={{fontSize:12,color:C.muted,padding:"8px 0"}}>{lens === "div" ? "No under-represented sector has a standout pick right now." : "No picks available right now."}</div>}
      {d && list.map(row)}
      {d && (d.youHold || []).length > 0 && (
        <div style={{marginTop:10,paddingTop:8,borderTop:`1px solid ${C.divider}`}}>
          <div style={{fontSize:10,fontWeight:800,color:C.muted,letterSpacing:"0.08em",marginBottom:2}}>TOP PICKS YOU ALREADY HOLD</div>
          {d.youHold.map(row)}
        </div>)}
      <div style={{fontSize:10,color:C.muted,marginTop:8,lineHeight:1.5}}>Tagged against what you already own. Informational, not advice.</div>
    </Card>
  );
}

function RotationCard({ tailored, onTx, onSelectTicker }) {
  const rots = tailored?.data?.rotations || [];
  if (!tailored?.data || rots.length === 0) return null;
  const timingTone = {"GOOD ENTRY":C.green, "WAIT":C.yellow, "EXTENDED":C.red};
  return (
    <Card>
      <SLabel>🔁 Rotate for better setups</SLabel>
      <div style={{fontSize:11,color:C.muted,lineHeight:1.5,marginBottom:10}}>
        These are flat — a materially higher-conviction setup exists for about the same money.
      </div>
      <div style={{display:"flex",flexDirection:"column",gap:12}}>
        {rots.map(r => {
          const sellSym = _pdNs(r.sell.ticker), buySym = _pdNs(r.buy.ticker);
          const timing = r.buy.timing;
          return (
            <div key={r.outTicker+r.inTicker} style={{padding:"11px 12px",borderRadius:12,background:C.surface,border:`1px solid ${C.border}`}}>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <div onClick={()=>onSelectTicker && onSelectTicker(r.sell.ticker)} style={{display:"flex",alignItems:"center",gap:8,flex:1,minWidth:0,cursor:"pointer"}}>
                  <TickerLogo ticker={r.sell.ticker} size={28}/>
                  <div style={{minWidth:0}}>
                    <div style={{fontSize:13,fontWeight:800,color:C.text}}>{r.sell.ticker}</div>
                    <div style={{fontSize:10,color:C.muted}}>Sell {r.sell.shares.toLocaleString("en",{maximumFractionDigits:4})} · {_pdMoney(r.sell.proceeds,0,sellSym)}
                      {r.sell.realizedPl != null && <span style={{color:_pdColor(r.sell.realizedPl)}}> ({_pdMoney(r.sell.realizedPl,0,sellSym)} P&amp;L)</span>}</div>
                  </div>
                </div>
                <span style={{fontSize:16,color:C.accent,flexShrink:0}}>→</span>
                <div onClick={()=>onSelectTicker && onSelectTicker(r.buy.ticker)} style={{display:"flex",alignItems:"center",gap:8,flex:1,minWidth:0,cursor:"pointer"}}>
                  <TickerLogo ticker={r.buy.ticker} size={28}/>
                  <div style={{minWidth:0}}>
                    <div style={{fontSize:13,fontWeight:800,color:C.text}}>{r.buy.ticker}
                      {r.buy.shortName && r.buy.shortName !== r.buy.ticker &&
                        <span style={{fontSize:10,color:C.muted,fontWeight:600}}> · {r.buy.shortName}</span>}</div>
                    <div style={{fontSize:10,color:C.muted}}>Buy {r.buy.shares.toLocaleString("en",{maximumFractionDigits:4})} · {_pdMoney(r.buy.cost,0,buySym)}</div>
                  </div>
                </div>
              </div>
              <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap",marginTop:8}}>
                <ScorePair sw={r.buy.swingScore} lt={r.buy.ltScore} size={10}/>
                <span style={{fontSize:10,color:C.green,fontWeight:700}}>+{r.gap} conviction</span>
                {timing && <span style={{fontSize:9,fontWeight:800,padding:"2px 6px",borderRadius:6,
                  color:timingTone[timing.label]||C.muted, background:(timingTone[timing.label]||C.muted)+"22"}}>{timing.label}</span>}
              </div>
              <div style={{fontSize:10,color:C.muted,marginTop:4}}>
                Swing {r.swingDelta>0?"+":""}{r.swingDelta} · LT {r.ltDelta>0?"+":""}{r.ltDelta}
                {timing?.label === "EXTENDED" ? " · Extended today — a pullback would be a cleaner entry." :
                 timing?.label === "GOOD ENTRY" ? " · Good entry right now." : ""}
              </div>
              <div style={{display:"flex",gap:8,marginTop:9}}>
                <button onClick={()=>onTx({type:"sell", ticker:r.sell.ticker, pid:r.sell.portfolioId, shares:r.sell.shares, source:"recommendation"})}
                  style={{..._pdMiniBtn, borderColor:C.red+"55", background:C.redBg, color:C.red}}>Log sell</button>
                <button onClick={()=>onTx({type:"buy", ticker:r.buy.ticker, pid:r.sell.portfolioId, shares:r.buy.shares, source:"recommendation"})}
                  style={_pdMiniBtn}>Log buy</button>
              </div>
            </div>
          );
        })}
      </div>
      <div style={{fontSize:10,color:C.muted,marginTop:10,lineHeight:1.5}}>
        Same value, higher conviction — not a rebalance. Realized P&amp;L is locked in on the sale; timing is a
        rules-based entry read, not a prediction. Informational, not advice or tax guidance.
      </div>
    </Card>
  );
}

// ── Concentration: rule-of-thumb caps drive flags; S&P weights are context only ──
function ConcentrationCard({ conc, rotation }) {
  const [open, setOpen] = useState(false);
  if (!conc) return null;
  const flags = conc.flags || [];
  const sectors = (conc.sectors || []).slice(0, open ? 12 : 5);
  return (
    <Card>
      <SLabel>Concentration</SLabel>
      {flags.length === 0 ? (
        <div style={{fontSize:12,color:C.green,fontWeight:700,marginBottom:10}}>
          ✓ Within your caps — sector ≤ {conc.caps?.sector}%, single position ≤ {conc.caps?.position}%</div>
      ) : (
        <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:12}}>
          {flags.map((f,i) => { const t = _PD_TONE[f.severity === "high" ? "risk" : "watch"];
            return (
              <div key={i} style={{display:"flex",alignItems:"center",gap:8,padding:"8px 10px",borderRadius:10,background:t.bg}}>
                <span style={{color:t.c,fontSize:13}}>{f.severity === "high" ? "⚠️" : "•"}</span>
                <span style={{fontSize:12,color:C.text,flex:1}}>{f.label}</span>
                {f.cap != null && <span style={{fontSize:10,color:C.muted}}>cap {f.cap}%</span>}
              </div>); })}
        </div>)}
      {sectors.map(sc => (
        <div key={sc.sector} style={{marginBottom:8}}>
          <div style={{display:"flex",justifyContent:"space-between",fontSize:11,marginBottom:3}}>
            <span style={{color:C.text}}>{sc.sector}</span>
            <span style={{color:C.muted}}><b style={{color:C.text}}>{sc.pct}%</b>{sc.benchmark != null ? ` · S&P ~${sc.benchmark}%` : ""}</span>
          </div>
          <div style={{position:"relative",height:6,borderRadius:3,background:"rgba(255,255,255,0.06)"}}>
            <div style={{width:`${Math.min(100,sc.pct)}%`,height:"100%",borderRadius:3,
              background: sc.pct > (conc.caps?.sector || 100) ? C.red : C.accent}}/>
            {sc.benchmark != null && <div title={`S&P ~${sc.benchmark}%`} style={{position:"absolute",top:-2,left:`${Math.min(100,sc.benchmark)}%`,
              width:2,height:10,background:"#9aa7c2",opacity:0.8}}/>}
          </div>
        </div>))}
      {(conc.sectors || []).length > 5 && (
        <div onClick={()=>setOpen(o=>!o)} style={{fontSize:11,color:C.accent,fontWeight:700,cursor:"pointer"}}>
          {open ? "Show fewer ▴" : `Show all ${(conc.sectors||[]).length} sectors ▾`}</div>)}
      <div style={{fontSize:10,color:C.muted,marginTop:8,lineHeight:1.5}}>
        Top 3 holdings = {conc.top3Pct}% of your portfolio. Flags use your caps, not the S&amp;P — the index is itself tech-heavy.
        {rotation && rotation.label && rotation.label !== "Low" ? ` Rotation risk is ${rotation.label.toLowerCase()} right now.` : ""}
        {" "}Informational only, not advice.
      </div>
    </Card>
  );
}

// ── Recent ledger activity (with delete — mistakes must be fixable) ───────────
function ActivityCard({ token, portfolios, pid, refreshKey, onChanged, onTx }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(null);
  const [err,  setErr]  = useState("");
  const ids = pid === "all" ? portfolios.map(p => p.id) : [pid];
  useEffect(() => {
    if (!open) return;
    let alive = true;
    Promise.all(ids.map(id => apiFetch(`/portfolio/${id}/transactions?limit=25`, token).catch(() => ({transactions:[]}))))
      .then(rs => { if (alive) setRows(rs.flatMap(r => r.transactions || [])
        .filter(t => t.source !== "migration")
        .sort((a,b) => String(b.ts).localeCompare(String(a.ts))).slice(0,25)); });
    return () => { alive = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, refreshKey, pid]);
  const retag = async (t) => {
    setErr("");
    try { await apiFetch(`/portfolio/${t.portfolio_id}/transactions/${t.id}`, token,
      {method:"PATCH", body:JSON.stringify({source: t.source === "recommendation" ? "manual" : "recommendation"})}); onChanged(); }
    catch(e) { setErr(e.message); }
  };
  const del = async (t) => {
    setErr("");
    try { await apiFetch(`/portfolio/${t.portfolio_id}/transactions/${t.id}`, token, {method:"DELETE"}); onChanged(); }
    catch(e) { setErr(e.message); }
  };
  const edit = (t) => onTx && onTx({
    type: t.type, ticker: t.ticker, pid: t.portfolio_id, editId: t.id,
    shares: t.shares, price: t.price, fees: t.fees, cash_amount: t.cash_amount,
    ratio: t.ratio, ts: t.ts, source: t.source,
  });
  const ns = (t) => _pdIsCad(t.ticker) ? "CA$" : "$";     // native currency of the security
  const desc = (t) => t.type==="deposit"||t.type==="withdrawal" ? `${t.type==="deposit"?"Deposit":"Withdrawal"} ${_pdMoney(t.cash_amount,2)}`
    : t.type==="dividend" ? `Dividend ${t.ticker} ${_pdMoney(t.cash_amount,2,ns(t))}`
    : t.type==="split" ? `Split ${t.ticker} ${t.ratio}:1`
    : t.type==="adjust" ? `Cost basis ${t.ticker} → ${_pdMoney(t.price,2,ns(t))}`
    : `${t.type.toUpperCase()} ${Number(t.shares).toLocaleString("en",{maximumFractionDigits:4})} ${t.ticker} @ ${_pdMoney(t.price,2,ns(t))}`;
  return (
    <Card style={{padding:14}}>
      <div onClick={()=>setOpen(o=>!o)} style={{cursor:"pointer"}}><SLabel>Activity {open ? "▾" : "▸"}</SLabel></div>
      {open && !rows && <div style={{fontSize:12,color:C.muted}}>Loading…</div>}
      {open && rows && rows.length===0 && <div style={{fontSize:12,color:C.muted}}>No transactions yet.</div>}
      {open && rows && rows.map(t => (
        <div key={t.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"7px 0",borderTop:`1px solid ${C.divider}`}}>
          <div><div style={{fontSize:12,color:C.text}}>{desc(t)}</div>
            <div style={{fontSize:10,color:C.muted}}>{String(t.ts).slice(0,10)}{t.source==="blob_sync"?" · optimizer edit":""}
              {t.source==="recommendation" && <span style={{color:C.accent}}> · 🤖 followed a suggestion</span>}
              {["buy","sell"].includes(t.type) && ["manual","recommendation"].includes(t.source) && (
                <span onClick={()=>retag(t)} style={{color:C.accent,cursor:"pointer",fontWeight:700}}>
                  {t.source==="recommendation" ? " · untag" : " · tag as suggestion"}</span>)}
            </div></div>
          <div style={{display:"flex",gap:4}}>
            {["manual","recommendation"].includes(t.source) && (
              <button onClick={()=>edit(t)} title="Edit" style={{background:"none",border:"none",color:C.muted,fontSize:15,cursor:"pointer"}}>✎</button>)}
            <button onClick={()=>del(t)} title="Delete" style={{background:"none",border:"none",color:C.muted,fontSize:16,cursor:"pointer"}}>🗑</button>
          </div>
        </div>
      ))}
      {err && <div style={{fontSize:11,color:C.red,marginTop:6}}>{err}</div>}
    </Card>
  );
}

// ── Today's actions: ALL of them, swipeable, with dots ────────────────────────────────────────────────
function ActionSlide({ a, pos, total, holdings, onTx, onSelectTicker, sym }) {
  const tone = a.kind==="risk" ? C.red : a.kind==="opportunity" ? C.green : C.accent;
  const held = a.ticker && holdings.find(h => h.ticker === a.ticker && (!a.portfolioId || h.portfolioId === a.portfolioId));
  const label = a.kind==="risk" ? "⚠️ Today's action" : a.kind==="opportunity" ? "🎯 Today's action" : "✅ Today";
  return (
    <Card style={{border:`1px solid ${tone}55`,background:`linear-gradient(135deg, ${tone}14, ${C.card} 60%)`,height:"100%",boxSizing:"border-box"}}>
      <SLabel>{label}{total > 1 ? ` · ${pos} of ${total}` : ""}</SLabel>
      <div style={{display:"flex",alignItems:"center",gap:10}}>
        {a.ticker && <div onClick={()=>onSelectTicker && onSelectTicker(a.ticker)} style={{cursor:"pointer",display:"flex"}}><TickerLogo ticker={a.ticker} size={38}/></div>}
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontSize:18,fontWeight:900,color:C.text}}>{a.headline}
            {a.riskLabel && <span style={{marginLeft:8,fontSize:9,fontWeight:800,padding:"2px 6px",borderRadius:6,color:C.red,background:C.redBg,verticalAlign:"middle"}}>{a.riskLabel}</span>}
            {a.urgency === "high" && <span style={{marginLeft:8,fontSize:9,fontWeight:800,padding:"2px 6px",borderRadius:6,color:C.red,background:C.redBg,verticalAlign:"middle"}}>URGENT</span>}</div>
          {a.shortName && a.shortName !== a.ticker && <div style={{fontSize:11,color:C.muted,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{a.shortName}</div>}
          {a.changePct!=null && <div style={{fontSize:12,fontWeight:700,color:_pdColor(a.changePct)}}>{_pdPct(a.changePct,2)} today</div>}
        </div>
        {a.swingScore!=null && <ScoreDonut score={Math.round(a.swingScore)} size={44}/>}
      </div>
      {a.detail && <div style={{fontSize:12,color:C.muted,lineHeight:1.55,marginTop:8}}>{a.detail}</div>}
      {a.weightPct!=null && a.targetPct!=null && (
        <div style={{fontSize:12,color:C.text,marginTop:6}}>Weight <b>{a.weightPct}%</b> → recommended <b>{a.targetPct}%</b></div>)}
      {a.deltaShares != null && a.deltaShares !== 0 && a.deltaValue != null && (
        <div style={{fontSize:12,fontWeight:800,marginTop:4,color:a.deltaShares>0?C.green:C.red}}>
          {a.deltaShares>0?"Buy":"Sell"} {Math.abs(a.deltaShares).toLocaleString("en")} share{Math.abs(a.deltaShares)===1?"":"s"} (~{_pdMoney(Math.abs(a.deltaValue),0,sym)})</div>)}
      {a.ticker && (
        <div style={{display:"flex",gap:8,marginTop:10}}>
          {held && <button onClick={()=>onTx({type: a.action==="ADD"?"buy":"sell", ticker:a.ticker, pid:held.portfolioId, source:a.action ? "recommendation" : undefined})} style={{
            padding:"9px 16px",borderRadius:10,border:"none",background:tone,color:"#04121a",fontWeight:800,fontSize:12,cursor:"pointer",fontFamily:"inherit"}}>Adjust ▸</button>}
          <button onClick={()=>onSelectTicker && onSelectTicker(a.ticker)} style={{padding:"9px 16px",borderRadius:10,
            border:`1px solid ${C.border}`,background:"transparent",color:C.muted,fontWeight:700,fontSize:12,cursor:"pointer",fontFamily:"inherit"}}>Details</button>
        </div>)}
    </Card>
  );
}

function TodaysActions({ actions, holdings, onTx, onSelectTicker, sym }) {
  const [idx, setIdx] = useState(0);
  const ref = React.useRef(null);
  const n = actions.length;
  const onScroll = () => { const el = ref.current; if (!el || !el.clientWidth) return; const i = Math.round(el.scrollLeft / (el.clientWidth + 12)); if (i !== idx) setIdx(i); };
  const go = (i) => { const el = ref.current; if (el) el.scrollTo({left: i * (el.clientWidth + 12), behavior: "smooth"}); };
  useEffect(() => { if (idx >= n) { setIdx(0); if (ref.current) ref.current.scrollLeft = 0; } }, [n, idx]);
  return (
    <div>
      <style>{".gp-snap::-webkit-scrollbar{display:none}"}</style>
      <div ref={ref} onScroll={onScroll} className="gp-snap" style={{display:"flex",gap:12,overflowX:n>1?"auto":"hidden",scrollSnapType:"x mandatory",
        scrollbarWidth:"none",WebkitOverflowScrolling:"touch"}}>
        {actions.map((a,i) => (
          <div key={i} style={{flex:"0 0 100%",minWidth:0,scrollSnapAlign:"start"}}>
            <ActionSlide a={a} pos={i+1} total={n} holdings={holdings} onTx={onTx} onSelectTicker={onSelectTicker} sym={sym}/>
          </div>))}
      </div>
      {n > 1 && (
        <div style={{display:"flex",justifyContent:"center",alignItems:"center",gap:8,marginTop:8}}>
          {actions.map((a,i) => (
            <span key={i} onClick={()=>go(i)} title={a.headline}
              style={{width:i===idx?18:7,height:7,borderRadius:4,cursor:"pointer",transition:"all .2s",
                background:i===idx ? (a.kind==="risk"?C.red:a.kind==="opportunity"?C.green:C.accent) : "rgba(255,255,255,0.18)"}}/>))}
        </div>)}
    </div>
  );
}

// ── Dashboard (populated) ─────────────────────────────────────────────────────
function PortfolioDashboard({ dash, token, scope, onSelectTicker, onBuild, onTx, universeData, sym="$", currency, fxRates, actKey, onReload, onCore, horizon, visible=true }) {
  const s = dash.snapshot || {}, hero = dash.hero || {};
  const tailored = useTailoredPicks(token, scope, actKey, horizon);
  const activistMap = useActivistFilings((dash.holdings || []).map(h => h.ticker), token);   // v8.4.418 — 13D chip
  const insiderMap  = useInsiderClusters((dash.holdings || []).map(h => h.ticker), token);   // v8.4.420 — insider buy-cluster chip
  const secs = Object.entries(s.sectorBreakdown || {});
  const pills = s.actionSummary ? [
    ["EXIT", s.actionSummary.exit, C.red], ["TRIM", s.actionSummary.trim, C.yellow],
    ["ADD", s.actionSummary.add, C.green], ["HOLD", s.actionSummary.hold, C.blue]].filter(p => p[1] > 0) : [];
  const stat = (k, v, col) => (
    <div><div style={{fontSize:10,color:C.muted,letterSpacing:"0.06em",textTransform:"uppercase"}}>{k}</div>
      <div style={{fontSize:14,fontWeight:800,color:col||C.text}}>{v}</div></div>);
  return (
    <div style={{display:"flex",flexDirection:"column",gap:12}}>
      <FeatureTour tourId="portfolio_dashboard" version={2} steps={PORTFOLIO_DASHBOARD_STEPS} active={visible}/>
      {/* 1 · Today's actions (all of them; swipe) */}
      <div data-tour="pd-actions">
      <TodaysActions actions={(dash.actions && dash.actions.length) ? dash.actions : [hero]} holdings={dash.holdings || []}
        onTx={onTx} onSelectTicker={onSelectTicker} sym={sym}/>
      </div>

      {/* 2 · Snapshot */}
      <div data-tour="pd-snapshot"><Card>
        <SLabel>Snapshot</SLabel>
        <div style={{display:"flex",gap:14,alignItems:"center"}}>
          <PortfolioDonut segments={s.donut || []} total={s.totalValue} sym={sym}/>
          <div style={{flex:1,display:"grid",gridTemplateColumns:"1fr 1fr",gap:"10px 8px"}}>
            {stat("Today", `${_pdPct(s.dayChangePct,2)}`, _pdColor(s.dayChangePct))}
            {stat("Today " + sym.replace(/^US|^CA/, ""), _pdMoney(s.dayChangeDollar,0,sym), _pdColor(s.dayChangeDollar))}
            {stat("Unrealized", _pdPct(s.unrealizedPlPct), _pdColor(s.unrealizedPl))}
            {stat("Beta", s.avgBeta!=null ? s.avgBeta.toFixed(2) : "—")}
          </div>
        </div>
        <div style={{display:"flex",flexWrap:"wrap",gap:"6px 12px",marginTop:12}}>
          {(s.donut||[]).map((g,i) => (
            <div key={g.label+i} style={{display:"flex",alignItems:"center",gap:5,fontSize:11,color:C.muted}}>
              <span style={{width:8,height:8,borderRadius:2,background:g.isCash?"#3b4a63":g.label==="Other"?"#6b7a99":_PD_PALETTE[i%_PD_PALETTE.length]}}/>
              {g.label} <span style={{color:C.text,fontWeight:700}}>{g.pct}%</span>
            </div>))}
        </div>
        {dash.fxNote && <div style={{fontSize:10,color:C.muted,marginTop:10}}>{dash.fxNote}</div>}
        <div style={{display:"flex",alignItems:"center",gap:14,marginTop:14,paddingTop:12,borderTop:`1px solid ${C.divider}`}}>
          <ScoreDonut score={s.healthScore} size={52} label={s.healthApprox ? "Health ≈" : "Health"}/>
          <div style={{flex:1}}>
            <div style={{fontSize:12,color:C.muted}}>{s.positionCount} position{s.positionCount===1?"":"s"} · {dash.regime?.label || "—"} regime</div>
            {(dash.unscored || []).length > 0 && (
              <div style={{fontSize:10,color:C.yellow,marginTop:4,lineHeight:1.4}}>
                Still scoring {dash.unscored.join(", ")} — health {s.healthApprox ? "is approximate until" : "and verdicts appear once"} they're scored.</div>)}
            <div style={{display:"flex",gap:6,marginTop:6,flexWrap:"wrap"}}>
              {pills.map(([l,n,c]) => <span key={l} style={{fontSize:10,fontWeight:800,padding:"3px 8px",borderRadius:8,color:c,background:c+"1f"}}>{n} {l}</span>)}
            </div>
          </div>
        </div>
        {secs.length > 0 && (
          <div style={{marginTop:14}}>
            <div style={{display:"flex",height:8,borderRadius:4,overflow:"hidden",background:"rgba(255,255,255,0.05)"}}>
              {secs.map(([k,v],i) => <div key={k} title={`${k} ${v}%`} style={{width:`${v}%`,background:_PD_PALETTE[i%_PD_PALETTE.length]}}/>)}
            </div>
            <div style={{display:"flex",flexWrap:"wrap",gap:"4px 12px",marginTop:8}}>
              {secs.slice(0,6).map(([k,v],i) => <span key={k} style={{fontSize:10,color:C.muted}}>
                <span style={{display:"inline-block",width:7,height:7,borderRadius:2,marginRight:4,background:_PD_PALETTE[i%_PD_PALETTE.length]}}/>{k} {v}%</span>)}
            </div>
          </div>)}
      </Card></div>

      {/* 2b · Performance (P&L breakdown + value curve) */}
      <div data-tour="pd-performance"><PerformanceCard token={token} scope={scope} view={dash} currency={currency} fxRates={fxRates} sym={sym} refreshKey={actKey}/></div>

      {/* 3 · Briefing (async) */}
      <div data-tour="pd-briefing"><PortfolioBriefingCard token={token} scope={scope} horizon={horizon}/></div>

      {/* 4 · Holdings */}
      <div data-tour="pd-holdings">
      <HoldingsFeed holdings={dash.holdings || []} onSelectTicker={onSelectTicker} sym={sym} onCore={onCore} verdictNote={dash.optimizerError} activistMap={activistMap} insiderMap={insiderMap}
        onEditCost={(h) => onTx({type:"adjust", ticker:h.ticker, pid:h.portfolioId})}
        onAdjust={(h) => onTx({type:(h.action==="EXIT"||h.action==="TRIM") ? "sell" : "buy", ticker:h.ticker, pid:h.portfolioId,
          source:["EXIT","TRIM","ADD"].includes(h.action) ? "recommendation" : undefined})}/>
      </div>

      {/* 5 · Cash (+ Clear margin / cash-aware guidance) */}
      <div data-tour="pd-rotation"><RotationCard tailored={tailored} onTx={onTx} onSelectTicker={onSelectTicker}/></div>

      <div data-tour="pd-cash"><CashCard dash={dash} sym={sym} onTx={onTx} token={token} onReload={onReload} horizon={horizon} onSelectTicker={onSelectTicker}/></div>

      {/* 5a · Gem picks tailored to this portfolio (async) */}
      <div data-tour="pd-discover"><TailoredPicksCard tailored={tailored} onSelectTicker={onSelectTicker} onTx={onTx}/></div>

      {/* 5b · Concentration */}
      <div data-tour="pd-concentration"><ConcentrationCard conc={dash.concentration} rotation={dash.rotation}/></div>

      {/* 6 · Log a transaction + Build entry */}
      <div style={{display:"flex",gap:10}}>
        <button onClick={()=>onTx({type:"buy"})} style={{..._pdMiniBtn,flex:1,padding:"13px"}}>＋ Add transaction</button>
        <button onClick={onBuild} style={{..._pdMiniBtn,flex:1,padding:"13px"}}>🛠 Build a new portfolio</button>
      </div>
    </div>
  );
}
const _pdMiniBtn = {whiteSpace:"nowrap",flexShrink:0,padding:"8px 12px",borderRadius:10,border:`1px solid rgba(0,212,255,0.45)`,background:"rgba(0,212,255,0.1)",
  color:C.accent,fontWeight:800,fontSize:12,cursor:"pointer",fontFamily:"inherit"};

// ── Manage portfolios: create / rename / delete + margin APR (replaces the old Optimizer tab's chrome) ──
function ManagePortfoliosSheet({ token, portfolios, onClose, onChanged, onDeleted }) {
  const [names, setNames] = useState({});
  const [rates, setRates] = useState({});
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const run = async (key, fn) => {
    setBusy(key); setErr("");
    try { await fn(); await onChanged(); } catch (e) { setErr(e.message || "Something went wrong"); }
    setBusy("");
  };
  const inp = {padding:"9px 10px",borderRadius:10,background:C.surface,border:`1px solid ${C.border}`,color:C.text,fontSize:16,fontFamily:"inherit",outline:"none",boxSizing:"border-box"};
  return (
    <div onClick={onClose} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:9000,display:"flex",alignItems:"flex-end",justifyContent:"center"}}>
      <div onClick={e=>e.stopPropagation()} style={{width:"100%",maxWidth:520,maxHeight:"92vh",overflowY:"auto",background:C.card,borderRadius:"20px 20px 0 0",
        border:`1px solid ${C.border}`,padding:"16px 16px calc(20px + env(safe-area-inset-bottom))"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
          <div style={{fontSize:16,fontWeight:800,color:C.text}}>Manage portfolios</div>
          <button onClick={onClose} style={{background:"none",border:"none",color:C.muted,fontSize:22,cursor:"pointer"}}>×</button>
        </div>
        {portfolios.map(p => {
          const nm = names[p.id] ?? p.name, rt = rates[p.id] ?? (p.marginRate ?? "");
          return (
            <div key={p.id} style={{padding:"12px",borderRadius:12,background:C.surface,border:`1px solid ${C.border}`,marginBottom:10}}>
              <div style={{display:"flex",gap:8}}>
                <input value={nm} onChange={e=>setNames(n=>({...n,[p.id]:e.target.value}))} maxLength={40} style={{...inp,flex:1,minWidth:0}}/>
                <button disabled={busy==="n"+p.id || !nm.trim() || nm.trim()===p.name} onClick={()=>run("n"+p.id, () => apiFetch(`/portfolio/${p.id}`, token, {method:"PATCH", body:JSON.stringify({name:nm})}))}
                  style={{..._pdMiniBtn,opacity:(!nm.trim()||nm.trim()===p.name)?0.4:1}}>Rename</button>
              </div>
              <div style={{display:"flex",gap:8,alignItems:"center",marginTop:8}}>
                <span style={{fontSize:11,color:C.muted,whiteSpace:"nowrap"}}>Margin APR</span>
                <input value={rt} onChange={e=>setRates(r=>({...r,[p.id]:e.target.value.replace(/[^0-9.]/g,"")}))} inputMode="decimal" placeholder="%" style={{...inp,width:80}}/>
                <button disabled={busy==="r"+p.id || String(rt)===String(p.marginRate ?? "")} onClick={()=>run("r"+p.id, () => apiFetch(`/portfolio/${p.id}/margin-rate`, token,
                  {method:"PUT", body:JSON.stringify({rate: rt === "" ? null : parseFloat(rt)})}))}
                  style={{..._pdMiniBtn,opacity:String(rt)===String(p.marginRate ?? "")?0.4:1}}>Save</button>
                <div style={{flex:1}}/>
                <button onClick={()=>{ if (window.confirm(`Delete "${p.name}" and its full transaction history? This can't be undone.`)) run("d"+p.id, async () => { await apiFetch(`/portfolio/${p.id}`, token, {method:"DELETE"}); onDeleted(p.id); }); }}
                  style={{padding:"8px 10px",borderRadius:10,border:`1px solid ${C.red}55`,background:"transparent",color:C.red,fontWeight:800,fontSize:12,cursor:"pointer",fontFamily:"inherit"}}>Delete</button>
              </div>
            </div>);
        })}
        <div style={{display:"flex",gap:8,marginTop:4}}>
          <input value={newName} onChange={e=>setNewName(e.target.value)} maxLength={40} placeholder="New portfolio name" style={{...inp,flex:1,minWidth:0}}/>
          <button disabled={busy==="new" || !newName.trim()} onClick={()=>run("new", async () => { await apiFetch("/portfolio/new", token, {method:"POST", body:JSON.stringify({name:newName})}); setNewName(""); })}
            style={{..._pdMiniBtn,opacity:newName.trim()?1:0.4}}>＋ Create</button>
        </div>
        {err && <div style={{color:C.red,fontSize:12,marginTop:10}}>{err}</div>}
      </div>
    </div>
  );
}

// ── Home: state machine + [My Portfolio | Build] + one portfolio selector ─────
function PortfolioHome(props) {
  const { user, token, onSelectTicker, globalHorizon, universeData, currency, fxRates } = props;
  const [seg,   setSeg]   = useState(null);          // null until first dashboard load decides
  const [scope, setScope] = useState("all");
  const [dash,  setDash]  = useState(null);
  const [err,   setErr]   = useState("");
  const [loading, setLoading] = useState(true);
  const [sheet, setSheet] = useState(null);          // {type, ticker, pid}
  const [manage, setManage] = useState(false);
  const [slow, setSlow] = useState(false);
  const [actKey, setActKey] = useState(0);
  const reqRef = React.useRef(0);

  const load = React.useCallback(async (force=false) => {
    const n = ++reqRef.current;
    setLoading(true);
    const qs = new URLSearchParams();
    if (scope !== "all") qs.set("portfolio_id", scope);
    if (globalHorizon) qs.set("horizon", globalHorizon);
    if (force) qs.set("force", "true");
    try {
      // First request after opening the app can hit a transient failure (iOS PWA waking its connection, or a
      // slow cold server build that the server still finishes + caches). Retry silently instead of making the
      // user tap Retry; the second attempt is usually an instant cache hit.
      const url = "/portfolio/dashboard" + (qs.toString() ? "?"+qs.toString() : "");
      const d = await _aiAgentFetch(() => apiFetch(url, token), {maxAttempts: 3, delayMs: 1500});
      if (n !== reqRef.current) return;
      setDash(d); setErr("");
      setSeg(prev => prev || (d.hasHoldings ? "mine" : "build"));
    } catch(e) {
      if (n === reqRef.current) { setErr(e.message || "Couldn't load your portfolio"); setSeg(prev => prev || "mine"); }
    }
    if (n === reqRef.current) setLoading(false);
  }, [token, scope, globalHorizon]);

  useEffect(() => { load(false); }, [load]);
  // Warm cache is cheap — keep the numbers fresh while the market is open and the tab is showing.
  useEffect(() => {
    if (seg !== "mine" || !dash?.marketOpen) return;
    const id = setInterval(() => load(false), 60000);     // v8.4.443: server cache is 90s while open (was 30 min) — a minute keeps holdings fresh
    return () => clearInterval(id);
  }, [seg, dash?.marketOpen, load]);

  useEffect(() => {
    if (!(loading && !dash)) { setSlow(false); return; }
    const t = setTimeout(() => setSlow(true), 6000);
    return () => clearTimeout(t);
  }, [loading, dash]);

  const goSeg = (next) => {
    setSeg(next);
    if (next === "mine") { load(true); setTimeout(() => load(true), 2200); } // optimizer auto-save is debounced 1.5s
  };
  const refreshAfterTx = () => { setSheet(null); setActKey(k => k+1); load(true); };

  const portfolios = dash?.portfolios || [];
  const defaultPid = (sheet?.pid) || (scope !== "all" ? scope : (portfolios[0]?.id || `p_${Date.now()}`));
  const populated = !!dash?.hasHoldings;
  const view = React.useMemo(() => dash ? _pdRebase(dash, currency, fxRates) : null, [dash, currency, fxRates]);

  return (
    <div style={{padding:"0 16px 100px"}}>
      <FrozenHeader padTop={16} padBottom={12}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,paddingRight:38,marginBottom:12}}>
          <div style={{display:"flex",alignItems:"center",gap:10,minWidth:0}}>
            <AppLogo size={32}/>
            <div style={{minWidth:0}}>
              <div style={{fontSize:20,fontWeight:900,color:C.text,letterSpacing:"-0.02em",lineHeight:1}}>Portfolio</div>
              <div style={{fontSize:10,color:C.muted,marginTop:2,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
                {seg==="build" ? "Research & build a new portfolio"
                  : populated ? `${dash.snapshot?.positionCount ?? 0} position${dash.snapshot?.positionCount===1?"":"s"} · ${scope==="all" ? "all portfolios" : (portfolios.find(p=>p.id===scope)?.name || "portfolio")}`
                  : "Add holdings to get started"}
              </div>
            </div>
          </div>
          {seg==="mine" && (
            <button onClick={()=>setManage(true)} title="Manage portfolios" style={{background:"none",border:`1px solid ${C.border}`,borderRadius:10,
              color:C.muted,fontSize:13,padding:"5px 9px",cursor:"pointer",fontFamily:"inherit",flexShrink:0}}>⚙</button>)}
          {seg==="mine" && portfolios.length > 1 && (
            <select value={scope} onChange={e=>setScope(e.target.value)} style={{background:C.surface,color:C.text,
              border:`1px solid ${C.border}`,borderRadius:10,padding:"6px 8px",fontSize:12,fontFamily:"inherit",maxWidth:132,minWidth:0}}>
              <option value="all">All portfolios</option>
              {portfolios.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>)}
        </div>
        {/* v8.4.445 — freshness line on its own row above the tabs (it used to sit inside the title block and squeeze the portfolio picker) */}
        {seg === "mine" && populated && dash?.freshness && (
          <div style={{marginBottom:6,marginTop:-4}}><PortfolioFreshness fr={dash.freshness} generatedAt={dash.generatedAt}/></div>)}
        <div style={{display:"flex",background:C.surface,borderRadius:12,padding:4,border:`1px solid ${C.border}`}}>
          {[{id:"mine",label:"My Portfolio"},{id:"build",label:"Build"}].map(t => (
            <button key={t.id} onClick={()=>goSeg(t.id)} style={{flex:1,padding:"8px 4px",borderRadius:9,border:"none",cursor:"pointer",
              fontFamily:"inherit",fontSize:12,fontWeight:700,
              background:seg===t.id?"rgba(0,212,255,0.15)":"transparent",color:seg===t.id?C.accent:"rgba(255,255,255,0.4)",
              borderBottom:seg===t.id?`2px solid ${C.accent}`:"2px solid transparent"}}>{t.label}</button>))}
        </div>
      </FrozenHeader>
      <div style={{height:14}}/>

      {seg === "build" && <PortfolioBuilder user={user} token={token} watchlist={props.watchlist} setWatchlist={props.setWatchlist}
        onSelectTicker={onSelectTicker} globalHorizon={globalHorizon} onNavigate={props.onNavigate} tradePref={props.tradePref} embedded visible={props.visible}/>}

      {(seg === "mine" || seg === null) && (      /* null = first load in flight: show the loading state, not a blank screen */
        <div>
          {loading && !dash && (
            <div style={{textAlign:"center",color:C.muted,fontSize:13,padding:"40px 0",lineHeight:1.6}}>
              Loading your portfolio…
              {slow && <div style={{fontSize:11,marginTop:6}}>The first load after opening can take a few seconds — hang tight.</div>}
            </div>)}
          {err && !dash && (
            <Card><div style={{color:C.red,fontSize:13,marginBottom:10}}>{err}</div>
              <button onClick={()=>load(true)} style={_pdMiniBtn}>Retry</button></Card>)}
          {dash && !populated && (
            <div style={{display:"flex",flexDirection:"column",gap:12}}>
              <FeatureTour tourId="portfolio_empty" version={1} steps={PORTFOLIO_EMPTY_STEPS} active={props.visible}/>
              <Card style={{textAlign:"center",padding:"28px 18px"}}>
                <div style={{fontSize:32,marginBottom:8}}>💼</div>
                <div style={{fontSize:17,fontWeight:900,color:C.text,marginBottom:6}}>Add holdings to unlock your dashboard</div>
                <div style={{fontSize:12,color:C.muted,lineHeight:1.55,marginBottom:16}}>
                  See your value, health, daily action and an AI briefing — all tied to your real positions.</div>
                <div style={{display:"flex",gap:10,justifyContent:"center",flexWrap:"wrap"}}>
                  <button data-tour="pd-empty-build" onClick={()=>goSeg("build")} style={{..._pdMiniBtn,padding:"12px 16px"}}>🛠 Build a portfolio</button>
                  <button data-tour="pd-empty-manual" onClick={()=>setSheet({type:"buy"})} style={{..._pdMiniBtn,padding:"12px 16px"}}>＋ Add holdings manually</button>
                </div>
              </Card>
              <div data-tour="pd-empty-picks"><GemPicksCard token={token} horizon={globalHorizon} onSelectTicker={onSelectTicker} currency={currency} fxRates={fxRates}/></div>
            </div>)}
          {dash && populated && (
            <>
              <PortfolioDashboard dash={view} horizon={globalHorizon} sym={_pdSym(currency)} currency={currency} fxRates={fxRates} actKey={actKey} token={token} scope={scope} onSelectTicker={onSelectTicker} visible={props.visible}
                onBuild={()=>goSeg("build")} onTx={(s)=>setSheet(s)} universeData={universeData} onReload={()=>load(true)}
                onCore={async (h, core) => { await apiFetch(`/portfolio/${h.portfolioId}/holdings/${encodeURIComponent(h.ticker)}/core`, token,
                  {method:"PUT", body:JSON.stringify({core})}); await load(true); }}/>
              <div style={{height:12}}/>
              <ActivityCard token={token} portfolios={portfolios} pid={scope} refreshKey={actKey} onChanged={refreshAfterTx} onTx={(s)=>setSheet(s)}/>
            </>)}
        </div>
      )}

      {manage && (
        <ManagePortfoliosSheet token={token} portfolios={portfolios} onClose={()=>setManage(false)}
          onChanged={() => load(true)} onDeleted={(id) => { if (scope === id) setScope("all"); }}/>)}

      {sheet && (
        <TransactionSheet token={token} portfolios={portfolios.length ? portfolios : [{id:defaultPid,name:"My Portfolio"}]}
          pid={defaultPid} holdings={dash?.holdings || []} cashTracked={!!dash?.cash?.tracked} cashValue={dash?.cash?.value ?? 0}
          initial={sheet} universeData={universeData} onClose={()=>setSheet(null)} onDone={refreshAfterTx}/>)}
    </div>
  );
}

// ── Entry: flag-gated. Backend flag off → legacy 3-tab screen, unchanged. ─────
function PortfolioScreen(props) {
  const [enabled, setEnabled] = useState(_ledgerEnabledCache);
  useEffect(() => {
    if (_ledgerVerified) return;
    let alive = true;
    (async () => {
      for (let i = 0; i < 4 && alive; i++) {
        try {
          const d = await apiFetch("/portfolio/ledger/status", props.token);
          _ledgerEnabledCache = !!d?.enabled; _ledgerVerified = true;
          try { localStorage.setItem(_LEDGER_LS, _ledgerEnabledCache ? "1" : "0"); } catch (_) {}
          if (alive) setEnabled(_ledgerEnabledCache);
          return;
        } catch (e) {
          if (e?.name === "AuthError") return;
          await new Promise(r => setTimeout(r, 1500 * (i + 1)));
        }
      }
      // every attempt failed: keep the last known state; only if we never knew do we fall back to legacy
      if (alive && _ledgerEnabledCache === null) setEnabled(false);
    })();
    return () => { alive = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (enabled === null) return <div style={{textAlign:"center",color:C.muted,fontSize:13,padding:"60px 0"}}>Loading…</div>;
  return enabled ? <PortfolioHome {...props}/> : <LegacyPortfolioScreen {...props}/>;
}

function LegacyPortfolioScreen({ user, token, watchlist, setWatchlist, onSelectTicker, globalHorizon, onNavigate, scanData, universeData, upsertUniverse, tradePref, currency, fxRates, embedded=false }) {
  const [tab, setTab] = useState("builder"); // "builder" | "optimizer" | "briefing"

  const tabBar = (
    <>
      {/* Tab switcher */}
      <div style={{display:"flex",gap:0,
        background:C.surface,borderRadius:12,padding:4,
        border:`1px solid ${C.border}`}}>
        {[
          {id:"builder",  label:"Builder",  icon:<LTIcon size={13} color="currentColor"/>},
          {id:"optimizer",label:"Optimizer",icon:<SwingIcon size={13} color="currentColor"/>},
          {id:"briefing", label:"AI Briefing", icon:<span style={{fontSize:13}}>🤖</span>},
        ].filter(t => !(embedded && t.id === "briefing")).map(t=>(
          <button key={t.id} onClick={()=>setTab(t.id)} style={{
            flex:1,padding:"8px 4px",borderRadius:9,border:"none",
            cursor:"pointer",fontFamily:"inherit",fontSize:12,fontWeight:700,
            display:"flex",alignItems:"center",justifyContent:"center",gap:6,
            background:tab===t.id?"rgba(0,212,255,0.15)":"transparent",
            color:tab===t.id?C.accent:"rgba(255,255,255,0.4)",
            borderBottom:tab===t.id?`2px solid ${C.accent}`:"2px solid transparent",
            transition:"all 0.2s",
          }}>
            {t.icon}{t.label}
          </button>
        ))}
      </div>
    </>
  );

  return (
    <div style={{padding: embedded ? 0 : "0 0 100px"}}>
      {embedded ? <div style={{padding:"0 16px",marginBottom:14}}>{tabBar}</div> : (
        <>
      <FrozenHeader bleed={0} padTop={16} padBottom={12}>
      {/* Title */}
      <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:12}}>
        <AppLogo size={32}/>
        <div style={{fontSize:20,fontWeight:900,color:C.text,letterSpacing:"-0.02em",lineHeight:1}}>
          Portfolio
        </div>
      </div>
{tabBar}
      </FrozenHeader>
      <div style={{height:14}}/>
        </>
      )}

      {tab === "builder"  && <PortfolioBuilder  user={user} token={token} watchlist={watchlist} setWatchlist={setWatchlist} onSelectTicker={onSelectTicker} globalHorizon={globalHorizon} onNavigate={onNavigate} tradePref={tradePref}/>}
      {tab === "optimizer"&& <PortfolioOptimizer user={user} token={token} onSelectTicker={onSelectTicker} globalHorizon={globalHorizon} scanData={scanData} universeData={universeData} upsertUniverse={upsertUniverse} tradePref={tradePref} currency={currency} fxRates={fxRates}/>}
      {/* v8.4.152: keep AI Briefing mounted (just hidden) instead of mount/unmount
          on tab switch. The agent call can take 10-30s on a cold backend —
          mounting it from the moment the Portfolio screen opens lets it start
          (and retry) in the background, so by the time the user taps the
          "AI Briefing" tab it has often already resolved instead of showing
          the first-try failure. */}
      {!embedded && (
      <div style={{display: tab === "briefing" ? "block" : "none"}}>
        <PortfolioBriefing user={user} token={token} setTab={setTab}/>
      </div>
      )}
    </div>
  );
}

// ─── Portfolio Builder (extracted from old PortfolioScreen) ───────────────────
function PortfolioBuilder({ user, token, watchlist, setWatchlist, onSelectTicker, globalHorizon, onNavigate, tradePref, embedded=false, visible=true }) {
  const [amount,    setAmount]   = useState("10000");
  const [strategy,  setStrategy] = useState(tradePref || "blended");
  const horizon = globalHorizon || "6m"; // always use global horizon from landing
  const [data,      setData]     = useState(null);
  const [loading,   setLoading]  = useState(false);
  const [error,     setError]    = useState("");
  const [progress,  setProgress] = useState({done:0, total:0, status:""});
  const [adding,    setAdding]   = useState({});
  // v8.4.304: sort the recommendation list like the Watchlist (persisted).
  const [allocSort, setAllocSort] = useState(() => {
    try { return localStorage.getItem("gempulse_opt_sort") || "swing"; } catch (_) { return "swing"; }
  });
  // Advanced filter state (v7.9.46). All-empty = "Auto" (current behavior).
  // The first time the user toggles ANY value, Auto turns off; clearing all
  // values returns to Auto. Backend mirrors this same shape via query params.
  const [advFilters, setAdvFilters] = useState({
    caps:    [],   // ['small','mid','large']
    styles:  [],   // ['growth','value','dividend']
    sectors: [],   // ['Technology','Healthcare',...]
    vol:     null, // 'low' | 'normal' | 'high' | null
  });
  const [showAdvanced, setShowAdvanced] = useState(false);
  // Saved portfolio state
  const [saved,       setSaved]      = useState([]);
  const [saveName,    setSaveName]   = useState("");
  const [showSaveBox, setShowSaveBox]= useState(false);
  const [saving,      setSaving]     = useState(false);
  const [saveMsg,     setSaveMsg]    = useState("");
  const [viewSaved,   setViewSaved]  = useState(null);
  const [perfData,    setPerfData]   = useState({});
  const [loadingPerf, setLoadingPerf]= useState({});

  React.useEffect(() => {
    apiFetch("/portfolio/saved", token)
      .then(d => setSaved(Array.isArray(d) ? d : []))
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-load performance (return %) for any saved portfolio not yet fetched
  React.useEffect(() => {
    saved.forEach(p => {
      if (!perfData[p.id] && !loadingPerf[p.id]) {
        setLoadingPerf(prev => ({...prev, [p.id]:true}));
        apiFetch(`/portfolio/saved/${p.id}/performance`, token)
          .then(perf => setPerfData(prev => ({...prev, [p.id]:perf})))
          .catch(() => {})
          .finally(() => setLoadingPerf(prev => ({...prev, [p.id]:false})));
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saved]);

  const savePortfolio = async () => {
    if (!data) return;
    const name = saveName.trim() || `Portfolio ${new Date().toLocaleDateString()}`;
    setSaving(true); setSaveMsg("");
    try {
      await apiFetch("/portfolio/saved", token, {
        method:"POST",
        body: JSON.stringify({
          name, amount:data.amount, strategy:data.strategy,
          horizon:data.horizon, profile:data.profile,
          allocations:data.allocations,
        }),
      });
      const updated = await apiFetch("/portfolio/saved", token);
      setSaved(Array.isArray(updated) ? updated : []);
      setViewSaved(null);  // return to list view so new snapshot is visible
      setSaveMsg("✅ Saved!"); setShowSaveBox(false); setSaveName("");
      setTimeout(() => setSaveMsg(""), 3000);
    } catch(e) { setSaveMsg(`❌ ${e.message}`); }
    finally { setSaving(false); }
  };

  const deleteSaved = async (id) => {
    try {
      await apiFetch(`/portfolio/saved/${id}`, token, {method:"DELETE"});
      setSaved(prev => prev.filter(p => p.id !== id));
      if (viewSaved?.id === id) setViewSaved(null);
    } catch(e) {}
  };

  const loadPerformance = async (portfolio) => {
    if (perfData[portfolio.id]) { setViewSaved(portfolio); return; }
    setLoadingPerf(p => ({...p, [portfolio.id]:true}));
    try {
      const perf = await apiFetch(`/portfolio/saved/${portfolio.id}/performance`, token);
      setPerfData(p => ({...p, [portfolio.id]:perf}));
      setViewSaved(portfolio);
    } catch(e) {}
    finally { setLoadingPerf(p => ({...p, [portfolio.id]:false})); }
  };

  // Auto = no advanced filters touched. Mirrored on the backend.
  const isAutoFilter = advFilters.caps.length === 0
                    && advFilters.styles.length === 0
                    && advFilters.sectors.length === 0
                    && advFilters.vol === null;
  const advFilterCount = advFilters.caps.length + advFilters.styles.length
                       + advFilters.sectors.length + (advFilters.vol ? 1 : 0);

  const resetAdvFilters = () => setAdvFilters({
    caps: [], styles: [], sectors: [], vol: null,
  });

  const build = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt < 500) { setError("Minimum $500"); return; }
    setLoading(true); setError(""); setData(null);
    setProgress({done:0, total:100, status:"Scanning market universe…"});
    // Fake progress since backend is one call — animate to ~85% then complete
    let prog = 0;
    const interval = setInterval(() => {
      prog = Math.min(prog + Math.random() * 8, 85);
      setProgress(p => ({...p, done:Math.round(prog),
        status: prog < 30 ? "Scoring stocks…" : prog < 60 ? "Applying diversification rules…" : "Calculating allocations…"}));
    }, 1200);
    // Build query string. Advanced filter params are only included when
    // not in Auto mode — keeps the URL clean and proves the backend default
    // is preserved when no filter is touched.
    const params = new URLSearchParams({ amount: String(amt), strategy, horizon });
    if (advFilters.caps.length)    params.set("caps",    advFilters.caps.join(","));
    if (advFilters.styles.length)  params.set("styles",  advFilters.styles.join(","));
    if (advFilters.sectors.length) params.set("sectors", advFilters.sectors.join(","));
    if (advFilters.vol)            params.set("vol",     advFilters.vol);
    const url = `/portfolio/build?${params.toString()}`;
    // Auto-retry: universe cache may still be warming on cold backend start,
    // or a transient network blip may cause a first-attempt failure.
    // 2 attempts total (immediate + 1 retry after 2 s). 422 "no candidates"
    // errors are NOT retried — those are filter mismatches, not transient.
    const delays = [0, 2000];
    let lastErr = null;
    try {
      for (let attempt = 0; attempt < delays.length; attempt++) {
        if (attempt > 0) {
          setProgress(p => ({...p, status:"Retrying…"}));
          await new Promise(r => setTimeout(r, delays[attempt]));
        }
        try {
          const d = await apiFetch(url, token, { method:"POST" });
          clearInterval(interval);
          setProgress({done:100, total:100, status:"Done"});
          setData(d);
          return;
        } catch(e) {
          // Don't retry filter-mismatch 422s — they'll fail identically
          const msg = e.message || "";
          if (msg.includes("no_candidates_match_filters") || msg.includes("422")) {
            setError("No stocks match the selected advanced filters — try loosening one (or reset to Auto).");
            return;
          }
          lastErr = e;
        }
      }
      setError(lastErr?.message || "Failed to build portfolio");
    } finally {
      clearInterval(interval);
      setLoading(false);
    }
  };

  const addToWatchlist = async (ticker) => {
    if (!setWatchlist || watchlist.includes(ticker)) return;
    setAdding(p => ({...p, [ticker]:true}));
    try {
      // v8.4.88: atomic add
      const d = await apiFetch("/watchlist/add", token, {method:"POST", body:JSON.stringify({ticker})});
      if (d?.tickers) setWatchlist(d.tickers);
    } catch(e) { /* silently fail */ }
    finally { setAdding(p => ({...p, [ticker]:false})); }
  };

  const AMOUNTS = [1000,5000,10000,25000,50000];
  const STRATEGIES = [
    {key:"swing",   label:"Swing",
     iconPath:<path d="M2 16 Q6 8 10 12 Q14 16 18 6 L22 2M22 2h-5M22 2v5" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>,
     desc:"Best short-term momentum signals"},
    {key:"lt",      label:"Long-term",
     iconPath:<><rect x="3" y="12" width="4" height="9" rx="1" stroke="currentColor" strokeWidth="2" fill="none"/><rect x="10" y="7" width="4" height="14" rx="1" stroke="currentColor" strokeWidth="2" fill="none"/><rect x="17" y="3" width="4" height="18" rx="1" stroke="currentColor" strokeWidth="2" fill="none"/></>,
     desc:"Best fundamental LT scores"},
    {key:"blended", label:"Blended",
     iconPath:<><circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" fill="none"/><path d="M12 2v3M12 19v3M2 12h3M19 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M5.6 18.4l2.1-2.1M16.3 7.7l2.1-2.1" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></>,
     desc:"Optimal mix of swing + LT"},
  ];

  const sectorColors = {
    Technology:"#00d4ff",
    Financials:"#00e676","Financial Services":"#00e676",
    Healthcare:"#ff6b9d",
    Energy:"#ffca28",
    Industrials:"#ff7043",
    "Consumer Cyclical":"#ab47bc","Consumer Defensive":"#7e57c2",
    "Communication Services":"#26c6da",
    Materials:"#66bb6a","Basic Materials":"#66bb6a",
    "Real Estate":"#ffa726",
    Utilities:"#8d6e63",
    ETF:"#78909c",
    Other:"#546e7a",
  };
  const sectorIcons = {
    Technology:"💻",Financials:"🏦","Financial Services":"🏦",
    Healthcare:"⚕️",Energy:"⚡",Industrials:"🏭",
    "Consumer Cyclical":"🛍️","Consumer Defensive":"🛒",
    "Communication Services":"📡",Materials:"⛏️",
    "Real Estate":"🏢",Utilities:"🔌",ETF:"📊",Other:"📈",
  };

  return (
    <div style={{padding: embedded ? 0 : "0 16px 100px"}}>
      {embedded && <FeatureTour tourId="portfolio_build" version={1} steps={PORTFOLIO_BUILD_STEPS} active={visible}/>}
      {!embedded && (
      <div style={{padding:"16px 0 12px",display:"flex",alignItems:"center",gap:10}}>
        <AppLogo size={32}/>
        <div>
          <div style={{fontSize:20,fontWeight:900,color:C.text,letterSpacing:"-0.02em",lineHeight:1}}>
            Portfolio Builder
          </div>
          <div style={{fontSize:10,color:C.muted,marginTop:2}}>
            AI-optimised allocation
          </div>
        </div>
      </div>)}

      {/* Controls card */}
      <div data-tour="build-amount-strategy" style={{background:C.surface,borderRadius:14,padding:"14px",
        border:`1px solid ${C.border}`,marginBottom:14}}>

        {/* Amount selector */}
        <div style={{fontSize:10,fontWeight:700,color:C.muted,
          letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:8}}>
          Investment amount
        </div>
        <div style={{display:"flex",gap:6,marginBottom:12,flexWrap:"wrap"}}>
          {AMOUNTS.map(a=>(
            <button key={a} onClick={()=>setAmount(String(a))} style={{
              padding:"6px 12px",borderRadius:20,cursor:"pointer",
              fontFamily:"inherit",fontSize:12,fontWeight:600,
              border:`1px solid ${String(a)===amount?C.accent:C.border}`,
              background:String(a)===amount?"rgba(0,212,255,0.12)":"none",
              color:String(a)===amount?C.accent:C.muted,
            }}>${(a/1000).toFixed(0)}K</button>
          ))}
        </div>
        <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:14}}>
          <span style={{fontSize:18,color:C.muted,flexShrink:0}}>$</span>
          <input
            type="number" min="500" step="500"
            value={amount}
            onChange={e=>setAmount(e.target.value)}
            style={{flex:1,background:C.card,border:`1px solid ${C.border}`,
              borderRadius:8,padding:"8px 12px",color:C.text,fontSize:14,
              fontFamily:"monospace",outline:"none"}}
            placeholder="Custom amount"
          />
        </div>

        {/* Strategy toggle */}
        <div style={{fontSize:10,fontWeight:700,color:C.muted,
          letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:8}}>
          Strategy
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:14}}>
          {STRATEGIES.map(s=>(
            <button key={s.key} onClick={()=>setStrategy(s.key)} style={{
              display:"flex",alignItems:"center",gap:10,
              padding:"10px 14px",borderRadius:10,cursor:"pointer",
              fontFamily:"inherit",textAlign:"left",
              border:`1px solid ${strategy===s.key?C.accent:C.border}`,
              background:strategy===s.key?"rgba(0,212,255,0.08)":C.card,
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
                style={{flexShrink:0,color:strategy===s.key?C.accent:"rgba(255,255,255,0.35)"}}>
                {s.iconPath}
              </svg>
              <div style={{flex:1}}>
                <div style={{fontSize:12,fontWeight:600,
                  color:strategy===s.key?C.accent:C.text}}>
                  {s.label}
                </div>
                <div style={{fontSize:11,color:C.muted,marginTop:1}}>{s.desc}</div>
              </div>
              {strategy===s.key && (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <path d="M20 6L9 17l-5-5" stroke={C.accent} strokeWidth="2.5"
                    strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </button>
          ))}
        </div>

        {/* ── Advanced filters trigger row (v7.9.48) ──────────────────────
            Earlier (v7.9.46) this was an inline collapsible. Converted to
            a centered modal in v7.9.48 so the filter UI is consistent with
            the gap scanner filter and never depends on viewport-edge math. */}
        <button data-tour="build-advanced" onClick={()=>setShowAdvanced(true)} style={{
          width:"100%",display:"flex",alignItems:"center",justifyContent:"space-between",
          padding:"10px 12px",marginBottom:12,
          background:C.card,border:`1px solid ${C.border}`,borderRadius:10,
          cursor:"pointer",fontFamily:"inherit",color:C.text,
        }}>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <svg width="14" height="14" viewBox="0 0 16 16" style={{flexShrink:0}}>
              <path d="M2 3 H14 L10 8.5 V13 L6 13 V8.5 Z"
                fill="none" stroke={advFilterCount>0?C.accent:"rgba(255,255,255,0.45)"}
                strokeWidth="1.4" strokeLinejoin="round"/>
            </svg>
            <span style={{fontSize:12,fontWeight:700,
              color:advFilterCount>0?C.accent:C.muted,
              letterSpacing:"0.04em",textTransform:"uppercase"}}>
              Advanced Filters
            </span>
            <span style={{fontSize:10,fontWeight:700,
              background: isAutoFilter ? "rgba(0,212,255,0.12)" : "rgba(0,230,118,0.14)",
              color:      isAutoFilter ? C.accent             : C.green,
              borderRadius:20,padding:"2px 9px",
              border:`1px solid ${isAutoFilter?"rgba(0,212,255,0.3)":"rgba(0,230,118,0.3)"}`,
            }}>
              {isAutoFilter ? "Auto" : `${advFilterCount} active`}
            </span>
          </div>
          <span style={{fontSize:11,color:C.muted}}>Configure ›</span>
        </button>

        {/* Centered modal — contains all four filter sections. Backdrop
            click + the explicit "Done" / × button both dismiss. Uses the
            same inset:0+margin:auto centering trick as GapScannerFilters
            so the panel never depends on its trigger's viewport position. */}
        {showAdvanced && (
          <>
            <div onClick={()=>setShowAdvanced(false)} style={{
              position:"fixed", inset:0,
              background:"rgba(0,0,0,0.6)", zIndex:9000,
            }}/>
            <div onClick={e=>e.stopPropagation()} style={{
              position:"fixed", inset:0, margin:"auto",
              width:"min(440px, calc(100vw - 32px))",
              height:"fit-content",
              maxHeight:"min(720px, calc(100vh - 80px))",
              overflowY:"auto",
              background:"#0d1528", border:`1px solid ${C.border}`,
              borderRadius:16, padding:18, zIndex:9001,
              boxShadow:"0 12px 48px rgba(0,0,0,0.6)",
            }}>
              <div style={{display:"flex",justifyContent:"space-between",
                alignItems:"center",marginBottom:6}}>
                <div style={{fontSize:14,fontWeight:700,color:C.text}}>
                  Advanced Filters
                </div>
                <button onClick={()=>setShowAdvanced(false)} style={{
                  background:"none",border:"none",color:C.muted,cursor:"pointer",
                  fontSize:22,lineHeight:1,padding:0,fontFamily:"inherit",
                }} title="Close">×</button>
              </div>
              <div style={{fontSize:10,color:C.muted,marginBottom:14,
                lineHeight:1.5}}>
                Defaults to <span style={{color:C.accent,fontWeight:700}}>Auto</span> —
                builder picks the best mix for your profile.
                Toggle any filter below to override.
              </div>

              {/* Cap Size */}
              <div style={{fontSize:10,color:C.muted,marginBottom:5,fontWeight:700,
                letterSpacing:"0.06em",textTransform:"uppercase"}}>
                Market Cap
              </div>
              <div style={{display:"flex",gap:6,marginBottom:14,flexWrap:"wrap"}}>
                {[
                  {key:"small", label:"Small-cap", hint:"<$2B"},
                  {key:"mid",   label:"Mid-cap",   hint:"$2-10B"},
                  {key:"large", label:"Large-cap", hint:">$10B"},
                ].map(o => {
                  const sel = advFilters.caps.includes(o.key);
                  return (
                    <button key={o.key} onClick={()=>setAdvFilters(f=>({
                      ...f,
                      caps: sel ? f.caps.filter(x=>x!==o.key) : [...f.caps, o.key],
                    }))} style={{
                      flex:"1 1 90px",padding:"8px 10px",borderRadius:10,border:"none",
                      cursor:"pointer",fontFamily:"inherit",
                      background:sel?"rgba(0,212,255,0.12)":"rgba(255,255,255,0.04)",
                      outline:sel?`1px solid ${C.accent}`:"none",
                    }}>
                      <div style={{fontSize:11,fontWeight:700,
                        color:sel?C.accent:C.text}}>{o.label}</div>
                      <div style={{fontSize:9,color:C.muted,marginTop:1}}>{o.hint}</div>
                    </button>
                  );
                })}
              </div>

              {/* Style */}
              <div style={{fontSize:10,color:C.muted,marginBottom:5,fontWeight:700,
                letterSpacing:"0.06em",textTransform:"uppercase"}}>
                Investment Style
              </div>
              <div style={{display:"flex",gap:6,marginBottom:14,flexWrap:"wrap"}}>
                {[
                  {key:"growth",   label:"Growth",   hint:"earnings ≥15%"},
                  {key:"value",    label:"Value",    hint:"P/E<20, PEG<1.5"},
                  {key:"dividend", label:"Dividend", hint:"yield ≥2.5%"},
                ].map(o => {
                  const sel = advFilters.styles.includes(o.key);
                  return (
                    <button key={o.key} onClick={()=>setAdvFilters(f=>({
                      ...f,
                      styles: sel ? f.styles.filter(x=>x!==o.key) : [...f.styles, o.key],
                    }))} style={{
                      flex:"1 1 96px",padding:"8px 10px",borderRadius:10,border:"none",
                      cursor:"pointer",fontFamily:"inherit",
                      background:sel?"rgba(0,212,255,0.12)":"rgba(255,255,255,0.04)",
                      outline:sel?`1px solid ${C.accent}`:"none",
                    }}>
                      <div style={{fontSize:11,fontWeight:700,
                        color:sel?C.accent:C.text}}>{o.label}</div>
                      <div style={{fontSize:9,color:C.muted,marginTop:1}}>{o.hint}</div>
                    </button>
                  );
                })}
              </div>

              {/* Volatility */}
              <div style={{fontSize:10,color:C.muted,marginBottom:5,fontWeight:700,
                letterSpacing:"0.06em",textTransform:"uppercase"}}>
                Volatility (β)
              </div>
              <div style={{display:"flex",gap:6,marginBottom:14}}>
                {[
                  {key:"low",    label:"Low",    hint:"β <0.9"},
                  {key:"normal", label:"Normal", hint:"β 0.9–1.3"},
                  {key:"high",   label:"High",   hint:"β >1.3"},
                ].map(o => {
                  const sel = advFilters.vol === o.key;
                  return (
                    <button key={o.key}
                      onClick={()=>setAdvFilters(f=>({
                        ...f, vol: sel ? null : o.key,
                      }))}
                      style={{
                      flex:1,padding:"8px 8px",borderRadius:10,border:"none",
                      cursor:"pointer",fontFamily:"inherit",textAlign:"center",
                      background:sel?"rgba(0,212,255,0.12)":"rgba(255,255,255,0.04)",
                      outline:sel?`1px solid ${C.accent}`:"none",
                    }}>
                      <div style={{fontSize:11,fontWeight:700,
                        color:sel?C.accent:C.text}}>{o.label}</div>
                      <div style={{fontSize:9,color:C.muted,marginTop:1}}>{o.hint}</div>
                    </button>
                  );
                })}
              </div>

              {/* Sectors — multi-select chips */}
              <div style={{fontSize:10,color:C.muted,marginBottom:5,fontWeight:700,
                letterSpacing:"0.06em",textTransform:"uppercase"}}>
                Sectors {advFilters.sectors.length > 0 && `(${advFilters.sectors.length})`}
              </div>
              <div style={{display:"flex",gap:5,marginBottom:16,flexWrap:"wrap"}}>
                {[
                  "Technology","Healthcare","Financials","Consumer Cyclical",
                  "Communication Services","Industrials","Consumer Defensive",
                  "Energy","Utilities","Real Estate","Materials",
                ].map(s => {
                  const sel = advFilters.sectors.includes(s);
                  return (
                    <button key={s} onClick={()=>setAdvFilters(f=>({
                      ...f,
                      sectors: sel ? f.sectors.filter(x=>x!==s) : [...f.sectors, s],
                    }))} style={{
                      padding:"5px 11px",borderRadius:14,border:"none",
                      fontFamily:"inherit",fontSize:10,fontWeight:600,cursor:"pointer",
                      background:sel?"rgba(0,212,255,0.18)":"rgba(255,255,255,0.06)",
                      color:sel?C.accent:C.muted,
                      outline:sel?`1px solid ${C.accent}`:"none",
                    }}>{s}</button>
                  );
                })}
              </div>

              {/* Modal action row */}
              <div style={{display:"flex",gap:8}}>
                <button onClick={resetAdvFilters} disabled={isAutoFilter}
                  style={{
                  flex:1,padding:"10px",borderRadius:10,
                  border:`1px solid ${C.border}`,background:"none",
                  color:isAutoFilter?"rgba(255,255,255,0.2)":C.muted,
                  fontFamily:"inherit",fontSize:12,fontWeight:600,
                  cursor:isAutoFilter?"default":"pointer",
                }}>↺ Reset to Auto</button>
                <button onClick={()=>setShowAdvanced(false)} style={{
                  flex:2,padding:"10px",borderRadius:10,border:"none",
                  background:C.accent,color:"#0a0f1e",
                  fontFamily:"inherit",fontSize:12,fontWeight:700,cursor:"pointer",
                }}>Done</button>
              </div>
            </div>
          </>
        )}

        {/* Active settings - read only, adjust from Home */}
        <div style={{padding:"10px 14px",background:C.card,borderRadius:10,
          marginBottom:14,border:`1px solid ${C.border}`}}>
          <div style={{display:"flex",alignItems:"center",flexWrap:"wrap",
            gap:6,fontSize:10,color:C.muted}}>
            <span>Active settings — adjust in</span>
            <button onClick={()=>onNavigate&&onNavigate("landing")} style={{
              fontSize:10,background:"rgba(0,212,255,0.1)",color:C.accent,
              borderRadius:20,padding:"2px 10px",border:"1px solid rgba(0,212,255,0.2)",
              cursor:"pointer",fontFamily:"inherit",fontWeight:700,
            }}>🏠 Home</button>
          </div>
          <div style={{display:"flex",gap:6,marginTop:8,flexWrap:"wrap"}}>
            <span style={{fontSize:11,fontWeight:700,color:C.accent,
              background:"rgba(0,212,255,0.1)",borderRadius:20,padding:"2px 10px",
              border:"1px solid rgba(0,212,255,0.2)"}}>
              {PROFILES.find(p=>p.key===user.risk_profile)?.emoji}{" "}
              {PROFILES.find(p=>p.key===user.risk_profile)?.label}
            </span>
            <span style={{fontSize:11,fontWeight:700,color:C.accent,
              background:"rgba(0,212,255,0.1)",borderRadius:20,padding:"2px 10px",
              border:"1px solid rgba(0,212,255,0.2)"}}>
              {HORIZONS.find(h=>h.key===horizon)?.label}
            </span>
          </div>
        </div>

        <button data-tour="build-submit" onClick={(e)=>{if(!loading){Haptics.heavy(e.currentTarget);build();}}} disabled={loading} style={{
          width:"100%",padding:"13px",borderRadius:12,border:"none",
          background:loading?"rgba(0,212,255,0.08)":C.accent,
          color:loading?C.accent:C.bg,
          fontSize:14,fontWeight:700,cursor:loading?"default":"pointer",
          fontFamily:"inherit",transition:"all 0.2s",
        }}>
          {loading
            ? <span style={{display:"flex",alignItems:"center",
                justifyContent:"center",gap:8}}>
                <span style={{width:14,height:14,border:`2px solid ${C.accent}`,
                  borderTopColor:"transparent",borderRadius:"50%",
                  display:"inline-block",animation:"spin 0.7s linear infinite"}}/>
                Analyzing market…
              </span>
            : "🏗️ Build Portfolio"}
        </button>

        {/* Progress bar */}
        {loading && (
          <div style={{marginTop:12}}>
            <div style={{display:"flex",justifyContent:"space-between",
              alignItems:"center",marginBottom:5}}>
              <span style={{fontSize:11,color:C.muted}}>{progress.status}</span>
              <span style={{fontSize:11,color:C.accent,fontFamily:"monospace"}}>
                {progress.done}%
              </span>
            </div>
            <div style={{height:4,background:"rgba(255,255,255,0.06)",borderRadius:2,overflow:"hidden"}}>
              <div style={{
                height:"100%",borderRadius:2,
                background:`linear-gradient(90deg,${C.accent},#00ff88)`,
                width:`${progress.done}%`,transition:"width 1s ease",
              }}/>
            </div>
          </div>
        )}

        {error && (
          <div style={{fontSize:12,color:C.red,marginTop:10,textAlign:"center"}}>
            {error}
          </div>
        )}
      </div>

      {/* Results */}
      {data && (
        <>
          {/* Summary card */}
          <Card style={{marginBottom:12}}>
            <div style={{display:"flex",justifyContent:"space-between",
              alignItems:"flex-start",marginBottom:14}}>
              <div>
                <div style={{fontSize:16,fontWeight:800,color:C.text}}>
                  ${data.totalInvested.toLocaleString()} invested
                </div>
                <div style={{fontSize:11,color:C.muted,marginTop:3}}>
                  {data.stockCount} positions · {data.profileLabel} ·{" "}
                  {STRATEGIES.find(s=>s.key===data.strategy)?.label}
                </div>
                {/* Applied advanced filters chip — only shown when not in Auto mode.
                    Lets the user trace which filter shaped the result. */}
                {data.appliedFilters && !data.isAutoFilter && (
                  <div style={{display:"flex",flexWrap:"wrap",gap:4,marginTop:6}}>
                    {data.appliedFilters.caps?.map(c=>(
                      <span key={`cap-${c}`} style={{fontSize:9,fontWeight:700,
                        background:"rgba(0,212,255,0.1)",color:C.accent,
                        borderRadius:10,padding:"2px 7px",
                        border:"1px solid rgba(0,212,255,0.25)"}}>
                        {c}-cap
                      </span>
                    ))}
                    {data.appliedFilters.styles?.map(s=>(
                      <span key={`style-${s}`} style={{fontSize:9,fontWeight:700,
                        background:"rgba(0,212,255,0.1)",color:C.accent,
                        borderRadius:10,padding:"2px 7px",
                        border:"1px solid rgba(0,212,255,0.25)"}}>
                        {s}
                      </span>
                    ))}
                    {data.appliedFilters.vol && (
                      <span style={{fontSize:9,fontWeight:700,
                        background:"rgba(0,212,255,0.1)",color:C.accent,
                        borderRadius:10,padding:"2px 7px",
                        border:"1px solid rgba(0,212,255,0.25)"}}>
                        {data.appliedFilters.vol}-vol
                      </span>
                    )}
                    {data.appliedFilters.sectors?.length > 0 && (
                      <span style={{fontSize:9,fontWeight:700,
                        background:"rgba(0,212,255,0.1)",color:C.accent,
                        borderRadius:10,padding:"2px 7px",
                        border:"1px solid rgba(0,212,255,0.25)"}}
                        title={data.appliedFilters.sectors.join(", ")}>
                        {data.appliedFilters.sectors.length} sector{data.appliedFilters.sectors.length>1?"s":""}
                      </span>
                    )}
                  </div>
                )}
              </div>
              <div style={{textAlign:"right"}}>
                <div style={{fontSize:11,color:C.muted,display:"flex",alignItems:"center",
                  gap:4,justifyContent:"flex-end"}}>
                  Diversification
                  <InfoTip text="Diversification score (0–100): sectors × 10 pts each (max 70) + up to 30 pts for low concentration. 70+ = well diversified · 40–69 = moderate · <40 = concentrated"/>
                </div>
                <div style={{fontSize:18,fontWeight:700,
                  color:data.diversificationScore>=70?C.green:
                        data.diversificationScore>=40?C.yellow:C.red}}>
                  {data.diversificationScore}/100
                </div>
              </div>
            </div>

            {data.cashReserve > 0 && (
              <div style={{display:"flex",justifyContent:"space-between",
                padding:"8px 12px",background:C.surface,borderRadius:8,
                marginBottom:12,border:`1px solid ${C.border}`}}>
                <span style={{fontSize:12,color:C.muted}}>💵 Cash reserve</span>
                <span style={{fontSize:12,fontWeight:600,color:C.text,
                  fontFamily:"monospace"}}>
                  ${data.cashReserve.toLocaleString()}
                  {" "}({(data.cashReserve/data.amount*100).toFixed(1)}%)
                </span>
              </div>
            )}

            {/* Sector breakdown bar */}
            <div style={{fontSize:10,fontWeight:700,color:C.muted,
              letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:8}}>
              Sector allocation
            </div>
            <div style={{height:10,borderRadius:5,overflow:"hidden",
              display:"flex",marginBottom:8}}>
              {Object.entries(data.sectorBreakdown)
                .sort((a,b)=>b[1]-a[1])
                .map(([sec,amt])=>(
                  <div key={sec} style={{
                    flex:amt,
                    background:sectorColors[sec]||"#546e7a",
                    transition:"flex 0.5s ease",
                  }} title={`${sec}: $${amt}`}/>
                ))}
            </div>
            <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
              {Object.entries(data.sectorBreakdown)
                .sort((a,b)=>b[1]-a[1])
                .map(([sec,amt])=>(
                  <div key={sec} style={{display:"flex",alignItems:"center",
                    gap:5,fontSize:10,
                    background:`${sectorColors[sec]||"#546e7a"}18`,
                    border:`1px solid ${sectorColors[sec]||"#546e7a"}40`,
                    borderRadius:20,padding:"3px 8px",color:C.muted}}>
                    <span style={{fontSize:11}}>{sectorIcons[sec]||"📈"}</span>
                    <span style={{color:sectorColors[sec]||"#546e7a",fontWeight:600}}>
                      {sec}
                    </span>
                    <span style={{color:"rgba(255,255,255,0.4)"}}>
                      {(amt/data.amount*100).toFixed(0)}%
                    </span>
                  </div>
                ))}
            </div>
          </Card>

          {/* Recommendation sort — Swing / LT / A-Z (matches the Watchlist),
              persisted. Each card keeps its own optimizer rank badge (#a.rank). */}
          <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:10,
            justifyContent:"flex-end"}}>
            <span style={{fontSize:10,color:C.muted,marginRight:2}}>Sort</span>
            {[["swing","Swing"],["lt","LT"],["alpha","A–Z"]].map(([v,l])=>(
              <button key={v} onClick={()=>{ setAllocSort(v); try { localStorage.setItem("gempulse_opt_sort", v); } catch(_){} }} style={{
                padding:"3px 10px",borderRadius:20,fontSize:10,fontWeight:700,
                border:`1px solid ${allocSort===v?C.accent:C.border}`,
                background:allocSort===v?"rgba(0,212,255,0.12)":"none",
                color:allocSort===v?C.accent:C.muted,cursor:"pointer",
                fontFamily:"inherit",transition:"all 0.15s"}}>{l}</button>
            ))}
          </div>

          {/* Allocation cards */}
          {[...data.allocations].sort((x,y)=>
              allocSort==="alpha" ? String(x.ticker).localeCompare(String(y.ticker))
            : allocSort==="lt"    ? ((y.ltScore||0)-(x.ltScore||0)) || String(x.ticker).localeCompare(String(y.ticker))
            :                       ((y.swingScore||0)-(x.swingScore||0)) || String(x.ticker).localeCompare(String(y.ticker))
          ).map((a,i)=>{
            const inWatchlist = watchlist.includes(a.ticker);
            return (
              <Card key={a.ticker} style={{marginBottom:10,cursor:"pointer"}}
                onClick={()=>onSelectTicker(a.ticker)}>
                <div style={{display:"flex",alignItems:"center",gap:10}}>
                  {/* Rank */}
                  <div style={{width:24,height:24,borderRadius:"50%",flexShrink:0,
                    background:"rgba(0,212,255,0.1)",display:"flex",
                    alignItems:"center",justifyContent:"center"}}>
                    <span style={{fontSize:10,fontWeight:800,color:C.accent}}>
                      #{a.rank}
                    </span>
                  </div>

                  <TickerLogo ticker={a.ticker} size={30}/>

                  <div style={{flex:1,minWidth:0}}>
                    <div style={{display:"flex",alignItems:"center",gap:6,
                      flexWrap:"wrap",marginBottom:1}}>
                      <span style={{fontSize:15,fontWeight:800,color:C.text,
                        fontFamily:"monospace"}}>{a.ticker}</span>
                      <SignalPill type={a.swingType} size="sm"/>
                      {inWatchlist
                        ? <span style={{fontSize:9,background:"rgba(0,230,118,0.15)",
                            color:C.green,borderRadius:4,padding:"1px 5px",
                            fontWeight:700,border:"1px solid rgba(0,230,118,0.3)"}}>
                            ★ Watchlist
                          </span>
                        : <button
                            onClick={e=>{e.stopPropagation();addToWatchlist(a.ticker);}}
                            disabled={adding[a.ticker]}
                            style={{fontSize:9,background:"rgba(0,212,255,0.1)",
                              color:C.accent,borderRadius:4,padding:"2px 6px",
                              fontWeight:700,border:`1px solid rgba(0,212,255,0.25)`,
                              cursor:"pointer",fontFamily:"inherit",flexShrink:0}}>
                            {adding[a.ticker] ? "…" : "+ Watchlist"}
                          </button>
                      }
                    </div>
                    <div style={{fontSize:10,color:C.muted,
                      overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                      {a.shortName}
                    </div>
                    <div style={{fontSize:10,color:C.muted,marginTop:1}}>
                      {a.sector} · <BetaPill beta={a.beta}/>
                    </div>
                  </div>

                  {/* Right: allocation */}
                  <div style={{textAlign:"right",flexShrink:0}}>
                    <div style={{fontSize:16,fontWeight:800,color:C.accent,
                      fontFamily:"monospace"}}>
                      ${a.dollar.toLocaleString()}
                    </div>
                    <div style={{fontSize:11,color:C.muted,fontFamily:"monospace"}}>
                      {a.pct}% · {a.shares} shares
                    </div>
                    <div style={{fontSize:11,color:a.changePct>=0?C.green:C.red,
                      fontFamily:"monospace"}}>
                      @${a.price} {a.changePct>=0?"+":""}{a.changePct}%
                      <span style={{fontSize:9,marginLeft:4}}>
                        <ListQuoteDot isLive={a.isLive}/>
                      </span>
                    </div>
                  </div>
                </div>

                {/* Score bar */}
                <div style={{display:"flex",gap:6,marginTop:10,
                  alignItems:"center"}}>
                  <div style={{flex:1,height:3,background:"rgba(255,255,255,0.06)",
                    borderRadius:2,overflow:"hidden"}}>
                    <div style={{height:"100%",borderRadius:2,
                      background:`linear-gradient(90deg,${C.accent},#00ff88)`,
                      width:`${Math.max(a.swingScore,a.ltScore)}%`,
                      transition:"width 0.5s ease"}}/>
                  </div>
                  <span style={{fontSize:10,color:C.muted,flexShrink:0}}>
                    SW {a.swingScore} · LT {a.ltScore}
                  </span>
                  {a.leftover > 0 && (
                    <span style={{fontSize:9,color:C.muted}}>
                      ${a.leftover} leftover
                    </span>
                  )}
                </div>
              </Card>
            );
          })}

          <Card style={{borderColor:"rgba(255,202,40,0.2)",marginTop:4}}>
            <div style={{fontSize:11,color:C.yellow,lineHeight:1.7}}>
              ⚠️ This is an educational portfolio allocation tool only.
              Not financial advice. Always consult a qualified advisor before investing.
              Past performance does not guarantee future results.
            </div>
          </Card>

          {/* Save Portfolio Snapshot */}
          <div style={{marginTop:12}}>
            {!showSaveBox ? (
              <button onClick={()=>setShowSaveBox(true)} style={{
                width:"100%",padding:"12px",borderRadius:12,
                border:`1px solid rgba(0,212,255,0.3)`,
                background:"rgba(0,212,255,0.08)",
                color:C.accent,fontSize:13,fontWeight:700,
                cursor:"pointer",fontFamily:"inherit",
              }}>💾 Save Portfolio Snapshot</button>
            ) : (
              <div style={{background:C.surface,borderRadius:12,padding:"14px",
                border:`1px solid rgba(0,212,255,0.3)`}}>
                <div style={{fontSize:12,color:C.muted,marginBottom:8}}>
                  Name this snapshot ({saved.length}/4 saved)
                </div>
                <input value={saveName} onChange={e=>setSaveName(e.target.value)}
                  placeholder={`Portfolio ${new Date().toLocaleDateString()}`}
                  style={{width:"100%",boxSizing:"border-box",background:C.card,
                    border:`1px solid ${C.border}`,borderRadius:8,padding:"9px 12px",
                    color:C.text,fontSize:13,fontFamily:"inherit",outline:"none",marginBottom:8}}/>
                {saveMsg && (
                  <div style={{fontSize:12,textAlign:"center",marginBottom:8,
                    color:saveMsg.startsWith("✅")?C.green:C.red}}>{saveMsg}</div>
                )}
                <div style={{display:"flex",gap:8}}>
                  <button onClick={savePortfolio} disabled={saving} style={{
                    flex:1,padding:"9px",borderRadius:8,border:"none",
                    background:C.accent,color:C.bg,fontSize:12,fontWeight:700,
                    cursor:"pointer",fontFamily:"inherit",opacity:saving?0.7:1,
                  }}>{saving?"Saving…":"Save"}</button>
                  <button onClick={()=>{setShowSaveBox(false);setSaveName("");setSaveMsg("");}} style={{
                    flex:1,padding:"9px",borderRadius:8,border:`1px solid ${C.border}`,
                    background:"none",color:C.muted,fontSize:12,
                    cursor:"pointer",fontFamily:"inherit",
                  }}>Cancel</button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Saved Portfolios */}
      {saved.length > 0 && !viewSaved && (
        <div style={{marginTop:data?16:0}}>
          <div style={{fontSize:10,fontWeight:700,color:C.muted,
            letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:10}}>
            Saved Snapshots
          </div>
          {saved.map(p => {
            const perf = perfData[p.id];
            const isLoading = loadingPerf[p.id];
            const ret = perf?.totalReturnPct;
            return (
              <div key={p.id} style={{background:C.surface,borderRadius:12,
                padding:"12px 14px",border:`1px solid ${C.border}`,marginBottom:10}}>
                <div style={{display:"flex",alignItems:"center",gap:10}}>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:13,fontWeight:700,color:C.text,
                      overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                      {p.name}
                    </div>
                    <div style={{fontSize:10,color:C.muted,marginTop:2}}>
                      ${p.amount?.toLocaleString()} · {p.strategy} · {new Date(p.saved_at).toLocaleDateString()}
                    </div>
                  </div>
                  {/* Return since inception — always visible once loaded */}
                  {ret != null ? (
                    <div style={{fontSize:15,fontWeight:800,fontFamily:"monospace",
                      color:ret>=0?C.green:C.red,minWidth:60,textAlign:"right"}}>
                      {ret>=0?"+":""}{ret.toFixed(1)}%
                    </div>
                  ) : (
                    <div style={{fontSize:10,color:C.muted,minWidth:60,textAlign:"right"}}>
                      {isLoading ? "…" : "—"}
                    </div>
                  )}
                  <button onClick={()=>loadPerformance(p)} title="View details"
                    disabled={isLoading} style={{
                    background:"none",border:`1px solid ${C.border}`,borderRadius:8,
                    padding:"5px 10px",color:C.muted,fontSize:11,
                    cursor:"pointer",fontFamily:"inherit"}}>↗</button>
                  <button onClick={()=>deleteSaved(p.id)} style={{
                    background:"none",border:"none",color:"rgba(255,82,82,0.5)",
                    fontSize:14,cursor:"pointer",padding:"4px"}}>✕</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Saved Portfolio Detail */}
      {viewSaved && (() => {
        const perf = perfData[viewSaved.id];
        return (
          <div style={{marginTop:16}}>
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:12}}>
              <button onClick={()=>setViewSaved(null)} style={{
                background:C.surface,border:`1px solid ${C.border}`,
                borderRadius:8,padding:"5px 12px",color:C.muted,
                fontSize:12,cursor:"pointer",fontFamily:"inherit"}}>← Back</button>
              <div style={{flex:1}}>
                <div style={{fontSize:15,fontWeight:800,color:C.text}}>{viewSaved.name}</div>
                <div style={{fontSize:10,color:C.muted}}>
                  Saved {new Date(viewSaved.saved_at).toLocaleDateString()} · ${viewSaved.amount?.toLocaleString()} · {viewSaved.strategy}
                </div>
              </div>
            </div>
            {perf ? (
              <Card style={{marginBottom:12}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
                  <div>
                    <div style={{fontSize:11,color:C.muted}}>Since Inception</div>
                    <div style={{fontSize:28,fontWeight:900,fontFamily:"monospace",
                      color:perf.totalReturnPct>=0?C.green:C.red}}>
                      {perf.totalReturnPct>=0?"+":""}{perf.totalReturnPct?.toFixed(2)}%
                    </div>
                  </div>
                  <div style={{textAlign:"right"}}>
                    <div style={{fontSize:11,color:C.muted}}>P&L</div>
                    <div style={{fontSize:18,fontWeight:700,fontFamily:"monospace",
                      color:perf.totalReturnDollar>=0?C.green:C.red}}>
                      {perf.totalReturnDollar>=0?"+":"-"}${Math.abs(perf.totalReturnDollar).toLocaleString()}
                    </div>
                    <div style={{fontSize:10,color:C.muted,marginTop:2}}>
                      ${perf.totalCurrent?.toLocaleString()} today
                    </div>
                  </div>
                </div>
                {/* Holdings Table Header */}
                <div style={{display:"grid",gridTemplateColumns:"1fr 60px 70px 70px 60px",
                  gap:4,padding:"6px 0",borderTop:`1px solid ${C.border}`,
                  borderBottom:`1px solid rgba(0,212,255,0.15)`}}>
                  {["Holding","Shares","Cost","Today","P&L"].map(h=>(
                    <div key={h} style={{fontSize:9,fontWeight:700,color:C.muted,
                      textTransform:"uppercase",letterSpacing:"0.08em",
                      textAlign:h==="Holding"?"left":"right"}}>{h}</div>
                  ))}
                </div>
                {perf.holdings?.sort((a,b)=>b.returnPct-a.returnPct).map(h=>(
                  <div key={h.ticker} style={{display:"grid",
                    gridTemplateColumns:"1fr 60px 70px 70px 60px",
                    gap:4,padding:"9px 0",borderBottom:`1px solid ${C.border}`,
                    alignItems:"center"}}>
                    {/* Holding name */}
                    <div style={{display:"flex",alignItems:"center",gap:6,minWidth:0}}>
                      <TickerLogo ticker={h.ticker} size={22}/>
                      <div style={{minWidth:0}}>
                        <div style={{fontSize:12,fontWeight:800,color:C.text,
                          fontFamily:"monospace"}}>{h.ticker}</div>
                        <div style={{fontSize:9,color:C.muted,
                          overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                          {h.shortName||h.ticker}
                        </div>
                      </div>
                    </div>
                    {/* Shares */}
                    <div style={{fontSize:11,color:C.muted,fontFamily:"monospace",
                      textAlign:"right"}}>{h.shares}</div>
                    {/* Cost price */}
                    <div style={{fontSize:11,color:C.muted,fontFamily:"monospace",
                      textAlign:"right"}}>${h.costPrice?.toFixed(2)}</div>
                    {/* Current price */}
                    <div style={{fontSize:11,fontWeight:700,fontFamily:"monospace",
                      textAlign:"right",color:C.text}}>${h.currentPrice?.toFixed(2)}</div>
                    {/* P&L */}
                    <div style={{textAlign:"right"}}>
                      <div style={{fontSize:12,fontWeight:800,fontFamily:"monospace",
                        color:h.returnPct>=0?C.green:"#ff5252"}}>
                        {h.returnPct>=0?"+":""}{h.returnPct?.toFixed(1)}%
                      </div>
                      <div style={{fontSize:9,color:h.returnDollar>=0?C.green:"#ff5252",
                        fontFamily:"monospace"}}>
                        {h.returnDollar>=0?"+":"-"}${Math.abs(h.returnDollar||0).toLocaleString()}
                      </div>
                    </div>
                  </div>
                ))}
              </Card>
            ) : (
              <div style={{textAlign:"center",padding:"20px",color:C.muted,fontSize:12}}>
                Loading performance…
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
}

// ─── Portfolio Optimizer ──────────────────────────────────────────────────────
// Format a percentage value to 1 decimal place — handles floats with trailing
// noise like 72.50000000000001 → 72.5, NaN/null → 0.0.
const _fmtPct1 = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(1) : "0.0";
};
// Format a score to 0 decimal places (integer-like) — combinedScore etc.
const _fmtScore = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.round(n).toString() : "—";
};

function PortfolioOptimizer({ user, token, onSelectTicker, globalHorizon, scanData, tradePref, universeData, upsertUniverse }) {
  const horizon = globalHorizon || "6m";
  const PORTFOLIOS_KEY = `optimizer_portfolios_${user?.email || "default"}`;

  // ── Multi-portfolio state ────────────────────────────────────────────────────
  const [portfolios,    setPortfolios]    = useState([]);  // [{id,name,holdings}]
  const [activeId,      setActiveId]      = useState(null);
  const [editingName,   setEditingName]   = useState(null); // id being renamed
  const [editNameVal,   setEditNameVal]   = useState("");
  const [loaded,        setLoaded]        = useState(false);

  // ── Input state ──────────────────────────────────────────────────────────────
  const [newTicker,     setNewTicker]     = useState("");
  const [newQty,        setNewQty]        = useState("1");
  const [newCost,       setNewCost]       = useState("");
  const [newName,       setNewName]       = useState("");
  const [cashToInvest,  setCashToInvest]  = useState(""); // additional cash to deploy

  // ── Analysis state ───────────────────────────────────────────────────────────
  const [result,        setResult]        = useState(null);
  const [loading,       setLoading]       = useState(false);
  const [error,         setError]         = useState("");
  const [progMsg,       setProgMsg]       = useState("");
  const [doneActions,   setDoneActions]   = useState(new Set()); // tickers confirmed by user
  // v8.4.307: sort the Recommendations list — Swing / LT / A-Z (persisted).
  const [posSort,       setPosSort]       = useState(() => {
    try { return localStorage.getItem("gempulse_optpos_sort") || "swing"; } catch (_) { return "swing"; }
  });

  // Helpers
  const activePortfolio = portfolios.find(p => p.id === activeId) || null;
  const holdings        = activePortfolio?.holdings || [];

  const setHoldings = (updater) => {
    setPortfolios(prev => prev.map(p =>
      p.id === activeId
        ? {...p, holdings: typeof updater === "function" ? updater(p.holdings) : updater}
        : p
    ));
    setResult(null); // clear stale results when holdings change
  };

  // ── Persistence — Supabase (survives browser/device changes) ────────────────
  React.useEffect(() => {
    apiFetch("/portfolio/optimizer", token)
      .then(remote => {
        if (Array.isArray(remote) && remote.length > 0) {
          // Ensure core_holding defaults to false for holdings loaded from Supabase
          const normalized = remote.map(p => ({
            ...p,
            holdings: (p.holdings || []).map(h => ({
              ...h,
              core_holding: h.core_holding || false,
            })).sort((a,b)=>String(a.ticker).localeCompare(String(b.ticker))),  // v8.4.300: A-Z on load
          }));
          setPortfolios(normalized);
          setActiveId(normalized[0].id);
        } else {
          // One-time migration from localStorage
          try {
            const local = localStorage.getItem(PORTFOLIOS_KEY);
            if (local) {
              const parsed = JSON.parse(local);
              if (Array.isArray(parsed) && parsed.length > 0) {
                setPortfolios(parsed);
                setActiveId(parsed[0].id);
                apiFetch("/portfolio/optimizer", token, {
                  method:"PUT", body:JSON.stringify({portfolios:parsed})
                }).then(()=>localStorage.removeItem(PORTFOLIOS_KEY)).catch(()=>{});
              }
            }
          } catch(e) {}
        }
      })
      .catch(() => {
        try {
          const local = localStorage.getItem(PORTFOLIOS_KEY);
          if (local) {
            const parsed = JSON.parse(local);
            if (Array.isArray(parsed) && parsed.length > 0) {
              setPortfolios(parsed); setActiveId(parsed[0].id);
            }
          }
        } catch(e) {}
      })
      .finally(() => setLoaded(true));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-save to Supabase on portfolio change (debounced 1.5s)
  const _saveTimer = React.useRef(null);
  React.useEffect(() => {
    if (!loaded || portfolios.length === 0) return;
    clearTimeout(_saveTimer.current);
    _saveTimer.current = setTimeout(() => {
      apiFetch("/portfolio/optimizer", token, {
        method:"PUT", body:JSON.stringify({portfolios})
      }).catch(()=>{});
    }, 1500);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portfolios, loaded]);

  // ── Portfolio management ─────────────────────────────────────────────────────
  const createPortfolio = () => {
    const id   = `p_${Date.now()}`;
    const name = `Portfolio ${portfolios.length + 1}`;
    setPortfolios(prev => [...prev, {id, name, holdings:[]}]);
    setActiveId(id);
    setResult(null);
  };

  const deletePortfolio = (id) => {
    setPortfolios(prev => {
      const next = prev.filter(p => p.id !== id);
      if (activeId === id) setActiveId(next[0]?.id || null);
      return next;
    });
    setResult(null);
  };

  const renamePortfolio = (id, name) => {
    setPortfolios(prev => prev.map(p => p.id === id ? {...p, name} : p));
    setEditingName(null);
  };

  // ── Holdings CRUD ────────────────────────────────────────────────────────────
  const addHolding = () => {
    const t   = newTicker.trim().toUpperCase();
    const qty = parseFloat(newQty);
    if (!t || !qty || qty <= 0 || !activeId) return;
    setHoldings(prev => {
      const exists = prev.find(h => h.ticker === t);
      const next = exists
        ? prev.map(h => h.ticker===t ? {...h, quantity: h.quantity+qty, shortName:h.shortName||newName} : h)
        : [...prev, {ticker:t, quantity:qty, avg_cost:parseFloat(newCost)||0, shortName:newName||""}];
      // v8.4.300: keep the input list A-Z so a specific holding is easy to find/edit.
      return [...next].sort((a,b)=>String(a.ticker).localeCompare(String(b.ticker)));
    });
    setNewTicker(""); setNewQty("1"); setNewCost(""); setNewName("");
  };

  const removeHolding    = (ticker) => setHoldings(prev => prev.filter(h => h.ticker !== ticker));
  const updateQty        = (ticker, delta) => setHoldings(prev => prev.map(h => {
    if (h.ticker !== ticker) return h;
    return {...h, quantity: Math.max(0.01, parseFloat((h.quantity+delta).toFixed(4)))};
  }));
  const updateField = (ticker, field, value) => {
    // Boolean fields handled separately
    if (typeof value === 'boolean') {
      setHoldings(prev => prev.map(h => h.ticker===ticker ? {...h, [field]:value} : h));
      return;
    }
    const v = parseFloat(value);
    if (isNaN(v) || v < 0) return;
    setHoldings(prev => prev.map(h => h.ticker===ticker ? {...h, [field]:v} : h));
  };

  // ── Analysis ─────────────────────────────────────────────────────────────────
  const analyze = async () => {
    if (holdings.length === 0) { setError("Add at least one holding"); return; }
    setLoading(true); setError(""); setResult(null);

    // Build cache from universeData (comprehensive) + scanData (watchlist fallback)
    // 35min staleness window aligns with Celery's 30-min refresh cycle + 5min grace.
    const STALE_U = 35 * 60 * 1000;
    const cacheMap = {};
    // universeData is the primary source — covers 200+ tickers
    Object.values(universeData||{}).forEach(r => {
      if (!r?.ticker) return;
      const age = Date.now() - (r.scoredAt||0);
      // Include if: price > 0 (fully scored) OR has real scores even if price missing
      if (age < STALE_U && (r.price > 0 || r.swingScore > 0 || r.ltScore > 0)) {
        cacheMap[r.ticker.toUpperCase()] = r;
      }
    });
    // scanData fills any gaps (watchlist-only tickers scored recently)
    (scanData?.results || []).forEach(r => {
      if (r?.ticker && !cacheMap[r.ticker.toUpperCase()]) {
        cacheMap[r.ticker.toUpperCase()] = r;
      }
    });
    const hitCount = holdings.filter(h => cacheMap[h.ticker.toUpperCase()]).length;
    setProgMsg(hitCount > 0 ? `${hitCount} cached, fetching ${holdings.length-hitCount} new…` : "Scoring your holdings…");

    const cached = {};
    holdings.forEach(h => {
      const t = h.ticker.toUpperCase();
      const r = cacheMap[t];
      // Sector: use scored data, fallback to SECTOR_HINTS, never send empty string
      const sector = (r?.sector) || SECTOR_HINTS[t] || SECTOR_HINTS[h.ticker] || "Other";
      if (!r || (r.swingScore === 0 && r.ltScore === 0 && r.price === 0)) {
        // No real score data — send only sector hint, no fake 50/50 scores
        // Backend will score this ticker fresh from yfinance
        // Sending swingScore:50 would override a fresh score with a bogus default
        cached[t] = { shortName:"", sector, price:0,
                      _sectorHintOnly: true };
        return;
      }
      cached[t] = {
        shortName:r.shortName||"", sector, price:r.price||0,
        changePct:r.changePct||0,
        // Null-coalescing only — score 0 is valid, null means "not yet scored"
        // Backend treats null scores as needing a fresh /analyze call
        swingScore: r.swingScore ?? null,
        swingType:  r.swingType  || "watch",
        ltScore:    r.ltScore    ?? null,
        beta:       r.beta       || 1.0,
        horizonReturn: r.horizonReturn ?? 0,
      };
    });

    // Build suggestions from universeData (full 175-ticker universe) 
    // NOT from scanData (watchlist only) to avoid watchlist bias.
    // universeData contains all tickers scored by the background engine.
    const heldSet  = new Set(holdings.map(h => h.ticker.toUpperCase()));
    const heldBase = new Set(holdings.map(h => h.ticker.toUpperCase().split(".")[0]));

    // Pull from universeData — broad universe, actionable signals only (not watch)
    const universeResults = Object.values(universeData || {})
      .filter(r => {
        if (!r?.ticker || r.price <= 0) return false;
        const t = r.ticker.toUpperCase(), tBase = t.split(".")[0];
        const type = (r.swingType || "watch").toLowerCase();
        return !heldSet.has(t) && !heldBase.has(tBase)
            && (r.swingScore || 0) > 55
            && type !== "watch"   // only buy/strong-buy signals, not idle watch
            && (Date.now() - (r.scoredAt || 0)) < 2 * 60 * 60 * 1000; // <2hr old
      })
      .map(r => {
        const t = r.ticker.toUpperCase();
        return {
          ticker:       r.ticker,
          shortName:    r.shortName || r.ticker,
          sector:       r.sector || SECTOR_HINTS[t] || "Other",
          price:        r.price || 0,
          swingScore:   r.swingScore || 0,
          ltScore:      r.ltScore || 0,
          swingType:    r.swingType || "watch",
          combinedScore:((r.swingScore||0) + (r.ltScore||0)) / 2,
        };
      })
      .sort((a,b) => b.combinedScore - a.combinedScore)
      .slice(0, 25);

    // Blend in any watchlist tickers not already in universeResults
    const universeSet = new Set(universeResults.map(r => r.ticker.toUpperCase()));
    const watchlistExtra = (scanData?.results || [])
      .filter(r => {
        if (!r?.ticker) return false;
        const t = r.ticker.toUpperCase();
        const type = (r.swingType || "watch").toLowerCase();
        return !heldSet.has(t) && !universeSet.has(t) && (r.swingScore||0) > 55 && type !== "watch";
      })
      .map(r => {
        const t = r.ticker.toUpperCase();
        return {
          ticker:r.ticker, shortName:r.shortName||r.ticker,
          sector:r.sector||SECTOR_HINTS[t]||"Other",
          price:r.price||0, swingScore:r.swingScore||0, ltScore:r.ltScore||0,
          swingType:r.swingType||"watch",
          combinedScore:((r.swingScore||0)+(r.ltScore||0))/2,
        };
      });

    const suggested = [...universeResults, ...watchlistExtra]
      .sort((a,b) => b.combinedScore - a.combinedScore)
      .slice(0, 20);

    const payload = {
      holdings: holdings.map(h => ({
        ticker:       h.ticker,
        quantity:     h.quantity,
        avg_cost:     h.avg_cost || 0,
        core_holding: h.core_holding || false,
      })),
      horizon, cached, suggested,
      extra_cash: parseFloat(cashToInvest)||0,
    };

    // Auto-retry up to 3 times — first attempt may time out while the backend
    // warms yfinance and Redis caches. Subsequent calls hit the server-side cache.
    let lastErr = null;
    for (let attempt = 1; attempt <= 3; attempt++) {
      try {
        if (attempt > 1) {
          setProgMsg(`Warming up — retrying (attempt ${attempt}/3)…`);
          await new Promise(r => setTimeout(r, 2000));
        }
        const d = await apiFetch(`/portfolio/optimize`, token, {
          method:"POST", body:JSON.stringify(payload),
        });
        setResult(d);
        lastErr = null;
        break;
      } catch(e) {
        lastErr = e;
        if (attempt < 3) continue;
      }
    }
    if (lastErr) {
      const msg = lastErr.message || "Analysis failed";
      if (msg.includes("fetch") || msg.includes("network") || msg.includes("500")) {
        setError("Server is warming up — please try again in a moment.");
      } else {
        setError(msg);
      }
    }
    setLoading(false); setProgMsg("");
  };

  // ── Display constants ────────────────────────────────────────────────────────
  const actionColor = {EXIT:"#ff5252",TRIM:"#ff7043",ADD:"#00e676",HOLD:C.accent,REVIEW:"#ffca28"};
  const actionBg    = {EXIT:"rgba(255,82,82,0.1)",TRIM:"rgba(255,112,67,0.1)",
                       ADD:"rgba(0,230,118,0.1)",HOLD:"rgba(0,212,255,0.08)",REVIEW:"rgba(255,202,40,0.1)"};
  const SC = {
    Technology:"#00d4ff", Financials:"#00e676","Financial Services":"#00e676",
    Healthcare:"#ff6b9d", Energy:"#ffca28", Industrials:"#ff7043",
    "Consumer Cyclical":"#ab47bc","Consumer Defensive":"#7e57c2",
    "Communication Services":"#26c6da", Materials:"#66bb6a","Basic Materials":"#66bb6a",
    "Real Estate":"#ffa726", Utilities:"#8d6e63", ETF:"#78909c", Other:"#546e7a",
  };
  const SI = {
    Technology:"💻",Financials:"🏦","Financial Services":"🏦",Healthcare:"⚕️",
    Energy:"⚡",Industrials:"🏭","Consumer Cyclical":"🛍️","Consumer Defensive":"🛒",
    "Communication Services":"📡",Materials:"⛏️","Basic Materials":"⛏️",
    "Real Estate":"🏢",Utilities:"🔌",ETF:"📊",Other:"📈",
  };
  const normSector = s => ({"Basic Materials":"Materials","Asset Management":"Financial Services","Banks—Diversified":"Financials"}[s]||s||"Other");

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div style={{padding:"0 16px",overflowX:"hidden",boxSizing:"border-box"}}>

      {/* Header */}
      <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:14}}>
        <AppLogo size={32}/>
        <div>
          <div style={{fontSize:20,fontWeight:900,color:C.text,letterSpacing:"-0.02em",lineHeight:1}}>
            Portfolio Optimizer
          </div>
          <div style={{fontSize:10,color:C.muted,marginTop:2}}>
            Analyze holdings · get rebalancing recommendations
          </div>
          <div style={{fontSize:10,color:C.muted,marginTop:2,display:"flex",alignItems:"center",gap:4}}>
            Set core holdings using
            <span style={{display:"inline-flex",alignItems:"center",gap:2,
              background:"rgba(255,202,40,0.13)",border:"1px solid rgba(255,202,40,0.3)",
              borderRadius:6,padding:"1px 5px",fontSize:9,fontWeight:700,color:"#ffca28"}}>
              💎 Core
            </span>
            <InfoTip text="Mark a position as a Core Holding to protect it from trim or sell recommendations. The optimizer will keep it at its current weight (or higher) and only suggest adding — never reducing. Ideal for conviction positions you want to hold through volatility." />
          </div>
        </div>
      </div>

      {/* ── Portfolio slots ── */}
      <div style={{marginBottom:14}}>
        <div style={{fontSize:10,fontWeight:700,color:C.muted,letterSpacing:"0.1em",
          textTransform:"uppercase",marginBottom:8}}>Your portfolios</div>
        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
          {portfolios.map(p => (
            <div key={p.id} style={{position:"relative"}}>
              {editingName === p.id ? (
                <div style={{display:"flex",gap:4,alignItems:"center"}}>
                  <input
                    autoFocus
                    value={editNameVal}
                    onChange={e=>setEditNameVal(e.target.value)}
                    onKeyDown={e=>{if(e.key==="Enter")renamePortfolio(p.id,editNameVal.trim()||p.name);if(e.key==="Escape")setEditingName(null);}}
                    style={{fontSize:16,fontWeight:700,background:C.card,border:`1px solid ${C.accent}`,
                      borderRadius:8,padding:"6px 10px",color:C.text,outline:"none",width:120,fontFamily:"inherit"}}
                  />
                  <button onClick={()=>renamePortfolio(p.id,editNameVal.trim()||p.name)}
                    style={{background:C.accent,border:"none",borderRadius:6,padding:"6px 8px",
                      color:C.bg,fontWeight:700,fontSize:11,cursor:"pointer",fontFamily:"inherit"}}>✓</button>
                  <button onClick={()=>setEditingName(null)}
                    style={{background:"rgba(255,255,255,0.08)",border:"none",borderRadius:6,
                      padding:"6px 8px",color:C.muted,fontSize:11,cursor:"pointer",fontFamily:"inherit"}}>✕</button>
                </div>
              ) : (
                <button
                  onClick={()=>{ setActiveId(p.id); setResult(null); }}
                  style={{
                    padding:"8px 14px",borderRadius:10,fontFamily:"inherit",fontSize:12,fontWeight:700,
                    cursor:"pointer",transition:"all 0.15s",
                    background: activeId===p.id ? "rgba(0,212,255,0.15)" : C.surface,
                    color:       activeId===p.id ? C.accent : C.muted,
                    border:      activeId===p.id ? `2px solid ${C.accent}` : `1px solid ${C.border}`,
                    display:"flex",alignItems:"center",gap:6,
                  }}>
                  <span>{p.name}</span>
                  <span style={{fontSize:10,color:activeId===p.id?C.accent:"rgba(255,255,255,0.25)"}}>
                    {p.holdings.length}
                  </span>
                  {/* Edit name */}
                  <span
                    onClick={e=>{e.stopPropagation();setEditingName(p.id);setEditNameVal(p.name);}}
                    style={{fontSize:11,color:"rgba(255,255,255,0.3)",cursor:"pointer",padding:"0 2px"}}
                    title="Rename">✏️</span>
                  {/* Delete */}
                  {portfolios.length > 1 && (
                    <span
                      onClick={e=>{e.stopPropagation();if(window.confirm(`Delete "${p.name}"?`))deletePortfolio(p.id);}}
                      style={{fontSize:11,color:"rgba(255,82,82,0.5)",cursor:"pointer",padding:"0 2px"}}
                      title="Delete">🗑</span>
                  )}
                </button>
              )}
            </div>
          ))}
          {portfolios.length < 4 && (
            <button onClick={createPortfolio} disabled={!loaded} title={!loaded?"Loading your portfolios…":undefined} style={{
              padding:"8px 14px",borderRadius:10,border:`1px dashed ${C.border}`,
              background:"none",color:"rgba(255,255,255,0.25)",fontSize:12,fontWeight:700,
              cursor:loaded?"pointer":"default",opacity:loaded?1:0.4,fontFamily:"inherit",display:"flex",alignItems:"center",gap:4,
            }}>+ New</button>
          )}
          {/* Export / Import */}
          <div style={{display:"flex",gap:5,marginLeft:"auto"}}>
            <button onClick={()=>{
              const data = JSON.stringify(portfolios, null, 2);
              const blob = new Blob([data], {type:"application/json"});
              const url  = URL.createObjectURL(blob);
              const a    = document.createElement("a");
              a.href = url; a.download = "portfolios.json"; a.click();
              URL.revokeObjectURL(url);
            }} title="Export portfolios to file"
              style={{padding:"6px 10px",borderRadius:8,border:`1px solid ${C.border}`,
                background:"none",color:C.muted,fontSize:11,cursor:"pointer",
                fontFamily:"inherit",fontWeight:600}}>
              ↓ Export
            </button>
            <label title="Import portfolios from file"
              style={{padding:"6px 10px",borderRadius:8,border:`1px solid ${C.border}`,
                background:"none",color:C.muted,fontSize:11,cursor:"pointer",
                fontFamily:"inherit",fontWeight:600,display:"inline-block"}}>
              ↑ Import
              <input type="file" accept=".json" style={{display:"none"}}
                onChange={e=>{
                  const file = e.target.files?.[0];
                  if (!file) return;
                  const reader = new FileReader();
                  reader.onload = ev => {
                    try {
                      const parsed = JSON.parse(ev.target.result);
                      if (Array.isArray(parsed) && parsed.length > 0) {
                        setPortfolios(parsed);
                        setActiveId(parsed[0].id);
                      } else { alert("Invalid portfolio file."); }
                    } catch { alert("Could not read file."); }
                  };
                  reader.readAsText(file);
                  e.target.value = "";
                }}/>
            </label>
          </div>
        </div>
      </div>

      {/* ── Still loading from Supabase ── */}
      {!loaded && portfolios.length === 0 && (
        <div style={{textAlign:"center",padding:"32px 16px",color:C.muted,fontSize:13}}>
          <div style={{fontSize:32,marginBottom:12}}>⏳</div>
          Loading your portfolios…
        </div>
      )}

      {/* ── No portfolio yet (only after load completes) ── */}
      {loaded && portfolios.length === 0 && (
        <div style={{textAlign:"center",padding:"32px 16px",color:C.muted,fontSize:13}}>
          <div style={{fontSize:32,marginBottom:12}}>📁</div>
          Tap <strong style={{color:C.accent}}>+ New</strong> to create your first portfolio
        </div>
      )}

      {/* ── Active portfolio ── */}
      {activePortfolio && (
        <>
          {/* Holdings input card */}
          <div style={{background:C.surface,borderRadius:14,padding:"14px",
            border:`1px solid ${C.border}`,marginBottom:14}}>
            <div style={{fontSize:10,fontWeight:700,color:C.muted,letterSpacing:"0.1em",
              textTransform:"uppercase",marginBottom:10}}>
              {activePortfolio.name} · holdings
            </div>

            {/* Existing holdings */}
            {holdings.length > 0 && (
              <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:12}}>
                {holdings.map(h=>(
                  <div key={h.ticker} style={{background:C.card,borderRadius:10,
                    padding:"10px 12px",border:`1px solid ${C.border}`}}>
                    {/* Top row */}
                    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
                      <TickerLogo ticker={h.ticker} size={22}/>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:14,fontWeight:800,color:C.text,fontFamily:"monospace"}}>
                          {h.ticker}
                        </div>
                        {h.shortName && h.shortName !== h.ticker && (
                          <div style={{fontSize:10,color:C.muted,
                            overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                            {h.shortName}
                          </div>
                        )}
                      </div>
                      <button onClick={()=>removeHolding(h.ticker)} style={{
                        width:22,height:22,borderRadius:"50%",border:"none",flexShrink:0,
                        background:"rgba(255,82,82,0.15)",color:"#ff5252",cursor:"pointer",
                        fontSize:13,display:"flex",alignItems:"center",justifyContent:"center",
                        fontFamily:"inherit"}}>×</button>
                    </div>
                    {/* Controls row */}
                    <div style={{display:"flex",gap:6,alignItems:"center"}}>
                      <div style={{display:"flex",alignItems:"center",gap:4,
                        background:C.surface,borderRadius:8,padding:"4px 6px",
                        border:`1px solid ${C.border}`,flex:"0 0 auto"}}>
                        <button onClick={()=>updateQty(h.ticker,-1)} style={{
                          width:20,height:20,borderRadius:"50%",border:"none",
                          background:"rgba(255,255,255,0.07)",color:C.muted,cursor:"pointer",
                          fontSize:14,display:"flex",alignItems:"center",justifyContent:"center",
                          fontFamily:"inherit",flexShrink:0}}>−</button>
                        <input type="number" min="0.01" step="1" value={h.quantity}
                          onChange={e=>updateField(h.ticker,"quantity",e.target.value)}
                          style={{width:48,background:"none",border:"none",color:C.text,
                            fontSize:16,fontFamily:"monospace",textAlign:"center",outline:"none"}}/>
                        <button onClick={()=>updateQty(h.ticker,1)} style={{
                          width:20,height:20,borderRadius:"50%",border:"none",
                          background:"rgba(255,255,255,0.07)",color:C.muted,cursor:"pointer",
                          fontSize:14,display:"flex",alignItems:"center",justifyContent:"center",
                          fontFamily:"inherit",flexShrink:0}}>+</button>
                      </div>
                      <div style={{display:"flex",alignItems:"center",gap:3,
                        background:C.surface,borderRadius:8,padding:"4px 8px",
                        border:`1px solid ${C.border}`,flex:1,minWidth:0}}>
                        <span style={{fontSize:11,color:"rgba(255,255,255,0.3)",flexShrink:0}}>@$</span>
                        <input type="number" min="0" step="0.01" value={h.avg_cost||""}
                          onChange={e=>updateField(h.ticker,"avg_cost",e.target.value||"0")}
                          placeholder="avg cost"
                          style={{flex:1,background:"none",border:"none",color:C.text,
                            fontSize:16,fontFamily:"monospace",outline:"none",minWidth:0,width:"100%"}}/>
                      </div>
                      {/* Core holding toggle */}
                      <button
                        onClick={()=>updateField(h.ticker,"core_holding",!h.core_holding)}
                        title={h.core_holding ? "Core holding — trim/exit suggestions ignored" : "Mark as core holding"}
                        style={{background:h.core_holding?"rgba(255,202,40,0.15)":"none",
                          border:`1px solid ${h.core_holding?"rgba(255,202,40,0.5)":C.border}`,
                          borderRadius:8,padding:"4px 8px",cursor:"pointer",
                          fontSize:14,lineHeight:1,flexShrink:0}}>
                        💎
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Add new holding */}
            <div style={{display:"flex",flexDirection:"column",gap:8,
              background:C.card,borderRadius:10,padding:"10px",border:`1px solid ${C.border}`}}>
              <div style={{fontSize:10,color:C.muted,letterSpacing:"0.06em",
                textTransform:"uppercase",marginBottom:2}}>Add holding</div>
              <TickerSearch value={newTicker} onChange={setNewTicker}
                onSelect={(t,name)=>{setNewTicker(t);setNewName(name||"");}}
                placeholder="Ticker — e.g. AAPL or SHOP.TO" size="sm" token={token}/>
              <input type="number" min="0.01" step="1" value={newQty}
                onChange={e=>setNewQty(e.target.value)} placeholder="Quantity (shares)"
                style={{width:"100%",background:C.surface,border:`1px solid ${C.border}`,
                  borderRadius:8,padding:"9px 12px",color:C.text,fontSize:16,
                  fontFamily:"monospace",outline:"none",boxSizing:"border-box"}}/>
              <div style={{display:"flex",gap:8,alignItems:"center"}}>
                <input type="number" min="0" step="0.01" value={newCost}
                  onChange={e=>setNewCost(e.target.value)} placeholder="Avg cost per share (optional)"
                  style={{flex:1,background:C.surface,border:`1px solid ${C.border}`,
                    borderRadius:8,padding:"9px 12px",color:C.text,fontSize:16,
                    fontFamily:"monospace",outline:"none"}}/>
                <button onClick={addHolding}
                  disabled={!newTicker.trim()||!newQty||parseFloat(newQty)<=0}
                  style={{
                    width:40,height:40,borderRadius:"50%",border:"none",flexShrink:0,
                    background:newTicker.trim()&&parseFloat(newQty)>0?C.accent:"rgba(255,255,255,0.1)",
                    color:newTicker.trim()&&parseFloat(newQty)>0?C.bg:"rgba(255,255,255,0.3)",
                    fontSize:22,fontWeight:700,cursor:newTicker.trim()?"pointer":"default",
                    display:"flex",alignItems:"center",justifyContent:"center",
                    fontFamily:"inherit",transition:"all 0.2s"}}>+</button>
              </div>
              <div style={{fontSize:10,color:"rgba(255,255,255,0.25)",lineHeight:1.5}}>
                Average cost calculates P&L and informs exit recommendations
              </div>
            </div>

            {/* Cash to invest */}
            <div style={{display:"flex",alignItems:"center",gap:10,marginTop:10,
              padding:"10px 12px",background:C.card,borderRadius:10,
              border:`1px solid ${C.border}`}}>
              <div style={{flex:1}}>
                <div style={{fontSize:10,fontWeight:700,color:C.muted,
                  letterSpacing:"0.06em",textTransform:"uppercase",marginBottom:4}}>
                  Additional cash to invest
                </div>
                <div style={{display:"flex",alignItems:"center",gap:6}}>
                  <span style={{fontSize:14,color:C.muted,fontWeight:700}}>$</span>
                  <input
                    type="number" min="0" step="100"
                    value={cashToInvest}
                    onChange={e=>setCashToInvest(e.target.value)}
                    placeholder="0"
                    style={{flex:1,background:"none",border:"none",color:C.text,
                      fontSize:16,fontFamily:"monospace",fontWeight:700,
                      outline:"none",width:"100%"}}
                  />
                </div>
              </div>
              {cashToInvest && parseFloat(cashToInvest) > 0 && (
                <div style={{fontSize:10,color:C.muted,textAlign:"right",flexShrink:0}}>
                  Will be included<br/>in deployment<br/>suggestions
                </div>
              )}
            </div>
          </div>

          {/* Optimize button */}
          {holdings.length > 0 && !loading && !result && (
            <div style={{paddingBottom:4}}>
              <div style={{fontSize:11,color:C.muted,marginBottom:8}}>
                {holdings.length} holding{holdings.length!==1?"s":""} · est.{" "}
                {Math.ceil(holdings.length*1.5)}–{Math.ceil(holdings.length*2.5)}s
              </div>
              <button onClick={(e)=>{Haptics.heavy(e.currentTarget);analyze();}} style={{
                width:"100%",padding:"14px",borderRadius:12,border:"none",
                background:`linear-gradient(135deg,${C.accent},#00e5aa)`,
                color:C.bg,fontSize:15,fontWeight:800,cursor:"pointer",fontFamily:"inherit",
                boxShadow:`0 4px 20px rgba(0,212,255,0.25)`}}>
                🔍 Optimize Portfolio
              </button>
            </div>
          )}

          {/* Loading */}
          {loading && (
            <div style={{background:C.surface,borderRadius:14,padding:"20px",
              border:`1px solid ${C.border}`,marginBottom:14,textAlign:"center"}}>
              <div style={{width:32,height:32,border:`3px solid rgba(0,212,255,0.2)`,
                borderTopColor:C.accent,borderRadius:"50%",
                animation:"spin 0.8s linear infinite",margin:"0 auto 12px"}}/>
              <div style={{fontSize:13,color:C.muted}}>{progMsg}</div>
            </div>
          )}

          {error && (
            <div style={{color:C.red,fontSize:12,textAlign:"center",marginBottom:12,
              padding:"10px",background:"rgba(255,82,82,0.08)",borderRadius:8,
              border:"1px solid rgba(255,82,82,0.2)"}}>{error}</div>
          )}

          {/* ── Results ── */}
          {result && (
            <>
              <button onClick={()=>{setResult(null);setDoneActions(new Set());}} style={{
                width:"100%",padding:"9px",borderRadius:10,border:`1px solid ${C.border}`,
                background:"none",color:C.muted,fontSize:12,fontWeight:600,
                cursor:"pointer",fontFamily:"inherit",marginBottom:14}}>
                ↩ Edit holdings &amp; re-analyze
              </button>

              {/* Health card */}
              <div style={{background:C.surface,borderRadius:14,padding:"16px",
                border:`1px solid ${C.border}`,marginBottom:12}}>
                <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:14}}>
                  <ScoreDonut score={result.healthScore} size={64} label="Health"/>
                  <div style={{flex:1}}>
                    <div style={{fontSize:16,fontWeight:800,color:C.text,marginBottom:4}}>
                      Portfolio Health
                    </div>
                    <div style={{fontSize:11,color:C.muted}}>
                      ${result.totalValue.toLocaleString()} · {result.positionCount} positions · β {result.avgBeta}
                    </div>
                    <div style={{display:"flex",gap:6,marginTop:8,flexWrap:"wrap"}}>
                      {Object.entries(result.actionSummary).map(([action,count])=>
                        count > 0 && (
                          <span key={action} style={{
                            fontSize:10,fontWeight:700,borderRadius:20,padding:"2px 8px",
                            color:actionColor[action.toUpperCase()]||C.muted,
                            background:actionBg[action.toUpperCase()]||"rgba(255,255,255,0.05)",
                          }}>
                            {count} {action.toUpperCase()}
                          </span>
                        )
                      )}
                    </div>
                  </div>
                </div>

                {/* Sector bar + legend (same as builder) */}
                {Object.keys(result.sectorBreakdown).length > 0 && (
                  <>
                    <div style={{fontSize:10,color:C.muted,marginBottom:6,
                      letterSpacing:"0.08em",textTransform:"uppercase"}}>Sector allocation</div>
                    <div style={{height:10,borderRadius:5,overflow:"hidden",display:"flex",marginBottom:8}}>
                      {Object.entries(result.sectorBreakdown).sort((a,b)=>b[1]-a[1]).map(([sec,pct])=>(
                        <div key={sec} title={`${normSector(sec)}: ${pct}%`} style={{
                          flex:pct,background:SC[normSector(sec)]||SC[sec]||"#546e7a",transition:"flex 0.5s"}}/>
                      ))}
                    </div>
                    <div style={{display:"flex",flexWrap:"wrap",gap:"6px 12px",marginBottom:4}}>
                      {Object.entries(result.sectorBreakdown).sort((a,b)=>b[1]-a[1]).map(([sec,pct])=>{
                        const ns = normSector(sec);
                        const overweight = pct > (result.maxSectorPct||30);
                        return (
                          <div key={sec} style={{display:"flex",alignItems:"center",gap:4}}>
                            <div style={{width:8,height:8,borderRadius:2,flexShrink:0,
                              background:SC[ns]||SC[sec]||"#546e7a",
                              outline:overweight?"2px solid #ffca28":"none",outlineOffset:1}}/>
                            <span style={{fontSize:10,color:overweight?"#ffca28":C.muted}}>
                              {overweight&&"⚠ "}{SI[ns]||""}{" "}{ns}{" "}
                              <span style={{fontFamily:"monospace",fontWeight:700,
                                color:overweight?"#ffca28":C.text}}>{pct}%</span>
                            </span>
                          </div>
                        );
                      })}
                    </div>
                    {Object.entries(result.sectorBreakdown).some(([,pct])=>pct>(result.maxSectorPct||30)) && (
                      <div style={{fontSize:10,color:"#ffca28",lineHeight:1.5,
                        padding:"6px 10px",borderRadius:8,marginTop:4,
                        background:"rgba(255,202,40,0.08)",border:"1px solid rgba(255,202,40,0.2)"}}>
                        ⚠ One or more sectors exceed your {result.maxSectorPct||30}% cap.
                        Consider trimming overweight positions or adding diversifying stocks.
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Per-position cards — with a Swing / LT / A-Z sort toggle (persisted). */}
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",
                marginBottom:8,gap:8,flexWrap:"wrap"}}>
                <div style={{fontSize:10,fontWeight:700,color:C.muted,letterSpacing:"0.1em",
                  textTransform:"uppercase"}}>Recommendations</div>
                <div style={{display:"flex",alignItems:"center",gap:6}}>
                  <span style={{fontSize:10,color:C.muted,marginRight:2}}>Sort</span>
                  {[["swing","Swing"],["lt","LT"],["alpha","A–Z"]].map(([v,l])=>(
                    <button key={v} onClick={()=>{ setPosSort(v); try { localStorage.setItem("gempulse_optpos_sort", v); } catch(_){} }} style={{
                      padding:"3px 10px",borderRadius:20,fontSize:10,fontWeight:700,
                      border:`1px solid ${posSort===v?C.accent:C.border}`,
                      background:posSort===v?"rgba(0,212,255,0.12)":"none",
                      color:posSort===v?C.accent:C.muted,cursor:"pointer",
                      fontFamily:"inherit",transition:"all 0.15s"}}>{l}</button>
                  ))}
                </div>
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:10,marginBottom:16}}>
                {[...result.positions].sort((x,y)=>
                    posSort==="alpha" ? String(x.ticker).localeCompare(String(y.ticker))
                  : posSort==="lt"    ? ((y.ltScore||0)-(x.ltScore||0)) || String(x.ticker).localeCompare(String(y.ticker))
                  :                     ((y.swingScore||0)-(x.swingScore||0)) || String(x.ticker).localeCompare(String(y.ticker))
                ).map(pos=>{
                  const ns = normSector(pos.sector);
                  const sectorOverweight = pos.trimReason === "sector_overweight";
                  // Dollar and share calculations for target weight
                  const targetDollar = pos.price > 0 ? Math.round(result.totalValue * pos.targetPct / 100) : null;
                  const targetShares = (targetDollar && pos.price > 0) ? Math.floor(targetDollar / pos.price) : null;
                  const deltaDollar  = targetDollar != null ? targetDollar - pos.marketValue : null;
                  const deltaShares  = (pos.price > 0 && deltaDollar != null) ? Math.floor(Math.abs(deltaDollar) / pos.price) : null;
                  // Cost base and P&L $
                  const h = holdings.find(hh => hh.ticker === pos.ticker);
                  const costBase = h && h.avg_cost > 0 ? h.avg_cost * h.quantity : null;
                  const plDollar = (costBase && pos.price > 0)
                    ? Math.round(pos.marketValue - costBase) : null;

                  return (
                  <div key={pos.ticker}
                    onClick={()=>onSelectTicker&&onSelectTicker(pos.ticker)}
                    style={{background:C.surface,borderRadius:14,padding:"14px",
                      border:`2px solid ${actionColor[pos.action]}22`,
                      cursor:"pointer",transition:"border-color 0.2s"}}>

                    {/* Row 1: logo + ticker + action + badges */}
                    <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:6}}>
                      <TickerLogo ticker={pos.ticker} size={28}/>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                          <span style={{fontSize:15,fontWeight:800,color:C.text,
                            fontFamily:"monospace"}}>{pos.ticker}</span>
                          <span style={{fontSize:11,fontWeight:800,borderRadius:6,
                            padding:"2px 8px",color:actionColor[pos.action],
                            background:actionBg[pos.action]}}>{pos.action}</span>
                          {pos.urgency==="high" && (
                            <span style={{fontSize:9,fontWeight:700,borderRadius:4,
                              padding:"1px 5px",background:"rgba(255,82,82,0.15)",
                              color:"#ff5252",letterSpacing:"0.04em"}}>URGENT</span>
                          )}
                          {pos.urgency==="medium" && (
                            <span style={{fontSize:9,fontWeight:700,borderRadius:4,
                              padding:"1px 5px",background:"rgba(255,202,40,0.12)",
                              color:"#ffca28",letterSpacing:"0.04em"}}>REVIEW</span>
                          )}
                          {pos.isETF && (
                            <span style={{fontSize:9,fontWeight:700,borderRadius:20,
                              padding:"1px 7px",background:"rgba(105,240,174,0.12)",
                              color:"#69f0ae",letterSpacing:"0.04em"}}>ETF</span>
                          )}
                          {pos.coreHolding && (
                            <span style={{fontSize:9,fontWeight:700,borderRadius:20,
                              padding:"2px 8px",letterSpacing:"0.03em",
                              background:"rgba(255,202,40,0.13)",color:"#ffca28",
                              display:"inline-flex",alignItems:"center",gap:3}}>
                              💎 Core Holding
                            </span>
                          )}
                        </div>
                        {/* Company name */}
                        <div style={{fontSize:11,color:"rgba(255,255,255,0.45)",marginTop:1,
                          overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                          {pos.shortName}
                        </div>
                        {/* Sector tag + overweight caution */}
                        <div style={{display:"flex",alignItems:"center",gap:5,marginTop:4,flexWrap:"wrap"}}>
                          <div style={{display:"flex",alignItems:"center",gap:3,
                            background:`${SC[ns]||"#546e7a"}18`,borderRadius:4,
                            padding:"1px 6px",border:`1px solid ${SC[ns]||"#546e7a"}30`}}>
                            <span style={{fontSize:10}}>{SI[ns]||"📈"}</span>
                            <span style={{fontSize:10,fontWeight:600,color:SC[ns]||"#546e7a"}}>
                              {ns}
                            </span>
                          </div>
                          {sectorOverweight && (
                            <span style={{fontSize:10,fontWeight:700,color:"#ffca28"}}>
                              ⚠ overweight ({_fmtPct1(pos.sectorPct)}%)
                            </span>
                          )}
                        </div>
                      </div>
                      {/* Current weight */}
                      <div style={{textAlign:"right",flexShrink:0}}>
                        <div style={{fontSize:14,fontWeight:700,color:C.text,
                          fontFamily:"monospace"}}>{_fmtPct1(pos.currentPct)}%</div>
                        <div style={{fontSize:10,color:C.muted,fontFamily:"monospace"}}>
                          ${pos.marketValue.toLocaleString()}
                        </div>
                      </div>
                    </div>

                    {/* Target weight row — with shares + $ translation */}
                    {pos.action !== "HOLD" && pos.action !== "REVIEW" && (
                      <div style={{borderRadius:8,padding:"8px 10px",marginBottom:8,
                        background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.06)"}}>
                        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
                          <span style={{fontSize:10,color:C.muted}}>Target weight</span>
                          <span style={{fontSize:12,fontWeight:700,fontFamily:"monospace",
                            color:C.muted,textDecoration:"line-through"}}>{_fmtPct1(pos.currentPct)}%</span>
                          <span style={{fontSize:11,color:C.muted}}>→</span>
                          <span style={{fontSize:13,fontWeight:800,fontFamily:"monospace",
                            color:actionColor[pos.action]}}>{_fmtPct1(pos.targetPct)}%</span>
                        </div>
                        {targetShares != null && (
                          <div style={{display:"flex",gap:12,flexWrap:"wrap"}}>
                            <div>
                              <div style={{fontSize:9,color:C.muted,marginBottom:1}}>Target value</div>
                              <div style={{fontSize:12,fontWeight:700,fontFamily:"monospace",
                                color:C.text}}>${targetDollar.toLocaleString()}</div>
                            </div>
                            <div>
                              <div style={{fontSize:9,color:C.muted,marginBottom:1}}>Target shares</div>
                              <div style={{fontSize:12,fontWeight:700,fontFamily:"monospace",
                                color:C.text}}>{targetShares.toLocaleString()}</div>
                            </div>
                            {deltaDollar != null && (
                              <div>
                                <div style={{fontSize:9,color:C.muted,marginBottom:1}}>
                                  {deltaDollar >= 0 ? "Buy" : "Sell"}
                                </div>
                                <div style={{fontSize:12,fontWeight:700,fontFamily:"monospace",
                                  color:deltaDollar>=0?C.green:C.red}}>
                                  {deltaDollar>=0?"+":"-"}${Math.abs(deltaDollar).toLocaleString()}
                                  {" "}({deltaShares} sh)
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* P&L row */}
                    {pos.price > 0 && (
                      <div style={{display:"flex",gap:10,flexWrap:"wrap",marginBottom:8,
                        padding:"6px 10px",borderRadius:8,background:"rgba(255,255,255,0.02)"}}>
                        <div>
                          <div style={{fontSize:9,color:C.muted}}>Market price</div>
                          <div style={{fontSize:12,fontWeight:700,fontFamily:"monospace",color:C.text}}>
                            ${pos.price.toLocaleString()}
                          </div>
                        </div>
                        {costBase != null && (
                          <>
                            <div>
                              <div style={{fontSize:9,color:C.muted}}>Cost base</div>
                              <div style={{fontSize:12,fontWeight:700,fontFamily:"monospace",color:C.muted}}>
                                ${Math.round(costBase).toLocaleString()}
                              </div>
                            </div>
                            <div>
                              <div style={{fontSize:9,color:C.muted}}>P&L</div>
                              <div style={{fontSize:12,fontWeight:700,fontFamily:"monospace",
                                color:plDollar>=0?C.green:C.red}}>
                                {plDollar>=0?"+":"-"}${Math.abs(plDollar).toLocaleString()}
                                {pos.plPct!=null&&(
                                  <span style={{fontSize:10,marginLeft:4,opacity:0.8}}>
                                    ({pos.plPct>=0?"+":""}{pos.plPct}%)
                                  </span>
                                )}
                              </div>
                            </div>
                          </>
                        )}
                        {pos.changePct != null && (
                          <div>
                            <div style={{fontSize:9,color:C.muted}}>Today</div>
                            <div style={{fontSize:12,fontWeight:700,fontFamily:"monospace",
                              color:pos.changePct>=0?C.green:C.red}}>
                              {pos.changePct>=0?"+":""}{pos.changePct}%
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Rationale */}
                    <div style={{fontSize:11,color:"rgba(255,255,255,0.45)",
                      lineHeight:1.6,marginBottom:8}}>{pos.rationale}</div>

                    {/* Score row */}
                    {pos.swingScore != null && (
                      <div style={{display:"flex",gap:12,alignItems:"center",flexWrap:"wrap"}}>
                        <div style={{display:"flex",alignItems:"center",gap:4}}>
                          <SwingIcon size={11} color={C.muted}/>
                          <span style={{fontSize:10,color:C.muted,fontFamily:"monospace"}}>{pos.swingScore}</span>
                        </div>
                        {universeData && universeData[(pos.ticker||"").toUpperCase()]?.pullbackFlag
                          ? <PullbackFlag/>
                          : (universeData && universeData[(pos.ticker||"").toUpperCase()]?.profitTakeFlag && <ExtendedFlag/>)}
                        {universeData && universeData[(pos.ticker||"").toUpperCase()]?.bounceFlag && <BounceFlag/>}
                        <div style={{display:"flex",alignItems:"center",gap:4}}>
                          <LTIcon size={11} color={C.muted}/>
                          <span style={{fontSize:10,color:C.muted,fontFamily:"monospace"}}>{pos.ltScore}</span>
                        </div>
                        {pos.horizonReturn != null && (
                          <span style={{fontSize:10,fontFamily:"monospace",marginLeft:"auto",
                            color:pos.horizonReturn>=0?C.green:C.red}}>
                            {pos.horizonReturn>=0?"+":""}{pos.horizonReturn}%
                            {" "}{HORIZONS.find(h=>h.key===horizon)?.label?.toLowerCase()}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Confirm button — marks the action done and fires haptic */}
                    {pos.action !== "HOLD" && pos.action !== "REVIEW" && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          const wasDone = doneActions.has(pos.ticker);
                          if (!wasDone) {
                            pos.action === "ADD"
                              ? Haptics.heavy(e.currentTarget)
                              : Haptics.medium(e.currentTarget);
                          } else {
                            Haptics.light(e.currentTarget);
                          }
                          setDoneActions(prev => {
                            const next = new Set(prev);
                            wasDone ? next.delete(pos.ticker) : next.add(pos.ticker);
                            return next;
                          });
                        }}
                        style={{
                          marginTop:10,width:"100%",padding:"7px",borderRadius:8,
                          border:"none",cursor:"pointer",fontFamily:"inherit",
                          fontSize:11,fontWeight:700,transition:"all 0.15s",
                          background: doneActions.has(pos.ticker)
                            ? "rgba(0,230,118,0.12)" : actionBg[pos.action],
                          color: doneActions.has(pos.ticker)
                            ? C.green : actionColor[pos.action],
                        }}>
                        {doneActions.has(pos.ticker)
                          ? "✓ Done — tap to undo"
                          : `Confirm ${pos.action}`}
                      </button>
                    )}
                  </div>
                  );
                })}
              </div>

              {/* Deploy section — freed capital + cash (also rendered for deficit warnings) */}
              {(result.builderPicks?.length > 0 || result.totalDeployable > 0 || result.needsExtraCash) && (
                <div style={{background:C.surface,borderRadius:14,padding:"14px",
                  border:`1px solid rgba(0,212,255,0.2)`,marginBottom:16}}>

                  {/* Header with total deployable */}
                  <div style={{marginBottom:12}}>
                    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
                      <div style={{fontSize:12,fontWeight:800,color:C.accent,
                        letterSpacing:"0.04em",textTransform:"uppercase"}}>
                        💎 Deploy capital
                      </div>
                      {result.totalDeployable > 0 && (
                        <div style={{fontSize:18,fontWeight:800,color:C.green,fontFamily:"monospace"}}>
                          ${result.totalDeployable.toLocaleString()}
                        </div>
                      )}
                    </div>

                    {/* Deficit warning — ADDs exceed trims + extra cash */}
                    {result.needsExtraCash && result.deficit > 0 && (
                      <div style={{fontSize:11,color:"#ffca28",lineHeight:1.5,
                        padding:"8px 10px",borderRadius:8,marginBottom:8,
                        background:"rgba(255,202,40,0.08)",border:"1px solid rgba(255,202,40,0.25)"}}>
                        ⚠ The ADD suggestions above need{" "}
                        <span style={{fontFamily:"monospace",fontWeight:700}}>
                          ${result.deficit.toLocaleString()}
                        </span>{" "}
                        more than your trims/exits free up. Add cash or skip some ADDs.
                      </div>
                    )}

                    {/* Capital breakdown */}
                    {(result.dryPowder > 0 || result.extraCash > 0) && (
                      <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:8}}>
                        {result.dryPowder > 0 && (
                          <div style={{flex:1,background:"rgba(255,112,67,0.08)",
                            borderRadius:8,padding:"6px 10px",border:"1px solid rgba(255,112,67,0.2)"}}>
                            <div style={{fontSize:9,color:"#ff7043",marginBottom:2}}>Net freed (after ADDs)</div>
                            <div style={{fontSize:13,fontWeight:700,color:"#ff7043",fontFamily:"monospace"}}>
                              ${result.dryPowder.toLocaleString()}
                            </div>
                          </div>
                        )}
                        {result.extraCash > 0 && (
                          <div style={{flex:1,background:"rgba(0,230,118,0.08)",
                            borderRadius:8,padding:"6px 10px",border:"1px solid rgba(0,230,118,0.2)"}}>
                            <div style={{fontSize:9,color:C.green,marginBottom:2}}>New cash</div>
                            <div style={{fontSize:13,fontWeight:700,color:C.green,fontFamily:"monospace"}}>
                              ${result.extraCash.toLocaleString()}
                            </div>
                          </div>
                        )}
                        {/* Recommended cash reserve based on risk profile */}
                        {result.totalDeployable > 0 && (
                          <div style={{flex:1,background:"rgba(255,255,255,0.04)",
                            borderRadius:8,padding:"6px 10px",border:`1px solid ${C.border}`}}>
                            <div style={{fontSize:9,color:C.muted,marginBottom:2}}>Keep as reserve</div>
                            <div style={{fontSize:13,fontWeight:700,color:C.muted,fontFamily:"monospace"}}>
                              ${Math.round(result.totalDeployable *
                                (result.profile==="conservative"?0.20:result.profile==="aggressive"?0.05:0.10)
                              ).toLocaleString()}
                              <span style={{fontSize:9,marginLeft:4,opacity:0.6}}>
                                ({result.profile==="conservative"?"20%":result.profile==="aggressive"?"5%":"10%"})
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    <div style={{fontSize:10,color:"rgba(255,255,255,0.3)",lineHeight:1.5}}>
                      Ranked by combined Swing + Long-term score. Suggested amounts split
                      after reserving {result.profile==="conservative"?"20%":result.profile==="aggressive"?"5%":"10%"} cash.
                    </div>
                  </div>

                  {/* Pick suggestions */}
                  {result.builderPicks?.length > 0 && (() => {
                    const reservePct = result.profile==="conservative"?0.20:result.profile==="aggressive"?0.05:0.10;
                    const deployable  = Math.round((result.totalDeployable||0) * (1-reservePct));
                    const perPick     = deployable > 0 ? Math.round(deployable / result.builderPicks.length) : null;
                    return (
                      <div style={{display:"flex",flexDirection:"column",gap:8}}>
                        {result.builderPicks.map((p,i)=>{
                          const ns2 = normSector(p.sector);
                          return (
                            <div key={p.ticker} onClick={()=>onSelectTicker&&onSelectTicker(p.ticker)}
                              style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",
                                background:C.card,borderRadius:10,border:`1px solid ${C.border}`,cursor:"pointer"}}>
                              <div style={{width:20,height:20,borderRadius:"50%",flexShrink:0,
                                background:"rgba(0,212,255,0.1)",border:`1px solid rgba(0,212,255,0.3)`,
                                display:"flex",alignItems:"center",justifyContent:"center",
                                fontSize:10,fontWeight:800,color:C.accent}}>{i+1}</div>
                              <TickerLogo ticker={p.ticker} size={24}/>
                              <div style={{flex:1,minWidth:0}}>
                                <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                                  <span style={{fontSize:13,fontWeight:800,color:C.text,
                                    fontFamily:"monospace"}}>{p.ticker}</span>
                                  <SignalPill type={p.swingType} size="sm"/>
                                </div>
                                <div style={{fontSize:10,color:"rgba(255,255,255,0.35)",marginTop:1,
                                  overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                                  {p.shortName}
                                </div>
                                <div style={{display:"flex",alignItems:"center",gap:3,marginTop:3}}>
                                  <div style={{display:"flex",alignItems:"center",gap:3,
                                    background:`${SC[ns2]||"#546e7a"}18`,borderRadius:4,
                                    padding:"1px 6px",border:`1px solid ${SC[ns2]||"#546e7a"}30`}}>
                                    <span style={{fontSize:9}}>{SI[ns2]||"📈"}</span>
                                    <span style={{fontSize:9,fontWeight:600,color:SC[ns2]||C.muted}}>{ns2}</span>
                                  </div>
                                </div>
                              </div>
                              <div style={{textAlign:"right",flexShrink:0}}>
                                {perPick && (
                                  <div style={{fontSize:12,fontWeight:700,color:C.green,
                                    fontFamily:"monospace"}}>~${perPick.toLocaleString()}</div>
                                )}
                                <div style={{fontSize:11,fontWeight:700,color:C.accent,
                                  fontFamily:"monospace"}}>{_fmtScore(p.combinedScore)}/100</div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* Diversification picks section */}
              {result.diversifyPicks?.length > 0 && (
                <div style={{background:C.surface,borderRadius:14,padding:"14px",
                  border:"1px solid rgba(102,187,106,0.25)",marginBottom:16}}>
                  <div style={{marginBottom:10}}>
                    <div style={{fontSize:12,fontWeight:800,color:"#66bb6a",
                      letterSpacing:"0.04em",textTransform:"uppercase",marginBottom:4}}>
                      🌿 Diversification plays
                    </div>
                    <div style={{fontSize:10,color:"rgba(255,255,255,0.3)",lineHeight:1.5}}>
                      Best-scoring picks from sectors underrepresented in your portfolio
                      (&lt;10% weight). Prioritises balance over pure return.
                    </div>
                  </div>
                  <div style={{display:"flex",flexDirection:"column",gap:8}}>
                    {result.diversifyPicks.map((p,i)=>{
                      const ns3 = normSector(p.sector);
                      const reservePct = result.profile==="conservative"?0.20:result.profile==="aggressive"?0.05:0.10;
                      const divDeployable = Math.round((result.totalDeployable||0) * (1-reservePct));
                      const perPick = divDeployable > 0 && result.diversifyPicks.length > 0
                        ? Math.round(divDeployable / result.diversifyPicks.length) : null;
                      return (
                        <div key={p.ticker} onClick={()=>onSelectTicker&&onSelectTicker(p.ticker)}
                          style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",
                            background:C.card,borderRadius:10,border:`1px solid ${C.border}`,cursor:"pointer"}}>
                          <div style={{width:20,height:20,borderRadius:"50%",flexShrink:0,
                            background:"rgba(102,187,106,0.15)",border:"1px solid rgba(102,187,106,0.4)",
                            display:"flex",alignItems:"center",justifyContent:"center",
                            fontSize:10,fontWeight:800,color:"#66bb6a"}}>{i+1}</div>
                          <TickerLogo ticker={p.ticker} size={24}/>
                          <div style={{flex:1,minWidth:0}}>
                            <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                              <span style={{fontSize:13,fontWeight:800,color:C.text,
                                fontFamily:"monospace"}}>{p.ticker}</span>
                              <SignalPill type={p.swingType} size="sm"/>
                            </div>
                            <div style={{fontSize:10,color:"rgba(255,255,255,0.35)",marginTop:1,
                              overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                              {p.shortName}
                            </div>
                            <div style={{display:"flex",alignItems:"center",gap:4,marginTop:3,flexWrap:"wrap"}}>
                              <div style={{display:"flex",alignItems:"center",gap:3,
                                background:`${SC[ns3]||"#546e7a"}18`,borderRadius:4,
                                padding:"1px 6px",border:`1px solid ${SC[ns3]||"#546e7a"}30`}}>
                                <span style={{fontSize:9}}>{SI[ns3]||"📈"}</span>
                                <span style={{fontSize:9,fontWeight:600,color:SC[ns3]||"#546e7a"}}>{ns3}</span>
                              </div>
                              {p.reason && (
                                <span style={{fontSize:9,color:"#66bb6a",opacity:0.8}}>{p.reason}</span>
                              )}
                            </div>
                          </div>
                          <div style={{textAlign:"right",flexShrink:0}}>
                            {perPick && (
                              <div style={{fontSize:12,fontWeight:700,color:"#66bb6a",
                                fontFamily:"monospace"}}>~${perPick.toLocaleString()}</div>
                            )}
                            <div style={{fontSize:11,fontWeight:700,color:C.accent,
                              fontFamily:"monospace"}}>{_fmtScore(p.combinedScore)}/100</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Disclaimer */}
              <div style={{fontSize:10,color:"rgba(255,255,255,0.2)",lineHeight:1.7,
                padding:"10px 12px",background:"rgba(255,255,255,0.02)",
                borderRadius:10,border:"1px solid rgba(255,255,255,0.05)",marginBottom:8}}>
                ⚠️ Educational tool only. Not financial advice. Verify all recommendations
                with your own research and consult a qualified advisor before acting.
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}



// --- Tweet Generator canvas card helper --------------------------------------
function _loadImg(src) {
  return new Promise(resolve => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload  = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

async function drawScoreCard(canvas, data) {
  const { ticker, shortName, swingScore, ltScore, price, changePct } = data;
  const W = 1080, H = 680;
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext("2d");

  // Load banner, candle bg + ticker logo.
  //
  // Canvas logo strategy:
  //   1. Backend /logo/{ticker}?fresh=1 — sequential CDN cascade (Parqet browser UA
  //      → FMP → SA → TV → Polygon). Bypasses disk cache. Always CORS-safe via proxy.
  //   2. Direct Parqet fallback — in case the API is unreachable (rare).
  //   3. Direct FMP fallback — last resort.
  //
  // Known limitation: Parqet's CDN doesn't set CORS headers so the canvas can't
  // load Parqet directly (crossOrigin=anonymous required). The backend proxy
  // bridges this by fetching Parqet server-side with a browser UA and returning
  // the image with ACAO headers. For tickers Parqet doesn't have (e.g. VRT),
  // FMP's named-placeholder logo is the fallback — accepted, low-priority.
  const clean = ticker.replace(/\.(TO|V|TSX)$/i, "");
  const loadLogo = async () => {
    for (const src of [
      `${API}/logo/${clean}?fresh=1`,
      `https://assets.parqet.com/logos/symbol/${clean}?format=png`,
      `https://financialmodelingprep.com/image-stock/${clean}.png`,
    ]) {
      const img = await _loadImg(src);
      if (img) return img;
    }
    return null;
  };
  // Cache-bust static assets so a fresh deploy isn't served the old image from
  // disk/PWA cache. Bump the suffix whenever header_cropped2.jpg / candle-bg.jpg
  // is replaced.
  const ASSET_V = "v9_86";
  const [banner, candle, logo] = await Promise.all([
    _loadImg(`/header_cropped2.jpg?${ASSET_V}`),
    _loadImg(`/candle-bg.jpg?${ASSET_V}`),
    loadLogo(),
  ]);

  // Background — solid dark base
  ctx.fillStyle = "#080c14";
  ctx.fillRect(0, 0, W, H);

  // Candle texture tiled at low opacity
  if (candle) {
    ctx.save();
    ctx.globalAlpha = 0.22;
    const cw = candle.naturalWidth, ch = candle.naturalHeight;
    for (let x = 0; x < W; x += cw)
      for (let y = 0; y < H; y += ch)
        ctx.drawImage(candle, x, y, cw, ch);
    ctx.restore();
  }

  // Banner — scale to full canvas width preserving the natural aspect ratio so
  // the entire banner (logo + branding) is visible. No top-crop. If the image
  // failed to load, fall back to a 150 px gradient band.
  let BANNER_H = 150;
  if (banner && banner.naturalWidth > 0) {
    BANNER_H = Math.round(W * banner.naturalHeight / banner.naturalWidth);
    ctx.drawImage(banner,
      0, 0, banner.naturalWidth, banner.naturalHeight,
      0, 0, W, BANNER_H);
  } else {
    const grad = ctx.createLinearGradient(0,0,W,0);
    grad.addColorStop(0,"#00d4ff"); grad.addColorStop(1,"#7b61ff");
    ctx.fillStyle = grad; ctx.fillRect(0, 0, W, BANNER_H);
    ctx.font = "bold 42px system-ui, sans-serif";
    ctx.fillStyle = "#e8eaf0"; ctx.textAlign = "center";
    ctx.fillText("GemPulse", W / 2, BANNER_H / 2 + 14);
  }

  // Card sits below banner with generous side margins so candle bg shows
  const CARD_PAD  = 120;         // left/right breathing room (~11% each side)
  const CARD_TOP  = BANNER_H + 18;
  const CARD_H    = H - CARD_TOP - 40; // 40px gap at bottom for disclaimer
  const CONTENT_Y = CARD_TOP + 20;

  // Dark card panel — narrower than full width
  ctx.save();
  ctx.beginPath();
  const cr = 16;
  const cx0 = CARD_PAD, cy0 = CARD_TOP, cw0 = W - CARD_PAD * 2, ch0 = CARD_H;
  ctx.moveTo(cx0 + cr, cy0);
  ctx.lineTo(cx0 + cw0 - cr, cy0); ctx.quadraticCurveTo(cx0 + cw0, cy0, cx0 + cw0, cy0 + cr);
  ctx.lineTo(cx0 + cw0, cy0 + ch0 - cr); ctx.quadraticCurveTo(cx0 + cw0, cy0 + ch0, cx0 + cw0 - cr, cy0 + ch0);
  ctx.lineTo(cx0 + cr, cy0 + ch0); ctx.quadraticCurveTo(cx0, cy0 + ch0, cx0, cy0 + ch0 - cr);
  ctx.lineTo(cx0, cy0 + cr); ctx.quadraticCurveTo(cx0, cy0, cx0 + cr, cy0);
  ctx.closePath();
  ctx.fillStyle = "rgba(8,12,20,0.65)";
  ctx.fill();
  ctx.strokeStyle = "rgba(0,212,255,0.12)"; ctx.lineWidth = 1;
  ctx.stroke();
  ctx.restore();

  // Ticker logo circle
  const LOGO_R = 36;
  const logoX  = W / 2 - 110;
  const logoY  = CONTENT_Y + LOGO_R;

  if (logo) {
    ctx.save();
    ctx.beginPath();
    ctx.arc(logoX, logoY, LOGO_R, 0, 2 * Math.PI);
    ctx.clip();
    ctx.drawImage(logo, logoX - LOGO_R, logoY - LOGO_R, LOGO_R * 2, LOGO_R * 2);
    ctx.restore();
    // subtle border ring
    ctx.beginPath();
    ctx.arc(logoX, logoY, LOGO_R, 0, 2 * Math.PI);
    ctx.strokeStyle = "rgba(0,212,255,0.3)"; ctx.lineWidth = 2;
    ctx.stroke();
  }

  // Ticker symbol — sits to the right of the logo
  const tickerX = logo ? logoX + LOGO_R + 16 : W / 2;
  ctx.font = "bold 58px system-ui, sans-serif";
  ctx.fillStyle = "#ffffff";
  ctx.textAlign = logo ? "left" : "center";
  ctx.fillText("$" + ticker, tickerX, CONTENT_Y + LOGO_R + 18);

  // Company name — centred under the logo+ticker row
  ctx.font = "20px system-ui, sans-serif";
  ctx.fillStyle = "#5a6a88"; ctx.textAlign = "center";
  ctx.fillText(shortName || ticker, W / 2, CONTENT_Y + LOGO_R * 2 + 30);

  // Price + change
  if (price) {
    const chgColor = (changePct || 0) >= 0 ? "#00e676" : "#ff5252";
    const chgStr   = changePct != null
      ? `  ${changePct >= 0 ? "+" : ""}${changePct.toFixed(2)}%` : "";
    ctx.font = "bold 26px system-ui, sans-serif";
    ctx.fillStyle = "#e8eaf0"; ctx.textAlign = "center";
    ctx.fillText(`$${price.toFixed(2)}`, W / 2 - 50, CONTENT_Y + LOGO_R * 2 + 66);
    ctx.fillStyle = chgColor;
    ctx.fillText(chgStr, W / 2 + 60, CONTENT_Y + LOGO_R * 2 + 66);
  }

  // Donut scores
  function drawDonut(cx, cy, score, label, title, R = 95) {
    const total = 2 * Math.PI;
    const pct   = Math.max(0, Math.min(score, 100)) / 100;
    const color = score >= 72 ? "#00e676" : score >= 58 ? "#69f0ae" : score >= 42 ? "#ffca28" : "#ff5252";
    // Stroke and font sizes scale with radius so the solo (smaller) donut
    // doesn't look like it's missing the inner score text.
    const lw         = Math.round(R * 0.275);   // ~26 for R=95, ~19 for R=70
    const scoreFont  = Math.round(R * 0.45);    // ~42 for R=95, ~32 for R=70
    const labelFont  = Math.round(R * 0.14);    // ~13 for R=95, ~10 for R=70
    const titleFont  = Math.round(R * 0.13);    // ~12 for R=95
    const scoreDy    = Math.round(R * 0.16);    // baseline offset for centred score
    const labelDy    = Math.round(R * 0.38);    // baseline offset for "STRONG BUY"
    const titleDy    = R + 14;                  // baseline offset above the donut

    ctx.beginPath();
    ctx.arc(cx, cy, R, 0, total);
    ctx.strokeStyle = "rgba(255,255,255,0.08)"; ctx.lineWidth = lw;
    ctx.stroke();

    ctx.beginPath();
    ctx.arc(cx, cy, R, -Math.PI / 2, -Math.PI / 2 + pct * total);
    ctx.strokeStyle = color; ctx.lineWidth = lw;
    ctx.lineCap = "round"; ctx.stroke();

    ctx.font = `bold ${scoreFont}px system-ui, sans-serif`;
    ctx.fillStyle = "#ffffff"; ctx.textAlign = "center";
    ctx.fillText(score.toFixed(1), cx, cy + scoreDy);

    ctx.font = `bold ${labelFont}px system-ui, sans-serif`;
    ctx.fillStyle = color;
    ctx.fillText(label.toUpperCase(), cx, cy + labelDy);

    ctx.font = `bold ${titleFont}px system-ui, sans-serif`;
    ctx.fillStyle = "#5a6a88";
    ctx.fillText(title, cx, cy - titleDy);
  }

  const swingLabel = swingScore >= 75 ? "Strong Buy" : swingScore >= 60 ? "Buy" : swingScore >= 45 ? "Watch" : "Pass";
  const ltLabel    = ltScore    >= 75 ? "Strong Buy" : ltScore    >= 60 ? "Buy" : ltScore    >= 45 ? "Watch" : "Pass";

  if (ltScore && ltScore > 0) {
    // Dual donuts — flank the centered price text.
    const R = 72;
    const donutY = CARD_TOP + CARD_H - 105;
    drawDonut(W / 2 - 185, donutY, swingScore, swingLabel, "SWING SCORE", R);
    drawDonut(W / 2 + 185, donutY, ltScore,    ltLabel,    "LONG-TERM",   R);
  } else {
    // Solo mode — centred under the price.
    const R = 55;
    const donutY = CARD_TOP + CARD_H - 85;
    drawDonut(W / 2, donutY, swingScore, swingLabel, "SWING SCORE", R);
  }

  // Bottom disclaimer
  ctx.font = "11px system-ui, sans-serif";
  ctx.fillStyle = "rgba(90,106,136,0.6)"; ctx.textAlign = "center";
  ctx.fillText("⚠ Not investment advice. For educational purposes only. Always conduct your own research.", W / 2, H - 18);

  // Bottom accent bar
  const grad = ctx.createLinearGradient(0,0,W,0);
  grad.addColorStop(0,"#00d4ff"); grad.addColorStop(1,"#7b61ff");
  ctx.fillStyle = grad; ctx.fillRect(0, H - 4, W, 4);
}

// --- Tweet Generator component -----------------------------------------------
const TWEET_TICKER_RE = /^[A-Z]{1,5}(?:\.(?:TO|V|TSX))?$/;

function TweetGenerator({ token, user }) {
  const [candidates,  setCandidates]  = React.useState([]);
  const [loadingC,    setLoadingC]    = React.useState(false);
  const [selected,    setSelected]    = React.useState(null);
  const [tweet,       setTweet]       = React.useState("");
  const [generating,  setGenerating]  = React.useState(false);
  const [copied,      setCopied]      = React.useState(false);
  const [cardData,    setCardData]    = React.useState(null);
  const [customTkr,   setCustomTkr]   = React.useState("");
  const [warning,     setWarning]     = React.useState("");
  const [tweetErr,    setTweetErr]    = React.useState("");
  // P1-1: digest → X thread. `thread` is an array of editable tweet strings.
  const [thread,       setThread]       = React.useState(null);
  const [threadCopied, setThreadCopied] = React.useState(false);
  const [copiedIdx,    setCopiedIdx]    = React.useState(null);
  // P1-2: bite-sized reply snippets (array of {text, charCount}).
  const [snippets,     setSnippets]     = React.useState(null);
  const [snipCopiedIdx,setSnipCopiedIdx]= React.useState(null);
  // P2-2: long-form single X post (string), up to 4000 chars.
  const [longform,     setLongform]     = React.useState(null);
  const [lfCopied,     setLfCopied]     = React.useState(false);
  // v8.4.302: pre-filled weekly Beehiiv newsletter ({subject,preview,markdown,html,...}).
  const [newsletter,   setNewsletter]   = React.useState(null);
  const [nlMdCopied,   setNlMdCopied]   = React.useState(false);
  const [nlHtmlCopied, setNlHtmlCopied] = React.useState(false);
  // Use the admin's selected risk profile so scores match what they see in
  // Watchlist / Discover / Top Swing Scores. Horizon defaults to 6m (the
  // canonical view used in Top Swing Scores on Landing).
  const profile = user?.risk_profile || "moderate";
  const horizon = "6m";
  const qs = `horizon=${horizon}&profile=${profile}`;
  const canvasRef = React.useRef(null);

  const fetchCandidates = () => {
    setLoadingC(true); setCandidates([]); setSelected(null); setTweet(""); setCardData(null); setWarning("");
    apiFetch(`/admin/tweet-candidates?${qs}`, token)
      .then(r => { setCandidates(r.candidates || []); setLoadingC(false); })
      .catch(() => setLoadingC(false));
  };

  React.useEffect(() => { fetchCandidates(); }, [profile]); // eslint-disable-line

  const generateFor = async (tickerObj) => {
    if (!tickerObj?.ticker) return;
    setGenerating(true); setTweet(""); setCardData(null); setWarning(""); setTweetErr(""); setThread(null); setSnippets(null); setLongform(null);
    try {
      // v8.4.386: async submit→poll (was a synchronous POST via _aiAgentFetch).
      // The tweet pipeline's cold-cache step-1 fan-out (8 parallel hops) + grounded
      // Gemini call can exceed Cloudflare's ~100s edge timeout — the client then
      // aborted+retried and the user re-clicked, so a tweet "took 3 attempts" even
      // though the backend always 200'd (just slowly). Same submit→poll pattern the
      // digest tweet already uses: the POST returns a request_id immediately and each
      // poll GET is fast, so the edge timeout can never fire.
      const sub = await apiFetch(`/admin/generate-tweet-async?ticker=${tickerObj.ticker}&${qs}`, token, { method:"POST" });
      const reqId = sub?.request_id;
      if (!reqId) throw new Error("no request_id returned");
      const start = Date.now();
      let finalInfo = null;
      while (Date.now() - start < 180000) {   // poll up to 3 min at 3s intervals
        await new Promise(res => setTimeout(res, 3000));
        const info = await apiFetch(`/admin/generate-tweet-async/${reqId}`, token);
        if (info?.status === "done")  { finalInfo = info; break; }
        if (info?.status === "error") { finalInfo = info; break; }
        // "running" / "queued" — keep polling
      }
      if (!finalInfo) throw new Error("timed out after 3 min");
      if (finalInfo.status === "error") {
        throw new Error(`${finalInfo.step || "?"}: ${finalInfo.error || "unknown"}`);
      }
      setTweet(finalInfo.tweet || "");
      setCardData(finalInfo);
      if (finalInfo.warning) setWarning(finalInfo.warning);
    } catch(e) {
      // v8.3.12: surface full error in a copy-able error box instead of
      // dumping it into the tweet textarea (where it looks like a tweet
      // and gets copied/posted by mistake). Post-v8.3.10 the backend
      // returns step-named errors like "Tweet step 3 (build prompt)
      // failed: TypeError: ..." which need to be visible verbatim.
      setTweetErr(e.message || "Unknown failure");
    }
    setGenerating(false);
  };

  const generate = () => generateFor(selected);

  // v8.4.48 → v8.4.51: turn today's market digest into a single X post.
  // Reuses the same data path the daily digest email uses so the headline
  // numbers match. Now uses the async submit→poll pattern so the per-
  // ticker sentiment cache miss can take its time without tripping the
  // Cloudflare 100s edge timeout.
  const generateDigestTweet = async () => {
    setGenerating(true); setTweet(""); setCardData(null); setWarning(""); setTweetErr(""); setThread(null); setSnippets(null); setLongform(null);
    try {
      const sub = await apiFetch("/admin/generate-digest-tweet", token, { method: "POST" });
      const reqId = sub?.request_id;
      if (!reqId) throw new Error("no request_id returned");
      // Poll up to 3 minutes at 4s intervals
      const start = Date.now();
      let finalInfo = null;
      while (Date.now() - start < 180000) {
        await new Promise(r => setTimeout(r, 4000));
        const info = await apiFetch(`/admin/generate-digest-tweet/${reqId}`, token);
        if (info?.status === "done")  { finalInfo = info; break; }
        if (info?.status === "error") { finalInfo = info; break; }
        // status === "running" or "queued" — keep polling
      }
      if (!finalInfo) throw new Error("timed out after 3 min");
      if (finalInfo.status === "error") {
        throw new Error(`${finalInfo.step || "?"}: ${finalInfo.error || "unknown"}`);
      }
      setTweet(finalInfo.tweet || "");
      // Reuse the cardData slot just to expose provider + sources in the UI.
      // No score card is rendered because there's no single ticker focus.
      setCardData({
        provider: finalInfo.provider,
        sources:  finalInfo.sources || [],
        _isDigest: true,
      });
      setSelected(null);
    } catch(e) {
      setTweetErr(e.message || "Unknown failure");
    }
    setGenerating(false);
  };

  // P1-1: turn today's digest into a 5-7 tweet X thread. Same async submit→poll
  // pattern as generateDigestTweet. Output is an array of editable tweets.
  const generateDigestThread = async () => {
    setGenerating(true); setTweet(""); setCardData(null); setWarning(""); setTweetErr(""); setThread(null); setSnippets(null); setLongform(null);
    try {
      const sub = await apiFetch("/admin/generate-digest-thread?tweets=7", token, { method: "POST" });
      const reqId = sub?.request_id;
      if (!reqId) throw new Error("no request_id returned");
      const start = Date.now();
      let finalInfo = null;
      while (Date.now() - start < 180000) {
        await new Promise(r => setTimeout(r, 4000));
        const info = await apiFetch(`/admin/generate-digest-thread/${reqId}`, token);
        if (info?.status === "done")  { finalInfo = info; break; }
        if (info?.status === "error") { finalInfo = info; break; }
      }
      if (!finalInfo) throw new Error("timed out after 3 min");
      if (finalInfo.status === "error") {
        throw new Error(`${finalInfo.step || "?"}: ${finalInfo.error || "unknown"}`);
      }
      setThread(finalInfo.thread || []);
      setSelected(null);
    } catch(e) {
      setTweetErr(e.message || "Unknown failure");
    }
    setGenerating(false);
  };

  const updateThreadTweet = (i, val) =>
    setThread(prev => (prev || []).map((t, idx) => (idx === i ? val : t)));

  const copyThread = () => {
    const all = (thread || []).join("\n\n");
    navigator.clipboard.writeText(all).then(() => {
      setThreadCopied(true); setTimeout(() => setThreadCopied(false), 2000);
    });
  };

  const copyOneTweet = (i) => {
    navigator.clipboard.writeText((thread || [])[i] || "").then(() => {
      setCopiedIdx(i); setTimeout(() => setCopiedIdx(prev => (prev === i ? null : prev)), 1600);
    });
  };

  // P1-2: short, link-free reply tweets to drop under big accounts. Synchronous
  // backend call. If a candidate chip is selected, anchor all snippets on it.
  const generateReplySnippets = async () => {
    setGenerating(true); setTweet(""); setCardData(null); setWarning(""); setTweetErr(""); setThread(null); setSnippets(null); setLongform(null);
    try {
      const tk = (selected?.ticker || "").trim();
      const q = tk ? `?ticker=${encodeURIComponent(tk)}&n=5` : `?n=5`;
      const r = await apiFetch(`/admin/generate-reply-snippets${q}`, token, { method: "POST" });
      setSnippets(r.snippets || []);
    } catch(e) {
      setTweetErr(e.message || "Unknown failure");
    }
    setGenerating(false);
  };

  const copyOneSnippet = (i) => {
    navigator.clipboard.writeText((snippets || [])[i]?.text || "").then(() => {
      setSnipCopiedIdx(i); setTimeout(() => setSnipCopiedIdx(prev => (prev === i ? null : prev)), 1600);
    });
  };

  // P2-2: long-form single X Premium post (≤4000 chars). Async submit→poll.
  const generateDigestLongform = async () => {
    setGenerating(true); setTweet(""); setCardData(null); setWarning(""); setTweetErr(""); setThread(null); setSnippets(null); setLongform(null);
    try {
      const sub = await apiFetch("/admin/generate-digest-longform", token, { method: "POST" });
      const reqId = sub?.request_id;
      if (!reqId) throw new Error("no request_id returned");
      const start = Date.now();
      let finalInfo = null;
      while (Date.now() - start < 180000) {
        await new Promise(r => setTimeout(r, 4000));
        const info = await apiFetch(`/admin/generate-digest-longform/${reqId}`, token);
        if (info?.status === "done")  { finalInfo = info; break; }
        if (info?.status === "error") { finalInfo = info; break; }
      }
      if (!finalInfo) throw new Error("timed out after 3 min");
      if (finalInfo.status === "error") {
        throw new Error(`${finalInfo.step || "?"}: ${finalInfo.error || "unknown"}`);
      }
      setLongform(finalInfo.post || "");
      setSelected(null);
    } catch(e) {
      setTweetErr(e.message || "Unknown failure");
    }
    setGenerating(false);
  };

  const copyLongform = () => {
    navigator.clipboard.writeText(longform || "").then(() => {
      setLfCopied(true); setTimeout(() => setLfCopied(false), 2000);
    });
  };

  // v8.4.302: build a pre-filled weekly Beehiiv issue (subject + Markdown + HTML)
  // from live weekly data. Async submit→poll (weekly digest + prose ≈ 40-120s).
  const generateNewsletter = async () => {
    setGenerating(true); setTweet(""); setCardData(null); setWarning(""); setTweetErr("");
    setThread(null); setSnippets(null); setLongform(null); setNewsletter(null);
    try {
      const sub = await apiFetch("/admin/generate-newsletter", token, { method: "POST" });
      const reqId = sub?.request_id;
      if (!reqId) throw new Error("no request_id returned");
      const start = Date.now();
      let finalInfo = null;
      while (Date.now() - start < 300000) {
        await new Promise(r => setTimeout(r, 5000));
        const info = await apiFetch(`/admin/generate-newsletter/${reqId}`, token);
        if (info?.status === "done")  { finalInfo = info; break; }
        if (info?.status === "error") { finalInfo = info; break; }
      }
      if (!finalInfo) throw new Error("timed out after 5 min");
      if (finalInfo.status === "error") {
        throw new Error(`${finalInfo.step || "?"}: ${finalInfo.error || "unknown"}`);
      }
      setNewsletter(finalInfo);
      setSelected(null);
    } catch(e) {
      setTweetErr(e.message || "Unknown failure");
    }
    setGenerating(false);
  };

  const copyNewsletterMd = () => {
    navigator.clipboard.writeText(newsletter?.markdown || "").then(() => {
      setNlMdCopied(true); setTimeout(() => setNlMdCopied(false), 2000);
    });
  };
  const copyNewsletterHtml = () => {
    navigator.clipboard.writeText(newsletter?.html || "").then(() => {
      setNlHtmlCopied(true); setTimeout(() => setNlHtmlCopied(false), 2000);
    });
  };

  const generateCustom = () => {
    const t = customTkr.toUpperCase().trim();
    if (!TWEET_TICKER_RE.test(t)) {
      setWarning("Enter 1-5 uppercase letters (optional .TO/.V/.TSX suffix).");
      return;
    }
    const tickerObj = { ticker: t, shortName: t };
    setSelected(tickerObj);
    generateFor(tickerObj);
  };

  // Render canvas whenever cardData changes (drawScoreCard is async — loads images first)
  // v8.4.48: skip when the cardData came from the digest path — there's no
  // single ticker to draw a score card around.
  React.useEffect(() => {
    if (!cardData || !canvasRef.current || cardData._isDigest) return;
    drawScoreCard(canvasRef.current, {
      ticker:    cardData.ticker,
      // Backend now returns shortName (resolved from cache or live fundamentals).
      // Fall back to the candidate chip's shortName for older API versions.
      shortName: cardData.shortName || selected?.shortName,
      swingScore: cardData.swingScore,
      ltScore:    cardData.ltScore,
      price:      cardData.price,
      changePct:  cardData.changePct,
    });
  }, [cardData, selected]);

  const downloadCard = () => {
    if (!canvasRef.current) return;
    const a = document.createElement("a");
    a.download = `gempulse-${selected?.ticker || "card"}.png`;
    a.href = canvasRef.current.toDataURL("image/png");
    a.click();
  };

  const copyTweet = () => {
    navigator.clipboard.writeText(tweet).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  };

  return (
    <div style={{background:C.surface, border:`1px solid ${C.border}`,
      borderRadius:14, padding:"16px", marginBottom:16}}>

      {/* Header */}
      <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14}}>
        <div style={{display:"flex", alignItems:"center", gap:8}}>
          <div style={{fontSize:10, fontWeight:700, color:C.muted,
            letterSpacing:"0.1em", textTransform:"uppercase"}}>
            ✦ X Post Generator
          </div>
          <span style={{fontSize:9, color:C.muted, fontFamily:"monospace",
            padding:"2px 6px", border:`1px solid ${C.border}`, borderRadius:5,
            textTransform:"uppercase", letterSpacing:"0.06em"}}
            title="Scores use your selected risk profile so they match Watchlist / Top Swing Scores. Change in Settings.">
            {profile.replace("_", " ")} · {horizon}
          </span>
        </div>
        <button onClick={fetchCandidates}
          style={{background:"none", border:`1px solid ${C.border}`, borderRadius:8,
            color:C.muted, fontSize:11, cursor:"pointer", padding:"4px 10px", fontFamily:"inherit"}}>
          {loadingC ? "Loading…" : "↻ Refresh"}
        </button>
      </div>

      {/* v8.4.48: daily digest → tweet. One-tap repackage of today's
          market wrap into a structured long-form X post (v8.4.99: condensed
          digest replica with sections/bullets, ~700-1100 chars — not a short
          280-char narrative). Same data path as the email. */}
      <div style={{display:"flex", alignItems:"center", gap:8, marginBottom:14}}>
      <button
        onClick={generateDigestTweet}
        disabled={generating}
        style={{
          flex:1, padding:"11px", borderRadius:10,
          cursor: generating ? "not-allowed" : "pointer",
          fontFamily:"inherit", fontWeight:700, fontSize:13,
          background: generating ? "rgba(0,212,255,0.06)" : "rgba(0,212,255,0.12)",
          border:`1px solid ${C.accent}`,
          color: generating ? "rgba(0,212,255,0.5)" : C.accent,
          opacity: generating ? 0.7 : 1,
          display:"flex", alignItems:"center", justifyContent:"center", gap:8,
        }}
        title="Repackage today's daily digest data into a single X post — same numbers as the email">
        <span>📊</span>
        <span>{generating ? "Generating digest tweet…" : "Generate today's market digest tweet"}</span>
      </button>
      <InfoTip text={"SHORT POST — one structured tweet (~700–1,100 chars): regime, sectors, top movers, what to watch. The condensed, scannable version of today's digest.\n\nUse when: your default daily timeline post.\n\nSteps:\n1) Tap to generate (uses today's fact-checked digest data).\n2) Sanity-check the numbers + catalysts.\n3) Post to your timeline.\n\nvs THREAD: the thread spreads the same data over 5–7 tweets for more reach + replies.\nvs LONG-FORM: long-form adds connective prose and runs up to 4,000 chars."} />
      </div>

      {/* P1-1: daily digest → 5-7 tweet X thread (hook → regime → rotation →
          mover → loser → setup → CTA). Same fact-checked digest source. */}
      <div style={{display:"flex", alignItems:"center", gap:8, marginBottom:14}}>
      <button
        onClick={generateDigestThread}
        disabled={generating}
        style={{
          flex:1, padding:"11px", borderRadius:10,
          cursor: generating ? "not-allowed" : "pointer",
          fontFamily:"inherit", fontWeight:700, fontSize:13,
          background: generating ? "rgba(0,212,255,0.06)" : "rgba(0,212,255,0.12)",
          border:`1px solid ${C.accent}`,
          color: generating ? "rgba(0,212,255,0.5)" : C.accent,
          opacity: generating ? 0.7 : 1,
          display:"flex", alignItems:"center", justifyContent:"center", gap:8,
        }}
        title="Turn today's digest into a 5-7 tweet thread you can post or schedule">
        <span>🧵</span>
        <span>{generating ? "Building thread…" : "Build today's digest thread"}</span>
      </button>
      <InfoTip text={"THREAD — 5–7 tweets from today's digest: hook → regime → rotation → top mover → key risk → setup → soft CTA.\n\nUse when: you want maximum reach + reply engagement. Threads out-impress single posts and every tweet is a re-entry point for the algorithm.\n\nSteps:\n1) Generate, then edit each tweet (watch the /280 counters — red = too long).\n2) Post tweet 1, then reply with 2, 3… OR paste all into the X composer / Typefully.\n3) Put any LINK in the last tweet or your first reply — never in tweet 1 (links in the hook suppress reach).\n\nvs SHORT POST: more surface area + virality.\nvs LONG-FORM: a thread is many small tweets; long-form is one big post."} />
      </div>

      {/* P2-2: long-form single X Premium post (≤4000 chars) */}
      <div style={{display:"flex", alignItems:"center", gap:8, marginBottom:14}}>
      <button
        onClick={generateDigestLongform}
        disabled={generating}
        style={{
          flex:1, padding:"11px", borderRadius:10,
          cursor: generating ? "not-allowed" : "pointer",
          fontFamily:"inherit", fontWeight:700, fontSize:13,
          background: generating ? "rgba(0,212,255,0.06)" : "rgba(0,212,255,0.12)",
          border:`1px solid ${C.accent}`,
          color: generating ? "rgba(0,212,255,0.5)" : C.accent,
          opacity: generating ? 0.7 : 1,
          display:"flex", alignItems:"center", justifyContent:"center", gap:8,
        }}
        title="Generate one long-form X Premium post (up to 4000 chars) from today's digest">
        <span>📰</span>
        <span>{generating ? "Writing long-form post…" : "Write long-form digest post"}</span>
      </button>
      <InfoTip text={"LONG-FORM POST — one X Premium post up to 4,000 chars. Structured like the digest but with connective prose so it reads as a single authoritative piece (X Premium required for >280 chars).\n\nUse when: a substantive post for your profile or a pinned tweet.\n\nSteps:\n1) Generate, review, trim if the /4000 counter goes red.\n2) Post as ONE tweet.\n3) Works well pinned to your profile.\n\nvs SHORT POST: more depth + narrative.\nvs THREAD: one scrollable post instead of a chain — lower reach ceiling than a thread, but reads like an article.\nvs EMAIL DIGEST: the 4:30pm email (opted-in subscribers) is the full private version; this public post is the teaser that drives newsletter signups."} />
      </div>

      {/* v8.4.302: pre-filled WEEKLY BEEHIIV NEWSLETTER (subject + Markdown + HTML) */}
      <div style={{display:"flex", alignItems:"center", gap:8, marginBottom:14}}>
      <button
        onClick={generateNewsletter}
        disabled={generating}
        style={{
          flex:1, padding:"11px", borderRadius:10,
          cursor: generating ? "not-allowed" : "pointer",
          fontFamily:"inherit", fontWeight:700, fontSize:13,
          background: generating ? "rgba(0,230,118,0.06)" : "rgba(0,230,118,0.12)",
          border:`1px solid ${C.green}`,
          color: generating ? "rgba(0,230,118,0.5)" : C.green,
          opacity: generating ? 0.7 : 1,
          display:"flex", alignItems:"center", justifyContent:"center", gap:8,
        }}
        title="Build this week's Beehiiv newsletter, pre-filled with live weekly data">
        <span>💌</span>
        <span>{generating ? "Building newsletter…" : "Build weekly Beehiiv newsletter"}</span>
      </button>
      <InfoTip text={"WEEKLY NEWSLETTER — a full, pre-filled Beehiiv issue in the locked 9-section structure: subject + preview, header w/ regime badge, opening hook, CTA button (x2, UTM-tracked), weekly gainers/decliners w/ catalysts, 'what the scores said', 'what to watch next week', footer.\n\nThe NUMBERS (weekly %, scores, regime, dates, links) are computed from live data — the AI only writes the glue prose (hook, catalyst tags, sizing note), grounded in the fact-checked weekly digest.\n\nSteps:\n1) Generate (~40-120s: it runs the weekly digest first, then the newsletter).\n2) Review subject + preview; eyeball the catalyst tags.\n3) Copy Markdown → paste into a Beehiiv text block (Beehiiv converts ##/**/- and links). OR Copy HTML → paste into a Beehiiv 'custom HTML' block for the styled version with real buttons.\n4) In Beehiiv, set the subject/preview from the fields shown.\n\nUTM links are baked into both CTA buttons so Beehiiv shows you top-vs-bottom clicks."} />
      </div>

      {/* Weekly newsletter output — subject/preview + Markdown box + Copy HTML */}
      {newsletter && (
        <div style={{marginBottom:14, border:`1px solid rgba(0,230,118,0.3)`, borderRadius:10, padding:12}}>
          <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8, flexWrap:"wrap", gap:6}}>
            <div style={{fontSize:10, color:C.green, letterSpacing:"0.06em", textTransform:"uppercase", fontWeight:700}}>
              💌 Weekly Beehiiv issue{newsletter.regime ? ` · ${newsletter.regime}` : ""}
            </div>
            <div style={{display:"flex", gap:6, alignItems:"center"}}>
              <span style={{fontSize:10, color: newsletter.fact_checked ? C.green : C.muted}}>
                {newsletter.fact_checked ? "fact-checked ✓" : "review before send"}
              </span>
              <button onClick={copyNewsletterHtml} style={{
                background:"none", border:`1px solid ${C.border}`, borderRadius:8,
                color: nlHtmlCopied ? C.green : C.muted, fontSize:11, cursor:"pointer",
                padding:"4px 10px", fontFamily:"inherit"}}>
                {nlHtmlCopied ? "HTML copied ✓" : "Copy HTML"}
              </button>
              <button onClick={copyNewsletterMd} style={{
                background:"none", border:`1px solid ${C.border}`, borderRadius:8,
                color: nlMdCopied ? C.green : C.muted, fontSize:11, cursor:"pointer",
                padding:"4px 10px", fontFamily:"inherit"}}>
                {nlMdCopied ? "Markdown copied ✓" : "Copy Markdown"}
              </button>
            </div>
          </div>
          {/* Subject + preview — copy these into Beehiiv's subject/preview fields */}
          <div style={{marginBottom:8}}>
            <div style={{fontSize:10, color:C.muted, marginBottom:2}}>Subject</div>
            <input value={newsletter.subject || ""} onChange={e=>setNewsletter({...newsletter, subject:e.target.value})}
              style={{width:"100%", boxSizing:"border-box", background:C.card, border:`1px solid ${C.border}`,
                borderRadius:8, padding:"7px 10px", color:C.text, fontSize:13, fontFamily:"inherit", outline:"none"}}/>
            <div style={{fontSize:10, color:C.muted, margin:"6px 0 2px"}}>Preview text</div>
            <input value={newsletter.preview || ""} onChange={e=>setNewsletter({...newsletter, preview:e.target.value})}
              style={{width:"100%", boxSizing:"border-box", background:C.card, border:`1px solid ${C.border}`,
                borderRadius:8, padding:"7px 10px", color:C.text, fontSize:13, fontFamily:"inherit", outline:"none"}}/>
          </div>
          <div style={{fontSize:10, color:C.muted, marginBottom:2}}>
            Body (Markdown — paste into a Beehiiv text block)
          </div>
          <textarea
            value={newsletter.markdown || ""}
            onChange={e => setNewsletter({...newsletter, markdown:e.target.value})}
            rows={18}
            style={{
              width:"100%", boxSizing:"border-box", resize:"vertical",
              background:C.card, border:`1px solid ${C.border}`,
              borderRadius:10, padding:"10px 12px", outline:"none",
              color:C.text, fontSize:14, lineHeight:1.5, fontFamily:"inherit"}}
          />
        </div>
      )}

      {/* Long-form post output — single editable box, /4000 counter */}
      {longform != null && longform !== "" && (
        <div style={{marginBottom:14}}>
          <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8}}>
            <div style={{fontSize:10, color:C.muted, letterSpacing:"0.06em", textTransform:"uppercase"}}>
              Long-form post
            </div>
            <button onClick={copyLongform} style={{
              background:"none", border:`1px solid ${C.border}`, borderRadius:8,
              color: lfCopied ? C.green : C.muted, fontSize:11, cursor:"pointer",
              padding:"4px 10px", fontFamily:"inherit"}}>
              {lfCopied ? "Copied ✓" : "Copy"}
            </button>
          </div>
          <textarea
            value={longform}
            onChange={e => setLongform(e.target.value)}
            rows={12}
            style={{
              width:"100%", boxSizing:"border-box", resize:"vertical",
              background:C.card, border:`1px solid ${longform.length > 4000 ? C.red : C.border}`,
              borderRadius:10, padding:"10px 12px", outline:"none",
              color:C.text, fontSize:16, lineHeight:1.55, fontFamily:"inherit"}}
          />
          <div style={{fontSize:10, color: longform.length > 4000 ? C.red : C.muted,
            fontFamily:"monospace", marginTop:4}}>
            {longform.length}/4000
          </div>
        </div>
      )}

      {/* P1-2: bite-sized, link-free reply snippets for big-account replies.
          Anchors on the selected candidate if one is picked. */}
      <button
        onClick={generateReplySnippets}
        disabled={generating}
        style={{
          width:"100%", padding:"11px", borderRadius:10, marginBottom:14,
          cursor: generating ? "not-allowed" : "pointer",
          fontFamily:"inherit", fontWeight:700, fontSize:13,
          background: generating ? "rgba(0,212,255,0.06)" : "rgba(0,212,255,0.12)",
          border:`1px solid ${C.accent}`,
          color: generating ? "rgba(0,212,255,0.5)" : C.accent,
          opacity: generating ? 0.7 : 1,
          display:"flex", alignItems:"center", justifyContent:"center", gap:8,
        }}
        title="Generate short, link-free reply tweets to drop under big trading accounts (Strategy 1)">
        <span>💬</span>
        <span>{generating ? "Generating…" : (selected?.ticker ? `Reply snippets for $${selected.ticker}` : "Generate reply snippets")}</span>
      </button>

      {/* Reply snippets output */}
      {snippets && snippets.length > 0 && (
        <div style={{marginBottom:14}}>
          <div style={{fontSize:10, color:C.muted, letterSpacing:"0.06em",
            textTransform:"uppercase", marginBottom:8}}>
            Reply snippets · drop under big accounts (no link)
          </div>
          <div style={{display:"flex", flexDirection:"column", gap:8}}>
            {snippets.map((s, i) => {
              const over = (s.text || "").length > 280;
              return (
                <div key={i} style={{
                  border:`1px solid ${over ? C.red : C.border}`, borderRadius:10,
                  padding:"8px 10px", background:C.card}}>
                  <div style={{color:C.text, fontSize:13, lineHeight:1.5, whiteSpace:"pre-wrap"}}>
                    {s.text}
                  </div>
                  <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginTop:6}}>
                    <span style={{fontSize:10, color: over ? C.red : C.muted, fontFamily:"monospace"}}>
                      {(s.text || "").length}/280
                    </span>
                    <button onClick={() => copyOneSnippet(i)} style={{
                      background:"none", border:`1px solid ${C.border}`, borderRadius:7,
                      color: snipCopiedIdx === i ? C.green : C.muted, fontSize:10, cursor:"pointer",
                      padding:"3px 9px", fontFamily:"inherit"}}>
                      {snipCopiedIdx === i ? "Copied ✓" : "Copy"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Thread output — editable per-tweet boxes with char counts */}
      {thread && thread.length > 0 && (
        <div style={{marginBottom:14}}>
          <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8}}>
            <div style={{fontSize:10, color:C.muted, letterSpacing:"0.06em", textTransform:"uppercase"}}>
              {thread.length}-tweet thread
            </div>
            <button onClick={copyThread} style={{
              background:"none", border:`1px solid ${C.border}`, borderRadius:8,
              color: threadCopied ? C.green : C.muted, fontSize:11, cursor:"pointer",
              padding:"4px 10px", fontFamily:"inherit"}}>
              {threadCopied ? "Copied all ✓" : "Copy all"}
            </button>
          </div>
          <div style={{display:"flex", flexDirection:"column", gap:8}}>
            {thread.map((t, i) => {
              const over = (t || "").length > 280;
              return (
                <div key={i} style={{
                  border:`1px solid ${over ? C.red : C.border}`, borderRadius:10,
                  padding:"8px 10px", background:C.card}}>
                  <textarea
                    value={t}
                    onChange={e => updateThreadTweet(i, e.target.value)}
                    rows={3}
                    style={{
                      width:"100%", boxSizing:"border-box", resize:"vertical",
                      background:"transparent", border:"none", outline:"none",
                      color:C.text, fontSize:16, lineHeight:1.5, fontFamily:"inherit"}}
                  />
                  <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginTop:4}}>
                    <span style={{fontSize:10, color: over ? C.red : C.muted, fontFamily:"monospace"}}>
                      {(t || "").length}/280
                    </span>
                    <button onClick={() => copyOneTweet(i)} style={{
                      background:"none", border:`1px solid ${C.border}`, borderRadius:7,
                      color: copiedIdx === i ? C.green : C.muted, fontSize:10, cursor:"pointer",
                      padding:"3px 9px", fontFamily:"inherit"}}>
                      {copiedIdx === i ? "Copied ✓" : "Copy"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Candidate chips */}
      {candidates.length > 0 && (
        <div style={{marginBottom:14}}>
          <div style={{fontSize:10, color:C.muted, marginBottom:8}}>
            Top swing candidates — select one to generate
          </div>
          <div style={{display:"flex", flexWrap:"wrap", gap:8}}>
            {candidates.map(c => (
              <button key={c.ticker}
                onClick={() => { setSelected(c); setTweet(""); setCardData(null); }}
                style={{
                  padding:"8px 12px", borderRadius:10, cursor:"pointer",
                  fontFamily:"inherit", border:`1px solid ${selected?.ticker === c.ticker ? C.accent : C.border}`,
                  background: selected?.ticker === c.ticker ? "rgba(0,212,255,0.1)" : C.card,
                  textAlign:"left",
                }}>
                <div style={{fontWeight:800, fontSize:13, fontFamily:"monospace",
                  color: selected?.ticker === c.ticker ? C.accent : C.text}}>
                  {c.ticker}
                </div>
                <div style={{fontSize:10, color:C.muted}}>{c.swingScore} · {c.swingLabel}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {loadingC && <div style={{fontSize:12, color:C.muted, marginBottom:12}}>Fetching top candidates…</div>}

      {/* Custom ticker input — any ticker, even outside the candidate list */}
      <div style={{display:"flex", gap:8, marginBottom:14}}>
        <input
          value={customTkr}
          onChange={e => setCustomTkr(e.target.value.toUpperCase())}
          onKeyDown={e => { if (e.key === "Enter" && !generating) generateCustom(); }}
          placeholder="Or type a ticker (e.g. NVDA, SHOP.TO)"
          maxLength={9}
          style={{
            flex:1, padding:"10px 12px", borderRadius:10,
            background:C.card, border:`1px solid ${C.border}`,
            color:C.text, fontSize:16, fontFamily:"monospace",
            outline:"none", boxSizing:"border-box",
          }}
        />
        <button
          onClick={generateCustom}
          disabled={generating || !customTkr.trim()}
          style={{
            padding:"10px 14px", borderRadius:10,
            cursor: generating || !customTkr.trim() ? "not-allowed" : "pointer",
            fontFamily:"inherit", fontWeight:700, fontSize:12,
            background: "rgba(123,97,255,0.12)",
            border:"1px solid rgba(123,97,255,0.3)", color:"#7b61ff",
            opacity: generating || !customTkr.trim() ? 0.5 : 1,
            whiteSpace:"nowrap",
          }}>
          Generate
        </button>
      </div>

      {/* Warning banner (e.g. ticker not yet scored) */}
      {warning && (
        <div style={{
          background:"rgba(255,202,40,0.08)", border:"1px solid rgba(255,202,40,0.3)",
          borderRadius:10, padding:"9px 12px", marginBottom:14,
          fontSize:11, color:"#ffca28", lineHeight:1.5,
        }}>⚠ {warning}</div>
      )}

      {/* Error box — backend returns step-named errors after v8.3.10 */}
      <AgentErrorBox message={tweetErr} onRetry={() => selected && generateFor(selected)}/>

      {/* Generate button — for candidate chip selection */}
      {selected && (
        <button onClick={generate} disabled={generating}
          style={{
            width:"100%", padding:"11px", borderRadius:10, cursor: generating ? "not-allowed" : "pointer",
            fontFamily:"inherit", fontWeight:700, fontSize:13,
            background: generating ? "rgba(0,212,255,0.08)" : "rgba(0,212,255,0.15)",
            border:`1px solid ${C.accent}`, color:C.accent, marginBottom:14,
          }}>
          {generating ? "Generating…" : `✦ Generate X post for $${selected.ticker}`}
        </button>
      )}

      {/* Post textarea — v8.4.2 Twitter Premium: larger, shows char count */}
      {tweet && (
        <div style={{marginBottom:14}}>
          <div style={{display:"flex", justifyContent:"space-between",
            alignItems:"center", marginBottom:6}}>
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              <div style={{fontSize:10, color:C.muted, letterSpacing:"0.06em",
                textTransform:"uppercase"}}>Draft post — edit before posting</div>
              {/* Twitter Premium badge */}
              <span style={{fontSize:9,fontWeight:700,
                background:"rgba(0,0,0,0.4)",border:"1px solid #1d9bf0",
                color:"#1d9bf0",borderRadius:4,padding:"1px 5px",
                letterSpacing:"0.04em"}}>X Premium ⚡</span>
            </div>
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              <div style={{fontSize:10, color: tweet.length > 1500 ? "#ffca28" : C.muted,
                fontFamily:"monospace"}}>
                {tweet.length} chars
              </div>
              {cardData?.provider && (
                <div style={{fontSize:9, color:C.muted, fontFamily:"monospace",
                  letterSpacing:"0.04em"}}>
                  {cardData.provider === "gemini"
                    ? `gemini · grounded · ${(cardData.sources || []).length} src`
                    : "groq · no grounding"}
                </div>
              )}
            </div>
          </div>
          <textarea
            value={tweet}
            onChange={e => setTweet(e.target.value)}
            rows={14}
            style={{
              width:"100%", boxSizing:"border-box", background:C.card,
              border:`1px solid ${C.border}`, borderRadius:10,
              color:C.text, fontSize:16, lineHeight:1.7,
              padding:"12px", fontFamily:"inherit", resize:"vertical",
            }}
          />
          {/* Grounding sources — links Gemini Search cited while drafting */}
          {(cardData?.sources || []).length > 0 && (
            <details style={{marginTop:8}}>
              <summary style={{fontSize:10, color:C.muted, cursor:"pointer",
                letterSpacing:"0.06em", textTransform:"uppercase",
                userSelect:"none"}}>
                Sources cited ({cardData.sources.length})
              </summary>
              <div style={{marginTop:6, paddingLeft:8,
                borderLeft:`2px solid ${C.border}`}}>
                {cardData.sources.map((s, i) => (
                  <a key={i} href={s.url} target="_blank" rel="noreferrer"
                    style={{
                      display:"block", fontSize:11, color:C.accent,
                      textDecoration:"none", padding:"3px 0",
                      overflow:"hidden", textOverflow:"ellipsis",
                      whiteSpace:"nowrap",
                    }}
                    title={s.url}>
                    {s.title || s.url}
                  </a>
                ))}
              </div>
            </details>
          )}
          <div style={{display:"flex", gap:8, marginTop:8}}>
            <button onClick={copyTweet}
              style={{
                flex:1, padding:"9px", borderRadius:9, cursor:"pointer",
                fontFamily:"inherit", fontWeight:700, fontSize:12,
                background: copied ? "rgba(0,230,118,0.15)" : C.card,
                border:`1px solid ${copied ? C.green : C.border}`,
                color: copied ? C.green : C.text,
              }}>
              {copied ? "✓ Copied!" : "Copy post"}
            </button>
            <a href="https://x.com/compose/tweet" target="_blank" rel="noreferrer"
              style={{
                flex:1, padding:"9px", borderRadius:9, cursor:"pointer",
                fontFamily:"inherit", fontWeight:700, fontSize:12, textAlign:"center",
                background:"rgba(29,161,242,0.12)", border:"1px solid rgba(29,161,242,0.3)",
                color:"#1da1f2", textDecoration:"none", display:"flex",
                alignItems:"center", justifyContent:"center",
              }}>
              Post on X ↗
            </a>
          </div>
        </div>
      )}

      {/* Score card canvas */}
      {cardData && (
        <div>
          <div style={{fontSize:10, color:C.muted, marginBottom:8, letterSpacing:"0.06em",
            textTransform:"uppercase"}}>Shareable score card</div>
          <canvas ref={canvasRef}
            style={{width:"100%", borderRadius:10, border:`1px solid ${C.border}`, display:"block"}} />
          <button onClick={downloadCard}
            style={{
              width:"100%", marginTop:8, padding:"9px", borderRadius:9, cursor:"pointer",
              fontFamily:"inherit", fontWeight:700, fontSize:12,
              background:"rgba(123,97,255,0.12)", border:"1px solid rgba(123,97,255,0.3)",
              color:"#7b61ff",
            }}>
            ↓ Download PNG
          </button>
        </div>
      )}
    </div>
  );
}

// --- Admin Screen -------------------------------------------------------------
// v8.4.410 — Service notice: one transactional email to every active user
// (policy/terms changes, outages). Preview → test to yourself → send to all.
const SERVICE_NOTICE_DEFAULT = {
  subject: "An update to GemPulse's Privacy Policy and Terms",
  body: [
    "Hi — a quick heads-up that we've updated our Privacy Policy and Terms of Use, effective October 9, 2026.",
    "In short: the policy now describes the AI services that write your briefings (they see tickers, scores and weights — never your name or email), the email and newsletter tools we use, and analytics on our public website. The Terms add a Beta and Pricing section: GemPulse is free during beta, your early-adopter tier is recorded on your account, and we will never charge you without your explicit agreement and at least 30 days' notice. Nothing changes inside the app.",
    "Read them here: gempulse.io/privacy and gempulse.io/terms",
    "Questions? Just reply to this email.",
  ].join("\n\n"),
};

function ServiceNoticeCard({ token, flash }) {
  const [open, setOpen] = useState(() => { try { return localStorage.getItem("gp_admin_notice_open") === "1"; } catch (_) { return false; } });
  const toggleOpen = () => setOpen(o => { const n = !o; try { localStorage.setItem("gp_admin_notice_open", n ? "1" : "0"); } catch (_) {} return n; });
  const [subject, setSubject] = useState(SERVICE_NOTICE_DEFAULT.subject);
  const [body, setBody] = useState(SERVICE_NOTICE_DEFAULT.body);
  const [preview, setPreview] = useState(null);   // {recipients, html}
  const [busy, setBusy] = useState("");
  const [status, setStatus] = useState(null);

  const call = (mode) => apiFetch("/admin/service-notice", token,
    { method: "POST", body: JSON.stringify({ subject, body, mode }) });

  const doPreview = async () => {
    setBusy("preview");
    try { setPreview(await call("preview")); } catch (e) { flash(`❌ ${e.message}`); }
    setBusy("");
  };
  const doTest = async () => {
    setBusy("test");
    try { const d = await call("test"); flash(d.ok ? `✅ ${d.detail}` : `❌ ${d.detail}`); } catch (e) { flash(`❌ ${e.message}`); }
    setBusy("");
  };
  const doSend = async () => {
    if (!preview) return;
    if (!window.confirm(`Send "${subject}" to ${preview.recipients} users? This emails everyone with an active account.`)) return;
    setBusy("send");
    try {
      const d = await call("send");
      flash(`✅ Sending to ${d.queued} users…`);
      for (let i = 0; i < 120; i++) {
        await new Promise(r => setTimeout(r, 2000));
        const s = await apiFetch("/admin/service-notice/status", token);
        setStatus(s);
        if (s.state !== "sending") break;
      }
    } catch (e) { flash(`❌ ${e.message}`); }
    setBusy("");
  };

  const inp = {width:"100%", boxSizing:"border-box", background:C.card, color:C.text, border:`1px solid ${C.border}`,
    borderRadius:8, padding:"8px 10px", fontSize:12, fontFamily:"inherit"};
  const btn = (primary, off = false) => ({flex:1, padding:"9px", borderRadius:9, fontSize:12, fontWeight:700, fontFamily:"inherit",
    cursor: busy || off ? "not-allowed" : "pointer", opacity: busy || off ? 0.45 : 1,
    border:`1px solid ${primary ? C.accent : C.border}`, background: primary ? "rgba(0,212,255,0.12)" : "none",
    color: primary ? C.accent : C.muted});

  return (
    <div style={{background:C.surface, border:`1px solid ${C.border}`, borderRadius:12, padding:"14px 16px", marginBottom:12}}>
      <div onClick={toggleOpen} style={{display:"flex", justifyContent:"space-between", alignItems:"center", cursor:"pointer", userSelect:"none"}}>
        <div style={{fontSize:13, fontWeight:800, color:C.text}}>📣 Service notice</div>
        <span style={{fontSize:14, color:C.accent, fontWeight:900}}>{open ? "▴" : "▾"}</span>
      </div>
      {open && <>
      <div style={{fontSize:10, color:C.muted, margin:"4px 0 10px", lineHeight:1.5}}>
        One email to every active user — account notices only (policy changes, outages), never marketing.
        Blank lines start a new paragraph.
      </div>
      <input value={subject} onChange={e => { setSubject(e.target.value); setPreview(null); }} style={{...inp, marginBottom:8}}/>
      <textarea value={body} onChange={e => { setBody(e.target.value); setPreview(null); }} rows={9}
        style={{...inp, resize:"vertical", lineHeight:1.5, marginBottom:10}}/>
      <div style={{display:"flex", gap:8, marginBottom: preview ? 10 : 0}}>
        <button onClick={doPreview} disabled={!!busy} style={btn(false)}>{busy === "preview" ? "…" : "Preview"}</button>
        <button onClick={doTest} disabled={!!busy} style={btn(false)}>{busy === "test" ? "…" : "Send test to me"}</button>
        <button onClick={doSend} disabled={!!busy || !preview} title={preview ? "" : "Preview first"} style={btn(true, !preview)}>
          {busy === "send" ? "Sending…" : preview ? `Send to ${preview.recipients}` : "Send to all"}
        </button>
      </div>
      {preview && (
        <iframe title="Notice preview" srcDoc={preview.html}
          style={{width:"100%", height:360, border:`1px solid ${C.border}`, borderRadius:8, background:"#080c14"}}/>
      )}
      {status && status.state !== "idle" && (
        <div style={{fontSize:11, color: status.failed?.length ? C.yellow : C.green, marginTop:8}}>
          {status.state === "sending" ? "Sending… " : "Done: "}{status.sent}/{status.total} sent
          {status.failed?.length ? ` · failed: ${status.failed.join(", ")}` : ""}
        </div>
      )}
      </>}
    </div>
  );
}

// ── Admin: monthly score scoreboard (does the live score rank returns?) — self-fetching ──
function ScoreScoreboardCard({ token }) {
  const [sb, setSb]     = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr]   = React.useState("");
  const load = React.useCallback(async () => {
    try { setSb(await apiFetch("/admin/score-scoreboard", token)); setErr(""); }
    catch (e) { setErr(e.message || "Could not load the scoreboard"); }
  }, [token]);
  React.useEffect(() => { if (token) load(); }, [token, load]);
  const run = async () => {
    setBusy(true); setErr("");
    try { await apiFetch("/admin/score-scoreboard/run", token, {method:"POST"}); setTimeout(() => { load(); setBusy(false); }, 12000); }
    catch (e) { setErr(e.message || "Could not start the run"); setBusy(false); }
  };
  const cur = sb && sb.available ? sb.current : null;
  const rows = cur ? (cur.summary || []).filter(r => r.tier === "mcap>=$300M") : [];
  const vcol = (v) => /^positive/.test(v) ? C.green : /^NEGATIVE/.test(v) ? C.red : C.muted;
  return (
    <div style={{background:C.card, border:`1px solid ${C.border}`, borderRadius:12, padding:"14px 16px", marginBottom:12}}>
      <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", gap:8, marginBottom:6}}>
        <div style={{fontSize:13, fontWeight:800, color:C.text}}>📊 Score scoreboard</div>
        <button onClick={run} disabled={busy} style={{fontSize:10, fontWeight:700, padding:"4px 10px", borderRadius:8, cursor:busy ? "default" : "pointer",
          background:"none", border:`1px solid ${C.border}`, color:busy ? C.muted : C.accent, fontFamily:"inherit"}}>
          {busy ? "Running…" : "Run now"}</button>
      </div>
      <div style={{fontSize:10, color:C.muted, lineHeight:1.5, marginBottom:8}}>
        Does the live score rank next-month / next-quarter returns? One observation per calendar month (mean daily rank-IC, stocks ≥ $300M cap),
        with a t-stat across MONTHS — the non-overlapping statistic. Needs ≥ 6 months (ideally 12+) before it means anything. Refreshes on the 1st of each month.
      </div>
      {err && <div style={{fontSize:11, color:C.red}}>{err}</div>}
      {sb && !sb.available && <div style={{fontSize:11, color:C.muted}}>{sb.reason || "No runs yet."}</div>}
      {cur && (
        <>
          <div style={{overflowX:"auto"}}>
            <table style={{width:"100%", borderCollapse:"collapse", fontSize:10}}>
              <thead><tr style={{color:C.muted, textAlign:"left"}}>
                {["Score","Horizon","Months","Mean IC","t","+ months","Verdict"].map(h => <th key={h} style={{padding:"3px 6px 3px 0", fontWeight:700}}>{h}</th>)}
              </tr></thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} style={{borderTop:`1px solid ${C.divider}`}}>
                    <td style={{padding:"4px 6px 4px 0", color:C.text, fontWeight:700}}>{r.score.replace("_score","")}</td>
                    <td style={{color:C.muted}}>{r.horizon}</td>
                    <td style={{color:C.text}}>{r.n_months || 0}</td>
                    <td style={{color:(r.mean_ic ?? 0) >= 0 ? C.green : C.red, fontFamily:"monospace"}}>{r.mean_ic != null ? (r.mean_ic >= 0 ? "+" : "") + r.mean_ic.toFixed(3) : "–"}</td>
                    <td style={{color:C.text, fontFamily:"monospace"}}>{r.t_stat != null ? r.t_stat : "–"}</td>
                    <td style={{color:C.muted}}>{r.months_pos != null ? `${r.months_pos}/${r.n_months}` : "–"}</td>
                    <td style={{color:vcol(r.verdict || "")}}>{r.verdict}</td>
                  </tr>))}
              </tbody>
            </table>
          </div>
          <div style={{fontSize:9, color:C.muted, marginTop:6}}>
            {cur.rows?.toLocaleString("en")} snapshots · {String(cur.first_date).slice(0,10)} → {String(cur.last_date).slice(0,10)} · run {String(cur.generated_at).slice(0,10)}
          </div>
        </>
      )}
    </div>
  );
}

// ── Admin: monthly post-earnings-drift OUT-OF-SAMPLE check — self-fetching ──
function PeadScoreboardCard({ token }) {
  const [sb, setSb]     = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr]   = React.useState("");
  const [, setTick]     = React.useState(0);
  const utc = (iso) => iso ? new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(iso) ? iso : iso + "Z") : null;     // backend stamps are naive UTC
  const load = React.useCallback(async () => {
    try { setSb(await apiFetch("/admin/pead-scoreboard", token)); setErr(""); }
    catch (e) { setErr(e.message || "Could not load the PEAD scoreboard"); }
  }, [token]);
  React.useEffect(() => { if (token) load(); }, [token, load]);
  const run = async () => {
    setBusy(true); setErr("");
    try { await apiFetch("/admin/pead-scoreboard/run", token, {method:"POST"}); setTimeout(() => { setBusy(false); load(); }, 1500); }
    catch (e) { setErr(e.message || "Could not queue the run"); setBusy(false); }
  };
  // queued = a run was requested after the newest finished run / job heartbeat -> poll until it lands
  const qT  = sb?.queued ? utc(sb.queued.ts) : null;
  const hbT = sb?.heartbeat ? utc(sb.heartbeat.ts) : null;
  const gT  = sb?.available ? utc(sb.current?.generated_at) : null;
  const pending = !!qT && (!hbT || qT > hbT) && (Date.now() - qT.getTime() < 40 * 60 * 1000);
  React.useEffect(() => {
    if (!pending) return;
    const id = setInterval(() => { load(); setTick(t => t + 1); }, 30000);
    return () => clearInterval(id);
  }, [pending, load]);
  const hbErr = sb?.heartbeat && typeof sb.heartbeat.result === "string" && sb.heartbeat.result.startsWith("error");
  const ago = (d) => { const m = Math.max(0, Math.round((Date.now() - d.getTime()) / 60000)); return m < 1 ? "just now" : m < 60 ? `${m} min ago` : `${Math.round(m / 60)} h ago`; };
  const cur  = sb && sb.available ? sb.current : null;
  const rows = cur ? (cur.rows || []).filter(r => r.window === "out_of_sample") : [];
  const isr  = cur ? (cur.rows || []).filter(r => r.window === "in_sample") : [];
  const vcol = (v) => /^confirms/.test(v || "") ? C.green : /^CONTRADICTS/.test(v || "") ? C.red : C.muted;
  const Table = ({data}) => (
    <div style={{overflowX:"auto"}}>
      <table style={{width:"100%", borderCollapse:"collapse", fontSize:10}}>
        <thead><tr style={{color:C.muted, textAlign:"left"}}>
          {["Cell","Hz","Months","Mean","t","Verdict"].map(h => <th key={h} style={{padding:"3px 6px 3px 0", fontWeight:700}}>{h}</th>)}
        </tr></thead>
        <tbody>
          {data.map((r, i) => (
            <tr key={i} style={{borderTop:`1px solid ${C.divider}`}}>
              <td style={{padding:"4px 6px 4px 0", color:C.text, fontWeight:700}}>{r.cell}{r.expected === "-" ? " ↓" : ""}</td>
              <td style={{color:C.muted}}>{r.horizon}d</td>
              <td style={{color:C.text}}>{r.n_months || 0}</td>
              <td style={{fontFamily:"monospace", color:C.text}}>{r.mean != null ? (r.mean >= 0 ? "+" : "") + r.mean.toFixed(r.unit === "IC" ? 3 : 2) : "–"}</td>
              <td style={{fontFamily:"monospace", color:C.text}}>{r.t != null ? r.t : "–"}</td>
              <td style={{color:vcol(r.verdict)}}>{r.verdict}</td>
            </tr>))}
        </tbody>
      </table>
    </div>);
  return (
    <div style={{background:C.card, border:`1px solid ${C.border}`, borderRadius:12, padding:"14px 16px", marginBottom:12}}>
      <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", gap:8, marginBottom:6}}>
        <div style={{fontSize:13, fontWeight:800, color:C.text}}>🧪 Earnings-drift check (out-of-sample)</div>
        <button onClick={run} disabled={busy} style={{fontSize:10, fontWeight:700, padding:"4px 10px", borderRadius:8, cursor:busy ? "default" : "pointer",
          background:"none", border:`1px solid ${C.border}`, color:busy ? C.muted : C.accent, fontFamily:"inherit"}}>
          {busy ? "Queued…" : "Run now"}</button>
      </div>
      <div style={{fontSize:10, color:C.muted, lineHeight:1.5, marginBottom:8}}>
        Research lead: bigger EPS surprises have tended to drift the same way for ~1–3 months (found on data we had already seen). Only reports
        AFTER {cur?.meta?.oos_start || "2026-10-09"} count as confirmation. One observation per reaction month; needs ≥ 6 months, ideally 12+.
        Runs on the 2nd of each month (can take ~15 min). Changes no score.
      </div>
      {err && <div style={{fontSize:11, color:C.red}}>{err}</div>}
      {/* run status: queued/running → finished → last job result (errors show here instead of silently nothing) */}
      {pending && (
        <div style={{fontSize:11, color:C.yellow, margin:"4px 0 8px", lineHeight:1.5}}>
          ⏳ Run queued {ago(qT)} — it's running on the manager (can take ~15 min if it must rebuild the price file). This card checks every 30 s.
        </div>
      )}
      {!pending && sb?.heartbeat && (
        <div style={{fontSize:10, color: hbErr ? C.red : C.muted, margin:"4px 0 8px", lineHeight:1.5}}>
          Last job {hbErr ? "FAILED" : "finished"} {ago(hbT)}{hbErr ? ` — ${String(sb.heartbeat.result).slice(0, 160)}` : ""}
        </div>
      )}
      {sb && !sb.available && !pending && <div style={{fontSize:11, color:C.muted}}>{sb.reason || "No runs yet."}</div>}
      {cur && (
        <>
          <div style={{fontSize:10, fontWeight:700, color:C.text, margin:"4px 0"}}>Out-of-sample ({cur.meta?.oos_events?.toLocaleString("en")} reports so far)</div>
          {rows.length ? <Table data={rows}/> : <div style={{fontSize:11, color:C.muted}}>No out-of-sample reports with matured returns yet.</div>}
          <details style={{marginTop:8}}>
            <summary style={{fontSize:10, color:C.muted, cursor:"pointer"}}>In-sample reference (already seen — selection-biased)</summary>
            <div style={{marginTop:6}}><Table data={isr}/></div>
          </details>
          <div style={{fontSize:9, color:C.muted, marginTop:6}}>
            {cur.meta?.events?.toLocaleString("en")} reports · reactions {cur.meta?.reaction_first} → {cur.meta?.reaction_last} · prices to {cur.meta?.prices_last} · run {String(cur.generated_at).slice(0,10)}
          </div>
        </>
      )}
    </div>
  );
}

function AdminScreen({ user, token, onBack }) {
  const [users,       setUsers]       = React.useState([]);
  const [loading,     setLoading]     = React.useState(true);
  const [error,       setError]       = React.useState("");
  const [msg,         setMsg]         = React.useState("");
  const [stats,       setStats]       = React.useState(null);
  const [showReset,   setShowReset]   = React.useState({});
  const [resetPw,     setResetPw]     = React.useState({});
  const [pendingReqs, setPendingReqs] = React.useState([]);
  const [tempPw,      setTempPw]      = React.useState({});
  // Distinguish "load failed" from "genuinely empty" so a transient API blip
  // (e.g. during a backend rebuild) never looks like deleted data. On failure
  // we keep the last-good list AND flag the error so a retry banner shows.
  const [pendingReqsErr, setPendingReqsErr] = React.useState(false);

  // ── Invite codes (v7.9.50) ─────────────────────────────────────────
  const [inviteCodes,    setInviteCodes]    = React.useState([]);
  const [inviteCodesErr, setInviteCodesErr] = React.useState(false);
  const [showCodeModal,  setShowCodeModal]  = React.useState(false);
  const [newCode,        setNewCode]        = React.useState({
    code: "", tier: "founder", max_uses: 1, email_bound: "",
    expires_in_days: "", notes: "",
  });
  const [creatingCode,   setCreatingCode]   = React.useState(false);

  // ── Tier edit popover (v7.9.60) ────────────────────────────────────
  // Tracks which user row's tier picker is open. Only one open at a time;
  // clicking another row closes the previous. null = no popover open.
  const [tierEditFor, setTierEditFor] = React.useState(null);

  // ── Invite codes filter (v7.9.62) ─────────────────────────────────
  // 'active' default — fully-used / expired / revoked codes auto-fall
  // into 'archived' so the active list stays focused on what's still
  // usable. User accounts who redeemed are unaffected.
  const [codeFilter, setCodeFilter] = React.useState("active");

  // ── Merged user management (v8.4.9) ────────────────────────────────
  const [abuseReport,    setAbuseReport]    = React.useState({});
  const [expandedUsers,  setExpandedUsers]  = React.useState({});
  const [restarting,     setRestarting]     = React.useState(false);
  const [restartMsg,     setRestartMsg]     = React.useState("");
  // ── Login history modal (v8.4.17) ──────────────────────────────────
  const [loginHistoryFor,  setLoginHistoryFor]  = React.useState(null); // {id, email, name}
  const [loginHistoryData, setLoginHistoryData] = React.useState([]);
  const [loginHistoryLoad, setLoginHistoryLoad] = React.useState(false);
  // ── Watchlist recovery modal (v8.4.78) ────────────────────────────
  const [wlRecoveryFor,    setWlRecoveryFor]    = React.useState(null); // {id, email, name}
  const [wlRecoveryData,   setWlRecoveryData]   = React.useState(null);
  const [wlRecoveryLoad,   setWlRecoveryLoad]   = React.useState(false);
  const [wlRestoring,      setWlRestoring]      = React.useState(false);
  const [wlRestoreMsg,     setWlRestoreMsg]      = React.useState("");
  // ── Newsletter signups (P2-3) ──────────────────────────────────────
  const [nlData,        setNlData]        = React.useState(null);   // {total, unsynced, beehiiv_configured, signups:[]}
  const [nlBackfilling, setNlBackfilling] = React.useState(false);
  // ── Weekly digest preview (Option B, Phase 1) ──────────────────────
  const [weeklyPreview, setWeeklyPreview] = React.useState(null);   // {text, beehiiv_markdown, session_date, flags}
  // ── Digest QA self-check flags (v8.4.154) ──────────────────────────
  const [digestQa, setDigestQa] = React.useState(null); // {full:{...}|null, r1k:{...}|null}
  // ── ML Insights (data health + score accuracy) ─────────────────────
  const [mlStats, setMlStats] = React.useState(null);
  const [mlAcc,   setMlAcc]   = React.useState(null);

  const setUserTier = async (u, newTier) => {
    try {
      // newTier is one of "founder", "friends_family", "beta", or null
      await apiFetch(`/admin/users/${u.id}/tier`, token, {
        method: "PATCH",
        body: JSON.stringify({ tier: newTier }),
      });
      setUsers(prev => prev.map(x =>
        x.id === u.id ? {...x, early_adopter_tier: newTier} : x));
      flash(`✅ ${u.name}: tier ${newTier ? `set to ${newTier}` : "cleared"}`);
      setTierEditFor(null);
    } catch(e) {
      flash(`❌ ${e.message}`);
    }
  };

  // ── System health (v7.9.55) — Manager VM disk/mem/cpu ──────────────
  // Polls /admin/system-health every 30s so the admin sees IO pressure
  // building up before Docker volume bloat takes the VM down. Errors are
  // recoverable — keep the last good payload visible while we retry.
  const [sysHealth,        setSysHealth]        = React.useState(null);
  const [sysHealthErr,     setSysHealthErr]     = React.useState("");
  const [sysHealthLoading, setSysHealthLoading] = React.useState(false);
  // Worker liveness is fetched SEPARATELY from /admin/workers/status so its
  // broker control broadcasts (slow / stalling when workers are down) can never
  // delay or hide the disk/mem/CPU dashboard above.
  const [workerStatus,     setWorkerStatus]     = React.useState(null);
  // v8.4.261: per-task Celery beat heartbeats (/admin/beat-status) — surfaces
  // when each scheduled task last ran + its result (e.g. warm_gap_scanner
  // "warmed N gappers") plus the dynamic/watchlist scoring-set sizes.
  const [beatStatus,       setBeatStatus]       = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;
    const load = async (silent = false) => {
      if (!silent) setSysHealthLoading(true);
      try {
        const d = await apiFetch("/admin/system-health", token);
        if (cancelled) return;
        setSysHealth(d);
        setSysHealthErr("");
      } catch (e) {
        if (cancelled) return;
        // Keep the last good payload visible; just surface the error.
        setSysHealthErr(e.message || "Health check failed");
      } finally {
        if (!cancelled && !silent) setSysHealthLoading(false);
      }
      // Independent, best-effort — never blocks/breaks the health card.
      try {
        const w = await apiFetch("/admin/workers/status", token);
        if (!cancelled) setWorkerStatus(w);
      } catch (_) { /* leave last-good worker status */ }
      try {
        const b = await apiFetch("/admin/beat-status", token);
        if (!cancelled) setBeatStatus(b);
      } catch (_) { /* leave last-good beat status */ }
    };
    load(false);
    const iv = setInterval(() => load(true), 30 * 1000);
    return () => { cancelled = true; clearInterval(iv); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const inp = {
    background:C.surface, border:`1px solid ${C.border}`,
    borderRadius:8, padding:"8px 12px", color:C.text,
    fontSize:16, fontFamily:"inherit", outline:"none",
    width:"100%", boxSizing:"border-box",
  };

  const flash = (m) => { setMsg(m); setTimeout(()=>setMsg(""), 3500); };

  // ── Newsletter signups loader + backfill (P2-3) ────────────────────
  const loadNewsletter = React.useCallback(async () => {
    try { setNlData(await apiFetch("/admin/newsletter-signups?limit=100", token)); }
    catch { setNlData(null); }
  }, [token]);
  React.useEffect(() => { loadNewsletter(); }, [loadNewsletter]);

  // ML Insights loaders (read-only diagnostics; Stage 1 SHAP lands Jul 2026)
  React.useEffect(() => {
    if (!token) return;
    apiFetch("/ml/stats", token).then(setMlStats).catch(() => setMlStats(null));
    apiFetch("/ml/accuracy?horizon=1m&min_score=0&bucket_size=10", token)
      .then(setMlAcc).catch(() => setMlAcc(null));
  }, [token]);

  // Digest QA self-check flags (v8.4.154)
  React.useEffect(() => {
    if (!token) return;
    apiFetch("/admin/digest-qa-flags", token).then(setDigestQa).catch(() => setDigestQa(null));
  }, [token]);

  const backfillNewsletter = async () => {
    if (nlBackfilling) return;
    setNlBackfilling(true);
    try {
      const r = await apiFetch("/admin/newsletter-backfill?limit=200", token, { method: "POST" });
      flash(`✅ Backfill: ${r.synced}/${r.attempted} synced · ${r.remaining_unsynced} left`);
      loadNewsletter();
    } catch(e) { flash(`❌ ${e.message}`); }
    finally { setNlBackfilling(false); }
  };

  const loadData = React.useCallback(async () => {
    setLoading(true); setError("");
    try {
      const [ud, pd, sd, cd, ar] = await Promise.allSettled([
        apiFetch("/admin/users",                          token),
        // No inner .catch here — let a rejection surface so we can show a retry
        // banner instead of silently blanking the list (which looked like data loss).
        apiFetch("/auth/pending-requests",                token),
        apiFetch("/admin/stats",                          token).catch(()=>null),
        // Fetch ALL codes (active + archived) in one call so the tab
        // counts can be computed client-side without a second roundtrip
        // when the user toggles between tabs.
        apiFetch("/admin/invite-codes?filter=all",        token),
        apiFetch("/admin/abuse-report",                   token).catch(()=>({flagged_users:[],sharing_warnings:[]})),
      ]);
      if (ud.status === "fulfilled") setUsers(ud.value.users || []);
      else setError(ud.reason?.message || "Failed to load users");
      // Pending requests / invite codes: on failure, KEEP the last-good list and
      // flag the error (retry banner) rather than overwriting with empty.
      if (pd.status === "fulfilled") { setPendingReqs(pd.value.requests || []); setPendingReqsErr(false); }
      else setPendingReqsErr(true);
      if (sd.status === "fulfilled" && sd.value) setStats(sd.value);
      if (cd.status === "fulfilled") { setInviteCodes(Array.isArray(cd.value) ? cd.value : []); setInviteCodesErr(false); }
      else setInviteCodesErr(true);
      if (ar.status === "fulfilled") {
        const m = {};
        (ar.value.flagged_users    || []).forEach(u => { m[u.user_id] = {...(m[u.user_id]||{}), flagged:true,  rate:u.request_count}; });
        (ar.value.sharing_warnings || []).forEach(u => { m[u.user_id] = {...(m[u.user_id]||{}), sharing:true,  ips:u.distinct_ips};   });
        setAbuseReport(m);
      }
    } catch(e) { setError(e.message); }
    finally { setLoading(false); }
  }, [token]);

  // ── Invite-code handlers ────────────────────────────────────────────
  const createInviteCode = async () => {
    setCreatingCode(true);
    try {
      const body = {
        code: newCode.code.trim() || undefined,
        early_adopter_tier: newCode.tier || undefined,
        max_uses: parseInt(newCode.max_uses, 10) || 1,
        email_bound: newCode.email_bound.trim() || undefined,
        expires_in_days: newCode.expires_in_days
          ? parseInt(newCode.expires_in_days, 10) : undefined,
        notes: newCode.notes.trim() || undefined,
      };
      const created = await apiFetch("/admin/invite-codes", token, {
        method:"POST", body: JSON.stringify(body),
      });
      // Surface the URL immediately so the admin can copy + share without a
      // second click. Reload the list so the new row appears.
      try {
        await navigator.clipboard?.writeText(created.invite_url);
        flash(`✅ Created ${created.code} — link copied to clipboard`);
      } catch (_) {
        flash(`✅ Created ${created.code}`);
      }
      setShowCodeModal(false);
      setNewCode({ code:"", tier:"founder", max_uses:1, email_bound:"",
                   expires_in_days:"", notes:"" });
      // Refresh list — pull ALL codes so both tabs stay accurate after a create
      const fresh = await apiFetch("/admin/invite-codes?filter=all", token).catch(()=>[]);
      setInviteCodes(Array.isArray(fresh) ? fresh : []);
    } catch (e) {
      flash(`❌ ${e.message}`);
    } finally {
      setCreatingCode(false);
    }
  };

  const copyInviteUrl = async (c) => {
    try {
      await navigator.clipboard.writeText(c.invite_url);
      flash(`✅ Copied invite link for ${c.code}`);
    } catch (_) {
      // Fallback: select-and-prompt for browsers without clipboard API
      window.prompt("Copy this invite link:", c.invite_url);
    }
  };

  const revokeInviteCode = async (c) => {
    if (!window.confirm(`Revoke invite code ${c.code}? Existing users who already used it stay valid; future redemptions will fail.`)) return;
    try {
      await apiFetch(`/admin/invite-codes/${c.id}`, token, { method:"DELETE" });
      setInviteCodes(prev => prev.filter(x => x.id !== c.id));
      flash(`✅ Revoked ${c.code}`);
    } catch (e) {
      flash(`❌ ${e.message}`);
    }
  };

  React.useEffect(() => { loadData(); }, [loadData]);

  const handleToggle = async (u) => {
    const verb = u.is_active ? "Deactivate" : "Reactivate";
    const note = u.is_active
      ? "Suspends access immediately. Reversible via Reactivate."
      : "Restores access for this user.";
    if (!window.confirm(`${verb} ${u.name}?\n\n${note}`)) return;
    try {
      await apiFetch(`/auth/admin/toggle/${u.id}`, token, { method:"POST" });
      setUsers(prev => prev.map(x => x.id===u.id ? {...x, is_active:!x.is_active} : x));
      flash(`✅ ${u.name} ${u.is_active ? "deactivated" : "reactivated"}`);
    } catch(e) { flash(`❌ ${e.message}`); }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Permanently delete ${u.name}? This cannot be undone.`)) return;
    try {
      await apiFetch(`/auth/admin/delete/${u.id}`, token, { method:"DELETE" });
      setUsers(prev => prev.filter(x => x.id !== u.id));
      flash(`✅ ${u.name} deleted`);
    } catch(e) { flash(`❌ ${e.message}`); }
  };

  const handleResetPw = async (u) => {
    if (!resetPw[u.id]?.trim()) { flash("❌ Enter a new password first"); return; }
    if (resetPw[u.id].length < 8) { flash("❌ Minimum 8 characters"); return; }
    try {
      await apiFetch(`/auth/admin/reset-password/${u.id}`, token, {
        method:"POST", body:JSON.stringify({ new_password: resetPw[u.id] }),
      });
      flash(`✅ Password reset for ${u.name}`);
      setResetPw(prev => ({...prev, [u.id]:""}));
      setShowReset(prev => ({...prev, [u.id]:false}));
    } catch(e) { flash(`❌ ${e.message}`); }
  };

  const handleApprove = async (req) => {
    // v8.4.66: leave blank → backend auto-generates a 10-char URL-safe
    // temp password and emails it via Resend. The success toast surfaces
    // the generated value so the admin can still share it manually if
    // the Resend email is delayed or filtered.
    const supplied = tempPw[req.id]?.trim() || "";
    try {
      const resp = await apiFetch(`/auth/admin/approve/${req.id}`, token, {
        method:"POST", body:JSON.stringify({ temp_password: supplied }),
      });
      setPendingReqs(prev => prev.filter(r => r.id !== req.id));
      const generated = !supplied && resp?.temp_password;
      const emailNote = resp?.email_sent === false ? " · ⚠ email failed" : "";
      flash(generated
        ? `✅ ${req.name} approved · auto pw: ${resp.temp_password}${emailNote}`
        : `✅ ${req.name} approved${emailNote}`);
    } catch(e) { flash(`❌ ${e.message}`); }
  };

  const handleDeny = async (req) => {
    try {
      await apiFetch(`/auth/admin/deny/${req.id}`, token, { method:"POST" });
      setPendingReqs(prev => prev.filter(r => r.id !== req.id));
      flash(`✅ Request from ${req.name} denied`);
    } catch(e) { flash(`❌ ${e.message}`); }
  };

  const handleBan = async (u) => {
    const reason = window.prompt(`Ban reason for ${u.name} (${u.email})?`, "ToS violation");
    if (reason === null) return; // cancelled
    try {
      await apiFetch(`/admin/ban/${u.id}`, token, {
        method:"POST", body: JSON.stringify({ reason: reason || "no reason given" }),
      });
      setUsers(prev => prev.map(x => x.id===u.id ? {...x, is_active:false, is_banned:true} : x));
      flash(`🚫 Banned ${u.email}`);
    } catch(e) { flash(`❌ ${e.message}`); }
  };

  const handleUnban = async (u) => {
    if (!window.confirm(`Unban ${u.name} (${u.email})?`)) return;
    try {
      await apiFetch(`/admin/unban/${u.id}`, token, { method:"POST" });
      setUsers(prev => prev.map(x => x.id===u.id ? {...x, is_active:true, is_banned:false} : x));
      flash(`✅ Unbanned ${u.email}`);
    } catch(e) { flash(`❌ ${e.message}`); }
  };

  const handleRestartApi = async () => {
    if (!window.confirm("Restart the API server? It will be unavailable for ~15 seconds.")) return;
    setRestarting(true); setRestartMsg("");
    try {
      await apiFetch("/admin/restart", token, { method:"POST" });
      setRestartMsg("⏳ Restarting… polling until back online");
      let attempts = 0;
      const poll = setInterval(async () => {
        attempts++;
        try {
          const h = await apiFetch("/health", token);
          if (h?.status === "ok") {
            clearInterval(poll); setRestarting(false);
            setRestartMsg("✅ API back online — v" + (h.version || ""));
          }
        } catch(_) {}
        if (attempts > 20) { clearInterval(poll); setRestarting(false); setRestartMsg("⚠️ Timeout — check VM"); }
      }, 2000);
    } catch(e) { setRestarting(false); setRestartMsg(`❌ ${e.message}`); }
  };

  const fmt = iso => {
    if (!iso) return "—";
    try {
      return new Date(iso+"Z").toLocaleString(undefined,
        {month:"short",day:"numeric",hour:"2-digit",minute:"2-digit"});
    } catch(e) { return iso?.slice(0,16)||"—"; }
  };

  const onlineCount = users.filter(u=>u.is_online).length;

  return (
    <div style={{padding:"0 16px 100px"}}>

      {/* Header (frozen) */}
      <FrozenHeader padTop={16} padBottom={14}>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <button onClick={onBack} style={{background:C.surface,border:`1px solid ${C.border}`,
            borderRadius:10,padding:"6px 12px",color:C.muted,fontSize:13,
            cursor:"pointer",fontFamily:"inherit"}}>← Back</button>
          <div style={{flex:1}}>
            <div style={{fontSize:18,fontWeight:900,color:C.text}}>🛡 Admin Dashboard</div>
            <div style={{fontSize:11,color:C.muted,marginTop:1}}>
              {users.length} registered · {onlineCount} online now
            </div>
          </div>
          <button onClick={loadData} style={{background:"none",border:"none",
            color:C.accent,fontSize:18,cursor:"pointer",fontFamily:"inherit"}}
            title="Refresh">↻</button>
        </div>
      </FrozenHeader>
      <div style={{height:16}}/>

      {/* Flash message */}
      {msg && (
        <div style={{fontSize:12,padding:"8px 12px",borderRadius:8,marginBottom:12,
          background:msg.startsWith("✅")?"rgba(0,230,118,0.1)":"rgba(255,82,82,0.1)",
          color:msg.startsWith("✅")?C.green:C.red,
          border:`1px solid ${msg.startsWith("✅")?"rgba(0,230,118,0.3)":"rgba(255,82,82,0.3)"}`}}>
          {msg}
        </div>
      )}

      {error && (
        <div style={{fontSize:12,color:C.red,padding:"10px 12px",borderRadius:8,
          background:"rgba(255,82,82,0.08)",marginBottom:12}}>{error}</div>
      )}

      {loading && (
        <div style={{textAlign:"center",padding:32,color:C.muted}}>
          <div style={{width:28,height:28,border:`3px solid rgba(0,212,255,0.2)`,
            borderTopColor:C.accent,borderRadius:"50%",
            animation:"spin 0.8s linear infinite",margin:"0 auto 10px"}}/>
          Loading users…
        </div>
      )}

      <ScoreScoreboardCard token={token}/>
      <PeadScoreboardCard token={token}/>

      {/* ── Digest QA self-check flags (v8.4.154) ── */}
      {digestQa && (digestQa.full?.flags?.length > 0 || digestQa.r1k?.flags?.length > 0) && (
        <div style={{background:"rgba(255,202,40,0.08)", border:"1px solid rgba(255,202,40,0.35)",
          borderRadius:12, padding:"14px 16px", marginBottom:12}}>
          <div style={{fontSize:13, fontWeight:800, color:"#ffca28", marginBottom:8}}>
            ⚠ Digest QA flags
          </div>
          {["r1k","full"].map(kind => {
            const d = digestQa[kind];
            if (!d || !(d.flags || []).length) return null;
            return (
              <div key={kind} style={{marginBottom:8}}>
                <div style={{fontSize:11, color:C.muted, marginBottom:4}}>
                  {kind === "r1k" ? "R1K (production)" : "Full"} digest — {d.date}
                </div>
                <ul style={{margin:0, paddingLeft:18, fontSize:11.5, color:C.text, lineHeight:1.6}}>
                  {d.flags.map((f, i) => <li key={i}>{f}</li>)}
                </ul>
              </div>
            );
          })}
          <div style={{fontSize:10, color:C.muted, marginTop:4}}>
            Advisory only — sends were NOT blocked. Review and consider follow-up prompt-rule fixes.
          </div>
        </div>
      )}

      {/* ── Newsletter signups (P2-3) — funnel visibility + backfill ── */}
      {/* Weekly recap preview (Option B, Phase 1) — review before Phase 2 wires sends */}
      {weeklyPreview && (
        <div style={{background:C.surface, border:`1px solid rgba(0,230,118,0.3)`,
          borderRadius:12, padding:"14px 16px", marginBottom:12}}>
          <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10, gap:8, flexWrap:"wrap"}}>
            <div style={{fontSize:13, fontWeight:800, color:C.text}}>
              🗓 Weekly Recap — {weeklyPreview.session_date}
              {weeklyPreview.flags?.length ? (
                <span style={{fontSize:10, color:"#ffca28", marginLeft:8}}>⚠ {weeklyPreview.flags.length} QA flag(s)</span>
              ) : null}
            </div>
            <div style={{display:"flex", gap:6}}>
              <button onClick={async ()=>{
                  try { await navigator.clipboard.writeText(weeklyPreview.beehiiv_markdown || weeklyPreview.text); flash("✅ Copied weekly recap — paste into Beehiiv"); }
                  catch(e) { flash(`❌ ${e.message}`); }
                }} style={{fontSize:10,fontWeight:700,background:"rgba(255,202,40,0.08)",color:"#ffca28",
                  border:"1px solid rgba(255,202,40,0.3)",borderRadius:6,padding:"3px 10px",cursor:"pointer",fontFamily:"inherit"}}>📋 Copy for Beehiiv</button>
              <button onClick={()=>setWeeklyPreview(null)} style={{fontSize:10,fontWeight:700,background:"none",color:C.muted,
                  border:`1px solid ${C.border}`,borderRadius:6,padding:"3px 10px",cursor:"pointer",fontFamily:"inherit"}}>✕</button>
            </div>
          </div>
          {weeklyPreview.flags?.length > 0 && (
            <ul style={{margin:"0 0 8px", paddingLeft:18, fontSize:11, color:"#ffca28", lineHeight:1.5}}>
              {weeklyPreview.flags.map((f,i)=><li key={i}>{f}</li>)}
            </ul>
          )}
          <div style={{whiteSpace:"pre-wrap", fontSize:12.5, color:C.text, lineHeight:1.6,
            maxHeight:380, overflowY:"auto", background:C.card, borderRadius:8, padding:"12px 14px",
            border:`1px solid ${C.border}`, fontFamily:"inherit"}}>{weeklyPreview.text}</div>
          <div style={{fontSize:10, color:C.muted, marginTop:8, fontStyle:"italic"}}>
            Preview only — this does not email any users. Phase 2 adds the daily/weekly toggle + Friday auto-send.
          </div>
        </div>
      )}

      <ServiceNoticeCard token={token} flash={flash}/>

      {nlData && (
        <div style={{background:C.surface, border:`1px solid ${C.border}`,
          borderRadius:12, padding:"14px 16px", marginBottom:12}}>
          <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10}}>
            <div style={{fontSize:13, fontWeight:800, color:C.text}}>💌 Newsletter signups</div>
            <button onClick={loadNewsletter} title="Refresh" style={{background:"none",
              border:`1px solid ${C.border}`, borderRadius:8, color:C.muted, fontSize:11,
              cursor:"pointer", padding:"4px 10px", fontFamily:"inherit"}}>↻</button>
          </div>
          <div style={{display:"flex", gap:20, marginBottom:10}}>
            <div>
              <div style={{fontSize:20, fontWeight:900, color:C.text}}>{nlData.total}</div>
              <div style={{fontSize:10, color:C.muted}}>total</div>
            </div>
            <div>
              <div style={{fontSize:20, fontWeight:900, color: nlData.unsynced > 0 ? "#ffca28" : C.green}}>{nlData.unsynced}</div>
              <div style={{fontSize:10, color:C.muted}}>unsynced</div>
            </div>
          </div>
          {!nlData.beehiiv_configured && (
            <div style={{fontSize:11, color:"#ffca28", marginBottom:8, lineHeight:1.5}}>
              ⚠ Beehiiv not configured — set BEEHIIV_API_KEY + BEEHIIV_PUB_ID on the VM, then backfill.
            </div>
          )}
          {nlData.unsynced > 0 && nlData.beehiiv_configured && (
            <button onClick={backfillNewsletter} disabled={nlBackfilling} style={{
              width:"100%", padding:"9px", borderRadius:9, border:`1px solid ${C.accent}`,
              background:"rgba(0,212,255,0.12)", color:C.accent, fontSize:12, fontWeight:700,
              fontFamily:"inherit", cursor: nlBackfilling ? "not-allowed" : "pointer",
              marginBottom:10, opacity: nlBackfilling ? 0.7 : 1}}>
              {nlBackfilling ? "Backfilling…" : `Backfill ${nlData.unsynced} to Beehiiv`}
            </button>
          )}
          {(nlData.signups || []).length > 0 && (
            <div style={{display:"flex", flexDirection:"column", gap:4, maxHeight:160, overflowY:"auto"}}>
              {nlData.signups.slice(0, 30).map((s, i) => (
                <div key={i} style={{display:"flex", justifyContent:"space-between", alignItems:"center",
                  fontSize:11, color:C.muted, padding:"3px 0", borderBottom:"1px solid rgba(255,255,255,0.04)"}}>
                  <span style={{color:C.text, overflow:"hidden", textOverflow:"ellipsis",
                    whiteSpace:"nowrap", maxWidth:"62%"}}>{s.email}</span>
                  <span style={{fontFamily:"monospace", fontSize:10}}>
                    {s.synced ? "✓ synced" : "○ local"} · {s.source || "—"}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── ML Insights — data-collection health + score accuracy ── */}
      {mlStats && mlStats.status !== "not_ready" && (
        <div style={{background:C.surface, border:`1px solid ${C.border}`,
          borderRadius:12, padding:"14px 16px", marginBottom:12}}>
          <div style={{fontSize:13, fontWeight:800, color:C.text, marginBottom:10}}>🧪 ML Insights</div>

          {/* DB stats */}
          <div style={{fontSize:11, color:C.muted, lineHeight:1.7, marginBottom:12}}>
            <span style={{color:C.text, fontWeight:700}}>{(mlStats.score_snapshots?.cnt || 0).toLocaleString()}</span> snapshots ·{" "}
            <span style={{color:C.text, fontWeight:700}}>{(mlStats.score_variants?.cnt || 0).toLocaleString()}</span> variants ·{" "}
            {mlStats.score_snapshots?.tickers || 0} tickers · {mlStats.db_size_mb || 0} MB<br/>
            range {mlStats.score_snapshots?.oldest || "—"} → {mlStats.score_snapshots?.newest || "—"}
          </div>

          {/* Forward-return fill progress */}
          {mlStats.forward_returns && (
            <div style={{marginBottom:12}}>
              <div style={{fontSize:10, color:C.muted, textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:6}}>
                Forward-return data fill
              </div>
              {[["1m","actual_return_1m"],["3m","actual_return_3m"],["6m","actual_return_6m"],["1y","actual_return_1y"]].map(([lbl, col]) => {
                const fr = mlStats.forward_returns[col] || {eligible:0, filled:0};
                const pct = fr.eligible > 0 ? Math.round(100 * (fr.filled || 0) / fr.eligible) : 0;
                return (
                  <div key={col} style={{display:"flex", alignItems:"center", gap:8, marginBottom:4}}>
                    <span style={{fontSize:11, color:C.text, width:22, fontFamily:"monospace"}}>{lbl}</span>
                    <div style={{flex:1, height:5, background:"rgba(255,255,255,0.06)", borderRadius:3, overflow:"hidden"}}>
                      <div style={{height:"100%", width:`${pct}%`, background: pct > 0 ? C.accent : "transparent", borderRadius:3}}/>
                    </div>
                    <span style={{fontSize:10, color:C.muted, fontFamily:"monospace", width:92, textAlign:"right"}}>
                      {(fr.filled || 0).toLocaleString()}/{(fr.eligible || 0).toLocaleString()}
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          {/* Score accuracy (1m forward return) */}
          <div style={{fontSize:10, color:C.muted, textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:6}}>
            Score accuracy · 1-month forward return
          </div>
          {(!mlAcc || mlAcc.status !== "ok" || !(mlAcc.buckets || []).length) ? (
            <div style={{fontSize:11, color:C.muted}}>
              {mlAcc && mlAcc.status && mlAcc.status !== "ok"
                ? `Pending — ${(mlAcc.pending_rows || 0).toLocaleString()} rows awaiting 1m forward returns.`
                : "No accuracy data yet."}
            </div>
          ) : (
            <div style={{display:"flex", flexDirection:"column", gap:2}}>
              <div style={{display:"flex", fontSize:9, color:C.muted, textTransform:"uppercase",
                letterSpacing:"0.05em", paddingBottom:3, borderBottom:"1px solid rgba(255,255,255,0.06)"}}>
                <span style={{flex:1}}>Score</span><span style={{width:46, textAlign:"right"}}>N</span>
                <span style={{width:64, textAlign:"right"}}>Avg Ret</span><span style={{width:52, textAlign:"right"}}>Win %</span>
              </div>
              {mlAcc.buckets.map((b, i) => (
                <div key={i} style={{display:"flex", fontSize:11, color:C.text, padding:"2px 0", fontFamily:"monospace"}}>
                  <span style={{flex:1}}>{b.bucket_floor}–{b.bucket_floor + (mlAcc.bucket_size || 10)}</span>
                  <span style={{width:46, textAlign:"right", color:C.muted}}>{b.n}</span>
                  <span style={{width:64, textAlign:"right", color: (b.avg_return || 0) > 0 ? C.green : C.red}}>
                    {(b.avg_return || 0) > 0 ? "+" : ""}{(b.avg_return || 0).toFixed(1)}%
                  </span>
                  <span style={{width:52, textAlign:"right",
                    color: b.win_rate_pct >= 60 ? C.green : b.win_rate_pct >= 50 ? C.yellow : C.red}}>
                    {(b.win_rate_pct || 0).toFixed(0)}%
                  </span>
                </div>
              ))}
            </div>
          )}

          <div style={{fontSize:9, color:C.muted, marginTop:10, lineHeight:1.5, opacity:0.85}}>
            Stage 1 (Jul 12 2026): LightGBM feature-importance / SHAP ranking lands here once 3-month forward returns fill.
          </div>
        </div>
      )}

      {/* ── System Health (v7.9.55) ───────────────────────────────
          Manager VM disk / memory / CPU stats — early warning for IO
          pressure before Docker volume bloat takes the API down. Polled
          every 30s by the load effect above. Color-coded: green healthy,
          amber warning, red critical (matching backend's status flag). */}
      {(sysHealth || sysHealthLoading) && (() => {
        const fmtBytes = (b) => {
          if (!b && b !== 0) return "—";
          if (b >= 1024**4) return `${(b/1024**4).toFixed(2)} TB`;
          if (b >= 1024**3) return `${(b/1024**3).toFixed(1)} GB`;
          if (b >= 1024**2) return `${(b/1024**2).toFixed(0)} MB`;
          if (b >= 1024)    return `${(b/1024).toFixed(0)} KB`;
          return `${b} B`;
        };
        const fmtUptime = (s) => {
          if (!s && s !== 0) return "—";
          const d = Math.floor(s / 86400);
          const h = Math.floor((s % 86400) / 3600);
          const m = Math.floor((s % 3600) / 60);
          if (d > 0) return `${d}d ${h}h`;
          if (h > 0) return `${h}h ${m}m`;
          return `${m}m`;
        };
        const StatusBadge = ({status}) => {
          const colors = {
            healthy:  {bg:"rgba(0,230,118,0.12)",  fg:C.green,  border:"rgba(0,230,118,0.3)",  label:"✓ Healthy"},
            warning:  {bg:"rgba(255,202,40,0.12)", fg:"#ffca28",border:"rgba(255,202,40,0.4)", label:"⚠ Warning"},
            critical: {bg:"rgba(255,82,82,0.15)",  fg:C.red,    border:"rgba(255,82,82,0.5)",  label:"✕ Critical"},
          }[status] || {bg:"rgba(255,255,255,0.06)",fg:C.muted,border:C.border,label:status||"—"};
          return (
            <span style={{fontSize:11,fontWeight:800,
              background:colors.bg,color:colors.fg,
              border:`1px solid ${colors.border}`,
              borderRadius:14,padding:"3px 11px"}}>
              {colors.label}
            </span>
          );
        };
        const ProgressBar = ({label, percent, used, total, warnAt=70, critAt=85, sub=""}) => {
          const pct = Math.max(0, Math.min(100, Number(percent) || 0));
          const color = pct >= critAt ? C.red
                      : pct >= warnAt ? "#ffca28"
                      : pct >= warnAt - 20 ? "#66bb6a" : "#00e676";
          return (
            <div style={{marginBottom:10}}>
              <div style={{display:"flex",justifyContent:"space-between",
                alignItems:"baseline",marginBottom:4}}>
                <span style={{fontSize:11,fontWeight:700,color:C.text,
                  letterSpacing:"0.04em",textTransform:"uppercase"}}>{label}</span>
                <span style={{fontSize:12,fontWeight:800,color,fontFamily:"monospace"}}>
                  {pct.toFixed(1)}%
                </span>
              </div>
              <div style={{height:6,background:"rgba(255,255,255,0.06)",
                borderRadius:3,overflow:"hidden"}}>
                <div style={{height:"100%",width:`${pct}%`,background:color,
                  borderRadius:3,transition:"width 0.4s ease"}}/>
              </div>
              <div style={{display:"flex",justifyContent:"space-between",
                fontSize:9,color:C.muted,marginTop:3,fontFamily:"monospace"}}>
                <span>{sub}</span>
                {used != null && total != null && (
                  <span>{fmtBytes(used)} / {fmtBytes(total)}</span>
                )}
              </div>
            </div>
          );
        };

        const status     = sysHealth?.status || (sysHealthLoading ? "checking" : "—");
        const statusColor = {
          healthy:  "rgba(0,230,118,0.25)",
          warning:  "rgba(255,202,40,0.4)",
          critical: "rgba(255,82,82,0.5)",
        }[status] || C.border;

        return (
          <div style={{marginBottom:16,
            background:C.surface,borderRadius:14,padding:"14px 16px",
            border:`1px solid ${statusColor}`}}>
            <div style={{display:"flex",alignItems:"center",
              justifyContent:"space-between",marginBottom:12,flexWrap:"wrap",gap:8}}>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <span style={{fontSize:12,fontWeight:800,color:C.text,
                  letterSpacing:"0.06em",textTransform:"uppercase"}}>
                  🖥 System Health
                </span>
                <StatusBadge status={status}/>
              </div>
              <span style={{fontSize:9,color:C.muted,fontFamily:"monospace"}}>
                {sysHealth?.checked_at
                  ? `Checked ${Math.max(0, Math.floor((Date.now()/1000) - sysHealth.checked_at))}s ago`
                  : sysHealthLoading ? "Checking…" : "—"}
              </span>
            </div>

            {sysHealthErr && (
              <div style={{fontSize:11,color:C.red,marginBottom:10,
                padding:"6px 10px",background:"rgba(255,82,82,0.08)",
                borderRadius:6}}>
                ⚠ {sysHealthErr}
                {sysHealth && <span style={{color:C.muted,marginLeft:6}}>
                  (showing last good reading)
                </span>}
              </div>
            )}

            {sysHealth && (
              <>
                {/* Critical alert banner — surfaces what to do, not just the
                    diagnosis. Only renders when status=critical. */}
                {sysHealth.status === "critical" && (
                  <div style={{fontSize:11,color:C.red,marginBottom:12,
                    padding:"8px 12px",background:"rgba(255,82,82,0.08)",
                    border:"1px solid rgba(255,82,82,0.3)",borderRadius:8,
                    lineHeight:1.6}}>
                    <strong>Action recommended:</strong> SSH to the VM and run{" "}
                    <code style={{background:"rgba(255,255,255,0.06)",
                      padding:"1px 5px",borderRadius:3,fontFamily:"monospace",
                      fontSize:10}}>docker system prune -a --volumes</code>{" "}
                    to reclaim Docker volume + image bloat. Check the "Top
                    directories" list below for hot spots.
                  </div>
                )}

                <ProgressBar label="Disk"
                  percent={sysHealth.disk?.percent}
                  used={sysHealth.disk?.used_bytes}
                  total={sysHealth.disk?.total_bytes}
                  warnAt={70} critAt={85}
                  sub={`${fmtBytes(sysHealth.disk?.free_bytes)} free · ${sysHealth.disk?.path || "/"}`}/>
                <ProgressBar label="Memory"
                  percent={sysHealth.memory?.percent}
                  used={sysHealth.memory?.used_bytes}
                  total={sysHealth.memory?.total_bytes}
                  warnAt={80} critAt={90}
                  sub={`${fmtBytes(sysHealth.memory?.available_bytes)} available`}/>
                {sysHealth.swap?.total_bytes > 0 && (
                  <ProgressBar label="Swap"
                    percent={sysHealth.swap?.percent}
                    used={sysHealth.swap?.used_bytes}
                    total={sysHealth.swap?.total_bytes}
                    warnAt={30} critAt={50}
                    sub="Sustained swap = memory pressure"/>
                )}
                <ProgressBar label="CPU"
                  percent={sysHealth.cpu?.percent}
                  warnAt={75} critAt={90}
                  sub={`${sysHealth.cpu?.count || "?"} cores`
                       + (sysHealth.cpu?.load_1m != null
                          ? ` · load ${sysHealth.cpu.load_1m}/${sysHealth.cpu.load_5m}/${sysHealth.cpu.load_15m}`
                          : "")}/>

                {/* Celery workers — online vs expected. The API serves scores
                    from cache so a dead worker is otherwise invisible; this is
                    the early warning that scoring has silently stopped. */}
                {workerStatus && (() => {
                  const w   = workerStatus;
                  const st  = w.status;
                  const col = st==="healthy" ? C.green
                            : st==="warning" ? "#ffa500"
                            : st==="critical"? C.red : C.muted;
                  const lbl = st==="healthy" ? "All online"
                            : st==="warning" ? "Degraded"
                            : st==="critical"? "Down" : "Unknown";

                  // Prune disk on worker host(s). workerName="" → all workers.
                  // Mirrors the manager Quick/Deep prune flow + refresh.
                  const doWorkerPrune = async (workerName, deep) => {
                    const who = workerName || "ALL worker hosts";
                    if (!window.confirm(
                      (deep ? "DEEP prune " : "Prune ") + who + "?\n\n"
                      + "Runs `docker system prune" + (deep ? " -a --volumes" : " -f") + "` on "
                      + (workerName ? "this worker's host." : "every worker host.")
                      + (deep ? "\n\nDeep also removes ALL unused images + volumes (slower next deploy)." : "")
                      + "\n\nActive services are unaffected."
                    )) return;
                    try {
                      const params = new URLSearchParams();
                      if (workerName) params.set("worker", workerName);
                      if (deep)       params.set("deep", "true");
                      const qs = params.toString();
                      const d = await apiFetch(`/admin/workers/prune${qs?`?${qs}`:""}`,
                        token, { method:"POST" });
                      flash(d.detail || "Worker prune queued");
                      // Async: the worker picks the command up within ~10s and writes
                      // its reclaim result back. Poll a couple of times to surface it.
                      const refresh = async () => {
                        try { setWorkerStatus(await apiFetch("/admin/workers/status", token)); } catch(_) {}
                      };
                      setTimeout(refresh, 13000);
                      setTimeout(refresh, 22000);
                    } catch(e) {
                      flash(`❌ ${e.message}`);
                    }
                  };
                  const btn = (bg,bd,fg) => ({fontSize:9,fontWeight:700,background:bg,
                    color:fg,border:`1px solid ${bd}`,borderRadius:6,padding:"3px 8px",
                    cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap"});

                  return (
                    <div style={{marginTop:12,padding:"10px 12px",background:C.card,
                      borderRadius:8,border:`1px solid ${col!==C.green?col:C.border}`}}>
                      <div style={{display:"flex",justifyContent:"space-between",
                        alignItems:"center",gap:8,flexWrap:"wrap",
                        marginBottom:(w.workers?.length||st!=="healthy")?8:0}}>
                        <div style={{display:"flex",alignItems:"center",gap:8}}>
                          <span style={{width:9,height:9,borderRadius:"50%",background:col,
                            boxShadow:`0 0 6px ${col}88`,display:"inline-block",flexShrink:0}}/>
                          <span style={{fontSize:11,fontWeight:700,color:C.text}}>Celery Workers</span>
                          <span style={{fontSize:12,fontWeight:800,fontFamily:"monospace",color:col}}>
                            {w.online}/{w.expected} · {lbl}
                          </span>
                        </div>
                        {w.online > 0 && (
                          <div style={{display:"flex",gap:5}}>
                            <button onClick={()=>doWorkerPrune("", false)}
                              style={btn("rgba(0,212,255,0.10)",C.accent,C.accent)}>🧹 Trim All</button>
                            <button onClick={()=>doWorkerPrune("", true)}
                              style={btn("rgba(255,82,82,0.08)","rgba(255,82,82,0.4)",C.red)}>Deep</button>
                          </div>
                        )}
                      </div>
                      {st==="critical" && (
                        <div style={{fontSize:10,color:C.red,marginBottom:w.workers?.length?8:0,lineHeight:1.5}}>
                          No workers responding — scoring is frozen (cache still serving stale scores).
                          On the worker VM(s): <code style={{background:"rgba(255,255,255,0.06)",
                            padding:"1px 4px",borderRadius:3,fontFamily:"monospace"}}>docker compose up -d celery-worker</code>.
                        </div>
                      )}
                      {st==="warning" && (
                        <div style={{fontSize:10,color:"#ffa500",marginBottom:w.workers?.length?8:0,lineHeight:1.5}}>
                          {w.expected - w.online} of {w.expected} worker{(w.expected-w.online)===1?"":"s"} offline — check the other VM node(s).
                        </div>
                      )}
                      {st==="unknown" && (
                        <div style={{fontSize:10,color:C.muted,marginBottom:0,lineHeight:1.5}}>
                          Couldn't inspect workers{w.error?`: ${w.error}`:""}.
                        </div>
                      )}
                      {w.workers?.length > 0 && (
                        <div style={{display:"flex",flexDirection:"column",gap:6}}>
                          {w.workers.map(n => {
                            const dp = n.disk_percent;
                            const dc = dp==null ? C.muted : dp>=85 ? C.red : dp>=70 ? "#ffa500" : C.green;
                            // Stale heartbeat (>40s) = node likely dropping off.
                            const stale = (n.last_seen_seconds!=null && n.last_seen_seconds > 40);
                            const lp = n.last_prune;
                            return (
                              <div key={n.name} style={{display:"flex",alignItems:"center",
                                justifyContent:"space-between",gap:8}}>
                                <div style={{minWidth:0,flex:1}}>
                                  <div style={{fontSize:10,fontFamily:"monospace",color:C.text,
                                    overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                                    <span style={{color:stale?"#ffa500":C.green}}>●</span> {n.name}
                                    {n.version && <span style={{color:C.muted,fontSize:8,fontWeight:600}}> v{n.version}</span>}
                                    {n.db_backend === "sqlite" && (
                                      <span title="No DATABASE_URL — DB-backed tasks (digest/alerts/watchlist) read an empty DB on this node"
                                        style={{color:C.red,fontSize:8,fontWeight:700}}> ⚠ no DB</span>
                                    )}
                                    {stale && <span style={{color:"#ffa500",fontSize:8}}> (stale {n.last_seen_seconds}s)</span>}
                                  </div>
                                  <div style={{fontSize:9,color:C.muted,fontFamily:"monospace",marginTop:1}}>
                                    {n.concurrency!=null?`${n.concurrency} proc`:""}
                                    {n.uptime_seconds!=null?` · up ${fmtUptime(n.uptime_seconds)}`:""}
                                    {dp!=null && <> · disk <span style={{color:dc,fontWeight:700}}>{dp}%</span>
                                      {n.disk_free_bytes!=null?` (${fmtBytes(n.disk_free_bytes)} free)`:""}</>}
                                  </div>
                                  {n.pending_cmd && (
                                    <div style={{fontSize:9,color:C.accent,marginTop:1}}>⏳ trim queued…</div>
                                  )}
                                  {!n.pending_cmd && lp && (
                                    <div style={{fontSize:9,marginTop:1,
                                      color: lp.ok===false ? C.red : C.green}}>
                                      {lp.ok===false
                                        ? `⚠ ${lp.detail||"trim failed"}`
                                        : `✓ trimmed ${fmtBytes(lp.total_bytes||0)}`}
                                    </div>
                                  )}
                                </div>
                                <button onClick={()=>doWorkerPrune(n.name, false)}
                                  style={btn("rgba(0,212,255,0.10)",C.accent,C.accent)}>Trim</button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {w.workers?.length > 0 && (
                        <div style={{fontSize:8.5,color:C.muted,marginTop:8,lineHeight:1.45,fontStyle:"italic"}}>
                          Trim runs `docker system prune` on the worker host (needs docker.sock mounted in
                          docker-compose.worker.yml + the updated image). Reclaims build-cache/dangling-image
                          bloat from `--no-cache` rebuilds — the usual cause of worker disk-full IO errors.
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* v8.4.261 — Scheduled Tasks (Celery beat heartbeats). Reads
                    /admin/beat-status: each task's last-run age + result, plus
                    the durable watchlist / churning dynamic scoring-set sizes. */}
                {beatStatus?.tasks && (
                  <div style={{marginTop:12,padding:"10px 12px",background:C.card,
                    borderRadius:8,border:`1px solid ${C.border}`}}>
                    <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",
                      justifyContent:"space-between",marginBottom:8}}>
                      <span style={{fontSize:11,fontWeight:700,color:C.text}}>Scheduled Tasks</span>
                      <span style={{fontSize:9,fontFamily:"monospace",color:C.muted}}>
                        universe {beatStatus.universe_size ?? "—"}
                        {beatStatus.watchlist_universe_size!=null && <> · watchlist <span style={{color:C.accent,fontWeight:700}}>{beatStatus.watchlist_universe_size}</span></>}
                        {beatStatus.dynamic_universe_size!=null && <> · dynamic {beatStatus.dynamic_universe_size}</>}
                      </span>
                    </div>
                    <div style={{display:"flex",flexDirection:"column",gap:5}}>
                      {Object.entries(beatStatus.tasks).map(([name, t]) => {
                        const mins  = t.minutes_ago;
                        const never = t.status === "never_seen";
                        const err   = t.status === "parse_error";
                        const ageColor = (never || err) ? C.red
                          : mins == null ? C.muted
                          : mins < 90   ? C.green
                          : mins < 1440 ? "#ffa500"
                          : C.red;
                        const ageLabel = never ? "never" : err ? "parse err"
                          : mins == null ? "—"
                          : mins < 60   ? `${Math.round(mins)}m ago`
                          : mins < 1440 ? `${(mins/60).toFixed(1)}h ago`
                          : `${(mins/1440).toFixed(1)}d ago`;
                        const res = t.result;
                        const resStr = res == null ? ""
                          : typeof res === "string" ? res
                          : (res.status || res.detail || JSON.stringify(res)).toString().slice(0,64);
                        return (
                          <div key={name} style={{display:"flex",alignItems:"baseline",
                            justifyContent:"space-between",gap:8}}>
                            <div style={{minWidth:0,flex:1}}>
                              <div style={{fontSize:10,fontFamily:"monospace",color:C.text,
                                overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                                <span style={{color:ageColor}}>●</span> {name}
                              </div>
                              {resStr && (
                                <div style={{fontSize:8.5,color:C.muted,fontFamily:"monospace",
                                  marginTop:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                                  {resStr}
                                </div>
                              )}
                            </div>
                            <span style={{fontSize:9,fontFamily:"monospace",color:ageColor,
                              flexShrink:0,fontWeight:700}}>{ageLabel}</span>
                          </div>
                        );
                      })}
                    </div>
                    <div style={{fontSize:8.5,color:C.muted,marginTop:8,lineHeight:1.45,fontStyle:"italic"}}>
                      Celery beat last-run heartbeats. warm_gap_scanner (*/5) publishes the gap:warm
                      snapshot; watchlist = durable scoring-set size (rebuilt from all users' watchlists each cycle).
                    </div>
                  </div>
                )}

                {/* Bottom row: uptime / processes / api RSS / last scored */}
                <div style={{display:"flex",gap:8,marginTop:12,flexWrap:"wrap"}}>
                  {[
                    {label:"Uptime",         value: fmtUptime(sysHealth.uptime_seconds), color: null},
                    {label:"Processes",      value: sysHealth.process_count ?? "—", color: null},
                    {label:"API RSS",        value: fmtBytes(sysHealth.api_rss_bytes), color: null},
                    {label:"Last Scored",    value: (() => {
                      const sa = sysHealth.last_scored_at;
                      if (!sa) return "—";
                      const agoS = Math.floor(Date.now() / 1000 - sa);
                      if (agoS < 0) return "just now";
                      if (agoS < 60)  return `${agoS}s ago`;
                      if (agoS < 3600) return `${Math.floor(agoS/60)}m ago`;
                      return `${Math.floor(agoS/3600)}h ${Math.floor((agoS%3600)/60)}m ago`;
                    })(), color: null},
                    {label:"Universe Keys",  value: sysHealth.universe_key_count ?? "—",
                     color: sysHealth.universe_key_count != null
                       ? (sysHealth.universe_key_count < 100 ? C.red
                          : sysHealth.universe_key_count < 400 ? "#ffa500"
                          : C.green)
                       : null},
                  ].map(({label,value,color})=>(
                    <div key={label} style={{flex:"1 1 30%",
                      background:C.card,borderRadius:8,padding:"8px 10px",
                      border:`1px solid ${color && color !== C.green ? color : C.border}`,minWidth:90}}>
                      <div style={{fontSize:8,color:C.muted,
                        textTransform:"uppercase",letterSpacing:"0.08em",
                        marginBottom:3}}>{label}</div>
                      <div style={{fontSize:13,fontWeight:800,color:color||C.text,
                        fontFamily:"monospace"}}>{value}</div>
                    </div>
                  ))}
                </div>

                {/* Top dirs by size — quick "what's eating my disk" view.
                    Renders only when the backend was able to walk at least
                    one of the candidate paths within its 1.5s deadline. */}
                {sysHealth.top_dirs?.length > 0 && (
                  <div style={{marginTop:12,padding:"10px 12px",
                    background:C.card,borderRadius:8,
                    border:`1px solid ${C.border}`}}>
                    <div style={{fontSize:9,color:C.muted,fontWeight:700,
                      letterSpacing:"0.08em",textTransform:"uppercase",
                      marginBottom:6}}>
                      Top directories ({sysHealth.disk?.path})
                    </div>
                    <div style={{display:"flex",flexDirection:"column",gap:3}}>
                      {sysHealth.top_dirs.map(d => (
                        <div key={d.path} style={{display:"flex",
                          justifyContent:"space-between",fontSize:11,
                          fontFamily:"monospace",color:C.text}}>
                          <span style={{color:C.muted}}>{d.path}</span>
                          <span style={{fontWeight:700}}>{fmtBytes(d.bytes)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {!sysHealth.host_disk_mounted && (
                  <div style={{fontSize:9,color:C.muted,marginTop:10,
                    fontStyle:"italic",lineHeight:1.5}}>
                    Note: container reads its own filesystem view ({sysHealth.disk?.path}).
                    For pixel-perfect host disk stats, bind-mount the host
                    root into the container by adding{" "}
                    <code style={{background:"rgba(255,255,255,0.06)",
                      padding:"1px 4px",borderRadius:3,fontFamily:"monospace"}}>
                      - /:/host:ro
                    </code>{" "}
                    to docker-compose.yml.
                  </div>
                )}

                {/* ── Email alert config (v7.9.56) ────────────────────
                    Surfaces the watchdog's wiring so the admin can verify
                    alerts will actually fire BEFORE disk hits 70%. The
                    Test button bypasses the threshold check (still
                    throttled) so the email pipeline can be confirmed
                    on demand. */}
                {sysHealth.alertConfig && (
                  <div style={{marginTop:14,padding:"12px 14px",
                    background:C.card,borderRadius:10,
                    border:`1px solid ${C.border}`}}>
                    <div style={{display:"flex",alignItems:"center",
                      justifyContent:"space-between",marginBottom:8,
                      flexWrap:"wrap",gap:6}}>
                      <div style={{display:"flex",alignItems:"center",gap:7}}>
                        <span style={{fontSize:9,color:C.muted,fontWeight:700,
                          letterSpacing:"0.08em",textTransform:"uppercase"}}>
                          📧 Email Alerts
                        </span>
                        <span style={{fontSize:9,fontWeight:700,
                          background: sysHealth.alertConfig.enabled
                            ? "rgba(0,230,118,0.12)" : "rgba(255,255,255,0.06)",
                          color: sysHealth.alertConfig.enabled
                            ? C.green : C.muted,
                          borderRadius:10,padding:"2px 8px",
                          border:`1px solid ${sysHealth.alertConfig.enabled
                            ? "rgba(0,230,118,0.3)" : C.border}`}}>
                          {sysHealth.alertConfig.enabled ? "Enabled" : "Disabled"}
                        </span>
                      </div>
                      <button onClick={async ()=>{
                          if (!window.confirm(
                            "Send a test 'warning' alert email now?\n\nThis bypasses "
                            + "the threshold but is still subject to the 24h throttle. "
                            + "If something goes wrong, the dashboard will show the "
                            + "specific reason (missing API key, unverified domain, etc.)."
                          )) return;
                          try {
                            const d = await apiFetch(
                              "/admin/system-health/test-alert?tier=warning",
                              token, { method:"POST" });
                            // v7.9.57: backend returns {ok, detail, reason}.
                            // detail already includes the right emoji + actionable fix.
                            flash(d.detail || (d.ok ? "Test sent" : "Test failed"));
                          } catch(e) {
                            flash(`❌ ${e.message}`);
                          }
                        }} style={{
                          fontSize:10,fontWeight:700,
                          background:"rgba(0,212,255,0.08)",color:C.accent,
                          border:`1px solid ${C.accent}`,
                          borderRadius:6,padding:"3px 10px",cursor:"pointer",
                          fontFamily:"inherit",
                        }}>Test email</button>
                    </div>
                    <div style={{fontSize:11,color:C.muted,lineHeight:1.7,
                      fontFamily:"monospace"}}>
                      <div>Recipient: <span style={{color:C.text}}>{sysHealth.alertConfig.recipient}</span></div>
                      <div>Thresholds: <span style={{color:"#ffca28"}}>warn ≥ {sysHealth.alertConfig.warning_pct}%</span>
                        {" · "}<span style={{color:C.red}}>critical ≥ {sysHealth.alertConfig.critical_pct}%</span></div>
                      <div>Throttle: max 1 email per tier per {sysHealth.alertConfig.throttle_hours}h</div>
                      {Object.keys(sysHealth.alertConfig.last_sent || {}).length > 0 && (
                        <div style={{marginTop:4}}>
                          Last sent:{" "}
                          {Object.entries(sysHealth.alertConfig.last_sent).map(([tier, ts]) => {
                            const ago = Math.max(0, Math.floor((Date.now()/1000) - ts));
                            const fmt = ago < 60 ? `${ago}s` : ago < 3600 ? `${Math.floor(ago/60)}m`
                                       : ago < 86400 ? `${Math.floor(ago/3600)}h` : `${Math.floor(ago/86400)}d`;
                            return <span key={tier} style={{
                              color: tier==="critical"?C.red:"#ffca28",marginRight:8}}>
                              {tier} · {fmt} ago
                            </span>;
                          })}
                        </div>
                      )}
                    </div>

                    {/* v7.9.58 — also test the gap-scanner email pipeline.
                        Sends to the requesting admin's address with their
                        own saved gap-alert filters applied so the layout
                        and filter logic can be verified end-to-end. */}
                    <div style={{marginTop:10,paddingTop:10,
                      borderTop:`1px solid ${C.border}`,
                      display:"flex",alignItems:"center",justifyContent:"space-between",
                      gap:8,flexWrap:"wrap"}}>
                      <div style={{fontSize:11,color:C.muted,lineHeight:1.5}}>
                        Test the daily gap-scanner email layout:
                      </div>
                      <button onClick={async ()=>{
                          if (!window.confirm(
                            "Send a test gap-scanner email to your admin address?\n\n"
                            + "Pulls live gappers + applies your saved gap-alert filters "
                            + "(if any). Verifies header / scores / RSI column / filter logic."
                          )) return;
                          try {
                            const d = await apiFetch(
                              "/admin/gap-scanner/test-email", token,
                              { method:"POST" });
                            flash(d.detail || (d.ok ? "Sent" : "Failed"));
                          } catch(e) {
                            flash(`❌ ${e.message}`);
                          }
                        }} style={{
                          fontSize:10,fontWeight:700,
                          background:"rgba(0,212,255,0.08)",color:C.accent,
                          border:`1px solid ${C.accent}`,
                          borderRadius:6,padding:"3px 10px",cursor:"pointer",
                          fontFamily:"inherit",
                        }}>📊 Test Gap Email</button>
                    </div>

                    {/* Test market digest email */}
                    <div style={{marginTop:10,paddingTop:10,
                      borderTop:`1px solid ${C.border}`,
                      display:"flex",alignItems:"center",justifyContent:"space-between",
                      gap:8,flexWrap:"wrap"}}>
                      <div style={{fontSize:11,color:C.muted,lineHeight:1.5}}>
                        Market digest emails (AI wrap-up):
                      </div>
                      <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                        {/* v8.4.94: R1K digest — production auto-send (Small-cap+, no Nano/Micro) */}
                        <button onClick={async ()=>{
                            if (!window.confirm(
                              "Generate and send today's R1K market digest (Small-cap+ movers only)?\n\n"
                              + "This is the same digest that auto-sends at 4:30pm ET."
                            )) return;
                            try {
                              flash("⏳ Generating R1K digest…");
                              const d = await apiFetch(
                                "/admin/test-market-digest-r1k", token, { method:"POST" });
                              flash(d.status === "queued"
                                ? `⏳ Queued — poll ${d.poll_url}`
                                : "❌ Failed to queue");
                            } catch(e) { flash(`❌ ${e.message}`); }
                          }} style={{
                            fontSize:10,fontWeight:700,
                            background:"rgba(0,230,118,0.12)",color:C.green,
                            border:`1px solid ${C.green}`,
                            borderRadius:6,padding:"3px 10px",cursor:"pointer",
                            fontFamily:"inherit",
                          }}>📰 R1K Email</button>
                        {/* Force-send R1K to ALL opted-in users now (bypasses beat + dedup) */}
                        <button onClick={async ()=>{
                            if (!window.confirm(
                              "Force-send R1K digest to ALL opted-in users RIGHT NOW?\n\n"
                              + "Bypasses schedule and dedup. Use when beat task isn't firing.\n"
                              + "Takes 30–90s — runs in the background, this button polls for the result."
                            )) return;
                            try {
                              flash("⏳ Force-sending R1K digest to all users…");
                              const sub = await apiFetch(
                                "/admin/force-send-digest-r1k", token, { method:"POST" });
                              const reqId = sub?.request_id;
                              if (!reqId) throw new Error("no request_id returned");
                              // v8.4.101: submit→poll — the synchronous call was tripping
                              // Cloudflare's 100s edge timeout ("Load failed" in the browser)
                              // even though the backend kept running and the email sent fine.
                              const start = Date.now();
                              let info = null;
                              while (Date.now() - start < 180000) {
                                await new Promise(r => setTimeout(r, 4000));
                                info = await apiFetch(`/admin/force-send-digest-r1k/${reqId}`, token);
                                if (info?.status === "done" || info?.status === "error") break;
                              }
                              if (!info || info.status === "running" || info.status === "queued") {
                                flash("⏳ Still running — check back via Force Send R1K again, or VM logs.");
                              } else if (info.status === "error") {
                                flash(`❌ ${info.error || "unknown error"}`);
                              } else if (info.sent > 0) {
                                flash(`✅ Sent to ${info.sent} user(s)`);
                              } else if (info.error) {
                                flash(`❌ ${info.error}`);
                              } else {
                                flash("⚠️ 0 users — check market_digest_opt_in");
                              }
                            } catch(e) { flash(`❌ ${e.message}`); }
                          }} style={{
                            fontSize:10,fontWeight:700,
                            background:"rgba(255,82,82,0.08)",color:"#ff5252",
                            border:"1px solid rgba(255,82,82,0.3)",
                            borderRadius:6,padding:"3px 10px",cursor:"pointer",
                            fontFamily:"inherit",
                          }}>🚀 Force Send R1K</button>
                        {/* Full-universe digest (admin-only, includes Nano/Micro-cap) */}
                        <button onClick={async ()=>{
                            if (!window.confirm(
                              "Generate full-universe digest (includes Nano/Micro-cap)?\n\n"
                              + "Admin-only — not sent to users automatically."
                            )) return;
                            try {
                              flash("⏳ Generating full-universe digest…");
                              const d = await apiFetch(
                                "/admin/test-market-digest", token,
                                { method:"POST" });
                              flash(d.sent ? `✅ Sent to ${d.email}` : "❌ Send failed");
                            } catch(e) {
                              flash(`❌ ${e.message}`);
                            }
                          }} style={{
                            fontSize:10,fontWeight:700,
                            background:"rgba(255,152,0,0.08)",color:"#ffa726",
                            border:"1px solid rgba(255,152,0,0.3)",
                            borderRadius:6,padding:"3px 10px",cursor:"pointer",
                            fontFamily:"inherit",
                          }}>📰 Full Email</button>
                        {/* v8.4.72: PDF preview — fetches printable HTML with the
                            bearer header (auth-safe), then opens as a blob URL so
                            the user can use Cmd+P / Ctrl+P → Save as PDF.
                            No PDF library on the server; browser native handler. */}
                        <button onClick={async ()=>{
                            try {
                              flash("⏳ Building digest…");
                              const base = (window.location.host.includes("localhost")
                                ? "http://localhost:8000"
                                : "https://api.gempulse.io");
                              const resp = await fetch(`${base}/admin/digest/preview-html`, {
                                headers: { Authorization: `Bearer ${token}` },
                              });
                              if (!resp.ok) {
                                const txt = await resp.text();
                                flash(`❌ ${resp.status}: ${txt.slice(0,80)}`);
                                return;
                              }
                              const html = await resp.text();
                              const blob = new Blob([html], { type: "text/html" });
                              const url  = URL.createObjectURL(blob);
                              window.open(url, "_blank", "noopener,noreferrer");
                              setTimeout(() => URL.revokeObjectURL(url), 60000);
                              flash("✅ Opened — use Cmd+P / Ctrl+P → Save as PDF");
                            } catch(e) {
                              flash(`❌ ${e.message}`);
                            }
                          }} style={{
                            fontSize:10,fontWeight:700,
                            background:"rgba(0,212,255,0.08)",color:C.accent,
                            border:`1px solid ${C.accent}`,
                            borderRadius:6,padding:"3px 10px",cursor:"pointer",
                            fontFamily:"inherit",
                          }}>📄 PDF Preview</button>
                        {/* v8.4.155: Copy the latest fact-checked digest, reformatted
                            as a Beehiiv-paste-friendly issue (title + body + CTA/disclaimer),
                            to the clipboard for the manual weekly Beehiiv recap. */}
                        <button onClick={async ()=>{
                            try {
                              flash("⏳ Fetching latest digest…");
                              const d = await apiFetch("/admin/digest-for-beehiiv?kind=r1k", token);
                              await navigator.clipboard.writeText(d.markdown);
                              flash(`✅ Copied ${d.date} digest to clipboard — paste into Beehiiv`
                                + (d.flags?.length ? ` (⚠ ${d.flags.length} QA flag(s) — review first)` : ""));
                            } catch(e) {
                              flash(`❌ ${e.message}`);
                            }
                          }} style={{
                            fontSize:10,fontWeight:700,
                            background:"rgba(255,202,40,0.08)",color:"#ffca28",
                            border:"1px solid rgba(255,202,40,0.3)",
                            borderRadius:6,padding:"3px 10px",cursor:"pointer",
                            fontFamily:"inherit",
                          }}>📋 Copy for Beehiiv</button>
                        {/* v8.4.197 (Option B, Phase 1): generate the WEEKLY recap on
                            demand, review it, and copy to clipboard for Beehiiv. Does
                            NOT email users — Phase 2 adds the daily/weekly toggle +
                            Friday auto-send once the weekly output is confirmed good. */}
                        <button onClick={async ()=>{
                            try {
                              flash("⏳ Generating weekly recap… (~20s)");
                              const d = await apiFetch("/admin/weekly-digest/preview", token);
                              setWeeklyPreview(d);
                              flash(`✅ Weekly recap ready for ${d.session_date}`
                                + (d.flags?.length ? ` (⚠ ${d.flags.length} QA flag(s))` : ""));
                            } catch(e) {
                              flash(`❌ ${e.message}`);
                            }
                          }} style={{
                            fontSize:10,fontWeight:700,
                            background:"rgba(0,230,118,0.08)",color:C.green,
                            border:"1px solid rgba(0,230,118,0.35)",
                            borderRadius:6,padding:"3px 10px",cursor:"pointer",
                            fontFamily:"inherit",
                          }}>🗓 Generate Weekly</button>
                        {/* Test the WEEKLY email render — sends to the admin only */}
                        <button onClick={async ()=>{
                            if (!window.confirm("Send a test weekly recap email to your own address?")) return;
                            try {
                              flash("⏳ Generating + sending weekly to you… (~20s)");
                              const d = await apiFetch("/admin/test-weekly-digest", token, { method:"POST" });
                              flash(d.sent ? `✅ Weekly test sent to ${d.test}` : `❌ ${d.error || "send failed"}`);
                            } catch(e) { flash(`❌ ${e.message}`); }
                          }} style={{
                            fontSize:10,fontWeight:700,
                            background:"rgba(0,230,118,0.08)",color:C.green,
                            border:"1px solid rgba(0,230,118,0.35)",
                            borderRadius:6,padding:"3px 10px",cursor:"pointer",
                            fontFamily:"inherit",
                          }}>📧 Test Weekly→Me</button>
                        {/* Clear R1K dedup — unblocks 4:30pm production send */}
                        <button onClick={async ()=>{
                            try {
                              const d = await apiFetch(
                                "/admin/digest-dedup-r1k", token, { method:"DELETE" });
                              flash(d.status === "cleared"
                                ? "✅ R1K dedup cleared — 4:30pm send unblocked"
                                : d.status === "already_clear"
                                ? "ℹ️ R1K already clear"
                                : `❌ ${d.error || "Failed"}`);
                            } catch(e) { flash(`❌ ${e.message}`); }
                          }} style={{
                            fontSize:10,fontWeight:700,
                            background:"rgba(0,212,255,0.08)",color:C.accent,
                            border:`1px solid rgba(0,212,255,0.3)`,
                            borderRadius:6,padding:"3px 10px",cursor:"pointer",
                            fontFamily:"inherit",
                          }}>🔓 Clear R1K</button>
                        {/* Clear full-universe dedup */}
                        <button onClick={async ()=>{
                            try {
                              const d = await apiFetch(
                                "/admin/digest-dedup", token, { method:"DELETE" });
                              flash(d.status === "cleared"
                                ? "✅ Full dedup cleared"
                                : d.status === "already_clear"
                                ? "ℹ️ Full already clear"
                                : `❌ ${d.error || "Failed"}`);
                            } catch(e) { flash(`❌ ${e.message}`); }
                          }} style={{
                            fontSize:10,fontWeight:700,
                            background:"rgba(255,152,0,0.06)",color:"#ffa726",
                            border:"1px solid rgba(255,152,0,0.25)",
                            borderRadius:6,padding:"3px 10px",cursor:"pointer",
                            fontFamily:"inherit",
                          }}>🔓 Clear Full</button>
                      </div>
                    </div>
                  </div>
                )}

                {/* ── Storage cleanup (v7.9.69) ────────────────────────────
                    On-demand `docker system prune` from the dashboard.
                    Useful when the disk-usage alert fires and you want to
                    reclaim space without SSHing to the VM. Two modes:

                      Quick prune (default)  — stopped containers, dangling
                                                images, build cache, unused
                                                networks. Safe-ish; equivalent
                                                of `docker system prune -f`.
                      Deep prune             — also nukes ALL unused images
                                                and volumes. Slower next
                                                deploy (images repull) and
                                                potentially destructive
                                                (named volumes you forgot).
                                                Stronger confirm dialog. */}
                <div style={{marginTop:10,paddingTop:10,
                  borderTop:`1px solid ${C.border}`}}>
                  <div style={{display:"flex",alignItems:"center",
                    justifyContent:"space-between",marginBottom:6,
                    flexWrap:"wrap",gap:6}}>
                    <span style={{fontSize:9,color:C.muted,fontWeight:700,
                      letterSpacing:"0.08em",textTransform:"uppercase"}}>
                      🧹 Storage Maintenance
                    </span>
                    <InfoTip text={
                      "Quick prune: removes stopped containers, dangling " +
                      "images, build cache, and unused networks. Safe to " +
                      "run anytime — equivalent of `docker system prune -f`.\n\n" +
                      "Deep prune: ALSO removes all unused images (slower " +
                      "next deploy as images need to re-pull) AND unused " +
                      "named volumes (review carefully — could delete data). " +
                      "Use only if Quick prune doesn't reclaim enough space.\n\n" +
                      "Both run via the Docker SDK against the host's Docker " +
                      "daemon. Requires admin role. Every invocation is logged " +
                      "with your email."
                    }/>
                  </div>
                  <div style={{fontSize:11,color:C.muted,marginBottom:8,lineHeight:1.5}}>
                    Reclaim disk space when the threshold alert fires.
                  </div>
                  <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                    {/* Click handler factored out (v7.9.70). Both buttons share
                        the same flow; the only difference is `?deep=true` and
                        the confirm dialog copy. After a successful prune, we
                        clear `sysHealthErr` because the 30-sec polling cycle
                        often fails mid-prune (Docker daemon briefly blocks
                        the socket) which leaves a stale red banner at the top
                        even though the actual prune succeeded. The fresh
                        sys-health refresh below is the source of truth — once
                        it succeeds, any prior polling error is stale. */}
                    <button onClick={async ()=>{
                        if (!window.confirm(
                          "Run Docker prune to reclaim space?\n\n"
                          + "This removes stopped containers, dangling images, "
                          + "build cache, and unused networks. Active services "
                          + "(api, celery, redis) are unaffected.\n\n"
                          + "Safe to run — same as `docker system prune -f`."
                        )) return;
                        try {
                          const d = await apiFetch(
                            "/admin/system/prune", token, { method:"POST" });
                          flash(d.detail || "Prune complete");
                          // Re-read system health so the disk bar reflects
                          // the freed space immediately. ALSO clear any
                          // stale polling error from mid-prune so the user
                          // doesn't see a misleading red banner after a
                          // successful action.
                          try {
                            const fresh = await apiFetch("/admin/system-health", token);
                            setSysHealth(fresh);
                            setSysHealthErr("");
                          } catch (_) {
                            // If the post-prune health refresh ALSO fails,
                            // clear the stale error anyway — the prune we
                            // just confirmed succeeded is more authoritative
                            // than a transient polling glitch.
                            setSysHealthErr("");
                          }
                        } catch(e) {
                          // Distinguish a real error from "request timed
                          // out but action probably worked" — apiFetch
                          // throws "Request timed out" on AbortController
                          // 180s firing. The actual prune may have completed
                          // server-side; tell the user to verify.
                          if (/timed out/i.test(e.message || "")) {
                            flash("⏱ Prune is taking longer than usual — refresh in a moment to see updated stats");
                            setSysHealthErr("");
                          } else {
                            flash(`❌ ${e.message}`);
                          }
                        }
                      }} style={{
                        flex:"1 1 130px",fontSize:11,fontWeight:700,
                        background:"rgba(0,212,255,0.10)",color:C.accent,
                        border:`1px solid ${C.accent}`,
                        borderRadius:7,padding:"6px 10px",cursor:"pointer",
                        fontFamily:"inherit",
                      }}>🧹 Quick Prune</button>
                    <button onClick={async ()=>{
                        if (!window.confirm(
                          "DEEP prune?\n\n"
                          + "Quick prune + ALL unused images + unused volumes.\n\n"
                          + "Slower next deploy (images repull), and any named "
                          + "volumes you stopped using will be permanently "
                          + "deleted. Active redis/data volumes are preserved "
                          + "because they're still in use.\n\n"
                          + "Only proceed if Quick prune didn't reclaim enough."
                        )) return;
                        if (!window.confirm(
                          "Confirm DEEP prune (destructive on unused volumes)?"
                        )) return;
                        try {
                          const d = await apiFetch(
                            "/admin/system/prune?deep=true", token, { method:"POST" });
                          flash(d.detail || "Deep prune complete");
                          try {
                            const fresh = await apiFetch("/admin/system-health", token);
                            setSysHealth(fresh);
                            setSysHealthErr("");
                          } catch (_) {
                            setSysHealthErr("");
                          }
                        } catch(e) {
                          if (/timed out/i.test(e.message || "")) {
                            flash("⏱ Deep prune is taking longer than usual — refresh in a moment to see updated stats");
                            setSysHealthErr("");
                          } else {
                            flash(`❌ ${e.message}`);
                          }
                        }
                      }} style={{
                        flex:"1 1 130px",fontSize:11,fontWeight:700,
                        background:"rgba(255,82,82,0.08)",color:C.red,
                        border:`1px solid rgba(255,82,82,0.4)`,
                        borderRadius:7,padding:"6px 10px",cursor:"pointer",
                        fontFamily:"inherit",
                      }}>🚨 Deep Prune</button>
                  </div>
                </div>
              </>
            )}
          </div>
        );
      })()}

      {/* ── Server Controls (v8.4.9) ─────────────────────────────── */}
      <div style={{background:C.surface,borderRadius:12,padding:"12px 14px",
        border:"1px solid rgba(255,82,82,0.2)",marginBottom:16}}>
        <div style={{fontSize:10,fontWeight:700,color:C.muted,letterSpacing:"0.1em",
          textTransform:"uppercase",marginBottom:10}}>⚡ Server Controls</div>
        <div style={{fontSize:11,color:C.muted,marginBottom:12,lineHeight:1.6}}>
          Use <strong style={{color:C.text}}>Restart API</strong> for preventive
          restarts when features stop loading (thread buildup). Docker brings it back
          in ~15s. <span style={{color:"#ffa500"}}>If fully down</span>, SSH to the VM
          and run <code style={{color:C.accent,fontSize:10}}>docker compose restart api</code>.
        </div>
        {restartMsg && (
          <div style={{fontSize:12,marginBottom:10,
            color:restartMsg.startsWith("✅")?C.green:restartMsg.startsWith("❌")?C.red:"#ffa500"}}>
            {restartMsg}
          </div>
        )}
        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
          <button onClick={handleRestartApi} disabled={restarting} style={{
            padding:"9px 18px",borderRadius:9,border:"1px solid rgba(255,82,82,0.4)",
            background:"rgba(255,82,82,0.1)",color:C.red,fontFamily:"inherit",
            fontSize:12,fontWeight:700,cursor:restarting?"not-allowed":"pointer",
            opacity:restarting?0.6:1,
          }}>
            {restarting ? "⏳ Restarting…" : "⚡ Restart API"}
          </button>
          <button onClick={async ()=>{
              if (!window.confirm(
                "Trigger a full universe rescore now?\n\n"
                + "Dispatches ~51 Celery chunks to re-score all ~810 tickers "
                + "immediately. Use this if 'Universe Keys' shows 0 or low (< 100)."
              )) return;
              try {
                const d = await apiFetch(
                  "/universe/trigger", token, { method:"POST" });
                flash(d.detail || "Universe refresh dispatched ✅");
              } catch(e) {
                flash(`❌ ${e.message}`);
              }
            }} style={{
              padding:"9px 18px",borderRadius:9,border:"1px solid rgba(0,212,255,0.3)",
              background:"rgba(0,212,255,0.08)",color:C.accent,fontFamily:"inherit",
              fontSize:12,fontWeight:700,cursor:"pointer",
          }}>
            🔄 Force Rescore
          </button>
        </div>
      </div>

      {/* ── Admin Tools ──────────────────────────────────────────── */}
      <AdminToolsPanel token={token} externalStats={stats} />

      {/* ── Tweet Generator ──────────────────────────────────────── */}
      <TweetGenerator token={token} user={user} />

      {/* ── Stats Panel ──────────────────────────────────────────── */}
      {stats && (
        <div style={{marginBottom:16}}>
          {/* KPI tiles */}
          <div style={{display:"flex",gap:8,marginBottom:8,flexWrap:"wrap"}}>
            {[
              {label:"Total Users",   value:stats.users?.total,        color:C.accent},
              {label:"Active Today",  value:stats.users?.active_today,  color:C.green},
              {label:"Active 7d",     value:stats.users?.active_7d,     color:C.green},
              {label:"Tickers Scored",value:stats.scores_today?.tickers_scored, color:"#ffca28"},
              {label:"IBD Leaders",   value:stats.scores_today?.leaders, color:"#ffca28"},
              {label:"Active Alerts", value:stats.alerts?.active,       color:"#ce93d8"},
              {label:"Triggered 7d",  value:stats.alerts?.triggered_7d, color:"#ff7043"},
            ].map(({label,value,color})=>(
              <div key={label} style={{flex:"1 1 28%",background:C.surface,
                borderRadius:10,padding:"10px 12px",border:`1px solid ${C.border}`,
                minWidth:80}}>
                <div style={{fontSize:8,color:C.muted,textTransform:"uppercase",
                  letterSpacing:"0.07em",marginBottom:4}}>{label}</div>
                <div style={{fontSize:20,fontWeight:900,fontFamily:"monospace",
                  color}}>{value ?? "—"}</div>
              </div>
            ))}
          </div>

          {/* Top scored tickers today */}
          {stats.top_today?.length > 0 && (
            <div style={{background:C.surface,borderRadius:12,padding:"12px 14px",
              border:`1px solid ${C.border}`}}>
              <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:8}}>
                <div style={{fontSize:10,fontWeight:700,color:C.muted,
                  letterSpacing:"0.08em",textTransform:"uppercase"}}>
                  Top Swing Scores Today
                </div>
                <InfoTip text={
                  "Scores pulled from the score_history table — written at the end of each " +
                  "30-min universe scoring cycle. May lag the homepage Top Swing Scores, which " +
                  "read live from the Redis cache (updated continuously during market hours).\n\n" +
                  "This list is also profile-agnostic: it shows the last stored swing score " +
                  "regardless of your risk-profile setting, whereas the homepage score adjusts " +
                  "dynamically to your selected profile (conservative / moderate / aggressive)."
                } />
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:5}}>
                {stats.top_today.map((t,i)=>(
                  <div key={t.ticker} style={{display:"flex",alignItems:"center",
                    gap:8,padding:"5px 0",
                    borderBottom:i<stats.top_today.length-1?`1px solid ${C.border}`:"none"}}>
                    <span style={{fontSize:10,color:C.muted,width:16,
                      textAlign:"right",flexShrink:0}}>#{i+1}</span>
                    <span style={{fontSize:12,fontWeight:800,fontFamily:"monospace",
                      color:C.text,flex:1}}>{t.ticker}</span>
                    {t.is_leader===1 && (
                      <span style={{fontSize:9,color:"#ffca28",fontWeight:700}}>⭐ Leader</span>
                    )}
                    {t.rs_rating != null && (
                      <span style={{fontSize:9,color:C.muted}}>RS {t.rs_rating}</span>
                    )}
                    <span style={{fontSize:12,fontWeight:700,fontFamily:"monospace",
                      color:C.accent}}>{t.swing_score?.toFixed(0)}</span>
                  </div>
                ))}
              </div>
              <div style={{fontSize:9,color:C.muted,marginTop:6}}>
                {stats.date} · {stats.scores_today?.tickers_scored || 0} tickers scored
              </div>
            </div>
          )}
        </div>
      )}

      {/* Pending access requests */}
      {!loading && (
        <div style={{marginBottom:16}}>
          <div style={{fontSize:10,fontWeight:700,color:"#ffca28",letterSpacing:"0.1em",
            textTransform:"uppercase",marginBottom:8}}>
            ⏳ Pending requests ({pendingReqs.length})
          </div>
          {pendingReqsErr && (
            <div style={{fontSize:12,color:"#ffca28",padding:"10px 14px",
              background:"rgba(255,202,40,0.08)",border:"1px solid rgba(255,202,40,0.3)",
              borderRadius:10,marginBottom:8,display:"flex",alignItems:"center",
              justifyContent:"space-between",gap:10}}>
              <span>⚠ Couldn't load requests (fetch error — your data is safe).</span>
              <button onClick={loadData} style={{fontSize:11,fontWeight:700,
                background:"rgba(255,202,40,0.15)",color:"#ffca28",
                border:"1px solid rgba(255,202,40,0.4)",borderRadius:6,
                padding:"4px 10px",cursor:"pointer",fontFamily:"inherit",flexShrink:0}}>↻ Retry</button>
            </div>
          )}
          {!pendingReqsErr && pendingReqs.length === 0 && (
            <div style={{fontSize:12,color:C.muted,padding:"10px 14px",
              background:C.surface,borderRadius:10,textAlign:"center"}}>
              No pending requests
            </div>
          )}
          {pendingReqs.map(req => (
            <div key={req.id} style={{background:C.surface,borderRadius:12,padding:"12px 14px",
              border:"1px solid rgba(255,202,40,0.3)",marginBottom:8}}>
              <div style={{fontSize:14,fontWeight:700,color:C.text}}>{req.name}</div>
              <div style={{fontSize:11,color:C.muted,marginBottom:8}}>{req.email}</div>
              {req.note && (
                <div style={{fontSize:11,color:C.muted,marginBottom:8,fontStyle:"italic"}}>
                  "{req.note}"
                </div>
              )}
              <input style={{...inp,marginBottom:8}}
                type="password"
                placeholder="Temp password — leave blank to auto-generate"
                autoComplete="new-password"
                value={tempPw[req.id]||""}
                onChange={e=>setTempPw(prev=>({...prev,[req.id]:e.target.value}))}/>
              <div style={{display:"flex",gap:8}}>
                <button onClick={()=>handleApprove(req)} style={{
                  flex:1,padding:"8px",borderRadius:8,border:"none",cursor:"pointer",
                  background:"rgba(0,230,118,0.15)",color:C.green,
                  fontSize:12,fontWeight:700,fontFamily:"inherit"}}>
                  ✓ Approve
                </button>
                <button onClick={()=>handleDeny(req)} style={{
                  flex:1,padding:"8px",borderRadius:8,border:"none",cursor:"pointer",
                  background:"rgba(255,82,82,0.1)",color:C.red,
                  fontSize:12,fontWeight:700,fontFamily:"inherit"}}>
                  ✗ Deny
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Invite codes (v7.9.50) ──────────────────────────────────── */}
      {!loading && (
        <div style={{marginBottom:16}}>
          {inviteCodesErr && (
            <div style={{fontSize:12,color:"#ffca28",padding:"10px 14px",
              background:"rgba(255,202,40,0.08)",border:"1px solid rgba(255,202,40,0.3)",
              borderRadius:10,marginBottom:8,display:"flex",alignItems:"center",
              justifyContent:"space-between",gap:10}}>
              <span>⚠ Couldn't load invite codes (fetch error — your codes are safe).</span>
              <button onClick={loadData} style={{fontSize:11,fontWeight:700,
                background:"rgba(255,202,40,0.15)",color:"#ffca28",
                border:"1px solid rgba(255,202,40,0.4)",borderRadius:6,
                padding:"4px 10px",cursor:"pointer",fontFamily:"inherit",flexShrink:0}}>↻ Retry</button>
            </div>
          )}
          {/* v7.9.62: split codes into Active vs Archived tabs.
              Archived = used_up | expired | revoked. Auto-classified
              server-side; users who already redeemed are unaffected. */}
          {(() => {
            const activeCodes   = inviteCodes.filter(c => c.status === "active");
            const archivedCodes = inviteCodes.filter(c => c.status !== "active");
            const visible = codeFilter === "active" ? activeCodes : archivedCodes;
            const TAB_BTN = (key, label, count) => {
              const sel = codeFilter === key;
              return (
                <button key={key} onClick={()=>setCodeFilter(key)} style={{
                  padding:"4px 10px",borderRadius:14,border:"none",cursor:"pointer",
                  fontFamily:"inherit",fontSize:10,fontWeight:700,
                  background:sel?"rgba(0,212,255,0.18)":"rgba(255,255,255,0.04)",
                  color:sel?C.accent:C.muted,
                  outline:sel?`1px solid ${C.accent}`:"none",
                }}>{label} ({count})</button>
              );
            };
            return (
              <>
                <div style={{display:"flex",alignItems:"center",
                  justifyContent:"space-between",marginBottom:8,
                  flexWrap:"wrap",gap:8}}>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    <div style={{fontSize:10,fontWeight:700,color:C.muted,
                      letterSpacing:"0.1em",textTransform:"uppercase"}}>
                      Invite Codes
                    </div>
                    <div style={{display:"flex",gap:4}}>
                      {TAB_BTN("active",   "Active",   activeCodes.length)}
                      {TAB_BTN("archived", "Archived", archivedCodes.length)}
                    </div>
                  </div>
                  <button onClick={()=>setShowCodeModal(true)} style={{
                    background:"rgba(0,212,255,0.12)",border:`1px solid ${C.accent}`,
                    borderRadius:8,padding:"5px 12px",color:C.accent,
                    fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
                    + Generate
                  </button>
                </div>
                {visible.length === 0 ? (
                  <div style={{fontSize:11,color:C.muted,padding:"12px 14px",
                    background:C.surface,borderRadius:10,border:`1px solid ${C.border}`,
                    textAlign:"center",fontStyle:"italic"}}>
                    {codeFilter === "active"
                      ? "No active codes — click Generate to create one."
                      : "No archived codes yet. Used-up / expired / revoked codes will appear here."}
                  </div>
                ) : visible.map(c => {
                  const expires = c.expires_at ? new Date(c.expires_at) : null;
                  const usesLabel = (c.max_uses || 0) === 0
                    ? `${c.redemption_count} used (unlimited)`
                    : `${c.redemption_count}/${c.max_uses} used`;
                  const tierLabel = {
                    founder: "🌟 Founder",
                    friends_family: "💛 F&F",
                    beta: "🧪 Beta",
                  }[c.early_adopter_tier];
                  // v7.9.62 status badge — derived server-side, see /admin/invite-codes
                  const statusMeta = {
                    active:   {label:"Active",   color:C.green,   border:"rgba(0,230,118,0.3)",  bg:"rgba(0,230,118,0.10)"},
                    used_up:  {label:"Used up",  color:C.muted,   border:C.border,                bg:"rgba(255,255,255,0.04)"},
                    expired:  {label:"Expired",  color:"#ffca28", border:"rgba(255,202,40,0.35)", bg:"rgba(255,202,40,0.08)"},
                    revoked:  {label:"Revoked",  color:C.red,     border:"rgba(255,82,82,0.35)",  bg:"rgba(255,82,82,0.08)"},
                  }[c.status] || null;
                  const isArchived = c.status !== "active";
                  return (
                    <div key={c.id} style={{background:C.surface,borderRadius:10,
                      padding:"10px 14px",marginBottom:8,
                      border:`1px solid ${C.border}`,
                      opacity:isArchived?0.7:1}}>
                      <div style={{display:"flex",alignItems:"center",gap:8,
                        marginBottom:6,flexWrap:"wrap"}}>
                        <span style={{fontSize:12,fontWeight:800,fontFamily:"monospace",
                          color:C.text,flex:"1 1 200px",wordBreak:"break-all"}}>
                          {c.code}
                        </span>
                        {statusMeta && (
                          <span style={{fontSize:9,fontWeight:700,
                            background:statusMeta.bg,color:statusMeta.color,
                            borderRadius:10,padding:"2px 7px",
                            border:`1px solid ${statusMeta.border}`,whiteSpace:"nowrap"}}>
                            {statusMeta.label}
                          </span>
                        )}
                        {tierLabel && (
                          <span style={{fontSize:9,fontWeight:700,
                            background:"rgba(0,212,255,0.1)",color:C.accent,
                            borderRadius:10,padding:"2px 7px",
                            border:"1px solid rgba(0,212,255,0.25)",whiteSpace:"nowrap"}}>
                            {tierLabel}
                          </span>
                        )}
                      </div>
                      <div style={{fontSize:10,color:C.muted,lineHeight:1.6}}>
                        {usesLabel}
                        {c.email_bound && <> · 🔒 {c.email_bound}</>}
                        {expires && (
                          <> · {c.status==="expired" ? "❌ expired" : `expires ${expires.toLocaleDateString()}`}</>
                        )}
                        {c.notes && <> · "{c.notes}"</>}
                      </div>
                      <div style={{display:"flex",gap:6,marginTop:8}}>
                        {/* Active codes: copy + revoke. Archived codes: hard
                            delete only when no redemptions exist (the audit
                            trail is empty so nothing's lost). Revoked codes
                            with redemptions stay forever as audit history. */}
                        {c.status === "active" ? (
                          <>
                            <button onClick={()=>copyInviteUrl(c)} style={{
                              flex:1,padding:"6px",borderRadius:7,border:`1px solid ${C.border}`,
                              background:"rgba(0,212,255,0.06)",color:C.accent,
                              fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>
                              📋 Copy link
                            </button>
                            <button onClick={()=>revokeInviteCode(c)} style={{
                              flex:1,padding:"6px",borderRadius:7,border:"none",
                              background:"rgba(255,82,82,0.1)",color:C.red,
                              fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>
                              Revoke
                            </button>
                          </>
                        ) : (
                          <>
                            {c.redemption_count === 0 && (
                              <button onClick={async ()=>{
                                if (!window.confirm(
                                  `Permanently delete code ${c.code}?\n\nThis can't be undone, but the code has no redemptions so nothing else is affected.`
                                )) return;
                                try {
                                  await apiFetch(
                                    `/admin/invite-codes/${c.id}/hard-delete`,
                                    token, { method:"DELETE" });
                                  setInviteCodes(prev => prev.filter(x => x.id !== c.id));
                                  flash(`✅ Deleted ${c.code}`);
                                } catch(e) { flash(`❌ ${e.message}`); }
                              }} style={{
                                flex:1,padding:"6px",borderRadius:7,border:"none",
                                background:"rgba(255,82,82,0.10)",color:C.red,
                                fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>
                                🗑 Delete
                              </button>
                            )}
                            <div style={{flex:1,padding:"6px",borderRadius:7,
                              fontSize:10,color:C.muted,fontStyle:"italic",
                              textAlign:"center",lineHeight:1.4}}>
                              {c.redemption_count > 0
                                ? `${c.redemption_count} user${c.redemption_count===1?"":"s"} kept`
                                : "audit only"}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </>
            );
          })()}
        </div>
      )}

      {/* Generate-code modal — same centered-modal pattern as the
          Portfolio Builder filter modal so all admin overlays feel coherent. */}
      {showCodeModal && (
        <>
          <div onClick={()=>setShowCodeModal(false)} style={{
            position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", zIndex:9000,
          }}/>
          <div onClick={e=>e.stopPropagation()} style={{
            position:"fixed", inset:0, margin:"auto",
            width:"min(420px, calc(100vw - 32px))",
            height:"fit-content",
            maxHeight:"min(640px, calc(100vh - 80px))",
            overflowY:"auto",
            background:"#0d1528", border:`1px solid ${C.border}`,
            borderRadius:16, padding:18, zIndex:9001,
            boxShadow:"0 12px 48px rgba(0,0,0,0.6)",
          }}>
            <div style={{display:"flex",justifyContent:"space-between",
              alignItems:"center",marginBottom:14}}>
              <div style={{fontSize:14,fontWeight:700,color:C.text}}>
                Generate Invite Code
              </div>
              <button onClick={()=>setShowCodeModal(false)} style={{
                background:"none",border:"none",color:C.muted,cursor:"pointer",
                fontSize:22,lineHeight:1,padding:0,fontFamily:"inherit",
              }}>×</button>
            </div>

            <div style={{fontSize:10,color:C.muted,marginBottom:5,fontWeight:700,
              letterSpacing:"0.06em",textTransform:"uppercase"}}>
              Custom code (optional)
            </div>
            <input style={{...inp,marginBottom:12}} type="text"
              placeholder="Leave blank to auto-generate"
              value={newCode.code}
              onChange={e=>setNewCode(c=>({...c, code:e.target.value}))}/>

            <div style={{display:"flex",alignItems:"center",gap:6,
              marginBottom:5}}>
              <span style={{fontSize:10,color:C.muted,fontWeight:700,
                letterSpacing:"0.06em",textTransform:"uppercase"}}>
                Tier
              </span>
              <InfoTip text={
                "Sets the user's early_adopter_tier flag (durable on the user record). " +
                "Today: shows a badge on registration + tracked for analytics. " +
                "At Stripe launch, this flag drives founder/F&F billing perks — " +
                "no code changes needed, just a one-line UPDATE.\n\n" +
                "🌟 FOUNDER — Earliest invitees. Highest tier. " +
                "Planned: free Pro forever (or biggest discount you decide).\n\n" +
                "💛 FRIENDS & FAMILY — Personal circle. " +
                "Planned: 1 year Pro free, then 50% off.\n\n" +
                "🧪 BETA — Public beta participants. " +
                "Planned: 6 months Pro free, or 25% off year 1.\n\n" +
                "These benefits are aspirational — final pricing decided " +
                "when Stripe launches. The tier flag is just the durable " +
                "label on each user that future billing logic reads."
              }/>
            </div>
            <div style={{display:"flex",gap:6,marginBottom:12}}>
              {[
                {key:"founder",        label:"🌟 Founder"},
                {key:"friends_family", label:"💛 F&F"},
                {key:"beta",           label:"🧪 Beta"},
              ].map(o => {
                const sel = newCode.tier === o.key;
                return (
                  <button key={o.key} onClick={()=>setNewCode(c=>({...c, tier:o.key}))}
                    style={{
                    flex:1,padding:"8px",borderRadius:8,border:"none",cursor:"pointer",
                    fontFamily:"inherit",fontSize:11,fontWeight:700,
                    background:sel?"rgba(0,212,255,0.12)":"rgba(255,255,255,0.04)",
                    color:sel?C.accent:C.text,
                    outline:sel?`1px solid ${C.accent}`:"none",
                  }}>{o.label}</button>
                );
              })}
            </div>

            <div style={{display:"flex",gap:8,marginBottom:12}}>
              <div style={{flex:1}}>
                <div style={{fontSize:10,color:C.muted,marginBottom:5,fontWeight:700,
                  letterSpacing:"0.06em",textTransform:"uppercase"}}>
                  Max uses
                </div>
                <input style={inp} type="number" min="0"
                  placeholder="1 (0=unlimited)"
                  value={newCode.max_uses}
                  onChange={e=>setNewCode(c=>({...c, max_uses:e.target.value}))}/>
              </div>
              <div style={{flex:1}}>
                <div style={{fontSize:10,color:C.muted,marginBottom:5,fontWeight:700,
                  letterSpacing:"0.06em",textTransform:"uppercase"}}>
                  Expires (days)
                </div>
                <input style={inp} type="number" min="0"
                  placeholder="never"
                  value={newCode.expires_in_days}
                  onChange={e=>setNewCode(c=>({...c, expires_in_days:e.target.value}))}/>
              </div>
            </div>

            <div style={{fontSize:10,color:C.muted,marginBottom:5,fontWeight:700,
              letterSpacing:"0.06em",textTransform:"uppercase"}}>
              Lock to email (optional)
            </div>
            <input style={{...inp,marginBottom:12}} type="email"
              placeholder="leave blank for any email"
              value={newCode.email_bound}
              onChange={e=>setNewCode(c=>({...c, email_bound:e.target.value}))}/>

            <div style={{fontSize:10,color:C.muted,marginBottom:5,fontWeight:700,
              letterSpacing:"0.06em",textTransform:"uppercase"}}>
              Notes (optional)
            </div>
            <input style={{...inp,marginBottom:14}} type="text"
              placeholder="e.g. 'Sent to Sarah 2026-04-25'"
              value={newCode.notes}
              onChange={e=>setNewCode(c=>({...c, notes:e.target.value}))}/>

            <div style={{display:"flex",gap:8}}>
              <button onClick={()=>setShowCodeModal(false)} style={{
                flex:1,padding:"10px",borderRadius:10,
                border:`1px solid ${C.border}`,background:"none",
                color:C.muted,fontFamily:"inherit",fontSize:12,fontWeight:600,
                cursor:"pointer",
              }}>Cancel</button>
              <button onClick={createInviteCode} disabled={creatingCode} style={{
                flex:2,padding:"10px",borderRadius:10,border:"none",
                background:creatingCode?"rgba(0,212,255,0.4)":C.accent,
                color:"#0a0f1e",fontFamily:"inherit",fontSize:12,fontWeight:700,
                cursor:creatingCode?"default":"pointer",
              }}>{creatingCode ? "Creating…" : "Generate code"}</button>
            </div>
          </div>
        </>
      )}

      {/* ── User list (v8.4.9 — merged from Settings; collapsible details) */}
      {!loading && users.length > 0 && (
        <div>
          <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:8}}>
            <div style={{fontSize:10,fontWeight:700,color:C.muted,
              letterSpacing:"0.1em",textTransform:"uppercase"}}>
              Users ({users.length})
            </div>
            <InfoTip text={
              "● Active — account enabled, no violation recorded.\n\n" +
              "Deactivate — temporarily suspends access (reversible, no violation flag). " +
              "Use for billing pauses, beta-slot management, or user request.\n\n" +
              "🚫 Ban — records a ToS violation (is_banned=1). Access revoked. " +
              "Reversible via Unban. Preserves the violation history for audit. " +
              "Use for abuse, credential sharing, or ToS breach.\n\n" +
              "Delete — permanently removes the user row from the database. " +
              "Cannot be undone. Use only for test accounts or legal deletion requests.\n\n" +
              "📧 Email Link — sends a self-service password reset link (expires 1h). " +
              "Preferred: admin never handles the password.\n\n" +
              "Set PW — manually sets a temporary password.\n\n" +
              "Abuse flags: ⚡ RATE = elevated requests/5min · 👥 IPs = multiple distinct IPs in 24h (credential sharing signal)."
            } />
            <div style={{flex:1}}/>
            {/* Expand All / Collapse All */}
            {(() => {
              const allExp = users.length > 0 && users.every(u => expandedUsers[u.id]);
              return (
                <button onClick={() => {
                  if (allExp) { setExpandedUsers({}); }
                  else { const m={}; users.forEach(u=>{m[u.id]=true;}); setExpandedUsers(m); }
                }} style={{padding:"3px 8px",borderRadius:6,border:`1px solid ${C.border}`,
                  background:"rgba(255,255,255,0.04)",color:C.muted,
                  fontSize:10,fontWeight:600,cursor:"pointer",fontFamily:"inherit",flexShrink:0}}>
                  {allExp ? "▴ Collapse All" : "▾ Expand All"}
                </button>
              );
            })()}
          </div>
          {/* Sort: current admin user first, then by id */}
          {[...users].sort((a,b) => {
            if (a.email === user?.email) return -1;
            if (b.email === user?.email) return  1;
            return a.id - b.id;
          }).map(u => {
            const abuse    = abuseReport[u.id] || {};
            const expanded = !!expandedUsers[u.id];
            const tierMeta = {
              founder:        {label:"🌟 Founder",    fg:"#ffca28", bg:"rgba(255,202,40,0.12)", border:"rgba(255,202,40,0.4)"},
              friends_family: {label:"💛 F&F",        fg:"#ff7043", bg:"rgba(255,112,67,0.12)",  border:"rgba(255,112,67,0.4)"},
              beta:           {label:"🧪 Beta",       fg:"#9aa8c0", bg:"rgba(154,168,192,0.10)", border:"rgba(154,168,192,0.3)"},
            };
            const cur  = u.early_adopter_tier;
            const tier = tierMeta[cur];
            return (
              <div key={u.id} style={{
                background: u.is_banned ? "rgba(255,80,80,0.04)" : C.surface,
                borderRadius:12, padding:"12px 14px", marginBottom:10,
                border:`1px solid ${u.is_banned?C.red:u.is_online?"rgba(0,230,118,0.3)":C.border}`,
                opacity: u.is_active || u.is_banned ? 1 : 0.6,
              }}>

                {/* ── Row 1: online dot · name · tier · role · status badge ── */}
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                  <div style={{width:8,height:8,borderRadius:"50%",flexShrink:0,
                    background:u.is_online?C.green:"rgba(255,255,255,0.15)",
                    boxShadow:u.is_online?"0 0 6px rgba(0,230,118,0.6)":"none"}}/>
                  <div style={{fontSize:14,fontWeight:700,color:C.text,flex:1,
                    overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                    {u.name}
                  </div>
                  {/* Tier pill — clickable to change */}
                  <div style={{position:"relative",flexShrink:0}}>
                    <button onClick={()=>setTierEditFor(prev => prev===u.id ? null : u.id)}
                      title="Click to change tier"
                      style={{fontSize:10,fontWeight:700,borderRadius:6,padding:"2px 8px",
                        border:`1px solid ${tier?.border||C.border}`,
                        background:tier?.bg||"rgba(255,255,255,0.04)",
                        color:tier?.fg||C.muted,
                        cursor:"pointer",fontFamily:"inherit",
                        outline:tierEditFor===u.id?`1px solid ${C.accent}`:"none"}}>
                      {tier?.label || "+ tier"}
                    </button>
                    {tierEditFor === u.id && (
                      <div style={{position:"absolute",right:0,top:"calc(100% + 4px)",
                        background:"#0d1528",border:`1px solid ${C.border}`,
                        borderRadius:10,padding:6,minWidth:140,zIndex:50,
                        boxShadow:"0 6px 20px rgba(0,0,0,0.5)",
                        display:"flex",flexDirection:"column",gap:2}}>
                        {[
                          {key:"founder",        label:"🌟 Founder"},
                          {key:"friends_family", label:"💛 Friends & Family"},
                          {key:"beta",           label:"🧪 Beta"},
                          {key:null,             label:"— None (clear)"},
                        ].map(opt => {
                          const sel = (cur||null) === opt.key;
                          return (
                            <button key={opt.key||"none"} onClick={()=>setUserTier(u, opt.key)}
                              style={{textAlign:"left",padding:"6px 10px",borderRadius:6,
                                background:sel?"rgba(0,212,255,0.10)":"none",
                                border:"none",cursor:"pointer",fontFamily:"inherit",
                                fontSize:11,fontWeight:600,color:sel?C.accent:C.text}}>
                              {opt.label}{sel?" ✓":""}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  {/* Role badge */}
                  <span style={{fontSize:10,fontWeight:700,borderRadius:6,padding:"2px 8px",
                    flexShrink:0,
                    background:u.role==="admin"?"rgba(0,212,255,0.15)":"rgba(255,255,255,0.07)",
                    color:u.role==="admin"?C.accent:C.muted}}>
                    {u.role}
                  </span>
                  {/* Status badge */}
                  <span style={{fontSize:10,fontWeight:700,borderRadius:6,padding:"2px 8px",
                    flexShrink:0,
                    background:u.is_banned?"rgba(255,82,82,0.15)":u.is_active?"rgba(0,230,118,0.1)":"rgba(255,255,255,0.06)",
                    color:u.is_banned?C.red:u.is_active?C.green:C.muted}}>
                    {u.is_banned?"🚫 Banned":u.is_active?"● Active":"● Inactive"}
                  </span>
                </div>

                {/* ── Row 2: email · Details toggle (far right) ─────── */}
                <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:8,paddingLeft:16}}>
                  <div style={{fontSize:11,color:C.muted,flex:1,
                    overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                    {u.email}
                  </div>
                  <button onClick={()=>setExpandedUsers(p=>({...p,[u.id]:!p[u.id]}))}
                    style={{padding:"3px 8px",borderRadius:6,border:`1px solid ${C.border}`,
                      background:"rgba(255,255,255,0.04)",color:C.muted,
                      fontSize:10,fontWeight:600,cursor:"pointer",fontFamily:"inherit",
                      flexShrink:0}}>
                    {expanded ? "▴ Hide" : "▾ Details"}
                  </button>
                </div>

                {/* ── Row 3: abuse flags · action buttons ──────────────── */}
                <div style={{display:"flex",gap:5,alignItems:"center",flexWrap:"wrap",paddingLeft:16}}>
                  {abuse.flagged && (
                    <span style={{fontSize:9,fontWeight:700,padding:"2px 6px",borderRadius:4,
                      background:"rgba(255,160,0,0.18)",color:"#ffa500",flexShrink:0}}>
                      ⚡ RATE {abuse.rate} req/5m
                    </span>
                  )}
                  {abuse.sharing && (
                    <span style={{fontSize:9,fontWeight:700,padding:"2px 6px",borderRadius:4,
                      background:"rgba(255,82,82,0.18)",color:C.red,flexShrink:0}}>
                      👥 {abuse.ips} IPs/24h
                    </span>
                  )}
                  <div style={{flex:1}}/>
                  {u.id !== user?.id ? (
                    <>
                      {/* v8.4.67: 🔔 Nudge button — only shown for users who
                          have never logged in (last_login_raw IS NULL). The
                          backend enforces a 24h cooldown per user. */}
                      {!u.last_login_raw && u.is_active && !u.is_banned && (
                        <button onClick={async ()=>{
                            if (!window.confirm(`Send a "haven't logged in yet" nudge email to ${u.email}?\n\nFriendly reminder with sign-in link + "forgot password" CTA. 24h cooldown per user.`)) return;
                            try {
                              const d = await apiFetch(`/admin/users/${u.id}/nudge`, token, {method:"POST"});
                              flash(d.detail || (d.ok ? "Nudge sent" : "Send failed"));
                            } catch(e) {
                              // 429 = cooldown; format the message inline so it's not just "Error"
                              flash(`❌ ${e.message}`);
                            }
                          }}
                          title={u.nudge_last_sent_at
                            ? `Last nudge: ${u.nudge_last_sent_at.slice(0,16)}`
                            : "Send a friendly login-reminder email"}
                          style={{padding:"5px 9px",borderRadius:7,border:"none",cursor:"pointer",
                            fontFamily:"inherit",fontSize:11,fontWeight:600,
                            background:"rgba(255,193,7,0.14)",color:"#ffc107"}}>
                          🔔
                        </button>
                      )}
                      {/* Email-link reset — preferred path */}
                      <button onClick={async ()=>{
                          if (!window.confirm(`Email a password-reset link to ${u.email}?\n\nThey click the link and set their own password. Expires in 1 hour.`)) return;
                          try {
                            const d = await apiFetch(`/admin/users/${u.id}/send-reset-email`, token, {method:"POST"});
                            flash(d.detail || (d.ok ? "Reset email sent" : "Send failed"));
                            if (!d.ok && d.reset_url_for_manual_share) {
                              try { await navigator.clipboard.writeText(d.reset_url_for_manual_share);
                                setTimeout(()=>flash("Reset URL copied for manual share"),2000);
                              } catch(_) {}
                            }
                          } catch(e) { flash(`❌ ${e.message}`); }
                        }}
                        style={{padding:"5px 9px",borderRadius:7,border:"none",cursor:"pointer",
                          fontFamily:"inherit",fontSize:11,fontWeight:600,
                          background:"rgba(0,230,118,0.12)",color:C.green}}>
                        📧
                      </button>
                      {/* Manual set PW — expands form in details */}
                      <button onClick={()=>{
                          setExpandedUsers(p=>({...p,[u.id]:true}));
                          setShowReset(p=>({...p,[u.id]:!p[u.id]}));
                        }}
                        style={{padding:"5px 9px",borderRadius:7,border:"none",cursor:"pointer",
                          fontFamily:"inherit",fontSize:11,fontWeight:600,
                          background:"rgba(0,212,255,0.1)",color:C.accent}}>
                        {showReset[u.id]?"✕ PW":"Set PW"}
                      </button>
                      {/* Ban / Unban */}
                      {!u.is_banned ? (
                        <button onClick={()=>handleBan(u)}
                          style={{padding:"5px 9px",borderRadius:7,border:"none",cursor:"pointer",
                            fontFamily:"inherit",fontSize:11,fontWeight:600,
                            background:"rgba(255,82,82,0.12)",color:C.red}}>
                          🚫 Ban
                        </button>
                      ) : (
                        <button onClick={()=>handleUnban(u)}
                          style={{padding:"5px 9px",borderRadius:7,border:"none",cursor:"pointer",
                            fontFamily:"inherit",fontSize:11,fontWeight:600,
                            background:"rgba(0,230,118,0.1)",color:C.green}}>
                          ✓ Unban
                        </button>
                      )}
                      {/* Deactivate / Reactivate */}
                      <button onClick={()=>handleToggle(u)}
                        style={{padding:"5px 9px",borderRadius:7,border:"none",cursor:"pointer",
                          fontFamily:"inherit",fontSize:11,fontWeight:600,
                          background:u.is_active?"rgba(255,202,40,0.1)":"rgba(0,230,118,0.1)",
                          color:u.is_active?C.yellow:C.green}}>
                        {u.is_active?"Deact.":"Reactiv."}
                      </button>
                      {/* Delete */}
                      <button onClick={()=>handleDelete(u)}
                        style={{padding:"5px 9px",borderRadius:7,border:"none",cursor:"pointer",
                          fontFamily:"inherit",fontSize:11,fontWeight:600,
                          background:"rgba(255,82,82,0.1)",color:C.red}}>
                        Delete
                      </button>
                    </>
                  ) : (
                    <span style={{fontSize:10,color:C.muted,fontStyle:"italic"}}>you</span>
                  )}
                </div>

                {/* ── Expanded details ─────────────────────────────────── */}
                {expanded && (
                  <div style={{marginTop:10,paddingTop:10,borderTop:`1px solid ${C.border}`}}>
                    {/* Stats grid */}
                    <div style={{display:"flex",gap:0,marginBottom:8,
                      background:C.card,borderRadius:8,overflow:"hidden",
                      border:`1px solid ${C.border}`}}>
                      {[
                        {label:"Logins",     val: u.login_count ?? 0,                    mono:true},
                        {label:"Last login", val: fmt(u.last_login_at),                  mono:false},
                        {label:"Last seen",  val: u.is_online?"🟢 Now":fmt(u.last_seen_at), mono:false, green:u.is_online},
                        {label:"Profile",    val: u.risk_profile || "—",                 mono:false},
                      ].map(({label,val,mono,green},i,arr) => (
                        <div key={label} style={{flex:1,padding:"7px 8px",
                          borderRight:i<arr.length-1?`1px solid ${C.border}`:"none",
                          minWidth:0}}>
                          <div style={{fontSize:8,color:C.muted,letterSpacing:"0.06em",
                            textTransform:"uppercase",marginBottom:2}}>{label}</div>
                          <div style={{fontSize:11,fontWeight:600,
                            color:green?C.green:C.text,
                            fontFamily:mono?"monospace":"inherit",
                            overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                            {val}
                          </div>
                        </div>
                      ))}
                    </div>
                    {/* Usage & Security table */}
                    <div style={{background:"rgba(0,0,0,0.2)",borderRadius:8,
                      padding:"8px 10px",marginBottom:showReset[u.id]?8:0,
                      border:`1px solid ${C.border}`}}>
                      <div style={{fontSize:9,fontWeight:700,color:C.muted,
                        letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:6}}>
                        Usage &amp; Security
                      </div>
                      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6}}>
                        {[
                          {label:"Last IP",
                           val: u.last_login_ip || "—",
                           color:"#7dd3fc", mono:true},
                          {label:"Country",
                           val: u.last_country || "—",
                           color: C.muted},
                          {label:"Req today",
                           val: u.req_today != null ? u.req_today : "—",
                           color: u.req_today > 500 ? C.red : u.req_today > 100 ? "#ffa500" : C.green,
                           mono: true},
                          {label:"Request rate",
                           val: abuse.flagged ? `${abuse.rate} req/5min` : "Normal",
                           color: abuse.flagged ? "#ffa500" : C.green},
                          {label:"Distinct IPs (24h)",
                           val: abuse.sharing ? `${abuse.ips} IPs detected` : "1 (normal)",
                           color: abuse.sharing ? C.red : C.green},
                          {label:"Logins total",
                           val: u.login_count ?? 0,
                           color: C.text, mono:true},
                          {label:"Device",
                           val: (() => {
                             const ua = u.last_user_agent || "";
                             if (!ua) return "—";
                             if (/python|requests|axios|curl|wget|scrapy/i.test(ua)) return "🤖 Script";
                             if (/iPhone|iPad/i.test(ua)) return "📱 iPhone/iPad";
                             if (/Android/i.test(ua)) return "📱 Android";
                             if (/Mac/i.test(ua) && /Safari/i.test(ua) && !/Chrome/i.test(ua)) return "🍎 Mac Safari";
                             if (/Windows/i.test(ua)) return "🖥 Windows";
                             if (/Mac/i.test(ua)) return "💻 Mac";
                             return "🌐 Browser";
                           })(),
                           color: (() => {
                             const ua = u.last_user_agent || "";
                             return /python|requests|axios|curl|wget|scrapy/i.test(ua) ? C.red : C.text;
                           })()},
                          {label:"ToS flag",
                           val: u.is_banned ? "🚫 Banned" : (abuse.flagged||abuse.sharing) ? "⚠ Flagged" : "Clean",
                           color: u.is_banned ? C.red : (abuse.flagged||abuse.sharing) ? "#ffa500" : C.green},
                          {label:"Risk profile",
                           val: u.risk_profile || "—",
                           color: C.muted},
                        ].map(({label,val,color,mono}) => (
                          <div key={label}>
                            <div style={{fontSize:8,color:C.muted,letterSpacing:"0.05em",
                              textTransform:"uppercase",marginBottom:1}}>{label}</div>
                            <div style={{fontSize:10,fontWeight:600,
                              color:color||C.text,
                              fontFamily:mono?"monospace":"inherit",
                              overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                              {val}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    {/* Recent endpoints — full width, horizontally scrollable so paths aren't cut */}
                    {(u.recent_endpoints||[]).length > 0 && (
                      <div style={{marginTop:6,background:"rgba(0,0,0,0.2)",
                        borderRadius:8,padding:"8px 10px",
                        border:`1px solid ${C.border}`}}>
                        <div style={{fontSize:9,fontWeight:700,color:C.muted,
                          letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:5}}>
                          Recent endpoints
                        </div>
                        <div style={{overflowX:"auto"}}>
                          {u.recent_endpoints.map((ep,i) => (
                            <div key={i} style={{fontSize:10,fontFamily:"monospace",
                              color: /analyze|universe/.test(ep) ? "#7dd3fc" : C.muted,
                              whiteSpace:"nowrap",lineHeight:1.7}}>
                              {ep}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {/* Login history + Recover watchlist — own row beneath endpoints */}
                    <div style={{marginTop:6,display:"flex",gap:6}}>
                      <button onClick={async ()=>{
                          setLoginHistoryFor(u);
                          setLoginHistoryData([]);
                          setLoginHistoryLoad(true);
                          try {
                            const d = await apiFetch(`/admin/login-history/${u.id}`, token);
                            setLoginHistoryData(d.events || []);
                          } catch(e) {
                            setLoginHistoryData([]);
                          } finally {
                            setLoginHistoryLoad(false);
                          }
                        }} style={{
                          flex:1,padding:"7px 10px",borderRadius:7,
                          border:`1px solid ${C.border}`,
                          background:"rgba(0,212,255,0.06)",
                          color:C.accent,fontFamily:"inherit",
                          fontSize:10,fontWeight:700,cursor:"pointer",
                        }}>
                        📋 Login history
                      </button>
                      <button onClick={async ()=>{
                          setWlRecoveryFor(u);
                          setWlRecoveryData(null);
                          setWlRestoreMsg("");
                          setWlRecoveryLoad(true);
                          try {
                            const d = await apiFetch(`/admin/watchlist-recovery?uid=${u.id}`, token);
                            setWlRecoveryData(d);
                          } catch(e) {
                            setWlRecoveryData({error: e.message || "Failed to load recovery data"});
                          } finally {
                            setWlRecoveryLoad(false);
                          }
                        }} style={{
                          flex:1,padding:"7px 10px",borderRadius:7,
                          border:`1px solid ${C.border}`,
                          background:"rgba(0,230,118,0.06)",
                          color:C.green,fontFamily:"inherit",
                          fontSize:10,fontWeight:700,cursor:"pointer",
                        }}>
                        🔍 Recover watchlist
                      </button>
                    </div>
                    {/* Watchlist — shows what this user is tracking */}
                    {(() => {
                      const wl = (u.watchlist || "").split(",").map(t=>t.trim().toUpperCase()).filter(Boolean);
                      if (!wl.length) return null;
                      return (
                        <div style={{background:"rgba(0,0,0,0.2)",borderRadius:8,
                          padding:"8px 10px",marginTop:6,
                          border:`1px solid ${C.border}`}}>
                          <div style={{fontSize:9,fontWeight:700,color:C.muted,
                            letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:6}}>
                            Watchlist ({wl.length} tickers)
                          </div>
                          <div style={{display:"flex",flexWrap:"wrap",gap:4}}>
                            {wl.map(t => (
                              <span key={t} style={{fontSize:10,fontWeight:700,
                                background:"rgba(0,212,255,0.08)",
                                border:"1px solid rgba(0,212,255,0.2)",
                                borderRadius:4,padding:"2px 6px",
                                color:"#7dd3fc",fontFamily:"monospace"}}>
                                {t}
                              </span>
                            ))}
                          </div>
                        </div>
                      );
                    })()}
                    {/* Set-password form (inside expanded panel) */}
                    {showReset[u.id] && (
                      <div style={{display:"flex",gap:8,marginTop:0}}>
                        <input style={{...inp,flex:1}}
                          type="password"
                          placeholder="New temporary password (min 8 chars)"
                          autoComplete="new-password"
                          value={resetPw[u.id]||""}
                          onChange={e=>setResetPw(prev=>({...prev,[u.id]:e.target.value}))}/>
                        <button onClick={()=>handleResetPw(u)}
                          style={{padding:"8px 14px",borderRadius:8,border:"none",cursor:"pointer",
                            background:C.accent,color:C.bg,fontSize:12,fontWeight:700,
                            fontFamily:"inherit",whiteSpace:"nowrap"}}>
                          Set PW
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!loading && users.length === 0 && !error && (
        <div style={{textAlign:"center",padding:"32px 16px",color:C.muted}}>
          No users found.
        </div>
      )}

      {/* ── Watchlist Recovery Modal (v8.4.78) ──────────────────────── */}
      {wlRecoveryFor && (
        <>
          <div onClick={()=>{setWlRecoveryFor(null);setWlRestoreMsg("");}}
            style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:9000}}/>
          <div style={{
            position:"fixed",inset:0,margin:"auto",zIndex:9001,
            width:"min(460px, calc(100vw - 32px))",
            height:"fit-content",
            maxHeight:"min(620px, calc(100vh - 60px))",
            background:C.surface,borderRadius:14,
            border:`1px solid ${C.border}`,
            padding:"16px",overflowY:"auto",
            boxSizing:"border-box",
          }}>
            <div style={{display:"flex",justifyContent:"space-between",
                         alignItems:"center",marginBottom:12}}>
              <div>
                <div style={{fontSize:13,fontWeight:700,color:C.text}}>
                  Watchlist Recovery
                </div>
                <div style={{fontSize:11,color:C.muted}}>{wlRecoveryFor.email}</div>
              </div>
              <button onClick={()=>{setWlRecoveryFor(null);setWlRestoreMsg("");}}
                style={{background:"none",border:"none",color:C.muted,
                  fontSize:20,cursor:"pointer",padding:"4px 8px"}}>✕</button>
            </div>

            {wlRecoveryLoad && (
              <div style={{textAlign:"center",padding:"24px",color:C.muted}}>Loading…</div>
            )}

            {!wlRecoveryLoad && wlRecoveryData?.error && (
              <div style={{padding:"12px",color:C.red,fontSize:12,
                           background:"rgba(255,82,82,0.08)",borderRadius:8}}>
                {wlRecoveryData.error}
                <div style={{marginTop:6,color:C.muted,fontSize:11}}>
                  Deploy v8.4.76+ on the VM first: git pull && docker compose restart api
                </div>
              </div>
            )}

            {!wlRecoveryLoad && wlRecoveryData && !wlRecoveryData.error && (()=>{
              const current  = wlRecoveryData.current_watchlist || [];
              const fromLog  = wlRecoveryData.log_tickers || [];
              const fromAlerts = wlRecoveryData.alert_tickers || [];
              const combined = wlRecoveryData.combined || [];
              // Tickers in combined but NOT in current watchlist = recovery candidates
              const recoverable = combined.filter(t => !current.includes(t));

              const Section = ({title, tickers, dim}) => tickers.length === 0 ? null : (
                <div style={{marginBottom:12}}>
                  <div style={{fontSize:9,fontWeight:700,color:dim ? "#444" : C.muted,
                    letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:5}}>
                    {title} ({tickers.length})
                  </div>
                  <div style={{display:"flex",flexWrap:"wrap",gap:4}}>
                    {tickers.map(t=>(
                      <span key={t} style={{fontSize:10,fontWeight:700,
                        background: dim ? "rgba(255,255,255,0.04)" : "rgba(0,212,255,0.08)",
                        border:`1px solid ${dim ? "rgba(255,255,255,0.1)" : "rgba(0,212,255,0.2)"}`,
                        borderRadius:4,padding:"2px 6px",
                        color: dim ? "#555" : "#7dd3fc",fontFamily:"monospace"}}>
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              );

              return (
                <>
                  <Section title="Currently on watchlist" tickers={current} dim={false}/>
                  <Section title="From watchlist change log" tickers={fromLog} dim={false}/>
                  <Section title="From price/swing alerts" tickers={fromAlerts} dim={false}/>

                  {recoverable.length > 0 ? (
                    <div style={{marginTop:4,padding:"10px 12px",
                                 background:"rgba(0,230,118,0.06)",
                                 border:"1px solid rgba(0,230,118,0.2)",
                                 borderRadius:10}}>
                      <div style={{fontSize:11,color:C.green,fontWeight:700,marginBottom:8}}>
                        {recoverable.length} recoverable tickers (not on current watchlist)
                      </div>
                      <div style={{display:"flex",flexWrap:"wrap",gap:4,marginBottom:10}}>
                        {recoverable.map(t=>(
                          <span key={t} style={{fontSize:10,fontWeight:700,
                            background:"rgba(0,230,118,0.10)",
                            border:"1px solid rgba(0,230,118,0.3)",
                            borderRadius:4,padding:"2px 6px",
                            color:C.green,fontFamily:"monospace"}}>{t}</span>
                        ))}
                      </div>
                      <div style={{fontSize:10,color:C.muted,marginBottom:8}}>
                        This will replace the current watchlist with the combined list
                        ({[...new Set([...current,...recoverable])].length} total tickers).
                      </div>
                      {wlRestoreMsg ? (
                        <div style={{fontSize:11,color: wlRestoreMsg.startsWith("✅") ? C.green : C.red,
                                     padding:"6px 10px",borderRadius:6,
                                     background: wlRestoreMsg.startsWith("✅")
                                       ? "rgba(0,230,118,0.08)" : "rgba(255,82,82,0.08)"}}>
                          {wlRestoreMsg}
                        </div>
                      ) : (
                        <button
                          disabled={wlRestoring}
                          onClick={async ()=>{
                            const merged = [...new Set([...current,...recoverable])];
                            setWlRestoring(true);
                            setWlRestoreMsg("");
                            try {
                              await apiFetch(`/watchlist`, token, {
                                method:"PUT",
                                body: JSON.stringify({tickers: merged}),
                              });
                              setWlRestoreMsg(`✅ Watchlist restored — ${merged.length} tickers saved.`);
                              // Refresh the users list so watchlist column updates
                              const d = await apiFetch("/admin/users", token);
                              if(d?.users) setUsers(d.users);
                            } catch(e) {
                              setWlRestoreMsg(`❌ ${e.message || "Restore failed"}`);
                            } finally {
                              setWlRestoring(false);
                            }
                          }}
                          style={{width:"100%",padding:"9px",borderRadius:8,
                                  background: wlRestoring ? "rgba(0,230,118,0.3)" : C.green,
                                  color:"#000",border:"none",fontWeight:700,
                                  fontSize:12,cursor: wlRestoring ? "not-allowed" : "pointer"}}>
                          {wlRestoring ? "Restoring…" : `✦ Restore ${recoverable.length} tickers to watchlist`}
                        </button>
                      )}
                    </div>
                  ) : (
                    <div style={{fontSize:11,color:C.muted,textAlign:"center",padding:"12px 0"}}>
                      No additional recovery tickers found beyond the current watchlist.
                    </div>
                  )}

                  <div style={{fontSize:9,color:"#333",marginTop:10,lineHeight:1.5}}>
                    Sources: watchlist change log (v8.4.76+) + all-time alerts history.
                    Log entries only available after v8.4.76 is deployed on the VM.
                  </div>
                </>
              );
            })()}
          </div>
        </>
      )}

      {/* ── Login History Modal (v8.4.17) ────────────────────────────── */}
      {loginHistoryFor && (
        <>
          <div onClick={()=>setLoginHistoryFor(null)}
            style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:9000}}/>
          <div style={{
            position:"fixed",inset:0,margin:"auto",zIndex:9001,
            width:"min(420px, calc(100vw - 32px))",
            height:"fit-content",
            maxHeight:"min(560px, calc(100vh - 80px))",
            background:C.surface,borderRadius:14,
            border:`1px solid ${C.border}`,
            padding:"16px",overflowY:"auto",
          }}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
              <div>
                <div style={{fontSize:13,fontWeight:700,color:C.text}}>
                  Login History
                </div>
                <div style={{fontSize:11,color:C.muted}}>{loginHistoryFor.email}</div>
              </div>
              <button onClick={()=>setLoginHistoryFor(null)}
                style={{background:"none",border:"none",color:C.muted,
                  fontSize:20,cursor:"pointer",padding:"4px 8px"}}>✕</button>
            </div>
            {loginHistoryLoad && (
              <div style={{textAlign:"center",padding:"24px",color:C.muted}}>Loading…</div>
            )}
            {!loginHistoryLoad && loginHistoryData.length === 0 && (
              <div style={{textAlign:"center",padding:"24px",color:C.muted,fontSize:12}}>
                No login events recorded yet.
              </div>
            )}
            {!loginHistoryLoad && loginHistoryData.map((ev, i) => {
              const ua = ev.user_agent || "";
              const device = /python|requests|curl|wget/i.test(ua) ? "🤖 Script"
                : /iPhone|iPad/i.test(ua) ? "📱 iPhone/iPad"
                : /Android/i.test(ua) ? "📱 Android"
                : /Windows/i.test(ua) ? "🖥 Windows"
                : /Mac/i.test(ua) ? "💻 Mac"
                : ua ? "🌐 Browser" : "—";
              const isScript = /python|requests|curl|wget/i.test(ua);
              const ts = ev.ts ? new Date(ev.ts + "Z") : null;
              const ago = ts ? (() => {
                const s = Math.floor((Date.now() - ts.getTime()) / 1000);
                if (s < 60) return `${s}s ago`;
                if (s < 3600) return `${Math.floor(s/60)}m ago`;
                if (s < 86400) return `${Math.floor(s/3600)}h ago`;
                return `${Math.floor(s/86400)}d ago`;
              })() : "";
              return (
                <div key={i} style={{
                  padding:"8px 10px",borderRadius:8,marginBottom:6,
                  background: isScript ? "rgba(255,82,82,0.06)" : "rgba(255,255,255,0.03)",
                  border:`1px solid ${isScript ? "rgba(255,82,82,0.2)" : C.border}`,
                }}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:3}}>
                    <span style={{fontSize:11,fontWeight:700,
                      color: isScript ? C.red : C.text}}>{device}</span>
                    <span style={{fontSize:10,color:C.muted,fontFamily:"monospace"}}>{ago}</span>
                  </div>
                  <div style={{fontSize:10,fontFamily:"monospace",color:"#7dd3fc"}}>
                    {ev.ip || "—"}{ev.country ? ` · ${ev.country}` : ""}
                  </div>
                  {ua && (
                    <div style={{fontSize:9,color:C.muted,marginTop:2,
                      overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}
                      title={ua}>{ua}</div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}


// --- Root app -----------------------------------------------------------------
// --- Splash Screen -----------------------------------------------------------
function SplashScreen({ onDone }) {
  React.useEffect(() => {
    const t = setTimeout(() => onDone(), 3500);
    return () => clearTimeout(t);
  }, [onDone]);

  return (
    <div style={{position:"fixed",inset:0,zIndex:9999,background:"#080c14",overflow:"hidden"}}>
      <img src="/splash.jpg" alt="" style={{
        position:"absolute",top:"50%",left:"50%",
        transform:"translate(-50%,-50%)",
        width:"100%",height:"auto",
      }}/>
      <div style={{position:"absolute",inset:0,pointerEvents:"none",
        background:"radial-gradient(circle at 50% 34%, rgba(0,212,255,0.6) 0%, rgba(0,212,255,0) 28%)",
        animation:"logoPulse 1.4s ease-in-out infinite"}}/>
      <div style={{position:"absolute",inset:0,pointerEvents:"none",
        background:"radial-gradient(circle at 50% 34%, rgba(0,212,255,0.3) 0%, rgba(0,212,255,0) 45%)",
        animation:"logoPulse 1.4s ease-in-out infinite 0.35s"}}/>
      <style>{`
        @keyframes logoPulse {
          0%   { opacity:0;   transform:scale(0.85); }
          30%  { opacity:1;   transform:scale(1.08); }
          60%  { opacity:0.5; transform:scale(1.0);  }
          100% { opacity:0;   transform:scale(0.85); }
        }
      `}</style>
    </div>
  );
}

// ── Auth persistence (v7.9.47, opt-in via "Keep me logged in" v7.9.52) ─────
// Token + user are cached in either localStorage (durable, user opted in via
// "Keep me logged in") or sessionStorage (session-only, default — clears when
// the tab closes). Boot reads from localStorage first, then sessionStorage.
//
// A background /auth/me call validates the cached token. If the backend
// rejects it (401 → AuthError → handleLogout) the storage gets cleared and
// the user falls back to the login screen.
const AUTH_STORAGE_KEY = "gempulse_auth";

// Try localStorage first (durable / "remember me"), then sessionStorage
// (per-session). Returns the token+user pair plus the store it came from
// so callers can keep writes to that same store on profile updates.
function _readCachedAuth() {
  for (const store of [
    { obj: typeof localStorage   !== "undefined" ? localStorage   : null, name: "local"   },
    { obj: typeof sessionStorage !== "undefined" ? sessionStorage : null, name: "session" },
  ]) {
    if (!store.obj) continue;
    try {
      const raw = store.obj.getItem(AUTH_STORAGE_KEY);
      if (!raw) continue;
      const parsed = JSON.parse(raw) || {};
      // Defensive: only honor entries that look complete. Anything else is
      // treated as a corrupt write and we skip to the next store.
      if (typeof parsed.token === "string" && parsed.token.length > 10
          && parsed.user && typeof parsed.user === "object") {
        return { token: parsed.token, user: parsed.user, store: store.name };
      }
    } catch (_) {}
  }
  return { token: null, user: null, store: null };
}

// Write to one store (durable=true → localStorage, false → sessionStorage)
// AND clear the other so an old persistent session doesn't outlive a fresh
// session-only login (or vice versa).
function _writeCachedAuth(token, user, durable = true) {
  const payload = JSON.stringify({ token, user, ts: Date.now() });
  try {
    if (durable) {
      localStorage.setItem(AUTH_STORAGE_KEY, payload);
      sessionStorage.removeItem(AUTH_STORAGE_KEY);
    } else {
      sessionStorage.setItem(AUTH_STORAGE_KEY, payload);
      localStorage.removeItem(AUTH_STORAGE_KEY);
    }
  } catch (_) {}
}

// Clear from both stores — called on logout / 401 so leftover entries
// in either store can't auto-rehydrate a dead session.
function _clearCachedAuth() {
  try { localStorage.removeItem(AUTH_STORAGE_KEY); } catch (_) {}
  try { sessionStorage.removeItem(AUTH_STORAGE_KEY); } catch (_) {}
}

// v8.4.308: global ticker-search overlay. Opens from the header magnifier so a
// ticker is one tap away from any screen (no more routing through Discover).
// Types-ahead over the user's watchlist + the scored universe; Enter/tap analyzes.
function TickerSearchOverlay({ universeData, watchlist, onSelect, onClose }) {
  const [q, setQ] = React.useState("");
  const inputRef = React.useRef(null);
  React.useEffect(() => { const t = setTimeout(() => { try { inputRef.current?.focus(); } catch (_) {} }, 60); return () => clearTimeout(t); }, []);
  const query = q.trim().toUpperCase();
  const suggestions = React.useMemo(() => {
    const pool = new Map();
    (watchlist || []).forEach(t => { const u = String(t).toUpperCase(); pool.set(u, { ticker: u }); });
    Object.values(universeData || {}).forEach(r => {
      if (r && r.ticker) { const u = String(r.ticker).toUpperCase(); pool.set(u, { ticker: u, shortName: r.shortName }); }
    });
    let arr = Array.from(pool.values());
    if (query) {
      arr = arr.filter(s => s.ticker.includes(query) || (s.shortName || "").toUpperCase().includes(query));
      arr.sort((a, b) => (b.ticker.startsWith(query) ? 1 : 0) - (a.ticker.startsWith(query) ? 1 : 0));
    }
    return arr.slice(0, 8);
  }, [query, universeData, watchlist]);
  const go = (t) => { const tk = String(t || query).trim().toUpperCase(); if (/^[A-Z]{1,6}(\.[A-Z]{1,3})?$/.test(tk)) { onSelect(tk); onClose(); } };
  return (
    <div onClick={onClose} style={{position:"fixed",inset:0,zIndex:60,
      background:"rgba(4,7,13,0.72)",backdropFilter:"blur(3px)",
      display:"flex",flexDirection:"column",alignItems:"center",
      paddingTop:"calc(env(safe-area-inset-top, 0px) + 60px)"}}>
      <div onClick={e=>e.stopPropagation()} style={{width:"100%",maxWidth:430,
        padding:"0 16px",boxSizing:"border-box"}}>
        <div style={{display:"flex",alignItems:"center",gap:8,background:C.surface,
          border:`1px solid ${C.accent}`,borderRadius:12,padding:"10px 12px"}}>
          <span style={{fontSize:16,opacity:0.7}}>🔍</span>
          <input ref={inputRef} value={q} onChange={e=>setQ(e.target.value)}
            onKeyDown={e=>{ if (e.key==="Enter") go(); if (e.key==="Escape") onClose(); }}
            placeholder="Search a ticker (e.g. AMD, NVDA.TO)…"
            style={{flex:1,background:"none",border:"none",outline:"none",
              color:C.text,fontSize:16,fontFamily:"inherit"}}/>
          <button onClick={onClose} style={{background:"none",border:"none",
            color:C.muted,fontSize:18,cursor:"pointer",fontFamily:"inherit",lineHeight:1}}>×</button>
        </div>
        {suggestions.length > 0 && (
          <div style={{background:C.surface,border:`1px solid ${C.border}`,
            borderRadius:12,marginTop:8,overflow:"hidden"}}>
            {suggestions.map(s=>(
              <button key={s.ticker} onClick={()=>go(s.ticker)} style={{width:"100%",
                display:"flex",alignItems:"center",gap:10,padding:"10px 12px",
                background:"none",border:"none",borderBottom:`1px solid ${C.border}`,
                cursor:"pointer",fontFamily:"inherit",textAlign:"left"}}>
                <TickerLogo ticker={s.ticker} size={24}/>
                <span style={{fontSize:14,fontWeight:800,color:C.text,fontFamily:"monospace"}}>{s.ticker}</span>
                {s.shortName && s.shortName !== s.ticker && (
                  <span style={{fontSize:11,color:C.muted,overflow:"hidden",
                    textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{s.shortName}</span>
                )}
              </button>
            ))}
          </div>
        )}
        {query && (
          <button onClick={()=>go()} style={{width:"100%",marginTop:8,padding:"11px",
            borderRadius:12,border:`1px solid ${C.accent}`,background:"rgba(0,212,255,0.12)",
            color:C.accent,fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
            Analyze {query} →
          </button>
        )}
      </div>
    </div>
  );
}

export default function App() {
  React.useEffect(() => { _bootstrapTourSeenState(); }, []);
  const [showSplash, setShowSplash] = React.useState(false);
  // Hydrate from localStorage on first mount. The pair is always read together
  // so we never end up with a token but no user (or vice-versa).
  const _bootAuth = React.useMemo(() => _readCachedAuth(), []);
  const [user,        setUser]      = useState(_bootAuth.user);
  const [token,       setToken]     = useState(_bootAuth.token);
  // Detect ?reset_token=... in the URL on first mount. When present, the
  // entire app reroutes to the reset-password screen until the user finishes
  // or cancels — see the resetToken branch near the render path below.
  const [resetToken, setResetToken] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const t = params.get("reset_token");
      return t && t.length > 10 ? t : null;
    } catch (_) {
      return null;
    }
  });
  // ?invite=CODE — routes to RegisterScreen. Same pattern as resetToken.
  // (v7.9.50)
  const [inviteCode, setInviteCode] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const c = params.get("invite");
      return c && c.length >= 4 ? c : null;
    } catch (_) {
      return null;
    }
  });
  const { updateAvailable, serverVersion } = useUpdateAvailable();
  const [screen,      setScreen]    = useState("landing");
  const [ticker,      setTicker]    = useState(null);
  // Horizon persists across app opens (was useState("6m") with no persistence, so
  // every reload reset a user's 1m/3m choice back to 6m). localStorage-backed.
  const [horizon,     setHorizonRaw] = useState(() => {
    try { return localStorage.getItem("gp_horizon") || "6m"; } catch { return "6m"; }
  });
  const setHorizon = React.useCallback((h) => {
    setHorizonRaw(h);
    try { localStorage.setItem("gp_horizon", h); } catch {}
  }, []);
  const [currency,    setCurrency]  = useState(() => localStorage.getItem("swingai_currency") || "native");
  const handleCurrencyChange = (cur) => {
    setCurrency(cur);
    localStorage.setItem("swingai_currency", cur);
  };
  const [tradePref,   setTradePref] = useState(() => localStorage.getItem("swingai_tradepref") || "blended");
  const handleTradePrefChange = (p) => { setTradePref(p); localStorage.setItem("swingai_tradepref", p); };
  // v8.4.308: accessibility text scale (Normal / Large / Larger) via root zoom.
  const [textScale,   setTextScale] = useState(() => localStorage.getItem("gempulse_text_scale") || "1");
  const handleTextScaleChange = (s) => { setTextScale(s); try { localStorage.setItem("gempulse_text_scale", s); } catch(_){} };
  React.useEffect(() => { try { document.documentElement.style.zoom = textScale; } catch (_) {} }, [textScale]);
  // v8.4.308: global ticker-search overlay (magnifier in the header opens it).
  const [searchOpen,  setSearchOpen] = useState(false);
  // v8.4.316: universe tickers reporting earnings within ~7 days → subtle 📅 tile badge.
  const [earningsSoon, setEarningsSoon] = useState({});
  const _earnSoonAt = React.useRef(0);
  React.useEffect(() => {
    if (!token) return;
    const load = () => {
      apiFetch("/calendar/earnings-upcoming?days=7", token)
        .then(d => { if (d?.byTicker) setEarningsSoon(d.byTicker); _earnSoonAt.current = Date.now(); })
        .catch(() => {});
    };
    load();
    // v8.4.333 — refetch on focus/visibility (throttled 60s) so a device that loaded
    // BEFORE the calendar was warmed picks up the "E" chips without a manual reload
    // (PC showed no chips while the phone that warmed it did).
    const onFocus = () => {
      if (document.visibilityState === "hidden") return;
      if (Date.now() - _earnSoonAt.current > 60000) load();
    };
    window.addEventListener("visibilitychange", onFocus);
    window.addEventListener("focus", onFocus);
    return () => {
      window.removeEventListener("visibilitychange", onFocus);
      window.removeEventListener("focus", onFocus);
    };
  }, [token]);
  const [fxRates,     setFxRates]   = React.useState({USDCAD: 1.36}); // fallback rate
  const [navTab,      setNavTab]    = useState("landing");
  // alertsRefreshKey: increment to trigger AlertsPanel re-fetch from anywhere in the app
  const [alertsRefreshKey, setAlertsRefreshKey] = useState(0);
  const handleAlertsChanged = React.useCallback(() => setAlertsRefreshKey(k => k + 1), []);

  // ── Universe data — single source of truth for all tabs ───────────────────
  // Map<ticker, ScoredResult> — populated by background scorer.
  // v8.4.165: lazy-init from localStorage (18h TTL) so an app open — or a
  // /universe timeout (the documented Cloudflare-524 → 56-ticker UNIVERSE_T1
  // fallback under VM load) — shows the last-known full universe INSTANTLY
  // instead of an empty grid or the hardcoded T1 set. The background /universe
  // fetch still runs and merges fresh scores over the stale ones (setUniverseData
  // is merge-based), so nothing disappears mid-refresh and the user always sees
  // a complete universe. Mirrors the existing `marketData` localStorage cache.
  const [universeData,    setUniverseData]    = useState(() => {
    try {
      const raw = localStorage.getItem("gempulse_universe");
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.data && (Date.now() - (parsed.ts || 0)) < 18 * 3600 * 1000) {
          return parsed.data;
        }
      }
    } catch (_) {}
    return {};
  });
  const [universePhase,   setUniversePhase]   = useState(0);  // 0=idle 1=T1 2=T2 3=done
  const universeAbortRef  = React.useRef(false); // set true on refresh to cancel scan
  const universeRef       = React.useRef(undefined); // ref mirror for use inside callbacks
  // Seed the ref mirror from the localStorage-hydrated initial state so callbacks
  // that read universeRef.current see the restored universe on first render too.
  if (universeRef.current === undefined) universeRef.current = { ...universeData };

  // Update universeData and keep ref in sync.
  // v8.3.14: normalise the key to UPPERCASE + strip whitespace so callers
  // passing "aapl", "AAPL ", or "AAPL" all hit the same slot. Without this,
  // a session that mixed lowercase calls (e.g. from /analyze response paths
  // where the response field varied case) accumulated duplicate entries —
  // user reported 2863 "stocks scored" when the universe is only ~810
  // (3.5× = case-variation explosion).
  const upsertUniverse = React.useCallback((ticker, result) => {
    const key = String(ticker || "").trim().toUpperCase();
    if (!key) return;
    // Merge-preserve existing fields that the caller didn't supply.
    // Many call sites (bgRefresh, lazy-fill, discovery scoreOne) pass a
    // partial dict — wholesale replace would wipe sparkline, rsRating,
    // patterns, isLeader written by an earlier path. v8.4.29: merge
    // onto prev so partial writes never erase complementary data.
    const prev = universeRef.current[key] || {};
    const normResult = { ...prev, ...result, ticker: key };
    // Preserve sparkline when caller omits it (so /analyze upserts don't
    // wipe Celery's 14-day sparkline written via /universe).
    if ((!Array.isArray(result.sparkline) || result.sparkline.length === 0)
        && Array.isArray(prev.sparkline) && prev.sparkline.length > 0) {
      normResult.sparkline = prev.sparkline;
    }
    universeRef.current = {...universeRef.current, [key]: normResult};
    setUniverseData(prevState => ({...prevState, [key]: normResult}));
  }, []);

  // Clear and restart the full universe scan
  const resetUniverse = React.useCallback(() => {
    universeAbortRef.current = true;   // signal any running scan to stop
    setTimeout(() => { universeAbortRef.current = false; }, 100);
    universeRef.current = {};
    setUniverseData({});
    setUniversePhase(0);
  }, []);

  // Lifted scan state -- persists when navigating to stock detail and back
  const [scanData,    setScanData]  = useState(null);
  const [scanLoading, setScanLoading] = useState(false);
  const [scanError,   setScanError] = useState("");
  const [watchlist,   setWatchlist] = useState([]);

  // ── Background pre-fetch for top-scored tickers ─────────────────────────
  // When the universe list first reaches 30+ entries, silently fire /analyze
  // calls for the top-12 tickers by combined score. This warms the full Redis
  // cache (candles + fundamentals) so tapping any top-scored stock opens
  // instantly instead of triggering a cold full-pipeline compute (8-15 s).
  // Watchlist tickers get priority — user is most likely to tap those.
  // Only fires once per ticker per session (prefetchedRef). Staggered 700 ms
  // apart so we don't hammer the VM with 12 simultaneous requests.
  const prefetchedRef = React.useRef(new Set());
  React.useEffect(() => {
    if (!token) return;
    const entries = Object.values(universeData);
    if (entries.length < 30) return;
    const scored = entries
      .filter(e => e.ticker && !e.ticker.endsWith(".TO") && !e.ticker.endsWith(".V"))
      .map(e => ({
        ticker: e.ticker,
        score: (e.ltScore || 0) + (e.swingScore || 0),
        inWatchlist: watchlist.includes(e.ticker),
      }))
      .sort((a, b) => (b.inWatchlist - a.inWatchlist) || (b.score - a.score));
    const toFetch = scored.slice(0, 12).map(e => e.ticker)
      .filter(t => !prefetchedRef.current.has(t));
    if (!toFetch.length) return;
    toFetch.forEach((ticker, i) => {
      setTimeout(() => {
        if (!prefetchedRef.current.has(ticker)) {
          prefetchedRef.current.add(ticker);
          apiFetch(`/analyze/${ticker}?horizon=${horizon}`, token)
            .catch(() => {});
        }
      }, i * 700);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [universePhase, watchlist, token]);

  // v8.4.165: persist the scored universe to localStorage once a scan completes
  // (phase 3) and the grid is healthy, so the NEXT app open shows the full
  // universe instantly (see the lazy-init above). Guarded at ≥200 tickers so a
  // /universe-timeout T1 fallback (~56 tickers) or a sparse post-restart cache
  // never overwrites a previously-good full snapshot with a thin one.
  //
  // v8.4.171 — CRITICAL FIX: the previous version stringified the FULL entries
  // (including the `sparkline` and `sparklineIntraday` arrays — the intraday one
  // can be ~390 floats/ticker). For ~1500 tickers that blows past the ~5MB
  // localStorage quota, so setItem threw QuotaExceededError, the catch swallowed
  // it, and NOTHING was ever stored — which is why the universe kept collapsing
  // to the partial /universe result (e.g. 48) on every reload despite the
  // "fix". We now persist a SLIM projection (scores/labels/price/sector/etc.,
  // no heavy arrays). Sparklines repopulate from the background /universe fetch
  // and are preserved by the merge there, so the only cost is a brief tick
  // before mini-charts fill — the score grid shows the full universe instantly.
  React.useEffect(() => {
    if (universePhase !== 3) return;
    try {
      const entries = Object.entries(universeData);
      if (entries.length < 200) return;
      const slim = {};
      for (const [k, v] of entries) {
        if (!v) continue;
        // Keep only the lightweight scalar fields the tiles/lists need on first
        // paint. Drop full sparkline / patterns (big arrays).
        // v8.4.214: sparklineIntraday re-added — v8.4.172 downsampled it from
        // ~390 → ~30 pts (~240B/ticker × 1000 tickers = ~240KB, well within quota).
        // Without it, every page load shows 3W until a /analyze hydration completes.
        slim[k] = {
          ticker: v.ticker, shortName: v.shortName, sector: v.sector,
          price: v.price, changePct: v.changePct, isLive: v.isLive,
          swingScore: v.swingScore, swingLabel: v.swingLabel, swingType: v.swingType,
          ltScore: v.ltScore, ltLabel: v.ltLabel, ltType: v.ltType,
          horizonReturn: v.horizonReturn, beta: v.beta, rsi: v.rsi,
          sentiment: v.sentiment, rsRating: v.rsRating, isLeader: v.isLeader,
          scoredAt: v.scoredAt,
          // patterns is a tiny array (0-5 short strings) — keep it so any
          // component reading d.patterns during the brief pre-fetch window is safe.
          patterns: Array.isArray(v.patterns) ? v.patterns : [],
          // sparklineIntraday: ~30 pts post-v8.4.172 downsampling — small enough to persist.
          sparklineIntraday: Array.isArray(v.sparklineIntraday) ? v.sparklineIntraday : [],
        };
      }
      localStorage.setItem("gempulse_universe",
        JSON.stringify({ ts: Date.now(), data: slim }));
    } catch (_) {
      // Still over quota (huge universe / other large keys)? Drop our snapshot
      // so a stale-but-present entry doesn't linger, and give up silently.
      try { localStorage.removeItem("gempulse_universe"); } catch (_e) {}
    }
  }, [universePhase, universeData]);

  // Lifted market pulse state — persisted to localStorage (30-min TTL) to survive tab switches
  const [marketData,  setMarketData] = useState(() => {
    try {
      const raw = localStorage.getItem("gempulse_market_pulse");
      if (!raw) return null;
      const { ts, data } = JSON.parse(raw);
      // v8.4.30: reject partial blobs at hydration so a stale write from an
      // older code path (e.g. with vix but no dxy / 10yr) can't permanently
      // shadow a fresh refresh. Require all three regime fields present.
      const r = data?.regime;
      const isComplete = r && r.vix > 0 && r.dxy != null && r.tenYrYield != null;
      if (Date.now() - ts < 30 * 60 * 1000 && isComplete) return data;
    } catch(e) {}
    return null;
  });
  const setMarketDataPersisted = React.useCallback((md) => {
    setMarketData(md);
    // Only persist when all required regime fields are present. Incomplete
    // data would mask a real backend issue on next load — let the next load
    // fetch fresh instead.
    const r = md && md.regime;
    if (r && r.vix > 0 && r.dxy != null && r.tenYrYield != null) {
      try { localStorage.setItem("gempulse_market_pulse", JSON.stringify({ ts: Date.now(), data: md })); }
      catch(e) {}
    }
  }, [setMarketData]);
  // Ref that ScannerScreen populates
  const runScanRef = React.useRef(null);
  const [lastScanTime, setLastScanTime] = useState(0);
  const triggerWatchlistScan = React.useCallback(() => {
    if (runScanRef.current) runScanRef.current();
  }, []);

  // Track which storage tier the current session uses so handleProfileChange
  // can keep the persisted blob in the SAME store (don't promote a session-only
  // login to a durable one just because the user changed their risk profile).
  const [authDurable, setAuthDurable] = useState(_bootAuth.store === "local");

  const handleLogin = (u, t, rememberMe = false) => {
    setShowSplash(true);
    setUser(u);
    setToken(t);
    setAuthDurable(!!rememberMe);
    _writeCachedAuth(t, u, !!rememberMe);
  };

  // ── Boot validation ───────────────────────────────────────────────────
  // We rendered with the cached user/token immediately to avoid a login
  // screen flash. Now in the background, hit /auth/me to confirm the token
  // is still valid. If the backend rejects it, AuthError → handleLogout
  // (which also clears localStorage). Any other error is left alone — the
  // user keeps working with the cached identity, and any real /auth-gated
  // endpoint will surface its own AuthError if the token is dead.
  React.useEffect(() => {
    if (!token || !user) return;
    let cancelled = false;
    (async () => {
      try {
        const me = await apiFetch("/auth/me", token);
        if (cancelled || !me) return;
        // Refresh local user copy in case role / risk_profile / is_active
        // changed server-side (e.g. admin promoted, user deactivated).
        setUser(me);
        // Preserve whichever storage tier hydrated the session — don't
        // accidentally promote sessionStorage → localStorage just because we
        // happened to refetch the user.
        _writeCachedAuth(token, me, authDurable);
      } catch (e) {
        if (cancelled) return;
        if (e?.name === "AuthError") {
          // Token rejected — the global handler will clear state once
          // logout cascades, but be explicit here in case this effect
          // races the handler initialization.
          _clearCachedAuth();
          setUser(null); setToken(null); setScreen("scanner");
          setScanData(null); setWatchlist([]);
        }
        // Network failures / 5xx are silent — keep the cached session so
        // the user can keep working offline-ish.
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // run exactly once on mount

  // v8.4.75: App-level watchlist loader — fires on every token change (mount,
  // login, re-login). Populates watchlist state before the user can navigate
  // anywhere so Discovery "+Watchlist" adds never race against an empty state.
  // Without this, watchlist is only loaded inside WatchlistScreen's useEffect;
  // if the user adds a ticker from Discovery before visiting the Watchlist tab,
  // PUT /watchlist would fire with only the new ticker and overwrite the DB.
  // Guard: only set state when server returns non-empty (prevents a brief 401
  // or network error from clearing valid watchlist state held from a prior load).
  React.useEffect(() => {
    if (!token) return;
    let cancelled = false;
    apiFetch("/watchlist", token)
      .then(d => {
        if (cancelled) return;
        const tickers = d.tickers || [];
        if (tickers.length > 0) setWatchlist(tickers);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Increment install-prompt session counter once per authenticated app load
  React.useEffect(() => {
    try {
      const n = parseInt(localStorage.getItem(INSTALL_SESSIONS_KEY) || '0', 10);
      localStorage.setItem(INSTALL_SESSIONS_KEY, String(n + 1));
    } catch (_) {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Background universe scorer ───────────────────────────────────────────────
  // Scores SCORING_UNIVERSE in two tiers. Tier 1 (~40 tickers) fires first so
  // the watchlist and key stocks are ready within ~60s. Tier 2 runs in background.
  // Results stream into universeData in real-time as each ticker scores.
  const scoreUniverseBackground = React.useCallback(async (tokenVal, watchlistTickers=[], hz="6m", rp="moderate") => {
    if (!tokenVal) return;
    universeAbortRef.current = false;
    setUniversePhase(1);

    try {
      // ── Fetch full universe from Redis cache (scores) ─────────────────────
      // hydrate_prices=false: skip the live-price hydration path entirely.
      // When yfinance is broken on the VM, hydrate_prices=auto spawns
      // YFinanceProvider threads that hang indefinitely (no socket timeout
      // on fast_info).  With each periodic 5-min refresh those threads
      // accumulate → OS resource exhaustion → /universe itself times out
      // → Cloudflare 524 → catch block → T1 fallback (57 stocks).
      //
      // The fix: return immediately from Redis cache (scores only); live
      // prices come from the per-ticker /analyze calls that already fire
      // in the background pre-fetch below.
      //
      // include= removed: when yfinance is broken on the VM the TSX-ticker
      // enrichment path inside /universe hangs indefinitely → CF 524 →
      // catch block → T1 fallback.  Missing watchlist tickers are handled
      // below via the /analyze/batch fallback after the universe loads.
      const uResp = await apiFetch(
        `/universe?horizon=${hz}&profile=${rp}&hydrate_prices=false`, tokenVal);
      if (universeAbortRef.current) return;
      const cached = uResp?.tickers || {};

      // ── Double-buffer: build staging locally, swap atomically ───────────
      // Previously each ticker called upsertUniverse() which triggered a
      // React state update per entry — causing 514 incremental re-renders
      // and leaving the UI in a half-filled state during refresh cycles.
      // Now we build the full new universe in a plain object and swap it in
      // one setUniverseData call, so users always see either the complete
      // old universe or the complete new one — never a partial mid-refresh
      // state. Old entries not present in the new response are dropped.
      const staging = {};
      const _isObj = (v) => v !== null && typeof v === 'object';
      for (const [ticker, d] of Object.entries(cached)) {
        if (universeAbortRef.current) return;
        const t_up = ticker.toUpperCase();
        // v8.3.10 fix: typeof null === 'object' in JS — must check d.x is
        // non-null AND object before treating as nested shape. Otherwise
        // tickers where the cache value is literally `null` (e.g. ltScore
        // failed to compute) would take the object branch and resolve to
        // `null?.score` = undefined → MRVL-style "missing LT" symptom.
        // Fallback chain: nested .score → flat ltScore → nested longTermScore.score
        const swScore = _isObj(d.swingScore) ? d.swingScore.score
                      : (d.swingScore ?? (_isObj(d.swingScoreFull) ? d.swingScoreFull.score : null));
        const ltScore = _isObj(d.ltScore)    ? d.ltScore.score
                      : (d.ltScore    ?? (_isObj(d.longTermScore)  ? d.longTermScore.score  : null));
        const swLabel = _isObj(d.swingScore) ? d.swingScore.label
                      : (d.swingLabel || (_isObj(d.swingScoreFull) ? d.swingScoreFull.label : ''));
        const swType  = _isObj(d.swingScore) ? d.swingScore.labelType
                      : (d.swingType  || (_isObj(d.swingScoreFull) ? d.swingScoreFull.labelType : 'watch'));
        const ltLabel = _isObj(d.ltScore)    ? d.ltScore.label
                      : (d.ltLabel    || (_isObj(d.longTermScore)  ? d.longTermScore.label    : ''));
        const ltType  = _isObj(d.ltScore)    ? d.ltScore.labelType
                      : (d.ltType     || (_isObj(d.longTermScore)  ? d.longTermScore.labelType : 'watch'));
        const ltHR    = d.horizonReturn ?? (_isObj(d.longTermScore) ? d.longTermScore.horizonReturn : null);
        staging[t_up] = {
          ticker:        t_up,
          shortName:     d.shortName    || d.fundamentals?.shortName || ticker,
          sector:        d.sector || d.fundamentals?.sector || SECTOR_HINTS[t_up] || '',
          price:         d.price        ?? 0,
          changePct:     d.changePct    ?? 0,   // ?? not || so -0.01% and genuine 0% survive
          isLive:        d.isLive       || false,
          swingScore:    swScore        ?? null,
          swingLabel:    swLabel        || '',
          swingType:     swType         || 'watch',
          ltScore:       ltScore        ?? null,
          ltLabel:       ltLabel        || '',
          ltType:        ltType         || 'watch',
          horizonReturn: ltHR           ?? null,
          beta:          d.beta         || 1.0,
          rsi:           d.rsi          || 50,
          sentiment:     d.sentiment?.label || d.sentiment || '',
          rsRating:      d.rsRating     ?? null,
          isLeader:      d.isLeader     || false,
          patterns:      Array.isArray(d.patterns) ? d.patterns : [],
          // v8.4.298: carry the caution/signal flags into universeData so surfaces that
          // read universeData DIRECTLY — the portfolio optimizer and the Discovery quick
          // lists (Bounce/Rotation) — can use them. The main merge previously dropped
          // these (only the per-list scoreTickers mappings had them), so the Bounce/
          // Rotation quick lists came up empty even with 57 flagged in the cache.
          profitTakeFlag:  d.profitTakeFlag  || false,
          pullbackFlag:    d.pullbackFlag    || false,
          bounceFlag:      d.bounceFlag      || false,
          bounceCandidate: d.bounceCandidate || false,
          rotationRisk:    d.rotationRisk    || false,
          setupQuality:    d.setupQuality    || null,   // v8.4.419 strong|neutral|weak (backend _holding_quality)
          dividendYield:   d.dividendYield ?? null,   // v8.4.299 decimal (0.03 = 3%)
          dollarVolume:    d.dollarVolume ?? null,   // v8.4.309 for the movers bubble chart
          marketCap:       d.marketCap ?? d.fundamentals?.marketCap ?? null,   // v8.4.317 bubble-chart size fallback
          scoredAt:          d.scoredAt ? d.scoredAt * 1000 : Date.now(),
          sparkline:         Array.isArray(d.sparkline) ? d.sparkline : [],
          // v8.4.78: include intraday sparkline from universe response.
          // Was missing → browser always had [] → 1D tab showed 3W-only.
          sparklineIntraday: Array.isArray(d.sparklineIntraday) ? d.sparklineIntraday : [],
        };
      }

      if (universeAbortRef.current) return;

      // Merge swap — new staging data wins for any ticker it contains, but
      // OLD entries for tickers NOT yet in this response are PRESERVED.
      // This is critical during mid-cycle refreshes: Celery scores 514
      // tickers in ~51 chunks over ~2 min. If the frontend fetches while
      // Celery is mid-run, /universe only returns the ~55 completed tickers.
      // Without merge, the UI would drop from 514→55 visible stocks.
      // With merge, users see 514 stocks the whole time — new scores replace
      // stale ones as they arrive, but nothing disappears mid-cycle.
      if (Object.keys(staging).length > 0) {
        setUniverseData(prev => {
          // Merge swap (see note above) — staging wins for any ticker it contains;
          // prev entries NOT in this response are preserved ONLY while still fresh.
          // A live universe ticker is re-scored every cycle so its scoredAt stays
          // current; a ticker that churned OUT of the backend universe (a gap-scanner
          // one-off, a dropped dynamic name) stops updating and ages out instead of
          // accumulating forever and inflating the "scored" count (the 2961 / 2464
          // bloat). The freshness window keeps mid-cycle partial responses safe
          // (the full prior set is still recent, so nothing disappears mid-run).
          const PRUNE_MS = 48 * 60 * 60 * 1000;
          const nowMs = Date.now();
          const merged = {};
          for (const [key, pv] of Object.entries(prev)) {
            if (staging[key]) continue;                       // refreshed below
            if (nowMs - (pv?.scoredAt || 0) < PRUNE_MS) merged[key] = pv;  // keep fresh
            // else: churned-out + stale → drop
          }
          // Staging priority for every field EXCEPT sparkline: if staging has an
          // empty sparkline (ticker not yet scored by Celery) but prev has one
          // (written by scoreOne / upsertUniverse), keep the prev value so
          // navigation doesn't wipe mini-chart data.
          for (const [key, sv] of Object.entries(staging)) {
            const prevSparkline   = prev[key]?.sparkline;
            const stagingSparkline  = sv.sparkline;
            // v8.4.78: also preserve sparklineIntraday across universe refreshes.
            // Without this, intraHydrate fills it via upsertUniverse but the next
            // 30s universe poll wipes it back to [] — stuck on 3W forever.
            const prevIntraday    = prev[key]?.sparklineIntraday;
            const stagingIntraday = sv.sparklineIntraday;
            merged[key] = {
              ...sv,
              sparkline: (Array.isArray(stagingSparkline) && stagingSparkline.length > 0)
                ? stagingSparkline
                : (Array.isArray(prevSparkline) && prevSparkline.length > 0)
                  ? prevSparkline
                  : [],
              sparklineIntraday: (Array.isArray(stagingIntraday) && stagingIntraday.length > 0)
                ? stagingIntraday
                : (Array.isArray(prevIntraday) && prevIntraday.length > 0)
                  ? prevIntraday
                  : [],
            };
          }
          universeRef.current = merged;
          return merged;
        });
      }

      setUniversePhase(Object.keys(staging).length > 0 ? 2 : 1);

      // Feed market pulse from EOD cache endpoint (survives until next open)
      try {
        const mp = await apiFetch('/market-pulse', tokenVal);
        const spy = mp?.spy || cached['SPY'] || cached['spy'];
        const regime = mp?.regime || spy?.regime;
        if (spy || regime) {
          setMarketData({
            spy:    spy || {},
            regime: regime,
            qqq:    mp?.qqq || cached['QQQ'] || null,
            dia:    mp?.dia || cached['DIA'] || null,
            tsx:    mp?.tsx || cached['XIU.TO'] || null,
            gld:    cached['GLD'] || null,
          });
          if (regime) {
            setScanData(prev => prev
              ? {...prev, regime}
              : {results:[], count:0, regime, profile:{key:"moderate"}});
            setLastScanTime(Date.now());
          }
        }
      } catch(e) {
        // Fallback: use SPY from universe cache
        const spy = cached['SPY'] || cached['spy'];
        if (spy?.regime) {
          setMarketData({spy, regime:spy.regime,
            qqq:cached['QQQ']||null, dia:cached['DIA']||null, gld:cached['GLD']||null});
        }
      }

      // Score watchlist tickers that either:
      //  (a) are completely absent from the universe cache (not a universe ticker), OR
      //  (b) exist in the cache but have null scores — Celery hasn't scored them yet
      //      this cycle, or scoring failed for that specific ticker.
      // Previously only (a) was caught, so watchlist stocks in the universe
      // but with null Redis scores (e.g. SMTC, UMAC, SNDK, ENLT) showed empty
      // tiles at initial load with no fallback firing for them.
      const missing = watchlistTickers.filter(t => {
        const key   = t?.toUpperCase();
        const entry = cached[key] || cached[t];
        if (!entry) return true; // (a) not in universe at all
        // (b) in universe but both scores are null
        const sw = _isObj(entry.swingScore) ? entry.swingScore.score : (entry.swingScore ?? null);
        const lt = _isObj(entry.ltScore)    ? entry.ltScore.score    : (entry.ltScore    ?? null);
        return sw == null && lt == null;
      });
      if (missing.length > 0 && !universeAbortRef.current) {
        try {
          const bResp = await apiFetch('/analyze/batch?horizon=6m', tokenVal,
            { method:'POST', body: JSON.stringify({ tickers: missing }) });
          for (const [ticker, d] of Object.entries(bResp?.results || {})) {
            if (universeAbortRef.current) break;
            const t_up = ticker.toUpperCase();
            upsertUniverse(ticker, {
              ticker, shortName:d.shortName||ticker,
              sector:d.fundamentals?.sector||SECTOR_HINTS[t_up]||'',
              price:d.price||0, changePct:d.changePct||0, isLive:d.isLive||false,
              swingScore:d.swingScore?.score??null, swingLabel:d.swingScore?.label||'',
              swingType:d.swingScore?.labelType||'watch',
              ltScore:d.longTermScore?.score??null, ltLabel:d.longTermScore?.label||'',
              ltType:d.longTermScore?.labelType||'watch',
              horizonReturn:d.longTermScore?.horizonReturn??null,
              beta:d.beta||1.0, rsi:d.rsi||50, sentiment:d.sentiment?.label||'',
              rsRating:d.rsRating??null, isLeader:d.isLeader||false,
              patterns:Array.isArray(d.patterns)?d.patterns:[], scoredAt:Date.now(),
            });
          }
        } catch(e) {}
      }

    } catch(fetchErr) {
      // /universe unavailable — fall back to batch scoring
      const t1 = [...new Set([...watchlistTickers, ...UNIVERSE_T1])];
      const BATCH_SIZE = 15;
      for (let i = 0; i < t1.length; i += BATCH_SIZE) {
        if (universeAbortRef.current) break;
        const chunk = t1.slice(i, i + BATCH_SIZE);
        try {
          const resp = await apiFetch('/analyze/batch?horizon=6m', tokenVal,
            { method:'POST', body: JSON.stringify({ tickers: chunk }) });
          for (const [ticker, d] of Object.entries(resp?.results || {})) {
            if (universeAbortRef.current) break;
            const t_up = ticker.toUpperCase();
            upsertUniverse(ticker, {
              ticker, shortName:d.shortName||ticker,
              sector:d.fundamentals?.sector||SECTOR_HINTS[t_up]||'',
              price:d.price||0, changePct:d.changePct||0, isLive:d.isLive||false,
              swingScore:d.swingScore?.score??null, swingLabel:d.swingScore?.label||'',
              swingType:d.swingScore?.labelType||'watch',
              ltScore:d.longTermScore?.score??null, ltLabel:d.longTermScore?.label||'',
              ltType:d.longTermScore?.labelType||'watch',
              horizonReturn:d.longTermScore?.horizonReturn??null,
              beta:d.beta||1.0, rsi:d.rsi||50, sentiment:d.sentiment?.label||'',
              rsRating:d.rsRating??null, isLeader:d.isLeader||false,
              patterns:Array.isArray(d.patterns)?d.patterns:[], scoredAt:Date.now(),
            });
          }
        } catch(e) {}
      }
    }

    if (!universeAbortRef.current) {
      // v8.3.15: self-heal stale duplicate keys that accumulated before
      // the upsertUniverse normalize fix (v8.3.14). The user reported
      // counts like 3319 — that's 810 unique tickers × ~4 case variations.
      // Collapse them here on every /universe refresh: rebuild the dict
      // with UPPERCASE+trimmed keys. Duplicates merge under the canonical
      // key (last-write-wins; the /universe response is authoritative).
      setUniverseData(prev => {
        const normalized = {};
        for (const [k, v] of Object.entries(prev)) {
          const nk = String(k || "").trim().toUpperCase();
          if (!nk || !/^[A-Z][A-Z0-9.-]{0,9}$/.test(nk)) continue;  // strip non-ticker keys
          normalized[nk] = v && v.ticker ? { ...v, ticker: nk } : v;
        }
        return normalized;
      });
      setUniversePhase(3);
      setLastScanTime(Date.now());
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [upsertUniverse]);

  // Fetch USD/CAD FX rate for currency conversion display
  React.useEffect(() => {
    fetch("https://query1.finance.yahoo.com/v8/finance/chart/CADUSD=X?interval=1d&range=2d",
      {headers:{"User-Agent":"Mozilla/5.0"}})
      .then(r => r.json())
      .then(d => {
        const rate = d?.chart?.result?.[0]?.meta?.regularMarketPrice;
        if (rate && rate > 0) setFxRates({USDCAD: 1/rate, CADUSD: rate});
      })
      .catch(()=>{});
  }, []);

  // Start background scan after login + auto-refresh during market hours.
  //
  // v7.9.63: in addition to the initial scan, we now re-run every 5 min
  // while the market is open.  hydrate_prices=false (changed from =auto):
  // the live-price hydration path spawned YFinanceProvider threads with no
  // socket timeout on yfinance.fast_info.  Under Yahoo throttling those
  // threads hang indefinitely and accumulate with each periodic call,
  // eventually exhausting the OS thread pool and causing /universe to time
  // out (Cloudflare 524) → app falls back to 57 hardcoded tickers.  Scores
  // are served from Redis cache; live prices come from /analyze/batch below.
  //
  // Why 5 min: balances freshness vs Finnhub free-tier rate limit (60/min).
  // ~80 US tickers per refresh × 12 refreshes/hour = 960/hour, well within
  // a single-user limit. Multi-tab/multi-user scenarios still safe because
  // each call is parallel-capped at 4 workers.
  React.useEffect(() => {
    if (!(user && token && watchlist !== null)) return;
    const rp = user?.risk_profile || "moderate";
    const initial = setTimeout(() => {
      scoreUniverseBackground(token, watchlist, horizon, rp);
    }, 800);
    // Periodic price refresh — only fires during market hours (pre/open/post).
    // Closed-market sessions skip the timer to avoid pointless network calls.
    const periodic = setInterval(() => {
      if (isMarketOpen()) {
        scoreUniverseBackground(token, watchlist, horizon, rp);
      }
    }, 5 * 60 * 1000);
    return () => { clearTimeout(initial); clearInterval(periodic); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, token]);

  // v8.4.299: refetch the universe when the app is FOREGROUNDED (tab focus / PWA resume),
  // throttled to once a minute. A backgrounded PWA doesn't re-mount React, so without this
  // the mount-only + market-hours-interval fetch left universeData stale on reopen — the
  // recurring "list is blank until I manually reload" problem, worst after-hours when the
  // periodic timer is off. Pairs with the reactive Bounce/Rotation lists so they refresh
  // automatically instead of needing a hard reload.
  React.useEffect(() => {
    if (!(user && token && watchlist !== null)) return;
    const rp = user?.risk_profile || "moderate";
    let last = Date.now();
    const onForeground = () => {
      if (document.visibilityState && document.visibilityState !== "visible") return;
      if (Date.now() - last < 60 * 1000) return;   // at most once/min
      last = Date.now();
      scoreUniverseBackground(token, watchlist, horizon, rp);
    };
    document.addEventListener("visibilitychange", onForeground);
    window.addEventListener("focus", onForeground);
    return () => {
      document.removeEventListener("visibilitychange", onForeground);
      window.removeEventListener("focus", onForeground);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, token, watchlist, horizon]);

  // Refresh universe scores in-place whenever the user changes horizon or risk profile.
  // Uses the same upsert pattern so Discovery/Watchlist scores update without a flash.
  const refreshUniverseForSettings = React.useCallback(async (hz, rp) => {
    if (!token) return;
    try {
      const uResp = await apiFetch(
        `/universe?horizon=${hz}&profile=${rp}&hydrate_prices=false`, token);
      const cached = uResp?.tickers || {};
      for (const [ticker, d] of Object.entries(cached)) {
        const t_up = ticker.toUpperCase();
        const swScore = typeof d.swingScore==='object' ? d.swingScore?.score  : d.swingScore;
        const ltScore = typeof d.ltScore   ==='object' ? d.ltScore?.score     : d.ltScore;
        const swLabel = typeof d.swingScore==='object' ? d.swingScore?.label  : d.swingLabel;
        const swType  = typeof d.swingScore==='object' ? d.swingScore?.labelType : d.swingType;
        const ltLabel = typeof d.ltScore   ==='object' ? d.ltScore?.label     : d.ltLabel;
        const ltType  = typeof d.ltScore   ==='object' ? d.ltScore?.labelType : d.ltType;
        const ltHR    = d.horizonReturn ?? (typeof d.longTermScore==='object' ? d.longTermScore?.horizonReturn : null);
        upsertUniverse(ticker, {
          ticker,
          shortName:     d.shortName    || ticker,
          sector:        d.sector || d.fundamentals?.sector || SECTOR_HINTS[t_up] || '',
          price:         d.price        || 0,
          changePct:     d.changePct    || 0,
          isLive:        d.isLive       || false,
          swingScore:    swScore        || 0,
          swingLabel:    swLabel        || '',
          swingType:     swType         || 'watch',
          ltScore:       ltScore        || 0,
          ltLabel:       ltLabel        || '',
          ltType:        ltType         || 'watch',
          horizonReturn: ltHR           ?? null,
          beta:          d.beta         || 1.0,
          rsi:           d.rsi          || 50,
          sentiment:     d.sentiment?.label || d.sentiment || '',
          rsRating:      d.rsRating     ?? null,
          isLeader:      d.isLeader     || false,
          patterns:      Array.isArray(d.patterns) ? d.patterns : [],
          // v8.4.299: carry the caution/signal flags here too so a horizon/profile change
          // doesn't strip them from universeData (Bounce/Rotation lists + optimizer read them).
          profitTakeFlag:  d.profitTakeFlag  || false,
          pullbackFlag:    d.pullbackFlag    || false,
          bounceFlag:      d.bounceFlag      || false,
          bounceCandidate: d.bounceCandidate || false,
          rotationRisk:    d.rotationRisk    || false,
          setupQuality:    d.setupQuality    || null,
          dividendYield:   d.dividendYield ?? null,
          dollarVolume:    d.dollarVolume ?? null,   // v8.4.309 movers bubble chart
          scoredAt:      d.scoredAt ? d.scoredAt * 1000 : Date.now(),
        });
      }
    } catch(e) {}
  }, [token, upsertUniverse]);

  // Re-fetch universe with new horizon/profile whenever either setting changes.
  // Skips on initial mount (universeData is already populated by scoreUniverseBackground).
  const isFirstRender = React.useRef(true);
  React.useEffect(() => {
    if (isFirstRender.current) { isFirstRender.current = false; return; }
    if (!user || !token) return;
    const rp = user?.risk_profile || "moderate";
    refreshUniverseForSettings(horizon, rp);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [horizon, user?.risk_profile]);

  // Heartbeat — tells backend this user is online (updates last_seen_at)
  React.useEffect(() => {
    if (!user || !token) return;
    const ping = () => apiFetch("/auth/heartbeat", token, {method:"POST"}).catch(()=>{});
    ping(); // immediate on login
    const iv = setInterval(ping, 5 * 60 * 1000); // every 5 min
    return () => clearInterval(iv);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, token]);
  const handleLogout = () => {
    setUser(null); setToken(null); setScreen("scanner");
    setScanData(null); setWatchlist([]);
    // Clear persisted auth so the next reload doesn't auto-rehydrate a
    // dead session. AuthError-driven logout funnels through this same path
    // so 401s anywhere in the app correctly clear localStorage too.
    _clearCachedAuth();
  };

  // Global auth error handler -- catches expired tokens across all screens
  const handleAuthError = React.useCallback((e) => {
    if (e?.name === "AuthError") {
      handleLogout();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleProfileChange = async (key) => {
    if (key) {
      setUser(prev => {
        const next = {...prev, risk_profile:key};
        // Keep persisted auth blob in sync so a refresh shows the same profile.
        // Use authDurable so a session-only login STAYS session-only (don't
        // accidentally promote it to localStorage).
        if (token) _writeCachedAuth(token, next, authDurable);
        return next;
      });
      try {
        await apiFetch("/auth/profile", token, {
          method:"PATCH",
          body: JSON.stringify({ risk_profile: key }),
        });
      } catch(e) { console.error("Profile update failed", e); }
    } else {
      setScreen("settings");
    }
  };

  const handleSelectTicker = (t) => { setTicker(t); setScreen("stock"); };
  const handleBack = () => {
    // Always return to the tab the user was on before going into stock detail
    const prev = navTab === "discover" ? "discover"
               : navTab === "portfolio" ? "portfolio"
               : navTab === "landing" ? "landing"
               : "scanner";
    setScreen(prev);
    setTicker(null);
  };

  // v8.4.304: per-screen scroll memory. The list screens (Watchlist/Home/Discover/
  // Portfolio) stay mounted (display:none) but the app scrolls the window, and
  // opening a stock resets it to the top. Track each screen's scroll while it's
  // active and restore it on return — so going back lands you where you left off.
  const scrollPosRef = React.useRef({});
  React.useEffect(() => {
    const onScroll = () => { scrollPosRef.current[screen] = window.scrollY; };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [screen]);
  React.useLayoutEffect(() => {
    if (screen === "stock") return;   // StockScreen scrolls itself to the top on open
    const y = scrollPosRef.current[screen] || 0;
    requestAnimationFrame(() => { try { window.scrollTo(0, y); } catch (_) {} });
  }, [screen]);

  // Hooks must run unconditionally on every render — declare the context value
  // BEFORE the `!user` early return below. user?.risk_profile is null before
  // login; defaults to "moderate" so scoreUniverseBackground still works.
  const universeContextValue = useMemo(() => ({
    data: universeData,
    phase: universePhase,
    upsertUniverse,
    resetUniverse,
    refresh: () => {
      // With the atomic double-buffer swap in scoreUniverseBackground, we no
      // longer need to clear the universe first. The old data stays visible
      // until the new full dataset is swapped in atomically at phase 2 — so
      // users see old→new rather than old→empty→new.
      scoreUniverseBackground(token, watchlist, horizon, user?.risk_profile || "moderate");
    },
  }), [universeData, universePhase, upsertUniverse, resetUniverse, scoreUniverseBackground, token, watchlist, horizon, user?.risk_profile]);

  // ── Password reset deep-link routing (v7.9.49) ──────────────────────
  // When the URL has ?reset_token=…, render the reset screen regardless of
  // login state. Reset flow wins over both LoginScreen (logged-out) and the
  // main app (logged-in). After completion or cancel, we clear the param
  // from the URL and force a fresh login (the reset itself is intentionally
  // not auto-login — exercising the new credential confirms it works).
  // ?invite=CODE → registration flow. Higher priority than the cached-auth
  // path because the user explicitly clicked an invite link, but lower
  // priority than the password-reset flow below since a reset link is more
  // urgent (the user is fixing a known credential issue).
  if (inviteCode && !user) {
    return <RegisterScreen inviteCode={inviteCode}
      onLogin={(u, t) => {
        // Clean the URL so a refresh doesn't re-route to register.
        try {
          const url = new URL(window.location.href);
          url.searchParams.delete("invite");
          window.history.replaceState({}, "", url.toString());
        } catch (_) {}
        setInviteCode(null);
        handleLogin(u, t);
      }}
      onDone={() => {
        // Cancel / "already have an account" — drop to LoginScreen.
        try {
          const url = new URL(window.location.href);
          url.searchParams.delete("invite");
          window.history.replaceState({}, "", url.toString());
        } catch (_) {}
        setInviteCode(null);
      }}/>;
  }

  if (resetToken) {
    return <ResetPasswordScreen token={resetToken} onDone={(wasReset) => {
      // Strip the query param from the URL without reloading.
      try {
        const u = new URL(window.location.href);
        u.searchParams.delete("reset_token");
        window.history.replaceState({}, "", u.toString());
      } catch (_) {}
      setResetToken(null);
      // Only force logout when a reset actually happened — the user must
      // re-authenticate with the NEW credential to confirm it works. If they
      // cancelled out (or the link was bad), leave any cached auth alone so
      // they're not unnecessarily booted back to login.
      if (wasReset) {
        _clearCachedAuth();
        setUser(null); setToken(null);
      }
    }}/>;
  }

  if (!user) return <LoginScreen onLogin={handleLogin}/>;

  return (
    <UniverseDataProvider value={universeContextValue}>
    <div style={{
      background:C.bg, minHeight:"100vh", color:C.text,
      fontFamily:"'SF Pro Display',-apple-system,'Helvetica Neue',sans-serif",
      // overflowX:"clip" (not "hidden") — "hidden" forces overflow-y to compute to
      // "auto", turning this shell into a Y-scroll container, which DEFEATS the
      // stock-detail sticky header (it sticks to this box instead of the viewport
      // and scrolls away). "clip" prevents horizontal bleed WITHOUT establishing a
      // scroll container, so document-level position:sticky works again.
      maxWidth:430, margin:"0 auto", position:"relative", overflowX:"clip",
      paddingTop:"env(safe-area-inset-top, 0px)",
    }}>
      {showSplash && <SplashScreen onDone={() => setShowSplash(false)}/>}
      {updateAvailable && !showSplash && <UpdateBanner serverVersion={serverVersion}/>}
      {/* Name prompt — shown once when logged-in user has no name set */}
      {user && token && !showSplash && !(user.name || "").trim() && (
        <NamePromptBanner token={token} user={user}
          onNameSaved={updated => setUser(prev => ({...prev, ...updated}))}/>
      )}
      {screen === "landing" && !showSplash && <InstallPrompt />}
      {/* v8.4.308: global search magnifier — a plain teal magnifier matching the
          admin header ↻ (borderless, C.accent). On the main tab screens (not stock
          detail / admin). v8.4.312: consistent top-right placement across the app;
          the Watchlist's ↻/manage now sit on their own row below it. */}
      {!showSplash && user && token && ["landing","scanner","discover","portfolio","settings"].includes(screen) && (
        /* v8.4.313: centered to the app frame (maxWidth 430) like the bottom nav —
           was position:fixed right:16 = pinned to the VIEWPORT edge, so on desktop
           it landed in the empty gutter far from the centered column (invisible). */
        <div style={{position:"fixed",zIndex:30,
          top:"calc(env(safe-area-inset-top, 0px) + 16px)",
          left:0,right:0,margin:"0 auto",width:"100%",maxWidth:430,
          pointerEvents:"none",display:"flex",justifyContent:"flex-end",
          paddingRight:16,boxSizing:"border-box"}}>
          <button onClick={()=>setSearchOpen(true)} aria-label="Search ticker" title="Search ticker"
            style={{border:"none",background:"none",padding:0,pointerEvents:"auto",
              width:32,height:32,color:C.accent,cursor:"pointer",display:"flex",
              alignItems:"center",justifyContent:"center"}}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
              stroke={C.accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="7"/><line x1="16.5" y1="16.5" x2="21" y2="21"/>
            </svg>
          </button>
        </div>
      )}
      {searchOpen && (
        <TickerSearchOverlay universeData={universeData} watchlist={watchlist}
          onSelect={handleSelectTicker} onClose={()=>setSearchOpen(false)}/>
      )}
      {/* Keep the Home/landing screen MOUNTED (hidden with CSS) once the splash is
          gone — like the scanner — so Market Pulse / Market Regime / etc. stay
          populated across navigation instead of unmounting + re-fetching (going
          blank) every time you return to it. */}
      {!showSplash && (
        <div style={{display: screen==="landing" ? "block" : "none"}}>
          <ErrorBoundary key="landing"><LandingScreen user={user} token={token} horizon={horizon} setHorizon={setHorizon} onProfileChange={handleProfileChange} onNavigate={(id)=>{setNavTab(id);setScreen(id);}} marketData={marketData} setMarketData={setMarketDataPersisted} onRefreshWatchlist={()=>{ resetUniverse(); setTimeout(()=>scoreUniverseBackground(token,watchlist),150); }} watchlistSize={watchlist.length} universePhase={universePhase} universeScoredCount={Object.keys(universeData).length} universeData={universeData} lastScanTime={lastScanTime} onSelectTicker={handleSelectTicker} onCancelScan={()=>{universeAbortRef.current=true;setUniversePhase(0);}} currency={currency} fxRates={fxRates} onAlertsChanged={handleAlertsChanged}/></ErrorBoundary>
        </div>
      )}
      {/* Always render scanner so state persists -- hide with CSS when not active */}
      <div style={{display: screen==="scanner" ? "block" : "none"}}>
        <ErrorBoundary key="scanner">
          <ScannerScreen
            visible={screen==="scanner"}
            user={user} token={token}
            onSelectTicker={handleSelectTicker}
            onProfileChange={handleProfileChange}
            scanData={scanData} setScanData={setScanData} setMarketData={setMarketDataPersisted} runScanRef={runScanRef} onScanComplete={setLastScanTime} universeData={universeData} upsertUniverse={upsertUniverse}
            scanLoading={scanLoading} setScanLoading={setScanLoading}
            scanError={scanError} setScanError={setScanError}
            watchlist={watchlist} setWatchlist={setWatchlist}
            onAuthError={handleAuthError}
            globalHorizon={horizon}
            tradePref={tradePref}
            currency={currency}
            fxRates={fxRates}
            earningsSoon={earningsSoon}
          />
        </ErrorBoundary>
      </div>
      <div style={{display: screen==="discover" ? "block" : "none"}}>
        <ErrorBoundary key="discover"><DiscoverScreen user={user} token={token} onSelectTicker={handleSelectTicker} onAuthError={handleAuthError} globalHorizon={horizon} watchlist={watchlist} setWatchlist={setWatchlist} lastScanTime={lastScanTime} universeData={universeData} upsertUniverse={upsertUniverse} tradePref={tradePref} currency={currency} fxRates={fxRates} earningsSoon={earningsSoon} visible={screen==="discover"}/></ErrorBoundary>
      </div>
      <div style={{display: screen==="portfolio" ? "block" : "none"}}>
        <ErrorBoundary key="portfolio"><PortfolioScreen user={user} token={token} watchlist={watchlist} setWatchlist={setWatchlist} onSelectTicker={handleSelectTicker} globalHorizon={horizon} onNavigate={(id)=>{setNavTab(id);setScreen(id);}} scanData={scanData} universeData={universeData} upsertUniverse={upsertUniverse} tradePref={tradePref} currency={currency} fxRates={fxRates} visible={screen==="portfolio"}/></ErrorBoundary>
      </div>
      {screen==="stock"     && ticker && <ErrorBoundary key={ticker}><StockScreen ticker={ticker} user={user} horizon={horizon} token={token} onBack={handleBack} onAuthError={handleAuthError} watchlist={watchlist} setWatchlist={setWatchlist} upsertUniverse={upsertUniverse}/></ErrorBoundary>}
      {screen==="settings"  && <ErrorBoundary key="settings"><SettingsScreen user={user} onProfileChange={handleProfileChange} onLogout={handleLogout} token={token} onAuthError={handleAuthError} onAdmin={()=>setScreen("admin")} currency={currency} onCurrencyChange={handleCurrencyChange} tradePref={tradePref} onTradePrefChange={handleTradePrefChange} alertsRefreshKey={alertsRefreshKey} textScale={textScale} onTextScaleChange={handleTextScaleChange}/></ErrorBoundary>}
      {screen==="admin" && user?.role==="admin" && <ErrorBoundary key="admin"><AdminScreen user={user} token={token} onBack={()=>setScreen("settings")}/></ErrorBoundary>}

      {/* Bottom nav -- professional engraved style */}
      {/* iOS position:fixed gotchas (do NOT reintroduce):
          - transform:translateX(-50%) breaks fixed positioning → use left/right:0 + margin:auto.
          - backdrop-filter:blur + will-change:transform promote this to its own
            compositing layer, which iOS mispositions during momentum scroll (the
            bar "floats" into the middle of the screen until app restart). The bg
            is already ~98% opaque, so a solid fill looks identical without the bug. */}
      <div style={{
        position:"fixed", bottom:0, left:0, right:0, margin:"0 auto",
        width:"100%", maxWidth:430,
        background:"rgba(8,12,20,0.98)",
        borderTop:"1px solid rgba(255,255,255,0.06)",
        display:"flex",
        paddingBottom:"env(safe-area-inset-bottom, 10px)",
        boxShadow:"0 -1px 0 rgba(255,255,255,0.04), 0 -8px 32px rgba(0,0,0,0.4)",
      }}>
        <FeatureTour tourId="app_intro" version={1} steps={APP_INTRO_STEPS} active={!!user && !!token}/>
        {[
          { id:"landing", label:"Home", path:
            <><path d="M12 3L2 12h3v9h6v-5h2v5h6v-9h3L12 3z" strokeWidth="1.5" fill="none"/></> },
          { id:"scanner", label:"Watchlist", path:
            <><polygon points="12,3 14.5,8.5 21,9.5 16.5,14 17.5,21 12,18 6.5,21 7.5,14 3,9.5 9.5,8.5" strokeWidth="1.5" fill="none"/></> },
          { id:"discover", label:"Discover", path:
            <><circle cx="11" cy="11" r="7" strokeWidth="1.5" fill="none"/><line x1="16.5" y1="16.5" x2="21" y2="21" strokeWidth="1.5" strokeLinecap="round"/><path d="M9 11 l2 2 4-4" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/></> },
          { id:"portfolio", label:"Portfolio", path:
            <><rect x="3" y="14" width="4" height="7" rx="1" strokeWidth="1.5" fill="none"/><rect x="10" y="9" width="4" height="12" rx="1" strokeWidth="1.5" fill="none"/><rect x="17" y="4" width="4" height="17" rx="1" strokeWidth="1.5" fill="none"/><path d="M5 10 Q8 7 12 5 Q15 3 19 3" strokeWidth="1.2" fill="none" strokeLinecap="round"/></> },
          { id:"settings", label:"Settings", path:
            <><circle cx="12" cy="12" r="3" strokeWidth="1.5" fill="none"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" strokeWidth="1.5" fill="none"/></> },
        ].map(tab => {
          const active = navTab===tab.id && screen!=="stock";
          return (
            <button key={tab.id} data-tour={"nav-"+tab.id}
              onClick={()=>{setNavTab(tab.id);setScreen(tab.id);}}
              style={{
                flex:1, padding:"10px 0 8px",
                background:"none", border:"none",
                cursor:"pointer",
                display:"flex", flexDirection:"column",
                alignItems:"center", gap:4,
                fontFamily:"inherit",
                position:"relative",
                transition:"all 0.2s",
              }}>
              {/* Active indicator line */}
              {active && (
                <div style={{
                  position:"absolute", top:0, left:"25%", right:"25%",
                  height:2, borderRadius:"0 0 2px 2px",
                  background:"linear-gradient(90deg,#00d4ff,#00e5aa)",
                  boxShadow:"0 0 8px rgba(0,212,255,0.6)",
                }}/>
              )}
              {/* SVG icon */}
              <svg
                width="22" height="22" viewBox="0 0 24 24"
                stroke={active ? "#00d4ff" : "rgba(255,255,255,0.28)"}
                fill="none"
                style={{
                  filter: active ? "drop-shadow(0 0 4px rgba(0,212,255,0.4))" : "none",
                  transition:"all 0.2s",
                }}>
                {tab.path}
              </svg>
              {/* Label */}
              <span style={{
                fontSize:9, fontWeight: active ? 700 : 500,
                letterSpacing:"0.05em",
                color: active ? "#00d4ff" : "rgba(255,255,255,0.25)",
                transition:"all 0.2s",
              }}>{tab.label}</span>
            </button>
          );
        })}
      </div>
    </div>
    </UniverseDataProvider>
  );
}
